/* Generated by restful-react */

import React from "react";
import {
  Get,
  GetProps,
  useGet,
  UseGetProps,
  Mutate,
  MutateProps,
  useMutate,
  UseMutateProps,
} from "restful-react";

type Require<T, R extends keyof T> = T & Required<Pick<T, R>>;
export const SPEC_VERSION = "1.0.0";
export interface Identifier {
  url?: string;
  id?: string;
}

/**
 * Response containing parent, child, and sibling resubmission data for a service request
 */
export interface ResubmissionDataResponse {
  parentResubmission?: ResubmissionReference;
  /**
   * List of child service requests that are resubmissions of this request
   */
  childResubmissions?: ResubmissionReference[];
  /**
   * List of sibling service requests that share the same parent resubmission
   */
  siblingResubmissions?: ResubmissionReference[];
}

/**
 * Reference to a related service request in the resubmission chain
 */
export interface ResubmissionReference {
  /**
   * The database ID of the service request
   */
  id: string;
  /**
   * The Cohere ID of the service request
   */
  cohereId: string;
}

/**
 * Configuration options related to submissions for a health plan
 */
export interface SubmissionConfigurationResponse {
  /**
   * Whether resubmission tracking is enabled for this health plan
   */
  resubmissionTrackingEnabled?: boolean;
  /**
   * Whether auth number should be used as the tracking identifier
   */
  authNumberAsTracking?: boolean;
}

export interface ServiceRequestRuleDataModelJson {
  name: string;
  type: string;
  fields?: ServiceRequestRuleDataModelJson[];
}

/**
 * Represents a single physical address
 */
export interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Represents a single physical address
 */
export type Appeal = StampedObject;

/**
 * Dictionary that contains external reference identifiers in EHRs
 */
export interface EHRReferenceInfo {
  vendor?: EhrVendor;
  practiceId?: string;
  patientId?: string;
  active?: boolean;
  departmentIds?: string[];
}

/**
 * Represents a single phone number
 */
export interface PhoneNumber {
  number?: string;
  extension?: string;
}

/**
 * Represents a loosely serialized field name and value
 */
export interface CustomField {
  fieldName?: string;
  valueString?: string;
}

export interface FilterAgeRange {
  minAge?: number;
  maxAge?: number;
}

export type FilterSupportedGender = "MALE" | "FEMALE" | "BOTH";

export type Filter =
  | { [key: string]: any }
  | FilterAgeRange
  | FilterSupportedGender;

/**
 * Code system interface based on https://www.hl7.org/fhir/terminologies.html
 */
export interface Coding {
  id?: string;
  code: string;
  description?: string;
  default?: boolean;
  system?: string;
}

export type DiagnosisCode = SemanticDiagnosisCode & {
  isDiseaseCode?: boolean;
  diseaseCode?: boolean;
  searchableCode?: string;
};

export type ProcedureCode = SemanticProcedureCode & {
  approvedUnits?: number;
  default?: boolean;
};

/**
 * Case overview citation click data
 */
export interface CaseOverviewCitationClickData {
  clickedAt?: string;
  attachmentId?: string;
  insightId?: string;
  insightName?: string;
  insightType?: string;
  entityName?: string;
  details?: string;
  citationClickSpansData?: ReviewAICitationClickSpanData[];
}

/**
 * Clinical evaluation citation click data
 */
export interface ClinicalEvaluationCitationClickData {
  clickedAt?: string;
  attachmentId?: string;
  insightId?: string;
  insightName?: string;
  insightType?: string;
  indicationTitle?: string;
  frameworkTitle?: string;
  determination?: string;
  reasoning?: string;
  citationClickSpansData?: ReviewAICitationClickSpanData[];
}

/**
 * Clinical evaluation condition selection data
 */
export interface ClinicalEvaluationConditionSelectionData {
  selectedAt?: string;
  conditionId?: string;
  conditionLabel?: string;
  wasSuggested?: boolean;
  selectionType?: ReviewAIClinicalEvaluationConditionSelectionSelectionType;
  availableConditions?: ClinicalEvaluationAvailableConditionData[];
  suggestedConditions?: ClinicalEvaluationSuggestedConditionData[];
}

/**
 * Casey citation click data
 */
export interface CaseyCitationClickData {
  clickedAt?: string;
  attachmentId?: string;
  answer?: string;
  caseyCitationClickSpansData?: ReviewAICitationClickSpanData[];
}

/**
 * Common Review AI citation click span data
 */
export interface ReviewAICitationClickSpanData {
  text?: string;
  pageNumber?: number;
  leftCoordinate?: number;
  topCoordinate?: number;
  height?: number;
  width?: number;
}

/**
 * Clinical evaluation available condition data
 */
export interface ClinicalEvaluationAvailableConditionData {
  conditionId?: string;
  conditionLabel?: string;
}

/**
 * Clinical evaluation suggested selection data
 */
export interface ClinicalEvaluationSuggestedConditionData {
  conditionId?: string;
  conditionLabel?: string;
  rank?: number;
  reason?: string;
}

/**
 * Casey answer feedback data
 */
export interface CaseyAnswerFeedbackData {
  clickedAt?: string;
  answer?: string;
  feedback?: string;
  selectedComments?: string[];
}

/**
 * Filter groups for Convene objects
 */
export interface FilterGroup {
  requestTypes?: RequestType[];
  procedureCodes?: TargetCode[];
  linesOfBusiness?: LineOfBusiness[];
  filterStates?: UnitedStates[];
  diagnosisCodes?: DiagnosisCode[];
  filterGroupName?: string;
  facilityCategories?: FacilityCategory[];
  healthPlans?: string[];
  authCategories?: AuthCategory[];
  expirationDate?: string;
  effectiveDate?: string;
  goLiveDate?: string;
  id: string;
  bodyArea?: string[];
}

/**
 * Base review filter
 */
export type ConveneFilterCommon = VersionedObject & {
  id?: string;
  name?: string;
  description?: string;
  type?:
    | "CLINICAL_SERVICE_FILTER"
    | "DIAGNOSIS_CODE_FILTER"
    | "PAYOR_FILTER"
    | "PROCEDURE_CODE_FILTER";
};

export type ClinicalServiceFilter = ConveneFilterCommon & {
  type?: "CLINICAL_SERVICE_FILTER";
  includes?: boolean;
  clinicalServiceIds?: string[];
  clinicalServices?: ClinicalService[];
};

export type AuthDecisionGroupWithdraw = {
  withdrawnReason?: string;
  withdrawRequestor?: WithdrawRequestor;
};

/**
 * succcessfull response for Authdecision group withdraw API
 */
export type AuthDecisionGroupWithdrawResponse = StandardFields & {
  finalAuthStatus?: string;
  serviceRequests?: AuthDecisionGroupServiceRequest;
};

export type DiagnosisCodeFilter = ConveneFilterCommon & {
  type?: "DIAGNOSIS_CODE_FILTER";
  diagnosisCodes?: DiagnosisCode[];
  semanticDiagnosisCodes?: DiagnosisCode[];
};

export type ProcedureCodeFilter = ConveneFilterCommon & {
  type?: "PROCEDURE_CODE_FILTER";
  semanticProcedureCodes?: ProcedureCode[];
  procedureCodes?: ProcedureCode[];
};

export type ConveneFilter =
  | ClinicalServiceFilter
  | DiagnosisCodeFilter
  | ProcedureCodeFilter;

/**
 * Shared fields amongst all stampable objects
 */
export interface StampedObject {
  dateCreated?: string;
  lastUpdated?: string;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * Shared fields amongst all objects with the WithHealthPlanAccessControl trait
 */
export interface HealthPlanAccessedObject {
  allowedHealthPlanIds?: string[];
  allowedHealthPlans?: HealthPlan[];
}

/**
 * Shared fields amongst all promotable objects
 */
export interface PromotableObject {
  productionReady?: boolean;
  promotionTimestamp?: string;
  productionReadyTimestamp?: string;
  promotedBy?: string;
}

/**
 * Shared fields amongst all version-able objects
 */
export interface VersionedObject {
  id?: string;
  versionClass?: string;
  versionName?: string;
  versionPath?: string;
  documentVersion?: string;
  originalVersionId?: string;
  versionOfId?: string;
  publishedAt?: string;
  publishedBy?: string;
  published?: boolean;
  updatedBy?: string;
  isCurrentDraft?: boolean;
  isCurrentProduction?: boolean;
  isCurrentPublished?: boolean;
  changeTypes?: string[];
  changeDescription?: string;
  versions?: VersionedObject[];
}

/**
 * Entity with tag information
 */
export interface TaggedEntity {
  tagIds?: string[];
  allTags?: Tag[];
  lastTagged?: string;
  lastTaggedBy?: string;
}

export interface UpdateReplaceOrAmendResponse {
  verb?: "AMEND" | "UPDATE" | "REPLACE" | "CONTINUATION";
  details?: {
    field?: string;
    message?: string;
  }[];
  serviceRequest?: ServiceRequestResponse;
}

export type ServiceRequestRule = VersionedObject &
  PromotableObject &
  StampedObject &
  HealthPlanAccessedObject & {
    id?: string;
    title?: string;
    description?: string;
    ruleLabel?: RuleLabel;
    active?: boolean;
    skipPatientSearch?: boolean;
    actions?: ServiceRequestRuleAction[];
    variableActionGroups?: VariableActionGroup[];
    filters?: ConveneFilter[];
    condition?: RuleCondition;
    linesOfBusiness?: LineOfBusiness[];
    issueStates?: UnitedStates[];
    diagnosisCodes?: DiagnosisCode[];
    semanticProcedureCodes?: ProcedureCode[];
    encounterTypes?: FacilityCategory[];
    filterIds?: string[];
    currentDraft?: ServiceRequestRule;
    impactMetrics?: ImpactMetric[];
    initiatives?: {
      id?: string;
      title?: string;
    }[];
    specialties?: string[];
    selectedGuidelineId?: string;
    ruleAssociatedGuidelineId?: string;
    diagnosisCodeFilterTypes?: DiagnosisCodeFilterType[];
    ruleLabelPriorityPublished?: number;
    ruleLabelPriorityProduction?: number;
    externalDescription?: string;
    isGeneratedByGuideline?: boolean | null;
    autoApprovalRate?: number;
    precisionOptimizedDecisioning?: PrecisionOptimizedDecisioning;
  };

/**
 * Request body for completing POD training
 */
export interface PodTrainingCompletionRequest {
  /**
   * The status of the training completion
   */
  status: "SUCCESS" | "FAILED";
  /**
   * Error message if status is FAILED
   */
  error?: string;
  baseline_metrics?: BaselineMetrics;
  /**
   * ESI training results data
   */
  esi_results?: TrainingData[];
  /**
   * The selected threshold from training
   */
  selected_threshold?: number;
  /**
   * Condition weights from ML analysis
   */
  condition_weights?: {
    [key: string]: MlInsights;
  };
}

/**
 * Complete training analytics data for a service request rule
 */
export interface RuleTrainingAnalytics {
  /**
   * Unique identifier for the analytics record
   */
  id: string;
  /**
   * Foreign key reference to the associated rule
   */
  ruleId: string;
  /**
   * Date when training was initiated
   */
  trainingStartedDate: string;
  /**
   * Date when training was requested
   */
  trainingRequestedDate?: string;
  /**
   * Date when training was completed (null if still in progress or failed)
   */
  trainingCompletedDate?: string | null;
  /**
   * Error message if training failed
   */
  error?: string | null;
  baselineMetrics?: BaselineMetrics;
  /**
   * Array of exactly 101 training threshold results
   */
  trainingResults?:
    | [
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData,
        TrainingData
      ]
    | null;
  /**
   * Timestamp when the analytics record was created
   */
  dateCreated?: string;
  /**
   * User name who created the resource
   */
  createdByName?: string;
  /**
   * User name who requested the resource
   */
  requestedByName?: string;
  /**
   * Timestamp when the analytics record was last updated
   */
  lastUpdated?: string;
}

/**
 * Baseline performance metrics before training optimization
 */
export interface BaselineMetrics {
  /**
   * Start date of the baseline measurement period
   */
  startDate?: string | null;
  /**
   * End date of the baseline measurement period
   */
  endDate?: string | null;
  /**
   * Overall approval rate (0.0 to 1.0)
   */
  approvalRate?: number | null;
  /**
   * Automatic approval rate (0.0 to 1.0)
   */
  autoApprovalRate?: number | null;
  /**
   * Rate of pended requests that were later approved (0.0 to 1.0)
   */
  pendToApproveRate?: number | null;
  /**
   * Rate of pended requests that were later denied (0.0 to 1.0)
   */
  pendToDenyRate?: number | null;
  /**
   * Rate of incorrect approvals (0.0 to 1.0)
   */
  falseApprovalRate?: number | null;
  /**
   * Number of incorrect approvals
   */
  falseApprovalCount?: number | null;
  /**
   * Total number of approved requests
   */
  approvedCount?: number | null;
  /**
   * Number of automatically approved requests
   */
  autoApprovedCount?: number | null;
  /**
   * Number of requests pended then approved
   */
  pendToApproveCount?: number | null;
  /**
   * Number of requests pended then denied
   */
  pendToDenyCount?: number | null;
  /**
   * Total number of requests in the baseline period
   */
  totalRequests?: number | null;
}

/**
 * Training performance data at a specific threshold
 */
export interface TrainingData {
  /**
   * Decision threshold value (0.0 to 1.0)
   */
  threshold: number;
  /**
   * Auto approvals count at this threshold
   */
  aa: number;
  /**
   * Pend to approvals count at this threshold
   */
  pta: number;
  /**
   * Pend to denials count at this threshold
   */
  ptd: number;
  /**
   * True approvals count at this threshold
   */
  ta: number;
  /**
   * False approvals count at this threshold
   */
  fa: number;
}

/**
 * Precision optimized decisioning configuration and status
 */
export interface PrecisionOptimizedDecisioning {
  /**
   * Whether POD is enabled for this rule
   */
  enabled?: boolean;
  status?: PrecisionOptimizedDecisioningStatus;
  /**
   * The selected decision threshold
   */
  threshold?: number | null;
}

/**
 * POD status
 */
export type PrecisionOptimizedDecisioningStatus =
  | "UNTRAINED"
  | "BUSY"
  | "SUCCESS"
  | "FAILED";

/**
 * ML insights data structure
 */
export interface MlInsights {
  /**
   * ML weight value
   */
  weight?: number;
  /**
   * Confidence score
   */
  confidence?: number;
}

/**
 * Variable action groups store a title, probability, a list of actions that could be picked based on probability, and a flag of whether the group was picked.
 */
export interface VariableActionGroup {
  title?: string;
  probability?: number;
  picked?: boolean;
  actions?: ServiceRequestRuleAction[];
}

/**
 * An action to be executed if a rule condition is satisified
 */
export type RuleAction =
  | SetValueRuleAction
  | RedirectRuleAction
  | RecommendChangeRuleAction;

/**
 * POST payload with data to test against a ServiceRequestRule
 */
export interface ServiceRequestRuleTestPayload {
  patientAttributes?: {
    codes?: {
      code: string;
      system: string;
    }[];
  }[];
}

/**
 * An abstract building block of a Rule
 */
export interface RuleCondition {
  hasEvidenceBasedNudge?: boolean;
  isStatement?: boolean;
  relativeWeight?: number;
  outcome?: boolean;
  statement?: RuleStatement;
  block?: RuleBlock;
  reusableCondition?: ReusableCondition;
  evaluator?: RuleEvaluator;
  indication?: Indication | null;
  indicationType?: string;
  internalIndicationDescription?: string;
  indicationMetAction?: RecommendChangeRuleAction;
  indicationUnmetAction?: RecommendChangeRuleAction;
  evidenceData?: string;
  evidenceId?: string;
  conditionId?: string;
  responseOptions?: ResponseOption[];
  mlInsights?: MLInsights;
}

/**
 * A complex boolean statement that includes a Logical Operator
 */
export interface RuleBlock {
  logicalOperator: LogicalOperator;
  logicalOperatorValue?: number;
  outcome?: boolean;
  condition?: RuleCondition;
  conditions?: RuleCondition[];
  /**
   * used within the Block component to identify certain behaviors
   */
  componentVal?: string;
}

/**
 * A base-case boolean statement that includes 2 operands, an operator, and an optional operator parameter
 */
export interface RuleStatement {
  operandA: RuleOperand;
  operandAData?: { [key: string]: any };
  operator: RuleOperator;
  transformer?: Transformer;
  comparator?: Comparator;
  outcome?: boolean;
  operandB: RuleOperand;
  operandBData?: { [key: string]: any };
  operatorParameter?: {
    count?: number;
  };
  statementType?: string;
}

/**
 * A collection of conditions
 */
export type ReusableCondition = VersionedObject &
  StampedObject & {
    id?: string;
    reusableConditionId?: string;
    name?: string;
    description?: string;
    isActive?: boolean;
    ruleIds?: string[];
    condition?: RuleCondition;
    indicationGroupIds?: string[];
    indicationUIDs?: string[];
    /**
     * map of ruleId -> map of indication_groupId -> list of indication_uid
     */
    ruleIdToCIM?: {
      [key: string]: {
        [key: string]: string[];
      };
    };
    /**
     * map of indication_groupId -> list of indication_uid to track indications within condition field
     */
    internalConditionCIMs?: {
      [key: string]: string[];
    };
    currentDraft?: ReusableCondition;
  };

/**
 * weights, scores, and biases an ML model generates for a given rule
 */
export interface MLInsights {
  weight?: number;
  bias?: number;
  score?: number;
  relativeImportance?: number;
  frequencyThreshold?: number;
}

/**
 * A base abstract class to support custom statements
 */
export type RuleEvaluator =
  | CodedEvaluator
  | NonCodedEvaluator
  | UoTCodedEvaluator
  | FindingStoreEvaluator;

export interface CodedEvaluator {
  type?:
    | "UtilizationOverTime"
    | "CodedAttributes"
    | "ProcedureCodes"
    | "AssessmentCodes"
    | "AssessmentMeasurements"
    | "DiagnosisCodes"
    | "SubmissionDate";
  operator?: RuleOperator;
  targetCodes?: TargetCode[];
  targetCodeGroups?: TargetCode[][];
  targetUnits?: number;
  maxAgeDays?: number;
  ageUnitType?: string;
  atLeastNumber?: number;
  outcome?: boolean;
  useEffectivePlanDate?: boolean;
}

export interface NonCodedEvaluator {
  type?:
    | "ServiceRequestStartDate"
    | "ClinicalNoteFinding"
    | "AttachmentMissingImaging"
    | "AttachmentMissingClinicalEvidence"
    | "ServiceRequestForActiveSmoker"
    | "BmiValue"
    | "KeyphraseSearch"
    | "SpecificQuantity"
    | "DateComparison"
    | "PayerCustomFields"
    | "ReferralOnFile"
    | "Benefits";
  evidenceType?: EvidenceType;
  operator?: RuleOperator;
  maxDaysAgo?: number;
  minimumProbability?: number;
  atLeastNumber?: number;
  quantity?: number;
  timeWindow?: number;
  timeUnit?: string;
  phrases?: string[];
  outcome?: boolean;
  threshold?: number;
  assertionType?: string;
  useBusinessCalendarLogic?: boolean;
}

export type ExtendedRuleOperator =
  | RuleOperator
  | "contains_any_dx_family"
  | "contains_no_dx_family";

export type PayerCustomFieldsEvaluator = NonCodedEvaluator & {
  fieldName?: string;
  valueString?: string;
  objectType?: string;
};

export type BenefitsEvaluator = NonCodedEvaluator & {
  fieldNames?: string[];
  valueString?: string;
  comparator?: string;
  groupIdMatch?: boolean;
};

export type ReferralOnFileEvaluator = NonCodedEvaluator & {
  dateFilter?: {
    lookbackAmount?: number;
    lookbackUnit?: string;
  };
  providerNpiMatch?: boolean;
  groupNpiMatch?: boolean;
};

export type BuyUpEvaluator = NonCodedEvaluator & {
  buyUpCategory?: string;
};

export interface StandardConceptEvaluator {
  type?: "StandardConcept";
  operator?: RuleOperator;
  concepts?: {
    conceptId?: string;
    conceptName?: string;
    minimumCount?: number;
  }[];
  outcome?: boolean;
}

export type UoTCodedEvaluator = CodedEvaluator & {
  authStatusFilter?: string[];
  hasUtilization?: boolean;
  numberOf?: string;
  includeCurrentServiceRequestUnits?: boolean;
  dates?: {
    useLookbackTimeframe?: boolean;
    comparison?:
      | "time_between_dates"
      | "exact_match"
      | "overlap_between_service_dates";
    dateFromIncomingRequest?: string;
    dateFromLastRequest?: string;
    datesToCompare?: string[];
    maxAgeDays?: number;
    operator?: RuleOperator;
    ageUnitType?: string;
  };
  dxCodes?: {
    customDxCodes?: TargetCode[];
    useDxCodesFromIncomingRequest?: boolean;
    overlapFilter?: ExtendedRuleOperator;
  };
  pxCodes?: {
    usePxCodesFromIncomingRequest?: boolean;
    overlapFilter?: RuleOperator;
  };
  units?: {
    function?: string;
    compareToIncomingRequest?: boolean;
  };
  siteOfServiceFilter?: {
    siteOfServiceFilterValue?: string;
  };
  performingProviderFilter?: {
    performingProviderFilterValue?: string;
  };
  performingFacilityFilter?: {
    performingFacilityFilterValue?: string;
  };
  authCategoryFilter?: {
    authCategoryFilterValue?: string;
  };
  bodyAreaFilter?: {
    bodyAreaFilterValue?: string;
  };
  clinicalServicesFilter?: {
    clinicalServicesOverlapValue?: string;
    clinicalServicesSearchTypeValue?: string;
    selectedClinicalServiceOptions?: {
      id?: string;
      label?: string;
    }[];
  };
  useInpatientDays?: boolean;
};

export interface FindingStoreEvaluator {
  type?: string;
  findingType?: FindingType;
  providerType?: string;
  picker?: FindingTypePickerOrAggregator;
  aggregator?: FindingTypePickerOrAggregator;
  outcome?: boolean;
  findingRecordFilters?: FindingRecordFilter[];
  mostRecentFindingField?: string;
}

export type DateComparisonEvaluator = NonCodedEvaluator & {
  dateField1?: string;
  dateField2?: string;
  operator?: RuleOperator;
  offsetOperator1?: string;
  offsetAmount1?: number;
  offsetUnit1?: string;
  offsetOperator2?: string;
  offsetAmount2?: number;
  offsetUnit2?: string;
};

export interface FindingRecord {
  id?: string;
  cohereEntityId?: string;
  findingType?: FindingType;
  findingInstant?: string;
}

export interface FindingRecordSearchPayload {
  max?: number;
  offset?: number;
  cohereEntity?: string;
  findingType?: string;
  filterOn?: ClausalCondition[];
  sortBy?: AbstractSort[];
}

export interface FindingRecordPostRequest {
  value?: string;
  findingRecordId?: string;
  findingTypeId?: string;
  findingTypeName?: string;
  findingTypeExtensions?: { [key: string]: any };
  findingProducerId?: string;
  findingProducerExtensions?: { [key: string]: any };
  jsonData?: {
    [key: string]: string;
  };
  cohereEntityId: string;
  findingInstant: string;
  source?: string;
}

export interface FindingRecordBulkSaveResponse {
  results?: FindingRecordBulkSaveItemResponse[];
}

export interface FindingRecordBulkSaveItemResponse {
  saved?: boolean;
  id?: string;
}

export interface FindingType {
  id?: string;
  name?: string;
  description?: string;
  cohereEntity?: string;
  originalVersionId?: string;
  value?: FindingTypeExtension;
  extensions?: FindingTypeExtension[];
  lastUpdated?: string;
  updatedBy?: string;
}

export interface FindingTypeExtension {
  name?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  type?: string;
  enumKeywords?: string[];
  required?: boolean;
  valueListString?: string[];
  valueString?: string;
}

export interface FindingTypePickerOrAggregator {
  type?: string;
  comparator?: string;
  targetValue?: {
    value?: number | boolean | string | string[];
  };
}

export interface FindingRecordFilter {
  fieldName?: string;
  extensionName?: string;
  initialComparisonField?: string;
  finalComparisonField?: string;
  comparator?: string;
  secondaryComparator?: string;
  expectedValue?: {
    value?: number | boolean | string | string[];
  };
  anchor?: string;
  maxDaysBeforeAnchor?: number;
  minDaysBeforeAnchor?: number;
}

export type EvidenceType =
  | "HomeExerciseProgram"
  | "PhysicalTherapy"
  | "ConservativeInjections"
  | "LifestyleModification"
  | "AdvancedImaging"
  | "ActiveSmoker"
  | "BMI";

export interface TargetCode {
  code?: string;
  system?: string;
  withInference?: boolean;
  relationshipTo?: string[];
  selectedDetails?: SelectedDetailObject[];
}

/**
 * Primitives, string array for tags, custom one for Code Groups, custom for PatientAttribute/RuleUtilizationOverTime
 */
export interface RuleOperand {
  value:
    | string
    | number
    | boolean
    | (string | RulePatientAttribute | RuleUtilizationOverTime)[]
    | StringCodeGroups;
}

export type StringCodeGroups = string[][];

export interface RulePatientAttribute {
  codes?: {
    system?: string;
    code?: string;
  }[];
  maxAgeDays?: number;
}

export interface RuleUtilizationOverTime {
  codes?: {
    system?: string;
    code?: string;
  }[];
  maxAgeDays?: number;
  targetUnits?: number;
  useEffectivePlanDate?: boolean;
}

export interface SimulationRequestPayload {
  /**
   * start of time block for count of SR, by submission date
   */
  startDate?: string;
  /**
   * end of time block for count of SR, by submission date
   */
  endDate?: string;
  /**
   * limit on count returned, if return would be higher return max instead
   */
  max?: number;
  /**
   * the source of simulation request. Currently support condition level or all rules source
   */
  source?: string;
}

export interface SimulationCountPayload {
  value?: number;
}

/**
 * Supported operators
 */
export type RuleOperator =
  | "contains_all"
  | "contains_any"
  | "contains_none"
  | "contains_at_least_x"
  | "equal_to"
  | "not_equal_to"
  | "greater_than"
  | "less_than"
  | "greater_than_equal"
  | "less_than_equal"
  | "in_list"
  | "not_in_list"
  | "is_subset"
  | "sum_list"
  | "is_not_set";

/**
 * Supported transformers
 */
export type Transformer = "patient_attribute_aggregate_units";

/**
 * Supported comparator
 */
export type Comparator = "patient_attribute" | "unit_on_px";

/**
 * In the future will probably extend a basic rule action
 */
export type ServiceRequestRuleAction = RuleAction;

/**
 * The object that has the rule label array list in order
 */
export type RuleLabelPriority = PromotableObject & {
  id?: string;
  ruleLabelIds?: string[];
  ruleLabels?: RuleLabel[];
  publishedAt?: string;
  publishedBy?: string;
  published?: boolean;
  updatedBy?: string;
  isCurrentDraft?: boolean;
  isCurrentPublished?: boolean;
  currentDraft?: RuleLabelPriority;
  versions?: RuleLabelPriority[];
};

/**
 * In the future will probably extend a basic service request rule label
 */
export type ServiceRequestRuleLabelPriority = RuleLabelPriority;

/**
 * Labels to make Rule hierarchy
 */
export interface RuleLabel {
  id?: string;
  name?: string;
  description?: string;
  rules?: ServiceRequestRule[];
  lastUpdated?: string;
  updatedBy?: string;
}

export type HealthPlanPayload = StampedObject & {
  name?: string;
  description?: string;
  enumeration?: string;
  taxIdNumber?: string;
  coverageFieldsForBenefitPlanGroupLookup?: string[];
  allowedPlacesOfServiceIds?: string[];
  allowedPlacesOfService?: PlaceOfService[];
  supportDelegatedVendors?: string[];
  useNPPESProviderRegistryProvidersInSearch?: boolean;
  acceptsReferrals?: boolean;
  allowedServiceRequestTypes?: string[];
  status?: string;
  displayNames?: string[];
  ssoMapping?: SsoMappingEntry[];
};

/**
 * A health plan / payor object
 */
export type HealthPlan = StampedObject &
  HealthPlanPayload & {
    id: string;
  };

/**
 * barebones health plan object for options dropdown
 */
export interface HealthPlanSelectOption {
  id: string;
  name: string;
}

export interface CountResult {
  count?: number;
}

export type CarePath = HealthPlanAccessedObject &
  PromotableObject &
  VersionedObject & {
    name?: string;
    description?: string;
    groupName?: string;
    abbreviation?: string;
    lastUpdated?: string;
    diseaseArea?: DiagnosisCode;
    diagnosisCodes?: DiagnosisCode[];
    semanticDiagnosisCodes?: DiagnosisCode[];
    serviceCategories?: ServiceCategory[];
    currentDraft?: CarePath;
    filters?: {
      AGE_RANGE?: {
        minAge?: number;
        maxAge?: number;
      };
      SUPPORTED_GENDERS?: string;
    };
  };

export type CarePathUpdatePayload = PromotableObject & {
  id?: string;
  name?: string;
  description?: string;
  published?: boolean;
  groupName?: string;
  abbreviation?: string;
  diseaseArea?: string;
  semanticDiagnosisCodes?: DiagnosisCode[];
  allowedHealthPlanIds?: string[];
  serviceCategories?: ServiceCategory[];
  currentDraft?: CarePath;
  filters?: {
    AGE_RANGE?: {
      minAge?: number;
      maxAge?: number;
    };
    SUPPORTED_GENDERS?: string;
  };
};

export interface CarePathJourneyFieldIds {
  id?: string;
  patient?: string;
  carePath?: string;
  primarySemanticDiagnosisCode?: {
    id?: string;
    code?: string;
    description?: string;
    system?: string;
  };
  attachments?: HasId[];
}

export interface CarePathJourneyExpansion {
  patient?: Patient;
  carePath?: CarePath;
  primaryDiagnosis?: DiagnosisCode;
  primarySemanticDiagnosisCode?: DiagnosisCode;
  attachments?: Attachment[];
}

/**
 * Care Path Journey for a specific Patient
 */
export type CarePathJourney = CarePathJourneyExpansion & StandardFields;

export type CarePathJourneyUpdatePayload = CarePathJourneyFieldIds;

export type CarePathJourneyCreatePayload = CarePathJourneyFieldIds & {
  patient: {};
  carePath: {};
};

export interface ServiceCategory {
  name?: string;
  clinicalServices?: ClinicalService[];
}

export interface ClinicalAssessment {
  patientId?: string;
  carePathJourneyId?: string;
  serviceRequestIds?: string[];
  questions?: ClinicalAssessmentResponse[];
  type?: "COHERE" | "AGADIA";
  isDraft?: boolean;
}

export interface ClinicalAssessmentResponse {
  id?: string;
  answerContexts?: {
    clinicalQuestionId?: string;
    serviceRequestId?: string;
  }[];
  clinicalQuestion?: ClinicalQuestion;
  externalClinicalQuestion?: ClinicalQuestion;
  answers?: {
    clinicalAssessmentId?: string;
    patientId?: string;
    optionId?: string;
    unitType?: string;
    codes?: {
      code: string;
      system: string;
    }[];
    contexts?: {
      id?: string;
      serviceRequestId?: string;
      carePathJourneyId?: string;
      clinicalAssessmentId?: string;
      clinicalQuestionId?: string;
      encounterId?: string;
    }[];
    valueBoolean?: boolean;
    valueDate?: string;
    valueDateTime?: string;
    valueDecimal?: number;
    valueFloat?: number;
    valueInstant?: string;
    valueInt?: number;
    valueString?: string;
    prepopulated?: boolean;
  }[];
}

/**
 * Configuration for creation of Episodic Auth
 */
export interface EpisodicAuthConfig {
  id?: string;
  clinicalService?: ClinicalService;
  semanticProcedureCodes?: ProcedureCode[];
  encounterType?: string;
  placeOfService?: PlaceOfService;
  units?: number;
  filterGroups?: FilterGroup[];
  originalClinicalServiceId?: string;
  startDateFilter?: EpisodicAuthDateFilter;
  endDateFilter?: EpisodicAuthDateFilter;
}

export type ClinicalQuestion = HealthPlanAccessedObject &
  VersionedObject &
  PromotableObject &
  StampedObject & {
    /**
     * Not required for creating, but require elsewhere
     */
    id?: string;
    internalDescription?: string;
    questionText?: string;
    measurementUnitClass?: MeasurementUnitClass;
    type?:
      | "SINGLE_SELECT"
      | "MULTI_SELECT"
      | "DATE"
      | "MEASUREMENT"
      | "FREE_TEXT";
    required?: boolean;
    clinicalServiceId?: string;
    clinicalServiceName?: string;
    parentServiceId?: string;
    carePathId?: string;
    externalAssessmentProperties?: {
      [key: string]: string;
    };
    responseOptions?: ResponseOption[];
    observationCodes?: ObservationCode[];
    filterGroups?: FilterGroup[];
    currentDraft?: ClinicalQuestion;
    displayCondition?: RuleCondition | null;
    relevantLinks?: ClinicalQuestionLink[];
    reusableCondition?: ReusableCondition;
  };

export interface ClinicalQuestionPayload {
  /**
   * Not required for creating, but require elsewhere
   */
  id?: string;
  internalDescription?: string;
  questionText?: string;
  measurementUnitClass?: MeasurementUnitClass;
  type?:
    | "SINGLE_SELECT"
    | "MULTI_SELECT"
    | "DATE"
    | "MEASUREMENT"
    | "FREE_TEXT";
  required?: boolean;
  filterGroups?: FilterGroup[];
  externalAssessmentProperties?: {
    [key: string]: string;
  };
  responseOptions?: ResponseOption[];
  observationCodes?: ObservationCode[];
  displayCondition?: RuleCondition | null;
  relevantLinks?: ClinicalQuestionLink[];
  reusableCondition?: ReusableCondition;
}

/**
 * an option for user to select in SINGLE-SELECT and MULTI-SELECT Clinical Questions
 */
export interface ResponseOption {
  responseOptionText?: string;
  observationCodes?: ObservationCode[];
  optionId?: string;
  externalAssessmentProperties?: {
    [key: string]: string;
  };
}

/**
 * a code to tag a clinical observation (measurement or finding)
 */
export type ObservationCode = SemanticObservationCode;

/**
 * A link used to reference material relevant to a clinical question
 */
export interface ClinicalQuestionLink {
  label?: string;
  url?: string;
}

/**
 * A tag used to label objects such as providers
 */
export type Tag = StampedObject &
  PromotableObject & {
    id?: string;
    name?: string;
    description?: string;
    mappingsCount?: number;
    startDate?: string;
    endDate?: string;
    procedureCodes?: string[];
    providerTagMappings?: ProviderTagMapping[];
  };

/**
 * An association of a tag and an NPI or TIN
 */
export interface ProviderTagMapping {
  id?: string;
  tagId?: string;
  npi?: string;
  tin?: string;
}

/**
 * Standard error response format
 */
export interface ErrorResponse {
  /**
   * Human-readable error message
   */
  message: string;
  /**
   * Additional error details (optional)
   */
  errors?: string[] | null;
}

export interface ServiceRequestUpdateErrorResponse {
  data?: {
    message?: string;
  };
  message?: string;
}

export interface PALCategory {
  id: string;
  name: string;
  description?: string;
}

export interface PlaceOfService {
  id: string;
  code: string;
  name: string;
  description?: string;
  encounterTypes?: ("INPATIENT" | "OUTPATIENT")[];
}

export interface UpsertPlaceholderPatientFormFields {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: Address;
  coverage?: Coverage;
  coverages?: Coverage[];
}

/**
 * A slimmed down Clinical Service with name, id, and procedure codes
 */
export interface ClinicalServiceSlim {
  clinicalServiceId?: string;
  clinicalServiceName?: string;
  procedureCodes?: string[];
}

/**
 * A clinical Service
 */
export type ClinicalService = HealthPlanAccessedObject &
  VersionedObject &
  PromotableObject &
  StampedObject & {
    id?: string;
    name?: string;
    description?: string;
    defaultSemanticProcedureCodes?: ProcedureCode[];
    internalDescription?: string;
    isSingleService?: boolean;
    carePathId?: string;
    carePathName?: string;
    serviceCategoryName?: string;
    procedureCodes?: ProcedureCode[];
    semanticProcedureCodes?: ProcedureCode[];
    defaultPlaceOfService?: PlaceOfService;
    allowedPlacesOfServiceIds?: string[];
    allowedPlacesOfService?: PlaceOfService[];
    defaultAuthType?: "ONETIME" | "RECURRING" | "NONE";
    defaultUnitType?: UnitType;
    defaultUnits?: number;
    defaultDuration?: number;
    requireAttachments?: boolean;
    typeOfAttachments?: string;
    allowAutoAuthorization?: boolean;
    episodicAuthConfigs?: EpisodicAuthConfig[];
    clinicalQuestions?: ClinicalQuestion[];
    clinicalQuestionIds?: string[];
    currentDraft?: ClinicalService;
    serviceTypes?: OscarServiceType[];
    caseType?: OscarCaseType;
    pxRequiredToSubmit?: boolean;
    allowedHealthPlans?: HealthPlan[];
    specialty?: string;
    isUnitsOnPx?: boolean;
    continuationEnabled?: boolean;
    hasUnitsOnPxChanged?: boolean;
    authCategory?: AuthCategory;
    authSubCategory?: string;
    bodyArea?: string[];
    linesOfBusiness?: LineOfBusiness[];
    encounterTypes?: FacilityCategory[];
  };

export type ClinicalServiceUpdatePayload = PromotableObject & {
  id?: string;
  name?: string;
  published?: boolean;
  description?: string;
  internalDescription?: string;
  defaultSemanticProcedureCodes?: ProcedureCode[];
  semanticProcedureCodes?: ProcedureCode[];
  defaultPlaceOfService?: string;
  allowedPlacesOfServiceIds?: string[];
  defaultAuthType?: "ONETIME" | "RECURRING" | "NONE";
  defaultUnitType?: UnitType;
  defaultUnits?: number;
  defaultDuration?: number;
  requireAttachments?: boolean;
  typeOfAttachments?: string;
  allowAutoAuthorization?: boolean;
  allowedHealthPlanIds?: string[];
  pxRequiredToSubmit?: boolean;
  serviceTypes?: OscarServiceType[];
  caseType?: OscarCaseType | null;
  clinicalQuestionIds?: string[];
  specialty?: string | null;
  isUnitsOnPx?: boolean;
  continuationEnabled?: boolean;
  authCategory?: AuthCategory | null;
  authSubCategory?: string | null;
  episodicAuthConfigs?: EpisodicAuthConfig[];
  bodyArea?: string[];
  linesOfBusiness?: LineOfBusiness[];
  encounterTypes?: FacilityCategory[];
};

/**
 * Contains either options for clarifying or bucketed services
 */
export interface ServiceDeterminationResponse {
  carePathOptions?: ServiceDeterminationQuestionOption[];
  categoryOptions?: ServiceDeterminationQuestionOption[];
  serviceOptions?: ServiceDeterminationQuestionOption[];
  serviceTypeOptions?: OscarServiceType[];
  placeOfServiceOptions?: ServiceDeterminationQuestionOption[];
  defaultPlaceOfService?: PlaceOfService;
  buckets?: ServiceDeterminationProcedureCodeBucket[];
  procedureCodeGroupOptions?: ProcedureCodeGroupOptions[];
}

/**
 * List of options for clarifying service determination questions
 */
export interface ServiceDeterminationQuestionOption {
  id?: string;
  name?: string;
  description?: string;
  isSingleService?: boolean;
  encounterTypes?: FacilityCategory[];
}

/**
 * Procedure codes bucketed by clinical service
 */
export interface ServiceDeterminationProcedureCodeBucket {
  clinicalService?: ClinicalService;
  serviceType?: OscarServiceType;
  procedureCodes?: ProcedureCode[];
  placeOfService?: PlaceOfService;
  carePath?: CarePath;
}

/**
 * A reference to an id
 */
export interface HasId {
  id?: string;
  code?: string;
  name?: string;
  description?: string;
}

/**
 * Represents a facility, that is a place where providers work
 */
export type Facility = StandardFields &
  PracticeCommon & {
    isPar?: boolean;
    preferredFacility?: boolean;
  };

/**
 * Represents a facility, that is a place where providers work
 */
export type AdditionalCareParticipant = StandardFields &
  PracticeCommon &
  TaggedEntity & {
    fieldConfigurations?: {
      fieldName?: string;
      fieldSpecification?: FormConfigurationFieldSpecification;
    }[];
    careParticipantType?: CareParticipantType;
    configuredEntityType?: EntityType;
  };

export type CareParticipantType =
  | "DRUG_SUPPLIER"
  | "DME_VENDOR"
  | "CLINIC"
  | "PROVIDER_GROUP";

export type EntityType = "PROVIDER" | "FACILITY" | "BOTH";

export interface FacilityProviderMedicareIdentifier {
  hasMedicareIdentifier?: boolean;
  medicareIdentifiedProviderType?: "PTAN" | "CCN";
  providerIdentifierNumber?: string;
}

export type PracticeCommon = {
  npi?: string;
  providerFacilityId?: string;
  name?: string;
  addresses?: Address[];
  phoneNumbers?: PhoneNumber[];
  tinList?: string[];
  manuallyCreated?: boolean;
  createdByUser?: boolean;
  ehrReferences?: EHRReferenceInfo[];
  locations?: Location[];
  source?: string;
  providerType?: string;
  selectedLocation?: Location;
  type?: string;
  taxonomyCode?: string;
  cohereCategory?: string;
  cohereSpecialty?: string;
  cohereSubspecialty?: string;
  cohereSpecialtySource?: string;
  healthPlanName?: string;
  performingProviderPTAN?: string;
  orderingProviderPTAN?: string;
  facilityProviderMedicareIdentifier?: FacilityProviderMedicareIdentifier;
};

/**
 * A combination of tin and address that is used for out of network checks
 */
export type Location = {
  id?: string;
  segments?: string[];
  tin?: string;
  address?: Address;
  networks?: string[];
  phoneNumbers?: PhoneNumber[];
  faxNumbers?: string[];
  verifiedFacilityFaxNumber?: string;
  isOutOfNetwork?: boolean;
  outOfNetworkExceptionReason?: string;
  outOfNetworkExceptionComment?: string;
  externalReferenceId?: string;
  manuallyEditedAddress?: boolean;
  name?: string;
  providerType?: string;
  providerSubType?: string;
  firstName?: string;
  lastName?: string;
  networkType?: NetworkType;
  areaType?: AreaType;
  payerSpecificData?: { [key: string]: any };
  payerCustomFields?: CustomField[];
};

export type NetworkType = "INN" | "OON" | "OOA" | "Unknown";

export type AreaType = "INA" | "OOA" | "Unknown";

export type PracticeCreatePayload = PracticeCommon;

export type PracticeUpdatePayload = PracticeCommon;

/**
 * A patient's insurance coverage info
 */
export interface Coverage {
  isAso?: boolean;
  benefitPackageNumber?: string;
  customerOrganizationName?: string;
  dependentCode?: string;
  financialProductCode?: string;
  grouperId?: string;
  groupId?: string;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
  legalEntity?: string;
  lineOfBusiness?: string;
  lineOfBusinessDescription?: string;
  baseMarketId?: string;
  riskBearingEntity?: string;
  market?: string;
  marketNumber?: string;
  networkId?: string;
  planType?: string;
  planId?: string;
  planStart?: string;
  planEnd?: string;
  platformCode?: string;
  productId?: string;
  relationshipDescription?: string;
  sourceSystem?: string;
  stateOfIssue?: string;
  subGroupId?: string;
  subscriberId?: string;
  primaryCareProvider?: PrimaryCareProvider;
  excluded?: boolean;
  exclusionDescription?: string;
  coverageExclusionDescription?: string;
  planActive?: boolean;
  lineOfBusinessType?: string;
  coverageLineOfBusinessType?: string;
  productType?: string;
  coverageProductType?: string;
  isCommercial?: boolean;
  isMedicare?: boolean;
  isMedicaid?: boolean;
  memberId?: string;
  medicareContract?: string;
  medicarePBP?: string;
  payerCustomFields?: CustomField[];
}

/**
 * Whether a coverage is active
 */
export interface IsCoverageActive {
  active?: boolean;
}

/**
 * The primary care provider for a coverage
 */
export interface PrimaryCareProvider {
  address?: Address;
  name?: string;
  firstName?: string;
  lastName?: string;
  npi?: string;
  grouperId?: string;
  effectiveDate?: string;
}

/**
 * An entity that can receive services
 */
export type Patient = StandardFields & {
  memberId?: string;
  name: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender: Gender;
  dateOfBirth: string;
  ssn?: string;
  address?: Address;
  mailingAddress?: Address;
  phones?: PhoneNumber[];
  insurer?: string;
  languagePreference?: LanguagePreference;
  /**
   * The primary coverage
   */
  coverage?: Coverage;
  /**
   * All coverages (pended, active (including primary), and future)
   */
  coverages?: Coverage[];
  ruleActions?: RuleActions;
  ehrReferences?: EHRReferenceInfo[];
  /**
   * All allowed service request types this patient can submit
   */
  allowedServiceRequestTypes?: string[];
  associatedPatient?: {
    patientId?: string;
    memberId?: string;
  };
  associatedTemporaryPatientIds?: AssociatedTemporaryPatient[];
  placeHolderMember?: boolean;
  replacedByMemberId?: string;
  replacesMemberId?: string;
  replacedByPatientId?: string;
  referralEligibility?: ReferralEligibility;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
  riskBearingEntity?: string;
  primaryCoverageLineOfBusiness?: string;
};

export interface PatientAccess {
  organization?: {
    id?: string;
  };
  patient?: {
    id?: string;
  };
}

export interface LanguagePreference {
  written?: string;
  spoken?: string;
}

/**
 * Episodic auth specific date filters
 */
export interface EpisodicAuthDateFilter {
  numberOfDays?: number;
  operator?: string;
  anchor?: string;
}

/**
 * Represents a patient with the rule actions
 */
export type PatientWithRules = Patient & RuleActions;

/**
 * Represents a patient with referral eligibility
 */
export type PatientWithReferralEligibility = Patient & {
  referralEligibility?: ReferralEligibility;
};

export interface HTMLResult {
  htmlResult?: string;
}

export type SearchableStatements = {
  serviceRequestPatientAttributes?: string[];
}[];

export interface CohereSequence {
  sequenceName?: string;
  sequenceValue?: string;
}

export type RuleActions = (
  | {
      ruleId?: string;
      actionType: "APPROVE";
      ruleName?: string;
      srId?: string;
    }
  | {
      ruleId?: string;
      actionType: "PENDING";
      ruleName?: string;
      srId?: string;
    }
  | {
      ruleId?: string;
      actionType: "SetValue";
      ruleName?: string;
      objectAttribute?: string;
      objectValue?: {
        value?: Unknown;
      };
      srId?: string;
    }
  | {
      ruleId?: string;
      actionType: "DISPLAY";
      ruleName?: string;
      message: string;
      srId?: string;
    }
  | RedirectRuleAction
  | RecommendChangeRuleAction
)[];

export interface RuleActionCommon {
  executed?: boolean;
  ruleId?: string;
  ruleName?: string;
  actionId?: string;
  groupId?: string;
}

export type SetValueRuleAction = RuleActionCommon & {
  type?: "SetValue";
  actionType?: "SetValue";
  objectAttribute?: string;
  objectValue?: {
    value?: Unknown;
  };
};

export type RedirectRuleAction = RuleActionCommon &
  WithAcceptAction & {
    type?: "Redirect";
    actionType?: "Redirect";
    title?: string;
    message: string;
    isOptional?: boolean;
    srId?: string;
  };

export type RecommendChangeRuleAction = RuleActionCommon &
  WithAcceptAction & {
    type?: "RecommendChange";
    actionType?: "RecommendChange";
    title?: string;
    message?: string;
    guidelineUrl?: string;
    outreachAttempts?: {
      subject?: string;
      message?: string;
      channel?: "FAX" | "SET_AUTH_STATUS";
      attemptAuthStatus?: AuthStatus;
      scheduleSendTemporalAnchor?: "NOW" | "TAT_EXPIRATION";
      /**
       * IS08601 formatted temporal duration (only date part)
       */
      scheduleSendOffset?: string;
      customFaxRecipientPhone?: PhoneNumber;
      customFaxRecipientName?: string;
      recipients?: Recipient[];
      bodyPageText?: string;
      sendServiceSummary?: boolean;
    }[];
    srId?: string;
    collectDeclineReason?: boolean;
    declineReasonRequired?: boolean;
  };

export interface WithAcceptAction {
  displayTarget?: DisplayTargetType;
  onAcceptAction?: OnAcceptActions;
  onAcceptAttribute?: string;
  onAcceptValue?: {
    value?: Unknown;
    reference?: string;
  };
  reviewTypeTargets?: ReviewTypeTarget[];
  nudgeType?: string;
}

export type ReviewTypeTarget = "NurseReview" | "MdReview" | "PeerToPeerReview";

export type ServiceRequestRuleRunDescription = StampedObject & {
  id?: string;
  ruleRunTiming?: RuleRunTiming;
  serviceRequest?: {
    id?: string;
  };
  results?: RuleRunResult[];
  dataModel?: ServiceRequestRuleDataModel;
  groupedResults?: GroupedResult[];
  ruleSetStatusInfo?: ServiceRequestRuleSetStatusInfo;
};

export interface GroupedResult {
  groupId?: string;
  groupResults?: RuleRunResult[];
  groupResultsOutcome?: boolean;
  stringifiedPxCodeList?: string;
  clinicalServiceName?: string;
}

export type ServiceRequestRuleRunDescriptions =
  ServiceRequestRuleRunDescription[];

export type RuleRunSimulation = StampedObject & {
  ruleId: string;
  startDate: string;
  endDate: string;
  max: number;
  serviceRequestCount: number;
  resultIsReady: boolean;
  source?: string;
  result?: RuleRunSimulationResult;
};

export interface RuleRunSimulationResult {
  autoApproval?: RuleRunSimulationCompare;
  pendThenApproval?: RuleRunSimulationCompare;
  pendThenDenial?: RuleRunSimulationCompare;
  pendThenWithdrawn?: RuleRunSimulationCompare;
  pendThenPartialApproved?: RuleRunSimulationCompare;
  pendThenVoided?: RuleRunSimulationCompare;
  pendOther?: RuleRunSimulationCompare;
}

export interface RuleRunSimulationCompare {
  baseline?: number;
  newResult?: number;
  delta?: number;
  deltaPercent?: number;
}

export interface RuleRunResult {
  rule?: {
    id?: string;
  };
  outcome?: boolean;
  skipped?: boolean;
  evaluationFailed?: boolean;
  ruleLabelPriority?: number;
  ruleBody?: ServiceRequestRule;
  score?: number;
  relativeImportance?: number;
  ruleAssociatedPolicyType?: GuidelineType;
}

export interface ServiceRequestRuleSetStatusInfo {
  ruleId?: string;
  ruleTitle?: string;
  authStatusToSetTo?: string;
}

export interface ServiceRequestRuleDataModel {
  patient?: {
    gender?: Gender;
  };
  attachments?: number;
  countOfClinicalServices?: number;
  additionalInsurance?: string;
  clinicalServiceNames?: string[];
  performingProviderTags?: string[];
  orderingProviderTags?: string[];
  placeOfServiceName?: string[];
  serviceRequestProcedureCodes?: string[];
  serviceRequestDiagnosisCodes?: string[];
  units?: {
    numberOfUnits?: number;
  };
  authType?: string;
  patientGender?: string;
  patientId?: string;
  patientAge?: number;
  serviceRequestPatientAttributes?: string[][];
  serviceRequestPatientMeasurements?: { [key: string]: any };
  clinicalService?: {
    id?: string;
  };
  serviceRequestProcedureCodesFull?: {
    groupId?: string;
    code?: string;
  }[];
}

export type Gender = string;

/**
 * Represents a provider, that is an entity that can provide services
 */
export type Provider = StandardFields & PracticeCommon & TaggedEntity;

/**
 * New way of representing a provider
 */
export type ProviderFacilityCatalogue = StandardFields & {
  id?: string;
  npi?: string;
  tinList?: string[];
  locations?: Location[];
  source?: string;
  healthPlanName?: string;
  recordInsertTimeStamp?: string;
  recordType?: "PROVIDER" | "FACILITY";
  manuallyCreated?: boolean;
  taxonomyCode?: string;
  cohereCategory?: string;
  cohereSpecialty?: string;
  cohereSubspecialty?: string;
  cohereSpecialtySource?: string;
};

export interface ManualProviderFacilityPayload {
  id?: string;
  npi: string;
  tinList: string[];
  locations: Location[];
  recordType: "PROVIDER" | "FACILITY";
  healthPlanName?: string;
}

export interface TransientServiceRequest {
  id?: string;
  /**
   * Unique identifier created to correlate service request to transient rule runs and select variable actions
   */
  workflowId?: string;
  clinicalService?: ClinicalService;
  semanticProcedureCodes?: ProcedureCode[];
  encounterType?: FacilityCategory;
  placeOfService?: string;
  startDate?: string;
  urgency?: Urgency;
  patient?: Patient;
  orderingProvider?: Provider;
  attachments?: Attachment[];
  primaryDiagnosis?: DiagnosisCode;
  primarySemanticDiagnosisCode?: DiagnosisCode;
  secondaryDiagnoses?: DiagnosisCode[];
  secondarySemanticDiagnosisCodes?: DiagnosisCode[];
  admissionDate?: string;
  dischargeDate?: string;
  patientStayDaysRequested?: number;
  isAdvanceNotification?: boolean;
  contractType?: string;
}

export interface ServiceRequestCommon {
  /**
   * Unique identifier created to correlate service request to transient rule runs and select variable actions
   */
  workflowId?: string;
  cohereId?: string;
  crdLogId?: string;
  authNumber?: string;
  uniqueReferenceId?: string;
  manualAuthNumberEntry?: boolean;
  authStatus?: AuthStatus;
  requestor?: Requestor;
  encounterType?: FacilityCategory;
  recurrenceType?: "ONETIME" | "RECURRING";
  startDate?: string;
  endDate?: string;
  admissionDate?: string;
  admissionTime?: string;
  expectedAdmissionDate?: string;
  authSubcategory?: string;
  patientStatus?: PatientStatus;
  areClinicalQuestionsSkipped?: boolean;
  expectedDischargeDate?: string;
  dischargeDate?: string;
  dischargeTime?: string;
  behavioralHealthReviewType?: BehavioralHealthReviewType;
  behavioralHealthAdmissionType?: BehavioralHealthAdmissionType;
  serviceType?: OscarServiceType;
  facilitySelectedTin?: string;
  performingProviderSelectedTin?: string;
  orderingProviderSelectedTin?: string;
  units?: number;
  approvedUnits?: number;
  isPal?: boolean;
  unitType?: UnitType;
  urgency?: Urgency;
  nudgeRangeofOptionsMetaData?: NudgeRangeofOptionsMetaData;
  participateCheck?: ParticipateCheck;
  pendingReason?: PendingReason;
  authorizationNote?: string;
  withdrawnReason?: string;
  isSrNoLongerNeeded?: boolean;
  withdrawRequestor?: WithdrawRequestor;
  isAdvanceCoverageDetermination?: boolean;
  createdByName?: string;
  createdByPhoneNumber?: string;
  createdByGroups?: string[];
  followUpFaxNumber?: PhoneNumber;
  semanticProcedureCodes?: ProcedureCode[];
  approvedSemanticProcedureCodes?: ProcedureCode[];
  approvedProcedureCodes?: ProcedureCode[];
  primarySemanticDiagnosisCode?: DiagnosisCode;
  secondarySemanticDiagnosisCodes?: DiagnosisCode[];
  caseId?: string;
  dateCreated?: string;
  ehrEncounterId?: string;
  ehrPatientId?: string;
  ehrDepartmentId?: string;
  ehrPracticeId?: string;
  ehrOrderId?: string;
  ehrVendor?: EhrVendor;
  type?: string;
  authDecisionGroup?: {
    id?: string;
    authNumber?: string;
    serviceRequestIds?: string[];
  };
  possibleAttachmentNudgeReasons?: PossibleAttachmentNudgeReasons[];
  workflowStep?: AuthBuilderWorkflowStep;
  facilityLocation?: Location | null;
  performingProviderLocation?: Location | null;
  orderingProviderLocation?: Location | null;
  requestType?: RequestType;
  /**
   * ID of the original service request that this resubmission is based on. Only populated for WISeR auths when this is a resubmission.
   */
  resubmissionOf?: string;
  requestTiming?: RequestTiming;
  authId?: string;
  isEscalated?: boolean;
  authorizationType?: AuthorizationType;
  patientStayDates?: PatientStayDate[];
  authorization?: {
    id?: string;
  };
  userSelectedOONException?: boolean;
  userSelectedNonPalCode?: boolean;
  claimsPaymentNote?: string;
  serviceStatusCode?: string;
  selectedOrderingProvider?: Provider | null;
  selectedPerformingProvider?: Provider | null;
  selectedPerformingProviderPractice?: Facility | null;
  selectedFacility?: Facility | null;
  additionalCareParticipants?: AdditionalCareParticipant[] | null;
  identifiers?: Identifier[];
  additionalInsurance?: AdditionalInsuranceRequest;
  userUpdatedTat?: boolean | null;
  editedPreferredLabels?: EditedPreferredLabel[];
  nonPxClinicalServiceIds?: string[];
  externalReferenceId?: string;
  externalReferenceIds?: string[];
  sourceSystem?: string;
  episodeGeneratedFromServiceRequest?: {
    id?: string;
  };
  additionalRecipients?: string[];
  dismissalReason?: string;
  contractType?: string;
  serviceRequestContactInfo?: ServiceRequestContactInfo;
  workflow?: Workflow;
  /**
   * Reason for retroactive service request submission
   */
  retroRequestReason?: string;
  /**
   * Date the service was completed
   */
  completedServiceDate?: string;
  /**
   * Indicates if dual plan decision is required for this service request
   */
  requiresDualPlanDecision?: boolean;
  requestUnitType?: UnitType;
  clinicalServiceUnits?: ClinicalServiceUnits[];
  inpatientType?: ServiceRequestInpatientType;
  inpatientSubType?: ServiceRequestInpatientSubType;
}

export interface PossibleAttachmentNudgeReasons {
  ruleId?: string;
  reasonText?: string;
  reason?:
    | "DONT_KNOW_WHAT_INFO"
    | "INFO_NOT_SPECIFIC_ENOUGH"
    | "NO_ACCESS_TO_INFO"
    | "DONT_HAVE_INFO"
    | "OTHER";
}

export interface ServiceRequestCommandPayload {
  semanticProcedureCodes?: ProcedureCode[];
  endDate?: string;
  units?: number;
  urgency?: Urgency;
  authStatus?: AuthStatus;
  pendingReason?: PendingReason;
  requestTiming?: RequestTiming;
  requestType?: RequestType;
  /**
   * ID of the original service request that this resubmission is based on. Only populated for WISeR auths when this is a resubmission.
   */
  resubmissionOf?: string;
  encounterType?: FacilityCategory;
  workflowStep?: AuthBuilderWorkflowStep;
  lastDecisionedDate?: string;
  nextReviewDate?: string;
  requestedLevelOfCareCode?: string;
  patientStatus?: PatientStatus;
  patientStayDates?: PatientStayDate[];
  admissionDate?: string;
  admissionTime?: string;
  admissionSource?: AdmissionSource;
  dischargeDate?: string;
  dischargeTime?: string;
  isReadmission?: boolean;
  retroRequestReason?: string;
  completedServiceDate?: string;
  anchorAuthNumber?: string;
  readmissionReason?: "RELATED" | "COMPLICATION";
  continuationGeneratedFromServiceRequest?: {
    id?: string;
  };
}

export interface IdempotentContinuationPayload {
  continuationType?:
    | "AUTO_APPROVAL"
    | "POST_DECISION"
    | "FINAL_DECISION"
    | "DRAFT";
  requestor?: Requestor;
}

export type FormConfigurationFieldSpec = "OMIT" | "OPTIONAL" | "REQUIRED";

export interface PayerProcedureCode {
  procedureCode?: string;
  palCategory?: string;
}

export interface FromCPTCodesRequest {
  codes?: string[];
  patientId?: string;
  startDate?: string;
}

export interface PropagationResult {
  details?: string;
  status?: string;
  environment?: string;
  propagated?: { [key: string]: any };
}

export type PolicyStamp = StampedObject & {
  id?: string;
  policyHtml?: string;
  policyType?: GuidelineType;
  policyUrl?: string;
  internalDescription?: string;
  policyTitle?: string;
  linesOfBusiness?: LineOfBusiness[];
  facilityCategories?: FacilityCategory[];
  healthPlans?: string[];
  statesOfUnion?: UnitedStates[];
  policyEffectiveDate?: string;
  policyExpirationDate?: string;
  nextInternalReviewDate?: string;
  guidelines?: Guideline[];
  accessibleToCohereUsers?: boolean;
  createdByLambda?: boolean;
  productID?: ProductIdType[];
};

/**
 * Update payload for Policy
 */
export type PolicyPayload = PromotableObject & {
  id?: string;
  active?: boolean;
  policyType?: GuidelineType;
  policyUrl?: string;
  internalDescription?: string;
  policyTitle?: string;
  linesOfBusiness?: LineOfBusiness[];
  facilityCategories?: FacilityCategory[];
  healthPlans?: string[];
  statesOfUnion?: UnitedStates[];
  policyEffectiveDate?: string;
  policyExpirationDate?: string;
  nextInternalReviewDate?: string;
  guidelineIds?: string[];
  guidelines?: Guideline[];
  filterIds?: string[];
  filters?: ConveneFilter[];
};

/**
 * Hold external partner information like external id and product
 */
export interface ExternalPartnerInfo {
  externalPartnerId?: string;
  product?: string;
  edition?: string;
  guidelineCode?: string;
  externalUrl?: string;
}

export type Guideline = VersionedObject &
  StampedObject &
  PromotableObject & {
    id?: string;
    active?: boolean;
    guidelineHtml?: string;
    internalDescription?: string;
    policyHtmlIndices?: number[];
    guidelineTitle?: string;
    policyStampId?: string;
    indications?: Indication[];
    linesOfBusiness?: LineOfBusiness[];
    facilityCategories?: FacilityCategory[];
    statesOfUnion?: UnitedStates[];
    annotatedTextList?: AnnotatedText[];
    blocks?: GuidelineBlock[];
    filterIds?: string[];
    filters?: ConveneFilter[];
    accessibleToCohereUsers?: boolean;
    policyId?: string;
    policyTitle?: string;
    policyType?: GuidelineType | null;
    effectiveDate?: string;
    expirationDate?: string;
    nextInternalReviewDate?: string;
    procedureCodes?: ProcedureCode[];
    diagnosisCodes?: DiagnosisCode[];
    healthPlans?: string[];
    documentVersion?: string;
    currentDraft?: Guideline;
    filterGroups?: FilterGroup[];
    externalPartnerInfo?: ExternalPartnerInfo;
    deleted?: boolean;
    deletionReason?: string;
    fromPolicyManagement?: boolean;
  };

export interface GuidelineBlock {
  text?: string;
  startIndex?: number;
  endIndex?: number;
  className?: string;
}

export interface Indication {
  uid?: string;
  guidelineHtmlIndices?: number[];
  indicationHtml?: string;
  indications?: Indication[];
  logicalOperator?: string;
  logicalOperatorValue?: number;
  indicationGroup?: string | null;
}

export interface IndicationElasticIndex {
  indicationId?: string;
  originalGuidelineId?: string;
  guidelineId?: string;
  indicationSearchText?: string;
  indicationHtml?: string;
  indicationGroup?: string | null;
  immediateParentIndicationId?: string;
  immediateParentIndicationSearchText?: string;
  lastUpdated?: string;
  isLeafNode?: boolean;
}

export interface Requestor {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  faxNumber?: PhoneNumber;
  channel?: RequestorChannel;
  requestorType?: RequestorType;
  user?: User;
  intakeTimestamp?: string;
  tatStartTimestamp?: string;
  aorReceivedTimestamp?: string;
  aorAddress?: Address;
  isCareRecommendationRequest?: boolean;
  isFormSubmittedWithoutUserInput?: boolean;
}

export type RequestorChannel =
  | "FAX"
  | "PHONE"
  | "EMAIL"
  | "MAIL"
  | "PORTAL"
  | "PORTAL_EHR"
  | "INTERNAL_API_REQUEST"
  | "CHAT"
  | "EMR"
  | "ESMD";

export type RequestorType =
  | "PROVIDER"
  | "ORDERING_PROVIDER"
  | "PERFORMING_PROVIDER"
  | "PERFORMING_FACILITY"
  | "MEMBER"
  | "MEMBER_REPRESENTATIVE"
  | "THIRD_PARTY";

export interface ServiceRequestExpansion {
  patient?: Patient;
  authorization?: AuthorizationResponse;
  appealIds?: string[];
  carePathJourney?: CarePathJourney;
  clinicalService?: ClinicalService;
  /**
   * contains the list of all clinical services from list of SemanticProcedureCode.groupID where SemanticProcedureCode.groupBy = CLINICAL_SERVICE
   */
  clinicalServices?: (ClinicalService | null)[];
  palCategory?: PALCategory;
  primaryDiagnosis?: DiagnosisCode;
  secondaryDiagnoses?: DiagnosisCode[];
  procedureCodes?: ProcedureCode[];
  approvedProcedureCodes?: ProcedureCode[];
  placeOfService?: PlaceOfService;
  facility?: Facility;
  orderingProvider?: Provider;
  performingProvider?: Provider;
  attachments?: Attachment[];
  intakeTimestamp?: string;
  delegatedVendor?: Vendor;
  paCaseId?: string;
  prescribedDrug?: Drug;
  palCheck?: PalCheckResponse;
}

export type AttachmentType =
  | "Current medication list"
  | "Clinical note"
  | "Diagnostic image"
  | "Diagnostic image report"
  | "Discharge plan"
  | "History and physical"
  | "Lab"
  | "Physician order"
  | "Progress note"
  | "RAD documentation"
  | "Treatment plan"
  | "Therapy note"
  | "Other";

export interface AttachmentCommon {
  name?: string;
  size?: number;
  type?: AttachmentType;
  integrationStatus?: string;
  practiceViewable?: boolean;
  hasMultiSingleServices?: boolean;
  contentType?: string;
  rotation?: number[];
  url?: string;
  attachmentGroupId?: string;
  usesCohereTemplate?: string;
  serviceRequest?: {
    id?: string;
    clinicalService?: {
      name?: string;
    };
  };
  carePathJourney?: {
    id?: string;
  };
  referralRequest?: {
    id?: string;
  };
  advanceNotification?: {
    id?: string;
  };
  attachmentSource?: string;
  attachmentVerificationMatchingChecks?: FindingRecord;
}

export type AttachmentCreatePayload = AttachmentCommon;

export type AttachmentWithUploadLink = StandardFields & AttachmentCommon;

export type RecipientType =
  | "PATIENT"
  | "PROVIDER"
  | "ORDERING_PROVIDER"
  | "PERFORMING_PROVIDER"
  | "PERFORMING_FACILITY"
  | "PCP"
  | "REQUESTOR"
  | "MEMBER_REPRESENTATIVE"
  | "REFERRING_PROVIDER"
  | "PERFORMING_SPECIALIST";

export type FinalDeterminationLetterAttachment = Attachment & {
  recipientType?: RecipientType;
  languageCode?: string;
  deliveryInfo?: LetterDeliveryInfo;
  notificationId?: string;
};

export type InternalFinalDeterminationLetterAttachment = AttachmentCommon &
  StandardFields & {
    healthPlanName?: string;
    letterAddressee?: LetterAddressee;
  };

export type InternalFinalDeterminationFaxAttachment = AttachmentCommon &
  StandardFields & {
    healthPlanName?: string;
    letterAddressee?: LetterAddressee;
    letterName?: string;
    reviewId?: string;
    addresseeName?: string;
    vendorFileName?: string;
    dateRequested?: string;
    zipDirectory?: string;
  };

export type TatExtensionLetterAttachment = AttachmentCommon &
  StandardFields & {
    healthPlanName?: string;
    letterAddressee?: LetterAddressee;
  };

export type LetterAddressee =
  | "PATIENT"
  | "PROVIDER"
  | "PCP"
  | "REQUESTOR"
  | "PERFORMING_PROVIDER"
  | "PERFORMING_FACILITY"
  | "MEMBER_REPRESENTATIVE";

export type FaxAttachment = Attachment & {
  healthPlanName?: string;
  createdByName?: string;
  createdByFaxNumber?: string;
  cohereId?: string;
  faxReceived?: string;
  attachedTo?: AttachedTo[];
};

export interface AttachedTo {
  id?: string;
  type?: string;
  linkedAttachment?: string;
  cohereId?: string;
}

export type Attachment = StandardFields &
  AttachmentCommon & {
    createdByName?: string;
    linkedFaxAttachment?: FaxAttachment;
    attachmentGroupId?: string;
  };

export interface AttachmentUpdatePayload {
  type?: AttachmentType;
  status?: AttachmentStatus;
  practiceViewable?: boolean;
  rotation?: number[];
}

export interface AttachmentOcrPages {
  attachmentId?: string;
  attachmentName?: string;
  pagesInfo?: AttachmentOcrPageInfo[];
}

export interface AttachmentOcrPageInfo {
  page_num?: number;
  page_width?: number;
  page_height?: number;
}

export interface AttachmentOcrTokens {
  attachmentId?: string;
  attachmentName?: string;
  ocrTokens?: AttachmentOcrToken[];
}

export interface AttachmentOcrToken {
  page_num?: number;
  block_num?: number;
  par_num?: number;
  line_num?: number;
  word_num?: number;
  left?: number;
  top?: number;
  width?: number;
  page_width?: number;
  page_height?: number;
  height?: number;
  text?: string;
  conf?: number;
}

export type AttachmentStatus = "IN_PROGRESS" | "COMPLETE" | "ERROR_UPLOADING";

export type DocumentType =
  | "Progress Note"
  | "Clinical Note"
  | "Therapy Note"
  | "Consult"
  | "Discharge Summary"
  | "ED Notes"
  | "H&P"
  | "Diagnostic Image/Imaging"
  | "Criteria Summary"
  | "Lab Report"
  | "Other";

export interface Segment {
  docDate?: string;
  docType?: DocumentType;
  docPages?: [number, number];
}

export interface FeedbackSegment {
  createdBy?: string;
  dateCreated?: string;
  segments?: Segment[];
}

export type DocSegmentation = StampedObject & {
  id: string;
  attachmentId: string;
  active?: boolean;
  serviceRequestId?: string;
  carePathJourneyId?: string;
  originalSegments?: Segment[];
  feedbackSegments?: FeedbackSegment[];
};

export type AttachmentGuidelineTextHighlight = StampedObject & {
  id?: string;
  attachmentId: string;
  serviceRequestId: string;
  guidelineId: string;
  reviewId?: string;
  authorType?: "ML_PREDICTION" | "USER_FEEDBACK";
  modelVersion?: number;
  matches?: Match[];
  generalStatus?: "DRAFT" | "COMPLETE";
};

export interface Match {
  attachmentSpan?: AttachmentSpan;
  indicationUid: string;
  text: string;
  matchCreator?: string;
  status: IndicationMatchStatus;
  highlightAreas?: HighlightArea[];
  editText?: string;
  editStartPage?: number;
  editEndPage?: number;
  isCited?: boolean;
}

/**
 * link status:
 *   REMOVED - used if a highlight is no longer linked to any indication
 *   UNLINKED - used if a highlight was unlinked from the corresponding indicationUid in IndicationMatch
 *   MISTAKE - used for new highlights added and removed by same user, won't make it to the backend
 *
 */
export type IndicationMatchStatus =
  | "ADDED"
  | "LINKED"
  | "REMOVED"
  | "UNLINKED"
  | "PRESERVED"
  | "MISTAKE";

export interface AttachmentSpan {
  start?: AttachmentCoordinate;
  end?: AttachmentCoordinate;
  confidence?: number;
}

export interface AttachmentCoordinate {
  page?: number;
  block?: number;
  paragraph?: number;
  line?: number;
  word?: number;
  wordText?: string;
  topCoordinate?: number;
  leftCoordinate?: number;
  height?: number;
  width?: number;
}

export interface HighlightArea {
  pageIndex: number;
  top: number;
  left: number;
  height: number;
  width: number;
}

export interface ServiceRequestFieldIds {
  patient?: string;
  carePathJourney?: string;
  clinicalService?: string;
  palCategory?: string;
  placeOfService?: string;
  orderingProvider?: string;
  performingProvider?: string;
  facility?: string;
  reconClaim?: string;
  authCategory?: AuthCategory | null;
  dischargedTo?: PatientDischargeStatus;
  admissionSource?: AdmissionSource;
  nextReviewDate?: string;
}

/**
 * Claim object
 */
export interface Claim {
  id?: string;
  status?: string;
  claimId?: number;
  serviceFromDate?: string;
  serviceToDate?: string;
  procedureCode?: string;
  hcpcsBaseCode1?: string;
  patientId?: string;
  patient?: Patient;
  serviceRequestId?: string;
}

/**
 * Base review object
 */
export type BaseReview = StandardFields & {
  serviceRequest?: {
    id?: string;
  };
  reviewStatus?: "DRAFT" | "COMPLETE" | "DISCARDED";
  dateCompleted?: string;
  completedBy?: string;
  reviewerDetails?: {
    reviewerId?: string;
    reviewerName?: string;
    title?: string;
    specialties?: string[];
  };
  createdByName?: string;
  updatedBy?: string;
  completedByName?: string;
  authorizationNote?: string;
  guidelines?: GuidelinePolicyMap[];
  withdrawnReason?: string;
  usedGuidelineIds?: string[];
  selectedGuidelineIds?: string[];
  wasUnlistedGuidelineUsed?: boolean;
  usedGuidelines?: Guideline[];
  withdrawRequestor?: WithdrawRequestor;
  healthPlanName: string;
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
  claimsPaymentNote?: string;
  isAudited?: boolean;
  patientStatus?: PatientStatus;
  patientStayDates?: PatientStayDate[];
  actualDischargeDateTime?: string;
  admissionDateTime?: string;
  admissionSource?: AdmissionSource;
  dischargedTo?: string;
  nextReviewDate?: string;
  /**
   * expected admission date
   */
  expectedAdmissionDate?: string;
  isReadmission?: boolean;
  anchorAuthNumber?: string;
  readmissionReason?: "RELATED" | "COMPLICATION";
  reviewerCreateContinuationAcknowledgement?: boolean;
  noClinicalInformationProvided?: boolean;
  additionalRecipients?: string[];
  draftOutreachAttempt?: OutreachAttempt | null;
  selectedDecisionExplanations?: GenericReason[];
  draftPatientStayDates?: PatientStayDate[];
  draftPatientStayDetails?: DraftPatientStayDetails | null;
  reviewChatbotMetadata?: ReviewChatbotMetadata | null;
  /**
   * Indicates if dual plan decision is required for this review
   */
  requiresDualPlanDecision?: boolean;
};

export type WithdrawRequestor =
  | "PATIENT"
  | "PROVIDER"
  | "MEMBER_REPRESENTATIVE";

export interface GuidelinePolicyMap {
  policyType: GuidelineType;
  guidelines?: Guideline[];
  guidelineIds?: string[];
  checkedIndications?: Indication[];
  unmetIndications?: Indication[];
  guidelineStates?: GuidelineIndicationStates[];
  guidelineMatchMap?: FilterMatchMap[];
}

export interface GuidelineIndicationStates {
  guidelineId?: string;
  indicationStates?: IndicationStates[];
}

export interface IndicationStates {
  indicationUid?: string;
  indicationState?: "UNDECIDED" | "MET" | "UNMET";
}

export interface AnnotatedText {
  text?: string;
  observationCode?: string;
  codeDescription?: string;
  startIndex?: number;
  endIndex?: number;
  htmlStartIndex?: number;
  htmlEndIndex?: number;
}

export interface FilterMatchMap {
  guidelineId?: string;
  stateMatch?: UnitedStates;
  facilityCategoriesMatch?: FacilityCategory;
  linesOfBusinessMatch?: LineOfBusiness;
  authCategoryMatch?: AuthCategory;
  procedureCodesMatch?: ProcedureCode[];
  diagnosisCodesMatch?: DiagnosisCode[];
  policyType?: GuidelineType;
  policyTitle?: string;
  policyUrl?: string;
  effectiveDate?: string;
  expirationDate?: string;
}

export type ReviewType =
  | NurseReview
  | OutOfNetworkReview
  | MdReview
  | PeerToPeerReview
  | AuditReview
  | NewHireAuditReview
  | OutOfScopeAuditReview
  | FaxAuditReview;

export interface ChangeRequestNote {
  /**
   * The unique identifier associated with this object
   */
  id?: string;
  /**
   * The ID of the serviceRequest which this ChangeRequestNote refers to
   */
  serviceRequestId: string;
  /**
   * Free form text which contains the requested changes to be made to this serviceRequest
   */
  text?: string;
  /**
   * The healthPlanName with which this ChangeRequestNote is associated
   */
  healthPlanName: string;
  lastUpdated?: string;
  updatedBy?: string;
  createdBy?: string;
  updatedByName?: string;
  createdByName?: string;
  changeToInpatient?: boolean;
  /**
   * List of attachments associated with this ChangeRequestNote
   */
  attachments?: {
    id?: string;
    name?: string;
  }[];
}

export interface ChangeRequestNoteCreateNoteRequest {
  text?: string;
  serviceRequestId?: string;
  healthPlanName?: string;
  changeToInpatient?: boolean;
  /**
   * List of attachments associated with this ChangeRequestNote
   */
  attachments?: {
    id?: string;
    name?: string;
  }[];
}

export interface CreateNoteRequest {
  /**
   * ID of the service request to create or retrieve a draft for
   */
  serviceRequestId: string;
  /**
   * Name of the health plan associated with the service request
   */
  healthPlanName: string;
  /**
   * Indicates whether the request is a change to inpatient status
   */
  changeToInpatient?: boolean;
  /**
   * Additional text or notes for the change request
   */
  text: string;
  /**
   * List of attachments associated with this ChangeRequestNote
   */
  attachments?: {
    id?: string;
    name?: string;
  }[];
}

export interface AutomatedReviewNoteAttribution {
  /**
   * Unique identifier for the attachment
   */
  attachmentId: string;
  /**
   * Start page of the note
   */
  pageStart: number;
}

export interface AutomatedReviewNoteFinding {
  /**
   * Text of the finding
   */
  text: string;
  attribution: AutomatedReviewNoteAttribution;
  highlights?: HighlightArea[];
}

export interface AutomatedReviewNoteNode {
  /**
   * Name of the finding
   */
  findingName: string;
  /**
   * Display name for the node
   */
  displayName: string;
  /**
   * List of findings within the node
   */
  nodes?: AutomatedReviewNoteFinding[];
  /**
   * Nested nodes under the current node
   */
  children?: AutomatedReviewNoteNode[];
}

export type AutomatedReviewNote = AutomatedReviewNoteNode[];

export type NurseReview = BaseReview & {
  reviewType: "NurseReview";
  criteriaDecision?: "CRITERIA_MET" | "CRITERIA_NOT_MET" | "AUTO_APPROVED";
  decisionReasoning?: string;
  automatedDecisionReasoning?: string;
  guidelineTypes?: GuidelineType[];
  guidelineLink?: string;
  approvedUnits?: number;
  approvedSemanticProcedureCodes?: ProcedureCode[];
  approvedProcedureCodes?: ProcedureCode[];
  criteriaUsedDescription?: string;
  labsDescription?: string;
  currentMedicationsDescription?: string;
  imagingResultsDescription?: string;
  nurseClinicalNote?: string;
  nudgeAttempted?: boolean;
  nurseReviewNudges?: NudgeInfo[];
  nudgeDescription?: string;
  reviewOutcome?: string;
  subspecialty?: Subspecialty;
  isClinicalPreprocessingReview?: boolean;
  denialReason?: DenialReason;
  denialLanguage?: string;
  denialsTemplate?: DenialsInfo | null;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  readmissionReason?: "RELATED" | "COMPLICATION";
  readmissionOutcome?: "OVERTURN" | "UPHOLD";
  additionalRecipients?: string[];
  coverageLevelDetails?: NurseReviewCoverageLevelDetails[];
  outOfNetworkReview?: OutOfNetworkReview;
  automatedReviewNote?: AutomatedReviewNote;
};

export interface NurseReviewCoverageLevelDetails {
  coverage?: Coverage;
  reviewOutcome?: string;
  criteriaDecision?: "CRITERIA_MET" | "CRITERIA_NOT_MET" | "AUTO_APPROVED";
  approvedUnits?: number;
  approvedSemanticProcedureCodes?: ProcedureCode[];
  denialReason?: DenialReason;
  denialLanguage?: string;
  denialsTemplate?: DenialsInfo | null;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  usedGuidelineIds?: string[];
  selectedGuidelineIds?: string[];
  selectedDecisionExplanations?: GenericReason[];
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
  claimsPaymentNote?: string;
}

export type NurseReviewSubmitPayload = NurseReview & {
  reviewOutcome: {};
  outOfNetworkReview?: {};
  semanticProcedureCodes?: ProcedureCode[];
};

/**
 * Out-of-network exception review
 */
export type OutOfNetworkReview = BaseReview & {
  reviewType: "OutOfNetworkReview";
  oonExceptionReasoning?: string;
  reviewOutcome?: ReviewOutcome;
  outcomeReason?: string;
  networkStatusOverride?: NetworkStatusOverride;
  medicalNecessityReviewId?: string;
};

export type OutOfNetworkReviewSubmitPayload = OutOfNetworkReview & {
  reviewOutcome: ReviewOutcome;
  claimsPaymentNote?: string;
  networkStatusOverride?: NetworkStatusOverride;
};

/**
 * Flags to override the original network status check for performing provider and facility.
 */
export interface NetworkStatusOverride {
  isOutOfNetworkPerformingProvider?: boolean;
  isOutOfNetworkFacility?: boolean;
}

export type AuditReview = BaseReview & {
  reviewType: "AuditReview";
  auditNotes?: string;
  auditOutcome?: AuditOutcome;
};

export type AuditReviewSubmitPayload = AuditReview & {
  auditOutcome: AuditOutcome;
};

export type NewHireAuditReviewSubmitPayload = NewHireAuditReview & {
  auditOutcome: AuditOutcome;
};

export type FaxAuditReviewSubmitPayload = FaxAuditReview & {
  auditOutcome: AuditOutcome;
};

export type OutOfScopeAuditReviewSubmitPayload = OutOfScopeAuditReview & {
  auditOutcome: AuditOutcome;
  reviewOutcome: "PENDING_RN_REVIEW" | "WITHDRAWN";
};

export interface NudgeInfo {
  nudgeType?: string;
  nudgeResultedInChange?: boolean;
  nudgeReachedCorrectContact?: boolean;
  nudgeDescription?: string;
}

export type Note = StampedObject & {
  id: string;
  serviceRequest: {
    id?: string;
  };
  healthPlanName: string;
  noteContent?: string;
  createdByName?: string;
  updatedByName?: string;
  noteType: NoteType;
  dSnpChCode?: string;
  dSnpMcoConfirmation?: string;
  dSnpMedicaidRecipientNumber?: string;
  hasSNFWaiver?: boolean;
  claimsPaymentNote?: string;
  dSnpMedicaidRecipientNumberOptional?: boolean;
  requestor?: string;
  coverageEndedBy?: string;
  appealRequestedOn?: string;
};

export type NoteType = string;

export interface NoteSubmitPayload {
  noteContent?: string;
  noteType: NoteType;
  dSnpChCode?: string;
  dSnpMcoConfirmation?: string;
  dSnpMedicaidRecipientNumber?: string;
  hasSNFWaiver?: boolean;
  requestor?: string;
  coverageEndedBy?: string;
  appealRequestedOn?: string;
  claimsPaymentNote?: string;
  nextReviewDate?: string;
  dSnpMedicaidRecipientNumberOptional?: boolean;
}

export interface ErrorMessage {
  status?: number;
  message?: string;
  error?: {
    code?: string;
    message?: string;
  };
}

export interface ErrorMessageEmbedded {
  data?: {
    message?: string;
    _embedded?: {
      errors?: {
        message?: string;
      }[];
    };
  };
}

export type NewHireAuditReview = BaseReview & {
  reviewType: "NewHireAuditReview";
  auditNotes?: string;
  auditOutcome?: AuditOutcome;
  auditedReviewContext?: AuditedReviewContext;
};

export type OutOfScopeAuditReview = BaseReview & {
  reviewType: "OutOfScopeAuditReview";
  reviewOutcome?: "PENDING_RN_REVIEW" | "WITHDRAWN";
  auditNotes?: string;
  auditOutcome?: AuditOutcome;
};

export type FaxAuditReview = BaseReview & {
  reviewType: "FaxAuditReview";
  auditNotes?: string;
  auditOutcome?: AuditOutcome;
};

export interface AuditedReviewContext {
  auditedReviewId: string;
  auditedReviewType?: string;
  auditedReviewerName: string;
  auditedReviewOutcome: ReviewOutcome;
  possibleReviewOutcomes: ReviewOutcome[];
}

export type AuditOutcome = "PASS" | "WITHDRAWN" | "FAIL";

export interface RedeterminationPayload {
  previousAuthStatus?: AuthStatus;
  nextAuthStatus?: AuthStatus;
  /**
   * Party requesting the appeal
   */
  requestorType?: string;
  /**
   * ServiceRequest appeal belongs to
   */
  serviceRequest?: {
    /**
     * Unique identifier of serviceRequest
     */
    id?: string;
  };
  /**
   * Health plan from the Service Request
   */
  healthPlanName?: string;
  /**
   * Delegated vendor from the Service Request
   */
  delegatedVendorName?: string;
  approvedUnits?: number;
  /**
   * Attachments uploaded to support the appeal
   */
  attachments?: Attachment[];
  /**
   * Medical procedure codes from the partially approved Service Request
   */
  approvedSemanticProcedureCodes?: ProcedureCode[];
  /**
   * Stay dates for partially approved inpatient Service Request
   */
  patientStayDates?: PatientStayDate[];
}

export type AppealPayload = Require<
  RedeterminationPayload & {
    /**
     * Unique human-readable ID
     */
    appealId?: string;
    /**
     * ID in external system
     */
    externalReferenceId?: string;
    /**
     * URL in external system, used for redirects
     */
    externalReferenceUrl?: string;
    /**
     * Whether we are initiating an appeal or logging a completed appeal
     */
    appealType?: "APPEAL_INITIATION" | "APPEAL_LOG";
    /**
     * Whether the appeal was made before or after the services were performed
     */
    appealTiming?: "PRE_SERVICE" | "POST_SERVICE";
    /**
     * Appeal status
     */
    appealStatus?: "IN_PROGRESS" | "COMPLETE";
    /**
     * Appeal decision (Required when appeal status is COMPLETE)
     */
    outcome?:
      | "APPROVED"
      | "DENIED"
      | "PARTIALLY_APPROVED"
      | "NOT_COMPLETED"
      | "WITHDRAWN";
    /**
     * Post appeal type
     */
    postAppealType?: "QIO" | "STANDARD";
    /**
     * Who ended the coverage of the patient
     */
    coverageEndedBy?: string;
    /**
     * Decision rationale, as HTML to preserve rich formatting
     */
    postAppealNote?: string;
    /**
     * Whether the appeal is expedited (pre-service only)
     */
    isExpedited?: boolean;
    /**
     * Manual means the appeal was created by a user, as opposed to a job
     */
    isManualAppeal?: boolean;
    appealChannel?: RequestorChannel;
    /**
     * Time when the appeal request was received by Cohere (UTC)
     */
    appealInitiationTimestamp?: string;
    /**
     * How long it took to complete the appeal, i.e. the time difference between initiation and completion
     */
    appealDecisionTimestamp?: string;
    /**
     * How long it took to complete the appeal, i.e. the time difference between initiation and completion
     */
    appealTurnAroundTimeInMilliseconds?: number;
    /**
     * The manually set date when the appeal was determined, used in the post-appeal log.
     */
    appealDeterminationDate?: string;
    /**
     * Small description of the case
     */
    caseDescription?: string;
    /**
     * userId of user who completed appeal
     */
    appealDecisionedBy?: string;
    /**
     * Confirmation from user that required files were uploaded
     */
    attestationOfRequiredDocs?: boolean;
  },
  "appealStatus"
>;

export type AppealResponse = Require<
  StandardFields &
    AppealPayload & {
      createdByName?: string;
    },
  "serviceRequest" | "healthPlanName" | "outcome" | "appealStatus"
>;

export interface ReopeningSubmissionPayload {
  reopening?: ReopeningPayload;
  serviceRequest?: ServiceRequestUpdatePayload;
}

export interface ReconsiderationSubmissionPayload {
  reconsideration?: ReconsiderationPayload;
  serviceRequest?: ServiceRequestUpdatePayload;
}

export type ReopeningPayload = RedeterminationPayload & {
  /**
   * Unique human-readable ID
   */
  reopeningId?: string;
  /**
   * Status of the reopening request
   */
  reopeningStatus?: "SUCCESS" | "FAILURE";
  /**
   * Reason for failure if the reopening request failed
   */
  failReason?: string;
  channel?: RequestorChannel;
  /**
   * Reason for failure if the reopening request failed
   */
  reopeningReason?: string;
  /**
   * Additional notes about the reopening
   */
  reopeningNote?: string;
  previousChangedValues?: ServiceRequestResponse;
  nextChangedValues?: ServiceRequestResponse;
  /**
   * Timestamp when the reopening was requested
   */
  reopeningTimestamp?: string;
};

export type Reopening = Require<
  StandardFields &
    ReopeningPayload & {
      createdByName?: string;
    },
  "serviceRequest" | "healthPlanName"
>;

export type ReconsiderationPayload = RedeterminationPayload & {
  /**
   * Outcome of the reconsideration request
   */
  outcome?:
    | "APPROVED"
    | "DENIED"
    | "PARTIALLY_APPROVED"
    | "PENDING_RN_REVIEW"
    | "PENDING_MD_REVIEW";
  /**
   * Unique human-readable ID
   */
  reconsiderationId?: string;
  /**
   * Status of the reconsideration request
   */
  reconsiderationStatus?: "SUCCESS" | "FAILURE";
  /**
   * Reason for failure if the reconsideration request failed
   */
  failReason?: string;
  channel?: RequestorChannel;
  /**
   * Reason for the reconsideration request
   */
  reconsiderationReason?: string;
  /**
   * Additional notes about the reconsideration
   */
  reconsiderationNote?: string;
  previousChangedValues?: ServiceRequestResponse;
  nextChangedValues?: ServiceRequestResponse;
  /**
   * Timestamp when the reconsideration was requested
   */
  reconsiderationTimestamp?: string;
};

export type Reconsideration = Require<
  StandardFields &
    ReconsiderationPayload & {
      createdByName?: string;
    },
  "serviceRequest" | "healthPlanName"
>;

export interface File {
  file?: string;
}

export type MdReview = BaseReview & {
  reviewType: "MdReview";
  determinationNote?: string;
  decisionExplanationDetails?: string;
  denialLanguage?: string;
  reviewOutcome?:
    | "APPROVED"
    | "WITHDRAWN"
    | "PARTIALLY_APPROVED"
    | "PENDING_MISSING_CLINICAL_INFO"
    | "PENDING_SCHEDULING_OUTREACH"
    | "RECOMMENDED_PARTIAL_APPROVAL"
    | "RECOMMENDED_DENIAL"
    | "PENDING_ADMIN_VOID"
    | "PENDING_RN_REVIEW"
    | "OUT_OF_SCOPE";
  approvedSemanticProcedureCodes?: ProcedureCode[];
  approvedProcedureCodes?: ProcedureCode[];
  approvedUnits?: number;
  denialsTemplate?: DenialsInfo | null;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  nudgeAttempted?: boolean;
  translatedMdNote?: string;
  mdModel?: {
    modelId?: string;
    modelVersion?: number;
    dateTrained?: string;
  };
  noteTranslationResults?: {
    outputLocation?: string;
    failureLocation?: string;
    retries?: number;
  };
  overwrittenTranslatedMdNote?: string;
  readmissionReason?: "RELATED" | "COMPLICATION";
  readmissionOutcome?: "OVERTURN" | "UPHOLD";
  coverageLevelDetails?: MdReviewCoverageLevelDetails[];
  automatedReviewNote?: AutomatedReviewNote;
  selectedDecisionExplanations?: GenericReason[];
  computedDecisionExplanation?: string;
};

export interface MdReviewCoverageLevelDetails {
  coverage?: Coverage;
  reviewOutcome?: string;
  approvedUnits?: number;
  approvedSemanticProcedureCodes?: ProcedureCode[];
  denialLanguage?: string;
  denialsTemplate?: DenialsInfo | null;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  usedGuidelineIds?: string[];
  selectedGuidelineIds?: string[];
  selectedDecisionExplanations?: GenericReason[];
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
  claimsPaymentNote?: string;
}

export type MdReviewSubmitPayload = MdReview & {
  reviewOutcome: {};
  approvedUnits?: number;
  approvedSemanticProcedureCodes?: ProcedureCode[];
  semanticProcedureCodes?: ProcedureCode[];
};

export type PeerToPeerReview = BaseReview & {
  reviewType: "PeerToPeerReview";
  determinationNote?: string;
  decisionExplanationDetails?: string;
  denialLanguage?: string;
  reviewOutcome?:
    | "APPROVED"
    | "WITHDRAWN"
    | "RECOMMENDED_PARTIAL_APPROVAL"
    | "RECOMMENDED_DENIAL"
    | "PARTIALLY_APPROVED"
    | "DENIED"
    | "PENDING_MISSING_CLINICAL_INFO"
    | "OUT_OF_SCOPE";
  approvedProcedureCodes?: ProcedureCode[];
  approvedSemanticProcedureCodes?: ProcedureCode[];
  approvedUnits?: number;
  denialsTemplate?: DenialsInfo;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  isPostDenialP2P?: boolean;
  computedDecisionExplanation?: string;
  selectedDecisionExplanations?: GenericReason[];
  nudgeAttempted?: boolean;
  translatedMdNote?: string;
  overwrittenTranslatedMdNote?: string;
  readmissionReason?: "RELATED" | "COMPLICATION";
  readmissionOutcome?: "OVERTURN" | "UPHOLD";
  coverageLevelDetails?: PeerToPeerCoverageLevelDetails[];
  automatedReviewNote?: AutomatedReviewNote;
};

export interface PeerToPeerCoverageLevelDetails {
  coverage?: Coverage;
  reviewOutcome?: string;
  approvedUnits?: number;
  approvedSemanticProcedureCodes?: ProcedureCode[];
  denialLanguage?: string;
  denialsTemplate?: DenialsInfo | null;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  usedGuidelineIds?: string[];
  selectedGuidelineIds?: string[];
  selectedDecisionExplanations?: GenericReason[];
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
  claimsPaymentNote?: string;
}

export type PeerToPeerReviewSubmitPayload = PeerToPeerReview & {
  reviewOutcome: {};
  approvedSemanticProcedureCodes?: ProcedureCode[];
  approvedUnits?: number;
};

export type PeerToPeerOutreachOutcome =
  | "UNRESPONSIVE"
  | "DECLINED"
  | "SUPPLIED_ADDITIONAL_INFO";

export interface PeerToPeerOutreachCreatePayload {
  outcome?: PeerToPeerOutreachOutcome;
  attemptedTimestamp?: string;
  healthPlanName?: string;
}

export type PeerToPeerOutreach = StandardFields &
  PeerToPeerOutreachCreatePayload & {
    serviceRequestId?: string;
  };

/**
 * Subset of attributes required to upsert a ClinicalReviewChangeRequest.
 */
export type ClinicalReviewChangeRequestPayload = StampedObject & {
  reasons: ReturnReviewReason[];
  details: string;
  resolved: boolean;
  review: {
    id?: string;
  };
  healthPlanName: string;
  returnRequestTo: ReturnRequestTo;
};

/**
 * Subset of attributes required for partial updates for a ClinicalReviewChangeRequest.
 */
export type ClinicalReviewChangeRequestPatchPayload =
  | StampedObject
  | { [key: string]: any };

/**
 * Subset of attributes to match its equivalent domain class from the backend.
 */
export type ClinicalReviewChangeRequest = ClinicalReviewChangeRequestPayload & {
  id?: string;
};

/**
 * Subset of all ServiceRequest data, suitable for a summary display
 */
export type ServiceRequestSearchResponse = StandardFields & {
  isMemberRequestedAuth?: boolean;
  sourceSystemLastUpdated?: string;
  bidirectionalUpdateEnabled?: boolean | null;
  authDecisionGroup?: {
    id?: string;
    authNumber?: string;
    serviceRequestIds?: string[];
  };
  reconClaim?: Claim;
  withdrawRequestorOption?: boolean;
  withdrawRequestor?: WithdrawRequestor;
  cohereId?: string;
  authNumber?: string;
  manualAuthNumberEntry?: boolean;
  authStatus?: AuthStatus;
  authorizationAuthStatus?: AuthStatus;
  authCategory?: AuthCategoryResponse;
  authSubcategory?: string;
  requestor?: Requestor;
  recurrenceType?: "ONETIME" | "RECURRING";
  startDate?: string;
  endDate?: string;
  requestType?: RequestType;
  requestTiming?: RequestTiming;
  externalReferenceId?: string;
  sourceSystem?: string;
  /**
   * ID of the original service request that this resubmission is based on. Only populated for WISeR auths when this is a resubmission.
   */
  resubmissionOf?: string;
  units?: number;
  approvedUnits?: number;
  pendingReason?: PendingReason;
  authorizationNote?: string;
  withdrawnReason?: string;
  isSrNoLongerNeeded?: boolean;
  intakeTimestamp?: string;
  tatStartTimestamp?: string;
  encounterType?: FacilityCategory;
  unitType?: UnitType;
  primaryDiagnosis?: {
    code?: string;
    description?: string;
  };
  secondaryDiagnoses?: {
    code?: string;
    description?: string;
  }[];
  orderingProvider?: {
    name?: string;
  };
  performingProvider?: {
    name?: string;
  };
  performingProviderLocation?: {
    isOutOfNetwork?: boolean;
  } | null;
  facility?: {
    name?: string;
  };
  facilityLocation?: {
    isOutOfNetwork?: boolean;
  } | null;
  patient?: {
    id?: string;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    phones?: PhoneNumber[];
  };
  lobType?: string;
  clinicalServices?: ({
    carePathName?: string;
    serviceCategoryName?: string;
    name?: string;
    clinicalServiceId?: string;
    isUnitsOnPx?: boolean;
    hasUnitsOnPxChanged?: boolean;
    id?: string;
    isCurrentPublished?: boolean;
    originalVersionId?: string;
    defaultDuration?: number;
    authCategory?: AuthCategory;
  } | null)[];
  clinicalService?: {
    carePathName?: string;
    serviceCategoryName?: string;
    name?: string;
    clinicalServiceId?: string;
    isUnitsOnPx?: boolean;
    hasUnitsOnPxChanged?: boolean;
    id?: string;
    isCurrentPublished?: boolean;
    originalVersionId?: string;
  };
  palCategory?: {
    name?: string;
  };
  procedureCodes?: ProcedureCode[];
  approvedProcedureCodes?: ProcedureCode[];
  integrationStatus?: IntegrationStatus;
  finalDeterminationLetterAttachments?: FinalDeterminationLetterAttachment[];
  internalFinalDeterminationLetterAttachments?: InternalFinalDeterminationLetterAttachment[];
  internalFinalDeterminationFaxAttachments?: InternalFinalDeterminationFaxAttachment[];
  serviceRequestRelations?: ServiceRequestRelation[];
  delegatedVendor?: string;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
  admissionDate?: string;
  expectedDischargeDate?: string;
  serviceType?: OscarServiceType;
  type?: string;
  prescribedDrug?: Drug;
  vendorIdentifier?: string;
  workflowStep?: AuthBuilderWorkflowStep;
  authValidityWindow?: AuthValidityWindowResponse;
  reviewOutcomeWithdrawOptions?: ReviewOutcomeWithdrawOption[];
  withdrawalReasonLabel?: string;
  initialDecisionDisposition?: AuthStatus;
  revisedDecisionTimestamp?: string;
  urgency?: Urgency;
  turnAroundTimeExtensionEligible?: boolean;
  turnAroundTimeExtension?: TurnAroundTimeExtension;
  authorization?: {
    id?: string;
    authSubcategory?: string;
  };
  isRestricted?: boolean;
  hasRestrictedCodes?: boolean;
  createdBy?: string;
  createdByName?: string;
  createdByGroups?: string[];
  createdByPhoneNumber?: string;
  patientStayDates?: PatientStayDate[];
  allowContinuations?: boolean;
  episodeGeneratedFromServiceRequest?: {
    id?: string;
  };
  restrictedDataSettings?: RestrictedDataSettings;
  medicalNecessityDecision?: AuthStatus;
  oonExceptionDecision?: AuthStatus;
  wasOonExceptionRequested?: boolean;
  isRai?: boolean;
  isReadmission?: boolean;
  anchorAuthNumber?: string;
  booking?: Appointment;
  readmissionReason?: "RELATED" | "COMPLICATION";
  isAdvanceNotification?: boolean | null;
  shouldEnableAuthVisibilityToggle?: boolean;
};

/**
 * Type of records being searched
 */
export type UnifiedSearchTypeParam =
  | "both"
  | "ServiceRequest"
  | "AdvanceNotification";

export interface UnifiedSearchResponseBody {
  requestListResponse?: UnifiedSearchResponseContent[];
  patientsAsCardsResponse?: {
    /**
     * The name of the patient.
     */
    patientName: string;
    /**
     * The unique ID of the patient.
     */
    patientId: string;
  }[];
}

export interface UnifiedSearchResponseContent {
  /**
   * What is the type of the item
   */
  domainType?: "ServiceRequest" | "AdvanceNotification";
  domainData?: ServiceRequestSearchResponse | AdvanceNotificationResponse;
}

export type ServiceRequestOutreachOpportunitySnapshot = ServiceRequestCommon &
  ServiceRequestExpansion;

/**
 * Represent all information needed to submit a service request to a carrier.
 */
export type ServiceRequestResponse = ServiceRequestCommon &
  ServiceRequestExpansion &
  StandardFields &
  IsReadOnly & {
    booking?: Appointment;
    crdLogId?: string;
    riskBearingEntity?: string;
    authCategory?: AuthCategoryResponse;
    formConfiguration?: {
      [key: string]: {
        fieldSpec: FormConfigurationFieldSpec;
      };
    };
    reviewOutcomeWithdrawOptions?: ReviewOutcomeWithdrawOption[];
    withdrawalReasonLabel?: string;
    withdrawRequestorOption?: boolean;
    reconClaim?: Claim;
    finalDeterminationLetterAttachments?: FinalDeterminationLetterAttachment[];
    internalFinalDeterminationLetterAttachments?: InternalFinalDeterminationLetterAttachment[];
    internalFinalDeterminationFaxAttachments?: InternalFinalDeterminationFaxAttachment[];
    tatExtensionLetterAttachments?: TatExtensionLetterAttachment[];
    integrationStatus?: IntegrationStatus;
    carePathName?: string;
    lobType?: string;
    associatedStateOfUnion?: string;
    peerToPeerOutreaches?: PeerToPeerOutreach[];
    integrationRetryCount?: number;
    integration?: {
      salesforce?: ExternalIntegration;
      welltok?: ExternalIntegration;
      matrix?: ExternalIntegration;
      carewebqi?: ExternalIntegration;
      fax_final_determination?: ExternalIntegration;
    };
    serviceRequestRelations?: ServiceRequestRelation[];
    healthPlanName?: string;
    healthPlanDisplayName?: string;
    isFacilityBased?: boolean;
    authorizationAuthStatus?: AuthStatus;
    caseType?: OscarCaseType;
    vendorIdentifier?: string;
    autoApprovalSnapshot?: AutoApprovalSnapshot;
    authValidityWindow?: AuthValidityWindowResponse;
    initialDecisionDisposition?: AuthStatus;
    revisedDecisionTimestamp?: string;
    decisionTimestamp?: string;
    turnAroundTimeExtensionEligible?: boolean;
    turnAroundTimeExtension?: TurnAroundTimeExtension;
    turnAroundTimeDueDate?: TurnAroundTime;
    expeditedTatUpdateTimestamp?: string;
    tatStartTimestamp?: string;
    authorization?: string;
    isRestricted?: boolean;
    hasRestrictedCodes?: boolean;
    createdBy?: string;
    createdByName?: string;
    createdByGroups?: string[];
    dischargedTo?: PatientDischargeStatusResponse;
    admissionSource?: AdmissionSourceResponse;
    intakeRequestType?: string;
    sourceSystem?: string;
    sourceSystemLastUpdated?: string;
    nonPalReason?: string;
    nonPalJustification?: string;
    isMostRecentEditableRequest?: boolean;
    allowContinuations?: boolean;
    isReadmission?: boolean;
    noClinicalInformationProvided?: boolean;
    anchorAuthNumber?: string;
    readmissionReason?: "RELATED" | "COMPLICATION";
    medicalNecessityDecision?: AuthStatus;
    oonExceptionDecision?: AuthStatus;
    wasOonExceptionRequested?: boolean;
    isMultiCoverageReviewRequired?: boolean;
    noCAQs?: boolean;
    noAttachments?: boolean;
    outcomeCoverageLevelDetails?: OutcomeCoverageLevelDetails[];
    restrictedDataSettings?: RestrictedDataSettings;
    isMemberRequestedAuth?: boolean;
    networkStatusOverride?: NetworkStatusOverride;
    paperlessOptedInWhenMailVendorTriggered?: boolean;
    isRai?: boolean;
    serviceRequestPalCategory?: string;
    retroRequestReason?: string;
    completedServiceDate?: string;
    isAdvanceNotification?: boolean | null;
    shouldEnableAuthVisibilityToggle?: boolean;
    episodeUnitsConfiguration?: EpisodeUnitsConfiguration;
  };

export interface Appointment {
  id?: string;
  status?: string;
  timezone?: string;
  scheduledTime?: string;
  verified?: boolean;
  verifiedAt?: string;
}

export interface OutcomeCoverageLevelDetails {
  coverage?: Coverage;
  authStatus?: AuthStatus;
  approvedSemanticProcedureCodes?: ProcedureCode[];
  claimsPaymentNote?: string;
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
  approvedUnits?: number;
  initialDecisionTimestamp?: string;
  initialDecisionDisposition?: AuthStatus;
}

export interface GroupedPatientStayDate {
  record?: {
    /**
     * Formatted date range for the stay period
     */
    dates?: string;
    /**
     * Formatted number of days in the stay period
     */
    days?: string;
    /**
     * Requested level of care code
     */
    requestedLoc?: string;
    /**
     * Approved level of care code
     */
    approvedLoc?: string;
    status?: AuthStatus;
    /**
     * Whether this stay period contains amended dates
     */
    isAmended?: boolean;
  };
  data?: {
    date?: string;
    initialReviewDecisionTimestamp?: string;
    requestedLevelOfCareCode?: string;
    approvedLevelOfCareCode?: string;
    status?: AuthStatus;
  }[];
}

export interface PatientStayDate {
  date?: string;
  reviewStatus?: "APPROVED" | "DENIED" | "PENDING" | "VOID" | "AMENDED";
  initialReviewDecision?:
    | "APPROVED"
    | "DENIED"
    | "PENDING"
    | "VOID"
    | "AMENDED";
  requestedLevelOfCareCode?: string;
  approvedLevelOfCareCode?: string;
}

export interface RestrictedDataSettings {
  lastUpdated?: string;
  updatedBy?: string;
  allowEditForAuthorizationParticipants?: boolean;
}

export interface SimilarServiceRequest {
  /**
   * The confidence score of the duplicate match, from 0.0 -> 1.0
   */
  score?: number;
  serviceRequest?: ServiceRequestResponse;
  similarFields?: string[];
}

/**
 * Subset of all ReferralRequest data, suitable for a summary display
 */
export type ReferralRequestSearchResponse = StandardFields & {
  cohereId?: string;
  startDate?: string;
  endDate?: string;
  specialty?: string;
  selectedPerformingSpecialist?: {
    name?: string;
  };
  selectedFacility?: Facility | null;
  patient?: {
    id?: string;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
  };
  healthPlanDisplayName?: string;
  healthPlanName?: string;
  isAdministrative?: boolean | null;
  referralStatus?: ReferralStatus;
};

export interface PossibleContinuationsAuthorizationResponse {
  id?: string;
  authStatus?: string;
  serviceRequests?: ServiceRequestResponse[];
}

export interface TurnAroundTimeExtension {
  healthPlan?: string;
  daysExtended?: number;
  previousTurnAroundTime?: TurnAroundTime;
  newTurnAroundTime?: TurnAroundTime;
}

export interface TurnAroundTime {
  regularTatDueDate?: string;
  expeditedTatDueDate?: string;
}

export interface AuthValidityWindowResponse {
  numberOfDays?: number;
  validityWindowEndDate?: string;
}

export interface AutoApprovalSnapshot {
  ruleId?: string;
  ruleTitle?: string;
  ruleLabelId?: string;
  ruleLabelName?: string;
}

/**
 * Representation of a kafka message document of a mail-vendor integration Data bundle of Kafka message document and corresponding service request
 */
export interface KafkaMessageDocumentResponse {
  serviceRequest?: ServiceRequestResponse;
  id?: string;
  index?: string;
  messageSentTimestamp?: string;
  messageReceivedTimestamp?: string;
  messageAgeSeconds?: string;
  messageKey?: string;
  messageValue?: string;
  originalMessageTimestamp?: string;
  originalMessageAgeSeconds?: string;
  originalMessageRetryCount?: string;
  jobName?: string;
  status?: KafkaIntegrationStatus;
  errorType?: string;
  errorDetails?: string;
}

/**
 * Contains the results of validation for a domain object.
 */
export interface ValidationResponse {
  internalValidation?: { [key: string]: any };
  externalValidation?: {
    request?: { [key: string]: any };
    response?: {
      success?: boolean;
      error_type?: string;
      errors?: { [key: string]: any };
    };
  };
}

/**
 * A relation between two service requests
 */
export type ServiceRequestRelation = StandardFields & {
  from?: RelatedServiceRequestSummary;
  to?: RelatedServiceRequestSummary;
  relation?: ServiceRequestRelationType;
};

/**
 * A shortened summary of a service request as part of the ServiceRequestRelation object
 */
export interface RelatedServiceRequestSummary {
  id?: string;
  cohereId?: string;
  authNumber?: string;
  semanticProcedureCodes?: ProcedureCode[];
  units?: number;
  clinicalService?: {
    name?: string;
    carePathName?: string;
    serviceCategoryName?: string;
  };
}

export type ServiceRequestRelationType = "FORK" | "REPLACEMENT" | "GROUP";

export interface AdmissionSourceResponse {
  enumName?: AdmissionSource;
  code?: string;
  name?: string;
  isNewbornAdmission?: boolean;
  description?: string;
}

export type AdmissionSource =
  | "PHYSICIAN_REFERRAL"
  | "CLINIC_REFERRAL"
  | "HMO_REFERRAL"
  | "TRANSFER_FROM_HOSPITAL"
  | "TRANSFER_FROM_A_SKILLED_NURSING_FACILITY_OR_INTERMEDIATE_CARE_FACILITY"
  | "TRANSFER_FROM_ANOTHER_HEALTH_CARE_FACILITY"
  | "EMERGENCY_ROOM"
  | "COURT_LAW_ENFORCEMENT"
  | "INFORMATION_NOT_AVAILABLE"
  | "RESERVED_FOR_NATIONAL_ASSIGNMENT"
  | "TRANSFER_FROM_HOSPITAL_INPATIENT_IN_THE_SAME_FACILITY_RESULTING_IN_A_SEPARATE_CLAIM_TO_THE_PAYER"
  | "TRANSFER_FROM_AMBULATORY_SURGICAL_CENTER"
  | "TRANSFER_FROM_HOSPICE_AND_IS_UNDER_A_HOSPICE_PLAN_OF_CARE_OR_ENROLLED_IN_HOSPICE_PROGRAM"
  | "NORMAL_DELIVERY"
  | "PREMATURE_DELIVERY"
  | "SICK_BABY"
  | "EXTRAMURAL_BIRTH";

export interface PatientDischargeStatusResponse {
  enumName: PatientDischargeStatus;
  code?: string;
  name?: string;
  dischargedTo?: string;
  patientStatus?: string;
}

export type PatientDischargeStatus =
  | "DISCHARGED_TO"
  | "HOME"
  | "SHORT_TERM_GENERAL_HOSPITAL_FOR_INPATIENT_CARE"
  | "SKILLED_NURSING_FACILITY"
  | "CUSTODIAL_OR_SUPPORTIVE_CARE"
  | "HOME_HEALTH"
  | "LEFT_AGAINST_MEDICAL_ADVICE_OR_DISCONTINUED_CARE"
  | "MEMBER_EXPIRED"
  | "HOSPICE_AT_HOME"
  | "HOSPICE_AT_MEDICAL_FACILITY"
  | "INPATIENT_REHABILITATION"
  | "LONGTERM_CARE_HOSPITAL"
  | "OTHER";

export interface LevelOfCareResponse {
  enumName?: string;
  code?: string;
}

export type LevelOfCare =
  | "MED_SURG_GYN"
  | "STEP_DOWN_INTERMEDIATE_ICU"
  | "ICU_CCU"
  | "SWING_BED"
  | "OBSTETRICS"
  | "OTHER"
  | "TRAUMA"
  | "BEHAVIORAL_MENTAL_HEALTH"
  | "REHABILITATION"
  | "HOSPICE_RESPITE"
  | "NURSERY"
  | "NICU"
  | "NICU_I"
  | "NICU_II"
  | "NICU_III"
  | "NICU_IV"
  | "SNF"
  | "SNF_I"
  | "SNF_II"
  | "SNF_III"
  | "SNF_IV"
  | "LTAC"
  | "SUBSTANCE_DETOX"
  | "OBSERVATION"
  | "PARTIAL_HOSPITALIZATION_PROGRAM"
  | "INTENSIVE_OUTPATIENT_PROGRAM"
  | "RESIDENTIAL";

export type PatientStatus =
  | "DISCHARGED"
  | "CURRENTLY_ADMITTED"
  | "EXPIRED"
  | "NOT_YET_ADMITTED";

export interface ExternalIntegration {
  systemId?: string;
  lastUpdatedAt?: string;
  status?: IntegrationStatus;
  retryCount?: number;
}

export type IntegrationStatus =
  | "READY"
  | "PROCESSING"
  | "SENT"
  | "REQUEST_ERROR"
  | "COMPLETE"
  | "FAILED"
  | "WITHDRAWN"
  | "INTERNALLY_COMPLETE";

export type KafkaIntegrationStatus = "SUCCESS" | "FAILURE" | "RETRIED";

export interface IntegrationMarkIndicator {
  /**
   * serviceRequestId
   */
  serviceRequestId?: string;
  /**
   * integrationType
   */
  integrationType?: string;
  /**
   * integrationStatus
   */
  integrationStatus?: string;
}

export interface IntegrationLog {
  id?: string;
  url?: string;
  integrationName?: string;
  requestJson?: string;
  responseJson?: string;
  requestXML?: string;
  responseXML?: string;
  requestType?: string;
  responseTimedOut?: boolean;
  responseCode?: string;
  objectId?: string;
  objectType?: string;
  status?: string;
  retryCount?: number;
  operationOutcomeId?: string;
  errorMessage?: string;
  requestDate?: string;
}

export type ServiceRequestCreatePayload = ServiceRequestCommon &
  ServiceRequestFieldIds & {
    recurrenceType?: {};
    startDate?: {};
    semanticPrimaryDiagnosis?: {};
    placeOfService?: {};
    tatStartTimestamp?: string;
    nextReviewDate?: string;
    expeditedTatUpdateTimestamp?: string;
    nonPalReason?: string;
    nonPalJustification?: string;
  };

export type ServiceRequestUpdatePayload = ServiceRequestCommon &
  ServiceRequestFieldIds;

export type ServiceRequestUpdateAmendOrReplacePayload =
  ServiceRequestUpdatePayload & {
    id: string;
  };

export interface StandardFields {
  id: string;
  dateCreated: string;
  lastUpdated: string;
}

export interface ReviewOutcomeWithdrawOption {
  id: string;
  label?: string;
  permission?: string;
  authStatus?: AuthStatus[];
  cancellationType?: AuthStatus;
  withdrawalRequestorId?: WithdrawRequestor;
}

export type AuthStatus =
  | "DRAFT"
  | "PENDING_FACILITY_VERIFICATION"
  | "PENDING_EXTERNAL_DETERMINATION"
  | "INTAKE"
  | "PENDING"
  | "PENDING_ASSESSMENT"
  | "WITHDRAWN"
  | "APPROVED"
  | "PARTIALLY_APPROVED"
  | "RECOMMENDED_PARTIAL_APPROVAL"
  | "RECOMMENDED_DENIAL"
  | "DENIED"
  | "VOIDED"
  | "DISMISSED"
  | "POST_DENIAL_PEER_TO_PEER"
  | "NO_LONGER_NEEDED"
  | "SUBMITTED";

export type Workflow =
  | "PENCIL_BUTTON"
  | "REVIEW"
  | "REVIEW_CORRECTIONS"
  | "APPEAL"
  | "REOPENING"
  | "RULES_ENGINE"
  | "UNKNOWN";

export interface AuthStatusWithLabel {
  id: string;
  label: string;
}

export interface PendingReasonsWithLabel {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface ReviewOutcomesWithLabel {
  id: string;
  label: string;
  disabled?: boolean;
  required?: boolean;
}

export type WithdrawnReasonUserSelection =
  | "ALTERNATIVE_SERVICES_RECOMMENDED_BY_REVIEWER"
  | "DIAGNOSIS_AGE_OR_GENDER_INCONSISTENCY"
  | "DISMISSAL_IMPROPER_PARTY"
  | "DISMISSAL_INVALID_REQUEST"
  | "DISMISSAL_MEMBER_EXPIRED"
  | "DOES_NOT_MEET_CLINICAL_POLICY"
  | "DUPLICATE_REQUEST"
  | "INACCURATE_REQUEST_DETAILS"
  | "INAPPROPRIATE_LEVEL_OF_CARE"
  | "NON_COHERE_REVIEW_ORG_RESPONSIBILITY"
  | "NOT_MEDICALLY_NECESSARY"
  | "OTHER_REVIEW_ORG_RESPONSIBILITY"
  | "PLAN_CONTRACTUAL_GEOGRAPHIC_CONSTRICTION"
  | "PLAN_CONTRACTUAL_GUIDELINES_NOT_FOLLOWED"
  | "PRIOR_AUTH_NOT_REQUIRED"
  | "REQUESTED_INFO_NOT_RECEIVED"
  | "REQUEST_DETAILS_INCONSISTENCY"
  | "SERVICE_EXCLUDED_FROM_BENEFIT_PLAN"
  | "SERVICE_NO_LONGER_REQUIRED"
  | "PATIENT_HAS_BEEN_DISCHARGED";

export type WithdrawnReasonRR =
  | "DUPLICATE_APPROVED_REQUEST"
  | "DUPLICATE_DENIED_REQUEST"
  | "EXCLUDED_FROM_AUTH_REQUEST"
  | "OS_CONCURRENT"
  | "OS_GEOGRAPHIC"
  | "OS_OTHER";

export type WithdrawnReasonIntegration = "INTEGRATION_FAILURE";

export type Subspecialty =
  | "HAND_WRIST_ELBOW"
  | "SHOULDER"
  | "SPINE"
  | "HIP_KNEE"
  | "TUMOR_ONCOLOGY"
  | "FOOT_ANKLE"
  | "TRAUMA"
  | "PEDIATRICS";

export type PendingReason =
  | "MISSING_CLINICAL_INFO"
  | "MISSING_NON_CLINICAL_INFO"
  | "RN_REVIEW"
  | "MD_REVIEW"
  | "NEW_HIRE_AUDIT_REVIEW"
  | "PEER_TO_PEER_REVIEW"
  | "OSCAR"
  | "PENDING_ASSESSMENT_SUBMISSION"
  | "NUDGE_OUTREACH"
  | "AUDIT_REVIEW"
  | "ADMIN_VOID"
  | "OTHER"
  | "OUT_OF_SCOPE"
  | "SCHEDULING_OUTREACH"
  | "OUT_OF_NETWORK_REVIEW"
  | "LETTER_WRITING";

export type DenialReason =
  | "EXPERIMENTAL_INVESTIGATIONAL"
  | "BENEFIT_LIMITATION"
  | "CONTRACTUAL";

export type DisplayTargetType =
  | "PATIENT_SELECTION"
  | "SITE_OF_SERVICE"
  | "PROCEDURE_CODE"
  | "ORDERING_PROVIDER"
  | "CLINICAL_ASSESSMENT"
  | "ATTACHMENTS_ON_PAGE"
  | "ATTACHMENTS"
  | "PRE_SUBMISSION_REVIEW"
  | "CLINICAL_REVIEW"
  | "URGENCY"
  | "REVIEW_NUDGES"
  | "SERVICE_QUANTITY";

export type RuleRunTiming =
  | "NUDGE"
  | "ON_SUBMISSION"
  | "POST_SUBMISSION"
  | "ON_REVIEW"
  | "ASYNC";

export type GuidelineType =
  | "COHERE"
  | "COHERE_NUDGE"
  | "NCD"
  | "LCD"
  | "MCG"
  | "PAYOR"
  | "ALTERNATIVE"
  | "NASS";

export type ProductIdType = "DUH" | "DUL";

export type FacilityCategory = "INPATIENT" | "OUTPATIENT";

export type ServiceRequestType = "MEDICAL" | "PHARMACY";

export type HealthPlanOption =
  | "HUMANA"
  | "OSCAR"
  | "GEISINGER"
  | "HIGHMARK"
  | "BCBS_SOUTH_CAROLINA"
  | "MEDICAL_MUTUAL_OF_OHIO"
  | "HEALTHPARTNERS"
  | "PAYCON_TEST"
  | "CYPRESS"
  | "AVERA_HEALTH_PLANS"
  | "BLUECROSS_BLUESHIELD_OF_TENNESSEE";

export type LineOfBusiness =
  | "Commercial"
  | "Commercial_ASO"
  | "Commercial_Exchange"
  | "MedSup"
  | "Medicaid"
  | "Medicare"
  | "Marketplace"
  | "Medicaid_Northeast"
  | "Other"
  | "Other_CHIP"
  | "Other_TPA"
  | "Other_DUAL"
  | "BCBS_STATE_HEALTH_PLAN"
  | "BCBS_G&I_MED_SUP"
  | "BCBS_G&I_OTHER"
  | "FEP"
  | "MEDICARE_ADVANTAGE"
  | "BCBS_G&I_EXCHANGE"
  | "BCHP_LF"
  | "BCHP_FI"
  | "BCBS_MAJOR_GROUP_FI"
  | "BCHP_Healthy_Blue"
  | "NAL_BLUE_SOLUTIONS"
  | "NATIONAL_ALLIANCE"
  | "BCHP_EXCHANGE"
  | "BCHP_ASO"
  | "BCBS_G&I_LF"
  | "BCBS_MAJOR_GROUP_ASO"
  | "D_SNP";

export type BehavioralHealthReviewType = "SUBSTANCE_ABUSE" | "MENTAL_HEALTH";

export type BehavioralHealthAdmissionType = "VOLUNTARY" | "INVOLUNTARY";

export type UnitType =
  | "PROCEDURE"
  | "NUMBER_OF_VISITS"
  | "EPISODES_OF_CARE"
  | "DAY"
  | "UNIT"
  | "RENTAL_DAY"
  | "RENTAL_MONTH"
  | "PURCHASE_ITEM"
  | "MEDICATION_DOSE"
  | "HOUR";

export type ServiceRequestInpatientType =
  | "POST_ACUTE"
  | "BEHAVIORAL"
  | "ACUTE"
  | "OTHER";

export type ServiceRequestInpatientSubType =
  | "HOSPICE"
  | "LTAC"
  | "IRF"
  | "SNF"
  | "MENTAL_HEALTH_ACUTE"
  | "SUD_ACUTE"
  | "MENTAL_HEALTH_RESIDENTIAL"
  | "SUD_RESIDENTIAL"
  | "TRANSPLANTS"
  | "NICU"
  | "MATERNITY"
  | "PEDIATRICS"
  | "MED_AND_SURG_UNPLANNED"
  | "MED_AND_SURG_PLANNED"
  | "OBSERVATION"
  | "OTHER";

export type MeasurementUnitClass =
  | "length"
  | "amount of substance"
  | "time"
  | "plane angle"
  | "mass"
  | "volume"
  | "electric charge"
  | "fraction"
  | "force"
  | "pressure"
  | "energy"
  | "power"
  | "temperature";

export interface Urgency {
  isExpedited?: boolean;
  reasonNote?: string;
}

export interface NudgeRangeofOptionsMetaData {
  isAccepted?: boolean;
  optionLabel?: string;
}

export interface ParticipateCheck {
  facilityParCheck?: boolean;
  performingProviderParCheck?: boolean;
}

export interface OONCheckRequest {
  patientId?: string;
  memberId?: string;
  effectiveDate?: string;
  npi?: string;
  npiType2?: string;
  location?: Location;
  workflowId?: string;
  serviceRequestId?: string;
  delegatedVendorName?: string;
  procedureCodes?: ProcedureCode[];
  providerType?: string;
  recordType?: string;
  source?: string;
  providerFacilityId?: string;
}

export interface ServiceRequestUserInfo {
  userId?: string;
  userName?: string;
  serviceRequestCount?: number;
}

export type OrganizationResponse = StandardFields & Organization;

/**
 * List of user IDs with the backOfficeAdmin role in the specified organization
 */
export type OrganizationAdminsResponse = string[];

export interface Organization {
  /**
   * The database id of this organization
   */
  id?: string;
  /**
   * Name of the organization, must be unique
   */
  name?: string;
  /**
   * Name of the user who last created the organization
   */
  createdByName?: string;
  /**
   * Name of the user who last updated the organization
   */
  updatedByName?: string;
  tinList?: string[];
  address?: Address;
  electronicLettersOptedIn?: boolean;
  onboardingVerified?: boolean;
  emailDomains?: string[];
  specialties?: string[];
  status?: OrganizationStatus;
  emailAutoVerificationEnabled?: boolean;
  healthPlans?: string[];
}

export type UserCreatePayload = User & {
  organizationName?: string;
};

export type UserResponse = StandardFields & User;

/**
 * SSO User Type
 */
export type SSOType = "AVAILITY" | "NAVINET";

export type SemanticGraph = SemanticConcept[];

export interface SemanticGraphIndicationCodes {
  inIndications?: SemanticGraph;
  allCodes?: SemanticGraph;
}

export type CodeSystem =
  | "hcc"
  | "hcpcs"
  | "icd10"
  | "snomed"
  | "cohere"
  | "cpt"
  | "rxnorm"
  | "ndc"
  | "TEMP-COHERE";

export type FeatureFlagStatus = "ON" | "OFF" | "LIMITED";

export interface OrganizationCsvList {
  /**
   * The database id of this organization
   */
  id?: string;
  /**
   * Name of the organization, must be unique
   */
  name?: string;
}

export interface FeatureFlagAdminView {
  id?: string;
  setting?: FeatureFlagStatus;
  description?: string;
  dateCreated?: string;
  lastUpdated?: string;
  booleanSettingValue?: boolean;
  limitedOrgsCount?: number;
}

export interface WithdrawalReason {
  id?: string;
  label?: string;
  permission?: string;
  authStatus?: AuthStatus[];
  cancellationType?: AuthStatus;
  withdrawalRequestorId?: WithdrawRequestor;
}

export interface SemanticCode {
  uri?: string;
  id?: string;
  cui?: string;
  code?: string;
  searchableCode?: string;
  legacyId?: string;
  label?: string;
  preferredLabel?: string;
  description?: string;
  type?: string;
  modifierCode?: string;
  system?: CodeSystem;
}

export type SemanticProcedureCode = SemanticCode & {
  code: string;
  units?: number;
  defaultUnits?: number;
  maxUnits?: number;
  unitType?: string;
  groupBy?: "ClinicalService" | "Policy" | "Unclassified";
  groupId?: string;
  groupOriginalVersionId?: string;
  attributeType?: string;
  selectedDetailObjects?: SelectedDetailObject[];
  relationshipTo?: string[];
  approvedUnits?: number;
  status?: string;
  palListStatus?: string;
  userAddedNonPal?: boolean;
  userAddedNonPalComment?: string;
  userSelectedAt?: string;
  identifiers?: Identifier[];
  addedAsPartOfBillableFamily?: boolean;
  isUserAddedBillableFamilyCode?: boolean;
  codeDeterminationReason?: CodeDeterminationReason;
};

export type SemanticObservationCode = SemanticCode & {
  code: string;
};

export type SemanticDiagnosisCode = SemanticCode & {
  code: string;
  isBillable?: boolean;
};

export interface Drug {
  drugDescription?: string;
  strength?: string;
  strengthForm?: string;
  unitOfMeasure?: string;
  quantityOfMeasure?: string;
  quantity?: string;
  daysSupply?: string;
  substitutions?: string;
  ndc?: string;
  uri?: string;
  cui?: string;
  aui?: string;
  sigText?: string;
  effectiveDate?: string;
  genericAllowed?: boolean | null;
  label?: string;
  dosageLabel?: string;
  dosageCui?: string;
  dosageCode?: string;
  dosageUri?: string;
  dosageTty?: string;
  route?: string;
}

export type RxnormDrug = SemanticCode & {
  tty?: string;
  strength?: string;
  route?: string;
  rxn_dose_form?: string;
  ndc?: string;
  ndcUris?: string[];
};

export type SemanticConcept = SemanticCode & {
  defaultUnits?: string;
  maxUnits?: string;
  unitType?: string;
  isBillable?: boolean;
  subClassOf?: string;
  aui?: string;
  conceptDateCreated?: string;
  conceptLastUpdated?: string;
  totals?: number;
  synonyms?: string[];
  synonymIds?: string[];
  isSearchTerm?: boolean;
  children?: SemanticConcept[];
  parents?: SemanticConcept[];
  mapsTo?: SemanticConcept[];
  mapsFrom?: SemanticConcept[];
  equivalentTo?: SemanticConcept[];
  mappings?: (SemanticConcept & {
    relationship?: string;
  })[];
  mappingsToDelete?: (SemanticConcept & {
    relationship?: string;
  })[];
};

export interface SelectedDetailObject {
  label?: string;
  modifierCode?: string;
  selectedDetailId?: string;
}

export interface User {
  /**
   * Unique identifier assigned to user
   */
  id?: string;
  /**
   * Full name of user
   */
  name?: string;
  /**
   * First name of user
   */
  firstName?: string;
  /**
   * Last name of user
   */
  lastName?: string;
  /**
   * User email, also used as login
   */
  email?: string;
  /**
   * Phone number for the user
   */
  phone?: string;
  faxNumber?: PhoneNumber;
  userType?: UserType;
  /**
   * List of user's health plans
   */
  organizationHealthPlans?: string[];
  /**
   * Comma separated values of UserType
   */
  roles?: UserType[];
  oktaStatus?: OktaUserStatus;
  /**
   * The id of the provider organization that the user belongs to
   */
  organization?: string;
  /**
   * The name of the provider organization that the user belongs to
   */
  organizationName?: string;
  extension?: UserExtension;
  /**
   * User creation date
   */
  dateCreated?: string;
  /**
   * Id of User that created the given User
   */
  createdBy?: string;
  /**
   * Describes whether a given user has been auto-verified by email address
   */
  wasEmailAutoVerified?: boolean;
  /**
   * Describes whether a user auto-verified is new
   */
  isNewEmailAutoVerifiedUser?: boolean;
  /**
   * cohere License Agreement Signed At
   */
  cohereLicenseAgreementSignedAt?: string;
  /**
   * The pending organization id that the user is trying to join
   */
  pendingOrganizationId?: string;
  /**
   * The pending organization name that the user is trying to join
   */
  pendingOrganizationName?: string;
  /**
   * The role they perform at the org
   */
  title?: string;
  /**
   * Areas of focus
   */
  specialties?: string[];
  /**
   * Is the user from a SSO
   */
  isSsoUser?: boolean;
  /**
   * opsGroup associated with the user
   */
  opsGroup?: string;
  /**
   * Type of SSO
   */
  ssoType?: string;
}

export interface UserExtension {
  id?: string;
  /**
   * Most recently input fax number by user, used for auto-filling
   */
  mostRecentlyUsedFaxNumber?: string;
}

/**
 * AKA the user role
 */
export type UserType =
  | "admin"
  | "backOffice"
  | "backOfficeAdmin"
  | "clinicalContent"
  | "internalUserAdmin"
  | "serviceOps"
  | "serviceOpsLeadership"
  | "nurseReviewer"
  | "mdReviewer"
  | "auditReviewer"
  | "user"
  | "finalReviewAuditor"
  | "reviewDenialSubmitter"
  | "authViewOnly"
  | "integrationViewer"
  | "featureFlagEditor"
  | "mDReviewerNewWorkflowBeta";

export interface UserActivity {
  /**
   * Type of activity that occurred
   */
  type: "IMPRESSION" | "INTERACTION" | "SERVICE_REQUEST_MODIFICATION";
  /**
   * What occured
   */
  event:
    | "PATIENT_REDIRECT"
    | "PATIENT_RECOMMEND_CHANGE"
    | "PAL_CHECK_REMOVE_CODES"
    | "FIELD_REDIRECT"
    | "FIELD_RECOMMENDED_CHANGE"
    | "UPDATE_DECISIONED_AUTH"
    | "REPLACE_DECISIONED_AUTH"
    | "AMEND_DECISIONED_AUTH"
    | "ATTACHED_FAX_TO_AUTH"
    | "SUBMIT_FAX_SERVICE_REQUEST"
    | "SEND_FAX_NOTICE"
    | "FAX_PATIENT_SEARCH"
    | "FAX_SERVICE_REQUEST_SEARCH"
    | "DISPLAY_BANNER"
    | "DISPLAY_MODAL"
    | "DISPLAY_EXPEDITED_CONFIRMATION_MODAL"
    | "OUTREACH_OPPORTUNITY_COMPLETED"
    | "UPLOAD_ATTACHMENT"
    | "AUTH_BUILDER_OTHER_SERVICE_SELECTED"
    | "AUTH_BUILDER_OTHER_SERVICE_DISPLAYED"
    | "NURSE_REVIEW"
    | "OUT_OF_NETWORK_REVIEW"
    | "MD_REVIEW"
    | "AUDIT_REVIEW"
    | "PTP_REVIEW"
    | "NEW_HIRE_AUDIT_REVIEW"
    | "DX_OOS_AUDIT_REVIEW"
    | "FAX_AUDIT_REVIEW"
    | "UPDATE_REQUESTOR_FAX"
    | "ORGANIZATION_EMAIL_AUTO_VERIFICATION_ELIGIBILITY_CHECK"
    | "ORGANIZATION_INFO_SUBMISSION"
    | "ORGANIZATION_MANUAL_VERIFICATION"
    | "ORGANIZATION_PATIENT_INFO_AUTO_VERIFICATION"
    | "EMAIL_VERIFICATION"
    | "ORGANIZATION_DELETE_EMAIL_AUTO_VERIFIED_USER"
    | "USER_HAS_NO_PATIENT_ACCESS"
    | "USE_CAREWEBQI"
    | "DOWNLOAD_ATTACHMENT"
    | "PAL_UPLOAD"
    | "EXCEEDED_MAX_UNITS_FOR_PXCODES"
    | "EHR_SR_POPULATION"
    | "CLINICAL_ASSESSMENT_SHOWN"
    | "CLINICAL_ASSESSMENT_SKIPPED_AA"
    | "CLINICAL_ASSESSMENT_SKIPPED_FAST_TRACK"
    | "CLINICAL_ASSESSMENT_SKIPPED_NO_QUESTIONS"
    | "NON_PAL_CODES_ADDED"
    | "RECENT_OP_CLICKED_NO_CHANGE"
    | "RECENT_OP_CLICKED_THEN_CHANGED"
    | "RECENT_OP_NOT_CLICKED"
    | "RECENT_PP_CLICKED_NO_CHANGE"
    | "RECENT_PP_CLICKED_THEN_CHANGED"
    | "RECENT_PP_NOT_CLICKED"
    | "RECENT_PPP_CLICKED_NO_CHANGE"
    | "RECENT_PPP_CLICKED_THEN_CHANGED"
    | "RECENT_PPP_NOT_CLICKED"
    | "RECENT_F_CLICKED_NO_CHANGE"
    | "RECENT_F_CLICKED_THEN_CHANGED"
    | "RECENT_F_NOT_CLICKED"
    | "RECENT_RP_CLICKED_NO_CHANGE"
    | "RECENT_RP_CLICKED_THEN_CHANGED"
    | "RECENT_RP_NOT_CLICKED"
    | "RECENT_SF_CLICKED_NO_CHANGE"
    | "RECENT_SF_CLICKED_THEN_CHANGED"
    | "RECENT_SF_NOT_CLICKED"
    | "RECENT_PS_CLICKED_NO_CHANGE"
    | "RECENT_PS_CLICKED_THEN_CHANGED"
    | "RECENT_PS_NOT_CLICKED"
    | "AUTHORIZATION_TRANSFER"
    | "VIEW_AUTH_DETAILS"
    | "POST_SUBMISSION_EDIT"
    | "TEMPORARY_PATIENT_CREATION"
    | "PATIENT_COVERAGE_UPDATE"
    | "ADD_ATTACHMENTS_SKIPPED"
    | "EDIT_ORIGINAL_DUPLICATE"
    | "DUPLICATE_MODAL_OPEN"
    | "CANCEL_DUPLICATE"
    | "CLOSE_DUPLICATE_MODAL"
    | "ORIGINAL_REQUEST_WITHDRAWAL"
    | "START_CONTINUATION_DUPLICATE"
    | "CONTINUE_WITH_DUPLICATE"
    | "SUBMIT_DUPLICATE"
    | "PROFILE_UPDATED"
    | "ACTIVATION_LINK_SENT"
    | "DELAY_SUBMIT_NUDGE_SEEN"
    | "DELAY_SUBMIT_SAVED_AND_EXITED"
    | "DELAY_SUBMIT_ADDED_ATTACHMENTS_AT_NUDGE"
    | "DELAY_SUBMIT_NUDGE_IGNORED"
    | "REQUIRED_DOCS_NUDGE"
    | "NO_LONGER_NEEDED_ATTESTATION"
    | "REFERRAL_FROM_FAX"
    | "START_NEW_SR"
    | "START_NEW_RF"
    | "ATTACHED_FAX_TO_REFERRAL"
    | "SEARCH_V1_MATCH_MEMBER_ID"
    | "SEARCH_V1_MATCH_COHERE_ID"
    | "SEARCH_V1_MATCH_AUTH_NUMBER"
    | "SEARCH_V1_MATCH_FIRST_NAME"
    | "SEARCH_V1_MATCH_LAST_NAME"
    | "SEARCH_V1_MATCH_FULL_NAME"
    | "SEARCH_V1_MATCH_PARTIAL_NAME"
    | "SEARCH_V1_NO_MATCH"
    | "SEARCH_V2_MATCH_MEMBER_ID"
    | "SEARCH_V2_MATCH_COHERE_ID"
    | "SEARCH_V2_MATCH_AUTH_NUMBER"
    | "SEARCH_V2_MATCH_FIRST_NAME"
    | "SEARCH_V2_MATCH_LAST_NAME"
    | "SEARCH_V2_MATCH_FULL_NAME"
    | "SEARCH_V2_MATCH_PARTIAL_NAME"
    | "SEARCH_V2_NO_MATCH"
    | "SEARCH_V2_BEYOND_ORG_MATCH_MEMBER_ID"
    | "SEARCH_V2_BEYOND_ORG_MATCH_COHERE_ID"
    | "SEARCH_V2_BEYOND_ORG_MATCH_AUTH_NUMBER"
    | "SEARCH_V2_BEYOND_ORG_MATCH_FIRST_NAME"
    | "SEARCH_V2_BEYOND_ORG_MATCH_LAST_NAME"
    | "SEARCH_V2_BEYOND_ORG_MATCH_FULL_NAME"
    | "SEARCH_V2_BEYOND_ORG_MATCH_PARTIAL_NAME"
    | "SEARCH_V2_BEYOND_ORG_NO_MATCH"
    | "SEARCH_UNIFIED_V1_MATCH_MEMBER_ID"
    | "SEARCH_UNIFIED_V1_MATCH_COHERE_ID"
    | "SEARCH_UNIFIED_V1_MATCH_AUTH_NUMBER"
    | "SEARCH_UNIFIED_V1_MATCH_FIRST_NAME"
    | "SEARCH_UNIFIED_V1_MATCH_LAST_NAME"
    | "SEARCH_UNIFIED_V1_MATCH_FULL_NAME"
    | "SEARCH_UNIFIED_V1_MATCH_PARTIAL_NAME"
    | "SEARCH_UNIFIED_V1_NO_MATCH"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_MEMBER_ID"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_COHERE_ID"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_AUTH_NUMBER"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_FIRST_NAME"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_LAST_NAME"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_FULL_NAME"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_MATCH_PARTIAL_NAME"
    | "SEARCH_UNIFIED_V1_BEYOND_ORG_NO_MATCH"
    | "GUIDELINE_TEXT_EDIT_AFTER_INGESTION"
    | "BOU_CLICKED_MY_ORG"
    | "REVIEW_ATTACHMENTS_AFTER_SUBMIT_MODAL"
    | "SKIP_ATTACHMENTS_VALIDATION_AFTER_SUBMIT"
    | "FOUND_EMPTY_ATTACHMENTS_SUBMISSION"
    | "FOUND_MISMATCH_ATTACHMENTS_SUBMISSION"
    | "REMOVED_MISMATCH_ATTACHMENT"
    | "REMOVED_AFTER_VIEW_MISMATCH_ATTACHMENT"
    | "VIEW_MISMATCH_ATTACHMENT"
    | "CASEY_CITATION_CLICK"
    | "CASEY_ANSWER_FEEDBACK"
    | "CASE_OVERVIEW_CITATION_CLICK"
    | "CLINICAL_EVALUATION_CITATION_CLICK"
    | "CLINICAL_EVALUATION_CONDITION_SELECTION"
    | "DOWNLOAD_NOTICES";
  stage: ActivityStage;
  field?: ActivityField;
  activityContext?: ActivityContext;
  beforeSnapshot?: ActivitySnapshot;
  afterSnapshot?: ActivitySnapshot;
  beforeGuidelineSnapshot?: GuidelineSnapshot;
  afterGuidelineSnapshot?: GuidelineSnapshot;
  attachmentGuidelineTextHighlightsSnapshot?: AttachmentGuidelineTextHighlightsSnapshot;
  /**
   * for Type.INTERACTION, whether the interaction was an explicit accept or reject (if known)
   */
  interactionAccept?: boolean;
}

/**
 * The stage of the process the event occurred
 */
export type ActivityStage =
  | "PATIENT_SEARCH"
  | "AUTH_CREATION"
  | "CLINICAL_ASSESSMENT"
  | "ADD_ATTACHMENTS"
  | "AUTH_SUMMARY"
  | "NURSE_REVIEW"
  | "DECISIONED_AUTH"
  | "COHERE_TEMPLATE_FAX_FLOW"
  | "NON_COHERE_TEMPLATE_FAX_FLOW"
  | "POST_SUBMISSION"
  | "REVIEW_CREATION"
  | "REVIEW_SUBMISSION"
  | "ORGANIZATION_ONBOARDING_FLOW"
  | "ORGANIZATION_DASHBOARD"
  | "GUIDELINE_REVIEW"
  | "PATIENT_SUMMARY"
  | "PAL_MANAGEMENT"
  | "DEDICATED_NUDGE_PAGE"
  | "SELECT_RECENT"
  | "ADD_TEMPORARY_PATIENT_FORM"
  | "PATIENT_MANAGEMENT"
  | "DUPLICATE_SUBMISSION_REVIEW"
  | "USER_DASHBOARD"
  | "ORGANIZATION_MANAGEMENT"
  | "DELAY_REQUEST_SUBMIT"
  | "NO_LONGER_NEEDED"
  | "REFERRAL_CREATION"
  | "SEARCH_MATCH_TRACKING"
  | "GUIDELINE_CREATION"
  | "CASEY"
  | "CASE_OVERVIEW"
  | "CLINICAL_EVALUATION"
  | "FAX_OUTCOME_OVERVIEW";

/**
 * field, if any, related to the event
 */
export type ActivityField =
  | "ENCOUNTER_TYPE"
  | "PROCEDURE_CODES"
  | "PLACE_OF_SERVICE"
  | "UNITS"
  | "PERFORMING_PROVIDER"
  | "ORDERING_PROVIDER"
  | "EXPEDITED"
  | "ASSESSMENT"
  | "ATTACHMENTS";

export interface ActivityContext {
  patientId?: string;
  clinicalServiceId?: string;
  clinicalServiceIds?: string[];
  ruleId?: string;
  actionId?: string;
  reviewId?: string;
  serviceRequestId?: string;
  cohereAuthId?: string;
  faxId?: string;
  outreachOpportunityId?: string;
  careJourneyId?: string;
  deletedUserName?: string;
  deletedUserEmail?: string;
  adminUserId?: string;
  organizationId?: string;
  /**
   * Unique identifier created to track workflow activity (For example, this id is created at the start of the auth builder workflow)
   */
  workflowId?: string;
  duplicateAuthInfo?: DuplicateAuthInfo;
  trackingNumber?: TrackingNumber;
  faxNoticeInfo?: FaxNoticeInfo;
  patientSearchQuery?: FaxSearchInfo;
  onboardingAutoVerificationStatus?: OnboardingAutoVerificationStatus;
  careWebQiReason?: string;
  userClickInfo?: UserClickInfo;
  searchInfo?: SearchInfo;
  copyPasteInfo?: CopyPasteInfo;
  attachmentId?: string;
  caseyAnswerFeedbackData?: CaseyAnswerFeedbackData;
  caseyCitationClickData?: CaseyCitationClickData;
  caseOverviewCitationClickData?: CaseOverviewCitationClickData;
  clinicalEvaluationCitationClickData?: ClinicalEvaluationCitationClickData;
  clinicalEvaluationConditionSelectionData?: ClinicalEvaluationConditionSelectionData;
  userId?: string;
  sessionId?: string;
  traceId?: string;
  helpfulnessFeedback?: string;
  palInfo?: PALInfo;
  procedureCodes?: ProcedureCode[];
  userEmail?: string;
  userRoles?: string;
  expeditedInfo?: ExpeditedInfo;
  attachmentsLength?: number;
  noLongerNeededReason?: string;
  referralRequestId?: string;
  guidelineId?: string;
  advanceNotificationId?: string;
  healthPlanName?: string;
  delegatedVendorName?: string;
}

export interface DuplicateAuthInfo {
  createdBy?: string;
  authStatus?: string;
}

export interface ExpeditedInfo {
  updatedUsingModal?: boolean;
  updatedUsingDates?: boolean;
}

export interface PALInfo {
  status?: "SUCCESS" | "FAIL";
  filename?: string;
  payer?: string;
  benefitPlanGroupName?: string;
  benefitPlanGroupId?: string;
  errorMessage?: string;
  entries?: number;
}

export interface UserClickInfo {
  clickType?: ClickType;
  clickCount?: number;
  fileName?: string;
  pageNumber?: number;
}

export type ClickType =
  | "PageRotation"
  | "ZoomIn"
  | "ZoomOut"
  | "ZoomToFit"
  | "OpenPatientSummaryHyperLink"
  | "PatientInformationCardToggle"
  | "ServiceRequestInformationCardToggle"
  | "ClinicalAssessmentCardToggle"
  | "OtherServiceRequestsCardToggle"
  | "ReviewsCardToggle"
  | "NudgesCardToggle"
  | "CarewebQICardToggle"
  | "NotesCardToggle";

export interface SearchInfo {
  docSegmentationId?: string;
  searchTerm?: string;
  dateSearchTriggered?: string;
  totalSearchResult?: number;
  attachmentId?: string;
  searchInfoByPages?: SearchInfoByPage[];
}

export interface SearchInfoByPage {
  docType?: DocumentType;
  docDate?: string;
  searchResult?: number;
  pageNumber?: number;
}

export interface CopyPasteInfo {
  copiedText?: string;
  page?: number;
  attachmentId?: string;
}

export type ReviewOutcome =
  | "APPROVED"
  | "WITHDRAWN"
  | "RECOMMENDED_APPROVAL"
  | "RECOMMENDED_PARTIAL_APPROVAL"
  | "RECOMMENDED_DENIAL"
  | "PARTIALLY_APPROVED"
  | "DENIED"
  | "PENDING_MISSING_CLINICAL_INFO"
  | "PENDING_ADMIN_VOID"
  | "PENDING_NUDGE_OUTREACH"
  | "PENDING_SCHEDULING_OUTREACH"
  | "PENDING_MD_REVIEW"
  | "PENDING_RN_REVIEW"
  | "MISROUTE"
  | "UNWORKABLE"
  | "OUT_OF_SCOPE"
  | "ADMINISTRATIVE_DENIAL";

export interface AttachmentGuidelineTextHighlightsSnapshot {
  reviewer?: string;
  dateCompleted?: string;
  reviewOutcome?: ReviewOutcome;
  auditOutcome?: "PASS" | "WITHDRAWN" | "FAIL";
  textHighlights?: AttachmentGuidelineTextHighlight[];
  numAttachments?: number;
  nonOcrAttachmentIds?: string[];
}

export interface GuidelineSnapshot {
  guidelineInfo?: GuidelineInfo;
}

export interface GuidelineInfo {
  guidelineType?: string;
  guidelineIds?: string[];
  checkedIndications?: string[];
}

export interface OnboardingAutoVerificationStatus {
  eligible?: boolean;
  enabled?: boolean;
  userEmail?: string;
  organizationTin?: string;
  organizationName?: string;
  successful?: boolean;
}

export interface ActivitySnapshot {
  procedureCodes?: string[];
  units?: number;
  unitType?: UnitType;
  placeOfServiceId?: string;
  orderingProviderId?: string;
  attachmentsCount?: number;
  performingProviderId?: string;
  primaryDiagnosisCode?: string;
  secondaryDiagnosisCodes?: string[];
  facilityId?: string;
  startDate?: string;
  endDate?: string;
  facilitySelectedTin?: string;
  performingProviderSelectedTin?: string;
  mongoPatientId?: string;
  isExpedited?: boolean;
  expeditedReason?: string;
  encounterType?: FacilityCategory;
  requestorFaxNumber?: string;
  serviceRequests?: ServiceRequestResponse[];
  coverageFieldUpdates?: { [key: string]: any };
}

export interface FaxNoticeInfo {
  faxbackRequestType: "SERVICE_REQUEST" | "REFERRAL_REQUEST";
  reasonForNotice: string;
  patientInfoMissing?: boolean;
  practiceAuthContactMissing?: boolean;
  diagnosisInfoMissing?: boolean;
  servicesRequestedInfoMissing?: boolean;
  orderingPhysicianInfoMissing?: boolean;
  facilityInfoMissing?: boolean;
  additionalNotes?: string;
  ineligiblePatient?: boolean;
  ineligibleProvider?: boolean;
  mustBePCPSubmitted?: boolean;
  referringProviderInfoMissing?: boolean;
  specialtyMissing?: boolean;
  specialistInfoMissing?: boolean;
  specialistPracticeInfoMissing?: boolean;
}

export interface FaxSearchInfo {
  /**
   * filter patients by member ID
   */
  memberId?: string;
  /**
   * filter patients by date of birth (yyyy-MM-dd)
   */
  dateOfBirth?: string;
  /**
   * filter patients by first name, should be all uppercased
   */
  firstName?: string;
  /**
   * filter patients by last name, should be all uppercased
   */
  lastName?: string;
}

/**
 * status of the user in okta
 */
export type OktaUserStatus =
  | "STAGED"
  | "PROVISIONED"
  | "ACTIVE"
  | "RECOVERY"
  | "LOCKED_OUT"
  | "PASSWORD_EXPIRED"
  | "SUSPENDED"
  | "DEPROVISIONED";

/**
 * If isReadOnly field is true, then the UI should not allow the user to edit.
 *
 */
export interface IsReadOnly {
  isReadOnly?: boolean;
}

export interface TrackingNumber {
  trackingNumber?: string;
}

export interface ServiceRequestCounts {
  all?: number;
  upcoming?: number;
  approved?: number;
  pendingReview?: number;
  denied?: number;
  partiallyApproved?: number;
  draft?: number;
  completed?: number;
  withdrawn?: number;
}

export interface OutreachOpportunity {
  id?: string;
  serviceRequest: ServiceRequestResponse;
  recommendChangeAction?: RecommendChangeRuleAction;
  originalRuleId?: string;
  status: OpportunityStatus;
  srBeforeSnapshot?: ServiceRequestOutreachOpportunitySnapshot;
  srAfterSnapshot?: ServiceRequestOutreachOpportunitySnapshot;
  outreachAttempts?: OutreachAttempt[];
  manualOpportunityTrigger?: ManualOpportunityTrigger;
  healthPlanName?: string;
}

export interface OutreachAttempt {
  id?: string;
  outreachOpportunity?: OutreachOpportunity;
  channel: AttemptChannel;
  status?: AttemptStatus;
  outreachOutcome?: OutreachOutcome;
  integrationRetryCount?: number;
  attemptNumber?: number;
  scheduledSend: string;
  subject?: string;
  message?: string;
  outreachNotes?: string;
  recipient?: Recipient;
  bodyPageText?: string;
  sendServiceSummary?: boolean;
  serviceRequestId?: string;
  serviceRequestIds?: string[];
  completedBy?: string;
  completedByName?: string;
  completedDate?: string;
  manual?: boolean;
  reviewId?: string;
  outreachType?: OutreachType;
  contactType?: ContactType;
  nudgeInfo?: NudgeInfo[];
  memberPhoneNumber?: PhoneNumber;
  memberFaxNumber?: PhoneNumber;
  memberEmail?: string;
  providerPhoneNumber?: PhoneNumber;
  providerFaxNumber?: PhoneNumber;
  providerEmail?: string;
  userProvidedEmail?: string;
  userProvidedFaxNumber?: PhoneNumber;
  healthPlanName?: string;
}

/**
 * represents the status of a Manual OutreachAttempt
 */
export type OutreachOutcome =
  | "SUCCESS"
  | "FAILED"
  | "OFFICE_STAFF_MEMBER_NOTIFIED"
  | "LEFT_SCRIPTED_MESSAGE"
  | "P2P_RIGHTS_GIVEN"
  | "UNABLE_TO_REACH"
  | "MEMBER_NOTIFIED"
  | "AUTHORIZED_REP_NOTIFIED"
  | "LEFT_SCRIPTED_MESSAGE"
  | "UNABLE_TO_REACH_NO_ANSWER";

/**
 * represents different types of outreach for inbound & outbound
 */
export type OutreachType =
  | "CREATE_A_NEW_SUBMISSION"
  | "EXISTING_AUTH_STATUS_INQUIRY"
  | "EXISTING_AUTH_UPDATE"
  | "SCHEDULING_OUTREACH"
  | "OTHER"
  | "MISSING_INFO_CLINICAL_FROM_PORTAL"
  | "MISSING_INFO_CLINICAL"
  | "MISSING_INFO_NON_CLINICAL"
  | "NOTIFICATION"
  | "NUDGES";

/**
 * represents different type of contact
 */
export type ContactType = "INBOUND" | "OUTBOUND";

/**
 * represents the status of an OutreachAttempt
 */
export type AttemptStatus =
  | "READY"
  | "QUEUED"
  | "PROCESSING"
  | "FAILED"
  | "ACCEPTED"
  | "REQUEST_ERROR"
  | "UNDELIVERABLE"
  | "WITHDRAWN";

/**
 * represents the action for an outreach attempt
 */
export type AttemptChannel = "EMAIL" | "PHONE" | "FAX" | "SET_AUTH_STATUS";

/**
 * represents the recipient of a fax
 */
export type Recipient =
  | "ORDERING_PROVIDER"
  | "PERFORMING_PROVIDER"
  | "REQUESTING_PROVIDER"
  | "MEMBER"
  | "MEMBER_REPRESENTATIVE"
  | "FACILITY"
  | "CUSTOM_FAX_RECIPIENT"
  | "PCP";

/**
 * status of outreach opportunities
 */
export type OpportunityStatus = "READY" | "COMPLETE" | "FAILED" | "WITHDRAWN";

/**
 * type of manually triggered OO
 */
export type ManualOpportunityTrigger = "MISSING_INFO";

/**
 * Oscar Single Service Case type field
 */
export type OscarCaseType = "MEDICAL" | "BEHAVIORAL_HEALTH";

/**
 * Oscar Single Service Service type field
 */
export type OscarServiceType =
  | "ACUTE_ADMISSION"
  | "APPLIED_BEHAVIOR_ANALYSIS"
  | "DETOX_ADMISSION"
  | "DIRECT_HOSPITAL_ADMISSION"
  | "DURABLE_MEDICAL_EQUIPMENT"
  | "ELECTIVE_SURGICAL_AND_NON_SURGICAL_PROCEDURES"
  | "ELECTROCONVULSIVE_THERAPY"
  | "EMERGENCY_ADMISSION"
  | "HOME_HEALTH_CARE"
  | "IMAGING_SERVICES"
  | "IN_HOME_BEHAVIORAL_HEALTH_SERVICE"
  | "INTENSIVE_CARE_COORDINATION"
  | "INTENSIVE_OUTPATIENT"
  | "LABORATORY_WORK"
  | "MEDICARE_PART_B_DETERMINED_DRUGS"
  | "NEURO_OR_PSYCHOLOGICAL_TESTING"
  | "OTHER_SERVICE_TYPE"
  | "OUTPATIENT_REHABILITATION_THERAPIES"
  | "PARTIAL_HOSPITALIZATION_PROGRAM"
  | "PHYSICIAN_ADMINISTERED_SPECIALTY_DRUGS"
  | "POST_ACUTE_INPATIENT_ADMISSION"
  | "RESIDENTIAL_TREATMENT_CENTER"
  | "TRANSCRANIAL_MAGNETIC_SIMULATION"
  | "TRANSPORTATION";

export type UnitedStates =
  | "AL"
  | "AK"
  | "AS"
  | "AZ"
  | "AR"
  | "CA"
  | "CO"
  | "CT"
  | "DE"
  | "DC"
  | "FL"
  | "GA"
  | "GU"
  | "HI"
  | "ID"
  | "IL"
  | "IN"
  | "IA"
  | "KS"
  | "KY"
  | "LA"
  | "ME"
  | "MD"
  | "MA"
  | "MI"
  | "MN"
  | "MS"
  | "MO"
  | "MT"
  | "NE"
  | "NV"
  | "NH"
  | "NJ"
  | "NM"
  | "NY"
  | "NC"
  | "ND"
  | "MP"
  | "OH"
  | "OK"
  | "OR"
  | "PA"
  | "RI"
  | "SC"
  | "SD"
  | "TN"
  | "TX"
  | "UT"
  | "VT"
  | "VI"
  | "VA"
  | "WA"
  | "WV"
  | "WI"
  | "WY"
  | "PR";

/**
 * Logical Operator Types for Convene Rules
 */
export type LogicalOperator =
  | "ALL"
  | "ANY"
  | "NOT"
  | "AT_LEAST"
  | "NOT_ANY"
  | "NOT_ALL"
  | "LESS_THAN";

export type Unknown = string | number | boolean | {}[] | { [key: string]: any };

export type OnAcceptActions =
  | "REPLACE_VALUE"
  | "LIMIT_TO_VALUES"
  | "ADD_VALUES"
  | "REMOVE_VALUES"
  | "ADD_ATTACHMENTS"
  | "WITHDRAW"
  | "NONE";

export type Vendor =
  | "HealthHelp"
  | "Agadia"
  | "Cohere"
  | "Humana"
  | "Geisinger"
  | "Oscar"
  | "HealthHelpV2"
  | "Medical Mutual of Ohio"
  | "Avera Health Plans"
  | "BlueCross BlueShield of Tennessee";

export type ReturnRequestTo = "MD_REVIEW" | "NURSE_REVIEW";

export type ReturnReviewReason =
  | "ADD_OR_REMOVE_GUIDELINE_USED_FOR_REVIEW"
  | "REVISIT_THE_GUIDELINE_CRITERIA"
  | "ADD_OR_AMEND_DENIAL_REASONING_EXPLANATION"
  | "REVISIT_PARTIALLY_APPROVED_DENIED_CPT_CODES"
  | "OTHER";

export interface HealthHelpPASRequestResponse {
  responseBundle?: {
    [key: string]: { [key: string]: any };
  };
  vendorIdentifier: string;
  accessToken: string;
}

export interface HealthHelpWithdrawResponse {
  responseBundle?: {
    [key: string]: { [key: string]: any };
  };
  vendorIdentifier: string;
}

export type PalCheckResponse = {
  pxCodes?: PalCheckProcedureCodeResponse[];
  activeCoverage?: Coverage;
} | null;

export interface PalCheckProcedureCodeResponse {
  procedureCode: string;
  isAuthRequired: boolean;
  authSubmissionVendor?: string;
}

export type CdsResponse = {
  cards?: Card[];
  extension?: CardExtension;
} | null;

export type CdsErrorResponse = {
  response?: { [key: string]: any };
  crdLogId?: string;
} | null;

export interface Card {
  uuid?: string;
  summary?: string;
  detail?: string;
  indicator?: string;
  source?: string;
  links?: Link[];
  selectionBehavior?: SelectionBehavior;
}

export interface Link {
  label?: string;
  type?: string;
  appContext?: string;
  url?: string;
}

export interface SelectionBehavior {
  value?: string;
}

export interface CardExtension {
  crdLogId?: string;
  delegatedVendor?: string;
  authSubmissionVendor?: string;
  isAuthRequired?: boolean;
  activeCoverage?: Coverage;
  extensionDetails?: CardExtensionDetails;
  procedureCodes?: CardExtensionDetails[];
  status?: CRDStatus;
}

export interface CardExtensionProcedureCode {
  code?: string;
  system?: string;
  delegatedVendor?: string;
  authSubmissionVendor?: string;
  originalDelegatedVendor?: string;
  originalAuthSubmissionVendor?: string;
  isAuthRequired?: boolean;
  palCheckMessage?: string;
  status?: CRDStatus;
}

export interface CardExtensionDetails {
  decisionReason?: string;
  delegatedVendor?: string;
  isAuthRequired?: boolean;
  palCheckMessage?: string;
  authSubmissionVendor?: string;
  status?: CRDStatus;
}

/**
 * defines if prior auth and/or advance notification is required
 */
export type CRDStatus =
  | "ADVANCE_NOTIFICATION_REQUIRED"
  | "PRIOR_AUTH_REQUIRED"
  | "NO_PRIOR_AUTH_OR_ADVANCE_NOTIFICATION_REQUIRED";

export interface OnboardingPatientInfo {
  memberId?: string;
  memberDateOfBirth?: string;
}

export interface OnboardingNewUserInfo {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  faxNumber?: PhoneNumber;
  userSpecialties?: string[];
}

export interface OnboardingNewOrgInfo {
  name?: string;
  tin?: string;
  phoneNumber?: PhoneNumber;
  faxNumber?: PhoneNumber;
  website?: string;
  npi?: string;
  primaryAddress?: Address;
  otherTins?: NameTinPair[];
  healthPlans?: string[];
  organizationSpecialty?: string;
  organizationSpecialties?: string[];
  organizationStructure?: string;
}

export interface NameTinPair {
  name: string;
  tin: string;
}

export interface OnboardingNewOrgRequest {
  memberIdValidationInfoList?: OnboardingPatientInfo[];
  newUserInfo?: OnboardingNewUserInfo;
  newOrganizationInfo?: OnboardingNewOrgInfo;
  enableEmailAutoVerification?: boolean;
}

export interface OnboardingNewOrgSsoRequest {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phoneNumber?: string;
  phoneExtension?: string;
  faxNumber?: string;
  isHealthcareProviderChecked?: boolean;
  userSpecialties?: string[];
  newOrganizationInfo?: OnboardingNewOrgInfo;
  enableEmailAutoVerification?: boolean;
}

export interface OnboardingJoinOrgRequest {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phoneNumber?: string;
  phoneExtension?: string;
  faxNumber?: string;
  isHealthcareProviderChecked?: boolean;
  userSpecialties?: string[];
  organizationId?: string;
  memberIdValidationInfoList?: OnboardingPatientInfo[];
}

export interface OnboardingManualOrgCreateRequest {
  npi?: string;
  orgName?: string;
  userName?: string;
  userPhone?: PhoneNumber;
  userEmail?: string;
  title?: string;
  userSpecialties?: string[];
  otherTins?: NameTinPair[];
  organizationAddress?: Address;
  specialty?: string;
  organizationSpecialties?: string[];
  structure?: string;
  healthPlans?: string[];
}

export type OrganizationStatus = "PENDING" | "ACTIVE";

export type Initiative = StampedObject & {
  id?: string;
  title?: string;
  description?: string;
  specialty?: string;
  linesOfBusiness?: LineOfBusiness[];
  healthPlans?: string[];
  impactMetrics?: ImpactMetric[];
  attachedRuleIds?: AttachedRule[];
  attachedRules?: ServiceRequestRule[];
};

export interface AttachedRule {
  ruleId?: string;
  attachedDate?: string;
}

export interface PostgridAddressVerificationBody {
  line1?: string;
  line2?: string;
  city?: string;
  provinceOrState?: string;
  postalOrZip?: string;
  country?: string;
}

/**
 * Controls appeals workflow for payer
 */
export interface DecisionAppealsConfigurationResponse {
  id?: string;
  enabled?: boolean;
  propagateToUpperEntities?: boolean;
  appealNoteForm?: {
    isAttachmentRequired?: boolean;
    allowedFileExtensions?: string[];
    coverageEndedByOptions?: DropdownOption[];
    formFieldConfigurations?: {
      [key: string]: FormFieldConfiguration;
    };
  };
  allowAppealInitiation?: boolean;
  numberOfAppealsAllowed?: number;
}

/**
 * Controls reconsiderations workflow for payer
 */
export interface PostDecisionReconsiderationsConfigurationResponse {
  id?: string;
  enabled?: boolean;
  reopening?: ReopeningConfiguration;
  reconsideration?: ReconsiderationConfiguration;
  appeal?: AppealConfiguration;
}

export interface ReopeningConfiguration {
  enabled?: boolean;
  eligibility?: {
    lobCriteria?: EligibilityCriteria[];
    defaultCriteria?: EligibilityCriteria;
  };
  reopeningForm?: {
    isAttachmentRequired?: boolean;
    allowedFileExtensions?: string[];
    formFieldConfigurations?: {
      [key: string]: FormFieldConfiguration;
    } | null;
  };
  reopeningReasons?: string[];
  requestorOptions?: string[];
}

export interface ReconsiderationConfiguration {
  enabled?: boolean;
  reconsiderationForm?: {
    isAttachmentRequired?: boolean;
    allowedFileExtensions?: string[];
    formFieldConfigurations?: {
      [key: string]: FormFieldConfiguration;
    };
  };
  reconsiderationReasons?: string[];
  requestorOptions?: string[];
}

export interface EligibilityCriteria {
  lineOfBusiness?: string;
  maxDaysSinceDecision?: number;
  maxNumberOfRedecisions?: number;
}

export interface AppealConfiguration {
  enabled?: boolean;
  propagateToUpperEntities?: boolean;
  appealNoteForm?: {
    isAttachmentRequired?: boolean;
    allowedFileExtensions?: string[];
    coverageEndedByOptions?: DropdownOption[];
    formFieldConfigurations?: {
      [key: string]: FormFieldConfiguration;
    };
  };
  allowAppealInitiation?: boolean;
  numberOfAppealsAllowed?: number;
}

export interface FormFieldConfiguration {
  fieldName?: string;
  fieldSpec: FormConfigurationFieldSpecification;
}

export interface PostgridAddressVerificationResponse {
  status: "success" | "failed";
  message: string;
  data: PostgridAddressVerificationResponseData;
}

/**
 * auth status attachments transition config for health plan
 */
export interface AuthStatusAttachmentsTransitionConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  healthPlanName?: string;
}

export interface PostgridAddressVerificationResponseData {
  city: string;
  country?: string;
  countryName?: string;
  errors?: {
    line1?: string[];
    generic?: string[];
    line2?: string[];
    postalOrZip?: string[];
    provinceOrState?: string[];
    city?: string[];
  };
  details?: {
    usMailingsDpvFootnote1?: "AA" | "A1";
    usMailingsDpvConfirmationIndicator?: "" | "N" | "Y" | "D" | "S";
    usPostnetBarcode?: string;
  };
  line1: string;
  line2: string;
  postalOrZip: string;
  provinceOrState: string;
  provinceOrStateName: string;
  status: "verified" | "corrected" | "failed";
  zipPlus4?: string;
}

export interface ImpactMetric {
  name: string;
  targetValue?: number;
}

export interface DenialsInfo {
  templateTitle?: string;
  formattedDenialsLanguage?: string;
  denialsTemplateId?: string;
  convertedToBlob?: boolean;
}

export type DenialsTemplate = VersionedObject &
  StampedObject &
  PromotableObject & {
    id?: string;
    templateTitle?: string;
    active?: boolean;
    authStatus?: AuthStatus[];
    guidelineIds?: string[];
    guidelines?: Guideline[];
    statesOfUnion?: UnitedStates[];
    denialsStatesOfUnion?: UnitedStates[];
    linesOfBusiness?: LineOfBusiness[];
    denialsLinesOfBusiness?: LineOfBusiness[];
    procedureCodes?: ProcedureCode[];
    policyTypes?: GuidelineType[];
    denialLanguage?: string;
    currentDraft?: DenialsTemplate;
    healthPlans?: string[];
    encounterTypes?: FacilityCategory[];
    isNonDetermination?: boolean;
    adhocNotificationType?: AdhocNotificationType;
    isCorrectionOnly?: boolean;
    contentType?: ContentType;
    mapToPolicy?: boolean;
  };

/**
 * Ad-hoc notification categories
 */
export type AdhocNotificationType =
  | "CONTINUITY_OF_CARE_DENIAL"
  | "CONTINUITY_OF_CARE_NEGOTIATIONS"
  | "CONTINUITY_OF_CARE_TERMS_AND_CONDITIONS"
  | "SCP_AS_PCP"
  | "NON_PAR_NEGATION_OF_PAYMENT"
  | "PROVIDER_APPEAL"
  | "REQUEST_FOR_ADDITIONAL_INFORMATION"
  | "CRITERIA_REQUEST"
  | "ATTEMPT_TO_REACH"
  | "REQUEST_MEDICAL_RECORDS"
  | "DISREGARD_DENIAL"
  | "PROVIDER_APPEAL_EXTERNAL_FEE_REFUND"
  | "DENC"
  | "PROVIDER_APPEAL_EXTERNAL_FEE_CONFIRMATION"
  | "QIO_APPEAL_DENC"
  | "QIO_APPEAL_DND"
  | "NOMNC"
  | "DENIAL_OF_EXPEDITED";

/**
 * content block type
 */
export type ContentType =
  | "APPROVED"
  | "DENIED"
  | "WITHDRAWN"
  | "DISMISSED"
  | "TAT_EXTENSION";

export type FinalDeterminationTemplate = StampedObject &
  PromotableObject & {
    id?: string;
    templateTitle?: string;
    templateBody?: string;
    footer?: string;
    imageList?: string[];
    active?: boolean;
    inProduction?: boolean;
    isProviderDenial?: boolean;
    templateType?: string;
    templateLocation?: string | null;
    enhancedTemplateVersionId?: string | null;
    enhancedTemplateVersions?: FinalDeterminationTemplateVersion[] | null;
  };

export type FinalDeterminationTemplateVersion = {
  versionId?: string;
  lastChange?: string;
  createdBy?: string;
};

export interface VatNotification {
  id?: string;
  phoneNumber?: string;
  attempts?: number;
  callId?: string;
  successful?: boolean;
  processTime?: string;
  status?: VatStatus;
  interventionResult?: VatInterventionResult;
  dateCreated?: string;
}

export type TATNotification = StandardFields &
  StampedObject & {
    id?: string;
    healthPlanName?: string;
    serviceRequestId?: string;
    patientId?: string;
    notificationType?: string;
    integration?: {
      matrix?: ExternalIntegration;
    };
  };

export type VatStatus =
  | "BUSY"
  | "UNKNOWN"
  | "NO_ANSWER"
  | "UNCERTAIN"
  | "INBOUND_PRIMARY_NO"
  | "PRIMARY_NO"
  | "INBOUND_PRIMARY_YES"
  | "PRIMARY_YES"
  | "INBOUND_WRONG_NUMBER"
  | "WRONG_NUMBER"
  | "INBOUND_SECONDARY_NO"
  | "SECONDARY_NO"
  | "AUTHENTICATED"
  | "INBOUND_AUTHENTICATED"
  | "INBOUND_LEAVE_MESSAGE"
  | "LEAVE_MESSAGE"
  | "ABANDONED_WAIT"
  | "INBOUND_NON_RESPONSIVE"
  | "NON_RESPONSIVE"
  | "ANSWERING_MACHINE";

export type VatInterventionResult = "CallCompleted" | "DOBAuthFail";

/**
 * Base class for notifications
 */
export type Notification = StampedObject & {
  id?: string;
  healthPlanName?: string;
  purpose?: NotificationPurpose;
  recipient?: NotificationRecipient;
  adhocNotificationType?: AdhocNotificationType;
  statusList?: NotificationStatus[];
  errorReason?: NotificationErrorReason;
  errorMessage?: string;
  dryRun?: boolean;
  manuallyCreated?: boolean;
  manuallyCreatedBy?: string;
  manuallyCreatedByName?: string;
  isExpedited?: boolean;
  languageCode?: string;
  context?: { [key: string]: any };
  regularTatDueDate?: string;
  expeditedTatDueDate?: string;
  recipientId?: string;
  delegatedVendorName?: string;
  triggeredByMissingInformation?: boolean;
  riskBearingEntity?: string;
  secondaryLanguageContext?: { [key: string]: any };
  isTranslationRequired?: boolean;
  letterLanguage?: string;
  translationIntegrationFields?: TranslationIntegration;
};

/**
 * Set of attributes that are persisted during the translation integration of the notification
 */
export interface TranslationIntegration {
  targetLanguage?: string;
  sourceLanguage?: string;
  submissionDate?: string;
  payloadContent?: string;
  externalRequestId?: string;
  translationContentReceivedDate?: string;
  downloadRetriesCount?: number;
  statusFetchRetriesCount?: number;
  uploadRetriesCount?: number;
}

export type NotificationStatus =
  | "INITIAL"
  | "PDF_GENERATED"
  | "ERROR_GENERATING_PDF"
  | "TEMPLATE_JSON_STAGED"
  | "ERROR_STAGING_TEMPLATE_JSON"
  | "SENT_TO_VENDOR"
  | "DRY_RUN_COMPLETE"
  | "ERROR_SENDING_TO_VENDOR"
  | "COMPLETE"
  | "FAILED"
  | "ERROR_SENDING_FAX"
  | "INVALID_FAX_NUMBER"
  | "OUTBOUND_FILE_RECEIVED"
  | "OUTBOUND_FILE_NOT_RECEIVED"
  | "MAIL_RETURNED"
  | "RECEIVED_INBOUND_ERROR"
  | "INBOUND_FAILED"
  | "INBOUND_COMPLETE"
  | "HISTORICAL"
  | "RETRIED"
  | "EXPIRED"
  | "WITHDRAWN"
  | "INITIAL_DRY_RUN"
  | "PDF_GENERATED_DRY_RUN"
  | "PDF_DELIVERED_TO_DRY_RUN_BUCKET"
  | "ERROR_DELIVERING_PDF_TO_DRY_RUN_BUCKET"
  | "DRY_RUN_FAILED"
  | "ERROR_STAGING_PDF"
  | "PDF_STAGED"
  | "ERROR_CONSTRUCTING_CONTEXT";

export type NotificationErrorReason =
  | "ERROR_STORING_PDF"
  | "ERROR_GENERATING_PDF"
  | "ERROR_CONSTRUCTING_CONTEXT"
  | "ERROR_SENDING_FAX"
  | "INVALID_FAX_NUMBER";

export type NotificationPurpose =
  | "FINAL_DETERMINATION"
  | "VAT"
  | "TAT_EXTENSION"
  | "ADHOC";

export type NotificationRecipient =
  | "PCP"
  | "ORDERING_PROVIDER"
  | "PATIENT"
  | "PERFORMING_PROVIDER"
  | "PERFORMING_FACILITY"
  | "REQUESTOR"
  | "MEMBER_REPRESENTATIVE"
  | "REFERRING_PROVIDER"
  | "PERFORMING_SPECIALIST";

export type MailVendor = "MATRIX" | "RRD" | "GEISINGER_PRINT_SHOP";

export type InboundZipFileTypes =
  | "ACK"
  | "LETTERS_SUMMARY"
  | "RETURN_MAIL"
  | "ERROR"
  | "MATRIX_NOTIFICATION_LETTER";

export interface HasPDF {
  pdfTemplateId?: string;
  pdfTemplateTitle?: string;
  pdfUrl?: string;
  documentTemplateDryRunPDFURL?: string;
}

export interface HasRequestContext {
  serviceRequestId?: string;
  referralRequestId?: string;
}

export interface HasRiskBearingEntity {
  riskBearingEntity?: string;
}

/**
 * Notification delivery info
 */
export interface LetterDeliveryInfo {
  processingStartDateTime?: string;
  mailingDateTime?: string;
  deliveryMethod?: string;
  pages?: number;
  addressStatus?: string;
  suppressionReason?: string;
  s3SourceUrl?: string;
  s3DestinationUrl?: string;
  patientRecipientId?: string;
  providerRecipientId?: string;
  recipientType?: RecipientType;
  outboundZipFileName?: string;
  inboundFileType?: string;
  inboundZipFileName?: InboundZipFileTypes;
  errorType?: string;
  errorDetails?: string;
}

/**
 * Letter notifications, all types
 */
export type LetterNotification = Notification &
  HasPDF & {
    contentBlockTitle?: string;
    correctionType?: string;
    letterTemplateTitle?: string;
    deliveryInfo?: LetterDeliveryInfo;
    mailVendor?: MailVendor;
  };

/**
 * Cohere-generated letters, including adhoc letters
 */
export type InternallyGeneratedLetterNotification = LetterNotification &
  HasRequestContext & {
    notificationType?: "InternallyGeneratedLetterNotification";
    letterTemplateId?: string;
  };

/**
 * Externally-generated letters
 */
export type ExternallyGeneratedLetterNotification = LetterNotification &
  HasRequestContext &
  HasRiskBearingEntity & {
    notificationType?: "ExternallyGeneratedLetterNotification";
    templateJSONUrl?: string;
  };

/**
 * Cohere-generated faxes
 */
export type FaxNotification = Notification &
  HasPDF &
  HasRequestContext &
  HasRiskBearingEntity & {
    notificationType?: "FaxNotification";
    letterTemplateId?: string;
    letterTemplateTitle?: string;
    faxNumber?: string;
    bucketName?: string;
  };

/**
 * Letters shown in Portal, but not physically mailed
 */
export type ElectronicLetterNotification =
  InternallyGeneratedLetterNotification;

export type FormConfigurationFieldSpecification =
  | "OMIT"
  | "OPTIONAL"
  | "REQUIRED";

export interface RequestFormConfigurationFieldSpecification {
  fieldSpec: FormConfigurationFieldSpecification;
}

export interface RequestStatusConfiguration {
  pendingWithDisplayHealthPlan?: boolean;
  /**
   * Mapping of auth status to display text override
   */
  authStatusDisplay?: {
    [key: string]: string;
  };
}

export interface OutOfNetworkCheckConfiguration {
  runOONCheckOnManuallyCreatedProviderFacility?: boolean;
  networkCheckTimeout?: number;
  shouldDefaultNetworkCheckFailureToUnknown?: boolean;
  enableOutOfAreaCheck?: boolean;
}

export interface PatientStayRangesConfiguration {
  blockUserIfLessThanRecommendedEndDate?: boolean;
  excludeDischargeDateOnEarlyDischarge?: boolean;
  displayImpliedDischargeDate?: {};
}

export interface PxCodeAttributeConfiguration {
  showPxCodeAttributeField?: boolean;
  delegatedVendorName?: string;
  hideModifiersForCodes?: string[];
}

export interface RelaxAuthVisibilityConfig {
  enabled?: boolean;
  enabledForLOBs?: string[];
}

export interface RecentlyUsedProviderConfiguration {
  excludeCmsProviders?: boolean;
  excludeManuallyCreatedProviders?: boolean;
}

export interface P2PIntegratedSchedulingConfiguration {
  enabledP2PDetails?: boolean;
}

export interface ManuallyCreatedProvidersConfiguration {
  enableManuallyCreatedProviders?: boolean;
  defaultNoProvidersTitle?: string;
  defaultNoProvidersDetails?: string;
  lineOfBusinessOverrides?: {
    lineOfBusiness?: string;
    enableManuallyCreatedProviders?: boolean;
    requireONNBenefits?: boolean | null;
    authCategoryMappings?: AuthCategoryMapping[] | null;
  }[];
}

export interface NotInContractConfiguration {
  healthPlanNotInContract?: boolean;
}

/**
 * Specifies how a ServiceRequestConfiguration should be constructed
 */
export type ServiceRequestConfigurationSpecification = StandardFields & {
  healthPlan?: HealthPlan;
  delegatedVendorName?: string;
  outOfNetworkCheckConfiguration?: OutOfNetworkCheckConfiguration;
  pxCodeAttributeConfiguration?: PxCodeAttributeConfiguration;
  formFieldConfigurations?: {
    [key: string]: RequestFormConfigurationFieldSpecification;
  };
  relaxAuthVisibilityConfig?: RelaxAuthVisibilityConfig;
};

/**
 * Specifies how a ServiceRequestConfiguration should be constructed
 */
export type ServiceRequestConfigurationSpecificationCreatePayload = {
  healthPlan?: string;
  delegatedVendorName?: string;
  formFieldConfigurations?: {
    fieldName?: string;
    fieldSpecification?: FormConfigurationFieldSpecification;
  }[];
};

export type BenefitPlanGroupResponse = StampedObject &
  PromotableObject & {
    id?: string;
    name?: string;
    healthPlanName?: string;
    records?: number;
    startDate?: string;
    endDate?: string;
    inpatientServicesRequirePriorAuth?: boolean;
    defaultInpatientDelegatedVendor?: string;
    excludedFromLookup?: boolean;
  };

export type BenefitPlanGroup = BenefitPlanGroupResponse & {
  healthPlan?: HealthPlan;
  planBenefitPackages?: {
    coverageIdentifier0?: string;
    coverageIdentifier1?: string;
    coverageIdentifier2?: string;
    coverageIdentifier3?: string;
  }[];
};

export interface ReplacePriorAuthListResponse {
  id?: string;
  success?: boolean;
  priorAuthListEntriesSize?: number;
}

export type EhrVendor = "ATHENAHEALTH";

export interface AuthDecisionGroupPatient {
  id?: string;
}

export interface AuthDecisionGroupServiceRequest {
  id?: string;
  authStatus?: AuthStatus;
  patient?: AuthDecisionGroupPatient;
  cohereId?: string;
  procedureCodes?: ProcedureCode[];
  clinicalService?: ClinicalService;
}

/**
 * succcessfull response
 */
export interface GetSameTierCrossEnvironmentResponse {
  entity?: { [key: string]: any };
  environment?: string;
}

/**
 * A collection of ServiceRequests that are part of the same integration
 */
export type AuthDecisionGroup = StandardFields & {
  authNumber?: string;
  authStatus?: string;
  serviceRequests?: AuthDecisionGroupServiceRequest[];
};

export type AuthBuilderWorkflowStep =
  | "GATHER_REQUIREMENTS_MEDICAL"
  | "GATHER_REQUIREMENTS_PHARMACY"
  | "SELECT_SERVICES"
  | "FILL_FORMS_CONTINUATION"
  | "FILL_FORMS_GENERAL_AUTH_SUBMISSION"
  | "FILL_FORMS_FAX"
  | "CLINICAL_ASSESSMENT"
  | "CLINICAL_ASSESSMENT_CONTINUATION"
  | "VENDOR_CLINICAL_ASSESSMENT"
  | "ADD_ATTACHMENTS"
  | "ADD_ATTACHMENTS_CONTINUATION"
  | "REVIEW_NUDGES"
  | "PRE_APPROVED_CARE_DETAILS"
  | "REVIEW"
  | "REVIEW_CONTINUATION"
  | "PROVIDER_DETAILS"
  | "SERVICE_DETAILS"
  | "MCG_AUTO_AUTH";

/**
 * admission category mappings for health plan
 */
export interface AdmissionCategoryMappingConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  admissionCategoryMappings?: CoherePayerValueMappings;
  healthPlanName?: string;
}

/**
 * level of care mappings for health plan
 */
export interface LevelOfCareMappingConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  levelOfCareMappings?: CoherePayerValueMappings;
  healthPlanName?: string;
}

/**
 * post-decision config for health plan
 */
export interface PostDecisionConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  disableOverturn?: boolean;
  disablePostTatOverturn?: boolean;
  disableWithdrawOption?: boolean;
  disablePostDenialOverturnContinuations?: boolean;
  healthPlanName?: string;
}

/**
 * automated denial letter config for health plan
 */
export interface AutomatedDenialLetterConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  usePreferredLabels?: boolean;
  submissionQA?: boolean;
  healthPlanName?: string;
  disabledLinesOfBusiness?: LineOfBusiness[] | null;
}

/**
 * non pal checkbox config for health plan
 */
export interface NonPalCheckboxConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enableLearnMoreButton?: boolean;
  reasonsToIncludeNonPalCodes?: NonPalReasons[] | null;
}

/**
 * salesforce config for health plan
 */
export interface SalesforceIntegrationConfigurationResponse {
  enabled?: boolean;
}

/**
 * other-insurance config for health plan
 */
export interface OtherInsuranceConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  formFieldConfigurationsList?: {
    fieldName?: string;
    fieldSpecification?: FormConfigurationFieldSpecification;
  }[];
  otherInsuranceOptions?: AdditionalInsurance[];
  healthPlanName?: string;
}

/**
 * scrub fields from responses tin configuration
 */
export interface ScrubFieldsFromResponsesConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  scrubTins?: boolean;
  healthPlanName?: string;
}

/**
 * letters expansion config for health plan
 */
export interface LettersExpansionConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  adhocLetter?: boolean;
  filterAdhocLetters?: boolean;
  notificationViewer?: boolean;
  singleAttachmentViewerRedesign?: boolean;
  correctionLetters?: boolean;
  canOverrideCorrectionLanguage?: boolean;
  shouldIncludeCorrectionLanguageByDefault?: boolean;
  qioAppealLetters?: boolean;
  healthPlanName?: string;
  enableLettersTranslation?: boolean;
}

/**
 * note config for health plan
 */
export interface NoteConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  noteTypeOptions?: NoteType[];
  dSnpChCodeOptions?: DropdownOption[];
  dSnpMcoConfirmationOptions?: DropdownOption[];
  negationAuthStatusesAllowedForAuthorizationNote?: AuthStatus[];
  customNoteTypes?: CustomNoteType[];
  healthPlanName?: string;
}

/**
 * tin config for health plan
 */
export interface TinConfigurationResponse {
  userTinValidation?: boolean;
}

/**
 * core-platform integration config for health plan
 */
export interface CorePlatformIntegrationConfigurationResponse {
  enabled?: boolean;
}

export interface FacilityBasedRequestConfigurationResponse {
  enabled?: boolean;
  levelOfCareIsMandatoryOnReviews?: boolean;
  includePatientStayDatesOnPlannedAdmission?: boolean;
  healthPlanName?: string;
  authCategoriesThatRequirePxCodes?: string[];
  aauthCategoryFieldMapping?: AuthCategoryFieldMapping[];
  hidePatientStayDatesForCurrentlyAdmittedPatientOnIntake?: boolean;
}

export interface PatientCoverageRiskBearingEntityEditsAllowedConfigurationResponse {
  enabled?: boolean;
  allowedValues?: string[];
}

export interface FeatureFlagConfigurationResponse {
  expandedOutreachAttemptContactOptions?: boolean;
  sortableReviewProcedureCodesTable?: boolean;
  savingReviewEnhancements?: boolean;
  postSubmissionCareRecommendation?: boolean;
  dischargeDateSameAsLastStayDate?: boolean;
  removeGuidelinesUsed?: boolean;
  bulkUpdateNegationStatusEnabled?: boolean;
  admitDateFirstStayDateValidation?: boolean;
  dischargeDateLastStayDateValidation?: boolean;
  allowRedeterminationProcedureCodeEdit?: boolean;
  dualPlanDecisionEnabled?: boolean;
  bcbsSCNotificationPhaseIIEnabled?: boolean;
  denialReasonByPxCode?: boolean;
}

export interface CodeDeterminationReason {
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
}

export interface ClinicalServiceLateSubmissionContinuationsProperties {
  clinicalServiceId?: string;
  maxSubmissionWindowDays?: number;
}

export interface FollowUpCaseConfigurationResponse {
  autoCreateAutoApprovedFollowUpCase?: boolean;
  autoCreateFinalDecisionFollowUpCase?: boolean;
  autoCreatePostDecisionFollowUpCase?: boolean;
}

export interface QueueManagementConfigurationResponse {
  denialLetterWritingCaseEnabled?: boolean;
  followUpCaseConfiguration?: FollowUpCaseConfigurationResponse;
}

export interface ContinuationConfigurationResponse {
  allowContinuations?: boolean;
  autoCreateFinalDecisionContinuations?: boolean;
  autoCreateAutoApprovedContinuations?: boolean;
  autoCreatePostDecisionContinuations?: boolean;
  healthPlanName?: string;
  lateSubmissionContinuations?: {
    clinicalServices?: ClinicalServiceLateSubmissionContinuationsProperties[];
  };
}

export interface DropdownOption {
  optionId: string;
  label: string;
}

export interface CustomNoteType {
  noteType: string;
  label: string;
}

/**
 * Toggles for service case hold functionality
 */
export interface CaseHoldConfigurationResponse {
  id?: string;
  enabled?: boolean;
  holdReasons?: string[];
  validCaseTypes?: string[];
}

/**
 * Configuration for Network Service
 */
export interface NetworkServiceConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  npiType2?: boolean;
}

/**
 * oon review config for health plan
 */
export interface OutOfNetworkReviewConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  networkStatusOverride?: boolean;
  networkStatusOverrideRequired?: boolean;
  exceptionReasonOptions?: DropdownOption[];
  healthPlanName?: string;
  oonWithMedNecReviews?: string[];
}

/**
 * enabled / disabled toggle for below review feature configuration
 */
export interface GenericReviewConfigurationToggle {
  enabled?: boolean;
}

/**
 * Generic collection of feature toggles related to unified review workflows
 */
export interface ReviewConfigurationResponse {
  id?: string;
  docSegmentation?: GenericReviewConfigurationToggle;
  denialReadingLevel?: GenericReviewConfigurationToggle;
  noClinicalInformationProvidedCheckbox?: GenericReviewConfigurationToggle;
}

/**
 * config for review chatbot
 */
export interface ReviewChatbotConfigurationResponse {
  enabled?: boolean;
  palLevelPromptConfiguration?: {
    [key: string]: ReviewChatbotConfigurationPrompt[];
  };
  standardPrompts?: ReviewChatbotConfigurationPrompt[];
}

/**
 * config for review note
 */
export interface ReviewNoteConfigurationResponse {
  runningReviewNoteEnabled?: boolean;
  decisionExplanationOptionsEnabled?: boolean;
  automatedReviewNoteConfiguration?: AutomatedReviewNoteConfiguration;
  decisionExplanationOptionsConfiguration?: DecisionExplanationOptionsConfiguration;
}

/**
 * config for decision explanations dropdown
 */
export interface DecisionExplanationOptionsConfiguration {
  enabled?: boolean;
  genericReasons?: GenericReason[];
  palCategorySpecificReasons?: PalCategorySpecificReason[];
  lineOfBusinessSpecificReasons?: LineOfBusinessSpecificReason[];
  decisionExplanationDetailsEnabled?: boolean;
}

/**
 * config for the automated review note
 */
export interface AutomatedReviewNoteConfiguration {
  enabled?: boolean;
  nodes?: AutomatedReviewNote;
}

/**
 * generic decision explanation reasons
 */
export interface GenericReason {
  reason: string;
  clinical: string;
  patientFriendlyEnglish?: string;
  patientFriendlySpanish?: string;
}

/**
 * Pal category-specific decision explanation reasons
 */
export interface PalCategorySpecificReason {
  categoryName: string;
  reasons: GenericReason[];
}

/**
 * Line of business specific decision explanation reasons
 */
export interface LineOfBusinessSpecificReason {
  lineOfBusiness: string;
  categoryName?: string;
  reasons: GenericReason[];
}

/**
 * retro request configuration
 */
export interface RetroRequestConfiguration {
  retroRequestConfigurationEnabled?: boolean;
  retroRequestReasons?: string[];
  retroRequestReasonsByLineOfBusiness?: RetroRequestReasonsByLineOfBusiness[];
}

export interface RetroRequestReasonsByLineOfBusiness {
  linesOfBusiness?: LineOfBusiness[];
  retroRequestReasons?: string[];
}

/**
 * retro request configuration response
 */
export interface RetroRequestConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  retroRequestConfigurationEnabled?: boolean;
  retroRequestReasons?: string[];
  retroRequestReasonsByLineOfBusiness?: RetroRequestReasonsByLineOfBusiness[];
  healthPlanName?: string;
}

/**
 * status transition config for health plans
 */
export interface StatusTransitionConfigurationResponse {
  id?: string;
  isPostDecisionUpdatesAllowed?: boolean;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  healthPlanName?: string;
}

/**
 * ShowPalStatusToReviewerConfiguration config by health plan
 */
export interface ShowPalStatusToReviewerConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
}

export interface ServiceRequestEditConfig {
  disableEditsByOpsGroups?: string[];
  disableFormEdits?: boolean;
  disableAttachmentEdits?: boolean;
  additionalRolesForAttachmentEdits?: string[];
  showRequestAnEditButton?: boolean;
  disableVoidsForBackOffice?: boolean;
  disableFormEditsMessage?: string;
  disableAttachmentEditsMessage?: string;
  disableClinicalAssessmentQuestionsEdits?: boolean;
  disableClinicalAssessmentQuestionsMessage?: string;
  showRequestAnEditButtonHeaderText?: string;
  showRequestAnEditButtonDescriptionText?: string;
  requestEditChangeToInpatientText?: string;
  editRequestViewModalFooterText?: string;
  enableBackOfficeExpediteEdits?: boolean;
  maxAllowedPxCodePerPayer?: number;
  srNoLongerNeededButton?: boolean;
  disableOutOfNetworkChanges?: boolean;
  disableEditForContinuations?: boolean;
  disableEditForContinuationsMessage?: string;
  /**
   * Display 'Request an edit' button and Allow Users to Upload additional attachments to Denied Request with Notes
   */
  showRequestAnAttachmentEditButton?: boolean;
}

export interface AuthStatusServiceRequestEditMapping {
  serviceRequestEditConfig?: ServiceRequestEditConfig;
  authStatus?: string;
}

/**
 * sr edit config for health plan (disabling/enabling specific edits)
 */
export interface ServiceRequestEditConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  defaultAuthStatusConfig?: ServiceRequestEditConfig;
  authStatusServiceRequestEditMappings?: AuthStatusServiceRequestEditMapping[];
}

/**
 * sr state config for health plan (disabling/enabling specific editable fields)
 */
export interface ServiceRequestStateConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  authStatusServiceRequestStateMappings?: AuthStatusServiceRequestStateMapping[];
}

export interface AuthStatusServiceRequestStateMapping {
  INITIAL?: EditableField[];
  CONTINUATION?: EditableField[];
  authStatus?: string;
}

export interface EditableField {
  field?: string;
  roles?: string;
  verb?: "AMEND" | "UPDATE" | "REPLACE" | "CONTINUATION" | "RUN_RULES";
  editable?: boolean;
}

export interface AdditionalInsurance {
  additionalInsurance?: AdditionalInsuranceOptions;
  additionalInsuranceText?: string;
}

export interface AdditionalInsuranceRequest {
  additionalInsurance?: AdditionalInsuranceOptions;
  additionalInsuranceText?: string;
  insuranceEventState?: string;
  insuranceEventDate?: string;
}

export type AdditionalInsuranceOptions =
  | "AUTO"
  | "WORKERS_COMPENSATION"
  | "NONE"
  | "OTHER";

export interface NonPalReasons {
  title?: string;
  description?: string;
}

/**
 * additionalCareParticipant config by health plan
 */
export interface AdditionalCareParticipantsFeatureConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  additionalCareParticipantList?: AdditionalCareParticipantConfig[];
}

/**
 * TemporaryMemberAuthsConfiguration config by health plan
 */
export interface TemporaryMemberAuthsConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  enableAddressValidation?: boolean;
  deduplicationJobEnabled?: boolean;
  nonCareRecommendationPatientDeduplicationMissingAlarm?: boolean;
  hoursTillNonCareRecommendationPatientDeduplicationMissingAlarm?: boolean;
  careRecommendationPlanType?: string;
  formOptionalFields?: TemporaryPatientFormField[];
  formCareRecommendationOptionalFields?: TemporaryPatientFormField[];
  patientFormDefaultValues?: { [key: string]: any };
  formCareRecommendationDefaultValues?: { [key: string]: any };
  lobToLobTypeMap?: { [key: string]: any };
}

export type TemporaryPatientFormField =
  | "FIRST_NAME"
  | "MIDDLE_NAME"
  | "LAST_NAME"
  | "DATE_OF_BIRTH"
  | "GENDER"
  | "ADDRESS_LINE_1"
  | "ADDRESS_LINE_2"
  | "CITY"
  | "STATE"
  | "TEMPORARY_MEMBER_ID"
  | "ZIP_CODE"
  | "PLAN_START_DATE"
  | "PLAN_END_DATE"
  | "HEALTH_PLAN"
  | "PLAN_TYPE"
  | "STATE_OF_ISSUE"
  | "LINE_OF_BUSINESS"
  | "LINE_OF_BUSINESS_DESCRIPTION"
  | "LINE_OF_BUSINESS_TYPE"
  | "PRODUCT_ID"
  | "GROUP_ID"
  | "COVERAGE_PRODUCT_TYPE"
  | "MARKET"
  | "MARKET_NUMBER"
  | "DEPENDENT_CODE"
  | "LEGAL_ENTITY"
  | "PRIMARY_CARE_PROVIDER";

/**
 * FaxIntakeConfiguration config by health plan
 */
export interface FaxIntakeConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  referralsCreationFromFaxEnabled?: boolean;
  faxChannels?: FaxChannels[];
  faxCloseOptions?: FaxCloseOptions[];
  manualFaxForwardingEnabled?: boolean;
  manualFaxForwardingNumbers?: ManualFaxForwardingNumbers[];
  manualFaxForwardingCloseOptions?: FaxCloseOptions[];
  unassignReasons?: UnassignReason[];
  displayFields?: DisplayField[];
}

export interface BidirectionalUpdateConfigurationResponse {
  /**
   * Whether bidirectional updates are enabled
   */
  enabled: boolean;
  /**
   * Whether refresh is allowed for bidirectional updates
   */
  allowRefresh?: boolean;
}

export interface McgAuthConfigurationResponse {
  enableMCGWorkflow: boolean;
  mcgHeaderText?: string;
  /**
   * Whether MCG workflow is enabled
   */
  mcgSubHeaderText?: string;
}

/**
 * Fax channel
 */
export interface FaxChannels {
  productId?: string;
  specialty?: string;
  delegatedVendorName?: string;
  riskBearingEntity?: string;
  shouldConsultAutoForwardingRules?: boolean;
  forwardingFaxNumber?: string;
}

export interface FaxCloseOptions {
  identifier?: string;
  label?: string;
}

export interface UnassignReason {
  label?: string;
  shortLabel?: string;
  value?: string;
}

export interface DisplayField {
  label: string;
  value: string;
}

export interface ManualFaxForwardingNumbers {
  label?: string;
  value?: string;
  userInput?: boolean;
}

/**
 * TransferAuthorizationConfiguration config by health plan
 */
export interface TransferAuthorizationConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  alwaysEnablePlaceholderMemberAuthTransfers?: boolean;
}

/**
 * AdvanceNotificationsConfiguration config by health plan
 */
export interface AdvanceNotificationsConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enableAdvanceNotifications?: boolean;
  attachFilesTitle?: string;
  attachFilesDescription?: string;
  allowSkippingCAQs?: boolean;
  priorAuthContactMessage?: string;
  learnMoreUrl?: string;
}

/**
 * ServiceRequestWithdrawalConfiguration config by health plan and delegated vendor
 */
export interface ServiceRequestWithdrawalConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enableAutomatedWithdrawalCorrectionLetters?: boolean;
  reviewerWithdrawalModalEnhanced?: boolean;
  /**
   * Disable all withdraw options (Withdraw, void, or dismissed) from Change service status, service request summary and review, buttons and options
   */
  disableAllWithdrawalFromPortal?: boolean;
  /**
   * When enabled, blocks transitions from APPROVED service requests to VOIDED, WITHDRAWN, and DENIED statuses
   */
  enableBlockingWithdrawalsForDecisionedSRs?: boolean;
  withdrawalReasons?: WithdrawalReason[];
}

/**
 * config for each additionalCareParticipant
 */
export interface AdditionalCareParticipantConfig {
  fieldConfigurations?: {
    fieldName?: string;
    fieldSpecification?: FormConfigurationFieldSpecification;
  }[];
  type?: CareParticipantType;
  entityType?: EntityType;
  dependency?: Dependency;
}

export interface Dependency {
  relation?: string;
  dependsOnKey?: string;
  dependsOnValue?: string[];
  condition?: string;
}

export type AuthorizationType = "MEDICAL" | "BEHAVIORAL" | "PHARMACY";

export type ApplicationConfiguration = StandardFields & {
  parentConfiguration?: {
    id?: string;
  };
};

export type HealthPlanApplicationConfiguration = ApplicationConfiguration & {
  healthPlanName?: string;
};

export type DelegatedVendorApplicationConfiguration =
  ApplicationConfiguration & {
    healthPlanName?: string;
    delegatedVendorName?: string;
  };

export interface HealthPlanApplicationConfigurationCreatePayload {
  healthPlan?: string;
}

export interface NotificationUpdateCommand {
  status?: NotificationStatus;
}

export interface GenerateAdhocLetterPreviewPayload {
  serviceRequestId?: string;
  adhocNotificationType?: string;
  correctionType?: string;
  contentBlockTitle?: string;
  content?: string;
  recipient?: string;
  shouldIncludeCorrectionLanguage?: boolean;
}

export interface CreateAdhocNotificationsPayload {
  serviceRequestId?: string;
  adhocNotificationType?: string;
  correctionType?: string;
  contentBlockTitle?: string;
  content?: string;
  recipients?: string[];
  customFaxNumber?: PhoneNumber;
  shouldIncludeCorrectionLanguage?: boolean;
}

export interface DelegatedVendorApplicationConfigurationCreatePayload {
  delegatedVendorName?: string;
  parentConfiguration?: string;
}

export type HealthPlanHierarchyTree = HealthPlanApplicationConfiguration & {
  childConfigurations?: DelegatedVendorApplicationConfiguration[];
};

export interface BillableFamily {
  parentCode?: string;
  billableFamilyCodes?: string[];
  billableFamilyName?: string;
}

export interface CloseOption {
  identifier: string;
  label: string;
}

export interface ManualFaxForwardingNumber {
  label: string;
  value: string;
  userInput?: boolean;
}

export interface ManualFaxForwardingCloseOption {
  label: string;
  identifier: string;
}

export interface GetFaxForwardLinesAndOptionsResponse {
  manualFaxForwardingEnabled: boolean;
  manualFaxForwardingCloseOptions: ManualFaxForwardingCloseOption[];
  manualFaxForwardingNumbers: ManualFaxForwardingNumber[];
}

export interface ContactInfoConfigurationResponse {
  contactDisplayText?: string;
  contactEmail?: string;
  contactPhoneNumber?: string;
}

export interface CollectSRContactInfoConfigurationResponse {
  enabled: boolean;
  faxFieldInfoRequired: boolean;
  allowedIntakeChannels?: string[];
}

export interface SurfaceBillableFamiliesConfigurationResponse {
  enabled: boolean;
  allowedLineOfBusiness: string[];
}

export type AttemptAction = "FAX" | "PHONE" | "EMAIL" | "SET_AUTH_STATUS";

export interface AttemptConfig {
  channel?: AttemptAction;
  backupChannel?: AttemptAction;
  hoursFromLastAttempt?: number;
  useBusinessHours?: boolean;
}

export interface MissingInfoParameters {
  minimumTimeToTAT?: number;
  attemptConfigs?: AttemptConfig[];
}

export interface MissingInformationOutreachAttempt {
  initiator?: string;
  missingInfoOutreaches?: MissingInfoParameters[];
}

export interface P2PMessaging {
  lineOfBusiness?: string;
  campaignId?: string;
  p2pText?: string;
}

export interface MissingInformationOutreachConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  enabled?: boolean;
  payerPhoneNumber?: string;
  payerFaxNumber?: string;
  missingInfoOutreachAttempts?: MissingInformationOutreachAttempt[];
  p2pMessaging?: P2PMessaging[];
  allowSendManualOutreachFromPortal?: boolean;
}

export interface MemberLookupConfigurationResponse {
  id?: string;
  featureConfigurationLevel?: FeatureConfigurationLevel;
  restrictMemberLookupForHealthPlan?: boolean;
  canUpsertEhrInformation?: boolean;
  requireAllBaseFields?: boolean;
  additionalRequiredFields?: string[];
}

export type RequestType = "INITIAL" | "CONTINUATION";

export type RequestTiming = "PRE_SERVICE" | "CONCURRENT" | "POST_SERVICE";

/**
 * contains minimal data for a service request, ideal for search, summaries etc...
 */
export interface ServiceRequestLightResponse {
  id?: string;
  cohereId?: string;
  dateSubmitted?: string;
  authStatus?: AuthStatus;
  RequestType?: RequestType;
  addedUnits?: boolean;
  addedPXCodes?: boolean;
  extendServiceDates?: boolean;
}

export interface AuthorizationsWithCountResponse {
  resultList?: AuthorizationResponse[];
  totalCount?: number;
}

export interface AuthorizationResponse {
  id?: string;
  serviceRequests?: ServiceRequestResponse[];
  serviceRequestsOnAuth?: ServiceRequestResponse[];
  authNumber?: string;
  authStatus?: AuthStatus;
  pendingReason?: PendingReason;
  semanticProcedureCodes?: ProcedureCode[];
  approvedSemanticProcedureCodes?: ProcedureCode[];
  startDate?: string;
  endDate?: string;
  dateCreated?: string;
  patient?: {
    id?: string;
  };
  healthPlanName?: string;
  requestedUnits?: number;
  approvedUnits?: number;
  units?: number;
  carePathName?: string;
  carePathJourney?: CarePathJourney;
  primaryDiagnosisCode?: DiagnosisCode;
  secondaryDiagnosisCodes?: DiagnosisCode[];
  clinicalService?: ClinicalService;
  clinicalServices?: (ClinicalService | null)[];
  placeOfService?: PlaceOfService;
  encounterType?: FacilityCategory;
  userSelectedOONException?: boolean;
  actualDischargeDateTime?: string;
  isRestricted?: boolean;
  nextReviewDate?: string;
  decisionTimestamp?: string;
  dischargedTo?: PatientDischargeStatusResponse;
  patientStatus?: PatientStatus;
  patientStayDates?: PatientStayDate[];
  additionalCareParticipants?: AdditionalCareParticipant[] | null;
  allowContinuations?: boolean;
  isPlaceholderMemberAuthorization?: boolean;
  isCareRecommendationRequest?: boolean;
  originalPatientHolders?: string[];
  selectedFacility?: Facility | null;
  reviews?: (
    | NurseReview
    | OutOfNetworkReview
    | MdReview
    | PeerToPeerReview
    | AuditReview
    | NewHireAuditReview
    | OutOfScopeAuditReview
    | FaxAuditReview
  )[];
  outreachAttempts?: OutreachAttempt[];
  notes?: Note[];
  appeals?: AppealResponse[];
  medicalNecessityDecision?: AuthStatus;
  oonExceptionDecision?: AuthStatus;
  restrictedDataSettings?: RestrictedDataSettings;
  isMemberRequestedAuth?: boolean;
  networkStatusOverride?: NetworkStatusOverride;
  isReadmission?: boolean;
  anchorAuthNumber?: string;
  readmissionReason?: "RELATED" | "COMPLICATION";
  sourceSystemLastUpdated?: string;
  authCategory?: AuthCategoryResponse;
  authSubcategory?: string;
  palCategory?: {
    name?: string;
  };
  lastUpdated?: string;
  /**
   * Indicates if dual plan decision is required for this authorization
   */
  requiresDualPlanDecision?: boolean;
}

export interface AuthorizationCommon {
  /**
   * authorization number, persists to authorization and all serviceRequests
   */
  authNumber?: string;
  /**
   * admission date
   */
  admissionDate?: string;
  /**
   * expected discharge date
   */
  expectedDischargeDate?: string;
  /**
   * expected admission date
   */
  expectedAdmissionDate?: string;
  /**
   * actual discharge date
   */
  actualDischargeDate?: string;
  /**
   * next review date
   */
  nextReviewDate?: string;
  orderingProvider?: Provider;
  performingProvider?: Provider;
  performingProviderLocation?: Location;
  /**
   * persists to all serviceRequests
   */
  performingProviderSelectedTin?: string;
  facility?: Facility;
  facilityLocation?: Location;
  /**
   * persists to all serviceRequests
   */
  facilitySelectedTin?: string;
  unitType?: UnitType;
  actualDischargeDateTime?: string;
}

export interface AuthorizationCreatePayload {
  /**
   * add existing serviceRequests to newly created authorization
   */
  serviceRequests?: ServiceRequestCreatePayload[];
  serviceRequestIds?: string[];
  patient?: string;
  healthPlanName?: string;
  type?: AuthorizationType;
  userSelectedOONException?: boolean;
  dischargedTo?: PatientDischargeStatus;
}

export type AuthorizationUpdatePayload = AuthorizationCommon & {
  id?: string;
  /**
   * add existing serviceRequests to authorization. Cannot belong to another authorization!
   */
  serviceRequestIds?: string[];
  /**
   * updates serviceRequests already on authorization
   */
  serviceRequests?: ServiceRequestUpdatePayload[];
  dischargedTo?: string;
  authStatus?: AuthStatus;
  /**
   * note to provider
   */
  authorizationNote?: string;
  /**
   * reason for withdrawal/void/dismissal
   */
  withdrawnReason?: string;
  withdrawRequestor?: WithdrawRequestor;
  workflow?: Workflow;
  patientStatus?: string;
  /**
   * indicates if dual plan decision is required for Medicare guidelines
   */
  requiresDualPlanDecision?: boolean;
};

/**
 * Used to describe and categorize auths, typically when these auths have no procedure codes
 */
export interface AuthCategoryResponse {
  enumName: AuthCategory;
  code?: string;
  name?: string;
  description?: string;
  isDefaultInpatientCategory?: boolean;
  subcategories?: string[];
}

/**
 * Used to describe and categorize auths, typically when these auths have no procedure codes
 */
export type AuthCategory =
  | "MEDICAL_CARE"
  | "SURGICAL"
  | "CONSULTATION"
  | "DIAGNOSTIC_XRAY"
  | "DIAGNOSTIC_LAB"
  | "RADIATION_THERAPY"
  | "ANESTHESIA"
  | "SURGICAL_ASSISTANCE"
  | "BLOOD"
  | "DURABLE_MEDICAL_EQUIPMENT_USED"
  | "DURABLE_MEDICAL_EQUIPMENT_PURCHASED"
  | "RENAL_SUPPLIES"
  | "PREADMISSION_TESTING"
  | "DURABLE_MEDICAL_EQUIPMENT_RENTAL"
  | "PNEUMONIA_VACCINE"
  | "SECOND_SURGICAL_OPINION"
  | "THIRD_SURGICAL_OPINION"
  | "SOCIAL_WORK"
  | "DIAGNOSTIC_DENTAL"
  | "PERIODONTICS"
  | "RESTORATIVE"
  | "ENDODONTICS"
  | "MAXILLOFACIAL_PROSTHETICS"
  | "ADJUNCTIVE_DENTAL_SERVICES"
  | "CHIROPRACTIC"
  | "DENTAL_CARE"
  | "DENTAL_CROWNS"
  | "ORTHODONTICS"
  | "PROSTHODONTICS"
  | "ORAL_SURGERY"
  | "PREVENTIVE_DENTAL"
  | "HOME_HEALTH_CARE"
  | "HOSPICE"
  | "RESPITE_CARE"
  | "LONG_TERM_CARE"
  | "LONG_TERM_ACUTE_CARE"
  | "MEDICALLY_RELATED_TRANSPORTATION"
  | "INVITRO_FERTILIZATION"
  | "MRI_SCAN"
  | "DONOR_PROCEDURES"
  | "ACUPUNCTURE"
  | "NEWBORN_CARE"
  | "PATHOLOGY"
  | "SMOKING_CESSATION"
  | "WELL_BABY_CARE"
  | "MATERNITY"
  | "TRANSPLANTS"
  | "AUDIOLOGY"
  | "INHALATION_THERAPY"
  | "DIAGNOSTIC_MEDICAL"
  | "PRIVATE_DUTY_NURSING"
  | "PROSTHETICS"
  | "DIALYSIS"
  | "OTOLOGY"
  | "CHEMOTHERAPY"
  | "ALLERGY_TESTING"
  | "IMMUNIZATIONS"
  | "ROUTINE_PHYSICAL"
  | "FAMILY_PLANNING"
  | "INFERTILITY"
  | "ABORTION"
  | "HIV_AIDS_TREATMENT"
  | "EMERGENCY_SERVICES"
  | "CANCER_TREATMENT"
  | "RETAIL_INDEPENDENT_PHARMACY"
  | "BRAND_NAME_PRESCRIPTION_DRUG"
  | "GENERIC_PRESCRIPTION_DRUG"
  | "PODIATRY"
  | "PSYCHIATRIC"
  | "PSYCHOTHERAPY"
  | "REHABILITATION"
  | "OCCUPATIONAL_THERAPY"
  | "PHYSICAL_MEDICINE"
  | "SPEECH_THERAPY"
  | "SKILLED_NURSING_CARE"
  | "SUBSTANCE_ABUSE"
  | "ALCOHOLISM_TREATMENT"
  | "DRUG_ADDICTION"
  | "OPTOMETRY"
  | "FRAMES"
  | "LENSES"
  | "ROUTINE_EYE_EXAM"
  | "NONMEDICALLY_NECESSARY_PHYSICAL_"
  | "EXPERIMENTAL_DRUG_THERAPY"
  | "BURN_CARE"
  | "INDEPENDENT_MEDICAL_EVALUATION"
  | "PSYCHIATRIC_TREATMENT_PARTIAL_HOSPITALIZATION"
  | "DAY_CARE_"
  | "COGNITIVE_THERAPY"
  | "MASSAGE_THERAPY"
  | "PULMONARY_REHABILITATION"
  | "CARDIAC_REHABILITATION"
  | "PEDIATRIC"
  | "NURSERY_ROOM_AND_BOARD"
  | "ORTHOPEDIC"
  | "CARDIAC"
  | "LYMPHATIC"
  | "GASTROINTESTINAL"
  | "ENDOCRINE"
  | "NEUROLOGY"
  | "GYNECOLOGICAL"
  | "OBSTETRICAL"
  | "CORONARY_CARE"
  | "SCREENING_XRAY"
  | "SCREENING_LABORATORY"
  | "MAMMOGRAM_HIGH_RISK_PATIENT"
  | "MAMMOGRAM_LOW_RISK_PATIENT"
  | "FLU_VACCINATION"
  | "EYEWEAR_ACCESSORIES"
  | "CASE_MANAGEMENT"
  | "DERMATOLOGY"
  | "DURABLE_MEDICAL_EQUIPMENT"
  | "DIABETIC_SUPPLIES"
  | "APPLIED_BEHAVIORAL_ANALYSIS_THERAPY"
  | "NONMEDICAL_EQUIPMENT_"
  | "PSYCHIATRIC_EMERGENCY"
  | "STEP_DOWN_UNIT"
  | "SKILLED_NURSING_FACILITY_HEAD_LEVEL_OF_CARE"
  | "SKILLED_NURSING_FACILITY_VENTILATOR_LEVEL_OF_CARE"
  | "LEVEL_OF_CARE_1"
  | "LEVEL_OF_CARE_2"
  | "LEVEL_OF_CARE_3"
  | "RADIOGRAPHS"
  | "DIAGNOSTIC_IMAGING"
  | "FIXED_PROSTHODONTICS"
  | "REMOVABLE_PROSTHODONTICS"
  | "INTRAORAL_IMAGES_COMPLETE_SERIES"
  | "ORAL_EVALUATION"
  | "DENTAL_PROPHYLAXIS"
  | "PANORAMIC_IMAGES"
  | "SEALANTS"
  | "FLUORIDE_TREATMENTS"
  | "DENTAL_IMPLANTS"
  | "TEMPOROMANDIBULAR_JOINT_DYSFUNCTION"
  | "LONG_TERM_CARE_PHARMACY"
  | "COMPREHENSIVE_MEDICATION_THERAPY_MANAGEMENT_REVIEW"
  | "TARGETED_MEDICATION_THERAPY_MANAGEMENT_REVIEW"
  | "DIETARY_NUTRITIONAL_SERVICES"
  | "TELEMEDICINE"
  | "PHARMACIST_SERVICES"
  | "DIABETIC_EDUCATION"
  | "EARLY_INTERVENTION"
  | "PREVENTIVE_SERVICES"
  | "SPECIALTY_PHARMACY"
  | "DURABLE_MEDICAL_EQUIPMENT_NEW"
  | "CAT_SCAN"
  | "OPHTHALMOLOGY"
  | "CONTACT_LENSES"
  | "ALLERGY"
  | "INTENSIVE_CARE"
  | "MENTAL_HEALTH"
  | "NEONATAL_INTENSIVE_CARE"
  | "ONCOLOGY"
  | "POSITRON_EMISSION_TOMOGRAPHY_SCAN"
  | "PHYSICAL_THERAPY"
  | "PULMONARY"
  | "RENAL"
  | "RESIDENTIAL_PSYCHIATRIC_TREATMENT"
  | "SERIOUS_MENTAL_HEALTH"
  | "TRANSITIONAL_CARE"
  | "TRANSITIONAL_NURSERY_CARE"
  | "URGENT_CARE";

/**
 * Predictions on fieldNames - uniqueness determined by source attachment, source model, and field name at hand
 */
export type SuggestedValue = StampedObject & {
  /**
   * unique SuggestedValue identifier
   */
  id?: string;
  /**
   * unique identifier for associated attachment
   */
  attachmentId: string;
  /**
   * the source attachment url
   */
  attachmentUrl?: string;
  /**
   * the OCR S3 output url
   */
  ocrOutputUrl?: string;
  /**
   * the page number from the source attachment this prediction was made from
   */
  pageNumber?: number;
  /**
   * height of the box the prediction was made in
   */
  relativeHeight?: number;
  /**
   * width of the box the prediction was made in
   */
  relativeWidth?: number;
  /**
   * The left-most coordinate of the box the prediction was made in
   */
  relativeLeft?: number;
  /**
   * The top-most coordinate of the box the prediction was made in
   */
  relativeTop?: number;
  /**
   * The source ML model that this prediction came from
   */
  source: string;
  /**
   * The version of the source ML model this prediction came from
   */
  sourceVersion: string;
  fieldName: SuggestedValueFieldName;
  /**
   * The value predicted for the field name the ML model made a prediction for
   */
  predictedValue?: string;
  /**
   * The confidence we have in the prediction
   */
  confidenceScore?: number;
  status?: SuggestedValueStatus;
  feedback?: SuggestedValueFeedback;
  /**
   * Denormalized field from associated Attachment to enforce multitenancy constraints
   */
  healthPlanName?: string;
  /**
   * Indicates if entity is sourced from a shadow deployed ML model
   */
  shadow?: boolean;
};

export type SuggestedValueFieldName =
  | "patientMemberId"
  | "patientName"
  | "patientFirstName"
  | "patientMiddleName"
  | "patientLastName"
  | "patientDateOfBirth"
  | "patientGender"
  | "patientSsn"
  | "patientMaritalStatus"
  | "patientAddress"
  | "patientMailingAddress"
  | "patientPhone"
  | "patientInsurer"
  | "patientLanguagePreference"
  | "orderingProviderName"
  | "orderingProviderNpi"
  | "orderingProviderSelectedTin"
  | "orderingProviderCreatedByGroup"
  | "orderingProviderPhoneNumber"
  | "orderingProviderFaxNumber"
  | "orderingProviderAddress"
  | "orderingProviderLocation"
  | "orderingProviderSource"
  | "orderingProviderProviderType"
  | "performingProviderName"
  | "performingProviderNpi"
  | "performingProviderSelectedTin"
  | "performingProviderCreatedByGroup"
  | "performingProviderPhoneNumber"
  | "performingProviderFaxNumber"
  | "performingProviderAddress"
  | "performingProviderLocation"
  | "performingProviderSource"
  | "performingProviderProviderType"
  | "performingProviderPracticeName"
  | "performingProviderPracticeNpi"
  | "performingProviderPracticeSelectedTin"
  | "facilityName"
  | "facilityNpi"
  | "facilitySelectedTin"
  | "facilityCreatedByGroup"
  | "facilityPhoneNumber"
  | "facilityFaxNumber"
  | "facilityAddress"
  | "facilityLocation"
  | "facilitySource"
  | "facilityProviderType"
  | "ehrVendor"
  | "semanticProcedureCode"
  | "secondarySemanticDiagnosisCode"
  | "placeOfServiceCode"
  | "serviceType"
  | "encounterType"
  | "startDate"
  | "endDate"
  | "units"
  | "approvedUnits"
  | "isExpedited"
  | "levelOfCare"
  | "admissionDate"
  | "expectedDischargeDate"
  | "authCategory"
  | "authSubcategory"
  | "behavioralHealthReviewType"
  | "behavioralHealthAdmissionType"
  | "palCategoryName"
  | "primarySemanticDiagnosisCode"
  | "requestorChannel"
  | "requestorFirstName"
  | "requestorLastName"
  | "requestorEmail"
  | "requestorPhoneNumber"
  | "requestorFaxNumber"
  | "requestorType"
  | "followUpFaxNumber"
  | "createdByGroup"
  | "createdByPhoneNumber"
  | "createdByName"
  | "createdByRole"
  | "vendorIdentifier"
  | "trackingNumber"
  | "dischargedTo"
  | "dischargeDate"
  | "dischargeDateTime";

export type SuggestedValueStatus = "RAW" | "VALID" | "INVALID";

export type SuggestedValueFeedback = "PENDING" | "ACCEPTED" | "REJECTED";

/**
 * Batch create response schema
 */
export interface BatchCreateSuggestedValueResponse {
  /**
   * unique identifier
   */
  id?: string;
  /**
   * whether or not the individual suggestedvalue was created
   */
  saved: boolean;
  /**
   * the attachment id
   */
  attachmentId?: string;
  /**
   * if the SuggestedValue was not saved, the error for why it didnt succeed
   */
  error?: string;
}

/**
 * Batch update feedback response schema
 */
export interface BatchUpdateSuggestedValueFeedbackResponse {
  /**
   * unique identifier
   */
  id?: string;
  /**
   * whether or not the individual suggestedvalue was created
   */
  updated: boolean;
  updatedFeedback?: SuggestedValueFeedback;
  /**
   * if the SuggestedValue was not updated, the error for why it didnt succeed
   */
  error?: string;
}

export type FeatureConfigurationLevel =
  | "HEALTH_PLAN"
  | "DELEGATED_VENDOR"
  | "SERVICE_REQUEST";

export interface AuthCategoryFieldMapping {
  fieldName?: string;
  fieldSpecification?: string;
  authCategory?: string[];
}

export type CoherePayerValueMappings = CoherePayerValueMapping[];

export interface CoherePayerValueMapping {
  cohereValue?: string;
  payerValue?: string;
}

export type AuthCategoryMappings = AuthCategoryMapping[];

export interface AuthCategoryMapping {
  cohereValue?: string;
  payerValues?: string[];
}

export interface ProcedureCodeGroupOptions {
  codes?: ProcedureCode[];
  categoryOptions?: ServiceDeterminationQuestionOption[];
  serviceOptions?: ServiceDeterminationQuestionOption[];
}

export interface ProcedureCodeGroupSelections {
  codes?: ProcedureCode[];
  selectedCategoryName?: string;
  selectedServicesIds?: string[];
}

export type MdNoteFeedback = StampedObject & {
  id?: string;
  reviewId?: string;
  serviceRequestID?: string;
  mdNote?: string;
  translatedMdNote?: string;
  submittedMdNote?: string;
  wasEdited?: boolean;
  procedureCodes?: string[];
  healthPlanName?: string;
  modelVersion?: number;
  modelTrainedDate?: string;
  reviewCompleteDate?: string;
  wasCustomLetterUsed?: boolean;
  wasMdNoteEditedInCustomLetter?: boolean;
  sanitizedMdNote?: string;
};

export type PreferredLabelFeedback = StampedObject & {
  code?: string;
  system?: string;
  serviceRequestId?: string;
  reviewId?: string;
  description?: string;
  preferredLabel?: string;
  submittedPreferredLabel?: string;
  wasEdited?: boolean;
  username?: string;
};

/**
 * Options for setting diagnosis code filters for Convene rules.
 */
export type DiagnosisCodeFilterType =
  | "PRIMARY_DIAGNOSIS_CODES"
  | "SECONDARY_DIAGNOSIS_CODES";

export interface BulkSavePreferredLabelFeedbackResponse {
  saved?: boolean;
  payload?: PreferredLabelFeedback;
  error?: string;
}

export interface EditedPreferredLabel {
  code?: string;
  label?: string;
}

export interface AbstractSort {
  key?: string;
  sortOrder?: "ASC" | "DESC";
}

export type AbstractOperator =
  | "eq"
  | "neq"
  | "gt"
  | "lt"
  | "gte"
  | "lte"
  | "inlist"
  | "ninlist";

export type Clause = "MUST" | "SHOULD," | "MUST_NOT";

export interface GenericCondition {
  key: string;
  operator: AbstractOperator;
  value: string | string[] | number | boolean;
}

export interface ClausalCondition {
  clause: Clause;
  conditions: GenericCondition[];
}

export interface ActuatorInfo {
  app?: AppDetails;
  git?: GitDetails;
}

export interface AppDetails {
  name?: string;
  version?: string;
  grailsVersion?: string;
}

export interface FeatureConfigurationVersion {
  versionNumber?: number;
  updatedBy?: string;
  lastUpdated?: string;
  updateReason?: string;
  configuration?: string;
}

export interface GitDetails {
  branch?: string;
  commit?: CommitDetails;
}

export interface CommitDetails {
  id?: string;
  time?: string;
}

export interface BulkOperationProviderTagMappingsBody {
  mappings?: ProviderTagMapping[];
  providerTag?: string;
}

export type BulkUpdateProviderTagMappingsResponse =
  BulkAddProviderTagMappingsResponse & BulkRemoveProviderTagMappingsResponse;

export interface BulkAddProviderTagMappingsResponse {
  tinsAdded?: number;
  npisAdded?: number;
  tagNameExistedAlready?: boolean;
  tagName?: string;
}

export interface BulkRemoveProviderTagMappingsResponse {
  tinsRemoved?: number;
  npisRemoved?: number;
  tagName?: string;
}

export interface ServiceChangeOption {
  id?: string;
  label?: string;
  procedureCode?: ProcedureCode;
  units?: number;
}

export type ReferralRequestWorflowStep =
  | "REFERRAL_REQUEST_DETAILS"
  | "REFERRAL_REQUEST_REVIEW";

export type ReferralStatus = "DRAFT" | "PENDING" | "APPROVED" | "DENIED";

export type CohereWorkflow = "AUTHORIZATIONS" | "REFERRALS";

export interface ReferralEligibility {
  delegated?: boolean;
  referralRequired?: boolean;
}

/**
 * This object will be used create/update payload for the request
 */
export interface ReferralRequestCommon {
  id?: string;
  cohereId?: string;
  authNumber?: string;
  manualAuthNumberEntry?: boolean;
  healthPlanName?: string;
  isAdministrative?: boolean;
  referralStatus?: ReferralStatus;
  requestor?: Requestor;
  startDate?: string;
  endDate?: string;
  authorization?: {
    id?: string;
  };
  selectedReferringProvider?: Provider | null;
  referringProviderSelectedLocation?: Location | null;
  referringProviderSelectedTin?: string | null;
  selectedPerformingSpecialist?: Provider | null;
  performingSpecialistSelectedLocation?: Location | null;
  performingSpecialistSelectedTin?: string | null;
  selectedFacility?: Facility | null;
  facilitySelectedLocation?: Location | null;
  facilitySelectedTin?: string | null;
  specialty?: string;
  primarySemanticDiagnosisCode?: DiagnosisCode;
  secondarySemanticDiagnosisCodes?: DiagnosisCode[];
  intakeTimestamp?: string;
  userUpdatedTat?: boolean | null;
}

/**
 * This object will be expand important fields of a referral
 */
export interface ReferralRequestExpandedFields {
  patient?: Patient;
  attachments?: Attachment[];
}

/**
 * This object just stores the foreign key of embedded entities
 */
export interface ReferralRequestFieldIds {
  patient?: string;
}

/**
 * This object will be returned as a response in create/update/submit APIs
 */
export type ReferralRequestResponse = ReferralRequestCommon &
  ReferralRequestExpandedFields &
  StandardFields & {
    integrationStatus?: IntegrationStatus;
    integration?: {
      salesforce?: ExternalIntegration;
      matrix?: ExternalIntegration;
    };
    healthPlanDisplayName?: string;
    healthPlanName?: string;
    createdBy?: string;
    createdByName?: string;
    createdByGroups?: string[];
  };

/**
 * This is a partial payload to create a new referral request
 */
export type ReferralRequestCreatePayload = ReferralRequestCommon &
  ReferralRequestFieldIds;

export interface ReferralRequestUpdateErrorResponse {
  data?: {
    message?: string;
  };
  message?: string;
}

/**
 * This has speciality name and type of specialty
 */
export interface ReferraRequestSpecialty {
  name?: string;
  medicalOrBehavioral?: string;
}

export interface ReviewChatbotConfigurationPrompt {
  modelQuestion?: string;
  displayedQuestion?: string;
}

export interface ReviewChatbotFinding {
  /**
   * Unique identifier for the chatbot finding record
   */
  findingRecordId?: string;
  /**
   * Unique identifier for the chatbot finding type
   */
  findingTypeId?: string;
  /**
   * Unique identifier for the chatbot finding type name
   */
  findingTypeName?: string;
  /**
   * Timestamp when the finding was created
   */
  findingInstant?: string;
  /**
   * ID of the associated service request
   */
  serviceRequestId?: string;
  /**
   * The extracted value from the chatbot finding
   */
  value?: string | null;
  /**
   * Prompt question used to call ml model
   */
  modelQuestion?: string | null;
  /**
   * Prompt displayed question used in frontend review assistant
   */
  displayedQuestion?: string | null;
  /**
   * The attachment sources linked to the answer
   */
  sources?: ReviewChatbotFindingSource[] | null;
  /**
   * User feedback on whether the finding was helpful
   */
  helpfulnessFeedback?: boolean;
  /**
   * User feedback on whether the finding was helpful and gave comments
   */
  helpfulnessFeedbackComments?: string[];
  /**
   * Whether the finding was manually input
   */
  manuallyInputPrompt?: boolean;
  /**
   * ID of the associated review
   */
  reviewId?: string;
  /**
   * Whether the user prompt hit a chatbot guardrail
   */
  guardrailsActivated?: boolean;
  /**
   * Reason code for stopping the chatbot response
   */
  stopReason?: string | null;
  /**
   * Reason label for stopping the chatbot response
   */
  stopReasonLabel?: string | null;
  /**
   * ID of the chatbot session
   */
  sessionId?: string | null;
  /**
   * ID used to track the interaction of the finding
   */
  traceId?: string | null;
  /**
   * Last Span ID
   */
  lastSpanId?: string | null;
  /**
   * Whether this is a click to answer FAQ or a normal use case of an online question/answer
   */
  isClickToAskQuestion?: boolean;
  /**
   * Whether the finding has encountered an error when generating the response
   */
  hasErrored?: boolean | null;
  /**
   * Details about the error encountered
   */
  errorDetails?: string | null;
  /**
   * Whether the streaming response was interrupted
   */
  wasStreamingInterrupted?: boolean | null;
  /**
   * Last answer before streaming was interrupted
   */
  streamingInterruptedWithAnswer?: string | null;
  /**
   * Timestamp when the question was asked
   */
  askedAt?: string | null;
  /**
   * Guardrail ID if the finding hit a guardrail
   */
  guardrailId?: string | null;
  /**
   * Guardrail first topic name if the finding hit a guardrail
   */
  guardrailFirstTopicName?: string | null;
  /**
   * All Guardrail topic names if the finding hit a guardrail
   */
  guardrailTopicNames?: string | null;
  /**
   * Time taken for guardrail validation
   */
  guardrailValidationTime?: number | null;
  /**
   * Guardrail action if the finding hit a guardrail
   */
  guardrailAction?: string | null;
  /**
   * Timestamp of the first streamed chunk
   */
  instantOfFirstStreamedChunk?: string | null;
}

export interface ReviewChatbotFindingSource {
  attachment_id?: string | null;
  page?: number | null;
  score?: number | null;
  span_text?: string | null;
  global_line_num?: number | null;
  first_last_ocr_coordinates?: ReviewChatbotFindingSourceFirstLastOcrCoordinates;
}

export type ReviewChatbotFindingSourceFirstLastOcrCoordinates = {
  first_token?: ReviewChatbotFindingSourceOcrCoordinatesToken;
  last_token?: ReviewChatbotFindingSourceOcrCoordinatesToken;
} | null;

export type ReviewChatbotFindingSourceOcrCoordinatesToken = {
  left?: number | null;
  top?: number | null;
  width?: number | null;
  height?: number | null;
  page_num?: number | null;
  level?: number | null;
  block_num?: number | null;
  par_num?: number | null;
  line_num?: number | null;
  word_num?: number | null;
  conf?: number | null;
  text?: string | null;
} | null;

export interface ReviewChatbotAnswerResponse {
  traceId?: string;
  lastSpanId?: string;
  answer?: string;
  user_question?: string;
  response_metadata?: {
    stopReason?: string | null;
  };
  sources?: {
    attachment_id?: string;
    page?: number;
    score?: number;
    span_text?: string;
  }[];
}

export interface ReviewChatbotApiResponse {
  responses?: ReviewChatbotAnswerResponse[];
}

/**
 * This is the guideline name and possible content title options for an MCG guideline we are planning to import
 */
export interface MCGGuidelineInformation {
  guidelineTitle?: string;
  contentTitles?: string[];
}

export interface AuthStatusUpdatePayload {
  reviewId?: string;
  cohereId?: string;
  correctionReason?: string;
  actionType?: "MOVE_FORWARD" | "MOVE_BACKWARD";
  pendingReason?: string;
  authStatus?: string;
  matrixIntegrationStatus?: string;
  nextReviewDate?: string;
  nextReviewDateAcknowledged?: boolean;
}

export interface ReviewSendLettersPayload {
  reviewId?: string;
  correctionReason?: string;
}

/**
 * example of success response for send finalDetermination letters
 */
export interface SendFinalDeterminationLetterResponse {
  notifications?: {
    id?: string;
    statusList?: string[];
    dateCreated?: string;
    updatedBy?: string;
    healthPlanName?: string;
    letterTemplateTitle?: string;
    mailVendor?: string;
    purpose?: string;
    context?: {
      templateFields?: {
        Id?: string;
        PerformingProviderName?: string;
        PerformingProviderAddress1?: string;
        PerformingProviderAddress2?: string;
        PerformingProviderCity?: string;
        PerformingProviderState?: string;
        PerformingProviderZipCode?: string;
        PatientAddress1?: string;
        PatientCity?: string;
        PatientState?: string;
        PatientZipCode?: string;
        PatientName?: string;
        PatientFirstName?: string;
        PatientMiddleName?: string;
        PatientLastName?: string;
        PatientDateOfBirth?: string;
        clinicalServiceName?: string;
      };
    };
    recipient?: string;
    riskBearingEntity?: string;
    dryRun?: boolean;
    lastUpdated?: string;
    serviceRequestId?: string;
    createdBy?: string;
    status?: string;
    notificationType?: string;
  }[];
}

/**
 * A key value pair between single-sign-on(SSO) codes and the display names they correspond to within a given health plan
 */
export interface SsoMappingEntry {
  ssoCode?: string;
  displayName?: string;
}

/**
 * Referral management configuration object
 */
export interface ReferralManagementFormConfiguration {
  formFieldConfigurations?: {
    [key: string]: ReferralConfiguration;
  } | null;
}

/**
 * Config object for referral fields
 */
export interface ReferralConfiguration {
  name?: string;
  fieldSpec: FormConfigurationFieldSpec;
  dependency?: ReferralFieldDependency;
  networkMessage?: NetworkMessage;
  reasons?: string[];
}

export interface ReferralFieldDependency {
  name: string;
  fieldSpec: FormConfigurationFieldSpec;
  value: string;
}

export interface NetworkMessage {
  inNetworkMessage?: string;
  outOfNetworkMessage?: string;
  unknownMessage?: string;
}

/**
 * The response of a manually triggered async rule run
 */
export interface ManualRuleRunSummary {
  date?: string;
  runStatus?: string;
  unique?: number;
  totalCount?: number;
  processed?: number;
  success?: number;
  error?: number;
  notFound?: number;
  skipped?: number;
  requestedBy?: string;
  notes?: string;
  serviceRequestResults?: any[];
}

/**
 * The initial and final auth status of a service request after a manual rule run
 */
export interface ManualRuleRunServiceRequestResult {
  serviceRequestId?: string;
  initialStatus?: string;
  finalStatus?: string;
  healthPlan?: string;
  status?: string;
  runDetails?: string;
}

export interface ServiceRequestContactInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  faxNumber?: PhoneNumber;
}

export interface CarewebqiUpdateResponse {
  success?: boolean;
  serviceRequestId?: string;
  patientId?: string;
  authStatus?: string;
}

export interface OutboundFax {
  id?: string;
  dateCreated?: string;
  lastUpdated?: string;
  size?: {};
  updatedBy?: string;
  serviceCaseId?: string;
  recipientName?: string;
  url?: string;
  faxNumber?: string;
  createdBy?: string;
  sendStatus?: IntegrationStatus;
}

export interface DraftPatientStayDetails {
  patientStatus?: PatientStatus;
  expectedAdmissionDate?: string;
  admissionDate?: string;
  admissionTime?: string;
  admissionSource?: AdmissionSourceResponse;
  admissionSourceEnum?: string;
  expectedDischargeDate?: string;
  dischargeDate?: string;
  dischargeTime?: string;
  dischargedTo?: PatientDischargeStatusResponse;
  dischargedToEnum?: string;
  isExpedited?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface ReviewChatbotMetadata {
  enabled?: boolean;
  queryable?: boolean;
  notQueryableReason?: "NO_ATTACHMENTS" | "ATTACHMENTS_NOT_ANALYZED";
  disabledForEfficiencyTesting?: boolean;
}

/**
 * Represents the AdvanceNotification object
 */
export type AdvanceNotification = StampedObject & {
  id: string;
  cohereId?: string;
  authNumber?: string;
  patientId: string;
  patient?: Patient;
  advanceNotificationStatus: AdvanceNotificationStatus;
  healthPlanName: string;
  healthPlanDisplayName?: string;
  delegatedVendorName?: string;
  requestor?: Requestor;
  createdByOrganization?: string;
  createdByPhoneNumber?: string;
  createdByRoles?: string[];
  createdByGroups?: string[];
  createdByName?: string;
  serviceRequestContactInfo?: ServiceRequestContactInfo;
  primarySemanticDiagnosisCode?: DiagnosisCode;
  secondarySemanticDiagnosisCodes?: DiagnosisCode[];
  semanticProcedureCodes?: ProcedureCode[];
  nonPxClinicalServiceIds?: string[];
  clinicalServiceIds?: string[];
  encounterType?: FacilityCategory;
  manualAuthNumberEntry?: boolean;
  patientCoverageAtSubmissionTime?: Coverage;
  patientStayDates?: PatientStayDate[];
  placeOfService?: PlaceOfService;
  serviceRequestPalCategory?: string;
  crdLogId?: string;
  patientStatus?: PatientStatus;
  specialty?: string;
  selectedOrderingProvider?: Provider | null;
  selectedPerformingProvider?: Provider | null;
  selectedPerformingProviderPractice?: Facility | null;
  selectedFacility?: Facility | null;
  additionalCareParticipants?: AdditionalCareParticipant[] | null;
  units?: number;
  authCategory?: AuthCategoryResponse;
  authSubcategory?: string;
  admissionDate?: string;
  expectedDischargeDate?: string;
  expectedAdmissionDate?: string;
  startDate?: string;
  endDate?: string;
  intakeTimestamp?: string;
  dischargeDate?: string;
  requestTiming?: RequestTiming;
  requestClassification?: RequestTiming;
  levelOfCare?: LevelOfCare;
  palCategory?: PALCategory;
  userSelectedOONException?: boolean;
  contractType?: string;
  formConfiguration?: {
    [key: string]: {
      fieldSpec: FormConfigurationFieldSpec;
    };
  };
  clinicalServices?: (ClinicalService | null)[];
  workflowStep?: AdvanceNotificationWorkflowStep;
};

export type AdvanceNotificationStatus = "DRAFT" | "SUBMITTED";

/**
 * This object will be returned as a response in create/update/submit APIs
 */
export type AdvanceNotificationResponse = AdvanceNotification & {
  attachments?: Attachment[];
};

/**
 * This is a partial payload to create a new advance notification
 */
export type AdvanceNotificationCreatePayload = AdvanceNotification;

export interface AdvanceNotificationUpdateErrorResponse {
  data?: {
    message?: string;
  };
  message?: string;
}

export interface AdvanceNotificationSubmitErrorResponse {
  data?: {
    message?: string;
  };
  message?: string;
}

export type AdvanceNotificationWorkflowStep = "ATTACHMENTS" | "PRE_SUBMISSION";

export interface AssociatedTemporaryPatient {
  patientId?: string;
  memberId?: string;
  createdAssociationTime?: string;
}

export interface ReviewAiInsight {
  insightId?: string;
  insightType?: string;
  insightName?: string;
  insightData?: { [key: string]: any };
}

export interface ReviewAiInsightCondition {
  id: string;
  label: string;
}

export interface ReviewAiInsightsResponse {
  insights?: ReviewAiInsight[];
}

export interface ReviewAiInsightSupportedConditionsResponse {
  supportedConditions?: ReviewAiInsightCondition[];
}

export interface ReviewAiInsightSummaryRequest {
  /**
   * ID of the entity to summarize insights for.
   */
  entityId: string;
  /**
   * Entity type (e.g., "ServiceRequest").
   */
  entityType: string;
}

export interface ReviewAIMetadata {
  serviceRequest: string;
  review: string;
  healthPlanName?: string;
  delegatedVendorName?: string;
  sessionId?: string;
  userId?: string;
  type: ReviewAIMetadataType;
  clinicalEvaluationConditionSelectionMetadata?: ClinicalEvaluationConditionSelectionMetadata;
}

export type ReviewAIMetadataType = "CLINICAL_EVALUATION_CONDITION_SELECTION";

export interface ClinicalEvaluationConditionSelectionMetadata {
  lastSelectedConditionId?: string;
  lastSelectedConditionLabel?: string;
  lastSelectionType?: string;
  availableConditions?: ReviewAIClinicalEvaluationConditionSelectionMetadataAvailableConditionItem[];
  suggestedConditions?: ReviewAIClinicalEvaluationConditionSelectionMetadataSuggestedConditionItem[];
  conditionSelectionHistory?: ReviewAIClinicalEvaluationConditionSelectionMetadataHistoryItem[];
}

export interface ReviewAIClinicalEvaluationConditionSelectionMetadataAvailableConditionItem {
  conditionId?: string;
  conditionLabel?: string;
}

export interface ReviewAIClinicalEvaluationConditionSelectionMetadataSuggestedConditionItem {
  conditionId?: string;
  conditionLabel?: string;
  rank?: number;
  reason?: string;
}

export interface ReviewAIClinicalEvaluationConditionSelectionMetadataHistoryItem {
  conditionId?: string;
  conditionLabel?: string;
  selectionType?: ReviewAIClinicalEvaluationConditionSelectionSelectionType;
  selectedAt?: string;
}

export type ReviewAIClinicalEvaluationConditionSelectionSelectionType =
  | "AUTO_SELECTED"
  | "USER_SELECTED";

export interface ReviewAiInsightClinicalRequest {
  /**
   * ID of the entity to summarize insights for.
   */
  entityId: string;
  /**
   * Entity type (e.g., "ServiceRequest").
   */
  entityType: string;
  /**
   * Framework to be used (e.g., "ACS").
   */
  framework?: string | null;
}

export interface ReviewAiInsightSupportedConditionsRequest {
  /**
   * ID of the entity to summarize insights for.
   */
  entityId: string;
  /**
   * Entity type (e.g., "ServiceRequest").
   */
  entityType: string;
}

/**
 * Request payload for initiating machine learning training for a ServiceRequestRule
 */
export interface TrainingRequest {
  /**
   * Training start date in ISO date format (YYYY-MM-DD).
   * Must be a date in the past.
   *
   */
  trainingStartDate: string;
}

/**
 * Response payload for successful training request submission
 */
export interface TrainingRequestResponse {
  /**
   * Success message confirming the training request was submitted
   */
  message: string;
}

export interface ClinicalServiceUnitsConfiguration {
  clinicalServiceName?: string;
  clinicalServiceId?: string;
  defaultUnitsSpecification?: DefaultUnitsSpecification;
  procedureCodeDefaultUnitsConfigurations?: ProcedureCodeDefaultUnitsConfiguration[];
}

export interface ProcedureCodeDefaultUnitsConfiguration {
  procedureCodes?: string[];
  defaultUnitsSpecification?: DefaultUnitsSpecification;
}

export interface DefaultUnitsSpecification {
  isEditable?: boolean;
  defaultUnits?: number | null;
  maxUnits?: number | null;
  minUnits?: number | null;
}

export interface ClinicalServiceUnits {
  originalClinicalServiceId?: string;
  units?: number;
  approvedUnits?: number;
  episodeUnit?: {
    episodesRequested?: number;
    unitsPerEpisode?: number;
  };
}

export interface EpisodeUnitsConfiguration {
  placeOfServiceId?: string;
  unitsPerEpisode?: number;
}

/**
 * {
 *     "errorCode": "String",
 *     "errorDescription": "String"
 * }
 *
 */
export type ExampleErrorResponseResponse = void;

export type GetHealthcheckProps = Omit<
  GetProps<void, unknown, void, void>,
  "path"
>;

export const GetHealthcheck = (props: GetHealthcheckProps) => (
  <Get<void, unknown, void, void> path={`/healthcheck`} {...props} />
);

export type UseGetHealthcheckProps = Omit<
  UseGetProps<void, unknown, void, void>,
  "path"
>;

export const useGetHealthcheck = (props: UseGetHealthcheckProps) =>
  useGet<void, unknown, void, void>(`/healthcheck`, props);

export interface GetResubmissionDataByServiceRequestIdPathParams {
  /**
   * The ID of the service request to retrieve resubmission data for
   */
  serviceRequestId: string;
}

export type GetResubmissionDataByServiceRequestIdProps = Omit<
  GetProps<
    ResubmissionDataResponse,
    void,
    void,
    GetResubmissionDataByServiceRequestIdPathParams
  >,
  "path"
> &
  GetResubmissionDataByServiceRequestIdPathParams;

/**
 * Get resubmission data for a service request
 *
 * Gets all resubmission data for a given service request ID including parent, child, and sibling resubmissions. This endpoint checks if resubmission tracking is enabled via feature configuration before proceeding.
 */
export const GetResubmissionDataByServiceRequestId = ({
  serviceRequestId,
  ...props
}: GetResubmissionDataByServiceRequestIdProps) => (
  <Get<
    ResubmissionDataResponse,
    void,
    void,
    GetResubmissionDataByServiceRequestIdPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/resubmissionData`}
    {...props}
  />
);

export type UseGetResubmissionDataByServiceRequestIdProps = Omit<
  UseGetProps<
    ResubmissionDataResponse,
    void,
    void,
    GetResubmissionDataByServiceRequestIdPathParams
  >,
  "path"
> &
  GetResubmissionDataByServiceRequestIdPathParams;

/**
 * Get resubmission data for a service request
 *
 * Gets all resubmission data for a given service request ID including parent, child, and sibling resubmissions. This endpoint checks if resubmission tracking is enabled via feature configuration before proceeding.
 */
export const useGetResubmissionDataByServiceRequestId = ({
  serviceRequestId,
  ...props
}: UseGetResubmissionDataByServiceRequestIdProps) =>
  useGet<
    ResubmissionDataResponse,
    void,
    void,
    GetResubmissionDataByServiceRequestIdPathParams
  >(
    (paramsInPath: GetResubmissionDataByServiceRequestIdPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/resubmissionData`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetAdmissionCategoryMappingConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetAdmissionCategoryMappingConfigurationProps = Omit<
  MutateProps<
    AdmissionCategoryMappingConfigurationResponse,
    unknown,
    GetAdmissionCategoryMappingConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetAdmissionCategoryMappingConfiguration = (
  props: GetAdmissionCategoryMappingConfigurationProps
) => (
  <Mutate<
    AdmissionCategoryMappingConfigurationResponse,
    unknown,
    GetAdmissionCategoryMappingConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/admissionCategoryMappingConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetAdmissionCategoryMappingConfigurationProps = Omit<
  UseMutateProps<
    AdmissionCategoryMappingConfigurationResponse,
    unknown,
    GetAdmissionCategoryMappingConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetAdmissionCategoryMappingConfiguration = (
  props: UseGetAdmissionCategoryMappingConfigurationProps
) =>
  useMutate<
    AdmissionCategoryMappingConfigurationResponse,
    unknown,
    GetAdmissionCategoryMappingConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/admissionCategoryMappingConfiguration/configureByHealthPlan`,
    props
  );

export interface GetLevelOfCareMappingConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetLevelOfCareMappingConfigurationProps = Omit<
  MutateProps<
    LevelOfCareMappingConfigurationResponse,
    unknown,
    GetLevelOfCareMappingConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetLevelOfCareMappingConfiguration = (
  props: GetLevelOfCareMappingConfigurationProps
) => (
  <Mutate<
    LevelOfCareMappingConfigurationResponse,
    unknown,
    GetLevelOfCareMappingConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/levelOfCareMappingConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetLevelOfCareMappingConfigurationProps = Omit<
  UseMutateProps<
    LevelOfCareMappingConfigurationResponse,
    unknown,
    GetLevelOfCareMappingConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetLevelOfCareMappingConfiguration = (
  props: UseGetLevelOfCareMappingConfigurationProps
) =>
  useMutate<
    LevelOfCareMappingConfigurationResponse,
    unknown,
    GetLevelOfCareMappingConfigurationQueryParams,
    void,
    void
  >("POST", `/levelOfCareMappingConfiguration/configureByHealthPlan`, props);

export interface GetFeatureConfigurationResponse {
  [key: string]: any;
}

export interface GetFeatureConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export interface GetFeatureConfigurationPathParams {
  configuration: string;
}

export type GetFeatureConfigurationProps = Omit<
  MutateProps<
    GetFeatureConfigurationResponse,
    unknown,
    GetFeatureConfigurationQueryParams,
    void,
    GetFeatureConfigurationPathParams
  >,
  "path" | "verb"
> &
  GetFeatureConfigurationPathParams;

export const GetFeatureConfiguration = ({
  configuration,
  ...props
}: GetFeatureConfigurationProps) => (
  <Mutate<
    GetFeatureConfigurationResponse,
    unknown,
    GetFeatureConfigurationQueryParams,
    void,
    GetFeatureConfigurationPathParams
  >
    verb="POST"
    path={`/${configuration}/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetFeatureConfigurationProps = Omit<
  UseMutateProps<
    GetFeatureConfigurationResponse,
    unknown,
    GetFeatureConfigurationQueryParams,
    void,
    GetFeatureConfigurationPathParams
  >,
  "path" | "verb"
> &
  GetFeatureConfigurationPathParams;

export const useGetFeatureConfiguration = ({
  configuration,
  ...props
}: UseGetFeatureConfigurationProps) =>
  useMutate<
    GetFeatureConfigurationResponse,
    unknown,
    GetFeatureConfigurationQueryParams,
    void,
    GetFeatureConfigurationPathParams
  >(
    "POST",
    (paramsInPath: GetFeatureConfigurationPathParams) =>
      `/${paramsInPath.configuration}/configureByHealthPlan`,
    { pathParams: { configuration }, ...props }
  );

export interface GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationProps =
  Omit<
    MutateProps<
      PatientCoverageRiskBearingEntityEditsAllowedConfigurationResponse,
      unknown,
      GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationQueryParams,
      void,
      void
    >,
    "path" | "verb"
  >;

export const GetPatientCoverageRiskBearingEntityEditsAllowedConfiguration = (
  props: GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationProps
) => (
  <Mutate<
    PatientCoverageRiskBearingEntityEditsAllowedConfigurationResponse,
    unknown,
    GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/patientCoverageRiskBearingEntityEditsAllowedConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetPatientCoverageRiskBearingEntityEditsAllowedConfigurationProps =
  Omit<
    UseMutateProps<
      PatientCoverageRiskBearingEntityEditsAllowedConfigurationResponse,
      unknown,
      GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationQueryParams,
      void,
      void
    >,
    "path" | "verb"
  >;

export const useGetPatientCoverageRiskBearingEntityEditsAllowedConfiguration = (
  props: UseGetPatientCoverageRiskBearingEntityEditsAllowedConfigurationProps
) =>
  useMutate<
    PatientCoverageRiskBearingEntityEditsAllowedConfigurationResponse,
    unknown,
    GetPatientCoverageRiskBearingEntityEditsAllowedConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/patientCoverageRiskBearingEntityEditsAllowedConfiguration/configureByHealthPlan`,
    props
  );

export interface GetFacilityBasedRequestConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetFacilityBasedRequestConfigurationProps = Omit<
  MutateProps<
    FacilityBasedRequestConfigurationResponse,
    unknown,
    GetFacilityBasedRequestConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetFacilityBasedRequestConfiguration = (
  props: GetFacilityBasedRequestConfigurationProps
) => (
  <Mutate<
    FacilityBasedRequestConfigurationResponse,
    unknown,
    GetFacilityBasedRequestConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/facilityBasedRequestConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetFacilityBasedRequestConfigurationProps = Omit<
  UseMutateProps<
    FacilityBasedRequestConfigurationResponse,
    unknown,
    GetFacilityBasedRequestConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetFacilityBasedRequestConfiguration = (
  props: UseGetFacilityBasedRequestConfigurationProps
) =>
  useMutate<
    FacilityBasedRequestConfigurationResponse,
    unknown,
    GetFacilityBasedRequestConfigurationQueryParams,
    void,
    void
  >("POST", `/facilityBasedRequestConfiguration/configureByHealthPlan`, props);

export interface GetRequestStatusConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetRequestStatusConfigurationProps = Omit<
  MutateProps<
    RequestStatusConfiguration,
    unknown,
    GetRequestStatusConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetRequestStatusConfiguration = (
  props: GetRequestStatusConfigurationProps
) => (
  <Mutate<
    RequestStatusConfiguration,
    unknown,
    GetRequestStatusConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/requestStatusConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetRequestStatusConfigurationProps = Omit<
  UseMutateProps<
    RequestStatusConfiguration,
    unknown,
    GetRequestStatusConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetRequestStatusConfiguration = (
  props: UseGetRequestStatusConfigurationProps
) =>
  useMutate<
    RequestStatusConfiguration,
    unknown,
    GetRequestStatusConfigurationQueryParams,
    void,
    void
  >("POST", `/requestStatusConfiguration/configureByHealthPlan`, props);

export interface GetSubmissionConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetSubmissionConfigurationProps = Omit<
  MutateProps<
    SubmissionConfigurationResponse,
    unknown,
    GetSubmissionConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetSubmissionConfiguration = (
  props: GetSubmissionConfigurationProps
) => (
  <Mutate<
    SubmissionConfigurationResponse,
    unknown,
    GetSubmissionConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/submissionConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetSubmissionConfigurationProps = Omit<
  UseMutateProps<
    SubmissionConfigurationResponse,
    unknown,
    GetSubmissionConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetSubmissionConfiguration = (
  props: UseGetSubmissionConfigurationProps
) =>
  useMutate<
    SubmissionConfigurationResponse,
    unknown,
    GetSubmissionConfigurationQueryParams,
    void,
    void
  >("POST", `/submissionConfiguration/configureByHealthPlan`, props);

export interface GetOtherInsuranceConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetOtherInsuranceConfigurationProps = Omit<
  MutateProps<
    OtherInsuranceConfigurationResponse,
    unknown,
    GetOtherInsuranceConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetOtherInsuranceConfiguration = (
  props: GetOtherInsuranceConfigurationProps
) => (
  <Mutate<
    OtherInsuranceConfigurationResponse,
    unknown,
    GetOtherInsuranceConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/OtherInsuranceConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetOtherInsuranceConfigurationProps = Omit<
  UseMutateProps<
    OtherInsuranceConfigurationResponse,
    unknown,
    GetOtherInsuranceConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetOtherInsuranceConfiguration = (
  props: UseGetOtherInsuranceConfigurationProps
) =>
  useMutate<
    OtherInsuranceConfigurationResponse,
    unknown,
    GetOtherInsuranceConfigurationQueryParams,
    void,
    void
  >("POST", `/OtherInsuranceConfiguration/configureByHealthPlan`, props);

export interface GetNonPalCheckboxConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetNonPalCheckboxConfigurationProps = Omit<
  MutateProps<
    NonPalCheckboxConfigurationResponse,
    unknown,
    GetNonPalCheckboxConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetNonPalCheckboxConfiguration = (
  props: GetNonPalCheckboxConfigurationProps
) => (
  <Mutate<
    NonPalCheckboxConfigurationResponse,
    unknown,
    GetNonPalCheckboxConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/NonPalCheckboxConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetNonPalCheckboxConfigurationProps = Omit<
  UseMutateProps<
    NonPalCheckboxConfigurationResponse,
    unknown,
    GetNonPalCheckboxConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetNonPalCheckboxConfiguration = (
  props: UseGetNonPalCheckboxConfigurationProps
) =>
  useMutate<
    NonPalCheckboxConfigurationResponse,
    unknown,
    GetNonPalCheckboxConfigurationQueryParams,
    void,
    void
  >("POST", `/NonPalCheckboxConfiguration/configureByHealthPlan`, props);

export interface GetSalesforceIntegrationConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetSalesforceIntegrationConfigurationProps = Omit<
  MutateProps<
    SalesforceIntegrationConfigurationResponse,
    unknown,
    GetSalesforceIntegrationConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetSalesforceIntegrationConfiguration = (
  props: GetSalesforceIntegrationConfigurationProps
) => (
  <Mutate<
    SalesforceIntegrationConfigurationResponse,
    unknown,
    GetSalesforceIntegrationConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/SalesforceConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetSalesforceIntegrationConfigurationProps = Omit<
  UseMutateProps<
    SalesforceIntegrationConfigurationResponse,
    unknown,
    GetSalesforceIntegrationConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetSalesforceIntegrationConfiguration = (
  props: UseGetSalesforceIntegrationConfigurationProps
) =>
  useMutate<
    SalesforceIntegrationConfigurationResponse,
    unknown,
    GetSalesforceIntegrationConfigurationQueryParams,
    void,
    void
  >("POST", `/SalesforceConfiguration/configureByHealthPlan`, props);

export interface GetScrubFieldsFromResponsesConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetScrubFieldsFromResponsesConfigurationProps = Omit<
  MutateProps<
    ScrubFieldsFromResponsesConfigurationResponse,
    unknown,
    GetScrubFieldsFromResponsesConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetScrubFieldsFromResponsesConfiguration = (
  props: GetScrubFieldsFromResponsesConfigurationProps
) => (
  <Mutate<
    ScrubFieldsFromResponsesConfigurationResponse,
    unknown,
    GetScrubFieldsFromResponsesConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/ScrubFieldsFromResponsesConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetScrubFieldsFromResponsesConfigurationProps = Omit<
  UseMutateProps<
    ScrubFieldsFromResponsesConfigurationResponse,
    unknown,
    GetScrubFieldsFromResponsesConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetScrubFieldsFromResponsesConfiguration = (
  props: UseGetScrubFieldsFromResponsesConfigurationProps
) =>
  useMutate<
    ScrubFieldsFromResponsesConfigurationResponse,
    unknown,
    GetScrubFieldsFromResponsesConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/ScrubFieldsFromResponsesConfiguration/configureByHealthPlan`,
    props
  );

export interface GetAdditionalCareParticipantsFeatureConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetAdditionalCareParticipantsFeatureConfigurationProps = Omit<
  MutateProps<
    AdditionalCareParticipantsFeatureConfigurationResponse,
    unknown,
    GetAdditionalCareParticipantsFeatureConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetAdditionalCareParticipantsFeatureConfiguration = (
  props: GetAdditionalCareParticipantsFeatureConfigurationProps
) => (
  <Mutate<
    AdditionalCareParticipantsFeatureConfigurationResponse,
    unknown,
    GetAdditionalCareParticipantsFeatureConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/AdditionalCareParticipantsFeatureConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetAdditionalCareParticipantsFeatureConfigurationProps = Omit<
  UseMutateProps<
    AdditionalCareParticipantsFeatureConfigurationResponse,
    unknown,
    GetAdditionalCareParticipantsFeatureConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetAdditionalCareParticipantsFeatureConfiguration = (
  props: UseGetAdditionalCareParticipantsFeatureConfigurationProps
) =>
  useMutate<
    AdditionalCareParticipantsFeatureConfigurationResponse,
    unknown,
    GetAdditionalCareParticipantsFeatureConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/AdditionalCareParticipantsFeatureConfiguration/configureByHealthPlan`,
    props
  );

export interface GetLettersExpansionConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetLettersExpansionConfigurationProps = Omit<
  MutateProps<
    LettersExpansionConfigurationResponse,
    unknown,
    GetLettersExpansionConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetLettersExpansionConfiguration = (
  props: GetLettersExpansionConfigurationProps
) => (
  <Mutate<
    LettersExpansionConfigurationResponse,
    unknown,
    GetLettersExpansionConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/LettersExpansionConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetLettersExpansionConfigurationProps = Omit<
  UseMutateProps<
    LettersExpansionConfigurationResponse,
    unknown,
    GetLettersExpansionConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetLettersExpansionConfiguration = (
  props: UseGetLettersExpansionConfigurationProps
) =>
  useMutate<
    LettersExpansionConfigurationResponse,
    unknown,
    GetLettersExpansionConfigurationQueryParams,
    void,
    void
  >("POST", `/LettersExpansionConfiguration/configureByHealthPlan`, props);

export interface GetServiceRequestEditConfigurationQueryParams {
  delegatedVendorName?: string;
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetServiceRequestEditConfigurationProps = Omit<
  MutateProps<
    ServiceRequestEditConfigurationResponse,
    unknown,
    GetServiceRequestEditConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetServiceRequestEditConfiguration = (
  props: GetServiceRequestEditConfigurationProps
) => (
  <Mutate<
    ServiceRequestEditConfigurationResponse,
    unknown,
    GetServiceRequestEditConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/ServiceRequestEditConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetServiceRequestEditConfigurationProps = Omit<
  UseMutateProps<
    ServiceRequestEditConfigurationResponse,
    unknown,
    GetServiceRequestEditConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetServiceRequestEditConfiguration = (
  props: UseGetServiceRequestEditConfigurationProps
) =>
  useMutate<
    ServiceRequestEditConfigurationResponse,
    unknown,
    GetServiceRequestEditConfigurationQueryParams,
    void,
    void
  >("POST", `/ServiceRequestEditConfiguration/configureByHealthPlan`, props);

export interface GetServiceRequestStateConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetServiceRequestStateConfigurationProps = Omit<
  MutateProps<
    ServiceRequestStateConfigurationResponse,
    unknown,
    GetServiceRequestStateConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetServiceRequestStateConfiguration = (
  props: GetServiceRequestStateConfigurationProps
) => (
  <Mutate<
    ServiceRequestStateConfigurationResponse,
    unknown,
    GetServiceRequestStateConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/ServiceRequestStateConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetServiceRequestStateConfigurationProps = Omit<
  UseMutateProps<
    ServiceRequestStateConfigurationResponse,
    unknown,
    GetServiceRequestStateConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetServiceRequestStateConfiguration = (
  props: UseGetServiceRequestStateConfigurationProps
) =>
  useMutate<
    ServiceRequestStateConfigurationResponse,
    unknown,
    GetServiceRequestStateConfigurationQueryParams,
    void,
    void
  >("POST", `/ServiceRequestStateConfiguration/configureByHealthPlan`, props);

export interface GetNoteConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetNoteConfigurationProps = Omit<
  MutateProps<
    NoteConfigurationResponse,
    unknown,
    GetNoteConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetNoteConfiguration = (props: GetNoteConfigurationProps) => (
  <Mutate<
    NoteConfigurationResponse,
    unknown,
    GetNoteConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/NoteConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetNoteConfigurationProps = Omit<
  UseMutateProps<
    NoteConfigurationResponse,
    unknown,
    GetNoteConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetNoteConfiguration = (props: UseGetNoteConfigurationProps) =>
  useMutate<
    NoteConfigurationResponse,
    unknown,
    GetNoteConfigurationQueryParams,
    void,
    void
  >("POST", `/NoteConfiguration/configureByHealthPlan`, props);

export interface GetTinConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetTinConfigurationProps = Omit<
  MutateProps<
    TinConfigurationResponse,
    unknown,
    GetTinConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetTinConfiguration = (props: GetTinConfigurationProps) => (
  <Mutate<
    TinConfigurationResponse,
    unknown,
    GetTinConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/TinConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetTinConfigurationProps = Omit<
  UseMutateProps<
    TinConfigurationResponse,
    unknown,
    GetTinConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetTinConfiguration = (props: UseGetTinConfigurationProps) =>
  useMutate<
    TinConfigurationResponse,
    unknown,
    GetTinConfigurationQueryParams,
    void,
    void
  >("POST", `/TinConfiguration/configureByHealthPlan`, props);

export interface GetCorePlatformIntegrationConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetCorePlatformIntegrationConfigurationProps = Omit<
  MutateProps<
    CorePlatformIntegrationConfigurationResponse,
    unknown,
    GetCorePlatformIntegrationConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetCorePlatformIntegrationConfiguration = (
  props: GetCorePlatformIntegrationConfigurationProps
) => (
  <Mutate<
    CorePlatformIntegrationConfigurationResponse,
    unknown,
    GetCorePlatformIntegrationConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/CorePlatformIntegrationConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetCorePlatformIntegrationConfigurationProps = Omit<
  UseMutateProps<
    CorePlatformIntegrationConfigurationResponse,
    unknown,
    GetCorePlatformIntegrationConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetCorePlatformIntegrationConfiguration = (
  props: UseGetCorePlatformIntegrationConfigurationProps
) =>
  useMutate<
    CorePlatformIntegrationConfigurationResponse,
    unknown,
    GetCorePlatformIntegrationConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/CorePlatformIntegrationConfiguration/configureByHealthPlan`,
    props
  );

export interface GetFaxIntakeConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetFaxIntakeConfigurationProps = Omit<
  MutateProps<
    FaxIntakeConfigurationResponse,
    unknown,
    GetFaxIntakeConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetFaxIntakeConfiguration = (
  props: GetFaxIntakeConfigurationProps
) => (
  <Mutate<
    FaxIntakeConfigurationResponse,
    unknown,
    GetFaxIntakeConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/FaxIntakeConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetFaxIntakeConfigurationProps = Omit<
  UseMutateProps<
    FaxIntakeConfigurationResponse,
    unknown,
    GetFaxIntakeConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetFaxIntakeConfiguration = (
  props: UseGetFaxIntakeConfigurationProps
) =>
  useMutate<
    FaxIntakeConfigurationResponse,
    unknown,
    GetFaxIntakeConfigurationQueryParams,
    void,
    void
  >("POST", `/FaxIntakeConfiguration/configureByHealthPlan`, props);

export interface GetMcgAuthConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetMcgAuthConfigurationProps = Omit<
  MutateProps<
    McgAuthConfigurationResponse,
    unknown,
    GetMcgAuthConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetMcgAuthConfiguration = (
  props: GetMcgAuthConfigurationProps
) => (
  <Mutate<
    McgAuthConfigurationResponse,
    unknown,
    GetMcgAuthConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/McgAuthConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetMcgAuthConfigurationProps = Omit<
  UseMutateProps<
    McgAuthConfigurationResponse,
    unknown,
    GetMcgAuthConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetMcgAuthConfiguration = (
  props: UseGetMcgAuthConfigurationProps
) =>
  useMutate<
    McgAuthConfigurationResponse,
    unknown,
    GetMcgAuthConfigurationQueryParams,
    void,
    void
  >("POST", `/McgAuthConfiguration/configureByHealthPlan`, props);

export interface GetBidirectionalUpdateConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetBidirectionalUpdateConfigurationProps = Omit<
  MutateProps<
    BidirectionalUpdateConfigurationResponse,
    unknown,
    GetBidirectionalUpdateConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetBidirectionalUpdateConfiguration = (
  props: GetBidirectionalUpdateConfigurationProps
) => (
  <Mutate<
    BidirectionalUpdateConfigurationResponse,
    unknown,
    GetBidirectionalUpdateConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/BidirectionalUpdateConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetBidirectionalUpdateConfigurationProps = Omit<
  UseMutateProps<
    BidirectionalUpdateConfigurationResponse,
    unknown,
    GetBidirectionalUpdateConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetBidirectionalUpdateConfiguration = (
  props: UseGetBidirectionalUpdateConfigurationProps
) =>
  useMutate<
    BidirectionalUpdateConfigurationResponse,
    unknown,
    GetBidirectionalUpdateConfigurationQueryParams,
    void,
    void
  >("POST", `/BidirectionalUpdateConfiguration/configureByHealthPlan`, props);

export interface GetShowPalStatusToReviewerConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetShowPalStatusToReviewerConfigurationProps = Omit<
  MutateProps<
    ShowPalStatusToReviewerConfigurationResponse,
    unknown,
    GetShowPalStatusToReviewerConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetShowPalStatusToReviewerConfiguration = (
  props: GetShowPalStatusToReviewerConfigurationProps
) => (
  <Mutate<
    ShowPalStatusToReviewerConfigurationResponse,
    unknown,
    GetShowPalStatusToReviewerConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/ShowPalStatusToReviewerConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetShowPalStatusToReviewerConfigurationProps = Omit<
  UseMutateProps<
    ShowPalStatusToReviewerConfigurationResponse,
    unknown,
    GetShowPalStatusToReviewerConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetShowPalStatusToReviewerConfiguration = (
  props: UseGetShowPalStatusToReviewerConfigurationProps
) =>
  useMutate<
    ShowPalStatusToReviewerConfigurationResponse,
    unknown,
    GetShowPalStatusToReviewerConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/ShowPalStatusToReviewerConfiguration/configureByHealthPlan`,
    props
  );

export interface GetSurfaceBillableFamiliesConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetSurfaceBillableFamiliesConfigurationProps = Omit<
  MutateProps<
    SurfaceBillableFamiliesConfigurationResponse,
    unknown,
    GetSurfaceBillableFamiliesConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetSurfaceBillableFamiliesConfiguration = (
  props: GetSurfaceBillableFamiliesConfigurationProps
) => (
  <Mutate<
    SurfaceBillableFamiliesConfigurationResponse,
    unknown,
    GetSurfaceBillableFamiliesConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/SurfaceBillableFamiliesConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetSurfaceBillableFamiliesConfigurationProps = Omit<
  UseMutateProps<
    SurfaceBillableFamiliesConfigurationResponse,
    unknown,
    GetSurfaceBillableFamiliesConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetSurfaceBillableFamiliesConfiguration = (
  props: UseGetSurfaceBillableFamiliesConfigurationProps
) =>
  useMutate<
    SurfaceBillableFamiliesConfigurationResponse,
    unknown,
    GetSurfaceBillableFamiliesConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/SurfaceBillableFamiliesConfiguration/configureByHealthPlan`,
    props
  );

export interface GetFeatureFlagConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetFeatureFlagConfigurationProps = Omit<
  MutateProps<
    FeatureFlagConfigurationResponse,
    unknown,
    GetFeatureFlagConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetFeatureFlagConfiguration = (
  props: GetFeatureFlagConfigurationProps
) => (
  <Mutate<
    FeatureFlagConfigurationResponse,
    unknown,
    GetFeatureFlagConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/FeatureFlagConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetFeatureFlagConfigurationProps = Omit<
  UseMutateProps<
    FeatureFlagConfigurationResponse,
    unknown,
    GetFeatureFlagConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetFeatureFlagConfiguration = (
  props: UseGetFeatureFlagConfigurationProps
) =>
  useMutate<
    FeatureFlagConfigurationResponse,
    unknown,
    GetFeatureFlagConfigurationQueryParams,
    void,
    void
  >("POST", `/FeatureFlagConfiguration/configureByHealthPlan`, props);

export interface GetMissingInformationOutreachConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
  delegatedVendorName?: string;
}

export type GetMissingInformationOutreachConfigurationProps = Omit<
  MutateProps<
    MissingInformationOutreachConfigurationResponse,
    unknown,
    GetMissingInformationOutreachConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetMissingInformationOutreachConfiguration = (
  props: GetMissingInformationOutreachConfigurationProps
) => (
  <Mutate<
    MissingInformationOutreachConfigurationResponse,
    unknown,
    GetMissingInformationOutreachConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/MissingInformationOutreachConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetMissingInformationOutreachConfigurationProps = Omit<
  UseMutateProps<
    MissingInformationOutreachConfigurationResponse,
    unknown,
    GetMissingInformationOutreachConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetMissingInformationOutreachConfiguration = (
  props: UseGetMissingInformationOutreachConfigurationProps
) =>
  useMutate<
    MissingInformationOutreachConfigurationResponse,
    unknown,
    GetMissingInformationOutreachConfigurationQueryParams,
    void,
    void
  >(
    "POST",
    `/MissingInformationOutreachConfiguration/configureByHealthPlan`,
    props
  );

export interface GetMemberLookupConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetMemberLookupConfigurationProps = Omit<
  MutateProps<
    MemberLookupConfigurationResponse,
    unknown,
    GetMemberLookupConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetMemberLookupConfiguration = (
  props: GetMemberLookupConfigurationProps
) => (
  <Mutate<
    MemberLookupConfigurationResponse,
    unknown,
    GetMemberLookupConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/MemberLookupConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetMemberLookupConfigurationProps = Omit<
  UseMutateProps<
    MemberLookupConfigurationResponse,
    unknown,
    GetMemberLookupConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetMemberLookupConfiguration = (
  props: UseGetMemberLookupConfigurationProps
) =>
  useMutate<
    MemberLookupConfigurationResponse,
    unknown,
    GetMemberLookupConfigurationQueryParams,
    void,
    void
  >("POST", `/MemberLookupConfiguration/configureByHealthPlan`, props);

export interface GetAssociateFaxToServiceRequestQueryParams {
  externalReferenceId?: string;
  serviceRequestId?: string;
}

export type GetAssociateFaxToServiceRequestProps = Omit<
  MutateProps<
    void,
    unknown,
    GetAssociateFaxToServiceRequestQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetAssociateFaxToServiceRequest = (
  props: GetAssociateFaxToServiceRequestProps
) => (
  <Mutate<void, unknown, GetAssociateFaxToServiceRequestQueryParams, void, void>
    verb="POST"
    path={`/highmark/associateFaxToServiceRequest`}
    {...props}
  />
);

export type UseGetAssociateFaxToServiceRequestProps = Omit<
  UseMutateProps<
    void,
    unknown,
    GetAssociateFaxToServiceRequestQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetAssociateFaxToServiceRequest = (
  props: UseGetAssociateFaxToServiceRequestProps
) =>
  useMutate<
    void,
    unknown,
    GetAssociateFaxToServiceRequestQueryParams,
    void,
    void
  >("POST", `/highmark/associateFaxToServiceRequest`, props);

export interface GetFaxCloseOptionsQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetFaxCloseOptionsProps = Omit<
  MutateProps<
    CloseOption[],
    unknown,
    GetFaxCloseOptionsQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetFaxCloseOptions = (props: GetFaxCloseOptionsProps) => (
  <Mutate<CloseOption[], unknown, GetFaxCloseOptionsQueryParams, void, void>
    verb="POST"
    path={`/faxIntakeConfiguration/faxCloseOptions`}
    {...props}
  />
);

export type UseGetFaxCloseOptionsProps = Omit<
  UseMutateProps<
    CloseOption[],
    unknown,
    GetFaxCloseOptionsQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetFaxCloseOptions = (props: UseGetFaxCloseOptionsProps) =>
  useMutate<CloseOption[], unknown, GetFaxCloseOptionsQueryParams, void, void>(
    "POST",
    `/faxIntakeConfiguration/faxCloseOptions`,
    props
  );

export interface GetFaxForwardLinesAndOptionsQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetFaxForwardLinesAndOptionsProps = Omit<
  MutateProps<
    GetFaxForwardLinesAndOptionsResponse,
    unknown,
    GetFaxForwardLinesAndOptionsQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetFaxForwardLinesAndOptions = (
  props: GetFaxForwardLinesAndOptionsProps
) => (
  <Mutate<
    GetFaxForwardLinesAndOptionsResponse,
    unknown,
    GetFaxForwardLinesAndOptionsQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/faxIntakeConfiguration/manualFaxForwarding`}
    {...props}
  />
);

export type UseGetFaxForwardLinesAndOptionsProps = Omit<
  UseMutateProps<
    GetFaxForwardLinesAndOptionsResponse,
    unknown,
    GetFaxForwardLinesAndOptionsQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetFaxForwardLinesAndOptions = (
  props: UseGetFaxForwardLinesAndOptionsProps
) =>
  useMutate<
    GetFaxForwardLinesAndOptionsResponse,
    unknown,
    GetFaxForwardLinesAndOptionsQueryParams,
    void,
    void
  >("POST", `/faxIntakeConfiguration/manualFaxForwarding`, props);

export interface GetCollectSRContactInfoConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetCollectSRContactInfoConfigurationProps = Omit<
  MutateProps<
    CollectSRContactInfoConfigurationResponse,
    unknown,
    GetCollectSRContactInfoConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetCollectSRContactInfoConfiguration = (
  props: GetCollectSRContactInfoConfigurationProps
) => (
  <Mutate<
    CollectSRContactInfoConfigurationResponse,
    unknown,
    GetCollectSRContactInfoConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/collectSRContactInfoConfiguration/SRContactInfoConfig`}
    {...props}
  />
);

export type UseGetCollectSRContactInfoConfigurationProps = Omit<
  UseMutateProps<
    CollectSRContactInfoConfigurationResponse,
    unknown,
    GetCollectSRContactInfoConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetCollectSRContactInfoConfiguration = (
  props: UseGetCollectSRContactInfoConfigurationProps
) =>
  useMutate<
    CollectSRContactInfoConfigurationResponse,
    unknown,
    GetCollectSRContactInfoConfigurationQueryParams,
    void,
    void
  >("POST", `/collectSRContactInfoConfiguration/SRContactInfoConfig`, props);

export interface GetTransferAuthorizationConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetTransferAuthorizationConfigurationProps = Omit<
  MutateProps<
    TransferAuthorizationConfigurationResponse,
    unknown,
    GetTransferAuthorizationConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetTransferAuthorizationConfiguration = (
  props: GetTransferAuthorizationConfigurationProps
) => (
  <Mutate<
    TransferAuthorizationConfigurationResponse,
    unknown,
    GetTransferAuthorizationConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/TransferAuthorizationConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetTransferAuthorizationConfigurationProps = Omit<
  UseMutateProps<
    TransferAuthorizationConfigurationResponse,
    unknown,
    GetTransferAuthorizationConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetTransferAuthorizationConfiguration = (
  props: UseGetTransferAuthorizationConfigurationProps
) =>
  useMutate<
    TransferAuthorizationConfigurationResponse,
    unknown,
    GetTransferAuthorizationConfigurationQueryParams,
    void,
    void
  >("POST", `/TransferAuthorizationConfiguration/configureByHealthPlan`, props);

export interface GetAdvanceNotificationsConfigurationQueryParams {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
}

export type GetAdvanceNotificationsConfigurationProps = Omit<
  MutateProps<
    AdvanceNotificationsConfigurationResponse,
    unknown,
    GetAdvanceNotificationsConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const GetAdvanceNotificationsConfiguration = (
  props: GetAdvanceNotificationsConfigurationProps
) => (
  <Mutate<
    AdvanceNotificationsConfigurationResponse,
    unknown,
    GetAdvanceNotificationsConfigurationQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/AdvanceNotificationsConfiguration/configureByHealthPlan`}
    {...props}
  />
);

export type UseGetAdvanceNotificationsConfigurationProps = Omit<
  UseMutateProps<
    AdvanceNotificationsConfigurationResponse,
    unknown,
    GetAdvanceNotificationsConfigurationQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useGetAdvanceNotificationsConfiguration = (
  props: UseGetAdvanceNotificationsConfigurationProps
) =>
  useMutate<
    AdvanceNotificationsConfigurationResponse,
    unknown,
    GetAdvanceNotificationsConfigurationQueryParams,
    void,
    void
  >("POST", `/AdvanceNotificationsConfiguration/configureByHealthPlan`, props);

export type GetHealthPlanHierarchyApplicationConfigurationsProps = Omit<
  GetProps<HealthPlanHierarchyTree[], unknown, void, void>,
  "path"
>;

export const GetHealthPlanHierarchyApplicationConfigurations = (
  props: GetHealthPlanHierarchyApplicationConfigurationsProps
) => (
  <Get<HealthPlanHierarchyTree[], unknown, void, void>
    path={`/healthPlanHierarchyManagement/applicationConfigurations`}
    {...props}
  />
);

export type UseGetHealthPlanHierarchyApplicationConfigurationsProps = Omit<
  UseGetProps<HealthPlanHierarchyTree[], unknown, void, void>,
  "path"
>;

export const useGetHealthPlanHierarchyApplicationConfigurations = (
  props: UseGetHealthPlanHierarchyApplicationConfigurationsProps
) =>
  useGet<HealthPlanHierarchyTree[], unknown, void, void>(
    `/healthPlanHierarchyManagement/applicationConfigurations`,
    props
  );

export type GetHealthPlanHierarchyFeatureConfigurationsProps = Omit<
  GetProps<string[], unknown, void, void>,
  "path"
>;

export const GetHealthPlanHierarchyFeatureConfigurations = (
  props: GetHealthPlanHierarchyFeatureConfigurationsProps
) => (
  <Get<string[], unknown, void, void>
    path={`/healthPlanHierarchyManagement/featureConfigurations`}
    {...props}
  />
);

export type UseGetHealthPlanHierarchyFeatureConfigurationsProps = Omit<
  UseGetProps<string[], unknown, void, void>,
  "path"
>;

export const useGetHealthPlanHierarchyFeatureConfigurations = (
  props: UseGetHealthPlanHierarchyFeatureConfigurationsProps
) =>
  useGet<string[], unknown, void, void>(
    `/healthPlanHierarchyManagement/featureConfigurations`,
    props
  );

export interface GetHealthPlanHierarchyFeatureConfigurationQueryParams {
  applicationConfigurationId: string;
  featureConfigurationName: string;
  parentApplicationConfigurationId?: string;
}

export type GetHealthPlanHierarchyFeatureConfigurationProps = Omit<
  GetProps<
    ApplicationConfiguration,
    unknown,
    GetHealthPlanHierarchyFeatureConfigurationQueryParams,
    void
  >,
  "path"
>;

export const GetHealthPlanHierarchyFeatureConfiguration = (
  props: GetHealthPlanHierarchyFeatureConfigurationProps
) => (
  <Get<
    ApplicationConfiguration,
    unknown,
    GetHealthPlanHierarchyFeatureConfigurationQueryParams,
    void
  >
    path={`/healthPlanHierarchyManagement/featureConfiguration`}
    {...props}
  />
);

export type UseGetHealthPlanHierarchyFeatureConfigurationProps = Omit<
  UseGetProps<
    ApplicationConfiguration,
    unknown,
    GetHealthPlanHierarchyFeatureConfigurationQueryParams,
    void
  >,
  "path"
>;

export const useGetHealthPlanHierarchyFeatureConfiguration = (
  props: UseGetHealthPlanHierarchyFeatureConfigurationProps
) =>
  useGet<
    ApplicationConfiguration,
    unknown,
    GetHealthPlanHierarchyFeatureConfigurationQueryParams,
    void
  >(`/healthPlanHierarchyManagement/featureConfiguration`, props);

export type GetHealthPlansAvailableForConfigurationProps = Omit<
  GetProps<HealthPlan[], unknown, void, void>,
  "path"
>;

export const GetHealthPlansAvailableForConfiguration = (
  props: GetHealthPlansAvailableForConfigurationProps
) => (
  <Get<HealthPlan[], unknown, void, void>
    path={`/healthPlanHierarchyManagement/configurableHealthPlans`}
    {...props}
  />
);

export type UseGetHealthPlansAvailableForConfigurationProps = Omit<
  UseGetProps<HealthPlan[], unknown, void, void>,
  "path"
>;

export const useGetHealthPlansAvailableForConfiguration = (
  props: UseGetHealthPlansAvailableForConfigurationProps
) =>
  useGet<HealthPlan[], unknown, void, void>(
    `/healthPlanHierarchyManagement/configurableHealthPlans`,
    props
  );

export type CreateDelegatedVendorApplicationConfigurationProps = Omit<
  MutateProps<
    DelegatedVendorApplicationConfiguration,
    unknown,
    void,
    DelegatedVendorApplicationConfigurationCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const CreateDelegatedVendorApplicationConfiguration = (
  props: CreateDelegatedVendorApplicationConfigurationProps
) => (
  <Mutate<
    DelegatedVendorApplicationConfiguration,
    unknown,
    void,
    DelegatedVendorApplicationConfigurationCreatePayload,
    void
  >
    verb="POST"
    path={`/delegatedVendorApplicationConfiguration`}
    {...props}
  />
);

export type UseCreateDelegatedVendorApplicationConfigurationProps = Omit<
  UseMutateProps<
    DelegatedVendorApplicationConfiguration,
    unknown,
    void,
    DelegatedVendorApplicationConfigurationCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const useCreateDelegatedVendorApplicationConfiguration = (
  props: UseCreateDelegatedVendorApplicationConfigurationProps
) =>
  useMutate<
    DelegatedVendorApplicationConfiguration,
    unknown,
    void,
    DelegatedVendorApplicationConfigurationCreatePayload,
    void
  >("POST", `/delegatedVendorApplicationConfiguration`, props);

export type CreateHealthPlanApplicationConfigurationProps = Omit<
  MutateProps<
    HealthPlanApplicationConfiguration,
    unknown,
    void,
    HealthPlanApplicationConfigurationCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const CreateHealthPlanApplicationConfiguration = (
  props: CreateHealthPlanApplicationConfigurationProps
) => (
  <Mutate<
    HealthPlanApplicationConfiguration,
    unknown,
    void,
    HealthPlanApplicationConfigurationCreatePayload,
    void
  >
    verb="POST"
    path={`/healthPlanApplicationConfiguration`}
    {...props}
  />
);

export type UseCreateHealthPlanApplicationConfigurationProps = Omit<
  UseMutateProps<
    HealthPlanApplicationConfiguration,
    unknown,
    void,
    HealthPlanApplicationConfigurationCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const useCreateHealthPlanApplicationConfiguration = (
  props: UseCreateHealthPlanApplicationConfigurationProps
) =>
  useMutate<
    HealthPlanApplicationConfiguration,
    unknown,
    void,
    HealthPlanApplicationConfigurationCreatePayload,
    void
  >("POST", `/healthPlanApplicationConfiguration`, props);

export interface UpdateNotificationResponse {
  notification?: Notification;
}

export interface UpdateNotificationPathParams {
  id: string;
}

export type UpdateNotificationProps = Omit<
  MutateProps<
    UpdateNotificationResponse,
    unknown,
    void,
    NotificationUpdateCommand,
    UpdateNotificationPathParams
  >,
  "path" | "verb"
> &
  UpdateNotificationPathParams;

export const UpdateNotification = ({
  id,
  ...props
}: UpdateNotificationProps) => (
  <Mutate<
    UpdateNotificationResponse,
    unknown,
    void,
    NotificationUpdateCommand,
    UpdateNotificationPathParams
  >
    verb="PATCH"
    path={`/notification/${id}`}
    {...props}
  />
);

export type UseUpdateNotificationProps = Omit<
  UseMutateProps<
    UpdateNotificationResponse,
    unknown,
    void,
    NotificationUpdateCommand,
    UpdateNotificationPathParams
  >,
  "path" | "verb"
> &
  UpdateNotificationPathParams;

export const useUpdateNotification = ({
  id,
  ...props
}: UseUpdateNotificationProps) =>
  useMutate<
    UpdateNotificationResponse,
    unknown,
    void,
    NotificationUpdateCommand,
    UpdateNotificationPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateNotificationPathParams) =>
      `/notification/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GenerateAdhocLetterPreviewResponse {
  message?: string;
}

export type GenerateAdhocLetterPreviewProps = Omit<
  MutateProps<
    GenerateAdhocLetterPreviewResponse,
    unknown,
    void,
    GenerateAdhocLetterPreviewPayload,
    void
  >,
  "path" | "verb"
>;

export const GenerateAdhocLetterPreview = (
  props: GenerateAdhocLetterPreviewProps
) => (
  <Mutate<
    GenerateAdhocLetterPreviewResponse,
    unknown,
    void,
    GenerateAdhocLetterPreviewPayload,
    void
  >
    verb="POST"
    path={`/notification/adhocPreview`}
    {...props}
  />
);

export type UseGenerateAdhocLetterPreviewProps = Omit<
  UseMutateProps<
    GenerateAdhocLetterPreviewResponse,
    unknown,
    void,
    GenerateAdhocLetterPreviewPayload,
    void
  >,
  "path" | "verb"
>;

export const useGenerateAdhocLetterPreview = (
  props: UseGenerateAdhocLetterPreviewProps
) =>
  useMutate<
    GenerateAdhocLetterPreviewResponse,
    unknown,
    void,
    GenerateAdhocLetterPreviewPayload,
    void
  >("POST", `/notification/adhocPreview`, props);

export interface CreateAdhocNotificationsResponse {
  notifications?: { [key: string]: any }[];
}

export type CreateAdhocNotificationsProps = Omit<
  MutateProps<
    CreateAdhocNotificationsResponse,
    ErrorResponse,
    void,
    CreateAdhocNotificationsPayload,
    void
  >,
  "path" | "verb"
>;

export const CreateAdhocNotifications = (
  props: CreateAdhocNotificationsProps
) => (
  <Mutate<
    CreateAdhocNotificationsResponse,
    ErrorResponse,
    void,
    CreateAdhocNotificationsPayload,
    void
  >
    verb="POST"
    path={`/notification/adhoc`}
    {...props}
  />
);

export type UseCreateAdhocNotificationsProps = Omit<
  UseMutateProps<
    CreateAdhocNotificationsResponse,
    ErrorResponse,
    void,
    CreateAdhocNotificationsPayload,
    void
  >,
  "path" | "verb"
>;

export const useCreateAdhocNotifications = (
  props: UseCreateAdhocNotificationsProps
) =>
  useMutate<
    CreateAdhocNotificationsResponse,
    ErrorResponse,
    void,
    CreateAdhocNotificationsPayload,
    void
  >("POST", `/notification/adhoc`, props);

export interface UpdateApplicationConfigurationPathParams {
  id: string;
}

export type UpdateApplicationConfigurationProps = Omit<
  MutateProps<
    ApplicationConfiguration,
    unknown,
    void,
    ApplicationConfiguration,
    UpdateApplicationConfigurationPathParams
  >,
  "path" | "verb"
> &
  UpdateApplicationConfigurationPathParams;

export const UpdateApplicationConfiguration = ({
  id,
  ...props
}: UpdateApplicationConfigurationProps) => (
  <Mutate<
    ApplicationConfiguration,
    unknown,
    void,
    ApplicationConfiguration,
    UpdateApplicationConfigurationPathParams
  >
    verb="PUT"
    path={`/applicationConfiguration/${id}`}
    {...props}
  />
);

export type UseUpdateApplicationConfigurationProps = Omit<
  UseMutateProps<
    ApplicationConfiguration,
    unknown,
    void,
    ApplicationConfiguration,
    UpdateApplicationConfigurationPathParams
  >,
  "path" | "verb"
> &
  UpdateApplicationConfigurationPathParams;

export const useUpdateApplicationConfiguration = ({
  id,
  ...props
}: UseUpdateApplicationConfigurationProps) =>
  useMutate<
    ApplicationConfiguration,
    unknown,
    void,
    ApplicationConfiguration,
    UpdateApplicationConfigurationPathParams
  >(
    "PUT",
    (paramsInPath: UpdateApplicationConfigurationPathParams) =>
      `/applicationConfiguration/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type GetHistoryResponse = {
  changes?: {
    commitMetadata: {
      id?: string;
      author?: string;
      authorName?: string;
      authorEmail?: string;
      commitDateInstant?: string;
    };
    type: "UPDATE" | "INITIAL";
    changes: { [key: string]: any };
  }[];
};

export interface GetHistoryQueryParams {
  domain: string;
}

export interface GetHistoryPathParams {
  objectId: string;
}

export type GetHistoryProps = Omit<
  GetProps<
    GetHistoryResponse,
    unknown,
    GetHistoryQueryParams,
    GetHistoryPathParams
  >,
  "path"
> &
  GetHistoryPathParams;

export const GetHistory = ({ objectId, ...props }: GetHistoryProps) => (
  <Get<GetHistoryResponse, unknown, GetHistoryQueryParams, GetHistoryPathParams>
    path={`/history/${objectId}`}
    {...props}
  />
);

export type UseGetHistoryProps = Omit<
  UseGetProps<
    GetHistoryResponse,
    unknown,
    GetHistoryQueryParams,
    GetHistoryPathParams
  >,
  "path"
> &
  GetHistoryPathParams;

export const useGetHistory = ({ objectId, ...props }: UseGetHistoryProps) =>
  useGet<
    GetHistoryResponse,
    unknown,
    GetHistoryQueryParams,
    GetHistoryPathParams
  >(
    (paramsInPath: GetHistoryPathParams) => `/history/${paramsInPath.objectId}`,
    { pathParams: { objectId }, ...props }
  );

export type GetServiceRequestHistoryResponse = {
  changes?: {
    commitMetadata: {
      id?: string;
      author?: string;
      authorName?: string;
      authorEmail?: string;
      commitDateInstant?: string;
    };
    type: "UPDATE" | "INITIAL";
    changes: { [key: string]: any };
  }[];
};

export interface GetServiceRequestHistoryPathParams {
  srId: string;
}

export type GetServiceRequestHistoryProps = Omit<
  GetProps<
    GetServiceRequestHistoryResponse,
    unknown,
    void,
    GetServiceRequestHistoryPathParams
  >,
  "path"
> &
  GetServiceRequestHistoryPathParams;

export const GetServiceRequestHistory = ({
  srId,
  ...props
}: GetServiceRequestHistoryProps) => (
  <Get<
    GetServiceRequestHistoryResponse,
    unknown,
    void,
    GetServiceRequestHistoryPathParams
  >
    path={`/history/serviceRequest/${srId}`}
    {...props}
  />
);

export type UseGetServiceRequestHistoryProps = Omit<
  UseGetProps<
    GetServiceRequestHistoryResponse,
    unknown,
    void,
    GetServiceRequestHistoryPathParams
  >,
  "path"
> &
  GetServiceRequestHistoryPathParams;

export const useGetServiceRequestHistory = ({
  srId,
  ...props
}: UseGetServiceRequestHistoryProps) =>
  useGet<
    GetServiceRequestHistoryResponse,
    unknown,
    void,
    GetServiceRequestHistoryPathParams
  >(
    (paramsInPath: GetServiceRequestHistoryPathParams) =>
      `/history/serviceRequest/${paramsInPath.srId}`,
    { pathParams: { srId }, ...props }
  );

export interface GetServiceRequestRulesQueryParams {
  /**
   * services for rule by name
   */
  query?: {};
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: {};
  status?: {};
  variableActions?: {};
  payorName?: {};
  carePathId?: {};
  statementFilter?: ({ [key: string]: any } | string)[];
  serviceCategoryName?: {};
  clinicalServiceId?: {};
  clinicalServiceNameQuery?: {};
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of drafts with productionReady set to true or false
   */
  productionReady?: string;
  /**
   * Rule labels
   */
  ruleLabelIds?: {};
  actionType?: {};
  authStatus?: {};
  valueToChange?: {};
}

export type GetServiceRequestRulesProps = Omit<
  GetProps<
    ServiceRequestRule[],
    unknown,
    GetServiceRequestRulesQueryParams,
    void
  >,
  "path"
>;

/**
 * List all service request rules
 */
export const GetServiceRequestRules = (props: GetServiceRequestRulesProps) => (
  <Get<ServiceRequestRule[], unknown, GetServiceRequestRulesQueryParams, void>
    path={`/serviceRequestRule`}
    {...props}
  />
);

export type UseGetServiceRequestRulesProps = Omit<
  UseGetProps<
    ServiceRequestRule[],
    unknown,
    GetServiceRequestRulesQueryParams,
    void
  >,
  "path"
>;

/**
 * List all service request rules
 */
export const useGetServiceRequestRules = (
  props: UseGetServiceRequestRulesProps
) =>
  useGet<
    ServiceRequestRule[],
    unknown,
    GetServiceRequestRulesQueryParams,
    void
  >(`/serviceRequestRule`, props);

export type CreateServiceRequestRuleProps = Omit<
  MutateProps<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a ServiceRequestRule
 *
 * Creates a new instance of a `ServiceRequestRule`.
 */
export const CreateServiceRequestRule = (
  props: CreateServiceRequestRuleProps
) => (
  <Mutate<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    void
  >
    verb="POST"
    path={`/serviceRequestRule`}
    {...props}
  />
);

export type UseCreateServiceRequestRuleProps = Omit<
  UseMutateProps<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a ServiceRequestRule
 *
 * Creates a new instance of a `ServiceRequestRule`.
 */
export const useCreateServiceRequestRule = (
  props: UseCreateServiceRequestRuleProps
) =>
  useMutate<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    void
  >("POST", `/serviceRequestRule`, props);

export interface CreateGuidelineServiceRequestRulePathParams {
  /**
   * A unique identifier for a `Guideline`.
   */
  guidelineId: string;
}

export type CreateGuidelineServiceRequestRuleProps = Omit<
  MutateProps<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    CreateGuidelineServiceRequestRulePathParams
  >,
  "path" | "verb"
> &
  CreateGuidelineServiceRequestRulePathParams;

export const CreateGuidelineServiceRequestRule = ({
  guidelineId,
  ...props
}: CreateGuidelineServiceRequestRuleProps) => (
  <Mutate<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    CreateGuidelineServiceRequestRulePathParams
  >
    verb="POST"
    path={`/guideline/${guidelineId}/rule`}
    {...props}
  />
);

export type UseCreateGuidelineServiceRequestRuleProps = Omit<
  UseMutateProps<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    CreateGuidelineServiceRequestRulePathParams
  >,
  "path" | "verb"
> &
  CreateGuidelineServiceRequestRulePathParams;

export const useCreateGuidelineServiceRequestRule = ({
  guidelineId,
  ...props
}: UseCreateGuidelineServiceRequestRuleProps) =>
  useMutate<
    ServiceRequestRule,
    ErrorResponse | void,
    void,
    ServiceRequestRule,
    CreateGuidelineServiceRequestRulePathParams
  >(
    "POST",
    (paramsInPath: CreateGuidelineServiceRequestRulePathParams) =>
      `/guideline/${paramsInPath.guidelineId}/rule`,
    { pathParams: { guidelineId }, ...props }
  );

export type ServiceRequestRulesBatchUpdateProps = Omit<
  MutateProps<ServiceRequestRule[], unknown, void, ServiceRequestRule[], void>,
  "path" | "verb"
>;

/**
 * Updated object IDs marked as production-ready to published.
 */
export const ServiceRequestRulesBatchUpdate = (
  props: ServiceRequestRulesBatchUpdateProps
) => (
  <Mutate<ServiceRequestRule[], unknown, void, ServiceRequestRule[], void>
    verb="PATCH"
    path={`/serviceRequestRule/batch`}
    {...props}
  />
);

export type UseServiceRequestRulesBatchUpdateProps = Omit<
  UseMutateProps<
    ServiceRequestRule[],
    unknown,
    void,
    ServiceRequestRule[],
    void
  >,
  "path" | "verb"
>;

/**
 * Updated object IDs marked as production-ready to published.
 */
export const useServiceRequestRulesBatchUpdate = (
  props: UseServiceRequestRulesBatchUpdateProps
) =>
  useMutate<ServiceRequestRule[], unknown, void, ServiceRequestRule[], void>(
    "PATCH",
    `/serviceRequestRule/batch`,
    props
  );

export interface CopyTrainingAnalyticsRequestBody {
  sourceRuleId?: string;
  targetRuleId?: string;
}

export type CopyTrainingAnalyticsProps = Omit<
  MutateProps<void, unknown, void, CopyTrainingAnalyticsRequestBody, void>,
  "path" | "verb"
>;

/**
 * Copy Rule Training analytics
 */
export const CopyTrainingAnalytics = (props: CopyTrainingAnalyticsProps) => (
  <Mutate<void, unknown, void, CopyTrainingAnalyticsRequestBody, void>
    verb="POST"
    path={`/serviceRequestRule/copyTrainingAnalytics`}
    {...props}
  />
);

export type UseCopyTrainingAnalyticsProps = Omit<
  UseMutateProps<void, unknown, void, CopyTrainingAnalyticsRequestBody, void>,
  "path" | "verb"
>;

/**
 * Copy Rule Training analytics
 */
export const useCopyTrainingAnalytics = (
  props: UseCopyTrainingAnalyticsProps
) =>
  useMutate<void, unknown, void, CopyTrainingAnalyticsRequestBody, void>(
    "POST",
    `/serviceRequestRule/copyTrainingAnalytics`,
    props
  );

export interface GetServiceRequestRulePathParams {
  /**
   * A unique identifier for a `ServieRequestRule`.
   */
  id: string;
}

export type GetServiceRequestRuleProps = Omit<
  GetProps<ServiceRequestRule, unknown, void, GetServiceRequestRulePathParams>,
  "path"
> &
  GetServiceRequestRulePathParams;

/**
 * Get a ServieRequestRule
 *
 * Gets the details of a single instance of a `ServieRequestRule`.
 */
export const GetServiceRequestRule = ({
  id,
  ...props
}: GetServiceRequestRuleProps) => (
  <Get<ServiceRequestRule, unknown, void, GetServiceRequestRulePathParams>
    path={`/serviceRequestRule/${id}`}
    {...props}
  />
);

export type UseGetServiceRequestRuleProps = Omit<
  UseGetProps<
    ServiceRequestRule,
    unknown,
    void,
    GetServiceRequestRulePathParams
  >,
  "path"
> &
  GetServiceRequestRulePathParams;

/**
 * Get a ServieRequestRule
 *
 * Gets the details of a single instance of a `ServieRequestRule`.
 */
export const useGetServiceRequestRule = ({
  id,
  ...props
}: UseGetServiceRequestRuleProps) =>
  useGet<ServiceRequestRule, unknown, void, GetServiceRequestRulePathParams>(
    (paramsInPath: GetServiceRequestRulePathParams) =>
      `/serviceRequestRule/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateServiceRequestRulePathParams {
  /**
   * A unique identifier for a `ServieRequestRule`.
   */
  id: string;
}

export type UpdateServiceRequestRuleProps = Omit<
  MutateProps<
    ServiceRequestRule,
    unknown,
    void,
    ServiceRequestRule,
    UpdateServiceRequestRulePathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestRulePathParams;

/**
 * Update a ServiceRequestRule
 *
 * Updates an existing `ServiceRequestRule`.
 */
export const UpdateServiceRequestRule = ({
  id,
  ...props
}: UpdateServiceRequestRuleProps) => (
  <Mutate<
    ServiceRequestRule,
    unknown,
    void,
    ServiceRequestRule,
    UpdateServiceRequestRulePathParams
  >
    verb="PUT"
    path={`/serviceRequestRule/${id}`}
    {...props}
  />
);

export type UseUpdateServiceRequestRuleProps = Omit<
  UseMutateProps<
    ServiceRequestRule,
    unknown,
    void,
    ServiceRequestRule,
    UpdateServiceRequestRulePathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestRulePathParams;

/**
 * Update a ServiceRequestRule
 *
 * Updates an existing `ServiceRequestRule`.
 */
export const useUpdateServiceRequestRule = ({
  id,
  ...props
}: UseUpdateServiceRequestRuleProps) =>
  useMutate<
    ServiceRequestRule,
    unknown,
    void,
    ServiceRequestRule,
    UpdateServiceRequestRulePathParams
  >(
    "PUT",
    (paramsInPath: UpdateServiceRequestRulePathParams) =>
      `/serviceRequestRule/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface DownloadRuleConditionResponse {}

export interface DownloadRuleConditionPathParams {
  /**
   * A unique identifier for a `ServiceRequestRule`.
   */
  ruleId: string;
}

export type DownloadRuleConditionProps = Omit<
  GetProps<
    DownloadRuleConditionResponse,
    unknown,
    void,
    DownloadRuleConditionPathParams
  >,
  "path"
> &
  DownloadRuleConditionPathParams;

/**
 * Starts download of CSV file.
 *
 */
export const DownloadRuleCondition = ({
  ruleId,
  ...props
}: DownloadRuleConditionProps) => (
  <Get<
    DownloadRuleConditionResponse,
    unknown,
    void,
    DownloadRuleConditionPathParams
  >
    path={`/serviceRequestRule/${ruleId}/download`}
    {...props}
  />
);

export type UseDownloadRuleConditionProps = Omit<
  UseGetProps<
    DownloadRuleConditionResponse,
    unknown,
    void,
    DownloadRuleConditionPathParams
  >,
  "path"
> &
  DownloadRuleConditionPathParams;

/**
 * Starts download of CSV file.
 *
 */
export const useDownloadRuleCondition = ({
  ruleId,
  ...props
}: UseDownloadRuleConditionProps) =>
  useGet<
    DownloadRuleConditionResponse,
    unknown,
    void,
    DownloadRuleConditionPathParams
  >(
    (paramsInPath: DownloadRuleConditionPathParams) =>
      `/serviceRequestRule/${paramsInPath.ruleId}/download`,
    { pathParams: { ruleId }, ...props }
  );

export interface GetServiceRequestRulesCountQueryParams {
  /**
   * services for rule by name
   */
  query?: {};
  status?: string;
  variableActions?: string;
  payorName?: string;
  carePathId?: string;
  statementFilter?: ({ [key: string]: any } | string)[];
  serviceCategoryName?: string;
  clinicalServiceId?: string;
  clinicalServiceNameQuery?: string;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collection of drafts with productionReady set to true or false
   */
  productionReady?: string;
  /**
   * Rule labels
   */
  ruleLabelIds?: string;
  actionType?: string;
  authStatus?: string;
  valueToChange?: string;
  healthPlanIds?: string;
  specialty?: string;
}

export type GetServiceRequestRulesCountProps = Omit<
  GetProps<CountResult, unknown, GetServiceRequestRulesCountQueryParams, void>,
  "path"
>;

/**
 * List All Rules
 *
 * Gets a list of all `ServiceRequestRules` entities.
 */
export const GetServiceRequestRulesCount = (
  props: GetServiceRequestRulesCountProps
) => (
  <Get<CountResult, unknown, GetServiceRequestRulesCountQueryParams, void>
    path={`/serviceRequestRule/count`}
    {...props}
  />
);

export type UseGetServiceRequestRulesCountProps = Omit<
  UseGetProps<
    CountResult,
    unknown,
    GetServiceRequestRulesCountQueryParams,
    void
  >,
  "path"
>;

/**
 * List All Rules
 *
 * Gets a list of all `ServiceRequestRules` entities.
 */
export const useGetServiceRequestRulesCount = (
  props: UseGetServiceRequestRulesCountProps
) =>
  useGet<CountResult, unknown, GetServiceRequestRulesCountQueryParams, void>(
    `/serviceRequestRule/count`,
    props
  );

export interface GetSimulationRunPathParams {
  /**
   * the rule id
   */
  id: string;
}

export type GetSimulationRunProps = Omit<
  GetProps<RuleRunSimulation, unknown, void, GetSimulationRunPathParams>,
  "path"
> &
  GetSimulationRunPathParams;

export const GetSimulationRun = ({ id, ...props }: GetSimulationRunProps) => (
  <Get<RuleRunSimulation, unknown, void, GetSimulationRunPathParams>
    path={`/serviceRequestRule/${id}/simulateRun`}
    {...props}
  />
);

export type UseGetSimulationRunProps = Omit<
  UseGetProps<RuleRunSimulation, unknown, void, GetSimulationRunPathParams>,
  "path"
> &
  GetSimulationRunPathParams;

export const useGetSimulationRun = ({
  id,
  ...props
}: UseGetSimulationRunProps) =>
  useGet<RuleRunSimulation, unknown, void, GetSimulationRunPathParams>(
    (paramsInPath: GetSimulationRunPathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/simulateRun`,
    { pathParams: { id }, ...props }
  );

export interface SimulationRunPathParams {
  /**
   * the rule id
   */
  id: string;
}

export type SimulationRunProps = Omit<
  MutateProps<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulationRunPathParams
  >,
  "path" | "verb"
> &
  SimulationRunPathParams;

export const SimulationRun = ({ id, ...props }: SimulationRunProps) => (
  <Mutate<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulationRunPathParams
  >
    verb="POST"
    path={`/serviceRequestRule/${id}/simulateRun`}
    {...props}
  />
);

export type UseSimulationRunProps = Omit<
  UseMutateProps<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulationRunPathParams
  >,
  "path" | "verb"
> &
  SimulationRunPathParams;

export const useSimulationRun = ({ id, ...props }: UseSimulationRunProps) =>
  useMutate<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulationRunPathParams
  >(
    "POST",
    (paramsInPath: SimulationRunPathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/simulateRun`,
    { pathParams: { id }, ...props }
  );

export interface GetSimulationRunDetailPathParams {
  /**
   * the rule id
   */
  id: string;
}

export type GetSimulationRunDetailProps = Omit<
  GetProps<void, unknown, void, GetSimulationRunDetailPathParams>,
  "path"
> &
  GetSimulationRunDetailPathParams;

export const GetSimulationRunDetail = ({
  id,
  ...props
}: GetSimulationRunDetailProps) => (
  <Get<void, unknown, void, GetSimulationRunDetailPathParams>
    path={`/serviceRequestRule/${id}/simulateRunDetail`}
    {...props}
  />
);

export type UseGetSimulationRunDetailProps = Omit<
  UseGetProps<void, unknown, void, GetSimulationRunDetailPathParams>,
  "path"
> &
  GetSimulationRunDetailPathParams;

export const useGetSimulationRunDetail = ({
  id,
  ...props
}: UseGetSimulationRunDetailProps) =>
  useGet<void, unknown, void, GetSimulationRunDetailPathParams>(
    (paramsInPath: GetSimulationRunDetailPathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/simulateRunDetail`,
    { pathParams: { id }, ...props }
  );

export interface CancelSimulationPathParams {
  /**
   * the rule id
   */
  id: string;
}

export type CancelSimulationProps = Omit<
  MutateProps<void, unknown, void, void, CancelSimulationPathParams>,
  "path" | "verb"
> &
  CancelSimulationPathParams;

export const CancelSimulation = ({ id, ...props }: CancelSimulationProps) => (
  <Mutate<void, unknown, void, void, CancelSimulationPathParams>
    verb="POST"
    path={`/serviceRequestRule/${id}/cancelSimulation`}
    {...props}
  />
);

export type UseCancelSimulationProps = Omit<
  UseMutateProps<void, unknown, void, void, CancelSimulationPathParams>,
  "path" | "verb"
> &
  CancelSimulationPathParams;

export const useCancelSimulation = ({
  id,
  ...props
}: UseCancelSimulationProps) =>
  useMutate<void, unknown, void, void, CancelSimulationPathParams>(
    "POST",
    (paramsInPath: CancelSimulationPathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/cancelSimulation`,
    { pathParams: { id }, ...props }
  );

export interface TestServiceRequestRulePathParams {
  /**
   * A unique identifier for a `ServieRequestRule`.
   */
  id: string;
}

export type TestServiceRequestRuleProps = Omit<
  MutateProps<
    RuleRunResult,
    unknown,
    void,
    ServiceRequestRuleTestPayload,
    TestServiceRequestRulePathParams
  >,
  "path" | "verb"
> &
  TestServiceRequestRulePathParams;

/**
 * Tests a ServiceRequestRule
 */
export const TestServiceRequestRule = ({
  id,
  ...props
}: TestServiceRequestRuleProps) => (
  <Mutate<
    RuleRunResult,
    unknown,
    void,
    ServiceRequestRuleTestPayload,
    TestServiceRequestRulePathParams
  >
    verb="POST"
    path={`/serviceRequestRule/${id}/test`}
    {...props}
  />
);

export type UseTestServiceRequestRuleProps = Omit<
  UseMutateProps<
    RuleRunResult,
    unknown,
    void,
    ServiceRequestRuleTestPayload,
    TestServiceRequestRulePathParams
  >,
  "path" | "verb"
> &
  TestServiceRequestRulePathParams;

/**
 * Tests a ServiceRequestRule
 */
export const useTestServiceRequestRule = ({
  id,
  ...props
}: UseTestServiceRequestRuleProps) =>
  useMutate<
    RuleRunResult,
    unknown,
    void,
    ServiceRequestRuleTestPayload,
    TestServiceRequestRulePathParams
  >(
    "POST",
    (paramsInPath: TestServiceRequestRulePathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/test`,
    { pathParams: { id }, ...props }
  );

export type RetrieveCodeDescriptionsProps = Omit<
  MutateProps<ObservationCode[], unknown, void, RuleCondition, void>,
  "path" | "verb"
>;

/**
 * Retrieves observation codes associated with a rule.
 */
export const RetrieveCodeDescriptions = (
  props: RetrieveCodeDescriptionsProps
) => (
  <Mutate<ObservationCode[], unknown, void, RuleCondition, void>
    verb="POST"
    path={`/serviceRequestRule/retrieveCodeDescriptions`}
    {...props}
  />
);

export type UseRetrieveCodeDescriptionsProps = Omit<
  UseMutateProps<ObservationCode[], unknown, void, RuleCondition, void>,
  "path" | "verb"
>;

/**
 * Retrieves observation codes associated with a rule.
 */
export const useRetrieveCodeDescriptions = (
  props: UseRetrieveCodeDescriptionsProps
) =>
  useMutate<ObservationCode[], unknown, void, RuleCondition, void>(
    "POST",
    `/serviceRequestRule/retrieveCodeDescriptions`,
    props
  );

export interface SimulateRulePathParams {
  /**
   * the rule id
   */
  id: string;
}

export type SimulateRuleProps = Omit<
  MutateProps<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulateRulePathParams
  >,
  "path" | "verb"
> &
  SimulateRulePathParams;

export const SimulateRule = ({ id, ...props }: SimulateRuleProps) => (
  <Mutate<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulateRulePathParams
  >
    verb="POST"
    path={`/serviceRequestRule/${id}/simulateRule`}
    {...props}
  />
);

export type UseSimulateRuleProps = Omit<
  UseMutateProps<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulateRulePathParams
  >,
  "path" | "verb"
> &
  SimulateRulePathParams;

export const useSimulateRule = ({ id, ...props }: UseSimulateRuleProps) =>
  useMutate<
    RuleRunSimulation,
    unknown,
    void,
    SimulationRequestPayload,
    SimulateRulePathParams
  >(
    "POST",
    (paramsInPath: SimulateRulePathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/simulateRule`,
    { pathParams: { id }, ...props }
  );

export interface GetRulesUsingGuidelineIdPathParams {
  /**
   * Guideline's Id
   */
  guidelineId: string;
}

export type GetRulesUsingGuidelineIdProps = Omit<
  GetProps<
    ServiceRequestRule[],
    unknown,
    void,
    GetRulesUsingGuidelineIdPathParams
  >,
  "path"
> &
  GetRulesUsingGuidelineIdPathParams;

/**
 * Gets all rules using GuidelineId
 */
export const GetRulesUsingGuidelineId = ({
  guidelineId,
  ...props
}: GetRulesUsingGuidelineIdProps) => (
  <Get<ServiceRequestRule[], unknown, void, GetRulesUsingGuidelineIdPathParams>
    path={`/serviceRequestRule/guideline/${guidelineId}`}
    {...props}
  />
);

export type UseGetRulesUsingGuidelineIdProps = Omit<
  UseGetProps<
    ServiceRequestRule[],
    unknown,
    void,
    GetRulesUsingGuidelineIdPathParams
  >,
  "path"
> &
  GetRulesUsingGuidelineIdPathParams;

/**
 * Gets all rules using GuidelineId
 */
export const useGetRulesUsingGuidelineId = ({
  guidelineId,
  ...props
}: UseGetRulesUsingGuidelineIdProps) =>
  useGet<
    ServiceRequestRule[],
    unknown,
    void,
    GetRulesUsingGuidelineIdPathParams
  >(
    (paramsInPath: GetRulesUsingGuidelineIdPathParams) =>
      `/serviceRequestRule/guideline/${paramsInPath.guidelineId}`,
    { pathParams: { guidelineId }, ...props }
  );

export interface UpdateRuleConditionFromIndicationSelectionPathParams {
  /**
   * rule's Id
   */
  ruleId: string;
}

export interface UpdateRuleConditionFromIndicationSelectionRequestBody {
  /**
   * Set of indication uids which should be in the rule condition.
   */
  selectedIndications: string[];
}

export type UpdateRuleConditionFromIndicationSelectionProps = Omit<
  MutateProps<
    ServiceRequestRule[],
    unknown,
    void,
    UpdateRuleConditionFromIndicationSelectionRequestBody,
    UpdateRuleConditionFromIndicationSelectionPathParams
  >,
  "path" | "verb"
> &
  UpdateRuleConditionFromIndicationSelectionPathParams;

/**
 * updates rule condition from selected indications
 */
export const UpdateRuleConditionFromIndicationSelection = ({
  ruleId,
  ...props
}: UpdateRuleConditionFromIndicationSelectionProps) => (
  <Mutate<
    ServiceRequestRule[],
    unknown,
    void,
    UpdateRuleConditionFromIndicationSelectionRequestBody,
    UpdateRuleConditionFromIndicationSelectionPathParams
  >
    verb="POST"
    path={`/serviceRequestRule/updateRuleConditionFromIndicationSelection/${ruleId}`}
    {...props}
  />
);

export type UseUpdateRuleConditionFromIndicationSelectionProps = Omit<
  UseMutateProps<
    ServiceRequestRule[],
    unknown,
    void,
    UpdateRuleConditionFromIndicationSelectionRequestBody,
    UpdateRuleConditionFromIndicationSelectionPathParams
  >,
  "path" | "verb"
> &
  UpdateRuleConditionFromIndicationSelectionPathParams;

/**
 * updates rule condition from selected indications
 */
export const useUpdateRuleConditionFromIndicationSelection = ({
  ruleId,
  ...props
}: UseUpdateRuleConditionFromIndicationSelectionProps) =>
  useMutate<
    ServiceRequestRule[],
    unknown,
    void,
    UpdateRuleConditionFromIndicationSelectionRequestBody,
    UpdateRuleConditionFromIndicationSelectionPathParams
  >(
    "POST",
    (paramsInPath: UpdateRuleConditionFromIndicationSelectionPathParams) =>
      `/serviceRequestRule/updateRuleConditionFromIndicationSelection/${paramsInPath.ruleId}`,
    { pathParams: { ruleId }, ...props }
  );

export interface CompletePodTrainingPathParams {
  /**
   * The unique identifier for the rule
   */
  ruleId: string;
}

export type CompletePodTrainingProps = Omit<
  MutateProps<
    ServiceRequestRule,
    void,
    void,
    PodTrainingCompletionRequest,
    CompletePodTrainingPathParams
  >,
  "path" | "verb"
> &
  CompletePodTrainingPathParams;

export const CompletePodTraining = ({
  ruleId,
  ...props
}: CompletePodTrainingProps) => (
  <Mutate<
    ServiceRequestRule,
    void,
    void,
    PodTrainingCompletionRequest,
    CompletePodTrainingPathParams
  >
    verb="PUT"
    path={`/serviceRequestRule/${ruleId}/updatePodTraining`}
    {...props}
  />
);

export type UseCompletePodTrainingProps = Omit<
  UseMutateProps<
    ServiceRequestRule,
    void,
    void,
    PodTrainingCompletionRequest,
    CompletePodTrainingPathParams
  >,
  "path" | "verb"
> &
  CompletePodTrainingPathParams;

export const useCompletePodTraining = ({
  ruleId,
  ...props
}: UseCompletePodTrainingProps) =>
  useMutate<
    ServiceRequestRule,
    void,
    void,
    PodTrainingCompletionRequest,
    CompletePodTrainingPathParams
  >(
    "PUT",
    (paramsInPath: CompletePodTrainingPathParams) =>
      `/serviceRequestRule/${paramsInPath.ruleId}/updatePodTraining`,
    { pathParams: { ruleId }, ...props }
  );

export interface GetTrainingAnalyticsPathParams {
  /**
   * Unique identifier of the service request rule
   */
  ruleId: string;
}

export type GetTrainingAnalyticsProps = Omit<
  GetProps<
    RuleTrainingAnalytics,
    ErrorResponse,
    void,
    GetTrainingAnalyticsPathParams
  >,
  "path"
> &
  GetTrainingAnalyticsPathParams;

/**
 * Get Training Analytics
 *
 * Retrieves detailed training analytics for a specific service request rule, including:
 * - Training completion status and dates
 * - Baseline performance metrics
 * - Detailed training results (101 threshold-based records)
 * - ML insights and model parameters
 *
 * This endpoint provides comprehensive analytics data to evaluate rule training performance
 * and optimization results.
 *
 */
export const GetTrainingAnalytics = ({
  ruleId,
  ...props
}: GetTrainingAnalyticsProps) => (
  <Get<
    RuleTrainingAnalytics,
    ErrorResponse,
    void,
    GetTrainingAnalyticsPathParams
  >
    path={`/serviceRequestRule/${ruleId}/trainingAnalytics`}
    {...props}
  />
);

export type UseGetTrainingAnalyticsProps = Omit<
  UseGetProps<
    RuleTrainingAnalytics,
    ErrorResponse,
    void,
    GetTrainingAnalyticsPathParams
  >,
  "path"
> &
  GetTrainingAnalyticsPathParams;

/**
 * Get Training Analytics
 *
 * Retrieves detailed training analytics for a specific service request rule, including:
 * - Training completion status and dates
 * - Baseline performance metrics
 * - Detailed training results (101 threshold-based records)
 * - ML insights and model parameters
 *
 * This endpoint provides comprehensive analytics data to evaluate rule training performance
 * and optimization results.
 *
 */
export const useGetTrainingAnalytics = ({
  ruleId,
  ...props
}: UseGetTrainingAnalyticsProps) =>
  useGet<
    RuleTrainingAnalytics,
    ErrorResponse,
    void,
    GetTrainingAnalyticsPathParams
  >(
    (paramsInPath: GetTrainingAnalyticsPathParams) =>
      `/serviceRequestRule/${paramsInPath.ruleId}/trainingAnalytics`,
    { pathParams: { ruleId }, ...props }
  );

export interface RequestTrainingPathParams {
  /**
   * The unique identifier for the ServiceRequestRule to train
   */
  ruleId: string;
}

export type RequestTrainingProps = Omit<
  MutateProps<
    TrainingRequestResponse,
    ErrorResponse,
    void,
    TrainingRequest,
    RequestTrainingPathParams
  >,
  "path" | "verb"
> &
  RequestTrainingPathParams;

/**
 * Request machine learning training for a ServiceRequestRule
 *
 * Initiates machine learning training for a ServiceRequestRule by validating the request,
 * updating the rule's continuous decisioning flag, and sending a Slack notification to the ML team.
 *
 */
export const RequestTraining = ({ ruleId, ...props }: RequestTrainingProps) => (
  <Mutate<
    TrainingRequestResponse,
    ErrorResponse,
    void,
    TrainingRequest,
    RequestTrainingPathParams
  >
    verb="POST"
    path={`/serviceRequestRule/${ruleId}/requestTraining`}
    {...props}
  />
);

export type UseRequestTrainingProps = Omit<
  UseMutateProps<
    TrainingRequestResponse,
    ErrorResponse,
    void,
    TrainingRequest,
    RequestTrainingPathParams
  >,
  "path" | "verb"
> &
  RequestTrainingPathParams;

/**
 * Request machine learning training for a ServiceRequestRule
 *
 * Initiates machine learning training for a ServiceRequestRule by validating the request,
 * updating the rule's continuous decisioning flag, and sending a Slack notification to the ML team.
 *
 */
export const useRequestTraining = ({
  ruleId,
  ...props
}: UseRequestTrainingProps) =>
  useMutate<
    TrainingRequestResponse,
    ErrorResponse,
    void,
    TrainingRequest,
    RequestTrainingPathParams
  >(
    "POST",
    (paramsInPath: RequestTrainingPathParams) =>
      `/serviceRequestRule/${paramsInPath.ruleId}/requestTraining`,
    { pathParams: { ruleId }, ...props }
  );

export interface GetAllChangeRequestNotesBySrIdQueryParams {
  serviceRequestId: string;
}

export type GetAllChangeRequestNotesBySrIdProps = Omit<
  GetProps<
    ChangeRequestNote[],
    unknown,
    GetAllChangeRequestNotesBySrIdQueryParams,
    void
  >,
  "path"
>;

/**
 * Get all change request notes by service request ID
 */
export const GetAllChangeRequestNotesBySrId = (
  props: GetAllChangeRequestNotesBySrIdProps
) => (
  <Get<
    ChangeRequestNote[],
    unknown,
    GetAllChangeRequestNotesBySrIdQueryParams,
    void
  >
    path={`/changeRequestNote/getAllBySrId`}
    {...props}
  />
);

export type UseGetAllChangeRequestNotesBySrIdProps = Omit<
  UseGetProps<
    ChangeRequestNote[],
    unknown,
    GetAllChangeRequestNotesBySrIdQueryParams,
    void
  >,
  "path"
>;

/**
 * Get all change request notes by service request ID
 */
export const useGetAllChangeRequestNotesBySrId = (
  props: UseGetAllChangeRequestNotesBySrIdProps
) =>
  useGet<
    ChangeRequestNote[],
    unknown,
    GetAllChangeRequestNotesBySrIdQueryParams,
    void
  >(`/changeRequestNote/getAllBySrId`, props);

export type CreateChangeRequestNoteProps = Omit<
  MutateProps<ChangeRequestNote, unknown, void, CreateNoteRequest, void>,
  "path" | "verb"
>;

/**
 * Create a new ChangeRequestNote for the specified service request
 */
export const CreateChangeRequestNote = (
  props: CreateChangeRequestNoteProps
) => (
  <Mutate<ChangeRequestNote, unknown, void, CreateNoteRequest, void>
    verb="POST"
    path={`/changeRequestNote/createNote`}
    {...props}
  />
);

export type UseCreateChangeRequestNoteProps = Omit<
  UseMutateProps<ChangeRequestNote, unknown, void, CreateNoteRequest, void>,
  "path" | "verb"
>;

/**
 * Create a new ChangeRequestNote for the specified service request
 */
export const useCreateChangeRequestNote = (
  props: UseCreateChangeRequestNoteProps
) =>
  useMutate<ChangeRequestNote, unknown, void, CreateNoteRequest, void>(
    "POST",
    `/changeRequestNote/createNote`,
    props
  );

export interface GetMostRecentChangeRequestNoteBySrIdQueryParams {
  serviceRequestId: string;
}

export type GetMostRecentChangeRequestNoteBySrIdProps = Omit<
  GetProps<
    ChangeRequestNote,
    unknown,
    GetMostRecentChangeRequestNoteBySrIdQueryParams,
    void
  >,
  "path"
>;

/**
 * Get the most recent change request note by service request ID
 */
export const GetMostRecentChangeRequestNoteBySrId = (
  props: GetMostRecentChangeRequestNoteBySrIdProps
) => (
  <Get<
    ChangeRequestNote,
    unknown,
    GetMostRecentChangeRequestNoteBySrIdQueryParams,
    void
  >
    path={`/changeRequestNote/getMostRecentBySrId`}
    {...props}
  />
);

export type UseGetMostRecentChangeRequestNoteBySrIdProps = Omit<
  UseGetProps<
    ChangeRequestNote,
    unknown,
    GetMostRecentChangeRequestNoteBySrIdQueryParams,
    void
  >,
  "path"
>;

/**
 * Get the most recent change request note by service request ID
 */
export const useGetMostRecentChangeRequestNoteBySrId = (
  props: UseGetMostRecentChangeRequestNoteBySrIdProps
) =>
  useGet<
    ChangeRequestNote,
    unknown,
    GetMostRecentChangeRequestNoteBySrIdQueryParams,
    void
  >(`/changeRequestNote/getMostRecentBySrId`, props);

export type CreateRuleLabelProps = Omit<
  MutateProps<RuleLabel, ErrorResponse | void, void, RuleLabel, void>,
  "path" | "verb"
>;

/**
 * Create a Rule Label
 *
 * Creates a new instance of a `RuleLabel`.
 */
export const CreateRuleLabel = (props: CreateRuleLabelProps) => (
  <Mutate<RuleLabel, ErrorResponse | void, void, RuleLabel, void>
    verb="POST"
    path={`/ruleLabel`}
    {...props}
  />
);

export type UseCreateRuleLabelProps = Omit<
  UseMutateProps<RuleLabel, ErrorResponse | void, void, RuleLabel, void>,
  "path" | "verb"
>;

/**
 * Create a Rule Label
 *
 * Creates a new instance of a `RuleLabel`.
 */
export const useCreateRuleLabel = (props: UseCreateRuleLabelProps) =>
  useMutate<RuleLabel, ErrorResponse | void, void, RuleLabel, void>(
    "POST",
    `/ruleLabel`,
    props
  );

export interface UpdateRuleLabelPathParams {
  /**
   * A unique identifier for a `RuleLabel`.
   */
  id: string;
}

export type UpdateRuleLabelProps = Omit<
  MutateProps<RuleLabel, unknown, void, RuleLabel, UpdateRuleLabelPathParams>,
  "path" | "verb"
> &
  UpdateRuleLabelPathParams;

/**
 * Update a Rule Label
 */
export const UpdateRuleLabel = ({ id, ...props }: UpdateRuleLabelProps) => (
  <Mutate<RuleLabel, unknown, void, RuleLabel, UpdateRuleLabelPathParams>
    verb="PUT"
    path={`/ruleLabel/${id}`}
    {...props}
  />
);

export type UseUpdateRuleLabelProps = Omit<
  UseMutateProps<
    RuleLabel,
    unknown,
    void,
    RuleLabel,
    UpdateRuleLabelPathParams
  >,
  "path" | "verb"
> &
  UpdateRuleLabelPathParams;

/**
 * Update a Rule Label
 */
export const useUpdateRuleLabel = ({ id, ...props }: UseUpdateRuleLabelProps) =>
  useMutate<RuleLabel, unknown, void, RuleLabel, UpdateRuleLabelPathParams>(
    "PUT",
    (paramsInPath: UpdateRuleLabelPathParams) =>
      `/ruleLabel/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type CreateServiceRequestRuleLabelPriorityProps = Omit<
  MutateProps<
    ServiceRequestRuleLabelPriority,
    ErrorResponse | void,
    void,
    ServiceRequestRuleLabelPriority,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a Service Request Rule Label Priority
 *
 * Creates a new instance of a `ServiceRequestRuleLabelPriority`.
 */
export const CreateServiceRequestRuleLabelPriority = (
  props: CreateServiceRequestRuleLabelPriorityProps
) => (
  <Mutate<
    ServiceRequestRuleLabelPriority,
    ErrorResponse | void,
    void,
    ServiceRequestRuleLabelPriority,
    void
  >
    verb="POST"
    path={`/serviceRequestRuleLabelPriority`}
    {...props}
  />
);

export type UseCreateServiceRequestRuleLabelPriorityProps = Omit<
  UseMutateProps<
    ServiceRequestRuleLabelPriority,
    ErrorResponse | void,
    void,
    ServiceRequestRuleLabelPriority,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a Service Request Rule Label Priority
 *
 * Creates a new instance of a `ServiceRequestRuleLabelPriority`.
 */
export const useCreateServiceRequestRuleLabelPriority = (
  props: UseCreateServiceRequestRuleLabelPriorityProps
) =>
  useMutate<
    ServiceRequestRuleLabelPriority,
    ErrorResponse | void,
    void,
    ServiceRequestRuleLabelPriority,
    void
  >("POST", `/serviceRequestRuleLabelPriority`, props);

export interface GetServiceRequestRuleLabelPriorityQueryParams {
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
}

export type GetServiceRequestRuleLabelPriorityProps = Omit<
  GetProps<
    ServiceRequestRuleLabelPriority[],
    unknown,
    GetServiceRequestRuleLabelPriorityQueryParams,
    void
  >,
  "path"
>;

/**
 * Get a Service Request Rule Label Priority array
 *
 * Gets the details of a single instance of a `ServiceRequestRuleLabelPriority`.
 */
export const GetServiceRequestRuleLabelPriority = (
  props: GetServiceRequestRuleLabelPriorityProps
) => (
  <Get<
    ServiceRequestRuleLabelPriority[],
    unknown,
    GetServiceRequestRuleLabelPriorityQueryParams,
    void
  >
    path={`/serviceRequestRuleLabelPriority`}
    {...props}
  />
);

export type UseGetServiceRequestRuleLabelPriorityProps = Omit<
  UseGetProps<
    ServiceRequestRuleLabelPriority[],
    unknown,
    GetServiceRequestRuleLabelPriorityQueryParams,
    void
  >,
  "path"
>;

/**
 * Get a Service Request Rule Label Priority array
 *
 * Gets the details of a single instance of a `ServiceRequestRuleLabelPriority`.
 */
export const useGetServiceRequestRuleLabelPriority = (
  props: UseGetServiceRequestRuleLabelPriorityProps
) =>
  useGet<
    ServiceRequestRuleLabelPriority[],
    unknown,
    GetServiceRequestRuleLabelPriorityQueryParams,
    void
  >(`/serviceRequestRuleLabelPriority`, props);

export interface UpdateServiceRequestRuleLabelPriorityPathParams {
  /**
   * A unique identifier for a `ServiceRequestRuleLabelPriority`.
   */
  id: string;
}

export type UpdateServiceRequestRuleLabelPriorityProps = Omit<
  MutateProps<
    ServiceRequestRuleLabelPriority,
    unknown,
    void,
    ServiceRequestRuleLabelPriority,
    UpdateServiceRequestRuleLabelPriorityPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestRuleLabelPriorityPathParams;

/**
 * Update a Service Request Rule Label Priority
 */
export const UpdateServiceRequestRuleLabelPriority = ({
  id,
  ...props
}: UpdateServiceRequestRuleLabelPriorityProps) => (
  <Mutate<
    ServiceRequestRuleLabelPriority,
    unknown,
    void,
    ServiceRequestRuleLabelPriority,
    UpdateServiceRequestRuleLabelPriorityPathParams
  >
    verb="PUT"
    path={`/serviceRequestRuleLabelPriority/${id}`}
    {...props}
  />
);

export type UseUpdateServiceRequestRuleLabelPriorityProps = Omit<
  UseMutateProps<
    ServiceRequestRuleLabelPriority,
    unknown,
    void,
    ServiceRequestRuleLabelPriority,
    UpdateServiceRequestRuleLabelPriorityPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestRuleLabelPriorityPathParams;

/**
 * Update a Service Request Rule Label Priority
 */
export const useUpdateServiceRequestRuleLabelPriority = ({
  id,
  ...props
}: UseUpdateServiceRequestRuleLabelPriorityProps) =>
  useMutate<
    ServiceRequestRuleLabelPriority,
    unknown,
    void,
    ServiceRequestRuleLabelPriority,
    UpdateServiceRequestRuleLabelPriorityPathParams
  >(
    "PUT",
    (paramsInPath: UpdateServiceRequestRuleLabelPriorityPathParams) =>
      `/serviceRequestRuleLabelPriority/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface CreateFilterGroupPathParams {
  clinicalQuestionId: string;
}

export type CreateFilterGroupProps = Omit<
  MutateProps<
    FilterGroup,
    ErrorResponse | void,
    void,
    FilterGroup,
    CreateFilterGroupPathParams
  >,
  "path" | "verb"
> &
  CreateFilterGroupPathParams;

/**
 * Create a FilterGroup
 *
 * Creates a new instance of a `FilterGroup`.
 */
export const CreateFilterGroup = ({
  clinicalQuestionId,
  ...props
}: CreateFilterGroupProps) => (
  <Mutate<
    FilterGroup,
    ErrorResponse | void,
    void,
    FilterGroup,
    CreateFilterGroupPathParams
  >
    verb="POST"
    path={`/clinicalQuestion/${clinicalQuestionId}/filterGroup`}
    {...props}
  />
);

export type UseCreateFilterGroupProps = Omit<
  UseMutateProps<
    FilterGroup,
    ErrorResponse | void,
    void,
    FilterGroup,
    CreateFilterGroupPathParams
  >,
  "path" | "verb"
> &
  CreateFilterGroupPathParams;

/**
 * Create a FilterGroup
 *
 * Creates a new instance of a `FilterGroup`.
 */
export const useCreateFilterGroup = ({
  clinicalQuestionId,
  ...props
}: UseCreateFilterGroupProps) =>
  useMutate<
    FilterGroup,
    ErrorResponse | void,
    void,
    FilterGroup,
    CreateFilterGroupPathParams
  >(
    "POST",
    (paramsInPath: CreateFilterGroupPathParams) =>
      `/clinicalQuestion/${paramsInPath.clinicalQuestionId}/filterGroup`,
    { pathParams: { clinicalQuestionId }, ...props }
  );

export interface GetFilterGroupPathParams {
  /**
   * A unique identifier for a `FilterGroup`.
   */
  id: string;
}

export type GetFilterGroupProps = Omit<
  GetProps<FilterGroup, unknown, void, GetFilterGroupPathParams>,
  "path"
> &
  GetFilterGroupPathParams;

/**
 * Get a FilterGroup
 *
 * Gets the details of a single instance of a `FilterGroup`.
 */
export const GetFilterGroup = ({ id, ...props }: GetFilterGroupProps) => (
  <Get<FilterGroup, unknown, void, GetFilterGroupPathParams>
    path={`/filterGroup/${id}`}
    {...props}
  />
);

export type UseGetFilterGroupProps = Omit<
  UseGetProps<FilterGroup, unknown, void, GetFilterGroupPathParams>,
  "path"
> &
  GetFilterGroupPathParams;

/**
 * Get a FilterGroup
 *
 * Gets the details of a single instance of a `FilterGroup`.
 */
export const useGetFilterGroup = ({ id, ...props }: UseGetFilterGroupProps) =>
  useGet<FilterGroup, unknown, void, GetFilterGroupPathParams>(
    (paramsInPath: GetFilterGroupPathParams) =>
      `/filterGroup/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateFilterGroupPathParams {
  /**
   * A unique identifier for a `FilterGroup`.
   */
  id: string;
}

export type UpdateFilterGroupProps = Omit<
  MutateProps<
    FilterGroup,
    unknown,
    void,
    FilterGroup,
    UpdateFilterGroupPathParams
  >,
  "path" | "verb"
> &
  UpdateFilterGroupPathParams;

/**
 * Update a FilterGroup
 *
 * Updates an existing `FilterGroup`.
 */
export const UpdateFilterGroup = ({ id, ...props }: UpdateFilterGroupProps) => (
  <Mutate<FilterGroup, unknown, void, FilterGroup, UpdateFilterGroupPathParams>
    verb="PUT"
    path={`/filterGroup/${id}`}
    {...props}
  />
);

export type UseUpdateFilterGroupProps = Omit<
  UseMutateProps<
    FilterGroup,
    unknown,
    void,
    FilterGroup,
    UpdateFilterGroupPathParams
  >,
  "path" | "verb"
> &
  UpdateFilterGroupPathParams;

/**
 * Update a FilterGroup
 *
 * Updates an existing `FilterGroup`.
 */
export const useUpdateFilterGroup = ({
  id,
  ...props
}: UseUpdateFilterGroupProps) =>
  useMutate<
    FilterGroup,
    unknown,
    void,
    FilterGroup,
    UpdateFilterGroupPathParams
  >(
    "PUT",
    (paramsInPath: UpdateFilterGroupPathParams) =>
      `/filterGroup/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type CreateClinicalServiceFilterProps = Omit<
  MutateProps<
    ClinicalServiceFilter,
    ErrorResponse | void,
    void,
    ClinicalServiceFilter,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a ClinicalServiceFilter
 *
 * Creates a new instance of a `ClinicalServiceFilter`.
 */
export const CreateClinicalServiceFilter = (
  props: CreateClinicalServiceFilterProps
) => (
  <Mutate<
    ClinicalServiceFilter,
    ErrorResponse | void,
    void,
    ClinicalServiceFilter,
    void
  >
    verb="POST"
    path={`/clinicalServiceFilter`}
    {...props}
  />
);

export type UseCreateClinicalServiceFilterProps = Omit<
  UseMutateProps<
    ClinicalServiceFilter,
    ErrorResponse | void,
    void,
    ClinicalServiceFilter,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a ClinicalServiceFilter
 *
 * Creates a new instance of a `ClinicalServiceFilter`.
 */
export const useCreateClinicalServiceFilter = (
  props: UseCreateClinicalServiceFilterProps
) =>
  useMutate<
    ClinicalServiceFilter,
    ErrorResponse | void,
    void,
    ClinicalServiceFilter,
    void
  >("POST", `/clinicalServiceFilter`, props);

export interface GetClinicalServiceFilterPathParams {
  /**
   * A unique identifier for a `ClinicalServiceFilter`.
   */
  id: string;
}

export type GetClinicalServiceFilterProps = Omit<
  GetProps<
    ClinicalServiceFilter,
    unknown,
    void,
    GetClinicalServiceFilterPathParams
  >,
  "path"
> &
  GetClinicalServiceFilterPathParams;

/**
 * Get a ClinicalServiceFilter
 *
 * Gets the details of a single instance of a `ClinicalServiceFilter`.
 */
export const GetClinicalServiceFilter = ({
  id,
  ...props
}: GetClinicalServiceFilterProps) => (
  <Get<ClinicalServiceFilter, unknown, void, GetClinicalServiceFilterPathParams>
    path={`/clinicalServiceFilter/${id}`}
    {...props}
  />
);

export type UseGetClinicalServiceFilterProps = Omit<
  UseGetProps<
    ClinicalServiceFilter,
    unknown,
    void,
    GetClinicalServiceFilterPathParams
  >,
  "path"
> &
  GetClinicalServiceFilterPathParams;

/**
 * Get a ClinicalServiceFilter
 *
 * Gets the details of a single instance of a `ClinicalServiceFilter`.
 */
export const useGetClinicalServiceFilter = ({
  id,
  ...props
}: UseGetClinicalServiceFilterProps) =>
  useGet<
    ClinicalServiceFilter,
    unknown,
    void,
    GetClinicalServiceFilterPathParams
  >(
    (paramsInPath: GetClinicalServiceFilterPathParams) =>
      `/clinicalServiceFilter/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateClinicalServiceFilterPathParams {
  /**
   * A unique identifier for a `ClinicalServiceFilter`.
   */
  id: string;
}

export type UpdateClinicalServiceFilterProps = Omit<
  MutateProps<
    ClinicalServiceFilter,
    unknown,
    void,
    ClinicalServiceFilter,
    UpdateClinicalServiceFilterPathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalServiceFilterPathParams;

/**
 * Update a ClinicalServiceFilter
 *
 * Updates an existing `ClinicalServiceFilter`.
 */
export const UpdateClinicalServiceFilter = ({
  id,
  ...props
}: UpdateClinicalServiceFilterProps) => (
  <Mutate<
    ClinicalServiceFilter,
    unknown,
    void,
    ClinicalServiceFilter,
    UpdateClinicalServiceFilterPathParams
  >
    verb="PUT"
    path={`/clinicalServiceFilter/${id}`}
    {...props}
  />
);

export type UseUpdateClinicalServiceFilterProps = Omit<
  UseMutateProps<
    ClinicalServiceFilter,
    unknown,
    void,
    ClinicalServiceFilter,
    UpdateClinicalServiceFilterPathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalServiceFilterPathParams;

/**
 * Update a ClinicalServiceFilter
 *
 * Updates an existing `ClinicalServiceFilter`.
 */
export const useUpdateClinicalServiceFilter = ({
  id,
  ...props
}: UseUpdateClinicalServiceFilterProps) =>
  useMutate<
    ClinicalServiceFilter,
    unknown,
    void,
    ClinicalServiceFilter,
    UpdateClinicalServiceFilterPathParams
  >(
    "PUT",
    (paramsInPath: UpdateClinicalServiceFilterPathParams) =>
      `/clinicalServiceFilter/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetLogoQueryParams {
  /**
   * The health plan name or display name associated with a given health plan which corresponds to a given health plan.
   */
  healthPlanDisplayName?: string;
  /**
   * The name of the health plan whose logo should be used.
   */
  healthPlanName?: string;
  /**
   * The id of the patient associated with care to ensure logo accurately represents coverage.
   */
  patientId?: string;
  /**
   * The context which defines where the logo will be used.
   */
  context?: string;
  /**
   * The SSO code passed in from URL parameter when users sign in using single sign on.
   */
  ssoCode?: string;
}

export type GetLogoProps = Omit<
  GetProps<string[], unknown, GetLogoQueryParams, void>,
  "path"
>;

/**
 * Get the name of the banded logo to use
 *
 * This will return the name of the branded logo to be sourced within a given context (app banner, patient summary, email, fax, etc.)
 */
export const GetLogo = (props: GetLogoProps) => (
  <Get<string[], unknown, GetLogoQueryParams, void>
    path={`/cobranding/logo`}
    {...props}
  />
);

export type UseGetLogoProps = Omit<
  UseGetProps<string[], unknown, GetLogoQueryParams, void>,
  "path"
>;

/**
 * Get the name of the banded logo to use
 *
 * This will return the name of the branded logo to be sourced within a given context (app banner, patient summary, email, fax, etc.)
 */
export const useGetLogo = (props: UseGetLogoProps) =>
  useGet<string[], unknown, GetLogoQueryParams, void>(
    `/cobranding/logo`,
    props
  );

export interface SetupLogoResponse {
  url?: string;
}

export type SetupLogoProps = Omit<
  MutateProps<SetupLogoResponse, unknown, void, void, void>,
  "path" | "verb"
>;

/**
 * Uploads a branded logo
 *
 * This will return the branded logo that has been uploaded
 */
export const SetupLogo = (props: SetupLogoProps) => (
  <Mutate<SetupLogoResponse, unknown, void, void, void>
    verb="POST"
    path={`/cobranding/logo`}
    {...props}
  />
);

export type UseSetupLogoProps = Omit<
  UseMutateProps<SetupLogoResponse, unknown, void, void, void>,
  "path" | "verb"
>;

/**
 * Uploads a branded logo
 *
 * This will return the branded logo that has been uploaded
 */
export const useSetupLogo = (props: UseSetupLogoProps) =>
  useMutate<SetupLogoResponse, unknown, void, void, void>(
    "POST",
    `/cobranding/logo`,
    props
  );

export interface GetLogoV2Response {
  url?: string;
}

export interface GetLogoV2QueryParams {
  /**
   * The health plan name or display name associated with a given health plan which corresponds to a given health plan.
   */
  healthPlanDisplayName?: string;
  /**
   * The name of the health plan whose logo should be used.
   */
  healthPlanName?: string;
  /**
   * The id of the patient associated with care to ensure logo accurately represents coverage.
   */
  patientId?: string;
  /**
   * The context which defines where the logo will be used.
   */
  context?: string;
  /**
   * The SSO code passed in from URL parameter when users sign in using single sign on.
   */
  ssoCode?: string;
}

export type GetLogoV2Props = Omit<
  GetProps<GetLogoV2Response, unknown, GetLogoV2QueryParams, void>,
  "path"
>;

/**
 * Get the name of the banded logo to use
 *
 * This will return the name of the branded logo to be sourced within a given context (app banner, patient summary, email, fax, etc.)
 */
export const GetLogoV2 = (props: GetLogoV2Props) => (
  <Get<GetLogoV2Response, unknown, GetLogoV2QueryParams, void>
    path={`/cobranding/logoV2`}
    {...props}
  />
);

export type UseGetLogoV2Props = Omit<
  UseGetProps<GetLogoV2Response, unknown, GetLogoV2QueryParams, void>,
  "path"
>;

/**
 * Get the name of the banded logo to use
 *
 * This will return the name of the branded logo to be sourced within a given context (app banner, patient summary, email, fax, etc.)
 */
export const useGetLogoV2 = (props: UseGetLogoV2Props) =>
  useGet<GetLogoV2Response, unknown, GetLogoV2QueryParams, void>(
    `/cobranding/logoV2`,
    props
  );

export interface GetLogosQueryParams {
  /**
   * The health plan name or display name associated with a given health plan which corresponds to a given health plan.
   */
  healthPlanDisplayName?: string;
  /**
   * The name of the health plan whose logo should be used.
   */
  healthPlanName?: string;
  /**
   * The id of the patient associated with care to ensure logo accurately represents coverage.
   */
  patientId?: string;
  /**
   * The context which defines where the logo will be used.
   */
  context?: string;
  /**
   * The SSO code passed in from URL parameter when users sign in using single sign on.
   */
  ssoCode?: string;
}

export type GetLogosProps = Omit<
  GetProps<string[], unknown, GetLogosQueryParams, void>,
  "path"
>;

/**
 * Get a list of cobranded logos based on parameters
 *
 * This will return a list the branded logo urls to be sourced within a given context (app banner, patient summary, email, fax, etc.)
 */
export const GetLogos = (props: GetLogosProps) => (
  <Get<string[], unknown, GetLogosQueryParams, void>
    path={`/cobranding/logos`}
    {...props}
  />
);

export type UseGetLogosProps = Omit<
  UseGetProps<string[], unknown, GetLogosQueryParams, void>,
  "path"
>;

/**
 * Get a list of cobranded logos based on parameters
 *
 * This will return a list the branded logo urls to be sourced within a given context (app banner, patient summary, email, fax, etc.)
 */
export const useGetLogos = (props: UseGetLogosProps) =>
  useGet<string[], unknown, GetLogosQueryParams, void>(
    `/cobranding/logos`,
    props
  );

export interface GetServiceRequestOutreachOpportunitiesPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestOutreachOpportunitiesProps = Omit<
  GetProps<
    OutreachOpportunity[],
    unknown,
    void,
    GetServiceRequestOutreachOpportunitiesPathParams
  >,
  "path"
> &
  GetServiceRequestOutreachOpportunitiesPathParams;

export const GetServiceRequestOutreachOpportunities = ({
  id,
  ...props
}: GetServiceRequestOutreachOpportunitiesProps) => (
  <Get<
    OutreachOpportunity[],
    unknown,
    void,
    GetServiceRequestOutreachOpportunitiesPathParams
  >
    path={`/serviceRequest/${id}/outreachOpportunity`}
    {...props}
  />
);

export type UseGetServiceRequestOutreachOpportunitiesProps = Omit<
  UseGetProps<
    OutreachOpportunity[],
    unknown,
    void,
    GetServiceRequestOutreachOpportunitiesPathParams
  >,
  "path"
> &
  GetServiceRequestOutreachOpportunitiesPathParams;

export const useGetServiceRequestOutreachOpportunities = ({
  id,
  ...props
}: UseGetServiceRequestOutreachOpportunitiesProps) =>
  useGet<
    OutreachOpportunity[],
    unknown,
    void,
    GetServiceRequestOutreachOpportunitiesPathParams
  >(
    (paramsInPath: GetServiceRequestOutreachOpportunitiesPathParams) =>
      `/serviceRequest/${paramsInPath.id}/outreachOpportunity`,
    { pathParams: { id }, ...props }
  );

export interface FormatSrAsFhirPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type FormatSrAsFhirProps = Omit<
  GetProps<string, unknown, void, FormatSrAsFhirPathParams>,
  "path"
> &
  FormatSrAsFhirPathParams;

export const FormatSrAsFhir = ({ id, ...props }: FormatSrAsFhirProps) => (
  <Get<string, unknown, void, FormatSrAsFhirPathParams>
    path={`/serviceRequest/${id}/formatAsFhir`}
    {...props}
  />
);

export type UseFormatSrAsFhirProps = Omit<
  UseGetProps<string, unknown, void, FormatSrAsFhirPathParams>,
  "path"
> &
  FormatSrAsFhirPathParams;

export const useFormatSrAsFhir = ({ id, ...props }: UseFormatSrAsFhirProps) =>
  useGet<string, unknown, void, FormatSrAsFhirPathParams>(
    (paramsInPath: FormatSrAsFhirPathParams) =>
      `/serviceRequest/${paramsInPath.id}/formatAsFhir`,
    { pathParams: { id }, ...props }
  );

export interface GetNextNegationStatusPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export interface GetNextNegationStatusRequestBody {
  withdrawnReason?: string;
}

export type GetNextNegationStatusProps = Omit<
  MutateProps<
    AuthStatus,
    unknown,
    void,
    GetNextNegationStatusRequestBody,
    GetNextNegationStatusPathParams
  >,
  "path" | "verb"
> &
  GetNextNegationStatusPathParams;

export const GetNextNegationStatus = ({
  id,
  ...props
}: GetNextNegationStatusProps) => (
  <Mutate<
    AuthStatus,
    unknown,
    void,
    GetNextNegationStatusRequestBody,
    GetNextNegationStatusPathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/getNextNegationStatus`}
    {...props}
  />
);

export type UseGetNextNegationStatusProps = Omit<
  UseMutateProps<
    AuthStatus,
    unknown,
    void,
    GetNextNegationStatusRequestBody,
    GetNextNegationStatusPathParams
  >,
  "path" | "verb"
> &
  GetNextNegationStatusPathParams;

export const useGetNextNegationStatus = ({
  id,
  ...props
}: UseGetNextNegationStatusProps) =>
  useMutate<
    AuthStatus,
    unknown,
    void,
    GetNextNegationStatusRequestBody,
    GetNextNegationStatusPathParams
  >(
    "POST",
    (paramsInPath: GetNextNegationStatusPathParams) =>
      `/serviceRequest/${paramsInPath.id}/getNextNegationStatus`,
    { pathParams: { id }, ...props }
  );

export interface GetNextAllowedAuthStatusesResponse {
  nextAllowedAuthStatuses?: AuthStatusWithLabel[];
}

export interface GetNextAllowedAuthStatusesPathParams {
  /**
   * A unique identifier for a service request
   */
  id: string;
}

export type GetNextAllowedAuthStatusesProps = Omit<
  GetProps<
    GetNextAllowedAuthStatusesResponse,
    unknown,
    void,
    GetNextAllowedAuthStatusesPathParams
  >,
  "path"
> &
  GetNextAllowedAuthStatusesPathParams;

export const GetNextAllowedAuthStatuses = ({
  id,
  ...props
}: GetNextAllowedAuthStatusesProps) => (
  <Get<
    GetNextAllowedAuthStatusesResponse,
    unknown,
    void,
    GetNextAllowedAuthStatusesPathParams
  >
    path={`/serviceRequest/${id}/nextAllowedAuthStatuses`}
    {...props}
  />
);

export type UseGetNextAllowedAuthStatusesProps = Omit<
  UseGetProps<
    GetNextAllowedAuthStatusesResponse,
    unknown,
    void,
    GetNextAllowedAuthStatusesPathParams
  >,
  "path"
> &
  GetNextAllowedAuthStatusesPathParams;

export const useGetNextAllowedAuthStatuses = ({
  id,
  ...props
}: UseGetNextAllowedAuthStatusesProps) =>
  useGet<
    GetNextAllowedAuthStatusesResponse,
    unknown,
    void,
    GetNextAllowedAuthStatusesPathParams
  >(
    (paramsInPath: GetNextAllowedAuthStatusesPathParams) =>
      `/serviceRequest/${paramsInPath.id}/nextAllowedAuthStatuses`,
    { pathParams: { id }, ...props }
  );

export interface GetAllowedPendingReasonsResponse {
  allowedPendingReasons?: PendingReasonsWithLabel[];
}

export interface GetAllowedPendingReasonsPathParams {
  /**
   * A unique identifier for a service request
   */
  id: string;
}

export type GetAllowedPendingReasonsProps = Omit<
  GetProps<
    GetAllowedPendingReasonsResponse,
    unknown,
    void,
    GetAllowedPendingReasonsPathParams
  >,
  "path"
> &
  GetAllowedPendingReasonsPathParams;

export const GetAllowedPendingReasons = ({
  id,
  ...props
}: GetAllowedPendingReasonsProps) => (
  <Get<
    GetAllowedPendingReasonsResponse,
    unknown,
    void,
    GetAllowedPendingReasonsPathParams
  >
    path={`/statusTransitionConfiguration/${id}/getAllowedPendingReasons`}
    {...props}
  />
);

export type UseGetAllowedPendingReasonsProps = Omit<
  UseGetProps<
    GetAllowedPendingReasonsResponse,
    unknown,
    void,
    GetAllowedPendingReasonsPathParams
  >,
  "path"
> &
  GetAllowedPendingReasonsPathParams;

export const useGetAllowedPendingReasons = ({
  id,
  ...props
}: UseGetAllowedPendingReasonsProps) =>
  useGet<
    GetAllowedPendingReasonsResponse,
    unknown,
    void,
    GetAllowedPendingReasonsPathParams
  >(
    (paramsInPath: GetAllowedPendingReasonsPathParams) =>
      `/statusTransitionConfiguration/${paramsInPath.id}/getAllowedPendingReasons`,
    { pathParams: { id }, ...props }
  );

export interface GetAllowedReviewOutcomesQueryParams {
  /**
   * A unique identifier for a review
   */
  reviewId?: string;
  /**
   * Review type to get outcomes for
   */
  reviewType?: string;
}

export interface GetAllowedReviewOutcomesPathParams {
  /**
   * A unique identifier for a service request
   */
  id: string;
}

export type GetAllowedReviewOutcomesProps = Omit<
  GetProps<
    ReviewOutcomesWithLabel[],
    unknown,
    GetAllowedReviewOutcomesQueryParams,
    GetAllowedReviewOutcomesPathParams
  >,
  "path"
> &
  GetAllowedReviewOutcomesPathParams;

export const GetAllowedReviewOutcomes = ({
  id,
  ...props
}: GetAllowedReviewOutcomesProps) => (
  <Get<
    ReviewOutcomesWithLabel[],
    unknown,
    GetAllowedReviewOutcomesQueryParams,
    GetAllowedReviewOutcomesPathParams
  >
    path={`/statusTransitionConfiguration/${id}/getAllowedReviewOutcomes`}
    {...props}
  />
);

export type UseGetAllowedReviewOutcomesProps = Omit<
  UseGetProps<
    ReviewOutcomesWithLabel[],
    unknown,
    GetAllowedReviewOutcomesQueryParams,
    GetAllowedReviewOutcomesPathParams
  >,
  "path"
> &
  GetAllowedReviewOutcomesPathParams;

export const useGetAllowedReviewOutcomes = ({
  id,
  ...props
}: UseGetAllowedReviewOutcomesProps) =>
  useGet<
    ReviewOutcomesWithLabel[],
    unknown,
    GetAllowedReviewOutcomesQueryParams,
    GetAllowedReviewOutcomesPathParams
  >(
    (paramsInPath: GetAllowedReviewOutcomesPathParams) =>
      `/statusTransitionConfiguration/${paramsInPath.id}/getAllowedReviewOutcomes`,
    { pathParams: { id }, ...props }
  );

export interface GetPreviousServiceRequestPathParams {
  /**
   * A unique identifier for a service request
   */
  id: string;
}

export type GetPreviousServiceRequestProps = Omit<
  GetProps<
    ServiceRequestResponse,
    unknown,
    void,
    GetPreviousServiceRequestPathParams
  >,
  "path"
> &
  GetPreviousServiceRequestPathParams;

export const GetPreviousServiceRequest = ({
  id,
  ...props
}: GetPreviousServiceRequestProps) => (
  <Get<
    ServiceRequestResponse,
    unknown,
    void,
    GetPreviousServiceRequestPathParams
  >
    path={`/serviceRequest/${id}/previousServiceRequest`}
    {...props}
  />
);

export type UseGetPreviousServiceRequestProps = Omit<
  UseGetProps<
    ServiceRequestResponse,
    unknown,
    void,
    GetPreviousServiceRequestPathParams
  >,
  "path"
> &
  GetPreviousServiceRequestPathParams;

export const useGetPreviousServiceRequest = ({
  id,
  ...props
}: UseGetPreviousServiceRequestProps) =>
  useGet<
    ServiceRequestResponse,
    unknown,
    void,
    GetPreviousServiceRequestPathParams
  >(
    (paramsInPath: GetPreviousServiceRequestPathParams) =>
      `/serviceRequest/${paramsInPath.id}/previousServiceRequest`,
    { pathParams: { id }, ...props }
  );

export interface GetFirstConsecutiveDecisionServiceRequestPathParams {
  /**
   * A unique identifier for a service request
   */
  id: string;
}

export type GetFirstConsecutiveDecisionServiceRequestProps = Omit<
  GetProps<
    ServiceRequestResponse,
    unknown,
    void,
    GetFirstConsecutiveDecisionServiceRequestPathParams
  >,
  "path"
> &
  GetFirstConsecutiveDecisionServiceRequestPathParams;

export const GetFirstConsecutiveDecisionServiceRequest = ({
  id,
  ...props
}: GetFirstConsecutiveDecisionServiceRequestProps) => (
  <Get<
    ServiceRequestResponse,
    unknown,
    void,
    GetFirstConsecutiveDecisionServiceRequestPathParams
  >
    path={`/serviceRequest/${id}/firstConsecutiveDecisionServiceRequest`}
    {...props}
  />
);

export type UseGetFirstConsecutiveDecisionServiceRequestProps = Omit<
  UseGetProps<
    ServiceRequestResponse,
    unknown,
    void,
    GetFirstConsecutiveDecisionServiceRequestPathParams
  >,
  "path"
> &
  GetFirstConsecutiveDecisionServiceRequestPathParams;

export const useGetFirstConsecutiveDecisionServiceRequest = ({
  id,
  ...props
}: UseGetFirstConsecutiveDecisionServiceRequestProps) =>
  useGet<
    ServiceRequestResponse,
    unknown,
    void,
    GetFirstConsecutiveDecisionServiceRequestPathParams
  >(
    (paramsInPath: GetFirstConsecutiveDecisionServiceRequestPathParams) =>
      `/serviceRequest/${paramsInPath.id}/firstConsecutiveDecisionServiceRequest`,
    { pathParams: { id }, ...props }
  );

export type CreateDiagnosisCodeFilterProps = Omit<
  MutateProps<
    DiagnosisCodeFilter,
    ErrorResponse | void,
    void,
    DiagnosisCodeFilter,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a DiagnosisCodeFilter
 *
 * Creates a new instance of a `DiagnosisCodeFilter`.
 */
export const CreateDiagnosisCodeFilter = (
  props: CreateDiagnosisCodeFilterProps
) => (
  <Mutate<
    DiagnosisCodeFilter,
    ErrorResponse | void,
    void,
    DiagnosisCodeFilter,
    void
  >
    verb="POST"
    path={`/diagnosisCodeFilter`}
    {...props}
  />
);

export type UseCreateDiagnosisCodeFilterProps = Omit<
  UseMutateProps<
    DiagnosisCodeFilter,
    ErrorResponse | void,
    void,
    DiagnosisCodeFilter,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a DiagnosisCodeFilter
 *
 * Creates a new instance of a `DiagnosisCodeFilter`.
 */
export const useCreateDiagnosisCodeFilter = (
  props: UseCreateDiagnosisCodeFilterProps
) =>
  useMutate<
    DiagnosisCodeFilter,
    ErrorResponse | void,
    void,
    DiagnosisCodeFilter,
    void
  >("POST", `/diagnosisCodeFilter`, props);

export interface GetDiagnosisCodeFilterPathParams {
  /**
   * A unique identifier for a `DiagnosisCodeFilter`.
   */
  id: string;
}

export type GetDiagnosisCodeFilterProps = Omit<
  GetProps<
    DiagnosisCodeFilter,
    unknown,
    void,
    GetDiagnosisCodeFilterPathParams
  >,
  "path"
> &
  GetDiagnosisCodeFilterPathParams;

/**
 * Get a DiagnosisCodeFilter
 *
 * Gets the details of a single instance of a `DiagnosisCodeFilter`.
 */
export const GetDiagnosisCodeFilter = ({
  id,
  ...props
}: GetDiagnosisCodeFilterProps) => (
  <Get<DiagnosisCodeFilter, unknown, void, GetDiagnosisCodeFilterPathParams>
    path={`/diagnosisCodeFilter/${id}`}
    {...props}
  />
);

export type UseGetDiagnosisCodeFilterProps = Omit<
  UseGetProps<
    DiagnosisCodeFilter,
    unknown,
    void,
    GetDiagnosisCodeFilterPathParams
  >,
  "path"
> &
  GetDiagnosisCodeFilterPathParams;

/**
 * Get a DiagnosisCodeFilter
 *
 * Gets the details of a single instance of a `DiagnosisCodeFilter`.
 */
export const useGetDiagnosisCodeFilter = ({
  id,
  ...props
}: UseGetDiagnosisCodeFilterProps) =>
  useGet<DiagnosisCodeFilter, unknown, void, GetDiagnosisCodeFilterPathParams>(
    (paramsInPath: GetDiagnosisCodeFilterPathParams) =>
      `/diagnosisCodeFilter/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateDiagnosisCodeFilterPathParams {
  /**
   * A unique identifier for a `DiagnosisCodeFilter`.
   */
  id: string;
}

export type UpdateDiagnosisCodeFilterProps = Omit<
  MutateProps<
    DiagnosisCodeFilter,
    unknown,
    void,
    DiagnosisCodeFilter,
    UpdateDiagnosisCodeFilterPathParams
  >,
  "path" | "verb"
> &
  UpdateDiagnosisCodeFilterPathParams;

/**
 * Update a DiagnosisCodeFilter
 *
 * Updates an existing `DiagnosisCodeFilter`.
 */
export const UpdateDiagnosisCodeFilter = ({
  id,
  ...props
}: UpdateDiagnosisCodeFilterProps) => (
  <Mutate<
    DiagnosisCodeFilter,
    unknown,
    void,
    DiagnosisCodeFilter,
    UpdateDiagnosisCodeFilterPathParams
  >
    verb="PUT"
    path={`/diagnosisCodeFilter/${id}`}
    {...props}
  />
);

export type UseUpdateDiagnosisCodeFilterProps = Omit<
  UseMutateProps<
    DiagnosisCodeFilter,
    unknown,
    void,
    DiagnosisCodeFilter,
    UpdateDiagnosisCodeFilterPathParams
  >,
  "path" | "verb"
> &
  UpdateDiagnosisCodeFilterPathParams;

/**
 * Update a DiagnosisCodeFilter
 *
 * Updates an existing `DiagnosisCodeFilter`.
 */
export const useUpdateDiagnosisCodeFilter = ({
  id,
  ...props
}: UseUpdateDiagnosisCodeFilterProps) =>
  useMutate<
    DiagnosisCodeFilter,
    unknown,
    void,
    DiagnosisCodeFilter,
    UpdateDiagnosisCodeFilterPathParams
  >(
    "PUT",
    (paramsInPath: UpdateDiagnosisCodeFilterPathParams) =>
      `/diagnosisCodeFilter/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type CreateProcedureCodeFilterProps = Omit<
  MutateProps<
    ProcedureCodeFilter,
    ErrorResponse | void,
    void,
    ProcedureCodeFilter,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a ProcedureCodeFilter
 *
 * Creates a new instance of a `ProcedureCodeFilter`.
 */
export const CreateProcedureCodeFilter = (
  props: CreateProcedureCodeFilterProps
) => (
  <Mutate<
    ProcedureCodeFilter,
    ErrorResponse | void,
    void,
    ProcedureCodeFilter,
    void
  >
    verb="POST"
    path={`/procedureCodeFilter`}
    {...props}
  />
);

export type UseCreateProcedureCodeFilterProps = Omit<
  UseMutateProps<
    ProcedureCodeFilter,
    ErrorResponse | void,
    void,
    ProcedureCodeFilter,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a ProcedureCodeFilter
 *
 * Creates a new instance of a `ProcedureCodeFilter`.
 */
export const useCreateProcedureCodeFilter = (
  props: UseCreateProcedureCodeFilterProps
) =>
  useMutate<
    ProcedureCodeFilter,
    ErrorResponse | void,
    void,
    ProcedureCodeFilter,
    void
  >("POST", `/procedureCodeFilter`, props);

export interface GetProcedureCodeFilterPathParams {
  /**
   * A unique identifier for a `ProcedureCodeFilter`.
   */
  id: string;
}

export type GetProcedureCodeFilterProps = Omit<
  GetProps<
    ProcedureCodeFilter,
    unknown,
    void,
    GetProcedureCodeFilterPathParams
  >,
  "path"
> &
  GetProcedureCodeFilterPathParams;

/**
 * Get a ProcedureCodeFilter
 *
 * Gets the details of a single instance of a `ProcedureCodeFilter`.
 */
export const GetProcedureCodeFilter = ({
  id,
  ...props
}: GetProcedureCodeFilterProps) => (
  <Get<ProcedureCodeFilter, unknown, void, GetProcedureCodeFilterPathParams>
    path={`/procedureCodeFilter/${id}`}
    {...props}
  />
);

export type UseGetProcedureCodeFilterProps = Omit<
  UseGetProps<
    ProcedureCodeFilter,
    unknown,
    void,
    GetProcedureCodeFilterPathParams
  >,
  "path"
> &
  GetProcedureCodeFilterPathParams;

/**
 * Get a ProcedureCodeFilter
 *
 * Gets the details of a single instance of a `ProcedureCodeFilter`.
 */
export const useGetProcedureCodeFilter = ({
  id,
  ...props
}: UseGetProcedureCodeFilterProps) =>
  useGet<ProcedureCodeFilter, unknown, void, GetProcedureCodeFilterPathParams>(
    (paramsInPath: GetProcedureCodeFilterPathParams) =>
      `/procedureCodeFilter/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateProcedureCodeFilterPathParams {
  /**
   * A unique identifier for a `ProcedureCodeFilter`.
   */
  id: string;
}

export type UpdateProcedureCodeFilterProps = Omit<
  MutateProps<
    ProcedureCodeFilter,
    unknown,
    void,
    ProcedureCodeFilter,
    UpdateProcedureCodeFilterPathParams
  >,
  "path" | "verb"
> &
  UpdateProcedureCodeFilterPathParams;

/**
 * Update a ProcedureCodeFilter
 *
 * Updates an existing `ProcedureCodeFilter`.
 */
export const UpdateProcedureCodeFilter = ({
  id,
  ...props
}: UpdateProcedureCodeFilterProps) => (
  <Mutate<
    ProcedureCodeFilter,
    unknown,
    void,
    ProcedureCodeFilter,
    UpdateProcedureCodeFilterPathParams
  >
    verb="PUT"
    path={`/procedureCodeFilter/${id}`}
    {...props}
  />
);

export type UseUpdateProcedureCodeFilterProps = Omit<
  UseMutateProps<
    ProcedureCodeFilter,
    unknown,
    void,
    ProcedureCodeFilter,
    UpdateProcedureCodeFilterPathParams
  >,
  "path" | "verb"
> &
  UpdateProcedureCodeFilterPathParams;

/**
 * Update a ProcedureCodeFilter
 *
 * Updates an existing `ProcedureCodeFilter`.
 */
export const useUpdateProcedureCodeFilter = ({
  id,
  ...props
}: UseUpdateProcedureCodeFilterProps) =>
  useMutate<
    ProcedureCodeFilter,
    unknown,
    void,
    ProcedureCodeFilter,
    UpdateProcedureCodeFilterPathParams
  >(
    "PUT",
    (paramsInPath: UpdateProcedureCodeFilterPathParams) =>
      `/procedureCodeFilter/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type GetclinicalquestionsProps = Omit<
  GetProps<ClinicalQuestion[], unknown, void, void>,
  "path"
>;

/**
 * List All clinicalquestions
 *
 * Gets a list of all `ClinicalQuestion` entities.
 */
export const Getclinicalquestions = (props: GetclinicalquestionsProps) => (
  <Get<ClinicalQuestion[], unknown, void, void>
    path={`/clinicalQuestion`}
    {...props}
  />
);

export type UseGetclinicalquestionsProps = Omit<
  UseGetProps<ClinicalQuestion[], unknown, void, void>,
  "path"
>;

/**
 * List All clinicalquestions
 *
 * Gets a list of all `ClinicalQuestion` entities.
 */
export const useGetclinicalquestions = (props: UseGetclinicalquestionsProps) =>
  useGet<ClinicalQuestion[], unknown, void, void>(`/clinicalQuestion`, props);

export interface CreateClinicalQuestionPathParams {
  /**
   * The id of the clinical service that the question belongs to.
   */
  clinicalServiceId: string;
}

export type CreateClinicalQuestionProps = Omit<
  MutateProps<
    ClinicalQuestion,
    ErrorResponse | void,
    void,
    ClinicalQuestionPayload,
    CreateClinicalQuestionPathParams
  >,
  "path" | "verb"
> &
  CreateClinicalQuestionPathParams;

/**
 * Create a ClinicalQuestion
 *
 * Creates a new instance of a `ClinicalQuestion`.
 */
export const CreateClinicalQuestion = ({
  clinicalServiceId,
  ...props
}: CreateClinicalQuestionProps) => (
  <Mutate<
    ClinicalQuestion,
    ErrorResponse | void,
    void,
    ClinicalQuestionPayload,
    CreateClinicalQuestionPathParams
  >
    verb="POST"
    path={`/clinicalService/${clinicalServiceId}/clinicalQuestion`}
    {...props}
  />
);

export type UseCreateClinicalQuestionProps = Omit<
  UseMutateProps<
    ClinicalQuestion,
    ErrorResponse | void,
    void,
    ClinicalQuestionPayload,
    CreateClinicalQuestionPathParams
  >,
  "path" | "verb"
> &
  CreateClinicalQuestionPathParams;

/**
 * Create a ClinicalQuestion
 *
 * Creates a new instance of a `ClinicalQuestion`.
 */
export const useCreateClinicalQuestion = ({
  clinicalServiceId,
  ...props
}: UseCreateClinicalQuestionProps) =>
  useMutate<
    ClinicalQuestion,
    ErrorResponse | void,
    void,
    ClinicalQuestionPayload,
    CreateClinicalQuestionPathParams
  >(
    "POST",
    (paramsInPath: CreateClinicalQuestionPathParams) =>
      `/clinicalService/${paramsInPath.clinicalServiceId}/clinicalQuestion`,
    { pathParams: { clinicalServiceId }, ...props }
  );

export interface GetPublishedClinicalServicePathParams {
  /**
   * originalVersionId of clinical service
   */
  originalVersionId: string;
}

export type GetPublishedClinicalServiceProps = Omit<
  GetProps<
    ClinicalService,
    unknown,
    void,
    GetPublishedClinicalServicePathParams
  >,
  "path"
> &
  GetPublishedClinicalServicePathParams;

export const GetPublishedClinicalService = ({
  originalVersionId,
  ...props
}: GetPublishedClinicalServiceProps) => (
  <Get<ClinicalService, unknown, void, GetPublishedClinicalServicePathParams>
    path={`/clinicalService/${originalVersionId}/getPublishedVersionFromOriginalVersion`}
    {...props}
  />
);

export type UseGetPublishedClinicalServiceProps = Omit<
  UseGetProps<
    ClinicalService,
    unknown,
    void,
    GetPublishedClinicalServicePathParams
  >,
  "path"
> &
  GetPublishedClinicalServicePathParams;

export const useGetPublishedClinicalService = ({
  originalVersionId,
  ...props
}: UseGetPublishedClinicalServiceProps) =>
  useGet<ClinicalService, unknown, void, GetPublishedClinicalServicePathParams>(
    (paramsInPath: GetPublishedClinicalServicePathParams) =>
      `/clinicalService/${paramsInPath.originalVersionId}/getPublishedVersionFromOriginalVersion`,
    { pathParams: { originalVersionId }, ...props }
  );

export interface SearchClinicalServicesWithCodesQueryParams {
  /**
   * Searches for clinical services by contents of 'name' field
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  /**
   * If true, returns only Single Services
   */
  isSingleService?: boolean;
  /**
   * filters clinicalServices by given health plan name
   */
  healthPlanName?: string;
  /**
   * Searches for clinical services that have any of these codes (comma separated string of px codes)
   */
  codes?: string;
}

export type SearchClinicalServicesWithCodesProps = Omit<
  GetProps<
    ClinicalService[],
    unknown,
    SearchClinicalServicesWithCodesQueryParams,
    void
  >,
  "path"
>;

/**
 * searches for ClinicalService that have certain px codes
 */
export const SearchClinicalServicesWithCodes = (
  props: SearchClinicalServicesWithCodesProps
) => (
  <Get<
    ClinicalService[],
    unknown,
    SearchClinicalServicesWithCodesQueryParams,
    void
  >
    path={`/clinicalService/searchWithCodes`}
    {...props}
  />
);

export type UseSearchClinicalServicesWithCodesProps = Omit<
  UseGetProps<
    ClinicalService[],
    unknown,
    SearchClinicalServicesWithCodesQueryParams,
    void
  >,
  "path"
>;

/**
 * searches for ClinicalService that have certain px codes
 */
export const useSearchClinicalServicesWithCodes = (
  props: UseSearchClinicalServicesWithCodesProps
) =>
  useGet<
    ClinicalService[],
    unknown,
    SearchClinicalServicesWithCodesQueryParams,
    void
  >(`/clinicalService/searchWithCodes`, props);

export interface GetGuidelinesOnPolicyStampResponse {
  usedGuidelines?: Guideline[];
  unusedGuidelines?: Guideline[];
}

export interface GetGuidelinesOnPolicyStampQueryParams {
  /**
   * used to determine if a guideline has been used by a template with matching authstatus
   */
  authStatus: AuthStatus[];
}

export interface GetGuidelinesOnPolicyStampPathParams {
  /**
   * A unique identifier for a `Policy`.
   */
  id: string;
}

export type GetGuidelinesOnPolicyStampProps = Omit<
  GetProps<
    GetGuidelinesOnPolicyStampResponse,
    unknown,
    GetGuidelinesOnPolicyStampQueryParams,
    GetGuidelinesOnPolicyStampPathParams
  >,
  "path"
> &
  GetGuidelinesOnPolicyStampPathParams;

/**
 * Gets the requested policy stamp's guidelines
 *
 * Gets the `Guidelines` of a single instance of a `PolicyStamp`.
 */
export const GetGuidelinesOnPolicyStamp = ({
  id,
  ...props
}: GetGuidelinesOnPolicyStampProps) => (
  <Get<
    GetGuidelinesOnPolicyStampResponse,
    unknown,
    GetGuidelinesOnPolicyStampQueryParams,
    GetGuidelinesOnPolicyStampPathParams
  >
    path={`/policyStamp/${id}/getGuidelines`}
    {...props}
  />
);

export type UseGetGuidelinesOnPolicyStampProps = Omit<
  UseGetProps<
    GetGuidelinesOnPolicyStampResponse,
    unknown,
    GetGuidelinesOnPolicyStampQueryParams,
    GetGuidelinesOnPolicyStampPathParams
  >,
  "path"
> &
  GetGuidelinesOnPolicyStampPathParams;

/**
 * Gets the requested policy stamp's guidelines
 *
 * Gets the `Guidelines` of a single instance of a `PolicyStamp`.
 */
export const useGetGuidelinesOnPolicyStamp = ({
  id,
  ...props
}: UseGetGuidelinesOnPolicyStampProps) =>
  useGet<
    GetGuidelinesOnPolicyStampResponse,
    unknown,
    GetGuidelinesOnPolicyStampQueryParams,
    GetGuidelinesOnPolicyStampPathParams
  >(
    (paramsInPath: GetGuidelinesOnPolicyStampPathParams) =>
      `/policyStamp/${paramsInPath.id}/getGuidelines`,
    { pathParams: { id }, ...props }
  );

export interface GetPolicyStampQueryParams {
  query?: string;
  max?: number;
  policyType?: string;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  expirationStartDate?: string;
  expirationEndDate?: string;
  stateOfUnion?: string;
  view?: string;
}

export type GetPolicyStampProps = Omit<
  GetProps<PolicyStamp[], unknown, GetPolicyStampQueryParams, void>,
  "path"
>;

/**
 * List All Policy Stamps
 *
 * Gets a list of all `PolicyStamp` entities.
 */
export const GetPolicyStamp = (props: GetPolicyStampProps) => (
  <Get<PolicyStamp[], unknown, GetPolicyStampQueryParams, void>
    path={`/policyStamp`}
    {...props}
  />
);

export type UseGetPolicyStampProps = Omit<
  UseGetProps<PolicyStamp[], unknown, GetPolicyStampQueryParams, void>,
  "path"
>;

/**
 * List All Policy Stamps
 *
 * Gets a list of all `PolicyStamp` entities.
 */
export const useGetPolicyStamp = (props: UseGetPolicyStampProps) =>
  useGet<PolicyStamp[], unknown, GetPolicyStampQueryParams, void>(
    `/policyStamp`,
    props
  );

export type CreatePolicyStampProps = Omit<
  MutateProps<PolicyStamp, ErrorResponse | void, void, PolicyStamp, void>,
  "path" | "verb"
>;

/**
 * Create a Policy Stamp
 *
 * Creates a new instance of a `PolicyStamp`.
 */
export const CreatePolicyStamp = (props: CreatePolicyStampProps) => (
  <Mutate<PolicyStamp, ErrorResponse | void, void, PolicyStamp, void>
    verb="POST"
    path={`/policyStamp`}
    {...props}
  />
);

export type UseCreatePolicyStampProps = Omit<
  UseMutateProps<PolicyStamp, ErrorResponse | void, void, PolicyStamp, void>,
  "path" | "verb"
>;

/**
 * Create a Policy Stamp
 *
 * Creates a new instance of a `PolicyStamp`.
 */
export const useCreatePolicyStamp = (props: UseCreatePolicyStampProps) =>
  useMutate<PolicyStamp, ErrorResponse | void, void, PolicyStamp, void>(
    "POST",
    `/policyStamp`,
    props
  );

export interface GetSinglePolicyStampPathParams {
  /**
   * A unique identifier for a `PolicyStamp`.
   */
  id: string;
}

export type GetSinglePolicyStampProps = Omit<
  GetProps<PolicyStamp, unknown, void, GetSinglePolicyStampPathParams>,
  "path"
> &
  GetSinglePolicyStampPathParams;

/**
 * Gets the requested policy stamp
 *
 * Gets the details of a single instance of a `PolicyStamp`.
 */
export const GetSinglePolicyStamp = ({
  id,
  ...props
}: GetSinglePolicyStampProps) => (
  <Get<PolicyStamp, unknown, void, GetSinglePolicyStampPathParams>
    path={`/policyStamp/${id}`}
    {...props}
  />
);

export type UseGetSinglePolicyStampProps = Omit<
  UseGetProps<PolicyStamp, unknown, void, GetSinglePolicyStampPathParams>,
  "path"
> &
  GetSinglePolicyStampPathParams;

/**
 * Gets the requested policy stamp
 *
 * Gets the details of a single instance of a `PolicyStamp`.
 */
export const useGetSinglePolicyStamp = ({
  id,
  ...props
}: UseGetSinglePolicyStampProps) =>
  useGet<PolicyStamp, unknown, void, GetSinglePolicyStampPathParams>(
    (paramsInPath: GetSinglePolicyStampPathParams) =>
      `/policyStamp/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdatePolicyStampPathParams {
  /**
   * A unique identifier for a `PolicyStamp`.
   */
  id: string;
}

export type UpdatePolicyStampProps = Omit<
  MutateProps<
    PolicyStamp,
    unknown,
    void,
    PolicyStamp,
    UpdatePolicyStampPathParams
  >,
  "path" | "verb"
> &
  UpdatePolicyStampPathParams;

/**
 * Update a PolicyStamp
 *
 * Updates an existing `PolicyStamp`.
 */
export const UpdatePolicyStamp = ({ id, ...props }: UpdatePolicyStampProps) => (
  <Mutate<PolicyStamp, unknown, void, PolicyStamp, UpdatePolicyStampPathParams>
    verb="PUT"
    path={`/policyStamp/${id}`}
    {...props}
  />
);

export type UseUpdatePolicyStampProps = Omit<
  UseMutateProps<
    PolicyStamp,
    unknown,
    void,
    PolicyStamp,
    UpdatePolicyStampPathParams
  >,
  "path" | "verb"
> &
  UpdatePolicyStampPathParams;

/**
 * Update a PolicyStamp
 *
 * Updates an existing `PolicyStamp`.
 */
export const useUpdatePolicyStamp = ({
  id,
  ...props
}: UseUpdatePolicyStampProps) =>
  useMutate<
    PolicyStamp,
    unknown,
    void,
    PolicyStamp,
    UpdatePolicyStampPathParams
  >(
    "PUT",
    (paramsInPath: UpdatePolicyStampPathParams) =>
      `/policyStamp/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeletePolicyStampProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a PolicyStamp
 *
 * Deletes an existing `PolicyStamp`.
 */
export const DeletePolicyStamp = (props: DeletePolicyStampProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/policyStamp`}
    {...props}
  />
);

export type UseDeletePolicyStampProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a PolicyStamp
 *
 * Deletes an existing `PolicyStamp`.
 */
export const useDeletePolicyStamp = (props: UseDeletePolicyStampProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/policyStamp`, {
    ...props,
  });

export interface CreatePolicyStampRevisionPathParams {
  id: string;
}

export type CreatePolicyStampRevisionProps = Omit<
  MutateProps<
    PolicyStamp,
    ErrorResponse | void,
    void,
    void,
    CreatePolicyStampRevisionPathParams
  >,
  "path" | "verb"
> &
  CreatePolicyStampRevisionPathParams;

/**
 * Create a Policy Stamp Revision
 *
 * A `PolicyStamp` revision to be created.
 */
export const CreatePolicyStampRevision = ({
  id,
  ...props
}: CreatePolicyStampRevisionProps) => (
  <Mutate<
    PolicyStamp,
    ErrorResponse | void,
    void,
    void,
    CreatePolicyStampRevisionPathParams
  >
    verb="POST"
    path={`/policyStamp/${id}/createRevision`}
    {...props}
  />
);

export type UseCreatePolicyStampRevisionProps = Omit<
  UseMutateProps<
    PolicyStamp,
    ErrorResponse | void,
    void,
    void,
    CreatePolicyStampRevisionPathParams
  >,
  "path" | "verb"
> &
  CreatePolicyStampRevisionPathParams;

/**
 * Create a Policy Stamp Revision
 *
 * A `PolicyStamp` revision to be created.
 */
export const useCreatePolicyStampRevision = ({
  id,
  ...props
}: UseCreatePolicyStampRevisionProps) =>
  useMutate<
    PolicyStamp,
    ErrorResponse | void,
    void,
    void,
    CreatePolicyStampRevisionPathParams
  >(
    "POST",
    (paramsInPath: CreatePolicyStampRevisionPathParams) =>
      `/policyStamp/${paramsInPath.id}/createRevision`,
    { pathParams: { id }, ...props }
  );

export interface GetGuidelineQueryParams {
  /**
   * services for guideline by name
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * returns collections of drafts marked as productionReady
   */
  productionReady?: string;
  published?: string;
  healthPlanName?: string;
  procedureCode?: string;
  policyType?: string;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
}

export type GetGuidelineProps = Omit<
  GetProps<Guideline[], unknown, GetGuidelineQueryParams, void>,
  "path"
>;

/**
 * List All Guidelines
 *
 * Gets a list of all `Guideline` entities.
 */
export const GetGuideline = (props: GetGuidelineProps) => (
  <Get<Guideline[], unknown, GetGuidelineQueryParams, void>
    path={`/guideline`}
    {...props}
  />
);

export type UseGetGuidelineProps = Omit<
  UseGetProps<Guideline[], unknown, GetGuidelineQueryParams, void>,
  "path"
>;

/**
 * List All Guidelines
 *
 * Gets a list of all `Guideline` entities.
 */
export const useGetGuideline = (props: UseGetGuidelineProps) =>
  useGet<Guideline[], unknown, GetGuidelineQueryParams, void>(
    `/guideline`,
    props
  );

export interface CreateGuidelineQueryParams {
  policyId: string;
}

export type CreateGuidelineProps = Omit<
  MutateProps<
    Guideline,
    ErrorResponse | void,
    CreateGuidelineQueryParams,
    Guideline,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a Guideline
 *
 * Creates a new instance of a `Guideline`.
 */
export const CreateGuideline = (props: CreateGuidelineProps) => (
  <Mutate<
    Guideline,
    ErrorResponse | void,
    CreateGuidelineQueryParams,
    Guideline,
    void
  >
    verb="POST"
    path={`/guideline`}
    {...props}
  />
);

export type UseCreateGuidelineProps = Omit<
  UseMutateProps<
    Guideline,
    ErrorResponse | void,
    CreateGuidelineQueryParams,
    Guideline,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a Guideline
 *
 * Creates a new instance of a `Guideline`.
 */
export const useCreateGuideline = (props: UseCreateGuidelineProps) =>
  useMutate<
    Guideline,
    ErrorResponse | void,
    CreateGuidelineQueryParams,
    Guideline,
    void
  >("POST", `/guideline`, props);

export interface GetSingleGuidelinePathParams {
  /**
   * A unique identifier for a `Guideline`.
   */
  id: string;
}

export type GetSingleGuidelineProps = Omit<
  GetProps<Guideline, unknown, void, GetSingleGuidelinePathParams>,
  "path"
> &
  GetSingleGuidelinePathParams;

/**
 * Gets the requested Guideline
 *
 * Gets the details of a single instance of a `Guideline`.
 */
export const GetSingleGuideline = ({
  id,
  ...props
}: GetSingleGuidelineProps) => (
  <Get<Guideline, unknown, void, GetSingleGuidelinePathParams>
    path={`/guideline/${id}`}
    {...props}
  />
);

export type UseGetSingleGuidelineProps = Omit<
  UseGetProps<Guideline, unknown, void, GetSingleGuidelinePathParams>,
  "path"
> &
  GetSingleGuidelinePathParams;

/**
 * Gets the requested Guideline
 *
 * Gets the details of a single instance of a `Guideline`.
 */
export const useGetSingleGuideline = ({
  id,
  ...props
}: UseGetSingleGuidelineProps) =>
  useGet<Guideline, unknown, void, GetSingleGuidelinePathParams>(
    (paramsInPath: GetSingleGuidelinePathParams) =>
      `/guideline/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateGuidelinePathParams {
  /**
   * A unique identifier for a `Guideline`.
   */
  id: string;
}

export type UpdateGuidelineProps = Omit<
  MutateProps<Guideline, unknown, void, Guideline, UpdateGuidelinePathParams>,
  "path" | "verb"
> &
  UpdateGuidelinePathParams;

/**
 * Update a Guideline
 *
 * Updates an existing `Guideline`.
 */
export const UpdateGuideline = ({ id, ...props }: UpdateGuidelineProps) => (
  <Mutate<Guideline, unknown, void, Guideline, UpdateGuidelinePathParams>
    verb="PUT"
    path={`/guideline/${id}`}
    {...props}
  />
);

export type UseUpdateGuidelineProps = Omit<
  UseMutateProps<
    Guideline,
    unknown,
    void,
    Guideline,
    UpdateGuidelinePathParams
  >,
  "path" | "verb"
> &
  UpdateGuidelinePathParams;

/**
 * Update a Guideline
 *
 * Updates an existing `Guideline`.
 */
export const useUpdateGuideline = ({ id, ...props }: UseUpdateGuidelineProps) =>
  useMutate<Guideline, unknown, void, Guideline, UpdateGuidelinePathParams>(
    "PUT",
    (paramsInPath: UpdateGuidelinePathParams) =>
      `/guideline/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteGuidelineProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Guideline
 *
 * Deletes an existing `Guideline`.
 */
export const DeleteGuideline = (props: DeleteGuidelineProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/guideline`}
    {...props}
  />
);

export type UseDeleteGuidelineProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Guideline
 *
 * Deletes an existing `Guideline`.
 */
export const useDeleteGuideline = (props: UseDeleteGuidelineProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/guideline`, {
    ...props,
  });

export interface GetIndicationsPathParams {
  /**
   * A unique identifier for a `IndicationElasticIndex`
   */
  originalVersionId: string;
}

export type GetIndicationsProps = Omit<
  GetProps<IndicationElasticIndex[], unknown, void, GetIndicationsPathParams>,
  "path"
> &
  GetIndicationsPathParams;

/**
 * Get an array of IndicationElasticIndex
 *
 * Gets a list of `IndicationElasticIndex`.
 */
export const GetIndications = ({
  originalVersionId,
  ...props
}: GetIndicationsProps) => (
  <Get<IndicationElasticIndex[], unknown, void, GetIndicationsPathParams>
    path={`/guideline/${originalVersionId}/indications`}
    {...props}
  />
);

export type UseGetIndicationsProps = Omit<
  UseGetProps<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsPathParams
  >,
  "path"
> &
  GetIndicationsPathParams;

/**
 * Get an array of IndicationElasticIndex
 *
 * Gets a list of `IndicationElasticIndex`.
 */
export const useGetIndications = ({
  originalVersionId,
  ...props
}: UseGetIndicationsProps) =>
  useGet<IndicationElasticIndex[], unknown, void, GetIndicationsPathParams>(
    (paramsInPath: GetIndicationsPathParams) =>
      `/guideline/${paramsInPath.originalVersionId}/indications`,
    { pathParams: { originalVersionId }, ...props }
  );

export interface AutoGenerateGuidelineIndicationsPathParams {
  /**
   * A unique identifier for a `Guideline`.
   */
  id: string;
}

export type AutoGenerateGuidelineIndicationsProps = Omit<
  MutateProps<
    Guideline,
    unknown,
    void,
    void,
    AutoGenerateGuidelineIndicationsPathParams
  >,
  "path" | "verb"
> &
  AutoGenerateGuidelineIndicationsPathParams;

/**
 * Updates the Guideline with auto-generated indications
 *
 * Gets a `Guideline` with updated indications.
 */
export const AutoGenerateGuidelineIndications = ({
  id,
  ...props
}: AutoGenerateGuidelineIndicationsProps) => (
  <Mutate<
    Guideline,
    unknown,
    void,
    void,
    AutoGenerateGuidelineIndicationsPathParams
  >
    verb="PUT"
    path={`/guideline/autoGenerateIndications/${id}`}
    {...props}
  />
);

export type UseAutoGenerateGuidelineIndicationsProps = Omit<
  UseMutateProps<
    Guideline,
    unknown,
    void,
    void,
    AutoGenerateGuidelineIndicationsPathParams
  >,
  "path" | "verb"
> &
  AutoGenerateGuidelineIndicationsPathParams;

/**
 * Updates the Guideline with auto-generated indications
 *
 * Gets a `Guideline` with updated indications.
 */
export const useAutoGenerateGuidelineIndications = ({
  id,
  ...props
}: UseAutoGenerateGuidelineIndicationsProps) =>
  useMutate<
    Guideline,
    unknown,
    void,
    void,
    AutoGenerateGuidelineIndicationsPathParams
  >(
    "PUT",
    (paramsInPath: AutoGenerateGuidelineIndicationsPathParams) =>
      `/guideline/autoGenerateIndications/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetGuidelineCountQueryParams {
  /**
   * services for guideline by name
   */
  query?: string;
  /**
   * returns collections of drafts marked as productionReady
   */
  productionReady?: string;
  healthPlanName?: string;
  procedureCode?: string;
  policyType?: string;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
}

export type GetGuidelineCountProps = Omit<
  GetProps<CountResult, unknown, GetGuidelineCountQueryParams, void>,
  "path"
>;

/**
 * List All policies
 *
 * Gets a list of all `Guidelines` entities.
 */
export const GetGuidelineCount = (props: GetGuidelineCountProps) => (
  <Get<CountResult, unknown, GetGuidelineCountQueryParams, void>
    path={`/guideline/count`}
    {...props}
  />
);

export type UseGetGuidelineCountProps = Omit<
  UseGetProps<CountResult, unknown, GetGuidelineCountQueryParams, void>,
  "path"
>;

/**
 * List All policies
 *
 * Gets a list of all `Guidelines` entities.
 */
export const useGetGuidelineCount = (props: UseGetGuidelineCountProps) =>
  useGet<CountResult, unknown, GetGuidelineCountQueryParams, void>(
    `/guideline/count`,
    props
  );

export type GuidelineBatchUpdateProps = Omit<
  MutateProps<Guideline[], unknown, void, Guideline[], void>,
  "path" | "verb"
>;

/**
 * Updated guideline as published.
 */
export const GuidelineBatchUpdate = (props: GuidelineBatchUpdateProps) => (
  <Mutate<Guideline[], unknown, void, Guideline[], void>
    verb="PATCH"
    path={`/guideline/batch`}
    {...props}
  />
);

export type UseGuidelineBatchUpdateProps = Omit<
  UseMutateProps<Guideline[], unknown, void, Guideline[], void>,
  "path" | "verb"
>;

/**
 * Updated guideline as published.
 */
export const useGuidelineBatchUpdate = (props: UseGuidelineBatchUpdateProps) =>
  useMutate<Guideline[], unknown, void, Guideline[], void>(
    "PATCH",
    `/guideline/batch`,
    props
  );

export interface GetServiceRequestGuidelinesQueryParams {
  /**
   * service request id
   */
  serviceRequestId?: string;
}

export type GetServiceRequestGuidelinesProps = Omit<
  GetProps<
    GuidelinePolicyMap[],
    unknown,
    GetServiceRequestGuidelinesQueryParams,
    void
  >,
  "path"
>;

/**
 * List All Guidelines for Service Request
 *
 * Gets a list of all `GuidelinePolicyMap` entities for a specific service request.
 */
export const GetServiceRequestGuidelines = (
  props: GetServiceRequestGuidelinesProps
) => (
  <Get<
    GuidelinePolicyMap[],
    unknown,
    GetServiceRequestGuidelinesQueryParams,
    void
  >
    path={`/serviceRequestGuidelines`}
    {...props}
  />
);

export type UseGetServiceRequestGuidelinesProps = Omit<
  UseGetProps<
    GuidelinePolicyMap[],
    unknown,
    GetServiceRequestGuidelinesQueryParams,
    void
  >,
  "path"
>;

/**
 * List All Guidelines for Service Request
 *
 * Gets a list of all `GuidelinePolicyMap` entities for a specific service request.
 */
export const useGetServiceRequestGuidelines = (
  props: UseGetServiceRequestGuidelinesProps
) =>
  useGet<
    GuidelinePolicyMap[],
    unknown,
    GetServiceRequestGuidelinesQueryParams,
    void
  >(`/serviceRequestGuidelines`, props);

export interface GetIndicationsByIndicationGroupPathParams {
  indicationGroupId: string;
}

export type GetIndicationsByIndicationGroupProps = Omit<
  GetProps<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationGroupPathParams
  >,
  "path"
> &
  GetIndicationsByIndicationGroupPathParams;

/**
 * Get an array of IndicationElasticIndex
 *
 * Gets a list of `IndicationElasticIndex`.
 */
export const GetIndicationsByIndicationGroup = ({
  indicationGroupId,
  ...props
}: GetIndicationsByIndicationGroupProps) => (
  <Get<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationGroupPathParams
  >
    path={`/indication/indicationGroupId/${indicationGroupId}`}
    {...props}
  />
);

export type UseGetIndicationsByIndicationGroupProps = Omit<
  UseGetProps<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationGroupPathParams
  >,
  "path"
> &
  GetIndicationsByIndicationGroupPathParams;

/**
 * Get an array of IndicationElasticIndex
 *
 * Gets a list of `IndicationElasticIndex`.
 */
export const useGetIndicationsByIndicationGroup = ({
  indicationGroupId,
  ...props
}: UseGetIndicationsByIndicationGroupProps) =>
  useGet<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationGroupPathParams
  >(
    (paramsInPath: GetIndicationsByIndicationGroupPathParams) =>
      `/indication/indicationGroupId/${paramsInPath.indicationGroupId}`,
    { pathParams: { indicationGroupId }, ...props }
  );

export interface GetIndicationsByIndicationUidPathParams {
  indicationUid: string;
}

export type GetIndicationsByIndicationUidProps = Omit<
  GetProps<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationUidPathParams
  >,
  "path"
> &
  GetIndicationsByIndicationUidPathParams;

/**
 * Get an array of IndicationElasticIndex
 *
 * Gets a list of `IndicationElasticIndex`.
 */
export const GetIndicationsByIndicationUid = ({
  indicationUid,
  ...props
}: GetIndicationsByIndicationUidProps) => (
  <Get<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationUidPathParams
  >
    path={`/indication/indicationUid/${indicationUid}`}
    {...props}
  />
);

export type UseGetIndicationsByIndicationUidProps = Omit<
  UseGetProps<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationUidPathParams
  >,
  "path"
> &
  GetIndicationsByIndicationUidPathParams;

/**
 * Get an array of IndicationElasticIndex
 *
 * Gets a list of `IndicationElasticIndex`.
 */
export const useGetIndicationsByIndicationUid = ({
  indicationUid,
  ...props
}: UseGetIndicationsByIndicationUidProps) =>
  useGet<
    IndicationElasticIndex[],
    unknown,
    void,
    GetIndicationsByIndicationUidPathParams
  >(
    (paramsInPath: GetIndicationsByIndicationUidPathParams) =>
      `/indication/indicationUid/${paramsInPath.indicationUid}`,
    { pathParams: { indicationUid }, ...props }
  );

export interface GetClinicalServicesQueryParams {
  /**
   * Searches for clinical services by name
   */
  query?: string;
  /**
   * Searches for clinical services by carePathId
   */
  carePathId?: string;
  /**
   * Searches for clinical services by serviceCategoryName
   */
  serviceCategoryName?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * If true, returns only Single Services
   */
  isSingleService?: boolean;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collection of services with productionReady set to true or false
   */
  productionReady?: string;
  /**
   * If false, returns only services that don't require procedure codes
   */
  pxRequiredToSubmit?: boolean;
  /**
   * filters clinicalServices by given health plan name
   */
  healthPlanName?: string;
  /**
   * filters clinicalServices by given health plan ids
   */
  healthPlanIds?: string;
  /**
   * returns a subset of fields
   */
  view?: string;
}

export type GetClinicalServicesProps = Omit<
  GetProps<ClinicalService[], unknown, GetClinicalServicesQueryParams, void>,
  "path"
>;

/**
 * List All clinicalServices
 *
 * Gets a list of all `ClinicalService` entities.
 */
export const GetClinicalServices = (props: GetClinicalServicesProps) => (
  <Get<ClinicalService[], unknown, GetClinicalServicesQueryParams, void>
    path={`/clinicalService`}
    {...props}
  />
);

export type UseGetClinicalServicesProps = Omit<
  UseGetProps<ClinicalService[], unknown, GetClinicalServicesQueryParams, void>,
  "path"
>;

/**
 * List All clinicalServices
 *
 * Gets a list of all `ClinicalService` entities.
 */
export const useGetClinicalServices = (props: UseGetClinicalServicesProps) =>
  useGet<ClinicalService[], unknown, GetClinicalServicesQueryParams, void>(
    `/clinicalService`,
    props
  );

export type CreateClinicalServiceWithoutCarePathProps = Omit<
  MutateProps<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a ClinicalService (used to create single services)
 */
export const CreateClinicalServiceWithoutCarePath = (
  props: CreateClinicalServiceWithoutCarePathProps
) => (
  <Mutate<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    void
  >
    verb="POST"
    path={`/clinicalService`}
    {...props}
  />
);

export type UseCreateClinicalServiceWithoutCarePathProps = Omit<
  UseMutateProps<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a ClinicalService (used to create single services)
 */
export const useCreateClinicalServiceWithoutCarePath = (
  props: UseCreateClinicalServiceWithoutCarePathProps
) =>
  useMutate<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    void
  >("POST", `/clinicalService`, props);

export interface GetClinicalServicesCountQueryParams {
  /**
   * Searches for clinical services by name
   */
  query?: string;
  /**
   * Searches for clinical services by carePathId
   */
  carePathId?: string;
  /**
   * Searches for clinical services by serviceCategoryName
   */
  serviceCategoryName?: string;
  /**
   * If true, returns only Single Services
   */
  isSingleService?: boolean;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collection of services with productionReady set to true or false
   */
  productionReady?: string;
  /**
   * If false, returns only services that don't require procedure codes
   */
  pxRequiredToSubmit?: boolean;
  /**
   * filters clinicalServices by given health plan name
   */
  healthPlanName?: string;
  /**
   * filters clinicalServices by given health plan ids
   */
  healthPlanIds?: string;
  specialty?: string;
}

export type GetClinicalServicesCountProps = Omit<
  GetProps<CountResult, unknown, GetClinicalServicesCountQueryParams, void>,
  "path"
>;

/**
 * List All Clinical Services
 *
 * Gets a list of all `ClinicalServices` entities.
 */
export const GetClinicalServicesCount = (
  props: GetClinicalServicesCountProps
) => (
  <Get<CountResult, unknown, GetClinicalServicesCountQueryParams, void>
    path={`/clinicalService/count`}
    {...props}
  />
);

export type UseGetClinicalServicesCountProps = Omit<
  UseGetProps<CountResult, unknown, GetClinicalServicesCountQueryParams, void>,
  "path"
>;

/**
 * List All Clinical Services
 *
 * Gets a list of all `ClinicalServices` entities.
 */
export const useGetClinicalServicesCount = (
  props: UseGetClinicalServicesCountProps
) =>
  useGet<CountResult, unknown, GetClinicalServicesCountQueryParams, void>(
    `/clinicalService/count`,
    props
  );

export type ClinicalServiceBatchUpdateProps = Omit<
  MutateProps<ClinicalService[], unknown, void, ClinicalService[], void>,
  "path" | "verb"
>;

/**
 * Updated clinical services/ single services as published.
 */
export const ClinicalServiceBatchUpdate = (
  props: ClinicalServiceBatchUpdateProps
) => (
  <Mutate<ClinicalService[], unknown, void, ClinicalService[], void>
    verb="PATCH"
    path={`/clinicalService/batch`}
    {...props}
  />
);

export type UseClinicalServiceBatchUpdateProps = Omit<
  UseMutateProps<ClinicalService[], unknown, void, ClinicalService[], void>,
  "path" | "verb"
>;

/**
 * Updated clinical services/ single services as published.
 */
export const useClinicalServiceBatchUpdate = (
  props: UseClinicalServiceBatchUpdateProps
) =>
  useMutate<ClinicalService[], unknown, void, ClinicalService[], void>(
    "PATCH",
    `/clinicalService/batch`,
    props
  );

export interface CreateClinicalServicePathParams {
  /**
   * A unique identifier for a `CarePath`.
   */
  carePathId: string;
  /**
   * The name of the service category that the service belongs to.
   */
  serviceCategoryName: string;
}

export type CreateClinicalServiceProps = Omit<
  MutateProps<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    CreateClinicalServicePathParams
  >,
  "path" | "verb"
> &
  CreateClinicalServicePathParams;

/**
 * Create a ClinicalService
 *
 * Creates a new instance of a `ClinicalService`.
 */
export const CreateClinicalService = ({
  carePathId,
  serviceCategoryName,
  ...props
}: CreateClinicalServiceProps) => (
  <Mutate<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    CreateClinicalServicePathParams
  >
    verb="POST"
    path={`/carePath/${carePathId}/serviceCategory/${serviceCategoryName}/clinicalService`}
    {...props}
  />
);

export type UseCreateClinicalServiceProps = Omit<
  UseMutateProps<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    CreateClinicalServicePathParams
  >,
  "path" | "verb"
> &
  CreateClinicalServicePathParams;

/**
 * Create a ClinicalService
 *
 * Creates a new instance of a `ClinicalService`.
 */
export const useCreateClinicalService = ({
  carePathId,
  serviceCategoryName,
  ...props
}: UseCreateClinicalServiceProps) =>
  useMutate<
    ClinicalService,
    ExampleErrorResponseResponse | void,
    void,
    ClinicalService,
    CreateClinicalServicePathParams
  >(
    "POST",
    (paramsInPath: CreateClinicalServicePathParams) =>
      `/carePath/${paramsInPath.carePathId}/serviceCategory/${paramsInPath.serviceCategoryName}/clinicalService`,
    { pathParams: { carePathId, serviceCategoryName }, ...props }
  );

export interface GetClinicalServiceDeterminationRequestBody {
  authCategory?: AuthCategory;
  authSubcategory?: string;
  patientId?: string;
  semanticProcedureCodes?: ProcedureCode[];
  nonPalSemanticProcedureCodes?: ProcedureCode[];
  primarySemanticDiagnosisCode?: DiagnosisCode;
  secondarySemanticDiagnosisCodes?: DiagnosisCode[];
  selectedServiceId?: string;
  selectedCategoryName?: string;
  selectedCarePathId?: string;
  selectedServiceType?: string;
  selectedPlaceOfServiceId?: string;
  encounterType?: FacilityCategory;
  serviceStartDate?: string;
  groupProcedureCodes?: boolean;
  procedureCodeGroupSelections?: ProcedureCodeGroupSelections[];
}

export type GetClinicalServiceDeterminationProps = Omit<
  MutateProps<
    ServiceDeterminationResponse,
    unknown,
    void,
    GetClinicalServiceDeterminationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const GetClinicalServiceDetermination = (
  props: GetClinicalServiceDeterminationProps
) => (
  <Mutate<
    ServiceDeterminationResponse,
    unknown,
    void,
    GetClinicalServiceDeterminationRequestBody,
    void
  >
    verb="POST"
    path={`/clinicalService/serviceDetermination`}
    {...props}
  />
);

export type UseGetClinicalServiceDeterminationProps = Omit<
  UseMutateProps<
    ServiceDeterminationResponse,
    unknown,
    void,
    GetClinicalServiceDeterminationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useGetClinicalServiceDetermination = (
  props: UseGetClinicalServiceDeterminationProps
) =>
  useMutate<
    ServiceDeterminationResponse,
    unknown,
    void,
    GetClinicalServiceDeterminationRequestBody,
    void
  >("POST", `/clinicalService/serviceDetermination`, props);

export interface GetOrganizationByTinEmailNameResponse {
  matchingOrgs?: Organization[];
  numOfMatchingOrgs?: string;
}

export interface GetOrganizationByTinEmailNameRequestBody {
  tinList?: string[];
  email: string;
  organizationName?: string;
}

export type GetOrganizationByTinEmailNameProps = Omit<
  MutateProps<
    GetOrganizationByTinEmailNameResponse,
    unknown,
    void,
    GetOrganizationByTinEmailNameRequestBody,
    void
  >,
  "path" | "verb"
>;

export const GetOrganizationByTinEmailName = (
  props: GetOrganizationByTinEmailNameProps
) => (
  <Mutate<
    GetOrganizationByTinEmailNameResponse,
    unknown,
    void,
    GetOrganizationByTinEmailNameRequestBody,
    void
  >
    verb="POST"
    path={`/onboarding/orgSearch`}
    {...props}
  />
);

export type UseGetOrganizationByTinEmailNameProps = Omit<
  UseMutateProps<
    GetOrganizationByTinEmailNameResponse,
    unknown,
    void,
    GetOrganizationByTinEmailNameRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useGetOrganizationByTinEmailName = (
  props: UseGetOrganizationByTinEmailNameProps
) =>
  useMutate<
    GetOrganizationByTinEmailNameResponse,
    unknown,
    void,
    GetOrganizationByTinEmailNameRequestBody,
    void
  >("POST", `/onboarding/orgSearch`, props);

export interface OnboardingCheckEmailCanAutoVerifyResponse {
  canAutoVerify?: boolean;
}

export interface OnboardingCheckEmailCanAutoVerifyQueryParams {
  /**
   * The full email entered in the onboarding flow
   */
  email?: string;
}

export type OnboardingCheckEmailCanAutoVerifyProps = Omit<
  GetProps<
    OnboardingCheckEmailCanAutoVerifyResponse,
    unknown,
    OnboardingCheckEmailCanAutoVerifyQueryParams,
    void
  >,
  "path"
>;

export const OnboardingCheckEmailCanAutoVerify = (
  props: OnboardingCheckEmailCanAutoVerifyProps
) => (
  <Get<
    OnboardingCheckEmailCanAutoVerifyResponse,
    unknown,
    OnboardingCheckEmailCanAutoVerifyQueryParams,
    void
  >
    path={`/onboarding/checkEmailCanAutoVerify`}
    {...props}
  />
);

export type UseOnboardingCheckEmailCanAutoVerifyProps = Omit<
  UseGetProps<
    OnboardingCheckEmailCanAutoVerifyResponse,
    unknown,
    OnboardingCheckEmailCanAutoVerifyQueryParams,
    void
  >,
  "path"
>;

export const useOnboardingCheckEmailCanAutoVerify = (
  props: UseOnboardingCheckEmailCanAutoVerifyProps
) =>
  useGet<
    OnboardingCheckEmailCanAutoVerifyResponse,
    unknown,
    OnboardingCheckEmailCanAutoVerifyQueryParams,
    void
  >(`/onboarding/checkEmailCanAutoVerify`, props);

export interface OnboardingOrganizationNameUniquenessCheckResponse {
  success?: boolean;
}

export interface OnboardingOrganizationNameUniquenessCheckQueryParams {
  /**
   * The user inputted name of the organization
   */
  organizationName?: string;
}

export type OnboardingOrganizationNameUniquenessCheckProps = Omit<
  GetProps<
    OnboardingOrganizationNameUniquenessCheckResponse,
    unknown,
    OnboardingOrganizationNameUniquenessCheckQueryParams,
    void
  >,
  "path"
>;

export const OnboardingOrganizationNameUniquenessCheck = (
  props: OnboardingOrganizationNameUniquenessCheckProps
) => (
  <Get<
    OnboardingOrganizationNameUniquenessCheckResponse,
    unknown,
    OnboardingOrganizationNameUniquenessCheckQueryParams,
    void
  >
    path={`/onboarding/organizationNameUniqueness`}
    {...props}
  />
);

export type UseOnboardingOrganizationNameUniquenessCheckProps = Omit<
  UseGetProps<
    OnboardingOrganizationNameUniquenessCheckResponse,
    unknown,
    OnboardingOrganizationNameUniquenessCheckQueryParams,
    void
  >,
  "path"
>;

export const useOnboardingOrganizationNameUniquenessCheck = (
  props: UseOnboardingOrganizationNameUniquenessCheckProps
) =>
  useGet<
    OnboardingOrganizationNameUniquenessCheckResponse,
    unknown,
    OnboardingOrganizationNameUniquenessCheckQueryParams,
    void
  >(`/onboarding/organizationNameUniqueness`, props);

export type CreateOnboardingUserActivityProps = Omit<
  MutateProps<void, unknown, void, UserActivity, void>,
  "path" | "verb"
>;

export const CreateOnboardingUserActivity = (
  props: CreateOnboardingUserActivityProps
) => (
  <Mutate<void, unknown, void, UserActivity, void>
    verb="POST"
    path={`/onboarding/userActivity`}
    {...props}
  />
);

export type UseCreateOnboardingUserActivityProps = Omit<
  UseMutateProps<void, unknown, void, UserActivity, void>,
  "path" | "verb"
>;

export const useCreateOnboardingUserActivity = (
  props: UseCreateOnboardingUserActivityProps
) =>
  useMutate<void, unknown, void, UserActivity, void>(
    "POST",
    `/onboarding/userActivity`,
    props
  );

export interface VerifyPatientInfoAndCreateOrgResponse {
  validated?: boolean;
  validationResult?: boolean[];
}

export type VerifyPatientInfoAndCreateOrgProps = Omit<
  MutateProps<
    VerifyPatientInfoAndCreateOrgResponse,
    unknown,
    void,
    OnboardingNewOrgRequest,
    void
  >,
  "path" | "verb"
>;

export const VerifyPatientInfoAndCreateOrg = (
  props: VerifyPatientInfoAndCreateOrgProps
) => (
  <Mutate<
    VerifyPatientInfoAndCreateOrgResponse,
    unknown,
    void,
    OnboardingNewOrgRequest,
    void
  >
    verb="POST"
    path={`/onboarding/validateAndCreateNewOrgByMemberId`}
    {...props}
  />
);

export type UseVerifyPatientInfoAndCreateOrgProps = Omit<
  UseMutateProps<
    VerifyPatientInfoAndCreateOrgResponse,
    unknown,
    void,
    OnboardingNewOrgRequest,
    void
  >,
  "path" | "verb"
>;

export const useVerifyPatientInfoAndCreateOrg = (
  props: UseVerifyPatientInfoAndCreateOrgProps
) =>
  useMutate<
    VerifyPatientInfoAndCreateOrgResponse,
    unknown,
    void,
    OnboardingNewOrgRequest,
    void
  >("POST", `/onboarding/validateAndCreateNewOrgByMemberId`, props);

export interface NewUserJoinExistingOrganizationResponse {
  autoVerified?: boolean;
  newUserCreated?: boolean;
  manualReviewCaseCreated?: boolean;
}

export type NewUserJoinExistingOrganizationProps = Omit<
  MutateProps<
    NewUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >,
  "path" | "verb"
>;

export const NewUserJoinExistingOrganization = (
  props: NewUserJoinExistingOrganizationProps
) => (
  <Mutate<
    NewUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >
    verb="POST"
    path={`/onboarding/joinExistingOrganization`}
    {...props}
  />
);

export type UseNewUserJoinExistingOrganizationProps = Omit<
  UseMutateProps<
    NewUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >,
  "path" | "verb"
>;

export const useNewUserJoinExistingOrganization = (
  props: UseNewUserJoinExistingOrganizationProps
) =>
  useMutate<
    NewUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >("POST", `/onboarding/joinExistingOrganization`, props);

export interface SsoUserJoinExistingOrganizationResponse {
  autoVerified?: boolean;
  newUserCreated?: boolean;
  manualReviewCaseCreated?: boolean;
}

export type SsoUserJoinExistingOrganizationProps = Omit<
  MutateProps<
    SsoUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >,
  "path" | "verb"
>;

export const SsoUserJoinExistingOrganization = (
  props: SsoUserJoinExistingOrganizationProps
) => (
  <Mutate<
    SsoUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >
    verb="POST"
    path={`/authenticatedOnboarding/joinExistingOrganization`}
    {...props}
  />
);

export type UseSsoUserJoinExistingOrganizationProps = Omit<
  UseMutateProps<
    SsoUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >,
  "path" | "verb"
>;

export const useSsoUserJoinExistingOrganization = (
  props: UseSsoUserJoinExistingOrganizationProps
) =>
  useMutate<
    SsoUserJoinExistingOrganizationResponse,
    unknown,
    void,
    OnboardingJoinOrgRequest,
    void
  >("POST", `/authenticatedOnboarding/joinExistingOrganization`, props);

export interface ManuallyCreateOrganizationResponse {
  autoVerified?: boolean;
}

export type ManuallyCreateOrganizationProps = Omit<
  MutateProps<
    ManuallyCreateOrganizationResponse,
    unknown,
    void,
    OnboardingManualOrgCreateRequest,
    void
  >,
  "path" | "verb"
>;

export const ManuallyCreateOrganization = (
  props: ManuallyCreateOrganizationProps
) => (
  <Mutate<
    ManuallyCreateOrganizationResponse,
    unknown,
    void,
    OnboardingManualOrgCreateRequest,
    void
  >
    verb="POST"
    path={`/onboarding/manuallyCreateOrganization`}
    {...props}
  />
);

export type UseManuallyCreateOrganizationProps = Omit<
  UseMutateProps<
    ManuallyCreateOrganizationResponse,
    unknown,
    void,
    OnboardingManualOrgCreateRequest,
    void
  >,
  "path" | "verb"
>;

export const useManuallyCreateOrganization = (
  props: UseManuallyCreateOrganizationProps
) =>
  useMutate<
    ManuallyCreateOrganizationResponse,
    unknown,
    void,
    OnboardingManualOrgCreateRequest,
    void
  >("POST", `/onboarding/manuallyCreateOrganization`, props);

export interface CreateSsoOrgResponse {
  validated?: boolean;
}

export type CreateSsoOrgProps = Omit<
  MutateProps<
    CreateSsoOrgResponse,
    unknown,
    void,
    OnboardingNewOrgSsoRequest,
    void
  >,
  "path" | "verb"
>;

export const CreateSsoOrg = (props: CreateSsoOrgProps) => (
  <Mutate<CreateSsoOrgResponse, unknown, void, OnboardingNewOrgSsoRequest, void>
    verb="POST"
    path={`/authenticatedOnboarding/newSsoOrg`}
    {...props}
  />
);

export type UseCreateSsoOrgProps = Omit<
  UseMutateProps<
    CreateSsoOrgResponse,
    unknown,
    void,
    OnboardingNewOrgSsoRequest,
    void
  >,
  "path" | "verb"
>;

export const useCreateSsoOrg = (props: UseCreateSsoOrgProps) =>
  useMutate<
    CreateSsoOrgResponse,
    unknown,
    void,
    OnboardingNewOrgSsoRequest,
    void
  >("POST", `/authenticatedOnboarding/newSsoOrg`, props);

export type GetOnboardingHealthPlansProps = Omit<
  GetProps<HealthPlanSelectOption[], unknown, void, void>,
  "path"
>;

export const GetOnboardingHealthPlans = (
  props: GetOnboardingHealthPlansProps
) => (
  <Get<HealthPlanSelectOption[], unknown, void, void>
    path={`/onboarding/healthPlans`}
    {...props}
  />
);

export type UseGetOnboardingHealthPlansProps = Omit<
  UseGetProps<HealthPlanSelectOption[], unknown, void, void>,
  "path"
>;

export const useGetOnboardingHealthPlans = (
  props: UseGetOnboardingHealthPlansProps
) =>
  useGet<HealthPlanSelectOption[], unknown, void, void>(
    `/onboarding/healthPlans`,
    props
  );

export interface UpdateSimpleFeatureFlagResponse {
  validated?: boolean;
}

export interface UpdateSimpleFeatureFlagRequestBody {
  id?: string;
  enabled?: boolean;
  setting?: FeatureFlagStatus;
  description?: string;
  whitelistedOrganizationList?: OrganizationCsvList[];
}

export type UpdateSimpleFeatureFlagProps = Omit<
  MutateProps<
    UpdateSimpleFeatureFlagResponse,
    unknown,
    void,
    UpdateSimpleFeatureFlagRequestBody,
    void
  >,
  "path" | "verb"
>;

export const UpdateSimpleFeatureFlag = (
  props: UpdateSimpleFeatureFlagProps
) => (
  <Mutate<
    UpdateSimpleFeatureFlagResponse,
    unknown,
    void,
    UpdateSimpleFeatureFlagRequestBody,
    void
  >
    verb="POST"
    path={`/simpleFeatureFlag`}
    {...props}
  />
);

export type UseUpdateSimpleFeatureFlagProps = Omit<
  UseMutateProps<
    UpdateSimpleFeatureFlagResponse,
    unknown,
    void,
    UpdateSimpleFeatureFlagRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useUpdateSimpleFeatureFlag = (
  props: UseUpdateSimpleFeatureFlagProps
) =>
  useMutate<
    UpdateSimpleFeatureFlagResponse,
    unknown,
    void,
    UpdateSimpleFeatureFlagRequestBody,
    void
  >("POST", `/simpleFeatureFlag`, props);

export type DeleteSimpleFeatureFlagProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Deletes
 */
export const DeleteSimpleFeatureFlag = (
  props: DeleteSimpleFeatureFlagProps
) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/simpleFeatureFlag`}
    {...props}
  />
);

export type UseDeleteSimpleFeatureFlagProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Deletes
 */
export const useDeleteSimpleFeatureFlag = (
  props: UseDeleteSimpleFeatureFlagProps
) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/simpleFeatureFlag`, {
    ...props,
  });

export type GetFeatureFlagLaunchDarklyResponse = {
  value?: boolean;
  kind?: string;
  errorKind?: string | null;
};

export interface GetFeatureFlagLaunchDarklyRequestBody {
  /**
   * The name of the flag to be evaluated
   */
  flagName?: string;
  /**
   * Context in which the feature flag should be evaluated
   */
  context?: { [key: string]: any } | null;
}

export type GetFeatureFlagLaunchDarklyProps = Omit<
  MutateProps<
    GetFeatureFlagLaunchDarklyResponse,
    unknown,
    void,
    GetFeatureFlagLaunchDarklyRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Gets a specific feature flag
 */
export const GetFeatureFlagLaunchDarkly = (
  props: GetFeatureFlagLaunchDarklyProps
) => (
  <Mutate<
    GetFeatureFlagLaunchDarklyResponse,
    unknown,
    void,
    GetFeatureFlagLaunchDarklyRequestBody,
    void
  >
    verb="POST"
    path={`/simpleFeatureFlag/launchDarklyEvaluate`}
    {...props}
  />
);

export type UseGetFeatureFlagLaunchDarklyProps = Omit<
  UseMutateProps<
    GetFeatureFlagLaunchDarklyResponse,
    unknown,
    void,
    GetFeatureFlagLaunchDarklyRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Gets a specific feature flag
 */
export const useGetFeatureFlagLaunchDarkly = (
  props: UseGetFeatureFlagLaunchDarklyProps
) =>
  useMutate<
    GetFeatureFlagLaunchDarklyResponse,
    unknown,
    void,
    GetFeatureFlagLaunchDarklyRequestBody,
    void
  >("POST", `/simpleFeatureFlag/launchDarklyEvaluate`, props);

export interface DownloadReleaseLimitedOrganizationsResponse {
  ok?: boolean;
}

export interface DownloadReleaseLimitedOrganizationsRequestBody {
  id?: string;
}

export type DownloadReleaseLimitedOrganizationsProps = Omit<
  MutateProps<
    DownloadReleaseLimitedOrganizationsResponse,
    unknown,
    void,
    DownloadReleaseLimitedOrganizationsRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Download the release limited organizations
 *
 * Downloads the release limited organizations.
 */
export const DownloadReleaseLimitedOrganizations = (
  props: DownloadReleaseLimitedOrganizationsProps
) => (
  <Mutate<
    DownloadReleaseLimitedOrganizationsResponse,
    unknown,
    void,
    DownloadReleaseLimitedOrganizationsRequestBody,
    void
  >
    verb="POST"
    path={`/simpleFeatureFlag/downloadLimitedReleaseOrganizations`}
    {...props}
  />
);

export type UseDownloadReleaseLimitedOrganizationsProps = Omit<
  UseMutateProps<
    DownloadReleaseLimitedOrganizationsResponse,
    unknown,
    void,
    DownloadReleaseLimitedOrganizationsRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Download the release limited organizations
 *
 * Downloads the release limited organizations.
 */
export const useDownloadReleaseLimitedOrganizations = (
  props: UseDownloadReleaseLimitedOrganizationsProps
) =>
  useMutate<
    DownloadReleaseLimitedOrganizationsResponse,
    unknown,
    void,
    DownloadReleaseLimitedOrganizationsRequestBody,
    void
  >("POST", `/simpleFeatureFlag/downloadLimitedReleaseOrganizations`, props);

export type GetFeatureFlagAdminViewProps = Omit<
  GetProps<FeatureFlagAdminView[], unknown, void, void>,
  "path"
>;

/**
 * Gets a list of feature flags in their admin view
 *
 * Gets all FeatureFlags with additional info
 */
export const GetFeatureFlagAdminView = (
  props: GetFeatureFlagAdminViewProps
) => (
  <Get<FeatureFlagAdminView[], unknown, void, void>
    path={`/simpleFeatureFlagAdminView`}
    {...props}
  />
);

export type UseGetFeatureFlagAdminViewProps = Omit<
  UseGetProps<FeatureFlagAdminView[], unknown, void, void>,
  "path"
>;

/**
 * Gets a list of feature flags in their admin view
 *
 * Gets all FeatureFlags with additional info
 */
export const useGetFeatureFlagAdminView = (
  props: UseGetFeatureFlagAdminViewProps
) =>
  useGet<FeatureFlagAdminView[], unknown, void, void>(
    `/simpleFeatureFlagAdminView`,
    props
  );

export interface GetClinicalQuestionPathParams {
  /**
   * A unique identifier for a `ClinicalQuestion`.
   */
  id: string;
}

export type GetClinicalQuestionProps = Omit<
  GetProps<ClinicalQuestion, unknown, void, GetClinicalQuestionPathParams>,
  "path"
> &
  GetClinicalQuestionPathParams;

/**
 * Get a ClinicalQuestion
 *
 * Gets the details of a single instance of a `ClinicalQuestion`.
 */
export const GetClinicalQuestion = ({
  id,
  ...props
}: GetClinicalQuestionProps) => (
  <Get<ClinicalQuestion, unknown, void, GetClinicalQuestionPathParams>
    path={`/clinicalQuestion/${id}`}
    {...props}
  />
);

export type UseGetClinicalQuestionProps = Omit<
  UseGetProps<ClinicalQuestion, unknown, void, GetClinicalQuestionPathParams>,
  "path"
> &
  GetClinicalQuestionPathParams;

/**
 * Get a ClinicalQuestion
 *
 * Gets the details of a single instance of a `ClinicalQuestion`.
 */
export const useGetClinicalQuestion = ({
  id,
  ...props
}: UseGetClinicalQuestionProps) =>
  useGet<ClinicalQuestion, unknown, void, GetClinicalQuestionPathParams>(
    (paramsInPath: GetClinicalQuestionPathParams) =>
      `/clinicalQuestion/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateClinicalQuestionPathParams {
  /**
   * A unique identifier for a `ClinicalQuestion`.
   */
  id: string;
}

export type UpdateClinicalQuestionProps = Omit<
  MutateProps<
    ClinicalQuestion,
    unknown,
    void,
    ClinicalQuestionPayload,
    UpdateClinicalQuestionPathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalQuestionPathParams;

/**
 * Update a ClinicalQuestion
 *
 * Updates an existing `ClinicalQuestion`.
 */
export const UpdateClinicalQuestion = ({
  id,
  ...props
}: UpdateClinicalQuestionProps) => (
  <Mutate<
    ClinicalQuestion,
    unknown,
    void,
    ClinicalQuestionPayload,
    UpdateClinicalQuestionPathParams
  >
    verb="PUT"
    path={`/clinicalQuestion/${id}`}
    {...props}
  />
);

export type UseUpdateClinicalQuestionProps = Omit<
  UseMutateProps<
    ClinicalQuestion,
    unknown,
    void,
    ClinicalQuestionPayload,
    UpdateClinicalQuestionPathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalQuestionPathParams;

/**
 * Update a ClinicalQuestion
 *
 * Updates an existing `ClinicalQuestion`.
 */
export const useUpdateClinicalQuestion = ({
  id,
  ...props
}: UseUpdateClinicalQuestionProps) =>
  useMutate<
    ClinicalQuestion,
    unknown,
    void,
    ClinicalQuestionPayload,
    UpdateClinicalQuestionPathParams
  >(
    "PUT",
    (paramsInPath: UpdateClinicalQuestionPathParams) =>
      `/clinicalQuestion/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteClinicalQuestionProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a ClinicalQuestion
 *
 * Deletes an existing `ClinicalQuestion`.
 */
export const DeleteClinicalQuestion = (props: DeleteClinicalQuestionProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/clinicalQuestion`}
    {...props}
  />
);

export type UseDeleteClinicalQuestionProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a ClinicalQuestion
 *
 * Deletes an existing `ClinicalQuestion`.
 */
export const useDeleteClinicalQuestion = (
  props: UseDeleteClinicalQuestionProps
) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/clinicalQuestion`, {
    ...props,
  });

export interface SearchClinicalServicesQueryParams {
  /**
   * Searches for clinical services by contents of 'name' field
   */
  query?: string;
}

export type SearchClinicalServicesProps = Omit<
  GetProps<ClinicalService[], unknown, SearchClinicalServicesQueryParams, void>,
  "path"
>;

/**
 * searches for ClinicalService
 */
export const SearchClinicalServices = (props: SearchClinicalServicesProps) => (
  <Get<ClinicalService[], unknown, SearchClinicalServicesQueryParams, void>
    path={`/clinicalService/search`}
    {...props}
  />
);

export type UseSearchClinicalServicesProps = Omit<
  UseGetProps<
    ClinicalService[],
    unknown,
    SearchClinicalServicesQueryParams,
    void
  >,
  "path"
>;

/**
 * searches for ClinicalService
 */
export const useSearchClinicalServices = (
  props: UseSearchClinicalServicesProps
) =>
  useGet<ClinicalService[], unknown, SearchClinicalServicesQueryParams, void>(
    `/clinicalService/search`,
    props
  );

export interface GetClinicalServiceQueryParams {
  /**
   * A unique query param to use `authCategory` for a ClinicalService
   */
  readOnlyAuthCategory?: string;
  /**
   * boolean on whether to create draft children when creating draft clinical service
   */
  updateChildren?: string;
}

export interface GetClinicalServicePathParams {
  /**
   * A unique identifier for a `ClinicalService`.
   */
  id: string;
}

export type GetClinicalServiceProps = Omit<
  GetProps<
    ClinicalService,
    unknown,
    GetClinicalServiceQueryParams,
    GetClinicalServicePathParams
  >,
  "path"
> &
  GetClinicalServicePathParams;

/**
 * Get a ClinicalService
 *
 * Gets the details of a single instance of a `ClinicalService`.
 */
export const GetClinicalService = ({
  id,
  ...props
}: GetClinicalServiceProps) => (
  <Get<
    ClinicalService,
    unknown,
    GetClinicalServiceQueryParams,
    GetClinicalServicePathParams
  >
    path={`/clinicalService/${id}`}
    {...props}
  />
);

export type UseGetClinicalServiceProps = Omit<
  UseGetProps<
    ClinicalService,
    unknown,
    GetClinicalServiceQueryParams,
    GetClinicalServicePathParams
  >,
  "path"
> &
  GetClinicalServicePathParams;

/**
 * Get a ClinicalService
 *
 * Gets the details of a single instance of a `ClinicalService`.
 */
export const useGetClinicalService = ({
  id,
  ...props
}: UseGetClinicalServiceProps) =>
  useGet<
    ClinicalService,
    unknown,
    GetClinicalServiceQueryParams,
    GetClinicalServicePathParams
  >(
    (paramsInPath: GetClinicalServicePathParams) =>
      `/clinicalService/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateClinicalServiceQueryParams {
  /**
   * A unique query param to use `authCategory` for a ClinicalService
   */
  readOnlyAuthCategory?: string;
  /**
   * boolean on whether to create draft children when creating draft clinical service
   */
  updateChildren?: string;
}

export interface UpdateClinicalServicePathParams {
  /**
   * A unique identifier for a `ClinicalService`.
   */
  id: string;
}

export type UpdateClinicalServiceProps = Omit<
  MutateProps<
    ClinicalService,
    unknown,
    UpdateClinicalServiceQueryParams,
    ClinicalServiceUpdatePayload,
    UpdateClinicalServicePathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalServicePathParams;

/**
 * Update a ClinicalService
 *
 * Updates an existing `ClinicalService`.
 */
export const UpdateClinicalService = ({
  id,
  ...props
}: UpdateClinicalServiceProps) => (
  <Mutate<
    ClinicalService,
    unknown,
    UpdateClinicalServiceQueryParams,
    ClinicalServiceUpdatePayload,
    UpdateClinicalServicePathParams
  >
    verb="PUT"
    path={`/clinicalService/${id}`}
    {...props}
  />
);

export type UseUpdateClinicalServiceProps = Omit<
  UseMutateProps<
    ClinicalService,
    unknown,
    UpdateClinicalServiceQueryParams,
    ClinicalServiceUpdatePayload,
    UpdateClinicalServicePathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalServicePathParams;

/**
 * Update a ClinicalService
 *
 * Updates an existing `ClinicalService`.
 */
export const useUpdateClinicalService = ({
  id,
  ...props
}: UseUpdateClinicalServiceProps) =>
  useMutate<
    ClinicalService,
    unknown,
    UpdateClinicalServiceQueryParams,
    ClinicalServiceUpdatePayload,
    UpdateClinicalServicePathParams
  >(
    "PUT",
    (paramsInPath: UpdateClinicalServicePathParams) =>
      `/clinicalService/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface DeleteClinicalServiceQueryParams {
  /**
   * A unique query param to use `authCategory` for a ClinicalService
   */
  readOnlyAuthCategory?: string;
  /**
   * boolean on whether to create draft children when creating draft clinical service
   */
  updateChildren?: string;
}

export type DeleteClinicalServiceProps = Omit<
  MutateProps<void, unknown, DeleteClinicalServiceQueryParams, string, void>,
  "path" | "verb"
>;

/**
 * Delete a ClinicalService
 *
 * Deletes an existing `ClinicalService`.
 */
export const DeleteClinicalService = (props: DeleteClinicalServiceProps) => (
  <Mutate<void, unknown, DeleteClinicalServiceQueryParams, string, void>
    verb="DELETE"
    path={`/clinicalService`}
    {...props}
  />
);

export type UseDeleteClinicalServiceProps = Omit<
  UseMutateProps<void, unknown, DeleteClinicalServiceQueryParams, string, void>,
  "path" | "verb"
>;

/**
 * Delete a ClinicalService
 *
 * Deletes an existing `ClinicalService`.
 */
export const useDeleteClinicalService = (
  props: UseDeleteClinicalServiceProps
) =>
  useMutate<void, unknown, DeleteClinicalServiceQueryParams, string, void>(
    "DELETE",
    `/clinicalService`,
    { ...props }
  );

export interface GetDiagnosisCodesQueryParams {
  /**
   * Searches for diagnosis code by contents of 'code' field
   */
  query?: string;
  /**
   * Filter to codes that either are billable (leaf) or non-billable (category)
   */
  isBillable?: boolean;
  /**
   * Max number of results to return (defaults 10, up to 100)
   */
  max?: number;
  /**
   * Starting result offset
   */
  offset?: number;
  /**
   * Matcher on the `code` field of a DiagnosisCode e.g. code=in:1,2,3
   */
  code?: string;
  /**
   * comma seperated list of terms to search by
   */
  termList?: string;
}

export type GetDiagnosisCodesProps = Omit<
  GetProps<DiagnosisCode[], unknown, GetDiagnosisCodesQueryParams, void>,
  "path"
>;

/**
 * List All diagnosiscodes
 *
 * Gets a list of all `DiagnosisCode` entities.
 */
export const GetDiagnosisCodes = (props: GetDiagnosisCodesProps) => (
  <Get<DiagnosisCode[], unknown, GetDiagnosisCodesQueryParams, void>
    path={`/diagnosisCode`}
    {...props}
  />
);

export type UseGetDiagnosisCodesProps = Omit<
  UseGetProps<DiagnosisCode[], unknown, GetDiagnosisCodesQueryParams, void>,
  "path"
>;

/**
 * List All diagnosiscodes
 *
 * Gets a list of all `DiagnosisCode` entities.
 */
export const useGetDiagnosisCodes = (props: UseGetDiagnosisCodesProps) =>
  useGet<DiagnosisCode[], unknown, GetDiagnosisCodesQueryParams, void>(
    `/diagnosisCode`,
    props
  );

export type CreateDiagnosisCodeProps = Omit<
  MutateProps<void, unknown, void, DiagnosisCode, void>,
  "path" | "verb"
>;

/**
 * Create a DiagnosisCode
 *
 * Creates a new instance of a `DiagnosisCode`.
 */
export const CreateDiagnosisCode = (props: CreateDiagnosisCodeProps) => (
  <Mutate<void, unknown, void, DiagnosisCode, void>
    verb="POST"
    path={`/diagnosisCode`}
    {...props}
  />
);

export type UseCreateDiagnosisCodeProps = Omit<
  UseMutateProps<void, unknown, void, DiagnosisCode, void>,
  "path" | "verb"
>;

/**
 * Create a DiagnosisCode
 *
 * Creates a new instance of a `DiagnosisCode`.
 */
export const useCreateDiagnosisCode = (props: UseCreateDiagnosisCodeProps) =>
  useMutate<void, unknown, void, DiagnosisCode, void>(
    "POST",
    `/diagnosisCode`,
    props
  );

export interface GetCarePathsQueryParams {
  /**
   * Searches for diagnosis code by contents of 'code' field
   */
  query?: string;
  /**
   * load less attributes
   */
  short?: boolean;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * returns collection of unexpanded care paths
   */
  withMinimalExpansion?: boolean;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of carePaths with productionReady set to true or false
   */
  productionReady?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * gets all versions of a single care path based on the first id of the series
   */
  originalVersionId?: string;
  /**
   * Searches for care paths that have the diagnosisCode in the array of diagnosisCodes
   */
  diagnosisCode?: string;
  /**
   * Gets care paths available to specified health plan by name
   */
  healthPlanName?: string;
  /**
   * Gets care paths available to health plans (comma separated Health plan IDs)
   */
  healthPlanIds?: string;
  /**
   * Gets care paths available to specific patient (based on patient's health plan)
   */
  patientId?: string;
}

export type GetCarePathsProps = Omit<
  GetProps<CarePath[], unknown, GetCarePathsQueryParams, void>,
  "path"
>;

/**
 * List All care paths
 *
 * Gets a list of all `CarePath` entities.
 */
export const GetCarePaths = (props: GetCarePathsProps) => (
  <Get<CarePath[], unknown, GetCarePathsQueryParams, void>
    path={`/carePath`}
    {...props}
  />
);

export type UseGetCarePathsProps = Omit<
  UseGetProps<CarePath[], unknown, GetCarePathsQueryParams, void>,
  "path"
>;

/**
 * List All care paths
 *
 * Gets a list of all `CarePath` entities.
 */
export const useGetCarePaths = (props: UseGetCarePathsProps) =>
  useGet<CarePath[], unknown, GetCarePathsQueryParams, void>(
    `/carePath`,
    props
  );

export type CreateCarePathProps = Omit<
  MutateProps<CarePath, unknown, void, CarePath, void>,
  "path" | "verb"
>;

/**
 * Create a CarePath
 *
 * Creates a new instance of a `CarePath`.
 */
export const CreateCarePath = (props: CreateCarePathProps) => (
  <Mutate<CarePath, unknown, void, CarePath, void>
    verb="POST"
    path={`/carePath`}
    {...props}
  />
);

export type UseCreateCarePathProps = Omit<
  UseMutateProps<CarePath, unknown, void, CarePath, void>,
  "path" | "verb"
>;

/**
 * Create a CarePath
 *
 * Creates a new instance of a `CarePath`.
 */
export const useCreateCarePath = (props: UseCreateCarePathProps) =>
  useMutate<CarePath, unknown, void, CarePath, void>(
    "POST",
    `/carePath`,
    props
  );

export interface GetCarePathPathParams {
  /**
   * A unique identifier for a `CarePath`.
   */
  carePathId: string;
}

export type GetCarePathProps = Omit<
  GetProps<CarePath, unknown, void, GetCarePathPathParams>,
  "path"
> &
  GetCarePathPathParams;

/**
 * Get a CarePath
 *
 * Gets the details of a single instance of a `CarePath`.
 */
export const GetCarePath = ({ carePathId, ...props }: GetCarePathProps) => (
  <Get<CarePath, unknown, void, GetCarePathPathParams>
    path={`/carePath/${carePathId}`}
    {...props}
  />
);

export type UseGetCarePathProps = Omit<
  UseGetProps<CarePath, unknown, void, GetCarePathPathParams>,
  "path"
> &
  GetCarePathPathParams;

/**
 * Get a CarePath
 *
 * Gets the details of a single instance of a `CarePath`.
 */
export const useGetCarePath = ({ carePathId, ...props }: UseGetCarePathProps) =>
  useGet<CarePath, unknown, void, GetCarePathPathParams>(
    (paramsInPath: GetCarePathPathParams) =>
      `/carePath/${paramsInPath.carePathId}`,
    { pathParams: { carePathId }, ...props }
  );

export interface UpdateCarePathPathParams {
  /**
   * A unique identifier for a `CarePath`.
   */
  carePathId: string;
}

export type UpdateCarePathProps = Omit<
  MutateProps<
    CarePath,
    unknown,
    void,
    CarePathUpdatePayload,
    UpdateCarePathPathParams
  >,
  "path" | "verb"
> &
  UpdateCarePathPathParams;

/**
 * Update a CarePath
 *
 * Updates an existing `CarePath`.
 */
export const UpdateCarePath = ({
  carePathId,
  ...props
}: UpdateCarePathProps) => (
  <Mutate<
    CarePath,
    unknown,
    void,
    CarePathUpdatePayload,
    UpdateCarePathPathParams
  >
    verb="PUT"
    path={`/carePath/${carePathId}`}
    {...props}
  />
);

export type UseUpdateCarePathProps = Omit<
  UseMutateProps<
    CarePath,
    unknown,
    void,
    CarePathUpdatePayload,
    UpdateCarePathPathParams
  >,
  "path" | "verb"
> &
  UpdateCarePathPathParams;

/**
 * Update a CarePath
 *
 * Updates an existing `CarePath`.
 */
export const useUpdateCarePath = ({
  carePathId,
  ...props
}: UseUpdateCarePathProps) =>
  useMutate<
    CarePath,
    unknown,
    void,
    CarePathUpdatePayload,
    UpdateCarePathPathParams
  >(
    "PUT",
    (paramsInPath: UpdateCarePathPathParams) =>
      `/carePath/${paramsInPath.carePathId}`,
    { pathParams: { carePathId }, ...props }
  );

export type DeleteCarePathProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a CarePath
 *
 * Deletes an existing `CarePath`.
 */
export const DeleteCarePath = (props: DeleteCarePathProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/carePath`}
    {...props}
  />
);

export type UseDeleteCarePathProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a CarePath
 *
 * Deletes an existing `CarePath`.
 */
export const useDeleteCarePath = (props: UseDeleteCarePathProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/carePath`, {
    ...props,
  });

export interface GetCarePathsCountQueryParams {
  /**
   * Searches for diagnosis code by contents of 'code' field
   */
  query?: string;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collection of carePaths with productionReady set to true or false
   */
  productionReady?: string;
  /**
   * gets all versions of a single care path based on the first id of the series
   */
  originalVersionId?: string;
  /**
   * Searches for care paths that have the diagnosisCode in the array of diagnosisCodes
   */
  diagnosisCode?: string;
  /**
   * Gets care paths available to specified health plan by name
   */
  healthPlanName?: string;
  /**
   * Gets care paths available to health plans (comma separated Health plan IDs)
   */
  healthPlanIds?: string;
  /**
   * Gets care paths available to specific patient (based on patient's health plan)
   */
  patientId?: string;
  specialty?: string;
}

export type GetCarePathsCountProps = Omit<
  GetProps<CountResult, unknown, GetCarePathsCountQueryParams, void>,
  "path"
>;

/**
 * List All care paths
 *
 * Gets a list of all `CarePath` entities.
 */
export const GetCarePathsCount = (props: GetCarePathsCountProps) => (
  <Get<CountResult, unknown, GetCarePathsCountQueryParams, void>
    path={`/carePath/count`}
    {...props}
  />
);

export type UseGetCarePathsCountProps = Omit<
  UseGetProps<CountResult, unknown, GetCarePathsCountQueryParams, void>,
  "path"
>;

/**
 * List All care paths
 *
 * Gets a list of all `CarePath` entities.
 */
export const useGetCarePathsCount = (props: UseGetCarePathsCountProps) =>
  useGet<CountResult, unknown, GetCarePathsCountQueryParams, void>(
    `/carePath/count`,
    props
  );

export type CarePathBatchUpdateProps = Omit<
  MutateProps<CarePath[], unknown, void, CarePath[], void>,
  "path" | "verb"
>;

/**
 * Updated carePaths as published.
 */
export const CarePathBatchUpdate = (props: CarePathBatchUpdateProps) => (
  <Mutate<CarePath[], unknown, void, CarePath[], void>
    verb="PATCH"
    path={`/carePath/batch`}
    {...props}
  />
);

export type UseCarePathBatchUpdateProps = Omit<
  UseMutateProps<CarePath[], unknown, void, CarePath[], void>,
  "path" | "verb"
>;

/**
 * Updated carePaths as published.
 */
export const useCarePathBatchUpdate = (props: UseCarePathBatchUpdateProps) =>
  useMutate<CarePath[], unknown, void, CarePath[], void>(
    "PATCH",
    `/carePath/batch`,
    props
  );

export interface GetServiceCategoryQueryParams {
  /**
   * searches for categories by name
   */
  query?: {};
  /**
   * filters by care path id
   */
  carePathId?: string;
}

export type GetServiceCategoryProps = Omit<
  GetProps<ServiceCategory[], unknown, GetServiceCategoryQueryParams, void>,
  "path"
>;

export const GetServiceCategory = (props: GetServiceCategoryProps) => (
  <Get<ServiceCategory[], unknown, GetServiceCategoryQueryParams, void>
    path={`/serviceCategory`}
    {...props}
  />
);

export type UseGetServiceCategoryProps = Omit<
  UseGetProps<ServiceCategory[], unknown, GetServiceCategoryQueryParams, void>,
  "path"
>;

export const useGetServiceCategory = (props: UseGetServiceCategoryProps) =>
  useGet<ServiceCategory[], unknown, GetServiceCategoryQueryParams, void>(
    `/serviceCategory`,
    props
  );

export interface GetCarePathJourneyPathParams {
  id: string;
}

export type GetCarePathJourneyProps = Omit<
  GetProps<CarePathJourney, unknown, void, GetCarePathJourneyPathParams>,
  "path"
> &
  GetCarePathJourneyPathParams;

export const GetCarePathJourney = ({
  id,
  ...props
}: GetCarePathJourneyProps) => (
  <Get<CarePathJourney, unknown, void, GetCarePathJourneyPathParams>
    path={`/carePathJourney/${id}`}
    {...props}
  />
);

export type UseGetCarePathJourneyProps = Omit<
  UseGetProps<CarePathJourney, unknown, void, GetCarePathJourneyPathParams>,
  "path"
> &
  GetCarePathJourneyPathParams;

export const useGetCarePathJourney = ({
  id,
  ...props
}: UseGetCarePathJourneyProps) =>
  useGet<CarePathJourney, unknown, void, GetCarePathJourneyPathParams>(
    (paramsInPath: GetCarePathJourneyPathParams) =>
      `/carePathJourney/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateCarePathJourneyPathParams {
  id: string;
}

export type UpdateCarePathJourneyProps = Omit<
  MutateProps<
    CarePathJourney,
    unknown,
    void,
    CarePathJourneyUpdatePayload,
    UpdateCarePathJourneyPathParams
  >,
  "path" | "verb"
> &
  UpdateCarePathJourneyPathParams;

export const UpdateCarePathJourney = ({
  id,
  ...props
}: UpdateCarePathJourneyProps) => (
  <Mutate<
    CarePathJourney,
    unknown,
    void,
    CarePathJourneyUpdatePayload,
    UpdateCarePathJourneyPathParams
  >
    verb="PATCH"
    path={`/carePathJourney/${id}`}
    {...props}
  />
);

export type UseUpdateCarePathJourneyProps = Omit<
  UseMutateProps<
    CarePathJourney,
    unknown,
    void,
    CarePathJourneyUpdatePayload,
    UpdateCarePathJourneyPathParams
  >,
  "path" | "verb"
> &
  UpdateCarePathJourneyPathParams;

export const useUpdateCarePathJourney = ({
  id,
  ...props
}: UseUpdateCarePathJourneyProps) =>
  useMutate<
    CarePathJourney,
    unknown,
    void,
    CarePathJourneyUpdatePayload,
    UpdateCarePathJourneyPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateCarePathJourneyPathParams) =>
      `/carePathJourney/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetClinicalAssessmentQueryParams {
  serviceRequestIds: string[];
}

export interface GetClinicalAssessmentPathParams {
  journeyId: string;
}

export type GetClinicalAssessmentProps = Omit<
  GetProps<
    ClinicalAssessment,
    unknown,
    GetClinicalAssessmentQueryParams,
    GetClinicalAssessmentPathParams
  >,
  "path"
> &
  GetClinicalAssessmentPathParams;

export const GetClinicalAssessment = ({
  journeyId,
  ...props
}: GetClinicalAssessmentProps) => (
  <Get<
    ClinicalAssessment,
    unknown,
    GetClinicalAssessmentQueryParams,
    GetClinicalAssessmentPathParams
  >
    path={`/carePathJourney/${journeyId}/clinicalAssessment`}
    {...props}
  />
);

export type UseGetClinicalAssessmentProps = Omit<
  UseGetProps<
    ClinicalAssessment,
    unknown,
    GetClinicalAssessmentQueryParams,
    GetClinicalAssessmentPathParams
  >,
  "path"
> &
  GetClinicalAssessmentPathParams;

export const useGetClinicalAssessment = ({
  journeyId,
  ...props
}: UseGetClinicalAssessmentProps) =>
  useGet<
    ClinicalAssessment,
    unknown,
    GetClinicalAssessmentQueryParams,
    GetClinicalAssessmentPathParams
  >(
    (paramsInPath: GetClinicalAssessmentPathParams) =>
      `/carePathJourney/${paramsInPath.journeyId}/clinicalAssessment`,
    { pathParams: { journeyId }, ...props }
  );

export interface GetSingleServiceClinicalAssessmentPathParams {
  id: string;
}

export type GetSingleServiceClinicalAssessmentProps = Omit<
  GetProps<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetSingleServiceClinicalAssessmentPathParams
  >,
  "path"
> &
  GetSingleServiceClinicalAssessmentPathParams;

export const GetSingleServiceClinicalAssessment = ({
  id,
  ...props
}: GetSingleServiceClinicalAssessmentProps) => (
  <Get<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetSingleServiceClinicalAssessmentPathParams
  >
    path={`/serviceRequest/${id}/clinicalAssessment`}
    {...props}
  />
);

export type UseGetSingleServiceClinicalAssessmentProps = Omit<
  UseGetProps<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetSingleServiceClinicalAssessmentPathParams
  >,
  "path"
> &
  GetSingleServiceClinicalAssessmentPathParams;

export const useGetSingleServiceClinicalAssessment = ({
  id,
  ...props
}: UseGetSingleServiceClinicalAssessmentProps) =>
  useGet<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetSingleServiceClinicalAssessmentPathParams
  >(
    (paramsInPath: GetSingleServiceClinicalAssessmentPathParams) =>
      `/serviceRequest/${paramsInPath.id}/clinicalAssessment`,
    { pathParams: { id }, ...props }
  );

export interface GetTestingAssessmentPathParams {
  id: string;
}

export type GetTestingAssessmentProps = Omit<
  GetProps<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetTestingAssessmentPathParams
  >,
  "path"
> &
  GetTestingAssessmentPathParams;

export const GetTestingAssessment = ({
  id,
  ...props
}: GetTestingAssessmentProps) => (
  <Get<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetTestingAssessmentPathParams
  >
    path={`/serviceRequestRule/${id}/testingTemplate`}
    {...props}
  />
);

export type UseGetTestingAssessmentProps = Omit<
  UseGetProps<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetTestingAssessmentPathParams
  >,
  "path"
> &
  GetTestingAssessmentPathParams;

export const useGetTestingAssessment = ({
  id,
  ...props
}: UseGetTestingAssessmentProps) =>
  useGet<
    ClinicalAssessment | void,
    {
      timestamp?: number;
      status?: number;
      error?: string;
      message?: string;
      path?: string;
      data?: {
        message?: string;
      };
    },
    void,
    GetTestingAssessmentPathParams
  >(
    (paramsInPath: GetTestingAssessmentPathParams) =>
      `/serviceRequestRule/${paramsInPath.id}/testingTemplate`,
    { pathParams: { id }, ...props }
  );

export type PostClinicalAssessmentProps = Omit<
  MutateProps<ClinicalAssessment, unknown, void, ClinicalAssessment, void>,
  "path" | "verb"
>;

export const PostClinicalAssessment = (props: PostClinicalAssessmentProps) => (
  <Mutate<ClinicalAssessment, unknown, void, ClinicalAssessment, void>
    verb="POST"
    path={`/clinicalAssessment`}
    {...props}
  />
);

export type UsePostClinicalAssessmentProps = Omit<
  UseMutateProps<ClinicalAssessment, unknown, void, ClinicalAssessment, void>,
  "path" | "verb"
>;

export const usePostClinicalAssessment = (
  props: UsePostClinicalAssessmentProps
) =>
  useMutate<ClinicalAssessment, unknown, void, ClinicalAssessment, void>(
    "POST",
    `/clinicalAssessment`,
    props
  );

export interface GetMultiSingleServiceClinicalAssessmentQueryParams {
  serviceRequestIds: string[];
}

export type GetMultiSingleServiceClinicalAssessmentProps = Omit<
  GetProps<
    ClinicalAssessment,
    unknown,
    GetMultiSingleServiceClinicalAssessmentQueryParams,
    void
  >,
  "path"
>;

export const GetMultiSingleServiceClinicalAssessment = (
  props: GetMultiSingleServiceClinicalAssessmentProps
) => (
  <Get<
    ClinicalAssessment,
    unknown,
    GetMultiSingleServiceClinicalAssessmentQueryParams,
    void
  >
    path={`/clinicalAssessment/multiSingleService`}
    {...props}
  />
);

export type UseGetMultiSingleServiceClinicalAssessmentProps = Omit<
  UseGetProps<
    ClinicalAssessment,
    unknown,
    GetMultiSingleServiceClinicalAssessmentQueryParams,
    void
  >,
  "path"
>;

export const useGetMultiSingleServiceClinicalAssessment = (
  props: UseGetMultiSingleServiceClinicalAssessmentProps
) =>
  useGet<
    ClinicalAssessment,
    unknown,
    GetMultiSingleServiceClinicalAssessmentQueryParams,
    void
  >(`/clinicalAssessment/multiSingleService`, props);

export type CreateCarePathJourneyProps = Omit<
  MutateProps<
    CarePathJourney,
    unknown,
    void,
    CarePathJourneyCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const CreateCarePathJourney = (props: CreateCarePathJourneyProps) => (
  <Mutate<CarePathJourney, unknown, void, CarePathJourneyCreatePayload, void>
    verb="POST"
    path={`/carePathJourney`}
    {...props}
  />
);

export type UseCreateCarePathJourneyProps = Omit<
  UseMutateProps<
    CarePathJourney,
    unknown,
    void,
    CarePathJourneyCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const useCreateCarePathJourney = (
  props: UseCreateCarePathJourneyProps
) =>
  useMutate<CarePathJourney, unknown, void, CarePathJourneyCreatePayload, void>(
    "POST",
    `/carePathJourney`,
    props
  );

export interface CreateCarePathJourneyAttachmentPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  id: string;
}

export type CreateCarePathJourneyAttachmentProps = Omit<
  MutateProps<
    Attachment,
    unknown,
    void,
    void,
    CreateCarePathJourneyAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateCarePathJourneyAttachmentPathParams;

/**
 * Add attachment to care path journey
 */
export const CreateCarePathJourneyAttachment = ({
  id,
  ...props
}: CreateCarePathJourneyAttachmentProps) => (
  <Mutate<
    Attachment,
    unknown,
    void,
    void,
    CreateCarePathJourneyAttachmentPathParams
  >
    verb="POST"
    path={`/carePathJourney/${id}/attachment`}
    {...props}
  />
);

export type UseCreateCarePathJourneyAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    unknown,
    void,
    void,
    CreateCarePathJourneyAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateCarePathJourneyAttachmentPathParams;

/**
 * Add attachment to care path journey
 */
export const useCreateCarePathJourneyAttachment = ({
  id,
  ...props
}: UseCreateCarePathJourneyAttachmentProps) =>
  useMutate<
    Attachment,
    unknown,
    void,
    void,
    CreateCarePathJourneyAttachmentPathParams
  >(
    "POST",
    (paramsInPath: CreateCarePathJourneyAttachmentPathParams) =>
      `/carePathJourney/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface GetCarePathJourneyAttachmentsPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  id: string;
}

export type GetCarePathJourneyAttachmentsProps = Omit<
  GetProps<
    Attachment[],
    unknown,
    void,
    GetCarePathJourneyAttachmentsPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentsPathParams;

/**
 * Search for Care Path Journey Attachments
 */
export const GetCarePathJourneyAttachments = ({
  id,
  ...props
}: GetCarePathJourneyAttachmentsProps) => (
  <Get<Attachment[], unknown, void, GetCarePathJourneyAttachmentsPathParams>
    path={`/carePathJourney/${id}/attachment`}
    {...props}
  />
);

export type UseGetCarePathJourneyAttachmentsProps = Omit<
  UseGetProps<
    Attachment[],
    unknown,
    void,
    GetCarePathJourneyAttachmentsPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentsPathParams;

/**
 * Search for Care Path Journey Attachments
 */
export const useGetCarePathJourneyAttachments = ({
  id,
  ...props
}: UseGetCarePathJourneyAttachmentsProps) =>
  useGet<Attachment[], unknown, void, GetCarePathJourneyAttachmentsPathParams>(
    (paramsInPath: GetCarePathJourneyAttachmentsPathParams) =>
      `/carePathJourney/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface GetCarePathJourneyAttachmentPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetCarePathJourneyAttachmentProps = Omit<
  GetProps<Attachment, unknown, void, GetCarePathJourneyAttachmentPathParams>,
  "path"
> &
  GetCarePathJourneyAttachmentPathParams;

/**
 * Get an Attachment associated with a Care Path
 *
 * Gets an existing `Attachment`.
 */
export const GetCarePathJourneyAttachment = ({
  id,
  attachmentId,
  ...props
}: GetCarePathJourneyAttachmentProps) => (
  <Get<Attachment, unknown, void, GetCarePathJourneyAttachmentPathParams>
    path={`/carePathJourney/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseGetCarePathJourneyAttachmentProps = Omit<
  UseGetProps<
    Attachment,
    unknown,
    void,
    GetCarePathJourneyAttachmentPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentPathParams;

/**
 * Get an Attachment associated with a Care Path
 *
 * Gets an existing `Attachment`.
 */
export const useGetCarePathJourneyAttachment = ({
  id,
  attachmentId,
  ...props
}: UseGetCarePathJourneyAttachmentProps) =>
  useGet<Attachment, unknown, void, GetCarePathJourneyAttachmentPathParams>(
    (paramsInPath: GetCarePathJourneyAttachmentPathParams) =>
      `/carePathJourney/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface DeleteCarePathJourneyAttachmentPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  id: string;
}

export type DeleteCarePathJourneyAttachmentProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    string,
    DeleteCarePathJourneyAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteCarePathJourneyAttachmentPathParams;

/**
 * Delete an Attachment associated with a Care Path
 *
 * Deletes an existing `Attachment`.
 */
export const DeleteCarePathJourneyAttachment = ({
  id,
  ...props
}: DeleteCarePathJourneyAttachmentProps) => (
  <Mutate<
    void,
    unknown,
    void,
    string,
    DeleteCarePathJourneyAttachmentPathParams
  >
    verb="DELETE"
    path={`/carePathJourney/${id}/attachment`}
    {...props}
  />
);

export type UseDeleteCarePathJourneyAttachmentProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteCarePathJourneyAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteCarePathJourneyAttachmentPathParams;

/**
 * Delete an Attachment associated with a Care Path
 *
 * Deletes an existing `Attachment`.
 */
export const useDeleteCarePathJourneyAttachment = ({
  id,
  ...props
}: UseDeleteCarePathJourneyAttachmentProps) =>
  useMutate<
    void,
    unknown,
    void,
    string,
    DeleteCarePathJourneyAttachmentPathParams
  >(
    "DELETE",
    (paramsInPath: DeleteCarePathJourneyAttachmentPathParams) =>
      `/carePathJourney/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface UpdateCarePathJourneyAttachmentPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type UpdateCarePathJourneyAttachmentProps = Omit<
  MutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateCarePathJourneyAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateCarePathJourneyAttachmentPathParams;

export const UpdateCarePathJourneyAttachment = ({
  id,
  attachmentId,
  ...props
}: UpdateCarePathJourneyAttachmentProps) => (
  <Mutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateCarePathJourneyAttachmentPathParams
  >
    verb="PATCH"
    path={`/carePathJourney/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseUpdateCarePathJourneyAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateCarePathJourneyAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateCarePathJourneyAttachmentPathParams;

export const useUpdateCarePathJourneyAttachment = ({
  id,
  attachmentId,
  ...props
}: UseUpdateCarePathJourneyAttachmentProps) =>
  useMutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateCarePathJourneyAttachmentPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateCarePathJourneyAttachmentPathParams) =>
      `/carePathJourney/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetCarePathJourneyAttachmentDownloadQueryParams {
  preferOcrEnhanced?: boolean;
}

export interface GetCarePathJourneyAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetCarePathJourneyAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    GetCarePathJourneyAttachmentDownloadQueryParams,
    GetCarePathJourneyAttachmentDownloadPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentDownloadPathParams;

/**
 * Download an Attachment associated with a Care Path
 *
 * Downloads an existing `Attachment`.
 */
export const GetCarePathJourneyAttachmentDownload = ({
  id,
  attachmentId,
  ...props
}: GetCarePathJourneyAttachmentDownloadProps) => (
  <Get<
    void,
    unknown,
    GetCarePathJourneyAttachmentDownloadQueryParams,
    GetCarePathJourneyAttachmentDownloadPathParams
  >
    path={`/carePathJourney/${id}/attachment/${attachmentId}/download`}
    {...props}
  />
);

export type UseGetCarePathJourneyAttachmentDownloadProps = Omit<
  UseGetProps<
    void,
    unknown,
    GetCarePathJourneyAttachmentDownloadQueryParams,
    GetCarePathJourneyAttachmentDownloadPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentDownloadPathParams;

/**
 * Download an Attachment associated with a Care Path
 *
 * Downloads an existing `Attachment`.
 */
export const useGetCarePathJourneyAttachmentDownload = ({
  id,
  attachmentId,
  ...props
}: UseGetCarePathJourneyAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    GetCarePathJourneyAttachmentDownloadQueryParams,
    GetCarePathJourneyAttachmentDownloadPathParams
  >(
    (paramsInPath: GetCarePathJourneyAttachmentDownloadPathParams) =>
      `/carePathJourney/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}/download`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetCarePathJourneyAttachmentAllowedFileTypesResponse {
  allowedFileExtensions?: string[];
}

export interface GetCarePathJourneyAttachmentAllowedFileTypesPathParams {
  /**
   * A unique identifier for a `CarePathJourney`.
   */
  carePathJourneyId: string;
}

export type GetCarePathJourneyAttachmentAllowedFileTypesProps = Omit<
  GetProps<
    GetCarePathJourneyAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetCarePathJourneyAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given care path journey
 */
export const GetCarePathJourneyAttachmentAllowedFileTypes = ({
  carePathJourneyId,
  ...props
}: GetCarePathJourneyAttachmentAllowedFileTypesProps) => (
  <Get<
    GetCarePathJourneyAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetCarePathJourneyAttachmentAllowedFileTypesPathParams
  >
    path={`/carePathJourney/${carePathJourneyId}/attachment/allowedFileTypes`}
    {...props}
  />
);

export type UseGetCarePathJourneyAttachmentAllowedFileTypesProps = Omit<
  UseGetProps<
    GetCarePathJourneyAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetCarePathJourneyAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetCarePathJourneyAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given care path journey
 */
export const useGetCarePathJourneyAttachmentAllowedFileTypes = ({
  carePathJourneyId,
  ...props
}: UseGetCarePathJourneyAttachmentAllowedFileTypesProps) =>
  useGet<
    GetCarePathJourneyAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetCarePathJourneyAttachmentAllowedFileTypesPathParams
  >(
    (paramsInPath: GetCarePathJourneyAttachmentAllowedFileTypesPathParams) =>
      `/carePathJourney/${paramsInPath.carePathJourneyId}/attachment/allowedFileTypes`,
    { pathParams: { carePathJourneyId }, ...props }
  );

export interface SearchSemanticConceptRequestBody {
  query?: string;
  system?: CodeSystem;
  limit?: number;
}

export type SearchSemanticConceptProps = Omit<
  MutateProps<
    SemanticGraph,
    unknown,
    void,
    SearchSemanticConceptRequestBody,
    void
  >,
  "path" | "verb"
>;

export const SearchSemanticConcept = (props: SearchSemanticConceptProps) => (
  <Mutate<SemanticGraph, unknown, void, SearchSemanticConceptRequestBody, void>
    verb="POST"
    path={`/semanticLayer/graphSearch`}
    {...props}
  />
);

export type UseSearchSemanticConceptProps = Omit<
  UseMutateProps<
    SemanticGraph,
    unknown,
    void,
    SearchSemanticConceptRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useSearchSemanticConcept = (
  props: UseSearchSemanticConceptProps
) =>
  useMutate<
    SemanticGraph,
    unknown,
    void,
    SearchSemanticConceptRequestBody,
    void
  >("POST", `/semanticLayer/graphSearch`, props);

export interface SingleConceptSearchRequestBody {
  query?: string;
  system?: CodeSystem[];
  limit?: number;
  exact?: string;
  termList?: string;
  type?: string;
  expandConcepts?: boolean;
  requireLegacyId?: boolean;
}

export type SingleConceptSearchProps = Omit<
  MutateProps<
    SemanticGraph,
    unknown,
    void,
    SingleConceptSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

export const SingleConceptSearch = (props: SingleConceptSearchProps) => (
  <Mutate<SemanticGraph, unknown, void, SingleConceptSearchRequestBody, void>
    verb="POST"
    path={`/semanticLayer/singleConceptSearch`}
    {...props}
  />
);

export type UseSingleConceptSearchProps = Omit<
  UseMutateProps<
    SemanticGraph,
    unknown,
    void,
    SingleConceptSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useSingleConceptSearch = (props: UseSingleConceptSearchProps) =>
  useMutate<SemanticGraph, unknown, void, SingleConceptSearchRequestBody, void>(
    "POST",
    `/semanticLayer/singleConceptSearch`,
    props
  );

export interface IndicationObservationCodeSearchRequestBody {
  query?: string;
  system?: CodeSystem;
  limit?: number;
  termList?: string;
  guidelineId?: string;
}

export type IndicationObservationCodeSearchProps = Omit<
  MutateProps<
    SemanticGraphIndicationCodes,
    unknown,
    void,
    IndicationObservationCodeSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

export const IndicationObservationCodeSearch = (
  props: IndicationObservationCodeSearchProps
) => (
  <Mutate<
    SemanticGraphIndicationCodes,
    unknown,
    void,
    IndicationObservationCodeSearchRequestBody,
    void
  >
    verb="POST"
    path={`/semanticLayer/indicationObservationCodeSearch`}
    {...props}
  />
);

export type UseIndicationObservationCodeSearchProps = Omit<
  UseMutateProps<
    SemanticGraphIndicationCodes,
    unknown,
    void,
    IndicationObservationCodeSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useIndicationObservationCodeSearch = (
  props: UseIndicationObservationCodeSearchProps
) =>
  useMutate<
    SemanticGraphIndicationCodes,
    unknown,
    void,
    IndicationObservationCodeSearchRequestBody,
    void
  >("POST", `/semanticLayer/indicationObservationCodeSearch`, props);

export interface GetSemanticConceptsQueryParams {
  query: string;
  system?: CodeSystem;
}

export type GetSemanticConceptsProps = Omit<
  GetProps<SemanticGraph, unknown, GetSemanticConceptsQueryParams, void>,
  "path"
>;

export const GetSemanticConcepts = (props: GetSemanticConceptsProps) => (
  <Get<SemanticGraph, unknown, GetSemanticConceptsQueryParams, void>
    path={`/semanticLayer`}
    {...props}
  />
);

export type UseGetSemanticConceptsProps = Omit<
  UseGetProps<SemanticGraph, unknown, GetSemanticConceptsQueryParams, void>,
  "path"
>;

export const useGetSemanticConcepts = (props: UseGetSemanticConceptsProps) =>
  useGet<SemanticGraph, unknown, GetSemanticConceptsQueryParams, void>(
    `/semanticLayer`,
    props
  );

export interface GetSemanticConceptGraphQueryParams {
  startAtNode?: boolean;
}

export interface GetSemanticConceptGraphPathParams {
  system: CodeSystem;
  id: string;
}

export type GetSemanticConceptGraphProps = Omit<
  GetProps<
    SemanticGraph,
    unknown,
    GetSemanticConceptGraphQueryParams,
    GetSemanticConceptGraphPathParams
  >,
  "path"
> &
  GetSemanticConceptGraphPathParams;

export const GetSemanticConceptGraph = ({
  system,
  id,
  ...props
}: GetSemanticConceptGraphProps) => (
  <Get<
    SemanticGraph,
    unknown,
    GetSemanticConceptGraphQueryParams,
    GetSemanticConceptGraphPathParams
  >
    path={`/semanticLayer/entity/${system}/${id}/graph`}
    {...props}
  />
);

export type UseGetSemanticConceptGraphProps = Omit<
  UseGetProps<
    SemanticGraph,
    unknown,
    GetSemanticConceptGraphQueryParams,
    GetSemanticConceptGraphPathParams
  >,
  "path"
> &
  GetSemanticConceptGraphPathParams;

export const useGetSemanticConceptGraph = ({
  system,
  id,
  ...props
}: UseGetSemanticConceptGraphProps) =>
  useGet<
    SemanticGraph,
    unknown,
    GetSemanticConceptGraphQueryParams,
    GetSemanticConceptGraphPathParams
  >(
    (paramsInPath: GetSemanticConceptGraphPathParams) =>
      `/semanticLayer/entity/${paramsInPath.system}/${paramsInPath.id}/graph`,
    { pathParams: { system, id }, ...props }
  );

export type UpdateSemanticConceptProps = Omit<
  MutateProps<SemanticConcept, unknown, void, SemanticConcept, void>,
  "path" | "verb"
>;

export const UpdateSemanticConcept = (props: UpdateSemanticConceptProps) => (
  <Mutate<SemanticConcept, unknown, void, SemanticConcept, void>
    verb="POST"
    path={`/semanticLayer/updateConcept`}
    {...props}
  />
);

export type UseUpdateSemanticConceptProps = Omit<
  UseMutateProps<SemanticConcept, unknown, void, SemanticConcept, void>,
  "path" | "verb"
>;

export const useUpdateSemanticConcept = (
  props: UseUpdateSemanticConceptProps
) =>
  useMutate<SemanticConcept, unknown, void, SemanticConcept, void>(
    "POST",
    `/semanticLayer/updateConcept`,
    props
  );

export type CreateSemanticConceptProps = Omit<
  MutateProps<SemanticConcept, unknown, void, SemanticConcept, void>,
  "path" | "verb"
>;

export const CreateSemanticConcept = (props: CreateSemanticConceptProps) => (
  <Mutate<SemanticConcept, unknown, void, SemanticConcept, void>
    verb="POST"
    path={`/semanticLayer/createConcept`}
    {...props}
  />
);

export type UseCreateSemanticConceptProps = Omit<
  UseMutateProps<SemanticConcept, unknown, void, SemanticConcept, void>,
  "path" | "verb"
>;

export const useCreateSemanticConcept = (
  props: UseCreateSemanticConceptProps
) =>
  useMutate<SemanticConcept, unknown, void, SemanticConcept, void>(
    "POST",
    `/semanticLayer/createConcept`,
    props
  );

export interface GetRxnormConceptsQueryParams {
  query?: string;
  max?: number;
  offset?: number;
}

export type GetRxnormConceptsProps = Omit<
  GetProps<RxnormDrug[], unknown, GetRxnormConceptsQueryParams, void>,
  "path"
>;

export const GetRxnormConcepts = (props: GetRxnormConceptsProps) => (
  <Get<RxnormDrug[], unknown, GetRxnormConceptsQueryParams, void>
    path={`/semanticLayer/rxnorm/search`}
    {...props}
  />
);

export type UseGetRxnormConceptsProps = Omit<
  UseGetProps<RxnormDrug[], unknown, GetRxnormConceptsQueryParams, void>,
  "path"
>;

export const useGetRxnormConcepts = (props: UseGetRxnormConceptsProps) =>
  useGet<RxnormDrug[], unknown, GetRxnormConceptsQueryParams, void>(
    `/semanticLayer/rxnorm/search`,
    props
  );

export interface GetRxnormSemanticDrugsPathParams {
  /**
   * A unique identifier for an rxnorm concept.
   */
  cui: string;
}

export type GetRxnormSemanticDrugsProps = Omit<
  GetProps<RxnormDrug[], unknown, void, GetRxnormSemanticDrugsPathParams>,
  "path"
> &
  GetRxnormSemanticDrugsPathParams;

export const GetRxnormSemanticDrugs = ({
  cui,
  ...props
}: GetRxnormSemanticDrugsProps) => (
  <Get<RxnormDrug[], unknown, void, GetRxnormSemanticDrugsPathParams>
    path={`/semanticLayer/rxnorm/${cui}/drugs`}
    {...props}
  />
);

export type UseGetRxnormSemanticDrugsProps = Omit<
  UseGetProps<RxnormDrug[], unknown, void, GetRxnormSemanticDrugsPathParams>,
  "path"
> &
  GetRxnormSemanticDrugsPathParams;

export const useGetRxnormSemanticDrugs = ({
  cui,
  ...props
}: UseGetRxnormSemanticDrugsProps) =>
  useGet<RxnormDrug[], unknown, void, GetRxnormSemanticDrugsPathParams>(
    (paramsInPath: GetRxnormSemanticDrugsPathParams) =>
      `/semanticLayer/rxnorm/${paramsInPath.cui}/drugs`,
    { pathParams: { cui }, ...props }
  );

export interface GetFindingTypesQueryParams {
  query?: string;
  max?: number;
  offset?: number;
  published?: boolean;
  useInRules?: boolean;
}

export type GetFindingTypesProps = Omit<
  GetProps<FindingType[], unknown, GetFindingTypesQueryParams, void>,
  "path"
>;

export const GetFindingTypes = (props: GetFindingTypesProps) => (
  <Get<FindingType[], unknown, GetFindingTypesQueryParams, void>
    path={`/findingType`}
    {...props}
  />
);

export type UseGetFindingTypesProps = Omit<
  UseGetProps<FindingType[], unknown, GetFindingTypesQueryParams, void>,
  "path"
>;

export const useGetFindingTypes = (props: UseGetFindingTypesProps) =>
  useGet<FindingType[], unknown, GetFindingTypesQueryParams, void>(
    `/findingType`,
    props
  );

export interface GetFindingTypeResponse {
  [key: string]: any;
}

export interface GetFindingTypePathParams {
  findingTypeId: string;
}

export type GetFindingTypeProps = Omit<
  GetProps<GetFindingTypeResponse, unknown, void, GetFindingTypePathParams>,
  "path"
> &
  GetFindingTypePathParams;

/**
 * Gets Finding Type for Finding Type Id
 */
export const GetFindingType = ({
  findingTypeId,
  ...props
}: GetFindingTypeProps) => (
  <Get<GetFindingTypeResponse, unknown, void, GetFindingTypePathParams>
    path={`/findingType/${findingTypeId}`}
    {...props}
  />
);

export type UseGetFindingTypeProps = Omit<
  UseGetProps<GetFindingTypeResponse, unknown, void, GetFindingTypePathParams>,
  "path"
> &
  GetFindingTypePathParams;

/**
 * Gets Finding Type for Finding Type Id
 */
export const useGetFindingType = ({
  findingTypeId,
  ...props
}: UseGetFindingTypeProps) =>
  useGet<GetFindingTypeResponse, unknown, void, GetFindingTypePathParams>(
    (paramsInPath: GetFindingTypePathParams) =>
      `/findingType/${paramsInPath.findingTypeId}`,
    { pathParams: { findingTypeId }, ...props }
  );

export interface GetServiceRequestCountsResponse {
  summary?: ServiceRequestCounts;
}

export interface GetServiceRequestCountsQueryParams {
  query?: string;
  viewRecordsReview?: string;
  healthPlanName?: string;
}

export type GetServiceRequestCountsProps = Omit<
  GetProps<
    GetServiceRequestCountsResponse,
    unknown,
    GetServiceRequestCountsQueryParams,
    void
  >,
  "path"
>;

export const GetServiceRequestCounts = (
  props: GetServiceRequestCountsProps
) => (
  <Get<
    GetServiceRequestCountsResponse,
    unknown,
    GetServiceRequestCountsQueryParams,
    void
  >
    path={`/serviceRequest/counts`}
    {...props}
  />
);

export type UseGetServiceRequestCountsProps = Omit<
  UseGetProps<
    GetServiceRequestCountsResponse,
    unknown,
    GetServiceRequestCountsQueryParams,
    void
  >,
  "path"
>;

export const useGetServiceRequestCounts = (
  props: UseGetServiceRequestCountsProps
) =>
  useGet<
    GetServiceRequestCountsResponse,
    unknown,
    GetServiceRequestCountsQueryParams,
    void
  >(`/serviceRequest/counts`, props);

export interface GetDelegatedVendorFromPxCodesResponse {
  delegatedVendor?: string;
}

export interface GetDelegatedVendorFromPxCodesQueryParams {
  patientId: string;
  startDate: string;
  procedureCodes: string[];
}

export type GetDelegatedVendorFromPxCodesProps = Omit<
  GetProps<
    GetDelegatedVendorFromPxCodesResponse,
    unknown,
    GetDelegatedVendorFromPxCodesQueryParams,
    void
  >,
  "path"
>;

export const GetDelegatedVendorFromPxCodes = (
  props: GetDelegatedVendorFromPxCodesProps
) => (
  <Get<
    GetDelegatedVendorFromPxCodesResponse,
    unknown,
    GetDelegatedVendorFromPxCodesQueryParams,
    void
  >
    path={`/delegatedVendor`}
    {...props}
  />
);

export type UseGetDelegatedVendorFromPxCodesProps = Omit<
  UseGetProps<
    GetDelegatedVendorFromPxCodesResponse,
    unknown,
    GetDelegatedVendorFromPxCodesQueryParams,
    void
  >,
  "path"
>;

export const useGetDelegatedVendorFromPxCodes = (
  props: UseGetDelegatedVendorFromPxCodesProps
) =>
  useGet<
    GetDelegatedVendorFromPxCodesResponse,
    unknown,
    GetDelegatedVendorFromPxCodesQueryParams,
    void
  >(`/delegatedVendor`, props);

export interface GenerateServiceRequestTrackingNumberQueryParams {
  query?: string;
}

export type GenerateServiceRequestTrackingNumberProps = Omit<
  MutateProps<
    TrackingNumber,
    unknown,
    GenerateServiceRequestTrackingNumberQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

/**
 * Gets a new tracking number (aka CohereId)
 */
export const GenerateServiceRequestTrackingNumber = (
  props: GenerateServiceRequestTrackingNumberProps
) => (
  <Mutate<
    TrackingNumber,
    unknown,
    GenerateServiceRequestTrackingNumberQueryParams,
    void,
    void
  >
    verb="POST"
    path={`/serviceRequest/generateTrackingNumber`}
    {...props}
  />
);

export type UseGenerateServiceRequestTrackingNumberProps = Omit<
  UseMutateProps<
    TrackingNumber,
    unknown,
    GenerateServiceRequestTrackingNumberQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

/**
 * Gets a new tracking number (aka CohereId)
 */
export const useGenerateServiceRequestTrackingNumber = (
  props: UseGenerateServiceRequestTrackingNumberProps
) =>
  useMutate<
    TrackingNumber,
    unknown,
    GenerateServiceRequestTrackingNumberQueryParams,
    void,
    void
  >("POST", `/serviceRequest/generateTrackingNumber`, props);

export type BatchSubmitServiceRequestsProps = Omit<
  MutateProps<
    ServiceRequestResponse[],
    ServiceRequestUpdateErrorResponse[],
    void,
    ServiceRequestResponse[],
    void
  >,
  "path" | "verb"
>;

export const BatchSubmitServiceRequests = (
  props: BatchSubmitServiceRequestsProps
) => (
  <Mutate<
    ServiceRequestResponse[],
    ServiceRequestUpdateErrorResponse[],
    void,
    ServiceRequestResponse[],
    void
  >
    verb="PATCH"
    path={`/serviceRequest/submit`}
    {...props}
  />
);

export type UseBatchSubmitServiceRequestsProps = Omit<
  UseMutateProps<
    ServiceRequestResponse[],
    ServiceRequestUpdateErrorResponse[],
    void,
    ServiceRequestResponse[],
    void
  >,
  "path" | "verb"
>;

export const useBatchSubmitServiceRequests = (
  props: UseBatchSubmitServiceRequestsProps
) =>
  useMutate<
    ServiceRequestResponse[],
    ServiceRequestUpdateErrorResponse[],
    void,
    ServiceRequestResponse[],
    void
  >("PATCH", `/serviceRequest/submit`, props);

export interface GetServiceRequestQueryParams {
  /**
   * Set to true when to don't want any fields to propagate on authorization while updating the service request.
   */
  skipAuthorizationUpdate?: boolean;
  /**
   * Set to true when you want a full authorization response instead of only id
   */
  expandAuthorization?: boolean;
  /**
   * Set to true if the Service Request needs to have integration status 'READY'
   */
  integrateServiceRequest?: boolean;
  /**
   * Set to true if the attachments of the service request need integration status 'READY'
   */
  integrateAttachments?: boolean;
  /**
   * Set to true if the claimsPaymentNote of the SR is being updated and a Note object should be created
   */
  createClaimsNote?: boolean;
  /**
   * ID of the patient to validate ServiceRequest ownership
   */
  patientId?: string;
}

export interface GetServiceRequestPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestProps = Omit<
  GetProps<
    ServiceRequestResponse,
    unknown,
    GetServiceRequestQueryParams,
    GetServiceRequestPathParams
  >,
  "path"
> &
  GetServiceRequestPathParams;

export const GetServiceRequest = ({ id, ...props }: GetServiceRequestProps) => (
  <Get<
    ServiceRequestResponse,
    unknown,
    GetServiceRequestQueryParams,
    GetServiceRequestPathParams
  >
    path={`/serviceRequest/${id}`}
    {...props}
  />
);

export type UseGetServiceRequestProps = Omit<
  UseGetProps<
    ServiceRequestResponse,
    unknown,
    GetServiceRequestQueryParams,
    GetServiceRequestPathParams
  >,
  "path"
> &
  GetServiceRequestPathParams;

export const useGetServiceRequest = ({
  id,
  ...props
}: UseGetServiceRequestProps) =>
  useGet<
    ServiceRequestResponse,
    unknown,
    GetServiceRequestQueryParams,
    GetServiceRequestPathParams
  >(
    (paramsInPath: GetServiceRequestPathParams) =>
      `/serviceRequest/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateServiceRequestQueryParams {
  /**
   * Set to true when to don't want any fields to propagate on authorization while updating the service request.
   */
  skipAuthorizationUpdate?: boolean;
  /**
   * Set to true when you want a full authorization response instead of only id
   */
  expandAuthorization?: boolean;
  /**
   * Set to true if the Service Request needs to have integration status 'READY'
   */
  integrateServiceRequest?: boolean;
  /**
   * Set to true if the attachments of the service request need integration status 'READY'
   */
  integrateAttachments?: boolean;
  /**
   * Set to true if the claimsPaymentNote of the SR is being updated and a Note object should be created
   */
  createClaimsNote?: boolean;
  /**
   * ID of the patient to validate ServiceRequest ownership
   */
  patientId?: string;
}

export interface UpdateServiceRequestPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type UpdateServiceRequestProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    ServiceRequestUpdateErrorResponse,
    UpdateServiceRequestQueryParams,
    ServiceRequestUpdatePayload,
    UpdateServiceRequestPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestPathParams;

export const UpdateServiceRequest = ({
  id,
  ...props
}: UpdateServiceRequestProps) => (
  <Mutate<
    ServiceRequestResponse,
    ServiceRequestUpdateErrorResponse,
    UpdateServiceRequestQueryParams,
    ServiceRequestUpdatePayload,
    UpdateServiceRequestPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${id}`}
    {...props}
  />
);

export type UseUpdateServiceRequestProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    ServiceRequestUpdateErrorResponse,
    UpdateServiceRequestQueryParams,
    ServiceRequestUpdatePayload,
    UpdateServiceRequestPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestPathParams;

export const useUpdateServiceRequest = ({
  id,
  ...props
}: UseUpdateServiceRequestProps) =>
  useMutate<
    ServiceRequestResponse,
    ServiceRequestUpdateErrorResponse,
    UpdateServiceRequestQueryParams,
    ServiceRequestUpdatePayload,
    UpdateServiceRequestPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateServiceRequestPathParams) =>
      `/serviceRequest/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface DeleteServiceRequestQueryParams {
  /**
   * Set to true when to don't want any fields to propagate on authorization while updating the service request.
   */
  skipAuthorizationUpdate?: boolean;
  /**
   * Set to true when you want a full authorization response instead of only id
   */
  expandAuthorization?: boolean;
  /**
   * Set to true if the Service Request needs to have integration status 'READY'
   */
  integrateServiceRequest?: boolean;
  /**
   * Set to true if the attachments of the service request need integration status 'READY'
   */
  integrateAttachments?: boolean;
  /**
   * Set to true if the claimsPaymentNote of the SR is being updated and a Note object should be created
   */
  createClaimsNote?: boolean;
  /**
   * ID of the patient to validate ServiceRequest ownership
   */
  patientId?: string;
}

export type DeleteServiceRequestProps = Omit<
  MutateProps<void, unknown, DeleteServiceRequestQueryParams, string, void>,
  "path" | "verb"
>;

/**
 * Soft delete
 */
export const DeleteServiceRequest = (props: DeleteServiceRequestProps) => (
  <Mutate<void, unknown, DeleteServiceRequestQueryParams, string, void>
    verb="DELETE"
    path={`/serviceRequest`}
    {...props}
  />
);

export type UseDeleteServiceRequestProps = Omit<
  UseMutateProps<void, unknown, DeleteServiceRequestQueryParams, string, void>,
  "path" | "verb"
>;

/**
 * Soft delete
 */
export const useDeleteServiceRequest = (props: UseDeleteServiceRequestProps) =>
  useMutate<void, unknown, DeleteServiceRequestQueryParams, string, void>(
    "DELETE",
    `/serviceRequest`,
    { ...props }
  );

export interface ExtendServiceRequestTurnAroundTimePathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type ExtendServiceRequestTurnAroundTimeProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    ExtendServiceRequestTurnAroundTimePathParams
  >,
  "path" | "verb"
> &
  ExtendServiceRequestTurnAroundTimePathParams;

/**
 * extend ServiceRequest TurnAroundTime
 */
export const ExtendServiceRequestTurnAroundTime = ({
  serviceRequestId,
  ...props
}: ExtendServiceRequestTurnAroundTimeProps) => (
  <Mutate<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    ExtendServiceRequestTurnAroundTimePathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/tatExtension`}
    {...props}
  />
);

export type UseExtendServiceRequestTurnAroundTimeProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    ExtendServiceRequestTurnAroundTimePathParams
  >,
  "path" | "verb"
> &
  ExtendServiceRequestTurnAroundTimePathParams;

/**
 * extend ServiceRequest TurnAroundTime
 */
export const useExtendServiceRequestTurnAroundTime = ({
  serviceRequestId,
  ...props
}: UseExtendServiceRequestTurnAroundTimeProps) =>
  useMutate<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    ExtendServiceRequestTurnAroundTimePathParams
  >(
    "POST",
    (paramsInPath: ExtendServiceRequestTurnAroundTimePathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/tatExtension`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetAllServiceRequestAttachmentsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetAllServiceRequestAttachmentsProps = Omit<
  GetProps<
    Attachment[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsPathParams
  >,
  "path"
> &
  GetAllServiceRequestAttachmentsPathParams;

/**
 * Get a list of attachments (sorted in descending order by dateCreated) for this service request and for other service request(s) that this service request's authorization might contain.
 *
 */
export const GetAllServiceRequestAttachments = ({
  serviceRequestId,
  ...props
}: GetAllServiceRequestAttachmentsProps) => (
  <Get<Attachment[], unknown, void, GetAllServiceRequestAttachmentsPathParams>
    path={`/serviceRequest/${serviceRequestId}/attachments`}
    {...props}
  />
);

export type UseGetAllServiceRequestAttachmentsProps = Omit<
  UseGetProps<
    Attachment[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsPathParams
  >,
  "path"
> &
  GetAllServiceRequestAttachmentsPathParams;

/**
 * Get a list of attachments (sorted in descending order by dateCreated) for this service request and for other service request(s) that this service request's authorization might contain.
 *
 */
export const useGetAllServiceRequestAttachments = ({
  serviceRequestId,
  ...props
}: UseGetAllServiceRequestAttachmentsProps) =>
  useGet<
    Attachment[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsPathParams
  >(
    (paramsInPath: GetAllServiceRequestAttachmentsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/attachments`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetAllServiceRequestAttachmentsOcrPagesInfoPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetAllServiceRequestAttachmentsOcrPagesInfoProps = Omit<
  GetProps<
    AttachmentOcrPages[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrPagesInfoPathParams
  >,
  "path"
> &
  GetAllServiceRequestAttachmentsOcrPagesInfoPathParams;

/**
 * Get a list of attachments pages information for a service request
 *
 */
export const GetAllServiceRequestAttachmentsOcrPagesInfo = ({
  serviceRequestId,
  ...props
}: GetAllServiceRequestAttachmentsOcrPagesInfoProps) => (
  <Get<
    AttachmentOcrPages[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrPagesInfoPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/attachments/pagesInfo`}
    {...props}
  />
);

export type UseGetAllServiceRequestAttachmentsOcrPagesInfoProps = Omit<
  UseGetProps<
    AttachmentOcrPages[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrPagesInfoPathParams
  >,
  "path"
> &
  GetAllServiceRequestAttachmentsOcrPagesInfoPathParams;

/**
 * Get a list of attachments pages information for a service request
 *
 */
export const useGetAllServiceRequestAttachmentsOcrPagesInfo = ({
  serviceRequestId,
  ...props
}: UseGetAllServiceRequestAttachmentsOcrPagesInfoProps) =>
  useGet<
    AttachmentOcrPages[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrPagesInfoPathParams
  >(
    (paramsInPath: GetAllServiceRequestAttachmentsOcrPagesInfoPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/attachments/pagesInfo`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetAllServiceRequestAttachmentsOcrTokensPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetAllServiceRequestAttachmentsOcrTokensProps = Omit<
  GetProps<
    AttachmentOcrTokens[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrTokensPathParams
  >,
  "path"
> &
  GetAllServiceRequestAttachmentsOcrTokensPathParams;

/**
 * Get a list of attachments ocr tokens for a service request
 *
 */
export const GetAllServiceRequestAttachmentsOcrTokens = ({
  serviceRequestId,
  ...props
}: GetAllServiceRequestAttachmentsOcrTokensProps) => (
  <Get<
    AttachmentOcrTokens[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrTokensPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/attachments/ocrTokens`}
    {...props}
  />
);

export type UseGetAllServiceRequestAttachmentsOcrTokensProps = Omit<
  UseGetProps<
    AttachmentOcrTokens[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrTokensPathParams
  >,
  "path"
> &
  GetAllServiceRequestAttachmentsOcrTokensPathParams;

/**
 * Get a list of attachments ocr tokens for a service request
 *
 */
export const useGetAllServiceRequestAttachmentsOcrTokens = ({
  serviceRequestId,
  ...props
}: UseGetAllServiceRequestAttachmentsOcrTokensProps) =>
  useGet<
    AttachmentOcrTokens[],
    unknown,
    void,
    GetAllServiceRequestAttachmentsOcrTokensPathParams
  >(
    (paramsInPath: GetAllServiceRequestAttachmentsOcrTokensPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/attachments/ocrTokens`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface RequestDoesExistBeyondOrgResponse {
  resourceFound?: string;
}

export interface RequestDoesExistBeyondOrgQueryParams {
  /**
   * The query to search for
   */
  searchQuery: string;
}

export type RequestDoesExistBeyondOrgProps = Omit<
  GetProps<
    RequestDoesExistBeyondOrgResponse,
    unknown,
    RequestDoesExistBeyondOrgQueryParams,
    void
  >,
  "path"
>;

export const RequestDoesExistBeyondOrg = (
  props: RequestDoesExistBeyondOrgProps
) => (
  <Get<
    RequestDoesExistBeyondOrgResponse,
    unknown,
    RequestDoesExistBeyondOrgQueryParams,
    void
  >
    path={`/serviceRequest/doesExist`}
    {...props}
  />
);

export type UseRequestDoesExistBeyondOrgProps = Omit<
  UseGetProps<
    RequestDoesExistBeyondOrgResponse,
    unknown,
    RequestDoesExistBeyondOrgQueryParams,
    void
  >,
  "path"
>;

export const useRequestDoesExistBeyondOrg = (
  props: UseRequestDoesExistBeyondOrgProps
) =>
  useGet<
    RequestDoesExistBeyondOrgResponse,
    unknown,
    RequestDoesExistBeyondOrgQueryParams,
    void
  >(`/serviceRequest/doesExist`, props);

export interface VerifyPatientDetailsBeyondOrgResponse {
  validated?: boolean;
  patientId?: string;
  serviceRequestId?: string;
}

export interface VerifyPatientDetailsBeyondOrgRequestBody {
  memberId?: string;
  dateOfBirth?: string;
  searchQuery?: string;
  responseType?: string;
}

export type VerifyPatientDetailsBeyondOrgProps = Omit<
  MutateProps<
    VerifyPatientDetailsBeyondOrgResponse,
    unknown,
    void,
    VerifyPatientDetailsBeyondOrgRequestBody,
    void
  >,
  "path" | "verb"
>;

export const VerifyPatientDetailsBeyondOrg = (
  props: VerifyPatientDetailsBeyondOrgProps
) => (
  <Mutate<
    VerifyPatientDetailsBeyondOrgResponse,
    unknown,
    void,
    VerifyPatientDetailsBeyondOrgRequestBody,
    void
  >
    verb="POST"
    path={`/patient/verifyPatientDetails`}
    {...props}
  />
);

export type UseVerifyPatientDetailsBeyondOrgProps = Omit<
  UseMutateProps<
    VerifyPatientDetailsBeyondOrgResponse,
    unknown,
    void,
    VerifyPatientDetailsBeyondOrgRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useVerifyPatientDetailsBeyondOrg = (
  props: UseVerifyPatientDetailsBeyondOrgProps
) =>
  useMutate<
    VerifyPatientDetailsBeyondOrgResponse,
    unknown,
    void,
    VerifyPatientDetailsBeyondOrgRequestBody,
    void
  >("POST", `/patient/verifyPatientDetails`, props);

export interface UnifiedSearchRequestBody {
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * Number of results to skip
   */
  offset?: number;
  /**
   * Fuzzy text search on serviceRequest object
   */
  query?: string;
  /**
   * Authorization status
   */
  authStatus?: string;
  /**
   * Authorization number
   */
  authNumber?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * Patient member ID
   */
  patientMemberId?: string;
  /**
   * Patient full name
   */
  patientName?: string;
  /**
   * createdBy
   */
  createdBy?: {};
  /**
   * Start date
   */
  startDate?: {};
  /**
   * Patient ID (can see service requests from other practices if this is specified)
   */
  patient?: {};
  searchType?: UnifiedSearchTypeParam;
  /**
   * Fields to order by
   */
  sort?: string;
  /**
   * Direction for sorting
   */
  order?: "asc" | "desc";
  withdrawnReason?: string;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
}

export type UnifiedSearchProps = Omit<
  MutateProps<
    UnifiedSearchResponseBody,
    unknown,
    void,
    UnifiedSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Which records are returned is affected by the parameters as well as the requesting user's group/practice.
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 *
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const UnifiedSearch = (props: UnifiedSearchProps) => (
  <Mutate<
    UnifiedSearchResponseBody,
    unknown,
    void,
    UnifiedSearchRequestBody,
    void
  >
    verb="POST"
    path={`/dashboard/search`}
    {...props}
  />
);

export type UseUnifiedSearchProps = Omit<
  UseMutateProps<
    UnifiedSearchResponseBody,
    unknown,
    void,
    UnifiedSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Which records are returned is affected by the parameters as well as the requesting user's group/practice.
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 *
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const useUnifiedSearch = (props: UseUnifiedSearchProps) =>
  useMutate<
    UnifiedSearchResponseBody,
    unknown,
    void,
    UnifiedSearchRequestBody,
    void
  >("POST", `/dashboard/search`, props);

export interface ServiceRequestSearchV2Response {
  serviceRequestListResponse?: ServiceRequestSearchResponse[];
  patientsAsCardsResponse?: {
    /**
     * The name of the patient.
     */
    patientName: string;
    /**
     * The unique ID of the patient.
     */
    patientId: string;
  }[];
}

export interface ServiceRequestSearchV2RequestBody {
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * Number of results to skip
   */
  offset?: number;
  /**
   * Fuzzy text search on serviceRequest object
   */
  query?: string;
  /**
   * Authorization status
   */
  authStatus?: string;
  /**
   * Authorization number
   */
  authNumber?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * Patient member ID
   */
  patientMemberId?: string;
  /**
   * Patient full name
   */
  patientName?: string;
  /**
   * createdBy
   */
  createdBy?: {};
  /**
   * Start date
   */
  startDate?: {};
  /**
   * Patient ID (can see service requests from other practices if this is specified)
   */
  patient?: {};
  /**
   * Fields to order by
   */
  sort?: string;
  /**
   * Direction for sorting
   */
  order?: "asc" | "desc";
  withdrawnReason?: string;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
}

export type ServiceRequestSearchV2Props = Omit<
  MutateProps<
    ServiceRequestSearchV2Response,
    unknown,
    void,
    ServiceRequestSearchV2RequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Which records are returned is affected by the parameters as well as the requesting user's group/practice.
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 *
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const ServiceRequestSearchV2 = (props: ServiceRequestSearchV2Props) => (
  <Mutate<
    ServiceRequestSearchV2Response,
    unknown,
    void,
    ServiceRequestSearchV2RequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/v2/search`}
    {...props}
  />
);

export type UseServiceRequestSearchV2Props = Omit<
  UseMutateProps<
    ServiceRequestSearchV2Response,
    unknown,
    void,
    ServiceRequestSearchV2RequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Which records are returned is affected by the parameters as well as the requesting user's group/practice.
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 *
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const useServiceRequestSearchV2 = (
  props: UseServiceRequestSearchV2Props
) =>
  useMutate<
    ServiceRequestSearchV2Response,
    unknown,
    void,
    ServiceRequestSearchV2RequestBody,
    void
  >("POST", `/serviceRequest/v2/search`, props);

export interface ServiceRequestSearchRequestBody {
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * Number of results to skip
   */
  offset?: number;
  /**
   * Fuzzy text search on serviceRequest object
   */
  query?: string;
  /**
   * Authorization status
   */
  authStatus?: string;
  /**
   * Authorization number
   */
  authNumber?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * Patient member ID
   */
  patientMemberId?: string;
  /**
   * Patient full name
   */
  patientName?: string;
  /**
   * createdBy
   */
  createdBy?: {};
  /**
   * Start date
   */
  startDate?: {};
  /**
   * Patient ID (can see service requests from other practices if this is specified)
   */
  patient?: {};
  /**
   * Fields to order by
   */
  sort?: string;
  /**
   * Direction for sorting
   */
  order?: "asc" | "desc";
  withdrawnReason?: string;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
}

export type ServiceRequestSearchProps = Omit<
  MutateProps<
    ServiceRequestSearchResponse[],
    unknown,
    void,
    ServiceRequestSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Which records are returned is affected by the parameters as well
 * as the requesting user's group/practice.
 *
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 *
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const ServiceRequestSearch = (props: ServiceRequestSearchProps) => (
  <Mutate<
    ServiceRequestSearchResponse[],
    unknown,
    void,
    ServiceRequestSearchRequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/search`}
    {...props}
  />
);

export type UseServiceRequestSearchProps = Omit<
  UseMutateProps<
    ServiceRequestSearchResponse[],
    unknown,
    void,
    ServiceRequestSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Which records are returned is affected by the parameters as well
 * as the requesting user's group/practice.
 *
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 *
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const useServiceRequestSearch = (props: UseServiceRequestSearchProps) =>
  useMutate<
    ServiceRequestSearchResponse[],
    unknown,
    void,
    ServiceRequestSearchRequestBody,
    void
  >("POST", `/serviceRequest/search`, props);

export interface ReferralRequestSearchRequestBody {
  /**
   * maximum number of results to return
   */
  max?: number;
  /**
   * Number of results to skip
   */
  offset?: number;
  /**
   * special check for results that match cohereId or authNumber
   */
  cohereIdOrAuthNumber?: string;
  /**
   * determines the order of the results
   */
  sort?: string;
  referralStatus?: ReferralStatus;
  /**
   * Patient member ID
   */
  patientMemberId?: string;
  /**
   * Patient full name
   */
  patientName?: string;
  /**
   * search bar text containing params
   */
  query?: string;
  healthPlanName?: string;
  healthPlanDisplayName?: string;
}

export type ReferralRequestSearchProps = Omit<
  MutateProps<
    ReferralRequestSearchResponse[],
    unknown,
    void,
    ReferralRequestSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * returns the set of referral requests that meet the posted parameters
 *
 */
export const ReferralRequestSearch = (props: ReferralRequestSearchProps) => (
  <Mutate<
    ReferralRequestSearchResponse[],
    unknown,
    void,
    ReferralRequestSearchRequestBody,
    void
  >
    verb="POST"
    path={`/referralRequest/search`}
    {...props}
  />
);

export type UseReferralRequestSearchProps = Omit<
  UseMutateProps<
    ReferralRequestSearchResponse[],
    unknown,
    void,
    ReferralRequestSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * returns the set of referral requests that meet the posted parameters
 *
 */
export const useReferralRequestSearch = (
  props: UseReferralRequestSearchProps
) =>
  useMutate<
    ReferralRequestSearchResponse[],
    unknown,
    void,
    ReferralRequestSearchRequestBody,
    void
  >("POST", `/referralRequest/search`, props);

export type SubmitReferralRequestProps = Omit<
  MutateProps<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestResponse,
    void
  >,
  "path" | "verb"
>;

export const SubmitReferralRequest = (props: SubmitReferralRequestProps) => (
  <Mutate<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestResponse,
    void
  >
    verb="PATCH"
    path={`/referralRequest/submit`}
    {...props}
  />
);

export type UseSubmitReferralRequestProps = Omit<
  UseMutateProps<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestResponse,
    void
  >,
  "path" | "verb"
>;

export const useSubmitReferralRequest = (
  props: UseSubmitReferralRequestProps
) =>
  useMutate<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestResponse,
    void
  >("PATCH", `/referralRequest/submit`, props);

export type GetPotentialDuplicatesForReferralRequestProps = Omit<
  MutateProps<string[], unknown, void, ReferralRequestCreatePayload, void>,
  "path" | "verb"
>;

export const GetPotentialDuplicatesForReferralRequest = (
  props: GetPotentialDuplicatesForReferralRequestProps
) => (
  <Mutate<string[], unknown, void, ReferralRequestCreatePayload, void>
    verb="POST"
    path={`/referralRequest/duplicateCheck`}
    {...props}
  />
);

export type UseGetPotentialDuplicatesForReferralRequestProps = Omit<
  UseMutateProps<string[], unknown, void, ReferralRequestCreatePayload, void>,
  "path" | "verb"
>;

export const useGetPotentialDuplicatesForReferralRequest = (
  props: UseGetPotentialDuplicatesForReferralRequestProps
) =>
  useMutate<string[], unknown, void, ReferralRequestCreatePayload, void>(
    "POST",
    `/referralRequest/duplicateCheck`,
    props
  );

export interface ReferralRequestFormConfigurationQueryParams {
  /**
   * id of patient
   */
  patientId?: string;
  /**
   * config type based on type of referral
   */
  isAdministrative?: boolean;
}

export type ReferralRequestFormConfigurationProps = Omit<
  GetProps<
    ReferralManagementFormConfiguration | void,
    unknown,
    ReferralRequestFormConfigurationQueryParams,
    void
  >,
  "path"
>;

export const ReferralRequestFormConfiguration = (
  props: ReferralRequestFormConfigurationProps
) => (
  <Get<
    ReferralManagementFormConfiguration | void,
    unknown,
    ReferralRequestFormConfigurationQueryParams,
    void
  >
    path={`/referralRequestFormConfiguration/search`}
    {...props}
  />
);

export type UseReferralRequestFormConfigurationProps = Omit<
  UseGetProps<
    ReferralManagementFormConfiguration | void,
    unknown,
    ReferralRequestFormConfigurationQueryParams,
    void
  >,
  "path"
>;

export const useReferralRequestFormConfiguration = (
  props: UseReferralRequestFormConfigurationProps
) =>
  useGet<
    ReferralManagementFormConfiguration | void,
    unknown,
    ReferralRequestFormConfigurationQueryParams,
    void
  >(`/referralRequestFormConfiguration/search`, props);

export interface GetEpisodeSummaryQueryParams {
  objectType?: "ServiceRequest" | "Authorization";
}

export interface GetEpisodeSummaryPathParams {
  /**
   * The CareWebQI identifier.
   */
  episodeSummaryId: string;
}

export type GetEpisodeSummaryProps = Omit<
  GetProps<
    void,
    unknown,
    GetEpisodeSummaryQueryParams,
    GetEpisodeSummaryPathParams
  >,
  "path"
> &
  GetEpisodeSummaryPathParams;

export const GetEpisodeSummary = ({
  episodeSummaryId,
  ...props
}: GetEpisodeSummaryProps) => (
  <Get<void, unknown, GetEpisodeSummaryQueryParams, GetEpisodeSummaryPathParams>
    path={`/carewebqi/${episodeSummaryId}`}
    {...props}
  />
);

export type UseGetEpisodeSummaryProps = Omit<
  UseGetProps<
    void,
    unknown,
    GetEpisodeSummaryQueryParams,
    GetEpisodeSummaryPathParams
  >,
  "path"
> &
  GetEpisodeSummaryPathParams;

export const useGetEpisodeSummary = ({
  episodeSummaryId,
  ...props
}: UseGetEpisodeSummaryProps) =>
  useGet<
    void,
    unknown,
    GetEpisodeSummaryQueryParams,
    GetEpisodeSummaryPathParams
  >(
    (paramsInPath: GetEpisodeSummaryPathParams) =>
      `/carewebqi/${paramsInPath.episodeSummaryId}`,
    { pathParams: { episodeSummaryId }, ...props }
  );

export interface CarewebqiDocumentationResponse {
  /**
   * url to request to
   */
  url: string;
  /**
   * params to pass to carewebqi endpoint via form data
   */
  params: { [key: string]: any };
}

export interface CarewebqiDocumentationPathParams {
  /**
   * The ServiceRequest identifier.
   */
  serviceRequestId: string;
}

export interface CarewebqiDocumentationRequestBody {
  /**
   * url to return to, usually window.location
   */
  returnUrl?: string;
}

export type CarewebqiDocumentationProps = Omit<
  MutateProps<
    CarewebqiDocumentationResponse,
    unknown,
    void,
    CarewebqiDocumentationRequestBody,
    CarewebqiDocumentationPathParams
  >,
  "path" | "verb"
> &
  CarewebqiDocumentationPathParams;

export const CarewebqiDocumentation = ({
  serviceRequestId,
  ...props
}: CarewebqiDocumentationProps) => (
  <Mutate<
    CarewebqiDocumentationResponse,
    unknown,
    void,
    CarewebqiDocumentationRequestBody,
    CarewebqiDocumentationPathParams
  >
    verb="PATCH"
    path={`/carewebqi/${serviceRequestId}`}
    {...props}
  />
);

export type UseCarewebqiDocumentationProps = Omit<
  UseMutateProps<
    CarewebqiDocumentationResponse,
    unknown,
    void,
    CarewebqiDocumentationRequestBody,
    CarewebqiDocumentationPathParams
  >,
  "path" | "verb"
> &
  CarewebqiDocumentationPathParams;

export const useCarewebqiDocumentation = ({
  serviceRequestId,
  ...props
}: UseCarewebqiDocumentationProps) =>
  useMutate<
    CarewebqiDocumentationResponse,
    unknown,
    void,
    CarewebqiDocumentationRequestBody,
    CarewebqiDocumentationPathParams
  >(
    "PATCH",
    (paramsInPath: CarewebqiDocumentationPathParams) =>
      `/carewebqi/${paramsInPath.serviceRequestId}`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetServiceRequestsQueryParams {
  /**
   * Patient ID (can see service requests from other practices if this is specified)
   */
  patient?: string;
  /**
   * The cohere id, or tracking number, of the request
   */
  cohereId?: string;
  /**
   * The cohere id/tracking number or the auth number, of the request
   */
  cohereIdOrAuthNumber?: string;
  authStatus?: string;
  /**
   * The view to return for a service request
   */
  view?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  /**
   * The include service request id of the request
   */
  includeServiceRequest?: string;
  /**
   * Only include service requests without an authorization
   */
  includeOnlyEmptyAuth?: boolean;
  sort?: string;
}

export type GetServiceRequestsProps = Omit<
  GetProps<
    ServiceRequestResponse[],
    unknown,
    GetServiceRequestsQueryParams,
    void
  >,
  "path"
>;

/**
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const GetServiceRequests = (props: GetServiceRequestsProps) => (
  <Get<ServiceRequestResponse[], unknown, GetServiceRequestsQueryParams, void>
    path={`/serviceRequest`}
    {...props}
  />
);

export type UseGetServiceRequestsProps = Omit<
  UseGetProps<
    ServiceRequestResponse[],
    unknown,
    GetServiceRequestsQueryParams,
    void
  >,
  "path"
>;

/**
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 *
 */
export const useGetServiceRequests = (props: UseGetServiceRequestsProps) =>
  useGet<
    ServiceRequestResponse[],
    unknown,
    GetServiceRequestsQueryParams,
    void
  >(`/serviceRequest`, props);

export type ValidateServiceRequestProps = Omit<
  MutateProps<
    ValidationResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const ValidateServiceRequest = (props: ValidateServiceRequestProps) => (
  <Mutate<ValidationResponse, unknown, void, ServiceRequestCreatePayload, void>
    verb="POST"
    path={`/serviceRequest/validate`}
    {...props}
  />
);

export type UseValidateServiceRequestProps = Omit<
  UseMutateProps<
    ValidationResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const useValidateServiceRequest = (
  props: UseValidateServiceRequestProps
) =>
  useMutate<
    ValidationResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >("POST", `/serviceRequest/validate`, props);

export type CreateMedicalServiceRequestProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const CreateMedicalServiceRequest = (
  props: CreateMedicalServiceRequestProps
) => (
  <Mutate<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >
    verb="POST"
    path={`/medicalServiceRequest`}
    {...props}
  />
);

export type UseCreateMedicalServiceRequestProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const useCreateMedicalServiceRequest = (
  props: UseCreateMedicalServiceRequestProps
) =>
  useMutate<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >("POST", `/medicalServiceRequest`, props);

export interface GetAuthValidityWindowQueryParams {
  /**
   * Patient ID
   */
  patient: string;
  /**
   * The encounter type of the auth
   */
  encounterType?: FacilityCategory;
  /**
   * The oscar service type (if applicable)
   */
  serviceType?: OscarServiceType;
  /**
   * The cohere id, or tracking number, of the request
   */
  startDate: string;
}

export type GetAuthValidityWindowProps = Omit<
  GetProps<
    AuthValidityWindowResponse,
    unknown,
    GetAuthValidityWindowQueryParams,
    void
  >,
  "path"
>;

export const GetAuthValidityWindow = (props: GetAuthValidityWindowProps) => (
  <Get<
    AuthValidityWindowResponse,
    unknown,
    GetAuthValidityWindowQueryParams,
    void
  >
    path={`/medicalServiceRequest/authValidityWindow`}
    {...props}
  />
);

export type UseGetAuthValidityWindowProps = Omit<
  UseGetProps<
    AuthValidityWindowResponse,
    unknown,
    GetAuthValidityWindowQueryParams,
    void
  >,
  "path"
>;

export const useGetAuthValidityWindow = (
  props: UseGetAuthValidityWindowProps
) =>
  useGet<
    AuthValidityWindowResponse,
    unknown,
    GetAuthValidityWindowQueryParams,
    void
  >(`/medicalServiceRequest/authValidityWindow`, props);

export interface CreatePharmacyServiceRequestRequestBody {
  startDate: string;
  primarySemanticDiagnosisCode?: DiagnosisCode;
  secondarySemanticDiagnosisCodes?: DiagnosisCode[];
  prescribedDrug?: Drug;
  orderingProvider?: Provider;
  patient?: Patient;
  semanticProcedureCodes?: ProcedureCode[];
  authStatus: AuthStatus;
  /**
   * Unique identifier created to correlate service request to transient rule runs and select variable actions
   */
  workflowId?: string;
  urgency?: Urgency;
}

export type CreatePharmacyServiceRequestProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    CreatePharmacyServiceRequestRequestBody,
    void
  >,
  "path" | "verb"
>;

export const CreatePharmacyServiceRequest = (
  props: CreatePharmacyServiceRequestProps
) => (
  <Mutate<
    ServiceRequestResponse,
    unknown,
    void,
    CreatePharmacyServiceRequestRequestBody,
    void
  >
    verb="POST"
    path={`/pharmacyServiceRequest`}
    {...props}
  />
);

export type UseCreatePharmacyServiceRequestProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    CreatePharmacyServiceRequestRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useCreatePharmacyServiceRequest = (
  props: UseCreatePharmacyServiceRequestProps
) =>
  useMutate<
    ServiceRequestResponse,
    unknown,
    void,
    CreatePharmacyServiceRequestRequestBody,
    void
  >("POST", `/pharmacyServiceRequest`, props);

export type UpdateReplaceOrAmendServiceRequestProps = Omit<
  MutateProps<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >,
  "path" | "verb"
>;

export const UpdateReplaceOrAmendServiceRequest = (
  props: UpdateReplaceOrAmendServiceRequestProps
) => (
  <Mutate<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >
    verb="POST"
    path={`/serviceRequest/updateReplaceOrAmend`}
    {...props}
  />
);

export type UseUpdateReplaceOrAmendServiceRequestProps = Omit<
  UseMutateProps<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >,
  "path" | "verb"
>;

export const useUpdateReplaceOrAmendServiceRequest = (
  props: UseUpdateReplaceOrAmendServiceRequestProps
) =>
  useMutate<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >("POST", `/serviceRequest/updateReplaceOrAmend`, props);

export type TestUpdateReplaceOrAmendServiceRequestProps = Omit<
  MutateProps<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >,
  "path" | "verb"
>;

export const TestUpdateReplaceOrAmendServiceRequest = (
  props: TestUpdateReplaceOrAmendServiceRequestProps
) => (
  <Mutate<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >
    verb="POST"
    path={`/serviceRequest/updateReplaceOrAmend/test`}
    {...props}
  />
);

export type UseTestUpdateReplaceOrAmendServiceRequestProps = Omit<
  UseMutateProps<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >,
  "path" | "verb"
>;

export const useTestUpdateReplaceOrAmendServiceRequest = (
  props: UseTestUpdateReplaceOrAmendServiceRequestProps
) =>
  useMutate<
    UpdateReplaceOrAmendResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >("POST", `/serviceRequest/updateReplaceOrAmend/test`, props);

export type WithdrawAndReplaceServiceRequestProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >,
  "path" | "verb"
>;

export const WithdrawAndReplaceServiceRequest = (
  props: WithdrawAndReplaceServiceRequestProps
) => (
  <Mutate<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >
    verb="POST"
    path={`/serviceRequest/withdrawAndReplace`}
    {...props}
  />
);

export type UseWithdrawAndReplaceServiceRequestProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >,
  "path" | "verb"
>;

export const useWithdrawAndReplaceServiceRequest = (
  props: UseWithdrawAndReplaceServiceRequestProps
) =>
  useMutate<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestUpdateAmendOrReplacePayload,
    void
  >("POST", `/serviceRequest/withdrawAndReplace`, props);

export interface SendReviewBackToReviewerPathParams {
  /**
   * A unique identifier for a ClinicalReview
   */
  reviewId: string;
}

export type SendReviewBackToReviewerProps = Omit<
  MutateProps<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    ClinicalReviewChangeRequestPayload,
    SendReviewBackToReviewerPathParams
  >,
  "path" | "verb"
> &
  SendReviewBackToReviewerPathParams;

export const SendReviewBackToReviewer = ({
  reviewId,
  ...props
}: SendReviewBackToReviewerProps) => (
  <Mutate<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    ClinicalReviewChangeRequestPayload,
    SendReviewBackToReviewerPathParams
  >
    verb="POST"
    path={`/review/${reviewId}/sendReviewBackToReviewer`}
    {...props}
  />
);

export type UseSendReviewBackToReviewerProps = Omit<
  UseMutateProps<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    ClinicalReviewChangeRequestPayload,
    SendReviewBackToReviewerPathParams
  >,
  "path" | "verb"
> &
  SendReviewBackToReviewerPathParams;

export const useSendReviewBackToReviewer = ({
  reviewId,
  ...props
}: UseSendReviewBackToReviewerProps) =>
  useMutate<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    ClinicalReviewChangeRequestPayload,
    SendReviewBackToReviewerPathParams
  >(
    "POST",
    (paramsInPath: SendReviewBackToReviewerPathParams) =>
      `/review/${paramsInPath.reviewId}/sendReviewBackToReviewer`,
    { pathParams: { reviewId }, ...props }
  );

export interface GetLatestClinicalReviewChangeRequestQueryParams {
  returnRequestTo: ReturnRequestTo;
  unresolvedOnly: boolean;
}

export interface GetLatestClinicalReviewChangeRequestPathParams {
  /**
   * A unique identifier for a ClinicalReview
   */
  reviewId: string;
}

export type GetLatestClinicalReviewChangeRequestProps = Omit<
  GetProps<
    ClinicalReviewChangeRequest,
    unknown,
    GetLatestClinicalReviewChangeRequestQueryParams,
    GetLatestClinicalReviewChangeRequestPathParams
  >,
  "path"
> &
  GetLatestClinicalReviewChangeRequestPathParams;

export const GetLatestClinicalReviewChangeRequest = ({
  reviewId,
  ...props
}: GetLatestClinicalReviewChangeRequestProps) => (
  <Get<
    ClinicalReviewChangeRequest,
    unknown,
    GetLatestClinicalReviewChangeRequestQueryParams,
    GetLatestClinicalReviewChangeRequestPathParams
  >
    path={`/clinicalReviewChangeRequest/${reviewId}/latest`}
    {...props}
  />
);

export type UseGetLatestClinicalReviewChangeRequestProps = Omit<
  UseGetProps<
    ClinicalReviewChangeRequest,
    unknown,
    GetLatestClinicalReviewChangeRequestQueryParams,
    GetLatestClinicalReviewChangeRequestPathParams
  >,
  "path"
> &
  GetLatestClinicalReviewChangeRequestPathParams;

export const useGetLatestClinicalReviewChangeRequest = ({
  reviewId,
  ...props
}: UseGetLatestClinicalReviewChangeRequestProps) =>
  useGet<
    ClinicalReviewChangeRequest,
    unknown,
    GetLatestClinicalReviewChangeRequestQueryParams,
    GetLatestClinicalReviewChangeRequestPathParams
  >(
    (paramsInPath: GetLatestClinicalReviewChangeRequestPathParams) =>
      `/clinicalReviewChangeRequest/${paramsInPath.reviewId}/latest`,
    { pathParams: { reviewId }, ...props }
  );

export interface GetClinicalReviewChangeRequestPathParams {
  /**
   * A unique identifier for a Clinical Review Change Request
   */
  clinicalReviewChangeRequestId: string;
}

export type GetClinicalReviewChangeRequestProps = Omit<
  GetProps<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    GetClinicalReviewChangeRequestPathParams
  >,
  "path"
> &
  GetClinicalReviewChangeRequestPathParams;

export const GetClinicalReviewChangeRequest = ({
  clinicalReviewChangeRequestId,
  ...props
}: GetClinicalReviewChangeRequestProps) => (
  <Get<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    GetClinicalReviewChangeRequestPathParams
  >
    path={`/clinicalReviewChangeRequest/${clinicalReviewChangeRequestId}`}
    {...props}
  />
);

export type UseGetClinicalReviewChangeRequestProps = Omit<
  UseGetProps<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    GetClinicalReviewChangeRequestPathParams
  >,
  "path"
> &
  GetClinicalReviewChangeRequestPathParams;

export const useGetClinicalReviewChangeRequest = ({
  clinicalReviewChangeRequestId,
  ...props
}: UseGetClinicalReviewChangeRequestProps) =>
  useGet<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    GetClinicalReviewChangeRequestPathParams
  >(
    (paramsInPath: GetClinicalReviewChangeRequestPathParams) =>
      `/clinicalReviewChangeRequest/${paramsInPath.clinicalReviewChangeRequestId}`,
    { pathParams: { clinicalReviewChangeRequestId }, ...props }
  );

export interface UpdateClinicalReviewChangeRequestPathParams {
  /**
   * A unique identifier for a Clinical Review Change Request
   */
  clinicalReviewChangeRequestId: string;
}

export type UpdateClinicalReviewChangeRequestProps = Omit<
  MutateProps<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    ClinicalReviewChangeRequestPatchPayload,
    UpdateClinicalReviewChangeRequestPathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalReviewChangeRequestPathParams;

export const UpdateClinicalReviewChangeRequest = ({
  clinicalReviewChangeRequestId,
  ...props
}: UpdateClinicalReviewChangeRequestProps) => (
  <Mutate<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    ClinicalReviewChangeRequestPatchPayload,
    UpdateClinicalReviewChangeRequestPathParams
  >
    verb="PATCH"
    path={`/clinicalReviewChangeRequest/${clinicalReviewChangeRequestId}`}
    {...props}
  />
);

export type UseUpdateClinicalReviewChangeRequestProps = Omit<
  UseMutateProps<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    ClinicalReviewChangeRequestPatchPayload,
    UpdateClinicalReviewChangeRequestPathParams
  >,
  "path" | "verb"
> &
  UpdateClinicalReviewChangeRequestPathParams;

export const useUpdateClinicalReviewChangeRequest = ({
  clinicalReviewChangeRequestId,
  ...props
}: UseUpdateClinicalReviewChangeRequestProps) =>
  useMutate<
    ClinicalReviewChangeRequest,
    unknown,
    void,
    ClinicalReviewChangeRequestPatchPayload,
    UpdateClinicalReviewChangeRequestPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateClinicalReviewChangeRequestPathParams) =>
      `/clinicalReviewChangeRequest/${paramsInPath.clinicalReviewChangeRequestId}`,
    { pathParams: { clinicalReviewChangeRequestId }, ...props }
  );

export interface GetLatestReviewLeadingToDenialPathParams {
  /**
   * A unique identifier for a `Review`.
   */
  id: string;
}

export type GetLatestReviewLeadingToDenialProps = Omit<
  GetProps<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    GetLatestReviewLeadingToDenialPathParams
  >,
  "path"
> &
  GetLatestReviewLeadingToDenialPathParams;

export const GetLatestReviewLeadingToDenial = ({
  id,
  ...props
}: GetLatestReviewLeadingToDenialProps) => (
  <Get<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    GetLatestReviewLeadingToDenialPathParams
  >
    path={`/review/${id}/getLatestReviewLeadingToDenial`}
    {...props}
  />
);

export type UseGetLatestReviewLeadingToDenialProps = Omit<
  UseGetProps<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    GetLatestReviewLeadingToDenialPathParams
  >,
  "path"
> &
  GetLatestReviewLeadingToDenialPathParams;

export const useGetLatestReviewLeadingToDenial = ({
  id,
  ...props
}: UseGetLatestReviewLeadingToDenialProps) =>
  useGet<
    MdReview | PeerToPeerReview | NurseReview,
    unknown,
    void,
    GetLatestReviewLeadingToDenialPathParams
  >(
    (paramsInPath: GetLatestReviewLeadingToDenialPathParams) =>
      `/review/${paramsInPath.id}/getLatestReviewLeadingToDenial`,
    { pathParams: { id }, ...props }
  );

export interface GetAllReviewsQueryParams {
  /**
   * Number of results to return
   */
  max?: number;
}

export interface GetAllReviewsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetAllReviewsProps = Omit<
  GetProps<
    ReviewType[],
    unknown,
    GetAllReviewsQueryParams,
    GetAllReviewsPathParams
  >,
  "path"
> &
  GetAllReviewsPathParams;

export const GetAllReviews = ({
  serviceRequestId,
  ...props
}: GetAllReviewsProps) => (
  <Get<ReviewType[], unknown, GetAllReviewsQueryParams, GetAllReviewsPathParams>
    path={`/serviceRequest/${serviceRequestId}/review`}
    {...props}
  />
);

export type UseGetAllReviewsProps = Omit<
  UseGetProps<
    ReviewType[],
    unknown,
    GetAllReviewsQueryParams,
    GetAllReviewsPathParams
  >,
  "path"
> &
  GetAllReviewsPathParams;

export const useGetAllReviews = ({
  serviceRequestId,
  ...props
}: UseGetAllReviewsProps) =>
  useGet<
    ReviewType[],
    unknown,
    GetAllReviewsQueryParams,
    GetAllReviewsPathParams
  >(
    (paramsInPath: GetAllReviewsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/review`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface CreateNurseReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateNurseReviewRequestBody {
  isClinicalPreprocessingReview?: boolean;
}

export type CreateNurseReviewProps = Omit<
  MutateProps<
    NurseReview,
    ErrorMessage,
    void,
    CreateNurseReviewRequestBody,
    CreateNurseReviewPathParams
  >,
  "path" | "verb"
> &
  CreateNurseReviewPathParams;

export const CreateNurseReview = ({
  serviceRequestId,
  ...props
}: CreateNurseReviewProps) => (
  <Mutate<
    NurseReview,
    ErrorMessage,
    void,
    CreateNurseReviewRequestBody,
    CreateNurseReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/nurseReview`}
    {...props}
  />
);

export type UseCreateNurseReviewProps = Omit<
  UseMutateProps<
    NurseReview,
    ErrorMessage,
    void,
    CreateNurseReviewRequestBody,
    CreateNurseReviewPathParams
  >,
  "path" | "verb"
> &
  CreateNurseReviewPathParams;

export const useCreateNurseReview = ({
  serviceRequestId,
  ...props
}: UseCreateNurseReviewProps) =>
  useMutate<
    NurseReview,
    ErrorMessage,
    void,
    CreateNurseReviewRequestBody,
    CreateNurseReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateNurseReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/nurseReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetNurseReviewsQueryParams {
  /**
   * Number of results to return
   */
  max?: number;
}

export interface GetNurseReviewsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetNurseReviewsProps = Omit<
  GetProps<
    NurseReview[],
    unknown,
    GetNurseReviewsQueryParams,
    GetNurseReviewsPathParams
  >,
  "path"
> &
  GetNurseReviewsPathParams;

export const GetNurseReviews = ({
  serviceRequestId,
  ...props
}: GetNurseReviewsProps) => (
  <Get<
    NurseReview[],
    unknown,
    GetNurseReviewsQueryParams,
    GetNurseReviewsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/nurseReview`}
    {...props}
  />
);

export type UseGetNurseReviewsProps = Omit<
  UseGetProps<
    NurseReview[],
    unknown,
    GetNurseReviewsQueryParams,
    GetNurseReviewsPathParams
  >,
  "path"
> &
  GetNurseReviewsPathParams;

export const useGetNurseReviews = ({
  serviceRequestId,
  ...props
}: UseGetNurseReviewsProps) =>
  useGet<
    NurseReview[],
    unknown,
    GetNurseReviewsQueryParams,
    GetNurseReviewsPathParams
  >(
    (paramsInPath: GetNurseReviewsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/nurseReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetNurseReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface GetNurseReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetNurseReviewProps = Omit<
  GetProps<
    NurseReview,
    unknown,
    GetNurseReviewQueryParams,
    GetNurseReviewPathParams
  >,
  "path"
> &
  GetNurseReviewPathParams;

export const GetNurseReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetNurseReviewProps) => (
  <Get<
    NurseReview,
    unknown,
    GetNurseReviewQueryParams,
    GetNurseReviewPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/nurseReview/${reviewId}`}
    {...props}
  />
);

export type UseGetNurseReviewProps = Omit<
  UseGetProps<
    NurseReview,
    unknown,
    GetNurseReviewQueryParams,
    GetNurseReviewPathParams
  >,
  "path"
> &
  GetNurseReviewPathParams;

export const useGetNurseReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetNurseReviewProps) =>
  useGet<
    NurseReview,
    unknown,
    GetNurseReviewQueryParams,
    GetNurseReviewPathParams
  >(
    (paramsInPath: GetNurseReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/nurseReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteNurseReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface DeleteNurseReviewPathParams {
  serviceRequestId: string;
}

export type DeleteNurseReviewProps = Omit<
  MutateProps<
    void,
    unknown,
    DeleteNurseReviewQueryParams,
    string,
    DeleteNurseReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteNurseReviewPathParams;

export const DeleteNurseReview = ({
  serviceRequestId,
  ...props
}: DeleteNurseReviewProps) => (
  <Mutate<
    void,
    unknown,
    DeleteNurseReviewQueryParams,
    string,
    DeleteNurseReviewPathParams
  >
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/nurseReview`}
    {...props}
  />
);

export type UseDeleteNurseReviewProps = Omit<
  UseMutateProps<
    void,
    unknown,
    DeleteNurseReviewQueryParams,
    string,
    DeleteNurseReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteNurseReviewPathParams;

export const useDeleteNurseReview = ({
  serviceRequestId,
  ...props
}: UseDeleteNurseReviewProps) =>
  useMutate<
    void,
    unknown,
    DeleteNurseReviewQueryParams,
    string,
    DeleteNurseReviewPathParams
  >(
    "DELETE",
    (paramsInPath: DeleteNurseReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/nurseReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateNurseReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface UpdateNurseReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateNurseReviewProps = Omit<
  MutateProps<
    NurseReview,
    unknown,
    UpdateNurseReviewQueryParams,
    NurseReview,
    UpdateNurseReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateNurseReviewPathParams;

export const UpdateNurseReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateNurseReviewProps) => (
  <Mutate<
    NurseReview,
    unknown,
    UpdateNurseReviewQueryParams,
    NurseReview,
    UpdateNurseReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/nurseReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateNurseReviewProps = Omit<
  UseMutateProps<
    NurseReview,
    unknown,
    UpdateNurseReviewQueryParams,
    NurseReview,
    UpdateNurseReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateNurseReviewPathParams;

export const useUpdateNurseReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateNurseReviewProps) =>
  useMutate<
    NurseReview,
    unknown,
    UpdateNurseReviewQueryParams,
    NurseReview,
    UpdateNurseReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateNurseReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/nurseReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitNurseReviewQueryParams {
  isUserAudited?: boolean;
  tatExtension?: boolean;
}

export interface SubmitNurseReviewPathParams {
  reviewId: string;
}

export type SubmitNurseReviewProps = Omit<
  MutateProps<
    NurseReview,
    ErrorMessage,
    SubmitNurseReviewQueryParams,
    NurseReviewSubmitPayload,
    SubmitNurseReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitNurseReviewPathParams;

export const SubmitNurseReview = ({
  reviewId,
  ...props
}: SubmitNurseReviewProps) => (
  <Mutate<
    NurseReview,
    ErrorMessage,
    SubmitNurseReviewQueryParams,
    NurseReviewSubmitPayload,
    SubmitNurseReviewPathParams
  >
    verb="POST"
    path={`/nurseReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitNurseReviewProps = Omit<
  UseMutateProps<
    NurseReview,
    ErrorMessage,
    SubmitNurseReviewQueryParams,
    NurseReviewSubmitPayload,
    SubmitNurseReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitNurseReviewPathParams;

export const useSubmitNurseReview = ({
  reviewId,
  ...props
}: UseSubmitNurseReviewProps) =>
  useMutate<
    NurseReview,
    ErrorMessage,
    SubmitNurseReviewQueryParams,
    NurseReviewSubmitPayload,
    SubmitNurseReviewPathParams
  >(
    "POST",
    (paramsInPath: SubmitNurseReviewPathParams) =>
      `/nurseReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardNurseReviewPathParams {
  reviewId: string;
}

export type DiscardNurseReviewProps = Omit<
  MutateProps<
    NurseReview,
    unknown,
    void,
    NurseReview,
    DiscardNurseReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardNurseReviewPathParams;

export const DiscardNurseReview = ({
  reviewId,
  ...props
}: DiscardNurseReviewProps) => (
  <Mutate<NurseReview, unknown, void, NurseReview, DiscardNurseReviewPathParams>
    verb="POST"
    path={`/nurseReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardNurseReviewProps = Omit<
  UseMutateProps<
    NurseReview,
    unknown,
    void,
    NurseReview,
    DiscardNurseReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardNurseReviewPathParams;

export const useDiscardNurseReview = ({
  reviewId,
  ...props
}: UseDiscardNurseReviewProps) =>
  useMutate<
    NurseReview,
    unknown,
    void,
    NurseReview,
    DiscardNurseReviewPathParams
  >(
    "POST",
    (paramsInPath: DiscardNurseReviewPathParams) =>
      `/nurseReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreateOutOfNetworkReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateOutOfNetworkReviewRequestBody {}

export type CreateOutOfNetworkReviewProps = Omit<
  MutateProps<
    OutOfNetworkReview,
    ErrorMessage,
    void,
    CreateOutOfNetworkReviewRequestBody,
    CreateOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  CreateOutOfNetworkReviewPathParams;

export const CreateOutOfNetworkReview = ({
  serviceRequestId,
  ...props
}: CreateOutOfNetworkReviewProps) => (
  <Mutate<
    OutOfNetworkReview,
    ErrorMessage,
    void,
    CreateOutOfNetworkReviewRequestBody,
    CreateOutOfNetworkReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/outOfNetworkReview`}
    {...props}
  />
);

export type UseCreateOutOfNetworkReviewProps = Omit<
  UseMutateProps<
    OutOfNetworkReview,
    ErrorMessage,
    void,
    CreateOutOfNetworkReviewRequestBody,
    CreateOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  CreateOutOfNetworkReviewPathParams;

export const useCreateOutOfNetworkReview = ({
  serviceRequestId,
  ...props
}: UseCreateOutOfNetworkReviewProps) =>
  useMutate<
    OutOfNetworkReview,
    ErrorMessage,
    void,
    CreateOutOfNetworkReviewRequestBody,
    CreateOutOfNetworkReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateOutOfNetworkReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfNetworkReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetOutOfNetworkReviewsQueryParams {
  /**
   * Number of results to return
   */
  max?: number;
}

export interface GetOutOfNetworkReviewsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetOutOfNetworkReviewsProps = Omit<
  GetProps<
    OutOfNetworkReview[],
    unknown,
    GetOutOfNetworkReviewsQueryParams,
    GetOutOfNetworkReviewsPathParams
  >,
  "path"
> &
  GetOutOfNetworkReviewsPathParams;

export const GetOutOfNetworkReviews = ({
  serviceRequestId,
  ...props
}: GetOutOfNetworkReviewsProps) => (
  <Get<
    OutOfNetworkReview[],
    unknown,
    GetOutOfNetworkReviewsQueryParams,
    GetOutOfNetworkReviewsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/outOfNetworkReview`}
    {...props}
  />
);

export type UseGetOutOfNetworkReviewsProps = Omit<
  UseGetProps<
    OutOfNetworkReview[],
    unknown,
    GetOutOfNetworkReviewsQueryParams,
    GetOutOfNetworkReviewsPathParams
  >,
  "path"
> &
  GetOutOfNetworkReviewsPathParams;

export const useGetOutOfNetworkReviews = ({
  serviceRequestId,
  ...props
}: UseGetOutOfNetworkReviewsProps) =>
  useGet<
    OutOfNetworkReview[],
    unknown,
    GetOutOfNetworkReviewsQueryParams,
    GetOutOfNetworkReviewsPathParams
  >(
    (paramsInPath: GetOutOfNetworkReviewsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfNetworkReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetOutOfNetworkReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetOutOfNetworkReviewProps = Omit<
  GetProps<OutOfNetworkReview, unknown, void, GetOutOfNetworkReviewPathParams>,
  "path"
> &
  GetOutOfNetworkReviewPathParams;

export const GetOutOfNetworkReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetOutOfNetworkReviewProps) => (
  <Get<OutOfNetworkReview, unknown, void, GetOutOfNetworkReviewPathParams>
    path={`/serviceRequest/${serviceRequestId}/outOfNetworkReview/${reviewId}`}
    {...props}
  />
);

export type UseGetOutOfNetworkReviewProps = Omit<
  UseGetProps<
    OutOfNetworkReview,
    unknown,
    void,
    GetOutOfNetworkReviewPathParams
  >,
  "path"
> &
  GetOutOfNetworkReviewPathParams;

export const useGetOutOfNetworkReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetOutOfNetworkReviewProps) =>
  useGet<OutOfNetworkReview, unknown, void, GetOutOfNetworkReviewPathParams>(
    (paramsInPath: GetOutOfNetworkReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfNetworkReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteOutOfNetworkReviewPathParams {
  serviceRequestId: string;
}

export type DeleteOutOfNetworkReviewProps = Omit<
  MutateProps<void, unknown, void, string, DeleteOutOfNetworkReviewPathParams>,
  "path" | "verb"
> &
  DeleteOutOfNetworkReviewPathParams;

export const DeleteOutOfNetworkReview = ({
  serviceRequestId,
  ...props
}: DeleteOutOfNetworkReviewProps) => (
  <Mutate<void, unknown, void, string, DeleteOutOfNetworkReviewPathParams>
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/outOfNetworkReview`}
    {...props}
  />
);

export type UseDeleteOutOfNetworkReviewProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteOutOfNetworkReviewPathParams;

export const useDeleteOutOfNetworkReview = ({
  serviceRequestId,
  ...props
}: UseDeleteOutOfNetworkReviewProps) =>
  useMutate<void, unknown, void, string, DeleteOutOfNetworkReviewPathParams>(
    "DELETE",
    (paramsInPath: DeleteOutOfNetworkReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfNetworkReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateOutOfNetworkReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateOutOfNetworkReviewProps = Omit<
  MutateProps<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    UpdateOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateOutOfNetworkReviewPathParams;

export const UpdateOutOfNetworkReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateOutOfNetworkReviewProps) => (
  <Mutate<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    UpdateOutOfNetworkReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/outOfNetworkReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateOutOfNetworkReviewProps = Omit<
  UseMutateProps<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    UpdateOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateOutOfNetworkReviewPathParams;

export const useUpdateOutOfNetworkReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateOutOfNetworkReviewProps) =>
  useMutate<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    UpdateOutOfNetworkReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateOutOfNetworkReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfNetworkReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitOutOfNetworkReviewQueryParams {
  isUserAudited?: boolean;
}

export interface SubmitOutOfNetworkReviewPathParams {
  reviewId: string;
}

export type SubmitOutOfNetworkReviewProps = Omit<
  MutateProps<
    OutOfNetworkReview,
    ErrorMessage,
    SubmitOutOfNetworkReviewQueryParams,
    OutOfNetworkReviewSubmitPayload,
    SubmitOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitOutOfNetworkReviewPathParams;

export const SubmitOutOfNetworkReview = ({
  reviewId,
  ...props
}: SubmitOutOfNetworkReviewProps) => (
  <Mutate<
    OutOfNetworkReview,
    ErrorMessage,
    SubmitOutOfNetworkReviewQueryParams,
    OutOfNetworkReviewSubmitPayload,
    SubmitOutOfNetworkReviewPathParams
  >
    verb="POST"
    path={`/outOfNetworkReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitOutOfNetworkReviewProps = Omit<
  UseMutateProps<
    OutOfNetworkReview,
    ErrorMessage,
    SubmitOutOfNetworkReviewQueryParams,
    OutOfNetworkReviewSubmitPayload,
    SubmitOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitOutOfNetworkReviewPathParams;

export const useSubmitOutOfNetworkReview = ({
  reviewId,
  ...props
}: UseSubmitOutOfNetworkReviewProps) =>
  useMutate<
    OutOfNetworkReview,
    ErrorMessage,
    SubmitOutOfNetworkReviewQueryParams,
    OutOfNetworkReviewSubmitPayload,
    SubmitOutOfNetworkReviewPathParams
  >(
    "POST",
    (paramsInPath: SubmitOutOfNetworkReviewPathParams) =>
      `/outOfNetworkReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardOutOfNetworkReviewPathParams {
  reviewId: string;
}

export type DiscardOutOfNetworkReviewProps = Omit<
  MutateProps<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    DiscardOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardOutOfNetworkReviewPathParams;

export const DiscardOutOfNetworkReview = ({
  reviewId,
  ...props
}: DiscardOutOfNetworkReviewProps) => (
  <Mutate<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    DiscardOutOfNetworkReviewPathParams
  >
    verb="POST"
    path={`/outOfNetworkReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardOutOfNetworkReviewProps = Omit<
  UseMutateProps<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    DiscardOutOfNetworkReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardOutOfNetworkReviewPathParams;

export const useDiscardOutOfNetworkReview = ({
  reviewId,
  ...props
}: UseDiscardOutOfNetworkReviewProps) =>
  useMutate<
    OutOfNetworkReview,
    unknown,
    void,
    OutOfNetworkReview,
    DiscardOutOfNetworkReviewPathParams
  >(
    "POST",
    (paramsInPath: DiscardOutOfNetworkReviewPathParams) =>
      `/outOfNetworkReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardP2PReviewPathParams {
  reviewId: string;
}

export type DiscardP2PReviewProps = Omit<
  MutateProps<
    PeerToPeerReview,
    unknown,
    void,
    PeerToPeerReview,
    DiscardP2PReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardP2PReviewPathParams;

export const DiscardP2PReview = ({
  reviewId,
  ...props
}: DiscardP2PReviewProps) => (
  <Mutate<
    PeerToPeerReview,
    unknown,
    void,
    PeerToPeerReview,
    DiscardP2PReviewPathParams
  >
    verb="PATCH"
    path={`/peerToPeerReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardP2PReviewProps = Omit<
  UseMutateProps<
    PeerToPeerReview,
    unknown,
    void,
    PeerToPeerReview,
    DiscardP2PReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardP2PReviewPathParams;

export const useDiscardP2PReview = ({
  reviewId,
  ...props
}: UseDiscardP2PReviewProps) =>
  useMutate<
    PeerToPeerReview,
    unknown,
    void,
    PeerToPeerReview,
    DiscardP2PReviewPathParams
  >(
    "PATCH",
    (paramsInPath: DiscardP2PReviewPathParams) =>
      `/peerToPeerReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreateAuditReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateAuditReviewRequestBody {}

export type CreateAuditReviewProps = Omit<
  MutateProps<
    AuditReview,
    ErrorMessage,
    void,
    CreateAuditReviewRequestBody,
    CreateAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateAuditReviewPathParams;

export const CreateAuditReview = ({
  serviceRequestId,
  ...props
}: CreateAuditReviewProps) => (
  <Mutate<
    AuditReview,
    ErrorMessage,
    void,
    CreateAuditReviewRequestBody,
    CreateAuditReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/auditReview`}
    {...props}
  />
);

export type UseCreateAuditReviewProps = Omit<
  UseMutateProps<
    AuditReview,
    ErrorMessage,
    void,
    CreateAuditReviewRequestBody,
    CreateAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateAuditReviewPathParams;

export const useCreateAuditReview = ({
  serviceRequestId,
  ...props
}: UseCreateAuditReviewProps) =>
  useMutate<
    AuditReview,
    ErrorMessage,
    void,
    CreateAuditReviewRequestBody,
    CreateAuditReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/auditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetAuditReviewsQueryParams {
  /**
   * Number of results to return
   */
  max?: number;
}

export interface GetAuditReviewsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetAuditReviewsProps = Omit<
  GetProps<
    AuditReview[],
    unknown,
    GetAuditReviewsQueryParams,
    GetAuditReviewsPathParams
  >,
  "path"
> &
  GetAuditReviewsPathParams;

export const GetAuditReviews = ({
  serviceRequestId,
  ...props
}: GetAuditReviewsProps) => (
  <Get<
    AuditReview[],
    unknown,
    GetAuditReviewsQueryParams,
    GetAuditReviewsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/auditReview`}
    {...props}
  />
);

export type UseGetAuditReviewsProps = Omit<
  UseGetProps<
    AuditReview[],
    unknown,
    GetAuditReviewsQueryParams,
    GetAuditReviewsPathParams
  >,
  "path"
> &
  GetAuditReviewsPathParams;

export const useGetAuditReviews = ({
  serviceRequestId,
  ...props
}: UseGetAuditReviewsProps) =>
  useGet<
    AuditReview[],
    unknown,
    GetAuditReviewsQueryParams,
    GetAuditReviewsPathParams
  >(
    (paramsInPath: GetAuditReviewsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/auditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetAuditReviewProps = Omit<
  GetProps<AuditReview, unknown, void, GetAuditReviewPathParams>,
  "path"
> &
  GetAuditReviewPathParams;

export const GetAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetAuditReviewProps) => (
  <Get<AuditReview, unknown, void, GetAuditReviewPathParams>
    path={`/serviceRequest/${serviceRequestId}/auditReview/${reviewId}`}
    {...props}
  />
);

export type UseGetAuditReviewProps = Omit<
  UseGetProps<AuditReview, unknown, void, GetAuditReviewPathParams>,
  "path"
> &
  GetAuditReviewPathParams;

export const useGetAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetAuditReviewProps) =>
  useGet<AuditReview, unknown, void, GetAuditReviewPathParams>(
    (paramsInPath: GetAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/auditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteAuditReviewPathParams {
  serviceRequestId: string;
}

export type DeleteAuditReviewProps = Omit<
  MutateProps<void, unknown, void, string, DeleteAuditReviewPathParams>,
  "path" | "verb"
> &
  DeleteAuditReviewPathParams;

export const DeleteAuditReview = ({
  serviceRequestId,
  ...props
}: DeleteAuditReviewProps) => (
  <Mutate<void, unknown, void, string, DeleteAuditReviewPathParams>
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/auditReview`}
    {...props}
  />
);

export type UseDeleteAuditReviewProps = Omit<
  UseMutateProps<void, unknown, void, string, DeleteAuditReviewPathParams>,
  "path" | "verb"
> &
  DeleteAuditReviewPathParams;

export const useDeleteAuditReview = ({
  serviceRequestId,
  ...props
}: UseDeleteAuditReviewProps) =>
  useMutate<void, unknown, void, string, DeleteAuditReviewPathParams>(
    "DELETE",
    (paramsInPath: DeleteAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/auditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateAuditReviewProps = Omit<
  MutateProps<
    AuditReview,
    unknown,
    void,
    AuditReview,
    UpdateAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateAuditReviewPathParams;

export const UpdateAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateAuditReviewProps) => (
  <Mutate<AuditReview, unknown, void, AuditReview, UpdateAuditReviewPathParams>
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/auditReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateAuditReviewProps = Omit<
  UseMutateProps<
    AuditReview,
    unknown,
    void,
    AuditReview,
    UpdateAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateAuditReviewPathParams;

export const useUpdateAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateAuditReviewProps) =>
  useMutate<
    AuditReview,
    unknown,
    void,
    AuditReview,
    UpdateAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/auditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitAuditReviewPathParams {
  reviewId: string;
}

export type SubmitAuditReviewProps = Omit<
  MutateProps<
    AuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    AuditReviewSubmitPayload,
    SubmitAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitAuditReviewPathParams;

export const SubmitAuditReview = ({
  reviewId,
  ...props
}: SubmitAuditReviewProps) => (
  <Mutate<
    AuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    AuditReviewSubmitPayload,
    SubmitAuditReviewPathParams
  >
    verb="POST"
    path={`/auditReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitAuditReviewProps = Omit<
  UseMutateProps<
    AuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    AuditReviewSubmitPayload,
    SubmitAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitAuditReviewPathParams;

export const useSubmitAuditReview = ({
  reviewId,
  ...props
}: UseSubmitAuditReviewProps) =>
  useMutate<
    AuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    AuditReviewSubmitPayload,
    SubmitAuditReviewPathParams
  >(
    "POST",
    (paramsInPath: SubmitAuditReviewPathParams) =>
      `/auditReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardAuditReviewPathParams {
  reviewId: string;
}

export type DiscardAuditReviewProps = Omit<
  MutateProps<
    AuditReview,
    unknown,
    void,
    AuditReview,
    DiscardAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardAuditReviewPathParams;

export const DiscardAuditReview = ({
  reviewId,
  ...props
}: DiscardAuditReviewProps) => (
  <Mutate<AuditReview, unknown, void, AuditReview, DiscardAuditReviewPathParams>
    verb="PATCH"
    path={`/auditReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardAuditReviewProps = Omit<
  UseMutateProps<
    AuditReview,
    unknown,
    void,
    AuditReview,
    DiscardAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardAuditReviewPathParams;

export const useDiscardAuditReview = ({
  reviewId,
  ...props
}: UseDiscardAuditReviewProps) =>
  useMutate<
    AuditReview,
    unknown,
    void,
    AuditReview,
    DiscardAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: DiscardAuditReviewPathParams) =>
      `/auditReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreateNewHireAuditReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateNewHireAuditReviewRequestBody {}

export type CreateNewHireAuditReviewProps = Omit<
  MutateProps<
    NewHireAuditReview,
    ErrorMessage,
    void,
    CreateNewHireAuditReviewRequestBody,
    CreateNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateNewHireAuditReviewPathParams;

export const CreateNewHireAuditReview = ({
  serviceRequestId,
  ...props
}: CreateNewHireAuditReviewProps) => (
  <Mutate<
    NewHireAuditReview,
    ErrorMessage,
    void,
    CreateNewHireAuditReviewRequestBody,
    CreateNewHireAuditReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/newHireAuditReview`}
    {...props}
  />
);

export type UseCreateNewHireAuditReviewProps = Omit<
  UseMutateProps<
    NewHireAuditReview,
    ErrorMessage,
    void,
    CreateNewHireAuditReviewRequestBody,
    CreateNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateNewHireAuditReviewPathParams;

export const useCreateNewHireAuditReview = ({
  serviceRequestId,
  ...props
}: UseCreateNewHireAuditReviewProps) =>
  useMutate<
    NewHireAuditReview,
    ErrorMessage,
    void,
    CreateNewHireAuditReviewRequestBody,
    CreateNewHireAuditReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateNewHireAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/newHireAuditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetNewHireAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetNewHireAuditReviewProps = Omit<
  GetProps<NewHireAuditReview, unknown, void, GetNewHireAuditReviewPathParams>,
  "path"
> &
  GetNewHireAuditReviewPathParams;

export const GetNewHireAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetNewHireAuditReviewProps) => (
  <Get<NewHireAuditReview, unknown, void, GetNewHireAuditReviewPathParams>
    path={`/serviceRequest/${serviceRequestId}/newHireAuditReview/${reviewId}`}
    {...props}
  />
);

export type UseGetNewHireAuditReviewProps = Omit<
  UseGetProps<
    NewHireAuditReview,
    unknown,
    void,
    GetNewHireAuditReviewPathParams
  >,
  "path"
> &
  GetNewHireAuditReviewPathParams;

export const useGetNewHireAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetNewHireAuditReviewProps) =>
  useGet<NewHireAuditReview, unknown, void, GetNewHireAuditReviewPathParams>(
    (paramsInPath: GetNewHireAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/newHireAuditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteNewHireAuditReviewPathParams {
  serviceRequestId: string;
}

export type DeleteNewHireAuditReviewProps = Omit<
  MutateProps<void, unknown, void, string, DeleteNewHireAuditReviewPathParams>,
  "path" | "verb"
> &
  DeleteNewHireAuditReviewPathParams;

export const DeleteNewHireAuditReview = ({
  serviceRequestId,
  ...props
}: DeleteNewHireAuditReviewProps) => (
  <Mutate<void, unknown, void, string, DeleteNewHireAuditReviewPathParams>
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/newHireAuditReview`}
    {...props}
  />
);

export type UseDeleteNewHireAuditReviewProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteNewHireAuditReviewPathParams;

export const useDeleteNewHireAuditReview = ({
  serviceRequestId,
  ...props
}: UseDeleteNewHireAuditReviewProps) =>
  useMutate<void, unknown, void, string, DeleteNewHireAuditReviewPathParams>(
    "DELETE",
    (paramsInPath: DeleteNewHireAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/newHireAuditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateNewHireAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateNewHireAuditReviewProps = Omit<
  MutateProps<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    UpdateNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateNewHireAuditReviewPathParams;

export const UpdateNewHireAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateNewHireAuditReviewProps) => (
  <Mutate<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    UpdateNewHireAuditReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/newHireAuditReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateNewHireAuditReviewProps = Omit<
  UseMutateProps<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    UpdateNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateNewHireAuditReviewPathParams;

export const useUpdateNewHireAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateNewHireAuditReviewProps) =>
  useMutate<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    UpdateNewHireAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateNewHireAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/newHireAuditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitNewHireAuditReviewPathParams {
  reviewId: string;
}

export type SubmitNewHireAuditReviewProps = Omit<
  MutateProps<
    NewHireAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    NewHireAuditReviewSubmitPayload,
    SubmitNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitNewHireAuditReviewPathParams;

export const SubmitNewHireAuditReview = ({
  reviewId,
  ...props
}: SubmitNewHireAuditReviewProps) => (
  <Mutate<
    NewHireAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    NewHireAuditReviewSubmitPayload,
    SubmitNewHireAuditReviewPathParams
  >
    verb="POST"
    path={`/newHireAuditReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitNewHireAuditReviewProps = Omit<
  UseMutateProps<
    NewHireAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    NewHireAuditReviewSubmitPayload,
    SubmitNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitNewHireAuditReviewPathParams;

export const useSubmitNewHireAuditReview = ({
  reviewId,
  ...props
}: UseSubmitNewHireAuditReviewProps) =>
  useMutate<
    NewHireAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    NewHireAuditReviewSubmitPayload,
    SubmitNewHireAuditReviewPathParams
  >(
    "POST",
    (paramsInPath: SubmitNewHireAuditReviewPathParams) =>
      `/newHireAuditReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardNewHireAuditReviewPathParams {
  reviewId: string;
}

export type DiscardNewHireAuditReviewProps = Omit<
  MutateProps<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    DiscardNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardNewHireAuditReviewPathParams;

export const DiscardNewHireAuditReview = ({
  reviewId,
  ...props
}: DiscardNewHireAuditReviewProps) => (
  <Mutate<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    DiscardNewHireAuditReviewPathParams
  >
    verb="PATCH"
    path={`/newHireAuditReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardNewHireAuditReviewProps = Omit<
  UseMutateProps<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    DiscardNewHireAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardNewHireAuditReviewPathParams;

export const useDiscardNewHireAuditReview = ({
  reviewId,
  ...props
}: UseDiscardNewHireAuditReviewProps) =>
  useMutate<
    NewHireAuditReview,
    unknown,
    void,
    NewHireAuditReview,
    DiscardNewHireAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: DiscardNewHireAuditReviewPathParams) =>
      `/newHireAuditReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreateOutOfScopeAuditReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateOutOfScopeAuditReviewRequestBody {}

export type CreateOutOfScopeAuditReviewProps = Omit<
  MutateProps<
    OutOfScopeAuditReview,
    ErrorMessage,
    void,
    CreateOutOfScopeAuditReviewRequestBody,
    CreateOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateOutOfScopeAuditReviewPathParams;

export const CreateOutOfScopeAuditReview = ({
  serviceRequestId,
  ...props
}: CreateOutOfScopeAuditReviewProps) => (
  <Mutate<
    OutOfScopeAuditReview,
    ErrorMessage,
    void,
    CreateOutOfScopeAuditReviewRequestBody,
    CreateOutOfScopeAuditReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/outOfScopeAuditReview`}
    {...props}
  />
);

export type UseCreateOutOfScopeAuditReviewProps = Omit<
  UseMutateProps<
    OutOfScopeAuditReview,
    ErrorMessage,
    void,
    CreateOutOfScopeAuditReviewRequestBody,
    CreateOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateOutOfScopeAuditReviewPathParams;

export const useCreateOutOfScopeAuditReview = ({
  serviceRequestId,
  ...props
}: UseCreateOutOfScopeAuditReviewProps) =>
  useMutate<
    OutOfScopeAuditReview,
    ErrorMessage,
    void,
    CreateOutOfScopeAuditReviewRequestBody,
    CreateOutOfScopeAuditReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateOutOfScopeAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfScopeAuditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetOutOfScopeAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetOutOfScopeAuditReviewProps = Omit<
  GetProps<
    OutOfScopeAuditReview,
    unknown,
    void,
    GetOutOfScopeAuditReviewPathParams
  >,
  "path"
> &
  GetOutOfScopeAuditReviewPathParams;

export const GetOutOfScopeAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetOutOfScopeAuditReviewProps) => (
  <Get<OutOfScopeAuditReview, unknown, void, GetOutOfScopeAuditReviewPathParams>
    path={`/serviceRequest/${serviceRequestId}/outOfScopeAuditReview/${reviewId}`}
    {...props}
  />
);

export type UseGetOutOfScopeAuditReviewProps = Omit<
  UseGetProps<
    OutOfScopeAuditReview,
    unknown,
    void,
    GetOutOfScopeAuditReviewPathParams
  >,
  "path"
> &
  GetOutOfScopeAuditReviewPathParams;

export const useGetOutOfScopeAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetOutOfScopeAuditReviewProps) =>
  useGet<
    OutOfScopeAuditReview,
    unknown,
    void,
    GetOutOfScopeAuditReviewPathParams
  >(
    (paramsInPath: GetOutOfScopeAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfScopeAuditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteOutOfScopeAuditReviewPathParams {
  serviceRequestId: string;
}

export type DeleteOutOfScopeAuditReviewProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    string,
    DeleteOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteOutOfScopeAuditReviewPathParams;

export const DeleteOutOfScopeAuditReview = ({
  serviceRequestId,
  ...props
}: DeleteOutOfScopeAuditReviewProps) => (
  <Mutate<void, unknown, void, string, DeleteOutOfScopeAuditReviewPathParams>
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/outOfScopeAuditReview`}
    {...props}
  />
);

export type UseDeleteOutOfScopeAuditReviewProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteOutOfScopeAuditReviewPathParams;

export const useDeleteOutOfScopeAuditReview = ({
  serviceRequestId,
  ...props
}: UseDeleteOutOfScopeAuditReviewProps) =>
  useMutate<void, unknown, void, string, DeleteOutOfScopeAuditReviewPathParams>(
    "DELETE",
    (paramsInPath: DeleteOutOfScopeAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfScopeAuditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateOutOfScopeAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateOutOfScopeAuditReviewProps = Omit<
  MutateProps<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    UpdateOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateOutOfScopeAuditReviewPathParams;

export const UpdateOutOfScopeAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateOutOfScopeAuditReviewProps) => (
  <Mutate<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    UpdateOutOfScopeAuditReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/outOfScopeAuditReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateOutOfScopeAuditReviewProps = Omit<
  UseMutateProps<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    UpdateOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateOutOfScopeAuditReviewPathParams;

export const useUpdateOutOfScopeAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateOutOfScopeAuditReviewProps) =>
  useMutate<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    UpdateOutOfScopeAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateOutOfScopeAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/outOfScopeAuditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitOutOfScopeAuditReviewPathParams {
  reviewId: string;
}

export type SubmitOutOfScopeAuditReviewProps = Omit<
  MutateProps<
    OutOfScopeAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    OutOfScopeAuditReviewSubmitPayload,
    SubmitOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitOutOfScopeAuditReviewPathParams;

export const SubmitOutOfScopeAuditReview = ({
  reviewId,
  ...props
}: SubmitOutOfScopeAuditReviewProps) => (
  <Mutate<
    OutOfScopeAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    OutOfScopeAuditReviewSubmitPayload,
    SubmitOutOfScopeAuditReviewPathParams
  >
    verb="PATCH"
    path={`/outOfScopeAuditReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitOutOfScopeAuditReviewProps = Omit<
  UseMutateProps<
    OutOfScopeAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    OutOfScopeAuditReviewSubmitPayload,
    SubmitOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitOutOfScopeAuditReviewPathParams;

export const useSubmitOutOfScopeAuditReview = ({
  reviewId,
  ...props
}: UseSubmitOutOfScopeAuditReviewProps) =>
  useMutate<
    OutOfScopeAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    OutOfScopeAuditReviewSubmitPayload,
    SubmitOutOfScopeAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: SubmitOutOfScopeAuditReviewPathParams) =>
      `/outOfScopeAuditReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardOutOfScopeAuditReviewPathParams {
  reviewId: string;
}

export type DiscardOutOfScopeAuditReviewProps = Omit<
  MutateProps<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    DiscardOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardOutOfScopeAuditReviewPathParams;

export const DiscardOutOfScopeAuditReview = ({
  reviewId,
  ...props
}: DiscardOutOfScopeAuditReviewProps) => (
  <Mutate<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    DiscardOutOfScopeAuditReviewPathParams
  >
    verb="PATCH"
    path={`/outOfScopeAuditReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardOutOfScopeAuditReviewProps = Omit<
  UseMutateProps<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    DiscardOutOfScopeAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardOutOfScopeAuditReviewPathParams;

export const useDiscardOutOfScopeAuditReview = ({
  reviewId,
  ...props
}: UseDiscardOutOfScopeAuditReviewProps) =>
  useMutate<
    OutOfScopeAuditReview,
    unknown,
    void,
    OutOfScopeAuditReview,
    DiscardOutOfScopeAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: DiscardOutOfScopeAuditReviewPathParams) =>
      `/outOfScopeAuditReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreateFaxAuditReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateFaxAuditReviewRequestBody {}

export type CreateFaxAuditReviewProps = Omit<
  MutateProps<
    FaxAuditReview,
    ErrorMessage,
    void,
    CreateFaxAuditReviewRequestBody,
    CreateFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateFaxAuditReviewPathParams;

export const CreateFaxAuditReview = ({
  serviceRequestId,
  ...props
}: CreateFaxAuditReviewProps) => (
  <Mutate<
    FaxAuditReview,
    ErrorMessage,
    void,
    CreateFaxAuditReviewRequestBody,
    CreateFaxAuditReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/faxAuditReview`}
    {...props}
  />
);

export type UseCreateFaxAuditReviewProps = Omit<
  UseMutateProps<
    FaxAuditReview,
    ErrorMessage,
    void,
    CreateFaxAuditReviewRequestBody,
    CreateFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  CreateFaxAuditReviewPathParams;

export const useCreateFaxAuditReview = ({
  serviceRequestId,
  ...props
}: UseCreateFaxAuditReviewProps) =>
  useMutate<
    FaxAuditReview,
    ErrorMessage,
    void,
    CreateFaxAuditReviewRequestBody,
    CreateFaxAuditReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateFaxAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/faxAuditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetFaxAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetFaxAuditReviewProps = Omit<
  GetProps<FaxAuditReview, unknown, void, GetFaxAuditReviewPathParams>,
  "path"
> &
  GetFaxAuditReviewPathParams;

export const GetFaxAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetFaxAuditReviewProps) => (
  <Get<FaxAuditReview, unknown, void, GetFaxAuditReviewPathParams>
    path={`/serviceRequest/${serviceRequestId}/faxAuditReview/${reviewId}`}
    {...props}
  />
);

export type UseGetFaxAuditReviewProps = Omit<
  UseGetProps<FaxAuditReview, unknown, void, GetFaxAuditReviewPathParams>,
  "path"
> &
  GetFaxAuditReviewPathParams;

export const useGetFaxAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetFaxAuditReviewProps) =>
  useGet<FaxAuditReview, unknown, void, GetFaxAuditReviewPathParams>(
    (paramsInPath: GetFaxAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/faxAuditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteFaxAuditReviewPathParams {
  serviceRequestId: string;
}

export type DeleteFaxAuditReviewProps = Omit<
  MutateProps<void, unknown, void, string, DeleteFaxAuditReviewPathParams>,
  "path" | "verb"
> &
  DeleteFaxAuditReviewPathParams;

export const DeleteFaxAuditReview = ({
  serviceRequestId,
  ...props
}: DeleteFaxAuditReviewProps) => (
  <Mutate<void, unknown, void, string, DeleteFaxAuditReviewPathParams>
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/faxAuditReview`}
    {...props}
  />
);

export type UseDeleteFaxAuditReviewProps = Omit<
  UseMutateProps<void, unknown, void, string, DeleteFaxAuditReviewPathParams>,
  "path" | "verb"
> &
  DeleteFaxAuditReviewPathParams;

export const useDeleteFaxAuditReview = ({
  serviceRequestId,
  ...props
}: UseDeleteFaxAuditReviewProps) =>
  useMutate<void, unknown, void, string, DeleteFaxAuditReviewPathParams>(
    "DELETE",
    (paramsInPath: DeleteFaxAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/faxAuditReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateFaxAuditReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateFaxAuditReviewProps = Omit<
  MutateProps<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    UpdateFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateFaxAuditReviewPathParams;

export const UpdateFaxAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateFaxAuditReviewProps) => (
  <Mutate<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    UpdateFaxAuditReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/faxAuditReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateFaxAuditReviewProps = Omit<
  UseMutateProps<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    UpdateFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateFaxAuditReviewPathParams;

export const useUpdateFaxAuditReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateFaxAuditReviewProps) =>
  useMutate<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    UpdateFaxAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateFaxAuditReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/faxAuditReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitFaxAuditReviewPathParams {
  reviewId: string;
}

export type SubmitFaxAuditReviewProps = Omit<
  MutateProps<
    FaxAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    FaxAuditReviewSubmitPayload,
    SubmitFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitFaxAuditReviewPathParams;

export const SubmitFaxAuditReview = ({
  reviewId,
  ...props
}: SubmitFaxAuditReviewProps) => (
  <Mutate<
    FaxAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    FaxAuditReviewSubmitPayload,
    SubmitFaxAuditReviewPathParams
  >
    verb="PATCH"
    path={`/faxAuditReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitFaxAuditReviewProps = Omit<
  UseMutateProps<
    FaxAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    FaxAuditReviewSubmitPayload,
    SubmitFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitFaxAuditReviewPathParams;

export const useSubmitFaxAuditReview = ({
  reviewId,
  ...props
}: UseSubmitFaxAuditReviewProps) =>
  useMutate<
    FaxAuditReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    FaxAuditReviewSubmitPayload,
    SubmitFaxAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: SubmitFaxAuditReviewPathParams) =>
      `/faxAuditReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardFaxAuditReviewPathParams {
  reviewId: string;
}

export type DiscardFaxAuditReviewProps = Omit<
  MutateProps<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    DiscardFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardFaxAuditReviewPathParams;

export const DiscardFaxAuditReview = ({
  reviewId,
  ...props
}: DiscardFaxAuditReviewProps) => (
  <Mutate<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    DiscardFaxAuditReviewPathParams
  >
    verb="PATCH"
    path={`/faxAuditReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardFaxAuditReviewProps = Omit<
  UseMutateProps<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    DiscardFaxAuditReviewPathParams
  >,
  "path" | "verb"
> &
  DiscardFaxAuditReviewPathParams;

export const useDiscardFaxAuditReview = ({
  reviewId,
  ...props
}: UseDiscardFaxAuditReviewProps) =>
  useMutate<
    FaxAuditReview,
    unknown,
    void,
    FaxAuditReview,
    DiscardFaxAuditReviewPathParams
  >(
    "PATCH",
    (paramsInPath: DiscardFaxAuditReviewPathParams) =>
      `/faxAuditReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreateAppealPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type CreateAppealProps = Omit<
  MutateProps<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateAppealPathParams
  >,
  "path" | "verb"
> &
  CreateAppealPathParams;

export const CreateAppeal = ({
  serviceRequestId,
  ...props
}: CreateAppealProps) => (
  <Mutate<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateAppealPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/appeal`}
    {...props}
  />
);

export type UseCreateAppealProps = Omit<
  UseMutateProps<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateAppealPathParams
  >,
  "path" | "verb"
> &
  CreateAppealPathParams;

export const useCreateAppeal = ({
  serviceRequestId,
  ...props
}: UseCreateAppealProps) =>
  useMutate<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateAppealPathParams
  >(
    "POST",
    (paramsInPath: CreateAppealPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/appeal`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface CreateOrUpdateAppealPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
  /**
   * A unique identifier for a `Appeal`.
   */
  appealId: string;
}

export type CreateOrUpdateAppealProps = Omit<
  MutateProps<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateOrUpdateAppealPathParams
  >,
  "path" | "verb"
> &
  CreateOrUpdateAppealPathParams;

export const CreateOrUpdateAppeal = ({
  serviceRequestId,
  appealId,
  ...props
}: CreateOrUpdateAppealProps) => (
  <Mutate<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateOrUpdateAppealPathParams
  >
    verb="PUT"
    path={`/serviceRequest/${serviceRequestId}/appeal/${appealId}`}
    {...props}
  />
);

export type UseCreateOrUpdateAppealProps = Omit<
  UseMutateProps<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateOrUpdateAppealPathParams
  >,
  "path" | "verb"
> &
  CreateOrUpdateAppealPathParams;

export const useCreateOrUpdateAppeal = ({
  serviceRequestId,
  appealId,
  ...props
}: UseCreateOrUpdateAppealProps) =>
  useMutate<
    AppealResponse,
    ErrorMessage,
    void,
    AppealPayload,
    CreateOrUpdateAppealPathParams
  >(
    "PUT",
    (paramsInPath: CreateOrUpdateAppealPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/appeal/${paramsInPath.appealId}`,
    { pathParams: { serviceRequestId, appealId }, ...props }
  );

export interface CreateMdReviewQueryParams {
  /**
   * A boolean param to indicate if this come from situation approve without P2P review.
   */
  approveWithoutP2P?: boolean;
}

export interface CreateMdReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreateMdReviewRequestBody {}

export type CreateMdReviewProps = Omit<
  MutateProps<
    MdReview,
    ErrorMessage,
    CreateMdReviewQueryParams,
    CreateMdReviewRequestBody,
    CreateMdReviewPathParams
  >,
  "path" | "verb"
> &
  CreateMdReviewPathParams;

export const CreateMdReview = ({
  serviceRequestId,
  ...props
}: CreateMdReviewProps) => (
  <Mutate<
    MdReview,
    ErrorMessage,
    CreateMdReviewQueryParams,
    CreateMdReviewRequestBody,
    CreateMdReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/mdReview`}
    {...props}
  />
);

export type UseCreateMdReviewProps = Omit<
  UseMutateProps<
    MdReview,
    ErrorMessage,
    CreateMdReviewQueryParams,
    CreateMdReviewRequestBody,
    CreateMdReviewPathParams
  >,
  "path" | "verb"
> &
  CreateMdReviewPathParams;

export const useCreateMdReview = ({
  serviceRequestId,
  ...props
}: UseCreateMdReviewProps) =>
  useMutate<
    MdReview,
    ErrorMessage,
    CreateMdReviewQueryParams,
    CreateMdReviewRequestBody,
    CreateMdReviewPathParams
  >(
    "POST",
    (paramsInPath: CreateMdReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/mdReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetMdReviewsQueryParams {
  /**
   * A boolean param to indicate if this come from situation approve without P2P review.
   */
  approveWithoutP2P?: boolean;
  /**
   * Number of results to return
   */
  max?: number;
}

export interface GetMdReviewsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetMdReviewsProps = Omit<
  GetProps<
    MdReview[],
    unknown,
    GetMdReviewsQueryParams,
    GetMdReviewsPathParams
  >,
  "path"
> &
  GetMdReviewsPathParams;

export const GetMdReviews = ({
  serviceRequestId,
  ...props
}: GetMdReviewsProps) => (
  <Get<MdReview[], unknown, GetMdReviewsQueryParams, GetMdReviewsPathParams>
    path={`/serviceRequest/${serviceRequestId}/mdReview`}
    {...props}
  />
);

export type UseGetMdReviewsProps = Omit<
  UseGetProps<
    MdReview[],
    unknown,
    GetMdReviewsQueryParams,
    GetMdReviewsPathParams
  >,
  "path"
> &
  GetMdReviewsPathParams;

export const useGetMdReviews = ({
  serviceRequestId,
  ...props
}: UseGetMdReviewsProps) =>
  useGet<MdReview[], unknown, GetMdReviewsQueryParams, GetMdReviewsPathParams>(
    (paramsInPath: GetMdReviewsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/mdReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetMdReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface GetMdReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetMdReviewProps = Omit<
  GetProps<MdReview, unknown, GetMdReviewQueryParams, GetMdReviewPathParams>,
  "path"
> &
  GetMdReviewPathParams;

export const GetMdReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetMdReviewProps) => (
  <Get<MdReview, unknown, GetMdReviewQueryParams, GetMdReviewPathParams>
    path={`/serviceRequest/${serviceRequestId}/mdReview/${reviewId}`}
    {...props}
  />
);

export type UseGetMdReviewProps = Omit<
  UseGetProps<MdReview, unknown, GetMdReviewQueryParams, GetMdReviewPathParams>,
  "path"
> &
  GetMdReviewPathParams;

export const useGetMdReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetMdReviewProps) =>
  useGet<MdReview, unknown, GetMdReviewQueryParams, GetMdReviewPathParams>(
    (paramsInPath: GetMdReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/mdReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeleteMdReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface DeleteMdReviewPathParams {
  serviceRequestId: string;
}

export type DeleteMdReviewProps = Omit<
  MutateProps<
    void,
    unknown,
    DeleteMdReviewQueryParams,
    string,
    DeleteMdReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteMdReviewPathParams;

export const DeleteMdReview = ({
  serviceRequestId,
  ...props
}: DeleteMdReviewProps) => (
  <Mutate<
    void,
    unknown,
    DeleteMdReviewQueryParams,
    string,
    DeleteMdReviewPathParams
  >
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/mdReview`}
    {...props}
  />
);

export type UseDeleteMdReviewProps = Omit<
  UseMutateProps<
    void,
    unknown,
    DeleteMdReviewQueryParams,
    string,
    DeleteMdReviewPathParams
  >,
  "path" | "verb"
> &
  DeleteMdReviewPathParams;

export const useDeleteMdReview = ({
  serviceRequestId,
  ...props
}: UseDeleteMdReviewProps) =>
  useMutate<
    void,
    unknown,
    DeleteMdReviewQueryParams,
    string,
    DeleteMdReviewPathParams
  >(
    "DELETE",
    (paramsInPath: DeleteMdReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/mdReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateMdReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface UpdateMdReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdateMdReviewProps = Omit<
  MutateProps<
    MdReview,
    unknown,
    UpdateMdReviewQueryParams,
    MdReview,
    UpdateMdReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateMdReviewPathParams;

export const UpdateMdReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdateMdReviewProps) => (
  <Mutate<
    MdReview,
    unknown,
    UpdateMdReviewQueryParams,
    MdReview,
    UpdateMdReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/mdReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdateMdReviewProps = Omit<
  UseMutateProps<
    MdReview,
    unknown,
    UpdateMdReviewQueryParams,
    MdReview,
    UpdateMdReviewPathParams
  >,
  "path" | "verb"
> &
  UpdateMdReviewPathParams;

export const useUpdateMdReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdateMdReviewProps) =>
  useMutate<
    MdReview,
    unknown,
    UpdateMdReviewQueryParams,
    MdReview,
    UpdateMdReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateMdReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/mdReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitMdReviewQueryParams {
  isUserAudited?: boolean;
}

export interface SubmitMdReviewPathParams {
  reviewId: string;
}

export type SubmitMdReviewProps = Omit<
  MutateProps<
    MdReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    SubmitMdReviewQueryParams,
    MdReviewSubmitPayload,
    SubmitMdReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitMdReviewPathParams;

export const SubmitMdReview = ({ reviewId, ...props }: SubmitMdReviewProps) => (
  <Mutate<
    MdReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    SubmitMdReviewQueryParams,
    MdReviewSubmitPayload,
    SubmitMdReviewPathParams
  >
    verb="POST"
    path={`/mdReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitMdReviewProps = Omit<
  UseMutateProps<
    MdReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    SubmitMdReviewQueryParams,
    MdReviewSubmitPayload,
    SubmitMdReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitMdReviewPathParams;

export const useSubmitMdReview = ({
  reviewId,
  ...props
}: UseSubmitMdReviewProps) =>
  useMutate<
    MdReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    SubmitMdReviewQueryParams,
    MdReviewSubmitPayload,
    SubmitMdReviewPathParams
  >(
    "POST",
    (paramsInPath: SubmitMdReviewPathParams) =>
      `/mdReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface PatchReviewDeterminationUpdatesPathParams {
  reviewId: string;
}

export interface PatchReviewDeterminationUpdatesRequestBody {
  nextReviewDate?: string;
  denialLanguage?: string;
  denialStatus?: "NOT_STARTED" | "DRAFT" | "COMPLETE";
  denialsTemplate?: DenialsInfo;
  overwrittenTranslatedMdNote?: string;
  editedPreferredLabels?: EditedPreferredLabel[];
  serviceStatusCode?: string;
  serviceStatusLabel?: string;
  claimsPaymentNote?: string;
  reviewerCreateContinuationAcknowledgement?: boolean;
  additionalRecipients?: string[];
}

export type PatchReviewDeterminationUpdatesProps = Omit<
  MutateProps<
    ReviewType,
    ErrorMessage,
    void,
    PatchReviewDeterminationUpdatesRequestBody,
    PatchReviewDeterminationUpdatesPathParams
  >,
  "path" | "verb"
> &
  PatchReviewDeterminationUpdatesPathParams;

export const PatchReviewDeterminationUpdates = ({
  reviewId,
  ...props
}: PatchReviewDeterminationUpdatesProps) => (
  <Mutate<
    ReviewType,
    ErrorMessage,
    void,
    PatchReviewDeterminationUpdatesRequestBody,
    PatchReviewDeterminationUpdatesPathParams
  >
    verb="PATCH"
    path={`/review/${reviewId}/patchReviewDeterminationUpdates`}
    {...props}
  />
);

export type UsePatchReviewDeterminationUpdatesProps = Omit<
  UseMutateProps<
    ReviewType,
    ErrorMessage,
    void,
    PatchReviewDeterminationUpdatesRequestBody,
    PatchReviewDeterminationUpdatesPathParams
  >,
  "path" | "verb"
> &
  PatchReviewDeterminationUpdatesPathParams;

export const usePatchReviewDeterminationUpdates = ({
  reviewId,
  ...props
}: UsePatchReviewDeterminationUpdatesProps) =>
  useMutate<
    ReviewType,
    ErrorMessage,
    void,
    PatchReviewDeterminationUpdatesRequestBody,
    PatchReviewDeterminationUpdatesPathParams
  >(
    "PATCH",
    (paramsInPath: PatchReviewDeterminationUpdatesPathParams) =>
      `/review/${paramsInPath.reviewId}/patchReviewDeterminationUpdates`,
    { pathParams: { reviewId }, ...props }
  );

export interface DiscardMdReviewPathParams {
  reviewId: string;
}

export type DiscardMdReviewProps = Omit<
  MutateProps<MdReview, unknown, void, MdReview, DiscardMdReviewPathParams>,
  "path" | "verb"
> &
  DiscardMdReviewPathParams;

export const DiscardMdReview = ({
  reviewId,
  ...props
}: DiscardMdReviewProps) => (
  <Mutate<MdReview, unknown, void, MdReview, DiscardMdReviewPathParams>
    verb="POST"
    path={`/mdReview/${reviewId}/discard`}
    {...props}
  />
);

export type UseDiscardMdReviewProps = Omit<
  UseMutateProps<MdReview, unknown, void, MdReview, DiscardMdReviewPathParams>,
  "path" | "verb"
> &
  DiscardMdReviewPathParams;

export const useDiscardMdReview = ({
  reviewId,
  ...props
}: UseDiscardMdReviewProps) =>
  useMutate<MdReview, unknown, void, MdReview, DiscardMdReviewPathParams>(
    "POST",
    (paramsInPath: DiscardMdReviewPathParams) =>
      `/mdReview/${paramsInPath.reviewId}/discard`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreatePeerToPeerReviewPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export interface CreatePeerToPeerReviewRequestBody {
  isPostDenialP2P?: boolean;
  isAudited?: boolean;
}

export type CreatePeerToPeerReviewProps = Omit<
  MutateProps<
    PeerToPeerReview,
    ErrorMessage,
    void,
    CreatePeerToPeerReviewRequestBody,
    CreatePeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  CreatePeerToPeerReviewPathParams;

export const CreatePeerToPeerReview = ({
  serviceRequestId,
  ...props
}: CreatePeerToPeerReviewProps) => (
  <Mutate<
    PeerToPeerReview,
    ErrorMessage,
    void,
    CreatePeerToPeerReviewRequestBody,
    CreatePeerToPeerReviewPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/peerToPeerReview`}
    {...props}
  />
);

export type UseCreatePeerToPeerReviewProps = Omit<
  UseMutateProps<
    PeerToPeerReview,
    ErrorMessage,
    void,
    CreatePeerToPeerReviewRequestBody,
    CreatePeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  CreatePeerToPeerReviewPathParams;

export const useCreatePeerToPeerReview = ({
  serviceRequestId,
  ...props
}: UseCreatePeerToPeerReviewProps) =>
  useMutate<
    PeerToPeerReview,
    ErrorMessage,
    void,
    CreatePeerToPeerReviewRequestBody,
    CreatePeerToPeerReviewPathParams
  >(
    "POST",
    (paramsInPath: CreatePeerToPeerReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/peerToPeerReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetPeerToPeerReviewsQueryParams {
  /**
   * Number of results to return
   */
  max?: number;
}

export interface GetPeerToPeerReviewsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetPeerToPeerReviewsProps = Omit<
  GetProps<
    PeerToPeerReview[],
    unknown,
    GetPeerToPeerReviewsQueryParams,
    GetPeerToPeerReviewsPathParams
  >,
  "path"
> &
  GetPeerToPeerReviewsPathParams;

export const GetPeerToPeerReviews = ({
  serviceRequestId,
  ...props
}: GetPeerToPeerReviewsProps) => (
  <Get<
    PeerToPeerReview[],
    unknown,
    GetPeerToPeerReviewsQueryParams,
    GetPeerToPeerReviewsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/peerToPeerReview`}
    {...props}
  />
);

export type UseGetPeerToPeerReviewsProps = Omit<
  UseGetProps<
    PeerToPeerReview[],
    unknown,
    GetPeerToPeerReviewsQueryParams,
    GetPeerToPeerReviewsPathParams
  >,
  "path"
> &
  GetPeerToPeerReviewsPathParams;

export const useGetPeerToPeerReviews = ({
  serviceRequestId,
  ...props
}: UseGetPeerToPeerReviewsProps) =>
  useGet<
    PeerToPeerReview[],
    unknown,
    GetPeerToPeerReviewsQueryParams,
    GetPeerToPeerReviewsPathParams
  >(
    (paramsInPath: GetPeerToPeerReviewsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/peerToPeerReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetPeerToPeerReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface GetPeerToPeerReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type GetPeerToPeerReviewProps = Omit<
  GetProps<
    PeerToPeerReview,
    unknown,
    GetPeerToPeerReviewQueryParams,
    GetPeerToPeerReviewPathParams
  >,
  "path"
> &
  GetPeerToPeerReviewPathParams;

export const GetPeerToPeerReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: GetPeerToPeerReviewProps) => (
  <Get<
    PeerToPeerReview,
    unknown,
    GetPeerToPeerReviewQueryParams,
    GetPeerToPeerReviewPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/peerToPeerReview/${reviewId}`}
    {...props}
  />
);

export type UseGetPeerToPeerReviewProps = Omit<
  UseGetProps<
    PeerToPeerReview,
    unknown,
    GetPeerToPeerReviewQueryParams,
    GetPeerToPeerReviewPathParams
  >,
  "path"
> &
  GetPeerToPeerReviewPathParams;

export const useGetPeerToPeerReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseGetPeerToPeerReviewProps) =>
  useGet<
    PeerToPeerReview,
    unknown,
    GetPeerToPeerReviewQueryParams,
    GetPeerToPeerReviewPathParams
  >(
    (paramsInPath: GetPeerToPeerReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/peerToPeerReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface DeletePeerToPeerReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface DeletePeerToPeerReviewPathParams {
  serviceRequestId: string;
}

export type DeletePeerToPeerReviewProps = Omit<
  MutateProps<
    void,
    unknown,
    DeletePeerToPeerReviewQueryParams,
    string,
    DeletePeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  DeletePeerToPeerReviewPathParams;

export const DeletePeerToPeerReview = ({
  serviceRequestId,
  ...props
}: DeletePeerToPeerReviewProps) => (
  <Mutate<
    void,
    unknown,
    DeletePeerToPeerReviewQueryParams,
    string,
    DeletePeerToPeerReviewPathParams
  >
    verb="DELETE"
    path={`/serviceRequest/${serviceRequestId}/peerToPeerReview`}
    {...props}
  />
);

export type UseDeletePeerToPeerReviewProps = Omit<
  UseMutateProps<
    void,
    unknown,
    DeletePeerToPeerReviewQueryParams,
    string,
    DeletePeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  DeletePeerToPeerReviewPathParams;

export const useDeletePeerToPeerReview = ({
  serviceRequestId,
  ...props
}: UseDeletePeerToPeerReviewProps) =>
  useMutate<
    void,
    unknown,
    DeletePeerToPeerReviewQueryParams,
    string,
    DeletePeerToPeerReviewPathParams
  >(
    "DELETE",
    (paramsInPath: DeletePeerToPeerReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/peerToPeerReview`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdatePeerToPeerReviewQueryParams {
  denialsWorkflow?: boolean;
}

export interface UpdatePeerToPeerReviewPathParams {
  serviceRequestId: string;
  reviewId: string;
}

export type UpdatePeerToPeerReviewProps = Omit<
  MutateProps<
    PeerToPeerReview,
    unknown,
    UpdatePeerToPeerReviewQueryParams,
    PeerToPeerReview,
    UpdatePeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  UpdatePeerToPeerReviewPathParams;

export const UpdatePeerToPeerReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UpdatePeerToPeerReviewProps) => (
  <Mutate<
    PeerToPeerReview,
    unknown,
    UpdatePeerToPeerReviewQueryParams,
    PeerToPeerReview,
    UpdatePeerToPeerReviewPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/peerToPeerReview/${reviewId}`}
    {...props}
  />
);

export type UseUpdatePeerToPeerReviewProps = Omit<
  UseMutateProps<
    PeerToPeerReview,
    unknown,
    UpdatePeerToPeerReviewQueryParams,
    PeerToPeerReview,
    UpdatePeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  UpdatePeerToPeerReviewPathParams;

export const useUpdatePeerToPeerReview = ({
  serviceRequestId,
  reviewId,
  ...props
}: UseUpdatePeerToPeerReviewProps) =>
  useMutate<
    PeerToPeerReview,
    unknown,
    UpdatePeerToPeerReviewQueryParams,
    PeerToPeerReview,
    UpdatePeerToPeerReviewPathParams
  >(
    "PATCH",
    (paramsInPath: UpdatePeerToPeerReviewPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/peerToPeerReview/${paramsInPath.reviewId}`,
    { pathParams: { serviceRequestId, reviewId }, ...props }
  );

export interface SubmitPeerToPeerReviewPathParams {
  reviewId: string;
}

export type SubmitPeerToPeerReviewProps = Omit<
  MutateProps<
    PeerToPeerReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    PeerToPeerReviewSubmitPayload,
    SubmitPeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitPeerToPeerReviewPathParams;

export const SubmitPeerToPeerReview = ({
  reviewId,
  ...props
}: SubmitPeerToPeerReviewProps) => (
  <Mutate<
    PeerToPeerReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    PeerToPeerReviewSubmitPayload,
    SubmitPeerToPeerReviewPathParams
  >
    verb="POST"
    path={`/peerToPeerReview/${reviewId}/submit`}
    {...props}
  />
);

export type UseSubmitPeerToPeerReviewProps = Omit<
  UseMutateProps<
    PeerToPeerReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    PeerToPeerReviewSubmitPayload,
    SubmitPeerToPeerReviewPathParams
  >,
  "path" | "verb"
> &
  SubmitPeerToPeerReviewPathParams;

export const useSubmitPeerToPeerReview = ({
  reviewId,
  ...props
}: UseSubmitPeerToPeerReviewProps) =>
  useMutate<
    PeerToPeerReview,
    | {
        /**
         * error message
         */
        message?: string;
      }
    | ErrorMessage,
    void,
    PeerToPeerReviewSubmitPayload,
    SubmitPeerToPeerReviewPathParams
  >(
    "POST",
    (paramsInPath: SubmitPeerToPeerReviewPathParams) =>
      `/peerToPeerReview/${paramsInPath.reviewId}/submit`,
    { pathParams: { reviewId }, ...props }
  );

export interface CreatePeerToPeerOutreachPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type CreatePeerToPeerOutreachProps = Omit<
  MutateProps<
    PeerToPeerOutreach,
    unknown,
    void,
    PeerToPeerOutreachCreatePayload,
    CreatePeerToPeerOutreachPathParams
  >,
  "path" | "verb"
> &
  CreatePeerToPeerOutreachPathParams;

export const CreatePeerToPeerOutreach = ({
  serviceRequestId,
  ...props
}: CreatePeerToPeerOutreachProps) => (
  <Mutate<
    PeerToPeerOutreach,
    unknown,
    void,
    PeerToPeerOutreachCreatePayload,
    CreatePeerToPeerOutreachPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/peerToPeerOutreach`}
    {...props}
  />
);

export type UseCreatePeerToPeerOutreachProps = Omit<
  UseMutateProps<
    PeerToPeerOutreach,
    unknown,
    void,
    PeerToPeerOutreachCreatePayload,
    CreatePeerToPeerOutreachPathParams
  >,
  "path" | "verb"
> &
  CreatePeerToPeerOutreachPathParams;

export const useCreatePeerToPeerOutreach = ({
  serviceRequestId,
  ...props
}: UseCreatePeerToPeerOutreachProps) =>
  useMutate<
    PeerToPeerOutreach,
    unknown,
    void,
    PeerToPeerOutreachCreatePayload,
    CreatePeerToPeerOutreachPathParams
  >(
    "POST",
    (paramsInPath: CreatePeerToPeerOutreachPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/peerToPeerOutreach`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface SaveNotePathParams {
  serviceRequestId: string;
}

export type SaveNoteProps = Omit<
  MutateProps<Note, ErrorMessage, void, NoteSubmitPayload, SaveNotePathParams>,
  "path" | "verb"
> &
  SaveNotePathParams;

export const SaveNote = ({ serviceRequestId, ...props }: SaveNoteProps) => (
  <Mutate<Note, ErrorMessage, void, NoteSubmitPayload, SaveNotePathParams>
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/note`}
    {...props}
  />
);

export type UseSaveNoteProps = Omit<
  UseMutateProps<
    Note,
    ErrorMessage,
    void,
    NoteSubmitPayload,
    SaveNotePathParams
  >,
  "path" | "verb"
> &
  SaveNotePathParams;

export const useSaveNote = ({ serviceRequestId, ...props }: UseSaveNoteProps) =>
  useMutate<Note, ErrorMessage, void, NoteSubmitPayload, SaveNotePathParams>(
    "POST",
    (paramsInPath: SaveNotePathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/note`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetServiceRequestNotesPathParams {
  serviceRequestId: string;
}

export type GetServiceRequestNotesProps = Omit<
  GetProps<Note[], ErrorMessage, void, GetServiceRequestNotesPathParams>,
  "path"
> &
  GetServiceRequestNotesPathParams;

export const GetServiceRequestNotes = ({
  serviceRequestId,
  ...props
}: GetServiceRequestNotesProps) => (
  <Get<Note[], ErrorMessage, void, GetServiceRequestNotesPathParams>
    path={`/note/${serviceRequestId}/serviceRequestNotes`}
    {...props}
  />
);

export type UseGetServiceRequestNotesProps = Omit<
  UseGetProps<Note[], ErrorMessage, void, GetServiceRequestNotesPathParams>,
  "path"
> &
  GetServiceRequestNotesPathParams;

export const useGetServiceRequestNotes = ({
  serviceRequestId,
  ...props
}: UseGetServiceRequestNotesProps) =>
  useGet<Note[], ErrorMessage, void, GetServiceRequestNotesPathParams>(
    (paramsInPath: GetServiceRequestNotesPathParams) =>
      `/note/${paramsInPath.serviceRequestId}/serviceRequestNotes`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetServiceRequestAutoApprovalResponse {
  /**
   * total amount of service request indices since launch
   */
  totalCount?: number;
  /**
   * amount of auto approved service request indices since launch
   */
  autoApprovalCount?: number;
  /**
   * total amount of service request indices today
   */
  todayTotalCount?: number;
  /**
   * amount of auto approved service request indices today
   */
  todayAutoApprovalCount?: number;
}

export type GetServiceRequestAutoApprovalProps = Omit<
  GetProps<GetServiceRequestAutoApprovalResponse, unknown, void, void>,
  "path"
>;

export const GetServiceRequestAutoApproval = (
  props: GetServiceRequestAutoApprovalProps
) => (
  <Get<GetServiceRequestAutoApprovalResponse, unknown, void, void>
    path={`/serviceRequestAutoApprovalRate`}
    {...props}
  />
);

export type UseGetServiceRequestAutoApprovalProps = Omit<
  UseGetProps<GetServiceRequestAutoApprovalResponse, unknown, void, void>,
  "path"
>;

export const useGetServiceRequestAutoApproval = (
  props: UseGetServiceRequestAutoApprovalProps
) =>
  useGet<GetServiceRequestAutoApprovalResponse, unknown, void, void>(
    `/serviceRequestAutoApprovalRate`,
    props
  );

export interface GetVatNotificationQueryParams {
  serviceRequestId?: string;
  patientId?: string;
}

export type GetVatNotificationProps = Omit<
  GetProps<VatNotification[], unknown, GetVatNotificationQueryParams, void>,
  "path"
>;

export const GetVatNotification = (props: GetVatNotificationProps) => (
  <Get<VatNotification[], unknown, GetVatNotificationQueryParams, void>
    path={`/vatNotification`}
    {...props}
  />
);

export type UseGetVatNotificationProps = Omit<
  UseGetProps<VatNotification[], unknown, GetVatNotificationQueryParams, void>,
  "path"
>;

export const useGetVatNotification = (props: UseGetVatNotificationProps) =>
  useGet<VatNotification[], unknown, GetVatNotificationQueryParams, void>(
    `/vatNotification`,
    props
  );

export interface GetServiceRequestFinalDeterminationLetterAttachmentsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestFinalDeterminationLetterAttachmentsProps = Omit<
  GetProps<
    Attachment[],
    unknown,
    void,
    GetServiceRequestFinalDeterminationLetterAttachmentsPathParams
  >,
  "path"
> &
  GetServiceRequestFinalDeterminationLetterAttachmentsPathParams;

/**
 * Search for Service Request Final Determination Letter Attachments
 */
export const GetServiceRequestFinalDeterminationLetterAttachments = ({
  id,
  ...props
}: GetServiceRequestFinalDeterminationLetterAttachmentsProps) => (
  <Get<
    Attachment[],
    unknown,
    void,
    GetServiceRequestFinalDeterminationLetterAttachmentsPathParams
  >
    path={`/serviceRequest/${id}/finalDeterminationLetterAttachment`}
    {...props}
  />
);

export type UseGetServiceRequestFinalDeterminationLetterAttachmentsProps = Omit<
  UseGetProps<
    Attachment[],
    unknown,
    void,
    GetServiceRequestFinalDeterminationLetterAttachmentsPathParams
  >,
  "path"
> &
  GetServiceRequestFinalDeterminationLetterAttachmentsPathParams;

/**
 * Search for Service Request Final Determination Letter Attachments
 */
export const useGetServiceRequestFinalDeterminationLetterAttachments = ({
  id,
  ...props
}: UseGetServiceRequestFinalDeterminationLetterAttachmentsProps) =>
  useGet<
    Attachment[],
    unknown,
    void,
    GetServiceRequestFinalDeterminationLetterAttachmentsPathParams
  >(
    (
      paramsInPath: GetServiceRequestFinalDeterminationLetterAttachmentsPathParams
    ) =>
      `/serviceRequest/${paramsInPath.id}/finalDeterminationLetterAttachment`,
    { pathParams: { id }, ...props }
  );

export interface GetFinalDeterminationLetterAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
  /**
   * A unique identifier for a `FinalDeterminationLetterAttachment`.
   */
  id: string;
}

export type GetFinalDeterminationLetterAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    void,
    GetFinalDeterminationLetterAttachmentDownloadPathParams
  >,
  "path"
> &
  GetFinalDeterminationLetterAttachmentDownloadPathParams;

/**
 * Download a final determination letter
 *
 * Downloads an existing `FinalDeterminationLetterAttachment`.
 */
export const GetFinalDeterminationLetterAttachmentDownload = ({
  serviceRequestId,
  id,
  ...props
}: GetFinalDeterminationLetterAttachmentDownloadProps) => (
  <Get<
    void,
    unknown,
    void,
    GetFinalDeterminationLetterAttachmentDownloadPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/finalDeterminationLetterAttachment/${id}/download`}
    {...props}
  />
);

export type UseGetFinalDeterminationLetterAttachmentDownloadProps = Omit<
  UseGetProps<
    void,
    unknown,
    void,
    GetFinalDeterminationLetterAttachmentDownloadPathParams
  >,
  "path"
> &
  GetFinalDeterminationLetterAttachmentDownloadPathParams;

/**
 * Download a final determination letter
 *
 * Downloads an existing `FinalDeterminationLetterAttachment`.
 */
export const useGetFinalDeterminationLetterAttachmentDownload = ({
  serviceRequestId,
  id,
  ...props
}: UseGetFinalDeterminationLetterAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    void,
    GetFinalDeterminationLetterAttachmentDownloadPathParams
  >(
    (paramsInPath: GetFinalDeterminationLetterAttachmentDownloadPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/finalDeterminationLetterAttachment/${paramsInPath.id}/download`,
    { pathParams: { serviceRequestId, id }, ...props }
  );

export interface GetInternalFinalDeterminationLetterAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
  /**
   * A unique identifier for an `InternalFinalDeterminationLetterAttachment`.
   */
  id: string;
}

export type GetInternalFinalDeterminationLetterAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationLetterAttachmentDownloadPathParams
  >,
  "path"
> &
  GetInternalFinalDeterminationLetterAttachmentDownloadPathParams;

/**
 * Download an internal final determination letter
 *
 * Downloads an existing `InternalFinalDeterminationLetterAttachment`.
 */
export const GetInternalFinalDeterminationLetterAttachmentDownload = ({
  serviceRequestId,
  id,
  ...props
}: GetInternalFinalDeterminationLetterAttachmentDownloadProps) => (
  <Get<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationLetterAttachmentDownloadPathParams
  >
    path={`/internalFinalDeterminationLetter/${serviceRequestId}/${id}/download`}
    {...props}
  />
);

export type UseGetInternalFinalDeterminationLetterAttachmentDownloadProps =
  Omit<
    UseGetProps<
      void,
      unknown,
      void,
      GetInternalFinalDeterminationLetterAttachmentDownloadPathParams
    >,
    "path"
  > &
    GetInternalFinalDeterminationLetterAttachmentDownloadPathParams;

/**
 * Download an internal final determination letter
 *
 * Downloads an existing `InternalFinalDeterminationLetterAttachment`.
 */
export const useGetInternalFinalDeterminationLetterAttachmentDownload = ({
  serviceRequestId,
  id,
  ...props
}: UseGetInternalFinalDeterminationLetterAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationLetterAttachmentDownloadPathParams
  >(
    (
      paramsInPath: GetInternalFinalDeterminationLetterAttachmentDownloadPathParams
    ) =>
      `/internalFinalDeterminationLetter/${paramsInPath.serviceRequestId}/${paramsInPath.id}/download`,
    { pathParams: { serviceRequestId, id }, ...props }
  );

export interface GetInternalFinalDeterminationFaxAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
  /**
   * A unique identifier for an `InternalFinalDeterminationLetterAttachment`.
   */
  id: string;
}

export type GetInternalFinalDeterminationFaxAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationFaxAttachmentDownloadPathParams
  >,
  "path"
> &
  GetInternalFinalDeterminationFaxAttachmentDownloadPathParams;

/**
 * Download an internal final determination fax
 *
 * Downloads an existing `InternalFinalDeterminationFaxAttachment`.
 */
export const GetInternalFinalDeterminationFaxAttachmentDownload = ({
  serviceRequestId,
  id,
  ...props
}: GetInternalFinalDeterminationFaxAttachmentDownloadProps) => (
  <Get<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationFaxAttachmentDownloadPathParams
  >
    path={`/internalFinalDeterminationFax/${serviceRequestId}/${id}/download`}
    {...props}
  />
);

export type UseGetInternalFinalDeterminationFaxAttachmentDownloadProps = Omit<
  UseGetProps<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationFaxAttachmentDownloadPathParams
  >,
  "path"
> &
  GetInternalFinalDeterminationFaxAttachmentDownloadPathParams;

/**
 * Download an internal final determination fax
 *
 * Downloads an existing `InternalFinalDeterminationFaxAttachment`.
 */
export const useGetInternalFinalDeterminationFaxAttachmentDownload = ({
  serviceRequestId,
  id,
  ...props
}: UseGetInternalFinalDeterminationFaxAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    void,
    GetInternalFinalDeterminationFaxAttachmentDownloadPathParams
  >(
    (
      paramsInPath: GetInternalFinalDeterminationFaxAttachmentDownloadPathParams
    ) =>
      `/internalFinalDeterminationFax/${paramsInPath.serviceRequestId}/${paramsInPath.id}/download`,
    { pathParams: { serviceRequestId, id }, ...props }
  );

export interface GetDenialsTemplatesForReviewQueryParams {
  authStatus?: string;
  usedGuidelineIds: string[];
  healthPlan?: string;
  lineOfBusiness?: string;
  state?: string;
  encounterTypes?: FacilityCategory[];
  /**
   * Filters which templates the backend will return. Default is determination only.
   */
  templateType?: "ALL" | "DETERMINATION" | "NON_DETERMINATION" | "CORRECTION";
  adhocNotificationType?: AdhocNotificationType;
  adhocNotificationTypes?: AdhocNotificationType[];
  excludeContentTypes?: ContentType[];
  filterContentTypes?: ContentType[];
  delegatedVendorName?: string;
}

export type GetDenialsTemplatesForReviewProps = Omit<
  GetProps<
    DenialsTemplate[],
    unknown,
    GetDenialsTemplatesForReviewQueryParams,
    void
  >,
  "path"
>;

export const GetDenialsTemplatesForReview = (
  props: GetDenialsTemplatesForReviewProps
) => (
  <Get<
    DenialsTemplate[],
    unknown,
    GetDenialsTemplatesForReviewQueryParams,
    void
  >
    path={`/review/denialsTemplates`}
    {...props}
  />
);

export type UseGetDenialsTemplatesForReviewProps = Omit<
  UseGetProps<
    DenialsTemplate[],
    unknown,
    GetDenialsTemplatesForReviewQueryParams,
    void
  >,
  "path"
>;

export const useGetDenialsTemplatesForReview = (
  props: UseGetDenialsTemplatesForReviewProps
) =>
  useGet<
    DenialsTemplate[],
    unknown,
    GetDenialsTemplatesForReviewQueryParams,
    void
  >(`/review/denialsTemplates`, props);

export interface CreateServiceRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type CreateServiceRequestAttachmentProps = Omit<
  MutateProps<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateServiceRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateServiceRequestAttachmentPathParams;

/**
 * Add attachment to service request
 */
export const CreateServiceRequestAttachment = ({
  id,
  ...props
}: CreateServiceRequestAttachmentProps) => (
  <Mutate<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateServiceRequestAttachmentPathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/attachment`}
    {...props}
  />
);

export type UseCreateServiceRequestAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateServiceRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateServiceRequestAttachmentPathParams;

/**
 * Add attachment to service request
 */
export const useCreateServiceRequestAttachment = ({
  id,
  ...props
}: UseCreateServiceRequestAttachmentProps) =>
  useMutate<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateServiceRequestAttachmentPathParams
  >(
    "POST",
    (paramsInPath: CreateServiceRequestAttachmentPathParams) =>
      `/serviceRequest/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestAttachmentsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestAttachmentsProps = Omit<
  GetProps<Attachment[], unknown, void, GetServiceRequestAttachmentsPathParams>,
  "path"
> &
  GetServiceRequestAttachmentsPathParams;

/**
 * Search for Service Request Attachments
 */
export const GetServiceRequestAttachments = ({
  id,
  ...props
}: GetServiceRequestAttachmentsProps) => (
  <Get<Attachment[], unknown, void, GetServiceRequestAttachmentsPathParams>
    path={`/serviceRequest/${id}/attachment`}
    {...props}
  />
);

export type UseGetServiceRequestAttachmentsProps = Omit<
  UseGetProps<
    Attachment[],
    unknown,
    void,
    GetServiceRequestAttachmentsPathParams
  >,
  "path"
> &
  GetServiceRequestAttachmentsPathParams;

/**
 * Search for Service Request Attachments
 */
export const useGetServiceRequestAttachments = ({
  id,
  ...props
}: UseGetServiceRequestAttachmentsProps) =>
  useGet<Attachment[], unknown, void, GetServiceRequestAttachmentsPathParams>(
    (paramsInPath: GetServiceRequestAttachmentsPathParams) =>
      `/serviceRequest/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface CreateReferralRequestAttachmentPathParams {
  /**
   * A unique identifier for a `Referral Request`.
   */
  id: string;
}

export type CreateReferralRequestAttachmentProps = Omit<
  MutateProps<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateReferralRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateReferralRequestAttachmentPathParams;

/**
 * Add attachment to referral request
 */
export const CreateReferralRequestAttachment = ({
  id,
  ...props
}: CreateReferralRequestAttachmentProps) => (
  <Mutate<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateReferralRequestAttachmentPathParams
  >
    verb="POST"
    path={`/referralRequest/${id}/attachment`}
    {...props}
  />
);

export type UseCreateReferralRequestAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateReferralRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateReferralRequestAttachmentPathParams;

/**
 * Add attachment to referral request
 */
export const useCreateReferralRequestAttachment = ({
  id,
  ...props
}: UseCreateReferralRequestAttachmentProps) =>
  useMutate<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateReferralRequestAttachmentPathParams
  >(
    "POST",
    (paramsInPath: CreateReferralRequestAttachmentPathParams) =>
      `/referralRequest/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface CreateAdvanceNotificationAttachmentPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
}

export type CreateAdvanceNotificationAttachmentProps = Omit<
  MutateProps<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateAdvanceNotificationAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateAdvanceNotificationAttachmentPathParams;

/**
 * Add attachment to an advance notification
 */
export const CreateAdvanceNotificationAttachment = ({
  id,
  ...props
}: CreateAdvanceNotificationAttachmentProps) => (
  <Mutate<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateAdvanceNotificationAttachmentPathParams
  >
    verb="POST"
    path={`/advanceNotification/${id}/attachment`}
    {...props}
  />
);

export type UseCreateAdvanceNotificationAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateAdvanceNotificationAttachmentPathParams
  >,
  "path" | "verb"
> &
  CreateAdvanceNotificationAttachmentPathParams;

/**
 * Add attachment to an advance notification
 */
export const useCreateAdvanceNotificationAttachment = ({
  id,
  ...props
}: UseCreateAdvanceNotificationAttachmentProps) =>
  useMutate<
    Attachment,
    {
      message?: string;
    },
    void,
    void,
    CreateAdvanceNotificationAttachmentPathParams
  >(
    "POST",
    (paramsInPath: CreateAdvanceNotificationAttachmentPathParams) =>
      `/advanceNotification/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface GetAdvanceNotificationAttachmentsPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
}

export type GetAdvanceNotificationAttachmentsProps = Omit<
  GetProps<
    Attachment[],
    unknown,
    void,
    GetAdvanceNotificationAttachmentsPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentsPathParams;

/**
 * Search for Advance Notification Attachments
 */
export const GetAdvanceNotificationAttachments = ({
  id,
  ...props
}: GetAdvanceNotificationAttachmentsProps) => (
  <Get<Attachment[], unknown, void, GetAdvanceNotificationAttachmentsPathParams>
    path={`/advanceNotification/${id}/attachment`}
    {...props}
  />
);

export type UseGetAdvanceNotificationAttachmentsProps = Omit<
  UseGetProps<
    Attachment[],
    unknown,
    void,
    GetAdvanceNotificationAttachmentsPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentsPathParams;

/**
 * Search for Advance Notification Attachments
 */
export const useGetAdvanceNotificationAttachments = ({
  id,
  ...props
}: UseGetAdvanceNotificationAttachmentsProps) =>
  useGet<
    Attachment[],
    unknown,
    void,
    GetAdvanceNotificationAttachmentsPathParams
  >(
    (paramsInPath: GetAdvanceNotificationAttachmentsPathParams) =>
      `/advanceNotification/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestNotificationsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetServiceRequestNotificationsProps = Omit<
  GetProps<
    (
      | InternallyGeneratedLetterNotification
      | ExternallyGeneratedLetterNotification
      | FaxNotification
    )[],
    unknown,
    void,
    GetServiceRequestNotificationsPathParams
  >,
  "path"
> &
  GetServiceRequestNotificationsPathParams;

/**
 * Search for Service Request Notifications
 */
export const GetServiceRequestNotifications = ({
  serviceRequestId,
  ...props
}: GetServiceRequestNotificationsProps) => (
  <Get<
    (
      | InternallyGeneratedLetterNotification
      | ExternallyGeneratedLetterNotification
      | FaxNotification
    )[],
    unknown,
    void,
    GetServiceRequestNotificationsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/notification`}
    {...props}
  />
);

export type UseGetServiceRequestNotificationsProps = Omit<
  UseGetProps<
    (
      | InternallyGeneratedLetterNotification
      | ExternallyGeneratedLetterNotification
      | FaxNotification
    )[],
    unknown,
    void,
    GetServiceRequestNotificationsPathParams
  >,
  "path"
> &
  GetServiceRequestNotificationsPathParams;

/**
 * Search for Service Request Notifications
 */
export const useGetServiceRequestNotifications = ({
  serviceRequestId,
  ...props
}: UseGetServiceRequestNotificationsProps) =>
  useGet<
    (
      | InternallyGeneratedLetterNotification
      | ExternallyGeneratedLetterNotification
      | FaxNotification
    )[],
    unknown,
    void,
    GetServiceRequestNotificationsPathParams
  >(
    (paramsInPath: GetServiceRequestNotificationsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/notification`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetNotificationDownloadPathParams {
  /**
   * A unique identifier for a `Notification`.
   */
  id: string;
}

export type GetNotificationDownloadProps = Omit<
  GetProps<void, unknown, void, GetNotificationDownloadPathParams>,
  "path"
> &
  GetNotificationDownloadPathParams;

/**
 * Download a notification
 *
 * Downloads an existing `Notification`.
 */
export const GetNotificationDownload = ({
  id,
  ...props
}: GetNotificationDownloadProps) => (
  <Get<void, unknown, void, GetNotificationDownloadPathParams>
    path={`/notification/${id}/download`}
    {...props}
  />
);

export type UseGetNotificationDownloadProps = Omit<
  UseGetProps<void, unknown, void, GetNotificationDownloadPathParams>,
  "path"
> &
  GetNotificationDownloadPathParams;

/**
 * Download a notification
 *
 * Downloads an existing `Notification`.
 */
export const useGetNotificationDownload = ({
  id,
  ...props
}: UseGetNotificationDownloadProps) =>
  useGet<void, unknown, void, GetNotificationDownloadPathParams>(
    (paramsInPath: GetNotificationDownloadPathParams) =>
      `/notification/${paramsInPath.id}/download`,
    { pathParams: { id }, ...props }
  );

export interface SendFaxResponse {
  isSuccess?: boolean;
  jobId?: string;
  error?: string;
  errorReason?: string;
  url?: string;
}

export type SendFaxProps = Omit<
  MutateProps<SendFaxResponse, unknown, void, void, void>,
  "path" | "verb"
>;

/**
 * Sends fax to fax number via fax vendor
 */
export const SendFax = (props: SendFaxProps) => (
  <Mutate<SendFaxResponse, unknown, void, void, void>
    verb="POST"
    path={`/fax/send`}
    {...props}
  />
);

export type UseSendFaxProps = Omit<
  UseMutateProps<SendFaxResponse, unknown, void, void, void>,
  "path" | "verb"
>;

/**
 * Sends fax to fax number via fax vendor
 */
export const useSendFax = (props: UseSendFaxProps) =>
  useMutate<SendFaxResponse, unknown, void, void, void>(
    "POST",
    `/fax/send`,
    props
  );

export interface ForwardFaxResponse {
  isSuccess?: boolean;
  jobId?: string;
  error?: string;
  errorReason?: string;
  url?: string;
}

export interface ForwardFaxRequestBody {
  faxNumber?: string;
  recipientName?: string;
  attachmentId?: string;
  serviceCaseId?: string;
}

export type ForwardFaxProps = Omit<
  MutateProps<ForwardFaxResponse, unknown, void, ForwardFaxRequestBody, void>,
  "path" | "verb"
>;

/**
 * Forwards fax from attachment id to fax number
 */
export const ForwardFax = (props: ForwardFaxProps) => (
  <Mutate<ForwardFaxResponse, unknown, void, ForwardFaxRequestBody, void>
    verb="POST"
    path={`/fax/forward`}
    {...props}
  />
);

export type UseForwardFaxProps = Omit<
  UseMutateProps<
    ForwardFaxResponse,
    unknown,
    void,
    ForwardFaxRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Forwards fax from attachment id to fax number
 */
export const useForwardFax = (props: UseForwardFaxProps) =>
  useMutate<ForwardFaxResponse, unknown, void, ForwardFaxRequestBody, void>(
    "POST",
    `/fax/forward`,
    props
  );

export interface GetFaxAttachmentPathParams {
  /**
   * A unique identifier for a fax `Attachment`.
   */
  id: string;
}

export type GetFaxAttachmentProps = Omit<
  GetProps<FaxAttachment, unknown, void, GetFaxAttachmentPathParams>,
  "path"
> &
  GetFaxAttachmentPathParams;

/**
 * Get a fax Attachment
 *
 * Gets an existing `Attachment`.
 */
export const GetFaxAttachment = ({ id, ...props }: GetFaxAttachmentProps) => (
  <Get<FaxAttachment, unknown, void, GetFaxAttachmentPathParams>
    path={`/faxAttachment/${id}`}
    {...props}
  />
);

export type UseGetFaxAttachmentProps = Omit<
  UseGetProps<FaxAttachment, unknown, void, GetFaxAttachmentPathParams>,
  "path"
> &
  GetFaxAttachmentPathParams;

/**
 * Get a fax Attachment
 *
 * Gets an existing `Attachment`.
 */
export const useGetFaxAttachment = ({
  id,
  ...props
}: UseGetFaxAttachmentProps) =>
  useGet<FaxAttachment, unknown, void, GetFaxAttachmentPathParams>(
    (paramsInPath: GetFaxAttachmentPathParams) =>
      `/faxAttachment/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetFaxAttachmentDownloadQueryParams {
  preferOcrEnhanced?: boolean;
}

export interface GetFaxAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `Attachment`.
   */
  id: string;
}

export type GetFaxAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    GetFaxAttachmentDownloadQueryParams,
    GetFaxAttachmentDownloadPathParams
  >,
  "path"
> &
  GetFaxAttachmentDownloadPathParams;

/**
 * Download a fax Attachment
 *
 * Downloads an existing `Attachment`.
 */
export const GetFaxAttachmentDownload = ({
  id,
  ...props
}: GetFaxAttachmentDownloadProps) => (
  <Get<
    void,
    unknown,
    GetFaxAttachmentDownloadQueryParams,
    GetFaxAttachmentDownloadPathParams
  >
    path={`/faxAttachment/${id}/download`}
    {...props}
  />
);

export type UseGetFaxAttachmentDownloadProps = Omit<
  UseGetProps<
    void,
    unknown,
    GetFaxAttachmentDownloadQueryParams,
    GetFaxAttachmentDownloadPathParams
  >,
  "path"
> &
  GetFaxAttachmentDownloadPathParams;

/**
 * Download a fax Attachment
 *
 * Downloads an existing `Attachment`.
 */
export const useGetFaxAttachmentDownload = ({
  id,
  ...props
}: UseGetFaxAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    GetFaxAttachmentDownloadQueryParams,
    GetFaxAttachmentDownloadPathParams
  >(
    (paramsInPath: GetFaxAttachmentDownloadPathParams) =>
      `/faxAttachment/${paramsInPath.id}/download`,
    { pathParams: { id }, ...props }
  );

export interface DownloadTatExtensionLetterAttachmentPathParams {
  /**
   * A unique identifier for a `TatExtensionLetterAttachment`.
   */
  id: string;
}

export type DownloadTatExtensionLetterAttachmentProps = Omit<
  GetProps<void, unknown, void, DownloadTatExtensionLetterAttachmentPathParams>,
  "path"
> &
  DownloadTatExtensionLetterAttachmentPathParams;

/**
 * Download a TAT Extension Letter Attachment
 *
 * Downloads an existing `TatExtensionLetterAttachment`.
 */
export const DownloadTatExtensionLetterAttachment = ({
  id,
  ...props
}: DownloadTatExtensionLetterAttachmentProps) => (
  <Get<void, unknown, void, DownloadTatExtensionLetterAttachmentPathParams>
    path={`/tatExtensionLetterAttachment/${id}/download`}
    {...props}
  />
);

export type UseDownloadTatExtensionLetterAttachmentProps = Omit<
  UseGetProps<
    void,
    unknown,
    void,
    DownloadTatExtensionLetterAttachmentPathParams
  >,
  "path"
> &
  DownloadTatExtensionLetterAttachmentPathParams;

/**
 * Download a TAT Extension Letter Attachment
 *
 * Downloads an existing `TatExtensionLetterAttachment`.
 */
export const useDownloadTatExtensionLetterAttachment = ({
  id,
  ...props
}: UseDownloadTatExtensionLetterAttachmentProps) =>
  useGet<void, unknown, void, DownloadTatExtensionLetterAttachmentPathParams>(
    (paramsInPath: DownloadTatExtensionLetterAttachmentPathParams) =>
      `/tatExtensionLetterAttachment/${paramsInPath.id}/download`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetServiceRequestAttachmentProps = Omit<
  GetProps<Attachment, unknown, void, GetServiceRequestAttachmentPathParams>,
  "path"
> &
  GetServiceRequestAttachmentPathParams;

/**
 * Get an Attachment associated with a Service Request.
 *
 * Gets an existing `Attachment`.
 */
export const GetServiceRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: GetServiceRequestAttachmentProps) => (
  <Get<Attachment, unknown, void, GetServiceRequestAttachmentPathParams>
    path={`/serviceRequest/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseGetServiceRequestAttachmentProps = Omit<
  UseGetProps<Attachment, unknown, void, GetServiceRequestAttachmentPathParams>,
  "path"
> &
  GetServiceRequestAttachmentPathParams;

/**
 * Get an Attachment associated with a Service Request.
 *
 * Gets an existing `Attachment`.
 */
export const useGetServiceRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: UseGetServiceRequestAttachmentProps) =>
  useGet<Attachment, unknown, void, GetServiceRequestAttachmentPathParams>(
    (paramsInPath: GetServiceRequestAttachmentPathParams) =>
      `/serviceRequest/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface DeleteServiceRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type DeleteServiceRequestAttachmentProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    string,
    DeleteServiceRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteServiceRequestAttachmentPathParams;

/**
 * Delete a Attachment
 *
 * Deletes an existing `Attachment`.
 */
export const DeleteServiceRequestAttachment = ({
  id,
  ...props
}: DeleteServiceRequestAttachmentProps) => (
  <Mutate<void, unknown, void, string, DeleteServiceRequestAttachmentPathParams>
    verb="DELETE"
    path={`/serviceRequest/${id}/attachment`}
    {...props}
  />
);

export type UseDeleteServiceRequestAttachmentProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteServiceRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteServiceRequestAttachmentPathParams;

/**
 * Delete a Attachment
 *
 * Deletes an existing `Attachment`.
 */
export const useDeleteServiceRequestAttachment = ({
  id,
  ...props
}: UseDeleteServiceRequestAttachmentProps) =>
  useMutate<
    void,
    unknown,
    void,
    string,
    DeleteServiceRequestAttachmentPathParams
  >(
    "DELETE",
    (paramsInPath: DeleteServiceRequestAttachmentPathParams) =>
      `/serviceRequest/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface UpdateServiceRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type UpdateServiceRequestAttachmentProps = Omit<
  MutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateServiceRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestAttachmentPathParams;

export const UpdateServiceRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: UpdateServiceRequestAttachmentProps) => (
  <Mutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateServiceRequestAttachmentPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseUpdateServiceRequestAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateServiceRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestAttachmentPathParams;

export const useUpdateServiceRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: UseUpdateServiceRequestAttachmentProps) =>
  useMutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateServiceRequestAttachmentPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateServiceRequestAttachmentPathParams) =>
      `/serviceRequest/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetReferralRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetReferralRequestAttachmentProps = Omit<
  GetProps<Attachment, unknown, void, GetReferralRequestAttachmentPathParams>,
  "path"
> &
  GetReferralRequestAttachmentPathParams;

/**
 * Get an Attachment associated with a Referral Request.
 *
 * Gets an existing `Attachment`.
 */
export const GetReferralRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: GetReferralRequestAttachmentProps) => (
  <Get<Attachment, unknown, void, GetReferralRequestAttachmentPathParams>
    path={`/referralRequest/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseGetReferralRequestAttachmentProps = Omit<
  UseGetProps<
    Attachment,
    unknown,
    void,
    GetReferralRequestAttachmentPathParams
  >,
  "path"
> &
  GetReferralRequestAttachmentPathParams;

/**
 * Get an Attachment associated with a Referral Request.
 *
 * Gets an existing `Attachment`.
 */
export const useGetReferralRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: UseGetReferralRequestAttachmentProps) =>
  useGet<Attachment, unknown, void, GetReferralRequestAttachmentPathParams>(
    (paramsInPath: GetReferralRequestAttachmentPathParams) =>
      `/referralRequest/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface DeleteReferralRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
}

export type DeleteReferralRequestAttachmentProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    string,
    DeleteReferralRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteReferralRequestAttachmentPathParams;

/**
 * Delete a Attachment
 *
 * Deletes an existing `Attachment`.
 */
export const DeleteReferralRequestAttachment = ({
  id,
  ...props
}: DeleteReferralRequestAttachmentProps) => (
  <Mutate<
    void,
    unknown,
    void,
    string,
    DeleteReferralRequestAttachmentPathParams
  >
    verb="DELETE"
    path={`/referralRequest/${id}/attachment`}
    {...props}
  />
);

export type UseDeleteReferralRequestAttachmentProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteReferralRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteReferralRequestAttachmentPathParams;

/**
 * Delete a Attachment
 *
 * Deletes an existing `Attachment`.
 */
export const useDeleteReferralRequestAttachment = ({
  id,
  ...props
}: UseDeleteReferralRequestAttachmentProps) =>
  useMutate<
    void,
    unknown,
    void,
    string,
    DeleteReferralRequestAttachmentPathParams
  >(
    "DELETE",
    (paramsInPath: DeleteReferralRequestAttachmentPathParams) =>
      `/referralRequest/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface UpdateReferralRequestAttachmentPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type UpdateReferralRequestAttachmentProps = Omit<
  MutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateReferralRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateReferralRequestAttachmentPathParams;

export const UpdateReferralRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: UpdateReferralRequestAttachmentProps) => (
  <Mutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateReferralRequestAttachmentPathParams
  >
    verb="PATCH"
    path={`/referralRequest/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseUpdateReferralRequestAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateReferralRequestAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateReferralRequestAttachmentPathParams;

export const useUpdateReferralRequestAttachment = ({
  id,
  attachmentId,
  ...props
}: UseUpdateReferralRequestAttachmentProps) =>
  useMutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateReferralRequestAttachmentPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateReferralRequestAttachmentPathParams) =>
      `/referralRequest/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetAdvanceNotificationAttachmentPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetAdvanceNotificationAttachmentProps = Omit<
  GetProps<
    Attachment,
    unknown,
    void,
    GetAdvanceNotificationAttachmentPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentPathParams;

/**
 * Get an Attachment associated with a AdvanceNotification
 *
 * Gets an existing `Attachment`.
 */
export const GetAdvanceNotificationAttachment = ({
  id,
  attachmentId,
  ...props
}: GetAdvanceNotificationAttachmentProps) => (
  <Get<Attachment, unknown, void, GetAdvanceNotificationAttachmentPathParams>
    path={`/advanceNotification/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseGetAdvanceNotificationAttachmentProps = Omit<
  UseGetProps<
    Attachment,
    unknown,
    void,
    GetAdvanceNotificationAttachmentPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentPathParams;

/**
 * Get an Attachment associated with a AdvanceNotification
 *
 * Gets an existing `Attachment`.
 */
export const useGetAdvanceNotificationAttachment = ({
  id,
  attachmentId,
  ...props
}: UseGetAdvanceNotificationAttachmentProps) =>
  useGet<Attachment, unknown, void, GetAdvanceNotificationAttachmentPathParams>(
    (paramsInPath: GetAdvanceNotificationAttachmentPathParams) =>
      `/advanceNotification/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface DeleteAdvanceNotificationAttachmentPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
}

export type DeleteAdvanceNotificationAttachmentProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    string,
    DeleteAdvanceNotificationAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteAdvanceNotificationAttachmentPathParams;

/**
 * Delete a Attachment associated with a AdvanceNotification
 *
 * Deletes an existing `Attachment`.
 */
export const DeleteAdvanceNotificationAttachment = ({
  id,
  ...props
}: DeleteAdvanceNotificationAttachmentProps) => (
  <Mutate<
    void,
    unknown,
    void,
    string,
    DeleteAdvanceNotificationAttachmentPathParams
  >
    verb="DELETE"
    path={`/advanceNotification/${id}/attachment`}
    {...props}
  />
);

export type UseDeleteAdvanceNotificationAttachmentProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    string,
    DeleteAdvanceNotificationAttachmentPathParams
  >,
  "path" | "verb"
> &
  DeleteAdvanceNotificationAttachmentPathParams;

/**
 * Delete a Attachment associated with a AdvanceNotification
 *
 * Deletes an existing `Attachment`.
 */
export const useDeleteAdvanceNotificationAttachment = ({
  id,
  ...props
}: UseDeleteAdvanceNotificationAttachmentProps) =>
  useMutate<
    void,
    unknown,
    void,
    string,
    DeleteAdvanceNotificationAttachmentPathParams
  >(
    "DELETE",
    (paramsInPath: DeleteAdvanceNotificationAttachmentPathParams) =>
      `/advanceNotification/${paramsInPath.id}/attachment`,
    { pathParams: { id }, ...props }
  );

export interface UpdateAdvanceNotificationAttachmentPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type UpdateAdvanceNotificationAttachmentProps = Omit<
  MutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateAdvanceNotificationAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateAdvanceNotificationAttachmentPathParams;

export const UpdateAdvanceNotificationAttachment = ({
  id,
  attachmentId,
  ...props
}: UpdateAdvanceNotificationAttachmentProps) => (
  <Mutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateAdvanceNotificationAttachmentPathParams
  >
    verb="PATCH"
    path={`/advanceNotification/${id}/attachment/${attachmentId}`}
    {...props}
  />
);

export type UseUpdateAdvanceNotificationAttachmentProps = Omit<
  UseMutateProps<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateAdvanceNotificationAttachmentPathParams
  >,
  "path" | "verb"
> &
  UpdateAdvanceNotificationAttachmentPathParams;

export const useUpdateAdvanceNotificationAttachment = ({
  id,
  attachmentId,
  ...props
}: UseUpdateAdvanceNotificationAttachmentProps) =>
  useMutate<
    Attachment,
    unknown,
    void,
    AttachmentUpdatePayload,
    UpdateAdvanceNotificationAttachmentPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateAdvanceNotificationAttachmentPathParams) =>
      `/advanceNotification/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetServiceRequestAttachmentDownloadQueryParams {
  preferOcrEnhanced?: boolean;
}

export interface GetServiceRequestAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetServiceRequestAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    GetServiceRequestAttachmentDownloadQueryParams,
    GetServiceRequestAttachmentDownloadPathParams
  >,
  "path"
> &
  GetServiceRequestAttachmentDownloadPathParams;

/**
 * Download an Attachment associated with a Service Request
 *
 * Downloads an existing `Attachment`.
 */
export const GetServiceRequestAttachmentDownload = ({
  id,
  attachmentId,
  ...props
}: GetServiceRequestAttachmentDownloadProps) => (
  <Get<
    void,
    unknown,
    GetServiceRequestAttachmentDownloadQueryParams,
    GetServiceRequestAttachmentDownloadPathParams
  >
    path={`/serviceRequest/${id}/attachment/${attachmentId}/download`}
    {...props}
  />
);

export type UseGetServiceRequestAttachmentDownloadProps = Omit<
  UseGetProps<
    void,
    unknown,
    GetServiceRequestAttachmentDownloadQueryParams,
    GetServiceRequestAttachmentDownloadPathParams
  >,
  "path"
> &
  GetServiceRequestAttachmentDownloadPathParams;

/**
 * Download an Attachment associated with a Service Request
 *
 * Downloads an existing `Attachment`.
 */
export const useGetServiceRequestAttachmentDownload = ({
  id,
  attachmentId,
  ...props
}: UseGetServiceRequestAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    GetServiceRequestAttachmentDownloadQueryParams,
    GetServiceRequestAttachmentDownloadPathParams
  >(
    (paramsInPath: GetServiceRequestAttachmentDownloadPathParams) =>
      `/serviceRequest/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}/download`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetAdvanceNotificationAttachmentDownloadPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
  /**
   * A unique identifier for a `Attachment`.
   */
  attachmentId: string;
}

export type GetAdvanceNotificationAttachmentDownloadProps = Omit<
  GetProps<
    void,
    unknown,
    void,
    GetAdvanceNotificationAttachmentDownloadPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentDownloadPathParams;

/**
 * Download an Attachment associated with a Advance Notification
 *
 * Downloads an existing `Attachment`.
 */
export const GetAdvanceNotificationAttachmentDownload = ({
  id,
  attachmentId,
  ...props
}: GetAdvanceNotificationAttachmentDownloadProps) => (
  <Get<void, unknown, void, GetAdvanceNotificationAttachmentDownloadPathParams>
    path={`/advanceNotification/${id}/attachment/${attachmentId}/download`}
    {...props}
  />
);

export type UseGetAdvanceNotificationAttachmentDownloadProps = Omit<
  UseGetProps<
    void,
    unknown,
    void,
    GetAdvanceNotificationAttachmentDownloadPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentDownloadPathParams;

/**
 * Download an Attachment associated with a Advance Notification
 *
 * Downloads an existing `Attachment`.
 */
export const useGetAdvanceNotificationAttachmentDownload = ({
  id,
  attachmentId,
  ...props
}: UseGetAdvanceNotificationAttachmentDownloadProps) =>
  useGet<
    void,
    unknown,
    void,
    GetAdvanceNotificationAttachmentDownloadPathParams
  >(
    (paramsInPath: GetAdvanceNotificationAttachmentDownloadPathParams) =>
      `/advanceNotification/${paramsInPath.id}/attachment/${paramsInPath.attachmentId}/download`,
    { pathParams: { id, attachmentId }, ...props }
  );

export interface GetAllAdvanceNotificationAttachmentsPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  advanceNotificationId: string;
}

export type GetAllAdvanceNotificationAttachmentsProps = Omit<
  GetProps<
    Attachment[],
    unknown,
    void,
    GetAllAdvanceNotificationAttachmentsPathParams
  >,
  "path"
> &
  GetAllAdvanceNotificationAttachmentsPathParams;

/**
 * Get a list of attachments (sorted in descending order by dateCreated) for this AN
 *
 */
export const GetAllAdvanceNotificationAttachments = ({
  advanceNotificationId,
  ...props
}: GetAllAdvanceNotificationAttachmentsProps) => (
  <Get<
    Attachment[],
    unknown,
    void,
    GetAllAdvanceNotificationAttachmentsPathParams
  >
    path={`/advanceNotification/${advanceNotificationId}/attachments`}
    {...props}
  />
);

export type UseGetAllAdvanceNotificationAttachmentsProps = Omit<
  UseGetProps<
    Attachment[],
    unknown,
    void,
    GetAllAdvanceNotificationAttachmentsPathParams
  >,
  "path"
> &
  GetAllAdvanceNotificationAttachmentsPathParams;

/**
 * Get a list of attachments (sorted in descending order by dateCreated) for this AN
 *
 */
export const useGetAllAdvanceNotificationAttachments = ({
  advanceNotificationId,
  ...props
}: UseGetAllAdvanceNotificationAttachmentsProps) =>
  useGet<
    Attachment[],
    unknown,
    void,
    GetAllAdvanceNotificationAttachmentsPathParams
  >(
    (paramsInPath: GetAllAdvanceNotificationAttachmentsPathParams) =>
      `/advanceNotification/${paramsInPath.advanceNotificationId}/attachments`,
    { pathParams: { advanceNotificationId }, ...props }
  );

export interface UpdateDocSegmentationPathParams {
  /**
   * A unique identifier for an `Attachment`.
   */
  attachmentId: string;
}

export interface UpdateDocSegmentationRequestBody {
  attachmentId?: string;
  serviceRequestId?: string;
  carePathJourneyId?: string;
  segments: Segment[];
}

export type UpdateDocSegmentationProps = Omit<
  MutateProps<
    DocSegmentation,
    unknown,
    void,
    UpdateDocSegmentationRequestBody,
    UpdateDocSegmentationPathParams
  >,
  "path" | "verb"
> &
  UpdateDocSegmentationPathParams;

/**
 * Sends feedback Doc Segmentation data for an attachment
 */
export const UpdateDocSegmentation = ({
  attachmentId,
  ...props
}: UpdateDocSegmentationProps) => (
  <Mutate<
    DocSegmentation,
    unknown,
    void,
    UpdateDocSegmentationRequestBody,
    UpdateDocSegmentationPathParams
  >
    verb="PATCH"
    path={`/docSegmentation/${attachmentId}`}
    {...props}
  />
);

export type UseUpdateDocSegmentationProps = Omit<
  UseMutateProps<
    DocSegmentation,
    unknown,
    void,
    UpdateDocSegmentationRequestBody,
    UpdateDocSegmentationPathParams
  >,
  "path" | "verb"
> &
  UpdateDocSegmentationPathParams;

/**
 * Sends feedback Doc Segmentation data for an attachment
 */
export const useUpdateDocSegmentation = ({
  attachmentId,
  ...props
}: UseUpdateDocSegmentationProps) =>
  useMutate<
    DocSegmentation,
    unknown,
    void,
    UpdateDocSegmentationRequestBody,
    UpdateDocSegmentationPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateDocSegmentationPathParams) =>
      `/docSegmentation/${paramsInPath.attachmentId}`,
    { pathParams: { attachmentId }, ...props }
  );

export interface GetDocSegmentationPathParams {
  /**
   * A unique identifier for an `Attachment`.
   */
  attachmentId: string;
}

export type GetDocSegmentationProps = Omit<
  GetProps<DocSegmentation, unknown, void, GetDocSegmentationPathParams>,
  "path"
> &
  GetDocSegmentationPathParams;

/**
 * Get ML Doc Segmentation data for a given Attachment
 */
export const GetDocSegmentation = ({
  attachmentId,
  ...props
}: GetDocSegmentationProps) => (
  <Get<DocSegmentation, unknown, void, GetDocSegmentationPathParams>
    path={`/docSegmentation/${attachmentId}`}
    {...props}
  />
);

export type UseGetDocSegmentationProps = Omit<
  UseGetProps<DocSegmentation, unknown, void, GetDocSegmentationPathParams>,
  "path"
> &
  GetDocSegmentationPathParams;

/**
 * Get ML Doc Segmentation data for a given Attachment
 */
export const useGetDocSegmentation = ({
  attachmentId,
  ...props
}: UseGetDocSegmentationProps) =>
  useGet<DocSegmentation, unknown, void, GetDocSegmentationPathParams>(
    (paramsInPath: GetDocSegmentationPathParams) =>
      `/docSegmentation/${paramsInPath.attachmentId}`,
    { pathParams: { attachmentId }, ...props }
  );

export interface GetDocSegmentationsQueryParams {
  attachmentIds: string[];
}

export type GetDocSegmentationsProps = Omit<
  GetProps<DocSegmentation[], unknown, GetDocSegmentationsQueryParams, void>,
  "path"
>;

export const GetDocSegmentations = (props: GetDocSegmentationsProps) => (
  <Get<DocSegmentation[], unknown, GetDocSegmentationsQueryParams, void>
    path={`/docSegmentations`}
    {...props}
  />
);

export type UseGetDocSegmentationsProps = Omit<
  UseGetProps<DocSegmentation[], unknown, GetDocSegmentationsQueryParams, void>,
  "path"
>;

export const useGetDocSegmentations = (props: UseGetDocSegmentationsProps) =>
  useGet<DocSegmentation[], unknown, GetDocSegmentationsQueryParams, void>(
    `/docSegmentations`,
    props
  );

export interface GetLatestAttachmentHighlightsForSRPathParams {
  reviewId: string;
}

export type GetLatestAttachmentHighlightsForSRProps = Omit<
  GetProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    GetLatestAttachmentHighlightsForSRPathParams
  >,
  "path"
> &
  GetLatestAttachmentHighlightsForSRPathParams;

/**
 * Returns highlights from latest SR review if any or from ML predictions
 */
export const GetLatestAttachmentHighlightsForSR = ({
  reviewId,
  ...props
}: GetLatestAttachmentHighlightsForSRProps) => (
  <Get<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    GetLatestAttachmentHighlightsForSRPathParams
  >
    path={`/attachmentGuidelineTextHighlight/serviceRequest/${reviewId}`}
    {...props}
  />
);

export type UseGetLatestAttachmentHighlightsForSRProps = Omit<
  UseGetProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    GetLatestAttachmentHighlightsForSRPathParams
  >,
  "path"
> &
  GetLatestAttachmentHighlightsForSRPathParams;

/**
 * Returns highlights from latest SR review if any or from ML predictions
 */
export const useGetLatestAttachmentHighlightsForSR = ({
  reviewId,
  ...props
}: UseGetLatestAttachmentHighlightsForSRProps) =>
  useGet<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    GetLatestAttachmentHighlightsForSRPathParams
  >(
    (paramsInPath: GetLatestAttachmentHighlightsForSRPathParams) =>
      `/attachmentGuidelineTextHighlight/serviceRequest/${paramsInPath.reviewId}`,
    { pathParams: { reviewId }, ...props }
  );

export interface GetAttachmentHighlightsQueryParams {
  reviewId?: string;
  serviceRequestId?: string;
  guidelineId?: string;
  max?: number;
  sort?: string;
  order?: "asc" | "desc";
}

export type GetAttachmentHighlightsProps = Omit<
  GetProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    GetAttachmentHighlightsQueryParams,
    void
  >,
  "path"
>;

export const GetAttachmentHighlights = (
  props: GetAttachmentHighlightsProps
) => (
  <Get<
    AttachmentGuidelineTextHighlight[],
    unknown,
    GetAttachmentHighlightsQueryParams,
    void
  >
    path={`/attachmentGuidelineTextHighlight`}
    {...props}
  />
);

export type UseGetAttachmentHighlightsProps = Omit<
  UseGetProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    GetAttachmentHighlightsQueryParams,
    void
  >,
  "path"
>;

export const useGetAttachmentHighlights = (
  props: UseGetAttachmentHighlightsProps
) =>
  useGet<
    AttachmentGuidelineTextHighlight[],
    unknown,
    GetAttachmentHighlightsQueryParams,
    void
  >(`/attachmentGuidelineTextHighlight`, props);

export type PostUserHighlightsProps = Omit<
  MutateProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >,
  "path" | "verb"
>;

/**
 * Sends feedback attachmentGuidelineTextHighlight data for guidelines and attachments under a serviceRequest
 */
export const PostUserHighlights = (props: PostUserHighlightsProps) => (
  <Mutate<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >
    verb="POST"
    path={`/attachmentGuidelineTextHighlight`}
    {...props}
  />
);

export type UsePostUserHighlightsProps = Omit<
  UseMutateProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >,
  "path" | "verb"
>;

/**
 * Sends feedback attachmentGuidelineTextHighlight data for guidelines and attachments under a serviceRequest
 */
export const usePostUserHighlights = (props: UsePostUserHighlightsProps) =>
  useMutate<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >("POST", `/attachmentGuidelineTextHighlight`, props);

export type PutUserHighlightsProps = Omit<
  MutateProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >,
  "path" | "verb"
>;

/**
 * Sends feedback attachmentGuidelineTextHighlight data for guidelines and attachments under a serviceRequest as draft
 */
export const PutUserHighlights = (props: PutUserHighlightsProps) => (
  <Mutate<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >
    verb="PUT"
    path={`/attachmentGuidelineTextHighlight`}
    {...props}
  />
);

export type UsePutUserHighlightsProps = Omit<
  UseMutateProps<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >,
  "path" | "verb"
>;

/**
 * Sends feedback attachmentGuidelineTextHighlight data for guidelines and attachments under a serviceRequest as draft
 */
export const usePutUserHighlights = (props: UsePutUserHighlightsProps) =>
  useMutate<
    AttachmentGuidelineTextHighlight[],
    unknown,
    void,
    AttachmentGuidelineTextHighlight[],
    void
  >("PUT", `/attachmentGuidelineTextHighlight`, props);

export interface GetServiceRequestAttachmentAllowedFileTypesResponse {
  allowedFileExtensions?: string[];
}

export interface GetServiceRequestAttachmentAllowedFileTypesPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetServiceRequestAttachmentAllowedFileTypesProps = Omit<
  GetProps<
    GetServiceRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetServiceRequestAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetServiceRequestAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given service request
 */
export const GetServiceRequestAttachmentAllowedFileTypes = ({
  serviceRequestId,
  ...props
}: GetServiceRequestAttachmentAllowedFileTypesProps) => (
  <Get<
    GetServiceRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetServiceRequestAttachmentAllowedFileTypesPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/attachment/allowedFileTypes`}
    {...props}
  />
);

export type UseGetServiceRequestAttachmentAllowedFileTypesProps = Omit<
  UseGetProps<
    GetServiceRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetServiceRequestAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetServiceRequestAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given service request
 */
export const useGetServiceRequestAttachmentAllowedFileTypes = ({
  serviceRequestId,
  ...props
}: UseGetServiceRequestAttachmentAllowedFileTypesProps) =>
  useGet<
    GetServiceRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetServiceRequestAttachmentAllowedFileTypesPathParams
  >(
    (paramsInPath: GetServiceRequestAttachmentAllowedFileTypesPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/attachment/allowedFileTypes`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetReferralRequestAttachmentAllowedFileTypesResponse {
  allowedFileExtensions?: string[];
}

export interface GetReferralRequestAttachmentAllowedFileTypesPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  referralRequestId: string;
}

export type GetReferralRequestAttachmentAllowedFileTypesProps = Omit<
  GetProps<
    GetReferralRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetReferralRequestAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetReferralRequestAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given referral request
 */
export const GetReferralRequestAttachmentAllowedFileTypes = ({
  referralRequestId,
  ...props
}: GetReferralRequestAttachmentAllowedFileTypesProps) => (
  <Get<
    GetReferralRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetReferralRequestAttachmentAllowedFileTypesPathParams
  >
    path={`/referralRequest/${referralRequestId}/attachment/allowedFileTypes`}
    {...props}
  />
);

export type UseGetReferralRequestAttachmentAllowedFileTypesProps = Omit<
  UseGetProps<
    GetReferralRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetReferralRequestAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetReferralRequestAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given referral request
 */
export const useGetReferralRequestAttachmentAllowedFileTypes = ({
  referralRequestId,
  ...props
}: UseGetReferralRequestAttachmentAllowedFileTypesProps) =>
  useGet<
    GetReferralRequestAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetReferralRequestAttachmentAllowedFileTypesPathParams
  >(
    (paramsInPath: GetReferralRequestAttachmentAllowedFileTypesPathParams) =>
      `/referralRequest/${paramsInPath.referralRequestId}/attachment/allowedFileTypes`,
    { pathParams: { referralRequestId }, ...props }
  );

export interface GetAdvanceNotificationAttachmentAllowedFileTypesResponse {
  allowedFileExtensions?: string[];
}

export interface GetAdvanceNotificationAttachmentAllowedFileTypesPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  advanceNotificationId: string;
}

export type GetAdvanceNotificationAttachmentAllowedFileTypesProps = Omit<
  GetProps<
    GetAdvanceNotificationAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetAdvanceNotificationAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given advance notification
 */
export const GetAdvanceNotificationAttachmentAllowedFileTypes = ({
  advanceNotificationId,
  ...props
}: GetAdvanceNotificationAttachmentAllowedFileTypesProps) => (
  <Get<
    GetAdvanceNotificationAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetAdvanceNotificationAttachmentAllowedFileTypesPathParams
  >
    path={`/advanceNotification/${advanceNotificationId}/attachment/allowedFileTypes`}
    {...props}
  />
);

export type UseGetAdvanceNotificationAttachmentAllowedFileTypesProps = Omit<
  UseGetProps<
    GetAdvanceNotificationAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetAdvanceNotificationAttachmentAllowedFileTypesPathParams
  >,
  "path"
> &
  GetAdvanceNotificationAttachmentAllowedFileTypesPathParams;

/**
 * Gets a list of allowed file types for attachments to a given advance notification
 */
export const useGetAdvanceNotificationAttachmentAllowedFileTypes = ({
  advanceNotificationId,
  ...props
}: UseGetAdvanceNotificationAttachmentAllowedFileTypesProps) =>
  useGet<
    GetAdvanceNotificationAttachmentAllowedFileTypesResponse,
    unknown,
    void,
    GetAdvanceNotificationAttachmentAllowedFileTypesPathParams
  >(
    (
      paramsInPath: GetAdvanceNotificationAttachmentAllowedFileTypesPathParams
    ) =>
      `/advanceNotification/${paramsInPath.advanceNotificationId}/attachment/allowedFileTypes`,
    { pathParams: { advanceNotificationId }, ...props }
  );

export interface GetServiceRequestRuleActionsQueryParams {
  /**
   * To specify which rules to run.
   */
  displayTarget?: DisplayTargetType;
  /**
   * If true, records the RRD audit run as NUDGE timing. No RRD persisted otherwise. Defaults to true
   */
  persistRuleRunDescription?: boolean;
  /**
   * The current workflow step of the auth builder. Needed for analytics
   */
  authStage?: AuthBuilderWorkflowStep;
}

export interface GetServiceRequestRuleActionsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestRuleActionsProps = Omit<
  GetProps<
    RuleActions,
    unknown,
    GetServiceRequestRuleActionsQueryParams,
    GetServiceRequestRuleActionsPathParams
  >,
  "path"
> &
  GetServiceRequestRuleActionsPathParams;

export const GetServiceRequestRuleActions = ({
  id,
  ...props
}: GetServiceRequestRuleActionsProps) => (
  <Get<
    RuleActions,
    unknown,
    GetServiceRequestRuleActionsQueryParams,
    GetServiceRequestRuleActionsPathParams
  >
    path={`/serviceRequest/${id}/ruleActions`}
    {...props}
  />
);

export type UseGetServiceRequestRuleActionsProps = Omit<
  UseGetProps<
    RuleActions,
    unknown,
    GetServiceRequestRuleActionsQueryParams,
    GetServiceRequestRuleActionsPathParams
  >,
  "path"
> &
  GetServiceRequestRuleActionsPathParams;

export const useGetServiceRequestRuleActions = ({
  id,
  ...props
}: UseGetServiceRequestRuleActionsProps) =>
  useGet<
    RuleActions,
    unknown,
    GetServiceRequestRuleActionsQueryParams,
    GetServiceRequestRuleActionsPathParams
  >(
    (paramsInPath: GetServiceRequestRuleActionsPathParams) =>
      `/serviceRequest/${paramsInPath.id}/ruleActions`,
    { pathParams: { id }, ...props }
  );

export interface GetBulkServiceRequestRuleActionsRequestBody {
  serviceRequestIds: string[];
  displayTarget: DisplayTargetType;
  authStage?: AuthBuilderWorkflowStep;
}

export type GetBulkServiceRequestRuleActionsProps = Omit<
  MutateProps<
    RuleActions,
    unknown,
    void,
    GetBulkServiceRequestRuleActionsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const GetBulkServiceRequestRuleActions = (
  props: GetBulkServiceRequestRuleActionsProps
) => (
  <Mutate<
    RuleActions,
    unknown,
    void,
    GetBulkServiceRequestRuleActionsRequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/bulkRuleActions`}
    {...props}
  />
);

export type UseGetBulkServiceRequestRuleActionsProps = Omit<
  UseMutateProps<
    RuleActions,
    unknown,
    void,
    GetBulkServiceRequestRuleActionsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useGetBulkServiceRequestRuleActions = (
  props: UseGetBulkServiceRequestRuleActionsProps
) =>
  useMutate<
    RuleActions,
    unknown,
    void,
    GetBulkServiceRequestRuleActionsRequestBody,
    void
  >("POST", `/serviceRequest/bulkRuleActions`, props);

export interface GetBulkSearchableStatementsRequestBody {
  serviceRequestIds: string[];
  displayTarget: DisplayTargetType;
  statement?: string;
}

export type GetBulkSearchableStatementsProps = Omit<
  MutateProps<
    SearchableStatements,
    unknown,
    void,
    GetBulkSearchableStatementsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const GetBulkSearchableStatements = (
  props: GetBulkSearchableStatementsProps
) => (
  <Mutate<
    SearchableStatements,
    unknown,
    void,
    GetBulkSearchableStatementsRequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/bulkSearchableStatements`}
    {...props}
  />
);

export type UseGetBulkSearchableStatementsProps = Omit<
  UseMutateProps<
    SearchableStatements,
    unknown,
    void,
    GetBulkSearchableStatementsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useGetBulkSearchableStatements = (
  props: UseGetBulkSearchableStatementsProps
) =>
  useMutate<
    SearchableStatements,
    unknown,
    void,
    GetBulkSearchableStatementsRequestBody,
    void
  >("POST", `/serviceRequest/bulkSearchableStatements`, props);

export interface GetTransientServiceRequestRuleActionsRequestBody {
  serviceRequest: TransientServiceRequest;
  displayTarget: DisplayTargetType;
  authStage?: AuthBuilderWorkflowStep;
}

export type GetTransientServiceRequestRuleActionsProps = Omit<
  MutateProps<
    RuleActions,
    unknown,
    void,
    GetTransientServiceRequestRuleActionsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const GetTransientServiceRequestRuleActions = (
  props: GetTransientServiceRequestRuleActionsProps
) => (
  <Mutate<
    RuleActions,
    unknown,
    void,
    GetTransientServiceRequestRuleActionsRequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/ruleActions`}
    {...props}
  />
);

export type UseGetTransientServiceRequestRuleActionsProps = Omit<
  UseMutateProps<
    RuleActions,
    unknown,
    void,
    GetTransientServiceRequestRuleActionsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useGetTransientServiceRequestRuleActions = (
  props: UseGetTransientServiceRequestRuleActionsProps
) =>
  useMutate<
    RuleActions,
    unknown,
    void,
    GetTransientServiceRequestRuleActionsRequestBody,
    void
  >("POST", `/serviceRequest/ruleActions`, props);

export interface StartHealthHelpIntegrationPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export interface StartHealthHelpIntegrationRequestBody {}

export type StartHealthHelpIntegrationProps = Omit<
  MutateProps<
    HealthHelpPASRequestResponse,
    unknown,
    void,
    StartHealthHelpIntegrationRequestBody,
    StartHealthHelpIntegrationPathParams
  >,
  "path" | "verb"
> &
  StartHealthHelpIntegrationPathParams;

export const StartHealthHelpIntegration = ({
  id,
  ...props
}: StartHealthHelpIntegrationProps) => (
  <Mutate<
    HealthHelpPASRequestResponse,
    unknown,
    void,
    StartHealthHelpIntegrationRequestBody,
    StartHealthHelpIntegrationPathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/startHealthHelpIntegration`}
    {...props}
  />
);

export type UseStartHealthHelpIntegrationProps = Omit<
  UseMutateProps<
    HealthHelpPASRequestResponse,
    unknown,
    void,
    StartHealthHelpIntegrationRequestBody,
    StartHealthHelpIntegrationPathParams
  >,
  "path" | "verb"
> &
  StartHealthHelpIntegrationPathParams;

export const useStartHealthHelpIntegration = ({
  id,
  ...props
}: UseStartHealthHelpIntegrationProps) =>
  useMutate<
    HealthHelpPASRequestResponse,
    unknown,
    void,
    StartHealthHelpIntegrationRequestBody,
    StartHealthHelpIntegrationPathParams
  >(
    "POST",
    (paramsInPath: StartHealthHelpIntegrationPathParams) =>
      `/serviceRequest/${paramsInPath.id}/startHealthHelpIntegration`,
    { pathParams: { id }, ...props }
  );

export interface WithdrawHealthHelpCasePathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export interface WithdrawHealthHelpCaseRequestBody {}

export type WithdrawHealthHelpCaseProps = Omit<
  MutateProps<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpCaseRequestBody,
    WithdrawHealthHelpCasePathParams
  >,
  "path" | "verb"
> &
  WithdrawHealthHelpCasePathParams;

export const WithdrawHealthHelpCase = ({
  id,
  ...props
}: WithdrawHealthHelpCaseProps) => (
  <Mutate<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpCaseRequestBody,
    WithdrawHealthHelpCasePathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/withdrawHealthHelpCase`}
    {...props}
  />
);

export type UseWithdrawHealthHelpCaseProps = Omit<
  UseMutateProps<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpCaseRequestBody,
    WithdrawHealthHelpCasePathParams
  >,
  "path" | "verb"
> &
  WithdrawHealthHelpCasePathParams;

export const useWithdrawHealthHelpCase = ({
  id,
  ...props
}: UseWithdrawHealthHelpCaseProps) =>
  useMutate<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpCaseRequestBody,
    WithdrawHealthHelpCasePathParams
  >(
    "POST",
    (paramsInPath: WithdrawHealthHelpCasePathParams) =>
      `/serviceRequest/${paramsInPath.id}/withdrawHealthHelpCase`,
    { pathParams: { id }, ...props }
  );

export interface WithdrawHealthHelpV2CasePathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export interface WithdrawHealthHelpV2CaseRequestBody {
  integrationStage?: string;
  requestType?: string;
  errorMessage?: string;
}

export type WithdrawHealthHelpV2CaseProps = Omit<
  MutateProps<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpV2CaseRequestBody,
    WithdrawHealthHelpV2CasePathParams
  >,
  "path" | "verb"
> &
  WithdrawHealthHelpV2CasePathParams;

export const WithdrawHealthHelpV2Case = ({
  id,
  ...props
}: WithdrawHealthHelpV2CaseProps) => (
  <Mutate<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpV2CaseRequestBody,
    WithdrawHealthHelpV2CasePathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/withdrawHealthHelpV2Case`}
    {...props}
  />
);

export type UseWithdrawHealthHelpV2CaseProps = Omit<
  UseMutateProps<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpV2CaseRequestBody,
    WithdrawHealthHelpV2CasePathParams
  >,
  "path" | "verb"
> &
  WithdrawHealthHelpV2CasePathParams;

export const useWithdrawHealthHelpV2Case = ({
  id,
  ...props
}: UseWithdrawHealthHelpV2CaseProps) =>
  useMutate<
    HealthHelpWithdrawResponse,
    unknown,
    void,
    WithdrawHealthHelpV2CaseRequestBody,
    WithdrawHealthHelpV2CasePathParams
  >(
    "POST",
    (paramsInPath: WithdrawHealthHelpV2CasePathParams) =>
      `/serviceRequest/${paramsInPath.id}/withdrawHealthHelpV2Case`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestRuleRunDescriptionsQueryParams {
  /**
   * Searches for rule runs based on service request
   */
  query?: {};
  serviceRequest?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: {};
  /**
   * Respond with only the rrd id and ruleRunTiming
   */
  minimalExpansion?: boolean;
}

export type GetServiceRequestRuleRunDescriptionsProps = Omit<
  GetProps<
    ServiceRequestRuleRunDescriptions,
    unknown,
    GetServiceRequestRuleRunDescriptionsQueryParams,
    void
  >,
  "path"
>;

/**
 * List all rule run descriptions
 *
 * Gets a list of all `ServiceRequestRuleRunDescriptions` entities.
 */
export const GetServiceRequestRuleRunDescriptions = (
  props: GetServiceRequestRuleRunDescriptionsProps
) => (
  <Get<
    ServiceRequestRuleRunDescriptions,
    unknown,
    GetServiceRequestRuleRunDescriptionsQueryParams,
    void
  >
    path={`/serviceRequestRuleRunDescription`}
    {...props}
  />
);

export type UseGetServiceRequestRuleRunDescriptionsProps = Omit<
  UseGetProps<
    ServiceRequestRuleRunDescriptions,
    unknown,
    GetServiceRequestRuleRunDescriptionsQueryParams,
    void
  >,
  "path"
>;

/**
 * List all rule run descriptions
 *
 * Gets a list of all `ServiceRequestRuleRunDescriptions` entities.
 */
export const useGetServiceRequestRuleRunDescriptions = (
  props: UseGetServiceRequestRuleRunDescriptionsProps
) =>
  useGet<
    ServiceRequestRuleRunDescriptions,
    unknown,
    GetServiceRequestRuleRunDescriptionsQueryParams,
    void
  >(`/serviceRequestRuleRunDescription`, props);

export interface GetServiceRequestRuleRunDescriptionQueryParams {
  /**
   * Searches for rule runs based on service request
   */
  query?: {};
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: {};
  /**
   * Respond with only the rrd id and ruleRunTiming
   */
  minimalExpansion?: boolean;
}

export interface GetServiceRequestRuleRunDescriptionPathParams {
  /**
   * A unique identifier for a `ServiceRequestRuleRunDescription`
   */
  id: string;
}

export type GetServiceRequestRuleRunDescriptionProps = Omit<
  GetProps<
    ServiceRequestRuleRunDescription,
    unknown,
    GetServiceRequestRuleRunDescriptionQueryParams,
    GetServiceRequestRuleRunDescriptionPathParams
  >,
  "path"
> &
  GetServiceRequestRuleRunDescriptionPathParams;

/**
 * Rule run description
 *
 * Gets a `ServiceRequestRuleRunDescription` entity.
 */
export const GetServiceRequestRuleRunDescription = ({
  id,
  ...props
}: GetServiceRequestRuleRunDescriptionProps) => (
  <Get<
    ServiceRequestRuleRunDescription,
    unknown,
    GetServiceRequestRuleRunDescriptionQueryParams,
    GetServiceRequestRuleRunDescriptionPathParams
  >
    path={`/serviceRequestRuleRunDescription/${id}`}
    {...props}
  />
);

export type UseGetServiceRequestRuleRunDescriptionProps = Omit<
  UseGetProps<
    ServiceRequestRuleRunDescription,
    unknown,
    GetServiceRequestRuleRunDescriptionQueryParams,
    GetServiceRequestRuleRunDescriptionPathParams
  >,
  "path"
> &
  GetServiceRequestRuleRunDescriptionPathParams;

/**
 * Rule run description
 *
 * Gets a `ServiceRequestRuleRunDescription` entity.
 */
export const useGetServiceRequestRuleRunDescription = ({
  id,
  ...props
}: UseGetServiceRequestRuleRunDescriptionProps) =>
  useGet<
    ServiceRequestRuleRunDescription,
    unknown,
    GetServiceRequestRuleRunDescriptionQueryParams,
    GetServiceRequestRuleRunDescriptionPathParams
  >(
    (paramsInPath: GetServiceRequestRuleRunDescriptionPathParams) =>
      `/serviceRequestRuleRunDescription/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestIntegrationLogsResponse {
  logs?: IntegrationLog[];
}

export interface GetServiceRequestIntegrationLogsPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestIntegrationLogsProps = Omit<
  GetProps<
    GetServiceRequestIntegrationLogsResponse,
    unknown,
    void,
    GetServiceRequestIntegrationLogsPathParams
  >,
  "path"
> &
  GetServiceRequestIntegrationLogsPathParams;

export const GetServiceRequestIntegrationLogs = ({
  id,
  ...props
}: GetServiceRequestIntegrationLogsProps) => (
  <Get<
    GetServiceRequestIntegrationLogsResponse,
    unknown,
    void,
    GetServiceRequestIntegrationLogsPathParams
  >
    path={`/serviceRequest/${id}/integrationLog`}
    {...props}
  />
);

export type UseGetServiceRequestIntegrationLogsProps = Omit<
  UseGetProps<
    GetServiceRequestIntegrationLogsResponse,
    unknown,
    void,
    GetServiceRequestIntegrationLogsPathParams
  >,
  "path"
> &
  GetServiceRequestIntegrationLogsPathParams;

export const useGetServiceRequestIntegrationLogs = ({
  id,
  ...props
}: UseGetServiceRequestIntegrationLogsProps) =>
  useGet<
    GetServiceRequestIntegrationLogsResponse,
    unknown,
    void,
    GetServiceRequestIntegrationLogsPathParams
  >(
    (paramsInPath: GetServiceRequestIntegrationLogsPathParams) =>
      `/serviceRequest/${paramsInPath.id}/integrationLog`,
    { pathParams: { id }, ...props }
  );

export interface GetReferralRequestIntegrationLogsResponse {
  logs?: IntegrationLog[];
}

export interface GetReferralRequestIntegrationLogsPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
}

export type GetReferralRequestIntegrationLogsProps = Omit<
  GetProps<
    GetReferralRequestIntegrationLogsResponse,
    unknown,
    void,
    GetReferralRequestIntegrationLogsPathParams
  >,
  "path"
> &
  GetReferralRequestIntegrationLogsPathParams;

export const GetReferralRequestIntegrationLogs = ({
  id,
  ...props
}: GetReferralRequestIntegrationLogsProps) => (
  <Get<
    GetReferralRequestIntegrationLogsResponse,
    unknown,
    void,
    GetReferralRequestIntegrationLogsPathParams
  >
    path={`/referralRequest/${id}/integrationLog`}
    {...props}
  />
);

export type UseGetReferralRequestIntegrationLogsProps = Omit<
  UseGetProps<
    GetReferralRequestIntegrationLogsResponse,
    unknown,
    void,
    GetReferralRequestIntegrationLogsPathParams
  >,
  "path"
> &
  GetReferralRequestIntegrationLogsPathParams;

export const useGetReferralRequestIntegrationLogs = ({
  id,
  ...props
}: UseGetReferralRequestIntegrationLogsProps) =>
  useGet<
    GetReferralRequestIntegrationLogsResponse,
    unknown,
    void,
    GetReferralRequestIntegrationLogsPathParams
  >(
    (paramsInPath: GetReferralRequestIntegrationLogsPathParams) =>
      `/referralRequest/${paramsInPath.id}/integrationLog`,
    { pathParams: { id }, ...props }
  );

export interface GetAdmissionSourceQueryParams {
  /**
   * A unique identifier for a `AdmissionSource`
   */
  code?: string;
  /**
   * Filter on admission sources for newborns
   */
  isNewbornAdmission?: boolean;
  /**
   * Text search on name and description
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
}

export type GetAdmissionSourceProps = Omit<
  GetProps<
    AdmissionSourceResponse[],
    unknown,
    GetAdmissionSourceQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets list of admission sources
 */
export const GetAdmissionSource = (props: GetAdmissionSourceProps) => (
  <Get<AdmissionSourceResponse[], unknown, GetAdmissionSourceQueryParams, void>
    path={`/admissionSource`}
    {...props}
  />
);

export type UseGetAdmissionSourceProps = Omit<
  UseGetProps<
    AdmissionSourceResponse[],
    unknown,
    GetAdmissionSourceQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets list of admission sources
 */
export const useGetAdmissionSource = (props: UseGetAdmissionSourceProps) =>
  useGet<
    AdmissionSourceResponse[],
    unknown,
    GetAdmissionSourceQueryParams,
    void
  >(`/admissionSource`, props);

export interface GetPatientDischargeStatusQueryParams {
  /**
   * A unique identifier for a `patientDischargeStatus`
   */
  code?: string;
  /**
   * Text search on name and description
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * A unique enum for a `patientDischargeStatus`
   */
  enumName?: any;
  /**
   * Health plan name for filtering discharge statuses
   */
  healthPlanName?: string;
  /**
   * Delegated vendor name for filtering discharge statuses
   */
  delegatedVendorName?: string;
}

export type GetPatientDischargeStatusProps = Omit<
  GetProps<
    PatientDischargeStatusResponse[],
    unknown,
    GetPatientDischargeStatusQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets list of patient discharge statuses
 */
export const GetPatientDischargeStatus = (
  props: GetPatientDischargeStatusProps
) => (
  <Get<
    PatientDischargeStatusResponse[],
    unknown,
    GetPatientDischargeStatusQueryParams,
    void
  >
    path={`/patientDischargeStatus`}
    {...props}
  />
);

export type UseGetPatientDischargeStatusProps = Omit<
  UseGetProps<
    PatientDischargeStatusResponse[],
    unknown,
    GetPatientDischargeStatusQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets list of patient discharge statuses
 */
export const useGetPatientDischargeStatus = (
  props: UseGetPatientDischargeStatusProps
) =>
  useGet<
    PatientDischargeStatusResponse[],
    unknown,
    GetPatientDischargeStatusQueryParams,
    void
  >(`/patientDischargeStatus`, props);

export interface GetLevelsOfCareQueryParams {
  /**
   * A unique identifier for a `levelOfCare`
   */
  code?: string;
  /**
   * Text search on code
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * The name of the health plan
   */
  healthPlanName?: string;
  /**
   * The name of the delegated vendor name
   */
  delegatedVendorName?: string;
}

export interface GetLevelsOfCareRequestBody {
  lineOfBusiness?: string;
}

export type GetLevelsOfCareProps = Omit<
  MutateProps<
    LevelOfCareResponse[],
    unknown,
    GetLevelsOfCareQueryParams,
    GetLevelsOfCareRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Gets list of levelsOfCare
 */
export const GetLevelsOfCare = (props: GetLevelsOfCareProps) => (
  <Mutate<
    LevelOfCareResponse[],
    unknown,
    GetLevelsOfCareQueryParams,
    GetLevelsOfCareRequestBody,
    void
  >
    verb="POST"
    path={`/levelOfCare`}
    {...props}
  />
);

export type UseGetLevelsOfCareProps = Omit<
  UseMutateProps<
    LevelOfCareResponse[],
    unknown,
    GetLevelsOfCareQueryParams,
    GetLevelsOfCareRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Gets list of levelsOfCare
 */
export const useGetLevelsOfCare = (props: UseGetLevelsOfCareProps) =>
  useMutate<
    LevelOfCareResponse[],
    unknown,
    GetLevelsOfCareQueryParams,
    GetLevelsOfCareRequestBody,
    void
  >("POST", `/levelOfCare`, props);

export interface SearchServiceRequestIntegrationsRequestBody {
  integrationType?: string;
  integrationStatus?: string;
  query?: string;
  max?: number;
  dateMax?: string;
  dateMin?: string;
}

export type SearchServiceRequestIntegrationsProps = Omit<
  MutateProps<
    ServiceRequestResponse[],
    unknown,
    void,
    SearchServiceRequestIntegrationsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const SearchServiceRequestIntegrations = (
  props: SearchServiceRequestIntegrationsProps
) => (
  <Mutate<
    ServiceRequestResponse[],
    unknown,
    void,
    SearchServiceRequestIntegrationsRequestBody,
    void
  >
    verb="POST"
    path={`/integration/search`}
    {...props}
  />
);

export type UseSearchServiceRequestIntegrationsProps = Omit<
  UseMutateProps<
    ServiceRequestResponse[],
    unknown,
    void,
    SearchServiceRequestIntegrationsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useSearchServiceRequestIntegrations = (
  props: UseSearchServiceRequestIntegrationsProps
) =>
  useMutate<
    ServiceRequestResponse[],
    unknown,
    void,
    SearchServiceRequestIntegrationsRequestBody,
    void
  >("POST", `/integration/search`, props);

export interface RetryKafkaIntegrationResponse {
  statusCode?: number;
}

export interface RetryKafkaIntegrationRequestBody {
  retryType?: string;
  index?: string;
  documentId?: string;
  jobName?: string;
  limit?: string;
}

export type RetryKafkaIntegrationProps = Omit<
  MutateProps<
    RetryKafkaIntegrationResponse,
    unknown,
    void,
    RetryKafkaIntegrationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const RetryKafkaIntegration = (props: RetryKafkaIntegrationProps) => (
  <Mutate<
    RetryKafkaIntegrationResponse,
    unknown,
    void,
    RetryKafkaIntegrationRequestBody,
    void
  >
    verb="POST"
    path={`/integration/retryKafkaIntegration`}
    {...props}
  />
);

export type UseRetryKafkaIntegrationProps = Omit<
  UseMutateProps<
    RetryKafkaIntegrationResponse,
    unknown,
    void,
    RetryKafkaIntegrationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useRetryKafkaIntegration = (
  props: UseRetryKafkaIntegrationProps
) =>
  useMutate<
    RetryKafkaIntegrationResponse,
    unknown,
    void,
    RetryKafkaIntegrationRequestBody,
    void
  >("POST", `/integration/retryKafkaIntegration`, props);

export interface SearchKafkaIntegrationRequestBody {
  integrationType?: string;
  integrationStatus?: string;
  query?: string;
  max?: number;
  dateMax?: string;
  dateMin?: string;
}

export type SearchKafkaIntegrationProps = Omit<
  MutateProps<
    KafkaMessageDocumentResponse[],
    unknown,
    void,
    SearchKafkaIntegrationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const SearchKafkaIntegration = (props: SearchKafkaIntegrationProps) => (
  <Mutate<
    KafkaMessageDocumentResponse[],
    unknown,
    void,
    SearchKafkaIntegrationRequestBody,
    void
  >
    verb="POST"
    path={`/integration/kafkaSearch`}
    {...props}
  />
);

export type UseSearchKafkaIntegrationProps = Omit<
  UseMutateProps<
    KafkaMessageDocumentResponse[],
    unknown,
    void,
    SearchKafkaIntegrationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useSearchKafkaIntegration = (
  props: UseSearchKafkaIntegrationProps
) =>
  useMutate<
    KafkaMessageDocumentResponse[],
    unknown,
    void,
    SearchKafkaIntegrationRequestBody,
    void
  >("POST", `/integration/kafkaSearch`, props);

export type BulkMarkIntegrationStatusProps = Omit<
  MutateProps<void, unknown, void, IntegrationMarkIndicator[], void>,
  "path" | "verb"
>;

/**
 * Mark set of integration statuses to READY
 */
export const BulkMarkIntegrationStatus = (
  props: BulkMarkIntegrationStatusProps
) => (
  <Mutate<void, unknown, void, IntegrationMarkIndicator[], void>
    verb="POST"
    path={`/integration/bulkUpdate`}
    {...props}
  />
);

export type UseBulkMarkIntegrationStatusProps = Omit<
  UseMutateProps<void, unknown, void, IntegrationMarkIndicator[], void>,
  "path" | "verb"
>;

/**
 * Mark set of integration statuses to READY
 */
export const useBulkMarkIntegrationStatus = (
  props: UseBulkMarkIntegrationStatusProps
) =>
  useMutate<void, unknown, void, IntegrationMarkIndicator[], void>(
    "POST",
    `/integration/bulkUpdate`,
    props
  );

export type BulkMarkReferralIntegrationStatusProps = Omit<
  MutateProps<void, unknown, void, IntegrationMarkIndicator[], void>,
  "path" | "verb"
>;

/**
 * Mark set of integration statuses to READY
 */
export const BulkMarkReferralIntegrationStatus = (
  props: BulkMarkReferralIntegrationStatusProps
) => (
  <Mutate<void, unknown, void, IntegrationMarkIndicator[], void>
    verb="POST"
    path={`/integration/referralBulkUpdate`}
    {...props}
  />
);

export type UseBulkMarkReferralIntegrationStatusProps = Omit<
  UseMutateProps<void, unknown, void, IntegrationMarkIndicator[], void>,
  "path" | "verb"
>;

/**
 * Mark set of integration statuses to READY
 */
export const useBulkMarkReferralIntegrationStatus = (
  props: UseBulkMarkReferralIntegrationStatusProps
) =>
  useMutate<void, unknown, void, IntegrationMarkIndicator[], void>(
    "POST",
    `/integration/referralBulkUpdate`,
    props
  );

export interface GetReviewStatusResponse {
  summary?: {
    reviewPresent?: boolean;
  };
}

export interface GetReviewStatusPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetReviewStatusProps = Omit<
  GetProps<GetReviewStatusResponse, unknown, void, GetReviewStatusPathParams>,
  "path"
> &
  GetReviewStatusPathParams;

export const GetReviewStatus = ({ id, ...props }: GetReviewStatusProps) => (
  <Get<GetReviewStatusResponse, unknown, void, GetReviewStatusPathParams>
    path={`/serviceRequest/${id}/reviewCheck`}
    {...props}
  />
);

export type UseGetReviewStatusProps = Omit<
  UseGetProps<
    GetReviewStatusResponse,
    unknown,
    void,
    GetReviewStatusPathParams
  >,
  "path"
> &
  GetReviewStatusPathParams;

export const useGetReviewStatus = ({ id, ...props }: UseGetReviewStatusProps) =>
  useGet<GetReviewStatusResponse, unknown, void, GetReviewStatusPathParams>(
    (paramsInPath: GetReviewStatusPathParams) =>
      `/serviceRequest/${paramsInPath.id}/reviewCheck`,
    { pathParams: { id }, ...props }
  );

export interface MarkReferralIntegrationStatusPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
}

export interface MarkReferralIntegrationStatusRequestBody {
  /**
   * integrationType
   */
  integrationType?: string;
  /**
   * integrationStatus
   */
  integrationStatus?: string;
}

export type MarkReferralIntegrationStatusProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    MarkReferralIntegrationStatusRequestBody,
    MarkReferralIntegrationStatusPathParams
  >,
  "path" | "verb"
> &
  MarkReferralIntegrationStatusPathParams;

/**
 * Mark integration status to READY
 */
export const MarkReferralIntegrationStatus = ({
  id,
  ...props
}: MarkReferralIntegrationStatusProps) => (
  <Mutate<
    void,
    unknown,
    void,
    MarkReferralIntegrationStatusRequestBody,
    MarkReferralIntegrationStatusPathParams
  >
    verb="POST"
    path={`/referralRequest/${id}/markIntegrationStatus`}
    {...props}
  />
);

export type UseMarkReferralIntegrationStatusProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    MarkReferralIntegrationStatusRequestBody,
    MarkReferralIntegrationStatusPathParams
  >,
  "path" | "verb"
> &
  MarkReferralIntegrationStatusPathParams;

/**
 * Mark integration status to READY
 */
export const useMarkReferralIntegrationStatus = ({
  id,
  ...props
}: UseMarkReferralIntegrationStatusProps) =>
  useMutate<
    void,
    unknown,
    void,
    MarkReferralIntegrationStatusRequestBody,
    MarkReferralIntegrationStatusPathParams
  >(
    "POST",
    (paramsInPath: MarkReferralIntegrationStatusPathParams) =>
      `/referralRequest/${paramsInPath.id}/markIntegrationStatus`,
    { pathParams: { id }, ...props }
  );

export interface MarkIntegrationStatusPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export interface MarkIntegrationStatusRequestBody {
  /**
   * integrationType
   */
  integrationType?: string;
  /**
   * integrationStatus
   */
  integrationStatus?: string;
}

export type MarkIntegrationStatusProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    MarkIntegrationStatusRequestBody,
    MarkIntegrationStatusPathParams
  >,
  "path" | "verb"
> &
  MarkIntegrationStatusPathParams;

/**
 * Mark integration status to READY
 */
export const MarkIntegrationStatus = ({
  id,
  ...props
}: MarkIntegrationStatusProps) => (
  <Mutate<
    void,
    unknown,
    void,
    MarkIntegrationStatusRequestBody,
    MarkIntegrationStatusPathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/markIntegrationStatus`}
    {...props}
  />
);

export type UseMarkIntegrationStatusProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    MarkIntegrationStatusRequestBody,
    MarkIntegrationStatusPathParams
  >,
  "path" | "verb"
> &
  MarkIntegrationStatusPathParams;

/**
 * Mark integration status to READY
 */
export const useMarkIntegrationStatus = ({
  id,
  ...props
}: UseMarkIntegrationStatusProps) =>
  useMutate<
    void,
    unknown,
    void,
    MarkIntegrationStatusRequestBody,
    MarkIntegrationStatusPathParams
  >(
    "POST",
    (paramsInPath: MarkIntegrationStatusPathParams) =>
      `/serviceRequest/${paramsInPath.id}/markIntegrationStatus`,
    { pathParams: { id }, ...props }
  );

export interface MarkIntegrationStatusDirectlyPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export interface MarkIntegrationStatusDirectlyRequestBody {
  /**
   * integrationType
   */
  integrationType?: string;
  /**
   * integrationStatus
   */
  integrationStatus?: string;
}

export type MarkIntegrationStatusDirectlyProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    MarkIntegrationStatusDirectlyRequestBody,
    MarkIntegrationStatusDirectlyPathParams
  >,
  "path" | "verb"
> &
  MarkIntegrationStatusDirectlyPathParams;

/**
 * Mark integration status directly in Mongo to bypass Gorm listeners
 */
export const MarkIntegrationStatusDirectly = ({
  id,
  ...props
}: MarkIntegrationStatusDirectlyProps) => (
  <Mutate<
    void,
    unknown,
    void,
    MarkIntegrationStatusDirectlyRequestBody,
    MarkIntegrationStatusDirectlyPathParams
  >
    verb="POST"
    path={`/serviceRequest/${id}/markIntegrationStatusDirectly`}
    {...props}
  />
);

export type UseMarkIntegrationStatusDirectlyProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    MarkIntegrationStatusDirectlyRequestBody,
    MarkIntegrationStatusDirectlyPathParams
  >,
  "path" | "verb"
> &
  MarkIntegrationStatusDirectlyPathParams;

/**
 * Mark integration status directly in Mongo to bypass Gorm listeners
 */
export const useMarkIntegrationStatusDirectly = ({
  id,
  ...props
}: UseMarkIntegrationStatusDirectlyProps) =>
  useMutate<
    void,
    unknown,
    void,
    MarkIntegrationStatusDirectlyRequestBody,
    MarkIntegrationStatusDirectlyPathParams
  >(
    "POST",
    (paramsInPath: MarkIntegrationStatusDirectlyPathParams) =>
      `/serviceRequest/${paramsInPath.id}/markIntegrationStatusDirectly`,
    { pathParams: { id }, ...props }
  );

export interface GetPrimaryCoveragePathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetPrimaryCoverageProps = Omit<
  GetProps<Coverage, unknown, void, GetPrimaryCoveragePathParams>,
  "path"
> &
  GetPrimaryCoveragePathParams;

export const GetPrimaryCoverage = ({
  id,
  ...props
}: GetPrimaryCoverageProps) => (
  <Get<Coverage, unknown, void, GetPrimaryCoveragePathParams>
    path={`/serviceRequest/${id}/patientPrimaryCoverage`}
    {...props}
  />
);

export type UseGetPrimaryCoverageProps = Omit<
  UseGetProps<Coverage, unknown, void, GetPrimaryCoveragePathParams>,
  "path"
> &
  GetPrimaryCoveragePathParams;

export const useGetPrimaryCoverage = ({
  id,
  ...props
}: UseGetPrimaryCoverageProps) =>
  useGet<Coverage, unknown, void, GetPrimaryCoveragePathParams>(
    (paramsInPath: GetPrimaryCoveragePathParams) =>
      `/serviceRequest/${paramsInPath.id}/patientPrimaryCoverage`,
    { pathParams: { id }, ...props }
  );

export interface GetAllCoveragesResponse {
  primaryActiveCoverage?: Coverage;
  additionalActiveCoverages?: Coverage[];
}

export interface GetAllCoveragesPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetAllCoveragesProps = Omit<
  GetProps<GetAllCoveragesResponse, unknown, void, GetAllCoveragesPathParams>,
  "path"
> &
  GetAllCoveragesPathParams;

export const GetAllCoverages = ({ id, ...props }: GetAllCoveragesProps) => (
  <Get<GetAllCoveragesResponse, unknown, void, GetAllCoveragesPathParams>
    path={`/serviceRequest/${id}/activeCoverages`}
    {...props}
  />
);

export type UseGetAllCoveragesProps = Omit<
  UseGetProps<
    GetAllCoveragesResponse,
    unknown,
    void,
    GetAllCoveragesPathParams
  >,
  "path"
> &
  GetAllCoveragesPathParams;

export const useGetAllCoverages = ({ id, ...props }: UseGetAllCoveragesProps) =>
  useGet<GetAllCoveragesResponse, unknown, void, GetAllCoveragesPathParams>(
    (paramsInPath: GetAllCoveragesPathParams) =>
      `/serviceRequest/${paramsInPath.id}/activeCoverages`,
    { pathParams: { id }, ...props }
  );

export interface RetryInboundFhirIntegrationPathParams {
  /**
   * A unique identifier for an `IntegrationLog`.
   */
  id: string;
}

export interface RetryInboundFhirIntegrationRequestBody {}

export type RetryInboundFhirIntegrationProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    RetryInboundFhirIntegrationRequestBody,
    RetryInboundFhirIntegrationPathParams
  >,
  "path" | "verb"
> &
  RetryInboundFhirIntegrationPathParams;

export const RetryInboundFhirIntegration = ({
  id,
  ...props
}: RetryInboundFhirIntegrationProps) => (
  <Mutate<
    void,
    unknown,
    void,
    RetryInboundFhirIntegrationRequestBody,
    RetryInboundFhirIntegrationPathParams
  >
    verb="POST"
    path={`/retryInboundFhirIntegration/${id}`}
    {...props}
  />
);

export type UseRetryInboundFhirIntegrationProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    RetryInboundFhirIntegrationRequestBody,
    RetryInboundFhirIntegrationPathParams
  >,
  "path" | "verb"
> &
  RetryInboundFhirIntegrationPathParams;

export const useRetryInboundFhirIntegration = ({
  id,
  ...props
}: UseRetryInboundFhirIntegrationProps) =>
  useMutate<
    void,
    unknown,
    void,
    RetryInboundFhirIntegrationRequestBody,
    RetryInboundFhirIntegrationPathParams
  >(
    "POST",
    (paramsInPath: RetryInboundFhirIntegrationPathParams) =>
      `/retryInboundFhirIntegration/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetTatNotificationByServiceRequestPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetTatNotificationByServiceRequestProps = Omit<
  GetProps<
    TATNotification,
    unknown,
    void,
    GetTatNotificationByServiceRequestPathParams
  >,
  "path"
> &
  GetTatNotificationByServiceRequestPathParams;

export const GetTatNotificationByServiceRequest = ({
  serviceRequestId,
  ...props
}: GetTatNotificationByServiceRequestProps) => (
  <Get<
    TATNotification,
    unknown,
    void,
    GetTatNotificationByServiceRequestPathParams
  >
    path={`/tatNotification/serviceRequest/${serviceRequestId}`}
    {...props}
  />
);

export type UseGetTatNotificationByServiceRequestProps = Omit<
  UseGetProps<
    TATNotification,
    unknown,
    void,
    GetTatNotificationByServiceRequestPathParams
  >,
  "path"
> &
  GetTatNotificationByServiceRequestPathParams;

export const useGetTatNotificationByServiceRequest = ({
  serviceRequestId,
  ...props
}: UseGetTatNotificationByServiceRequestProps) =>
  useGet<
    TATNotification,
    unknown,
    void,
    GetTatNotificationByServiceRequestPathParams
  >(
    (paramsInPath: GetTatNotificationByServiceRequestPathParams) =>
      `/tatNotification/serviceRequest/${paramsInPath.serviceRequestId}`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface MarkTatNotificationIntegrationStatusPathParams {
  /**
   * A unique identifier for a `TATNotification`.
   */
  id: string;
}

export interface MarkTatNotificationIntegrationStatusRequestBody {
  /**
   * integrationType
   */
  integrationType?: string;
  /**
   * integrationStatus
   */
  integrationStatus?: string;
}

export type MarkTatNotificationIntegrationStatusProps = Omit<
  MutateProps<
    TATNotification,
    unknown,
    void,
    MarkTatNotificationIntegrationStatusRequestBody,
    MarkTatNotificationIntegrationStatusPathParams
  >,
  "path" | "verb"
> &
  MarkTatNotificationIntegrationStatusPathParams;

export const MarkTatNotificationIntegrationStatus = ({
  id,
  ...props
}: MarkTatNotificationIntegrationStatusProps) => (
  <Mutate<
    TATNotification,
    unknown,
    void,
    MarkTatNotificationIntegrationStatusRequestBody,
    MarkTatNotificationIntegrationStatusPathParams
  >
    verb="PATCH"
    path={`/tatNotification/${id}/updateIntegrationStatus`}
    {...props}
  />
);

export type UseMarkTatNotificationIntegrationStatusProps = Omit<
  UseMutateProps<
    TATNotification,
    unknown,
    void,
    MarkTatNotificationIntegrationStatusRequestBody,
    MarkTatNotificationIntegrationStatusPathParams
  >,
  "path" | "verb"
> &
  MarkTatNotificationIntegrationStatusPathParams;

export const useMarkTatNotificationIntegrationStatus = ({
  id,
  ...props
}: UseMarkTatNotificationIntegrationStatusProps) =>
  useMutate<
    TATNotification,
    unknown,
    void,
    MarkTatNotificationIntegrationStatusRequestBody,
    MarkTatNotificationIntegrationStatusPathParams
  >(
    "PATCH",
    (paramsInPath: MarkTatNotificationIntegrationStatusPathParams) =>
      `/tatNotification/${paramsInPath.id}/updateIntegrationStatus`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestUsersQueryParams {
  query?: string;
  max?: number;
}

export type GetServiceRequestUsersProps = Omit<
  GetProps<
    ServiceRequestUserInfo[],
    unknown,
    GetServiceRequestUsersQueryParams,
    void
  >,
  "path"
>;

/**
 * Get service request users
 */
export const GetServiceRequestUsers = (props: GetServiceRequestUsersProps) => (
  <Get<
    ServiceRequestUserInfo[],
    unknown,
    GetServiceRequestUsersQueryParams,
    void
  >
    path={`/serviceRequest/users`}
    {...props}
  />
);

export type UseGetServiceRequestUsersProps = Omit<
  UseGetProps<
    ServiceRequestUserInfo[],
    unknown,
    GetServiceRequestUsersQueryParams,
    void
  >,
  "path"
>;

/**
 * Get service request users
 */
export const useGetServiceRequestUsers = (
  props: UseGetServiceRequestUsersProps
) =>
  useGet<
    ServiceRequestUserInfo[],
    unknown,
    GetServiceRequestUsersQueryParams,
    void
  >(`/serviceRequest/users`, props);

export interface PalCheckRequestBody {
  patientId?: string;
  encounterType?: FacilityCategory;
  procedureCodes?: string[];
  startDate?: string;
  isOONExceptionRequest?: boolean;
}

export type PalCheckProps = Omit<
  MutateProps<PalCheckResponse, unknown, void, PalCheckRequestBody, void>,
  "path" | "verb"
>;

/**
 * retrieve pal check of one procedure code or bucketed pal checks of multiple procedure codes
 */
export const PalCheck = (props: PalCheckProps) => (
  <Mutate<PalCheckResponse, unknown, void, PalCheckRequestBody, void>
    verb="POST"
    path={`/palCheck`}
    {...props}
  />
);

export type UsePalCheckProps = Omit<
  UseMutateProps<PalCheckResponse, unknown, void, PalCheckRequestBody, void>,
  "path" | "verb"
>;

/**
 * retrieve pal check of one procedure code or bucketed pal checks of multiple procedure codes
 */
export const usePalCheck = (props: UsePalCheckProps) =>
  useMutate<PalCheckResponse, unknown, void, PalCheckRequestBody, void>(
    "POST",
    `/palCheck`,
    props
  );

export interface CrdCheckRequestBody {
  resourceType?: string;
  entry?: {
    resource?: {
      resourceType?: string;
      [key: string]: any;
    };
  }[];
}

export type CrdCheckProps = Omit<
  MutateProps<CdsResponse, CdsErrorResponse, void, CrdCheckRequestBody, void>,
  "path" | "verb"
>;

export const CrdCheck = (props: CrdCheckProps) => (
  <Mutate<CdsResponse, CdsErrorResponse, void, CrdCheckRequestBody, void>
    verb="POST"
    path={`/fhir/cds/crd`}
    {...props}
  />
);

export type UseCrdCheckProps = Omit<
  UseMutateProps<
    CdsResponse,
    CdsErrorResponse,
    void,
    CrdCheckRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useCrdCheck = (props: UseCrdCheckProps) =>
  useMutate<CdsResponse, CdsErrorResponse, void, CrdCheckRequestBody, void>(
    "POST",
    `/fhir/cds/crd`,
    props
  );

export type PayerProcedureCodesFromCPTCodesProps = Omit<
  MutateProps<PayerProcedureCode[], unknown, void, FromCPTCodesRequest, void>,
  "path" | "verb"
>;

export const PayerProcedureCodesFromCPTCodes = (
  props: PayerProcedureCodesFromCPTCodesProps
) => (
  <Mutate<PayerProcedureCode[], unknown, void, FromCPTCodesRequest, void>
    verb="POST"
    path={`/payerProcedureCode/fromCPTCodes`}
    {...props}
  />
);

export type UsePayerProcedureCodesFromCPTCodesProps = Omit<
  UseMutateProps<
    PayerProcedureCode[],
    unknown,
    void,
    FromCPTCodesRequest,
    void
  >,
  "path" | "verb"
>;

export const usePayerProcedureCodesFromCPTCodes = (
  props: UsePayerProcedureCodesFromCPTCodesProps
) =>
  useMutate<PayerProcedureCode[], unknown, void, FromCPTCodesRequest, void>(
    "POST",
    `/payerProcedureCode/fromCPTCodes`,
    props
  );

export interface GetHealthPlanQueryParams {
  /**
   * Max number of results to return
   */
  max?: number;
}

export type GetHealthPlanProps = Omit<
  GetProps<HealthPlan[], unknown, GetHealthPlanQueryParams, void>,
  "path"
>;

export const GetHealthPlan = (props: GetHealthPlanProps) => (
  <Get<HealthPlan[], unknown, GetHealthPlanQueryParams, void>
    path={`/healthPlan`}
    {...props}
  />
);

export type UseGetHealthPlanProps = Omit<
  UseGetProps<HealthPlan[], unknown, GetHealthPlanQueryParams, void>,
  "path"
>;

export const useGetHealthPlan = (props: UseGetHealthPlanProps) =>
  useGet<HealthPlan[], unknown, GetHealthPlanQueryParams, void>(
    `/healthPlan`,
    props
  );

export interface CreateHealthPlanQueryParams {
  /**
   * Max number of results to return
   */
  max?: number;
}

export type CreateHealthPlanProps = Omit<
  MutateProps<
    HealthPlan,
    unknown,
    CreateHealthPlanQueryParams,
    HealthPlanPayload,
    void
  >,
  "path" | "verb"
>;

export const CreateHealthPlan = (props: CreateHealthPlanProps) => (
  <Mutate<
    HealthPlan,
    unknown,
    CreateHealthPlanQueryParams,
    HealthPlanPayload,
    void
  >
    verb="POST"
    path={`/healthPlan`}
    {...props}
  />
);

export type UseCreateHealthPlanProps = Omit<
  UseMutateProps<
    HealthPlan,
    unknown,
    CreateHealthPlanQueryParams,
    HealthPlanPayload,
    void
  >,
  "path" | "verb"
>;

export const useCreateHealthPlan = (props: UseCreateHealthPlanProps) =>
  useMutate<
    HealthPlan,
    unknown,
    CreateHealthPlanQueryParams,
    HealthPlanPayload,
    void
  >("POST", `/healthPlan`, props);

export interface GetHealthPlansForCurrentUserQueryParams {
  /**
   * Max number of results to return
   */
  max?: number;
  /**
   * Max number of results to return
   */
  healthPlanStatuses?: string[];
}

export type GetHealthPlansForCurrentUserProps = Omit<
  GetProps<
    HealthPlan[],
    unknown,
    GetHealthPlansForCurrentUserQueryParams,
    void
  >,
  "path"
>;

export const GetHealthPlansForCurrentUser = (
  props: GetHealthPlansForCurrentUserProps
) => (
  <Get<HealthPlan[], unknown, GetHealthPlansForCurrentUserQueryParams, void>
    path={`/healthPlan/me`}
    {...props}
  />
);

export type UseGetHealthPlansForCurrentUserProps = Omit<
  UseGetProps<
    HealthPlan[],
    unknown,
    GetHealthPlansForCurrentUserQueryParams,
    void
  >,
  "path"
>;

export const useGetHealthPlansForCurrentUser = (
  props: UseGetHealthPlansForCurrentUserProps
) =>
  useGet<HealthPlan[], unknown, GetHealthPlansForCurrentUserQueryParams, void>(
    `/healthPlan/me`,
    props
  );

export interface GetHealthPlanByIdPathParams {
  /**
   * A unique identifier
   */
  id: string;
}

export type GetHealthPlanByIdProps = Omit<
  GetProps<HealthPlan, unknown, void, GetHealthPlanByIdPathParams>,
  "path"
> &
  GetHealthPlanByIdPathParams;

export const GetHealthPlanById = ({ id, ...props }: GetHealthPlanByIdProps) => (
  <Get<HealthPlan, unknown, void, GetHealthPlanByIdPathParams>
    path={`/healthPlan/${id}`}
    {...props}
  />
);

export type UseGetHealthPlanByIdProps = Omit<
  UseGetProps<HealthPlan, unknown, void, GetHealthPlanByIdPathParams>,
  "path"
> &
  GetHealthPlanByIdPathParams;

export const useGetHealthPlanById = ({
  id,
  ...props
}: UseGetHealthPlanByIdProps) =>
  useGet<HealthPlan, unknown, void, GetHealthPlanByIdPathParams>(
    (paramsInPath: GetHealthPlanByIdPathParams) =>
      `/healthPlan/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateHealthPlanPathParams {
  /**
   * A unique identifier
   */
  id: string;
}

export type UpdateHealthPlanProps = Omit<
  MutateProps<
    HealthPlan,
    unknown,
    void,
    HealthPlanPayload,
    UpdateHealthPlanPathParams
  >,
  "path" | "verb"
> &
  UpdateHealthPlanPathParams;

export const UpdateHealthPlan = ({ id, ...props }: UpdateHealthPlanProps) => (
  <Mutate<
    HealthPlan,
    unknown,
    void,
    HealthPlanPayload,
    UpdateHealthPlanPathParams
  >
    verb="PATCH"
    path={`/healthPlan/${id}`}
    {...props}
  />
);

export type UseUpdateHealthPlanProps = Omit<
  UseMutateProps<
    HealthPlan,
    unknown,
    void,
    HealthPlanPayload,
    UpdateHealthPlanPathParams
  >,
  "path" | "verb"
> &
  UpdateHealthPlanPathParams;

export const useUpdateHealthPlan = ({
  id,
  ...props
}: UseUpdateHealthPlanProps) =>
  useMutate<
    HealthPlan,
    unknown,
    void,
    HealthPlanPayload,
    UpdateHealthPlanPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateHealthPlanPathParams) =>
      `/healthPlan/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type GetTemporaryMemberIdProps = Omit<
  GetProps<CohereSequence, unknown, void, void>,
  "path"
>;

export const GetTemporaryMemberId = (props: GetTemporaryMemberIdProps) => (
  <Get<CohereSequence, unknown, void, void>
    path={`/sequence/temporaryMemberId`}
    {...props}
  />
);

export type UseGetTemporaryMemberIdProps = Omit<
  UseGetProps<CohereSequence, unknown, void, void>,
  "path"
>;

export const useGetTemporaryMemberId = (props: UseGetTemporaryMemberIdProps) =>
  useGet<CohereSequence, unknown, void, void>(
    `/sequence/temporaryMemberId`,
    props
  );

export type UpsertPlaceholderPatientProps = Omit<
  MutateProps<Patient, unknown, void, UpsertPlaceholderPatientFormFields, void>,
  "path" | "verb"
>;

export const UpsertPlaceholderPatient = (
  props: UpsertPlaceholderPatientProps
) => (
  <Mutate<Patient, unknown, void, UpsertPlaceholderPatientFormFields, void>
    verb="POST"
    path={`/patient/upsertPlaceholderPatient`}
    {...props}
  />
);

export type UseUpsertPlaceholderPatientProps = Omit<
  UseMutateProps<
    Patient,
    unknown,
    void,
    UpsertPlaceholderPatientFormFields,
    void
  >,
  "path" | "verb"
>;

export const useUpsertPlaceholderPatient = (
  props: UseUpsertPlaceholderPatientProps
) =>
  useMutate<Patient, unknown, void, UpsertPlaceholderPatientFormFields, void>(
    "POST",
    `/patient/upsertPlaceholderPatient`,
    props
  );

export interface PatientCoveragesRequestBody {
  firstName?: string;
  dateOfBirth?: string;
  serviceRequestStartDate?: string;
  lastName?: string;
  healthPlanName?: string;
  fullName?: string;
}

export type PatientCoveragesProps = Omit<
  MutateProps<Coverage[], unknown, void, PatientCoveragesRequestBody, void>,
  "path" | "verb"
>;

/**
 * Search for patient's active coverages
 */
export const PatientCoverages = (props: PatientCoveragesProps) => (
  <Mutate<Coverage[], unknown, void, PatientCoveragesRequestBody, void>
    verb="POST"
    path={`/patient/searchCoverages`}
    {...props}
  />
);

export type UsePatientCoveragesProps = Omit<
  UseMutateProps<Coverage[], unknown, void, PatientCoveragesRequestBody, void>,
  "path" | "verb"
>;

/**
 * Search for patient's active coverages
 */
export const usePatientCoverages = (props: UsePatientCoveragesProps) =>
  useMutate<Coverage[], unknown, void, PatientCoveragesRequestBody, void>(
    "POST",
    `/patient/searchCoverages`,
    props
  );

export interface GetPatientQueryParams {
  includeReferralEligibility?: boolean;
}

export interface GetPatientPathParams {
  /**
   * A unique identifier
   */
  id: string;
}

export type GetPatientProps = Omit<
  GetProps<
    Patient,
    {
      message?: string;
    },
    GetPatientQueryParams,
    GetPatientPathParams
  >,
  "path"
> &
  GetPatientPathParams;

export const GetPatient = ({ id, ...props }: GetPatientProps) => (
  <Get<
    Patient,
    {
      message?: string;
    },
    GetPatientQueryParams,
    GetPatientPathParams
  >
    path={`/patient/${id}`}
    {...props}
  />
);

export type UseGetPatientProps = Omit<
  UseGetProps<
    Patient,
    {
      message?: string;
    },
    GetPatientQueryParams,
    GetPatientPathParams
  >,
  "path"
> &
  GetPatientPathParams;

export const useGetPatient = ({ id, ...props }: UseGetPatientProps) =>
  useGet<
    Patient,
    {
      message?: string;
    },
    GetPatientQueryParams,
    GetPatientPathParams
  >((paramsInPath: GetPatientPathParams) => `/patient/${paramsInPath.id}`, {
    pathParams: { id },
    ...props,
  });

export interface PatientSearchRequestBody {
  /**
   * filter patients by member ID
   */
  memberId?: string;
  /**
   * filter patients by date of birth (yyyy-MM-dd)
   */
  dateOfBirth?: string;
  /**
   * filter patients by first name, should be all uppercased
   */
  firstName?: string;
  /**
   * filter patients by last name, should be all uppercased
   */
  lastName?: string;
  /**
   * the healthPlan the patient belongs to
   */
  healthPlanName?: string;
  /**
   * NPI of ordering provider
   */
  orderingProviderNpi?: string;
  /**
   * List of associations to temporary members
   */
  associatedTemporaryPatientIds?: AssociatedTemporaryPatient[];
}

export type PatientSearchProps = Omit<
  MutateProps<
    PatientWithRules[],
    unknown,
    void,
    PatientSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Search for patients
 */
export const PatientSearch = (props: PatientSearchProps) => (
  <Mutate<PatientWithRules[], unknown, void, PatientSearchRequestBody, void>
    verb="POST"
    path={`/patient/search`}
    {...props}
  />
);

export type UsePatientSearchProps = Omit<
  UseMutateProps<
    PatientWithRules[],
    unknown,
    void,
    PatientSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Search for patients
 */
export const usePatientSearch = (props: UsePatientSearchProps) =>
  useMutate<PatientWithRules[], unknown, void, PatientSearchRequestBody, void>(
    "POST",
    `/patient/search`,
    props
  );

export interface UpdatePatientCoveragesPathParams {
  /**
   * A unique identifier
   */
  id: string;
}

export type UpdatePatientCoveragesProps = Omit<
  MutateProps<
    Patient,
    unknown,
    void,
    Patient,
    UpdatePatientCoveragesPathParams
  >,
  "path" | "verb"
> &
  UpdatePatientCoveragesPathParams;

export const UpdatePatientCoverages = ({
  id,
  ...props
}: UpdatePatientCoveragesProps) => (
  <Mutate<Patient, unknown, void, Patient, UpdatePatientCoveragesPathParams>
    verb="PATCH"
    path={`/patient/updateCoverages/${id}`}
    {...props}
  />
);

export type UseUpdatePatientCoveragesProps = Omit<
  UseMutateProps<
    Patient,
    unknown,
    void,
    Patient,
    UpdatePatientCoveragesPathParams
  >,
  "path" | "verb"
> &
  UpdatePatientCoveragesPathParams;

export const useUpdatePatientCoverages = ({
  id,
  ...props
}: UseUpdatePatientCoveragesProps) =>
  useMutate<Patient, unknown, void, Patient, UpdatePatientCoveragesPathParams>(
    "PATCH",
    (paramsInPath: UpdatePatientCoveragesPathParams) =>
      `/patient/updateCoverages/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface PatientSearchWithRulesRequestBody {
  /**
   * filter patients by member ID
   */
  memberId?: string;
  /**
   * filter patients by date of birth (yyyy-MM-dd)
   */
  dateOfBirth?: string;
  /**
   * filter patients by first name, should be all uppercased
   */
  firstName?: string;
  /**
   * filter patients by last name, should be all uppercased
   */
  lastName?: string;
  /**
   * the healthPlan the patient belongs to
   */
  healthPlanName?: string;
}

export type PatientSearchWithRulesProps = Omit<
  MutateProps<
    PatientWithRules[],
    unknown,
    void,
    PatientSearchWithRulesRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Search for patients with rule actions
 */
export const PatientSearchWithRules = (props: PatientSearchWithRulesProps) => (
  <Mutate<
    PatientWithRules[],
    unknown,
    void,
    PatientSearchWithRulesRequestBody,
    void
  >
    verb="POST"
    path={`/patient/search/ruleActions`}
    {...props}
  />
);

export type UsePatientSearchWithRulesProps = Omit<
  UseMutateProps<
    PatientWithRules[],
    unknown,
    void,
    PatientSearchWithRulesRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Search for patients with rule actions
 */
export const usePatientSearchWithRules = (
  props: UsePatientSearchWithRulesProps
) =>
  useMutate<
    PatientWithRules[],
    unknown,
    void,
    PatientSearchWithRulesRequestBody,
    void
  >("POST", `/patient/search/ruleActions`, props);

export interface PatientReferralEligibilityRequestBody {
  /**
   * filter patients by member ID
   */
  memberId?: string;
  /**
   * filter patients by date of birth (yyyy-MM-dd)
   */
  dateOfBirth?: string;
}

export type PatientReferralEligibilityProps = Omit<
  MutateProps<
    PatientWithReferralEligibility[],
    unknown,
    void,
    PatientReferralEligibilityRequestBody,
    void
  >,
  "path" | "verb"
>;

export const PatientReferralEligibility = (
  props: PatientReferralEligibilityProps
) => (
  <Mutate<
    PatientWithReferralEligibility[],
    unknown,
    void,
    PatientReferralEligibilityRequestBody,
    void
  >
    verb="POST"
    path={`/patient/referralEligibility`}
    {...props}
  />
);

export type UsePatientReferralEligibilityProps = Omit<
  UseMutateProps<
    PatientWithReferralEligibility[],
    unknown,
    void,
    PatientReferralEligibilityRequestBody,
    void
  >,
  "path" | "verb"
>;

export const usePatientReferralEligibility = (
  props: UsePatientReferralEligibilityProps
) =>
  useMutate<
    PatientWithReferralEligibility[],
    unknown,
    void,
    PatientReferralEligibilityRequestBody,
    void
  >("POST", `/patient/referralEligibility`, props);

export interface GetIsCoverageActiveResponse {
  active?: boolean;
}

export interface GetIsCoverageActiveQueryParams {
  serviceRequestId?: string;
}

export type GetIsCoverageActiveProps = Omit<
  GetProps<
    GetIsCoverageActiveResponse,
    unknown,
    GetIsCoverageActiveQueryParams,
    void
  >,
  "path"
>;

export const GetIsCoverageActive = (props: GetIsCoverageActiveProps) => (
  <Get<
    GetIsCoverageActiveResponse,
    unknown,
    GetIsCoverageActiveQueryParams,
    void
  >
    path={`/patient/isCoverageActive`}
    {...props}
  />
);

export type UseGetIsCoverageActiveProps = Omit<
  UseGetProps<
    GetIsCoverageActiveResponse,
    unknown,
    GetIsCoverageActiveQueryParams,
    void
  >,
  "path"
>;

export const useGetIsCoverageActive = (props: UseGetIsCoverageActiveProps) =>
  useGet<
    GetIsCoverageActiveResponse,
    unknown,
    GetIsCoverageActiveQueryParams,
    void
  >(`/patient/isCoverageActive`, props);

export interface GetPatientAccessResponse {
  hasAccess?: boolean;
}

export interface GetPatientAccessPathParams {
  patientId: string;
}

export type GetPatientAccessProps = Omit<
  GetProps<GetPatientAccessResponse, unknown, void, GetPatientAccessPathParams>,
  "path"
> &
  GetPatientAccessPathParams;

export const GetPatientAccess = ({
  patientId,
  ...props
}: GetPatientAccessProps) => (
  <Get<GetPatientAccessResponse, unknown, void, GetPatientAccessPathParams>
    path={`/patientAccess/${patientId}`}
    {...props}
  />
);

export type UseGetPatientAccessProps = Omit<
  UseGetProps<
    GetPatientAccessResponse,
    unknown,
    void,
    GetPatientAccessPathParams
  >,
  "path"
> &
  GetPatientAccessPathParams;

export const useGetPatientAccess = ({
  patientId,
  ...props
}: UseGetPatientAccessProps) =>
  useGet<GetPatientAccessResponse, unknown, void, GetPatientAccessPathParams>(
    (paramsInPath: GetPatientAccessPathParams) =>
      `/patientAccess/${paramsInPath.patientId}`,
    { pathParams: { patientId }, ...props }
  );

export interface BackfillPatientAccessRequestBody {
  patientId?: string;
}

export type BackfillPatientAccessProps = Omit<
  MutateProps<
    PatientAccess | void,
    unknown,
    void,
    BackfillPatientAccessRequestBody,
    void
  >,
  "path" | "verb"
>;

export const BackfillPatientAccess = (props: BackfillPatientAccessProps) => (
  <Mutate<
    PatientAccess | void,
    unknown,
    void,
    BackfillPatientAccessRequestBody,
    void
  >
    verb="POST"
    path={`/patientAccess/backfill`}
    {...props}
  />
);

export type UseBackfillPatientAccessProps = Omit<
  UseMutateProps<
    PatientAccess | void,
    unknown,
    void,
    BackfillPatientAccessRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useBackfillPatientAccess = (
  props: UseBackfillPatientAccessProps
) =>
  useMutate<
    PatientAccess | void,
    unknown,
    void,
    BackfillPatientAccessRequestBody,
    void
  >("POST", `/patientAccess/backfill`, props);

export interface GetPlaceOfServicesQueryParams {
  query?: string;
  encounterType?: string;
  healthPlanName?: string;
  clinicalServiceId?: string;
}

export type GetPlaceOfServicesProps = Omit<
  GetProps<PlaceOfService[], unknown, GetPlaceOfServicesQueryParams, void>,
  "path"
>;

export const GetPlaceOfServices = (props: GetPlaceOfServicesProps) => (
  <Get<PlaceOfService[], unknown, GetPlaceOfServicesQueryParams, void>
    path={`/placeOfService`}
    {...props}
  />
);

export type UseGetPlaceOfServicesProps = Omit<
  UseGetProps<PlaceOfService[], unknown, GetPlaceOfServicesQueryParams, void>,
  "path"
>;

export const useGetPlaceOfServices = (props: UseGetPlaceOfServicesProps) =>
  useGet<PlaceOfService[], unknown, GetPlaceOfServicesQueryParams, void>(
    `/placeOfService`,
    props
  );

export interface GetFacilitiesQueryParams {
  query?: string;
  /**
   * Number of results to return
   */
  max?: number;
  healthPlanName?: string;
  useCMSProviders?: string;
  /**
   * starting with result number
   */
  offset?: number;
}

export type GetFacilitiesProps = Omit<
  GetProps<Facility[], unknown, GetFacilitiesQueryParams, void>,
  "path"
>;

export const GetFacilities = (props: GetFacilitiesProps) => (
  <Get<Facility[], unknown, GetFacilitiesQueryParams, void>
    path={`/facility`}
    {...props}
  />
);

export type UseGetFacilitiesProps = Omit<
  UseGetProps<Facility[], unknown, GetFacilitiesQueryParams, void>,
  "path"
>;

export const useGetFacilities = (props: UseGetFacilitiesProps) =>
  useGet<Facility[], unknown, GetFacilitiesQueryParams, void>(
    `/facility`,
    props
  );

export type CreateFacilityProps = Omit<
  MutateProps<Facility, unknown, void, PracticeCreatePayload, void>,
  "path" | "verb"
>;

export const CreateFacility = (props: CreateFacilityProps) => (
  <Mutate<Facility, unknown, void, PracticeCreatePayload, void>
    verb="POST"
    path={`/facility`}
    {...props}
  />
);

export type UseCreateFacilityProps = Omit<
  UseMutateProps<Facility, unknown, void, PracticeCreatePayload, void>,
  "path" | "verb"
>;

export const useCreateFacility = (props: UseCreateFacilityProps) =>
  useMutate<Facility, unknown, void, PracticeCreatePayload, void>(
    "POST",
    `/facility`,
    props
  );

export interface GetFacilityPathParams {
  /**
   * A unique identifier for a `Facility`.
   */
  id: string;
}

export type GetFacilityProps = Omit<
  GetProps<Facility, unknown, void, GetFacilityPathParams>,
  "path"
> &
  GetFacilityPathParams;

/**
 * Get a Facility
 *
 * Gets the details of a single instance of a `Facility`.
 */
export const GetFacility = ({ id, ...props }: GetFacilityProps) => (
  <Get<Facility, unknown, void, GetFacilityPathParams>
    path={`/facility/${id}`}
    {...props}
  />
);

export type UseGetFacilityProps = Omit<
  UseGetProps<Facility, unknown, void, GetFacilityPathParams>,
  "path"
> &
  GetFacilityPathParams;

/**
 * Get a Facility
 *
 * Gets the details of a single instance of a `Facility`.
 */
export const useGetFacility = ({ id, ...props }: UseGetFacilityProps) =>
  useGet<Facility, unknown, void, GetFacilityPathParams>(
    (paramsInPath: GetFacilityPathParams) => `/facility/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateFacilityPathParams {
  /**
   * A unique identifier for a `Facility`.
   */
  id: string;
}

export type UpdateFacilityProps = Omit<
  MutateProps<Facility, unknown, void, Facility, UpdateFacilityPathParams>,
  "path" | "verb"
> &
  UpdateFacilityPathParams;

/**
 * Update a Facility
 *
 * Updates an existing `Facility`.
 */
export const UpdateFacility = ({ id, ...props }: UpdateFacilityProps) => (
  <Mutate<Facility, unknown, void, Facility, UpdateFacilityPathParams>
    verb="PUT"
    path={`/facility/${id}`}
    {...props}
  />
);

export type UseUpdateFacilityProps = Omit<
  UseMutateProps<Facility, unknown, void, Facility, UpdateFacilityPathParams>,
  "path" | "verb"
> &
  UpdateFacilityPathParams;

/**
 * Update a Facility
 *
 * Updates an existing `Facility`.
 */
export const useUpdateFacility = ({ id, ...props }: UseUpdateFacilityProps) =>
  useMutate<Facility, unknown, void, Facility, UpdateFacilityPathParams>(
    "PUT",
    (paramsInPath: UpdateFacilityPathParams) => `/facility/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteFacilityProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Facility
 *
 * Deletes an existing `Facility`.
 */
export const DeleteFacility = (props: DeleteFacilityProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/facility`}
    {...props}
  />
);

export type UseDeleteFacilityProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Facility
 *
 * Deletes an existing `Facility`.
 */
export const useDeleteFacility = (props: UseDeleteFacilityProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/facility`, {
    ...props,
  });

export interface GetProcedureCodesQueryParams {
  query?: string;
  clinicalServiceId?: string;
  /**
   * Matcher on the `code` field of a ProcedureCode e.g. code=in:1,2,3
   */
  code?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  /**
   * comma seperated list/lists of systems as a filter e.g. systems=revcode or systems=hcpcs,cpt
   */
  systems?: string;
  /**
   * comma seperated list of terms to search by
   */
  termList?: string;
}

export type GetProcedureCodesProps = Omit<
  GetProps<
    (SemanticProcedureCode | null)[],
    unknown,
    GetProcedureCodesQueryParams,
    void
  >,
  "path"
>;

export const GetProcedureCodes = (props: GetProcedureCodesProps) => (
  <Get<
    (SemanticProcedureCode | null)[],
    unknown,
    GetProcedureCodesQueryParams,
    void
  >
    path={`/procedureCode`}
    {...props}
  />
);

export type UseGetProcedureCodesProps = Omit<
  UseGetProps<
    (SemanticProcedureCode | null)[],
    unknown,
    GetProcedureCodesQueryParams,
    void
  >,
  "path"
>;

export const useGetProcedureCodes = (props: UseGetProcedureCodesProps) =>
  useGet<
    (SemanticProcedureCode | null)[],
    unknown,
    GetProcedureCodesQueryParams,
    void
  >(`/procedureCode`, props);

export interface GetObservationCodesQueryParams {
  query?: string;
  /**
   * The `code` field of an ObservationCode e.g. code=in:1,2,3
   */
  code?: string;
  /**
   * Comma delineated list of codes for bulk searches
   */
  termList?: string;
}

export type GetObservationCodesProps = Omit<
  GetProps<ObservationCode[], unknown, GetObservationCodesQueryParams, void>,
  "path"
>;

/**
 * Path used to search for codes
 */
export const GetObservationCodes = (props: GetObservationCodesProps) => (
  <Get<ObservationCode[], unknown, GetObservationCodesQueryParams, void>
    path={`/observationCode`}
    {...props}
  />
);

export type UseGetObservationCodesProps = Omit<
  UseGetProps<ObservationCode[], unknown, GetObservationCodesQueryParams, void>,
  "path"
>;

/**
 * Path used to search for codes
 */
export const useGetObservationCodes = (props: UseGetObservationCodesProps) =>
  useGet<ObservationCode[], unknown, GetObservationCodesQueryParams, void>(
    `/observationCode`,
    props
  );

export interface GetHccCodesQueryParams {
  query?: string;
  /**
   * The `code` field of an HccCode e.g. code=in:1,2,3
   */
  code?: string;
}

export type GetHccCodesProps = Omit<
  GetProps<SemanticCode[], unknown, GetHccCodesQueryParams, void>,
  "path"
>;

/**
 * Path used to search for HCC codes
 */
export const GetHccCodes = (props: GetHccCodesProps) => (
  <Get<SemanticCode[], unknown, GetHccCodesQueryParams, void>
    path={`/hccCode`}
    {...props}
  />
);

export type UseGetHccCodesProps = Omit<
  UseGetProps<SemanticCode[], unknown, GetHccCodesQueryParams, void>,
  "path"
>;

/**
 * Path used to search for HCC codes
 */
export const useGetHccCodes = (props: UseGetHccCodesProps) =>
  useGet<SemanticCode[], unknown, GetHccCodesQueryParams, void>(
    `/hccCode`,
    props
  );

export interface GetProviderTagsQueryParams {
  /**
   * Search query
   */
  query?: string;
  /**
   * Maximum number results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  /**
   * column to sort
   */
  sort?: string;
  /**
   * direction to sort (asc or desc)
   */
  order?: string;
  /**
   * whether to include the usage of the tag (slow query)
   */
  includeMappingsCount?: boolean;
}

export type GetProviderTagsProps = Omit<
  GetProps<Tag[], unknown, GetProviderTagsQueryParams, void>,
  "path"
>;

export const GetProviderTags = (props: GetProviderTagsProps) => (
  <Get<Tag[], unknown, GetProviderTagsQueryParams, void>
    path={`/providerTag`}
    {...props}
  />
);

export type UseGetProviderTagsProps = Omit<
  UseGetProps<Tag[], unknown, GetProviderTagsQueryParams, void>,
  "path"
>;

export const useGetProviderTags = (props: UseGetProviderTagsProps) =>
  useGet<Tag[], unknown, GetProviderTagsQueryParams, void>(
    `/providerTag`,
    props
  );

export type CreateProviderTagProps = Omit<
  MutateProps<Tag, ErrorResponse | void, void, Tag, void>,
  "path" | "verb"
>;

export const CreateProviderTag = (props: CreateProviderTagProps) => (
  <Mutate<Tag, ErrorResponse | void, void, Tag, void>
    verb="POST"
    path={`/providerTag`}
    {...props}
  />
);

export type UseCreateProviderTagProps = Omit<
  UseMutateProps<Tag, ErrorResponse | void, void, Tag, void>,
  "path" | "verb"
>;

export const useCreateProviderTag = (props: UseCreateProviderTagProps) =>
  useMutate<Tag, ErrorResponse | void, void, Tag, void>(
    "POST",
    `/providerTag`,
    props
  );

export interface UpdateProviderTagPathParams {
  /**
   * A unique identifier for a `Tag`.
   */
  id: string;
}

export type UpdateProviderTagProps = Omit<
  MutateProps<Tag, unknown, void, Tag, UpdateProviderTagPathParams>,
  "path" | "verb"
> &
  UpdateProviderTagPathParams;

/**
 * Update a Tag
 */
export const UpdateProviderTag = ({ id, ...props }: UpdateProviderTagProps) => (
  <Mutate<Tag, unknown, void, Tag, UpdateProviderTagPathParams>
    verb="PUT"
    path={`/providerTag/${id}`}
    {...props}
  />
);

export type UseUpdateProviderTagProps = Omit<
  UseMutateProps<Tag, unknown, void, Tag, UpdateProviderTagPathParams>,
  "path" | "verb"
> &
  UpdateProviderTagPathParams;

/**
 * Update a Tag
 */
export const useUpdateProviderTag = ({
  id,
  ...props
}: UseUpdateProviderTagProps) =>
  useMutate<Tag, unknown, void, Tag, UpdateProviderTagPathParams>(
    "PUT",
    (paramsInPath: UpdateProviderTagPathParams) =>
      `/providerTag/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetProvidersQueryParams {
  query?: string;
  healthPlanName?: string;
  useCMSProviders?: string;
}

export type GetProvidersProps = Omit<
  GetProps<Provider[], unknown, GetProvidersQueryParams, void>,
  "path"
>;

export const GetProviders = (props: GetProvidersProps) => (
  <Get<Provider[], unknown, GetProvidersQueryParams, void>
    path={`/provider`}
    {...props}
  />
);

export type UseGetProvidersProps = Omit<
  UseGetProps<Provider[], unknown, GetProvidersQueryParams, void>,
  "path"
>;

export const useGetProviders = (props: UseGetProvidersProps) =>
  useGet<Provider[], unknown, GetProvidersQueryParams, void>(
    `/provider`,
    props
  );

export type CreateProviderProps = Omit<
  MutateProps<Provider, unknown, void, PracticeCreatePayload, void>,
  "path" | "verb"
>;

export const CreateProvider = (props: CreateProviderProps) => (
  <Mutate<Provider, unknown, void, PracticeCreatePayload, void>
    verb="POST"
    path={`/provider`}
    {...props}
  />
);

export type UseCreateProviderProps = Omit<
  UseMutateProps<Provider, unknown, void, PracticeCreatePayload, void>,
  "path" | "verb"
>;

export const useCreateProvider = (props: UseCreateProviderProps) =>
  useMutate<Provider, unknown, void, PracticeCreatePayload, void>(
    "POST",
    `/provider`,
    props
  );

export interface GetProvidersOrFacilitiesQueryParams {
  query?: string;
  healthPlanName?: string;
  useCMSProviders?: string;
  /**
   * PROVIDER for ordering/performing providers and FACILITY for facilities
   */
  recordType?: string;
  deDuplicateManualProviders?: string;
  specialty?: string;
  limitedOonIndicator?: string;
}

export type GetProvidersOrFacilitiesProps = Omit<
  GetProps<
    ProviderFacilityCatalogue[],
    unknown,
    GetProvidersOrFacilitiesQueryParams,
    void
  >,
  "path"
>;

export const GetProvidersOrFacilities = (
  props: GetProvidersOrFacilitiesProps
) => (
  <Get<
    ProviderFacilityCatalogue[],
    unknown,
    GetProvidersOrFacilitiesQueryParams,
    void
  >
    path={`/providerFacilityCatalogue`}
    {...props}
  />
);

export type UseGetProvidersOrFacilitiesProps = Omit<
  UseGetProps<
    ProviderFacilityCatalogue[],
    unknown,
    GetProvidersOrFacilitiesQueryParams,
    void
  >,
  "path"
>;

export const useGetProvidersOrFacilities = (
  props: UseGetProvidersOrFacilitiesProps
) =>
  useGet<
    ProviderFacilityCatalogue[],
    unknown,
    GetProvidersOrFacilitiesQueryParams,
    void
  >(`/providerFacilityCatalogue`, props);

export interface GetProviderOrFacilityPathParams {
  /**
   * A unique identifier for a `ProviderFacilityCatalogue`.
   */
  id: string;
}

export type GetProviderOrFacilityProps = Omit<
  GetProps<
    ProviderFacilityCatalogue,
    unknown,
    void,
    GetProviderOrFacilityPathParams
  >,
  "path"
> &
  GetProviderOrFacilityPathParams;

export const GetProviderOrFacility = ({
  id,
  ...props
}: GetProviderOrFacilityProps) => (
  <Get<
    ProviderFacilityCatalogue,
    unknown,
    void,
    GetProviderOrFacilityPathParams
  >
    path={`/providerFacilityCatalogue/${id}`}
    {...props}
  />
);

export type UseGetProviderOrFacilityProps = Omit<
  UseGetProps<
    ProviderFacilityCatalogue,
    unknown,
    void,
    GetProviderOrFacilityPathParams
  >,
  "path"
> &
  GetProviderOrFacilityPathParams;

export const useGetProviderOrFacility = ({
  id,
  ...props
}: UseGetProviderOrFacilityProps) =>
  useGet<
    ProviderFacilityCatalogue,
    unknown,
    void,
    GetProviderOrFacilityPathParams
  >(
    (paramsInPath: GetProviderOrFacilityPathParams) =>
      `/providerFacilityCatalogue/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type UpsertManuallyEnteredProviderOrFacilityProps = Omit<
  MutateProps<
    ProviderFacilityCatalogue,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ManualProviderFacilityPayload,
    void
  >,
  "path" | "verb"
>;

export const UpsertManuallyEnteredProviderOrFacility = (
  props: UpsertManuallyEnteredProviderOrFacilityProps
) => (
  <Mutate<
    ProviderFacilityCatalogue,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ManualProviderFacilityPayload,
    void
  >
    verb="POST"
    path={`/providerFacilityCatalogue/upsertManuallyEnteredProviderOrFacility`}
    {...props}
  />
);

export type UseUpsertManuallyEnteredProviderOrFacilityProps = Omit<
  UseMutateProps<
    ProviderFacilityCatalogue,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ManualProviderFacilityPayload,
    void
  >,
  "path" | "verb"
>;

export const useUpsertManuallyEnteredProviderOrFacility = (
  props: UseUpsertManuallyEnteredProviderOrFacilityProps
) =>
  useMutate<
    ProviderFacilityCatalogue,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ManualProviderFacilityPayload,
    void
  >(
    "POST",
    `/providerFacilityCatalogue/upsertManuallyEnteredProviderOrFacility`,
    props
  );

export interface GetProviderFacilitySpecialtiesQueryParams {
  query?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * Starting with result number
   */
  offset?: number;
  /**
   * Health plan of provider facility
   */
  healthPlanName?: string;
}

export type GetProviderFacilitySpecialtiesProps = Omit<
  GetProps<
    ReferraRequestSpecialty[],
    unknown,
    GetProviderFacilitySpecialtiesQueryParams,
    void
  >,
  "path"
>;

export const GetProviderFacilitySpecialties = (
  props: GetProviderFacilitySpecialtiesProps
) => (
  <Get<
    ReferraRequestSpecialty[],
    unknown,
    GetProviderFacilitySpecialtiesQueryParams,
    void
  >
    path={`/providerFacilityCatalogue/fetchProviderFacilitySpecialties`}
    {...props}
  />
);

export type UseGetProviderFacilitySpecialtiesProps = Omit<
  UseGetProps<
    ReferraRequestSpecialty[],
    unknown,
    GetProviderFacilitySpecialtiesQueryParams,
    void
  >,
  "path"
>;

export const useGetProviderFacilitySpecialties = (
  props: UseGetProviderFacilitySpecialtiesProps
) =>
  useGet<
    ReferraRequestSpecialty[],
    unknown,
    GetProviderFacilitySpecialtiesQueryParams,
    void
  >(`/providerFacilityCatalogue/fetchProviderFacilitySpecialties`, props);

export interface GetProviderPathParams {
  /**
   * A unique identifier for a `Provider`.
   */
  id: string;
}

export type GetProviderProps = Omit<
  GetProps<Provider, unknown, void, GetProviderPathParams>,
  "path"
> &
  GetProviderPathParams;

/**
 * Get a Provider
 *
 * Gets the details of a single instance of a `Provider`.
 */
export const GetProvider = ({ id, ...props }: GetProviderProps) => (
  <Get<Provider, unknown, void, GetProviderPathParams>
    path={`/provider/${id}`}
    {...props}
  />
);

export type UseGetProviderProps = Omit<
  UseGetProps<Provider, unknown, void, GetProviderPathParams>,
  "path"
> &
  GetProviderPathParams;

/**
 * Get a Provider
 *
 * Gets the details of a single instance of a `Provider`.
 */
export const useGetProvider = ({ id, ...props }: UseGetProviderProps) =>
  useGet<Provider, unknown, void, GetProviderPathParams>(
    (paramsInPath: GetProviderPathParams) => `/provider/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateProviderPathParams {
  /**
   * A unique identifier for a `Provider`.
   */
  id: string;
}

export type UpdateProviderProps = Omit<
  MutateProps<Provider, unknown, void, Provider, UpdateProviderPathParams>,
  "path" | "verb"
> &
  UpdateProviderPathParams;

/**
 * Update a Provider
 *
 * Updates an existing `Provider`.
 */
export const UpdateProvider = ({ id, ...props }: UpdateProviderProps) => (
  <Mutate<Provider, unknown, void, Provider, UpdateProviderPathParams>
    verb="PUT"
    path={`/provider/${id}`}
    {...props}
  />
);

export type UseUpdateProviderProps = Omit<
  UseMutateProps<Provider, unknown, void, Provider, UpdateProviderPathParams>,
  "path" | "verb"
> &
  UpdateProviderPathParams;

/**
 * Update a Provider
 *
 * Updates an existing `Provider`.
 */
export const useUpdateProvider = ({ id, ...props }: UseUpdateProviderProps) =>
  useMutate<Provider, unknown, void, Provider, UpdateProviderPathParams>(
    "PUT",
    (paramsInPath: UpdateProviderPathParams) => `/provider/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteProviderProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Provider
 *
 * Deletes an existing `Provider`.
 */
export const DeleteProvider = (props: DeleteProviderProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/provider`}
    {...props}
  />
);

export type UseDeleteProviderProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Provider
 *
 * Deletes an existing `Provider`.
 */
export const useDeleteProvider = (props: UseDeleteProviderProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/provider`, {
    ...props,
  });

export interface ParsePdfIntoHtmlQueryParams {
  /**
   * Only Humana right now.
   */
  policyType: string;
}

export type ParsePdfIntoHtmlProps = Omit<
  MutateProps<HTMLResult, unknown, ParsePdfIntoHtmlQueryParams, void, void>,
  "path" | "verb"
>;

/**
 * Parse PDF into HTML
 */
export const ParsePdfIntoHtml = (props: ParsePdfIntoHtmlProps) => (
  <Mutate<HTMLResult, unknown, ParsePdfIntoHtmlQueryParams, void, void>
    verb="POST"
    path={`/parsePdf`}
    {...props}
  />
);

export type UseParsePdfIntoHtmlProps = Omit<
  UseMutateProps<HTMLResult, unknown, ParsePdfIntoHtmlQueryParams, void, void>,
  "path" | "verb"
>;

/**
 * Parse PDF into HTML
 */
export const useParsePdfIntoHtml = (props: UseParsePdfIntoHtmlProps) =>
  useMutate<HTMLResult, unknown, ParsePdfIntoHtmlQueryParams, void, void>(
    "POST",
    `/parsePdf`,
    props
  );

export interface GetOutofNetworkCheckResponse {
  isOutOfNetwork?: boolean;
  isExceptionRequired?: boolean;
  outOfNetworkReason?: string;
  networkType?: NetworkType;
  inNetworkExternalReferenceId?: string;
  inNetworkSource?: string;
  contractType?: string;
  areaType?: AreaType;
}

export type GetOutofNetworkCheckProps = Omit<
  MutateProps<
    GetOutofNetworkCheckResponse,
    unknown,
    void,
    OONCheckRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Determine if provider/facility is in-network
 */
export const GetOutofNetworkCheck = (props: GetOutofNetworkCheckProps) => (
  <Mutate<GetOutofNetworkCheckResponse, unknown, void, OONCheckRequest, void>
    verb="POST"
    path={`/outOfNetwork/check`}
    {...props}
  />
);

export type UseGetOutofNetworkCheckProps = Omit<
  UseMutateProps<
    GetOutofNetworkCheckResponse,
    unknown,
    void,
    OONCheckRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Determine if provider/facility is in-network
 */
export const useGetOutofNetworkCheck = (props: UseGetOutofNetworkCheckProps) =>
  useMutate<GetOutofNetworkCheckResponse, unknown, void, OONCheckRequest, void>(
    "POST",
    `/outOfNetwork/check`,
    props
  );

export interface GetLocationsQueryParams {
  /**
   * provider/facility npi
   */
  npi?: string;
  /**
   * provider/facility tin
   */
  tin?: string;
  /**
   * search term
   */
  query?: string;
  /**
   * search term
   */
  addressQuery?: string;
  /**
   * healthplan name
   */
  source?: string;
  /**
   * facility or provider
   */
  isFacility?: boolean;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
}

export type GetLocationsProps = Omit<
  GetProps<Location[], unknown, GetLocationsQueryParams, void>,
  "path"
>;

export const GetLocations = (props: GetLocationsProps) => (
  <Get<Location[], unknown, GetLocationsQueryParams, void>
    path={`/location/search`}
    {...props}
  />
);

export type UseGetLocationsProps = Omit<
  UseGetProps<Location[], unknown, GetLocationsQueryParams, void>,
  "path"
>;

export const useGetLocations = (props: UseGetLocationsProps) =>
  useGet<Location[], unknown, GetLocationsQueryParams, void>(
    `/location/search`,
    props
  );

export interface GetLicenseAgreementResponse {
  text: string;
  textHash: string;
}

export type GetLicenseAgreementProps = Omit<
  GetProps<GetLicenseAgreementResponse, unknown, void, void>,
  "path"
>;

export const GetLicenseAgreement = (props: GetLicenseAgreementProps) => (
  <Get<GetLicenseAgreementResponse, unknown, void, void>
    path={`/licenseAgreement`}
    {...props}
  />
);

export type UseGetLicenseAgreementProps = Omit<
  UseGetProps<GetLicenseAgreementResponse, unknown, void, void>,
  "path"
>;

export const useGetLicenseAgreement = (props: UseGetLicenseAgreementProps) =>
  useGet<GetLicenseAgreementResponse, unknown, void, void>(
    `/licenseAgreement`,
    props
  );

export interface SignLicenseAgreementRequestBody {
  textHash?: string;
}

export type SignLicenseAgreementProps = Omit<
  MutateProps<void, unknown, void, SignLicenseAgreementRequestBody, void>,
  "path" | "verb"
>;

export const SignLicenseAgreement = (props: SignLicenseAgreementProps) => (
  <Mutate<void, unknown, void, SignLicenseAgreementRequestBody, void>
    verb="POST"
    path={`/licenseAgreement/sign`}
    {...props}
  />
);

export type UseSignLicenseAgreementProps = Omit<
  UseMutateProps<void, unknown, void, SignLicenseAgreementRequestBody, void>,
  "path" | "verb"
>;

export const useSignLicenseAgreement = (props: UseSignLicenseAgreementProps) =>
  useMutate<void, unknown, void, SignLicenseAgreementRequestBody, void>(
    "POST",
    `/licenseAgreement/sign`,
    props
  );

export interface GetClaimQueryParams {
  query?: string;
  claimId?: string;
  patientId?: string;
  /**
   * Max number of results to return (defaults 10, up to 100)
   */
  max?: number;
}

export type GetClaimProps = Omit<
  GetProps<Claim[], unknown, GetClaimQueryParams, void>,
  "path"
>;

export const GetClaim = (props: GetClaimProps) => (
  <Get<Claim[], unknown, GetClaimQueryParams, void>
    path={`/claim`}
    {...props}
  />
);

export type UseGetClaimProps = Omit<
  UseGetProps<Claim[], unknown, GetClaimQueryParams, void>,
  "path"
>;

export const useGetClaim = (props: UseGetClaimProps) =>
  useGet<Claim[], unknown, GetClaimQueryParams, void>(`/claim`, props);

export interface GetClaimByIdPathParams {
  id: string;
}

export type GetClaimByIdProps = Omit<
  GetProps<Claim, unknown, void, GetClaimByIdPathParams>,
  "path"
> &
  GetClaimByIdPathParams;

export const GetClaimById = ({ id, ...props }: GetClaimByIdProps) => (
  <Get<Claim, unknown, void, GetClaimByIdPathParams>
    path={`/claim/${id}`}
    {...props}
  />
);

export type UseGetClaimByIdProps = Omit<
  UseGetProps<Claim, unknown, void, GetClaimByIdPathParams>,
  "path"
> &
  GetClaimByIdPathParams;

export const useGetClaimById = ({ id, ...props }: UseGetClaimByIdProps) =>
  useGet<Claim, unknown, void, GetClaimByIdPathParams>(
    (paramsInPath: GetClaimByIdPathParams) => `/claim/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetTaggedProvidersQueryParams {
  /**
   * Search query
   */
  query?: {};
  /**
   * Maximum number results to return
   */
  max?: {};
  /**
   * starting with result number
   */
  offset?: {};
  /**
   * column to sort
   */
  sort?: {};
  /**
   * direction to sort (asc or desc)
   */
  order?: {};
}

export type GetTaggedProvidersProps = Omit<
  GetProps<Provider[], unknown, GetTaggedProvidersQueryParams, void>,
  "path"
>;

/**
 * Search providers and their associated tags
 */
export const GetTaggedProviders = (props: GetTaggedProvidersProps) => (
  <Get<Provider[], unknown, GetTaggedProvidersQueryParams, void>
    path={`/taggedProvider`}
    {...props}
  />
);

export type UseGetTaggedProvidersProps = Omit<
  UseGetProps<Provider[], unknown, GetTaggedProvidersQueryParams, void>,
  "path"
>;

/**
 * Search providers and their associated tags
 */
export const useGetTaggedProviders = (props: UseGetTaggedProvidersProps) =>
  useGet<Provider[], unknown, GetTaggedProvidersQueryParams, void>(
    `/taggedProvider`,
    props
  );

export interface FindNewEmailVerifiedOrgUsersResponse {
  userIds?: string[];
}

export interface FindNewEmailVerifiedOrgUsersPathParams {
  id: string;
}

export type FindNewEmailVerifiedOrgUsersProps = Omit<
  GetProps<
    FindNewEmailVerifiedOrgUsersResponse,
    unknown,
    void,
    FindNewEmailVerifiedOrgUsersPathParams
  >,
  "path"
> &
  FindNewEmailVerifiedOrgUsersPathParams;

export const FindNewEmailVerifiedOrgUsers = ({
  id,
  ...props
}: FindNewEmailVerifiedOrgUsersProps) => (
  <Get<
    FindNewEmailVerifiedOrgUsersResponse,
    unknown,
    void,
    FindNewEmailVerifiedOrgUsersPathParams
  >
    path={`/user/${id}/findNewEmailVerifiedOrgUsers`}
    {...props}
  />
);

export type UseFindNewEmailVerifiedOrgUsersProps = Omit<
  UseGetProps<
    FindNewEmailVerifiedOrgUsersResponse,
    unknown,
    void,
    FindNewEmailVerifiedOrgUsersPathParams
  >,
  "path"
> &
  FindNewEmailVerifiedOrgUsersPathParams;

export const useFindNewEmailVerifiedOrgUsers = ({
  id,
  ...props
}: UseFindNewEmailVerifiedOrgUsersProps) =>
  useGet<
    FindNewEmailVerifiedOrgUsersResponse,
    unknown,
    void,
    FindNewEmailVerifiedOrgUsersPathParams
  >(
    (paramsInPath: FindNewEmailVerifiedOrgUsersPathParams) =>
      `/user/${paramsInPath.id}/findNewEmailVerifiedOrgUsers`,
    { pathParams: { id }, ...props }
  );

export interface ValidateEmailTokenResponse {
  /**
   * Whether the token validation was successful
   */
  status?: boolean;
  /**
   * Description of the validation result
   */
  desc?: string;
  /**
   * HTTP status code
   */
  statusCode?: number;
}

export interface ValidateEmailTokenRequestBody {
  /**
   * The email verification token
   */
  token?: string;
}

export type ValidateEmailTokenProps = Omit<
  MutateProps<
    ValidateEmailTokenResponse,
    {
      /**
       * Whether the operation was successful
       */
      status?: boolean;
      /**
       * Error description
       */
      desc?: string;
      /**
       * Error details
       */
      error?: string;
      /**
       * HTTP status code
       */
      statusCode?: number;
    },
    void,
    ValidateEmailTokenRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Validate the email verification token
 */
export const ValidateEmailToken = (props: ValidateEmailTokenProps) => (
  <Mutate<
    ValidateEmailTokenResponse,
    {
      /**
       * Whether the operation was successful
       */
      status?: boolean;
      /**
       * Error description
       */
      desc?: string;
      /**
       * Error details
       */
      error?: string;
      /**
       * HTTP status code
       */
      statusCode?: number;
    },
    void,
    ValidateEmailTokenRequestBody,
    void
  >
    verb="POST"
    path={`/user/validateEmailToken`}
    {...props}
  />
);

export type UseValidateEmailTokenProps = Omit<
  UseMutateProps<
    ValidateEmailTokenResponse,
    {
      /**
       * Whether the operation was successful
       */
      status?: boolean;
      /**
       * Error description
       */
      desc?: string;
      /**
       * Error details
       */
      error?: string;
      /**
       * HTTP status code
       */
      statusCode?: number;
    },
    void,
    ValidateEmailTokenRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Validate the email verification token
 */
export const useValidateEmailToken = (props: UseValidateEmailTokenProps) =>
  useMutate<
    ValidateEmailTokenResponse,
    {
      /**
       * Whether the operation was successful
       */
      status?: boolean;
      /**
       * Error description
       */
      desc?: string;
      /**
       * Error details
       */
      error?: string;
      /**
       * HTTP status code
       */
      statusCode?: number;
    },
    void,
    ValidateEmailTokenRequestBody,
    void
  >("POST", `/user/validateEmailToken`, props);

export interface GetAuthDecisionGroupPathParams {
  id: string;
}

export type GetAuthDecisionGroupProps = Omit<
  GetProps<AuthDecisionGroup, unknown, void, GetAuthDecisionGroupPathParams>,
  "path"
> &
  GetAuthDecisionGroupPathParams;

export const GetAuthDecisionGroup = ({
  id,
  ...props
}: GetAuthDecisionGroupProps) => (
  <Get<AuthDecisionGroup, unknown, void, GetAuthDecisionGroupPathParams>
    path={`/authDecisionGroup/${id}`}
    {...props}
  />
);

export type UseGetAuthDecisionGroupProps = Omit<
  UseGetProps<AuthDecisionGroup, unknown, void, GetAuthDecisionGroupPathParams>,
  "path"
> &
  GetAuthDecisionGroupPathParams;

export const useGetAuthDecisionGroup = ({
  id,
  ...props
}: UseGetAuthDecisionGroupProps) =>
  useGet<AuthDecisionGroup, unknown, void, GetAuthDecisionGroupPathParams>(
    (paramsInPath: GetAuthDecisionGroupPathParams) =>
      `/authDecisionGroup/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface WithdrawAuthDecisionGroupPathParams {
  /**
   * auth Decision Group Id.
   */
  authDecisionGroupId: string;
}

export type WithdrawAuthDecisionGroupProps = Omit<
  MutateProps<
    AuthDecisionGroupWithdrawResponse,
    unknown,
    void,
    AuthDecisionGroupWithdraw,
    WithdrawAuthDecisionGroupPathParams
  >,
  "path" | "verb"
> &
  WithdrawAuthDecisionGroupPathParams;

export const WithdrawAuthDecisionGroup = ({
  authDecisionGroupId,
  ...props
}: WithdrawAuthDecisionGroupProps) => (
  <Mutate<
    AuthDecisionGroupWithdrawResponse,
    unknown,
    void,
    AuthDecisionGroupWithdraw,
    WithdrawAuthDecisionGroupPathParams
  >
    verb="POST"
    path={`/authDecisionGroup/${authDecisionGroupId}/withdraw`}
    {...props}
  />
);

export type UseWithdrawAuthDecisionGroupProps = Omit<
  UseMutateProps<
    AuthDecisionGroupWithdrawResponse,
    unknown,
    void,
    AuthDecisionGroupWithdraw,
    WithdrawAuthDecisionGroupPathParams
  >,
  "path" | "verb"
> &
  WithdrawAuthDecisionGroupPathParams;

export const useWithdrawAuthDecisionGroup = ({
  authDecisionGroupId,
  ...props
}: UseWithdrawAuthDecisionGroupProps) =>
  useMutate<
    AuthDecisionGroupWithdrawResponse,
    unknown,
    void,
    AuthDecisionGroupWithdraw,
    WithdrawAuthDecisionGroupPathParams
  >(
    "POST",
    (paramsInPath: WithdrawAuthDecisionGroupPathParams) =>
      `/authDecisionGroup/${paramsInPath.authDecisionGroupId}/withdraw`,
    { pathParams: { authDecisionGroupId }, ...props }
  );

export interface RemoveAuthFromAuthDecisionGroupPathParams {
  serviceRequestId: string;
}

export type RemoveAuthFromAuthDecisionGroupProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    void,
    RemoveAuthFromAuthDecisionGroupPathParams
  >,
  "path" | "verb"
> &
  RemoveAuthFromAuthDecisionGroupPathParams;

export const RemoveAuthFromAuthDecisionGroup = ({
  serviceRequestId,
  ...props
}: RemoveAuthFromAuthDecisionGroupProps) => (
  <Mutate<void, unknown, void, void, RemoveAuthFromAuthDecisionGroupPathParams>
    verb="POST"
    path={`/authDecisionGroup/remove/${serviceRequestId}`}
    {...props}
  />
);

export type UseRemoveAuthFromAuthDecisionGroupProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    void,
    RemoveAuthFromAuthDecisionGroupPathParams
  >,
  "path" | "verb"
> &
  RemoveAuthFromAuthDecisionGroupPathParams;

export const useRemoveAuthFromAuthDecisionGroup = ({
  serviceRequestId,
  ...props
}: UseRemoveAuthFromAuthDecisionGroupProps) =>
  useMutate<
    void,
    unknown,
    void,
    void,
    RemoveAuthFromAuthDecisionGroupPathParams
  >(
    "POST",
    (paramsInPath: RemoveAuthFromAuthDecisionGroupPathParams) =>
      `/authDecisionGroup/remove/${paramsInPath.serviceRequestId}`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateTaggedProviderPathParams {
  /**
   * A unique identifier for a `TaggedProvider`.
   */
  id: string;
}

export interface UpdateTaggedProviderRequestBody {
  tagIds?: string[];
}

export type UpdateTaggedProviderProps = Omit<
  MutateProps<
    Provider,
    unknown,
    void,
    UpdateTaggedProviderRequestBody,
    UpdateTaggedProviderPathParams
  >,
  "path" | "verb"
> &
  UpdateTaggedProviderPathParams;

export const UpdateTaggedProvider = ({
  id,
  ...props
}: UpdateTaggedProviderProps) => (
  <Mutate<
    Provider,
    unknown,
    void,
    UpdateTaggedProviderRequestBody,
    UpdateTaggedProviderPathParams
  >
    verb="PUT"
    path={`/taggedProvider/${id}`}
    {...props}
  />
);

export type UseUpdateTaggedProviderProps = Omit<
  UseMutateProps<
    Provider,
    unknown,
    void,
    UpdateTaggedProviderRequestBody,
    UpdateTaggedProviderPathParams
  >,
  "path" | "verb"
> &
  UpdateTaggedProviderPathParams;

export const useUpdateTaggedProvider = ({
  id,
  ...props
}: UseUpdateTaggedProviderProps) =>
  useMutate<
    Provider,
    unknown,
    void,
    UpdateTaggedProviderRequestBody,
    UpdateTaggedProviderPathParams
  >(
    "PUT",
    (paramsInPath: UpdateTaggedProviderPathParams) =>
      `/taggedProvider/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetOrganizationsQueryParams {
  /**
   * Search organizations matching query
   */
  query?: {};
  /**
   * Maximum number results to return
   */
  max?: {};
}

export type GetOrganizationsProps = Omit<
  GetProps<OrganizationResponse[], unknown, GetOrganizationsQueryParams, void>,
  "path"
>;

/**
 * Will search for organizations if user can access them, or return the current user's org
 */
export const GetOrganizations = (props: GetOrganizationsProps) => (
  <Get<OrganizationResponse[], unknown, GetOrganizationsQueryParams, void>
    path={`/organization`}
    {...props}
  />
);

export type UseGetOrganizationsProps = Omit<
  UseGetProps<
    OrganizationResponse[],
    unknown,
    GetOrganizationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Will search for organizations if user can access them, or return the current user's org
 */
export const useGetOrganizations = (props: UseGetOrganizationsProps) =>
  useGet<OrganizationResponse[], unknown, GetOrganizationsQueryParams, void>(
    `/organization`,
    props
  );

export type CreateOrganizationRequestBody = Organization & {
  associatedTins?: NameTinPair[];
};

export type CreateOrganizationProps = Omit<
  MutateProps<
    OrganizationResponse,
    unknown,
    void,
    CreateOrganizationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const CreateOrganization = (props: CreateOrganizationProps) => (
  <Mutate<
    OrganizationResponse,
    unknown,
    void,
    CreateOrganizationRequestBody,
    void
  >
    verb="POST"
    path={`/organization`}
    {...props}
  />
);

export type UseCreateOrganizationProps = Omit<
  UseMutateProps<
    OrganizationResponse,
    unknown,
    void,
    CreateOrganizationRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useCreateOrganization = (props: UseCreateOrganizationProps) =>
  useMutate<
    OrganizationResponse,
    unknown,
    void,
    CreateOrganizationRequestBody,
    void
  >("POST", `/organization`, props);

export interface GetOrganizationByIdPathParams {
  id: string;
}

export type GetOrganizationByIdProps = Omit<
  GetProps<OrganizationResponse, unknown, void, GetOrganizationByIdPathParams>,
  "path"
> &
  GetOrganizationByIdPathParams;

export const GetOrganizationById = ({
  id,
  ...props
}: GetOrganizationByIdProps) => (
  <Get<OrganizationResponse, unknown, void, GetOrganizationByIdPathParams>
    path={`/organization/${id}`}
    {...props}
  />
);

export type UseGetOrganizationByIdProps = Omit<
  UseGetProps<
    OrganizationResponse,
    unknown,
    void,
    GetOrganizationByIdPathParams
  >,
  "path"
> &
  GetOrganizationByIdPathParams;

export const useGetOrganizationById = ({
  id,
  ...props
}: UseGetOrganizationByIdProps) =>
  useGet<OrganizationResponse, unknown, void, GetOrganizationByIdPathParams>(
    (paramsInPath: GetOrganizationByIdPathParams) =>
      `/organization/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateOrganizationPathParams {
  id: string;
}

export type UpdateOrganizationProps = Omit<
  MutateProps<
    OrganizationResponse,
    unknown,
    void,
    Organization,
    UpdateOrganizationPathParams
  >,
  "path" | "verb"
> &
  UpdateOrganizationPathParams;

/**
 * Update an Organization
 *
 * Updates an existing `Organization`.
 */
export const UpdateOrganization = ({
  id,
  ...props
}: UpdateOrganizationProps) => (
  <Mutate<
    OrganizationResponse,
    unknown,
    void,
    Organization,
    UpdateOrganizationPathParams
  >
    verb="PATCH"
    path={`/organization/${id}`}
    {...props}
  />
);

export type UseUpdateOrganizationProps = Omit<
  UseMutateProps<
    OrganizationResponse,
    unknown,
    void,
    Organization,
    UpdateOrganizationPathParams
  >,
  "path" | "verb"
> &
  UpdateOrganizationPathParams;

/**
 * Update an Organization
 *
 * Updates an existing `Organization`.
 */
export const useUpdateOrganization = ({
  id,
  ...props
}: UseUpdateOrganizationProps) =>
  useMutate<
    OrganizationResponse,
    unknown,
    void,
    Organization,
    UpdateOrganizationPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateOrganizationPathParams) =>
      `/organization/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteOrganizationProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Deletes an organization and all associated users
 */
export const DeleteOrganization = (props: DeleteOrganizationProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/organization`}
    {...props}
  />
);

export type UseDeleteOrganizationProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Deletes an organization and all associated users
 */
export const useDeleteOrganization = (props: UseDeleteOrganizationProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/organization`, {
    ...props,
  });

export interface GetAdminIdsPathParams {
  id: string;
}

export type GetAdminIdsProps = Omit<
  GetProps<OrganizationAdminsResponse, unknown, void, GetAdminIdsPathParams>,
  "path"
> &
  GetAdminIdsPathParams;

export const GetAdminIds = ({ id, ...props }: GetAdminIdsProps) => (
  <Get<OrganizationAdminsResponse, unknown, void, GetAdminIdsPathParams>
    path={`/organization/${id}/admins`}
    {...props}
  />
);

export type UseGetAdminIdsProps = Omit<
  UseGetProps<OrganizationAdminsResponse, unknown, void, GetAdminIdsPathParams>,
  "path"
> &
  GetAdminIdsPathParams;

export const useGetAdminIds = ({ id, ...props }: UseGetAdminIdsProps) =>
  useGet<OrganizationAdminsResponse, unknown, void, GetAdminIdsPathParams>(
    (paramsInPath: GetAdminIdsPathParams) =>
      `/organization/${paramsInPath.id}/admins`,
    { pathParams: { id }, ...props }
  );

export type CreateUserProps = Omit<
  MutateProps<UserResponse, unknown, void, UserCreatePayload, void>,
  "path" | "verb"
>;

export const CreateUser = (props: CreateUserProps) => (
  <Mutate<UserResponse, unknown, void, UserCreatePayload, void>
    verb="POST"
    path={`/user`}
    {...props}
  />
);

export type UseCreateUserProps = Omit<
  UseMutateProps<UserResponse, unknown, void, UserCreatePayload, void>,
  "path" | "verb"
>;

export const useCreateUser = (props: UseCreateUserProps) =>
  useMutate<UserResponse, unknown, void, UserCreatePayload, void>(
    "POST",
    `/user`,
    props
  );

export interface GetUsersQueryParams {
  /**
   * Lookup users belonging to a specific organization
   */
  organizationName?: {};
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: {};
  /**
   * Starting result offset
   */
  offset?: {};
  /**
   * Field to order by
   */
  sort?: {};
  /**
   * Text based user lookup
   */
  query?: {};
  /**
   * Organization user is pended to
   */
  pendingOrganizationId?: {};
}

export type GetUsersProps = Omit<
  GetProps<UserResponse[], unknown, GetUsersQueryParams, void>,
  "path"
>;

export const GetUsers = (props: GetUsersProps) => (
  <Get<UserResponse[], unknown, GetUsersQueryParams, void>
    path={`/user`}
    {...props}
  />
);

export type UseGetUsersProps = Omit<
  UseGetProps<UserResponse[], unknown, GetUsersQueryParams, void>,
  "path"
>;

export const useGetUsers = (props: UseGetUsersProps) =>
  useGet<UserResponse[], unknown, GetUsersQueryParams, void>(`/user`, props);

export interface GetUserByIdQueryParams {
  includeOrganizationName?: boolean;
}

export interface GetUserByIdPathParams {
  id: string;
}

export type GetUserByIdProps = Omit<
  GetProps<
    UserResponse,
    unknown,
    GetUserByIdQueryParams,
    GetUserByIdPathParams
  >,
  "path"
> &
  GetUserByIdPathParams;

export const GetUserById = ({ id, ...props }: GetUserByIdProps) => (
  <Get<UserResponse, unknown, GetUserByIdQueryParams, GetUserByIdPathParams>
    path={`/user/${id}`}
    {...props}
  />
);

export type UseGetUserByIdProps = Omit<
  UseGetProps<
    UserResponse,
    unknown,
    GetUserByIdQueryParams,
    GetUserByIdPathParams
  >,
  "path"
> &
  GetUserByIdPathParams;

export const useGetUserById = ({ id, ...props }: UseGetUserByIdProps) =>
  useGet<UserResponse, unknown, GetUserByIdQueryParams, GetUserByIdPathParams>(
    (paramsInPath: GetUserByIdPathParams) => `/user/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateUserQueryParams {
  includeOrganizationName?: boolean;
}

export interface UpdateUserPathParams {
  id: string;
}

export type UpdateUserProps = Omit<
  MutateProps<
    UserResponse,
    unknown,
    UpdateUserQueryParams,
    User,
    UpdateUserPathParams
  >,
  "path" | "verb"
> &
  UpdateUserPathParams;

export const UpdateUser = ({ id, ...props }: UpdateUserProps) => (
  <Mutate<
    UserResponse,
    unknown,
    UpdateUserQueryParams,
    User,
    UpdateUserPathParams
  >
    verb="PATCH"
    path={`/user/${id}`}
    {...props}
  />
);

export type UseUpdateUserProps = Omit<
  UseMutateProps<
    UserResponse,
    unknown,
    UpdateUserQueryParams,
    User,
    UpdateUserPathParams
  >,
  "path" | "verb"
> &
  UpdateUserPathParams;

export const useUpdateUser = ({ id, ...props }: UseUpdateUserProps) =>
  useMutate<
    UserResponse,
    unknown,
    UpdateUserQueryParams,
    User,
    UpdateUserPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateUserPathParams) => `/user/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface DeleteUserQueryParams {
  includeOrganizationName?: boolean;
}

export type DeleteUserProps = Omit<
  MutateProps<void, unknown, DeleteUserQueryParams, string, void>,
  "path" | "verb"
>;

/**
 * Deletes a user from okta and our db
 */
export const DeleteUser = (props: DeleteUserProps) => (
  <Mutate<void, unknown, DeleteUserQueryParams, string, void>
    verb="DELETE"
    path={`/user`}
    {...props}
  />
);

export type UseDeleteUserProps = Omit<
  UseMutateProps<void, unknown, DeleteUserQueryParams, string, void>,
  "path" | "verb"
>;

/**
 * Deletes a user from okta and our db
 */
export const useDeleteUser = (props: UseDeleteUserProps) =>
  useMutate<void, unknown, DeleteUserQueryParams, string, void>(
    "DELETE",
    `/user`,
    { ...props }
  );

export interface UnlockUserPathParams {
  id: string;
}

export type UnlockUserProps = Omit<
  MutateProps<void, unknown, void, User, UnlockUserPathParams>,
  "path" | "verb"
> &
  UnlockUserPathParams;

export const UnlockUser = ({ id, ...props }: UnlockUserProps) => (
  <Mutate<void, unknown, void, User, UnlockUserPathParams>
    verb="POST"
    path={`/user/${id}/unlock`}
    {...props}
  />
);

export type UseUnlockUserProps = Omit<
  UseMutateProps<void, unknown, void, User, UnlockUserPathParams>,
  "path" | "verb"
> &
  UnlockUserPathParams;

export const useUnlockUser = ({ id, ...props }: UseUnlockUserProps) =>
  useMutate<void, unknown, void, User, UnlockUserPathParams>(
    "POST",
    (paramsInPath: UnlockUserPathParams) => `/user/${paramsInPath.id}/unlock`,
    { pathParams: { id }, ...props }
  );

export interface DenyPendingOrgUserPathParams {
  id: string;
}

export type DenyPendingOrgUserProps = Omit<
  MutateProps<void, unknown, void, User, DenyPendingOrgUserPathParams>,
  "path" | "verb"
> &
  DenyPendingOrgUserPathParams;

export const DenyPendingOrgUser = ({
  id,
  ...props
}: DenyPendingOrgUserProps) => (
  <Mutate<void, unknown, void, User, DenyPendingOrgUserPathParams>
    verb="PUT"
    path={`/user/${id}/denyPendingOrg`}
    {...props}
  />
);

export type UseDenyPendingOrgUserProps = Omit<
  UseMutateProps<void, unknown, void, User, DenyPendingOrgUserPathParams>,
  "path" | "verb"
> &
  DenyPendingOrgUserPathParams;

export const useDenyPendingOrgUser = ({
  id,
  ...props
}: UseDenyPendingOrgUserProps) =>
  useMutate<void, unknown, void, User, DenyPendingOrgUserPathParams>(
    "PUT",
    (paramsInPath: DenyPendingOrgUserPathParams) =>
      `/user/${paramsInPath.id}/denyPendingOrg`,
    { pathParams: { id }, ...props }
  );

export interface ApprovePendingOrgUserPathParams {
  id: string;
}

export type ApprovePendingOrgUserProps = Omit<
  MutateProps<void, unknown, void, User, ApprovePendingOrgUserPathParams>,
  "path" | "verb"
> &
  ApprovePendingOrgUserPathParams;

export const ApprovePendingOrgUser = ({
  id,
  ...props
}: ApprovePendingOrgUserProps) => (
  <Mutate<void, unknown, void, User, ApprovePendingOrgUserPathParams>
    verb="PATCH"
    path={`/user/${id}/approvePendingOrg`}
    {...props}
  />
);

export type UseApprovePendingOrgUserProps = Omit<
  UseMutateProps<void, unknown, void, User, ApprovePendingOrgUserPathParams>,
  "path" | "verb"
> &
  ApprovePendingOrgUserPathParams;

export const useApprovePendingOrgUser = ({
  id,
  ...props
}: UseApprovePendingOrgUserProps) =>
  useMutate<void, unknown, void, User, ApprovePendingOrgUserPathParams>(
    "PATCH",
    (paramsInPath: ApprovePendingOrgUserPathParams) =>
      `/user/${paramsInPath.id}/approvePendingOrg`,
    { pathParams: { id }, ...props }
  );

export interface DeactivateUserPathParams {
  id: string;
}

export type DeactivateUserProps = Omit<
  MutateProps<void, unknown, void, User, DeactivateUserPathParams>,
  "path" | "verb"
> &
  DeactivateUserPathParams;

export const DeactivateUser = ({ id, ...props }: DeactivateUserProps) => (
  <Mutate<void, unknown, void, User, DeactivateUserPathParams>
    verb="POST"
    path={`/user/${id}/deactivate`}
    {...props}
  />
);

export type UseDeactivateUserProps = Omit<
  UseMutateProps<void, unknown, void, User, DeactivateUserPathParams>,
  "path" | "verb"
> &
  DeactivateUserPathParams;

export const useDeactivateUser = ({ id, ...props }: UseDeactivateUserProps) =>
  useMutate<void, unknown, void, User, DeactivateUserPathParams>(
    "POST",
    (paramsInPath: DeactivateUserPathParams) =>
      `/user/${paramsInPath.id}/deactivate`,
    { pathParams: { id }, ...props }
  );

export interface GetUserExtensionByIdPathParams {
  id: string;
}

export type GetUserExtensionByIdProps = Omit<
  GetProps<UserExtension, unknown, void, GetUserExtensionByIdPathParams>,
  "path"
> &
  GetUserExtensionByIdPathParams;

export const GetUserExtensionById = ({
  id,
  ...props
}: GetUserExtensionByIdProps) => (
  <Get<UserExtension, unknown, void, GetUserExtensionByIdPathParams>
    path={`/user/${id}/userExtension`}
    {...props}
  />
);

export type UseGetUserExtensionByIdProps = Omit<
  UseGetProps<UserExtension, unknown, void, GetUserExtensionByIdPathParams>,
  "path"
> &
  GetUserExtensionByIdPathParams;

export const useGetUserExtensionById = ({
  id,
  ...props
}: UseGetUserExtensionByIdProps) =>
  useGet<UserExtension, unknown, void, GetUserExtensionByIdPathParams>(
    (paramsInPath: GetUserExtensionByIdPathParams) =>
      `/user/${paramsInPath.id}/userExtension`,
    { pathParams: { id }, ...props }
  );

export interface UpdateUserExtensionPathParams {
  id: string;
}

export interface UpdateUserExtensionRequestBody {
  mostRecentlyUsedFaxNumber?: string;
}

export type UpdateUserExtensionProps = Omit<
  MutateProps<
    void,
    unknown,
    void,
    UpdateUserExtensionRequestBody,
    UpdateUserExtensionPathParams
  >,
  "path" | "verb"
> &
  UpdateUserExtensionPathParams;

export const UpdateUserExtension = ({
  id,
  ...props
}: UpdateUserExtensionProps) => (
  <Mutate<
    void,
    unknown,
    void,
    UpdateUserExtensionRequestBody,
    UpdateUserExtensionPathParams
  >
    verb="PUT"
    path={`/user/${id}/userExtension`}
    {...props}
  />
);

export type UseUpdateUserExtensionProps = Omit<
  UseMutateProps<
    void,
    unknown,
    void,
    UpdateUserExtensionRequestBody,
    UpdateUserExtensionPathParams
  >,
  "path" | "verb"
> &
  UpdateUserExtensionPathParams;

export const useUpdateUserExtension = ({
  id,
  ...props
}: UseUpdateUserExtensionProps) =>
  useMutate<
    void,
    unknown,
    void,
    UpdateUserExtensionRequestBody,
    UpdateUserExtensionPathParams
  >(
    "PUT",
    (paramsInPath: UpdateUserExtensionPathParams) =>
      `/user/${paramsInPath.id}/userExtension`,
    { pathParams: { id }, ...props }
  );

export interface ResendActivationEmailPathParams {
  id: string;
}

export type ResendActivationEmailProps = Omit<
  MutateProps<void, unknown, void, User, ResendActivationEmailPathParams>,
  "path" | "verb"
> &
  ResendActivationEmailPathParams;

export const ResendActivationEmail = ({
  id,
  ...props
}: ResendActivationEmailProps) => (
  <Mutate<void, unknown, void, User, ResendActivationEmailPathParams>
    verb="POST"
    path={`/user/${id}/resendActivationEmail`}
    {...props}
  />
);

export type UseResendActivationEmailProps = Omit<
  UseMutateProps<void, unknown, void, User, ResendActivationEmailPathParams>,
  "path" | "verb"
> &
  ResendActivationEmailPathParams;

export const useResendActivationEmail = ({
  id,
  ...props
}: UseResendActivationEmailProps) =>
  useMutate<void, unknown, void, User, ResendActivationEmailPathParams>(
    "POST",
    (paramsInPath: ResendActivationEmailPathParams) =>
      `/user/${paramsInPath.id}/resendActivationEmail`,
    { pathParams: { id }, ...props }
  );

export interface ValidateUniqueEmailResponse {
  unique?: boolean;
}

export type ValidateUniqueEmailProps = Omit<
  GetProps<ValidateUniqueEmailResponse, unknown, void, void>,
  "path"
>;

export const ValidateUniqueEmail = (props: ValidateUniqueEmailProps) => (
  <Get<ValidateUniqueEmailResponse, unknown, void, void>
    path={`/user/validateUniqueEmail`}
    {...props}
  />
);

export type UseValidateUniqueEmailProps = Omit<
  UseGetProps<ValidateUniqueEmailResponse, unknown, void, void>,
  "path"
>;

export const useValidateUniqueEmail = (props: UseValidateUniqueEmailProps) =>
  useGet<ValidateUniqueEmailResponse, unknown, void, void>(
    `/user/validateUniqueEmail`,
    props
  );

export interface UpdateUserOpsGroupsResponse {
  successfulUpdates: {}[];
  failedUpdates: {}[];
}

export interface UpdateUserOpsGroupsRequestBody {
  newOpsGroup: string;
  logins: string[];
}

export type UpdateUserOpsGroupsProps = Omit<
  MutateProps<
    UpdateUserOpsGroupsResponse,
    unknown,
    void,
    UpdateUserOpsGroupsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const UpdateUserOpsGroups = (props: UpdateUserOpsGroupsProps) => (
  <Mutate<
    UpdateUserOpsGroupsResponse,
    unknown,
    void,
    UpdateUserOpsGroupsRequestBody,
    void
  >
    verb="POST"
    path={`/user/updateUserOpsGroups`}
    {...props}
  />
);

export type UseUpdateUserOpsGroupsProps = Omit<
  UseMutateProps<
    UpdateUserOpsGroupsResponse,
    unknown,
    void,
    UpdateUserOpsGroupsRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useUpdateUserOpsGroups = (props: UseUpdateUserOpsGroupsProps) =>
  useMutate<
    UpdateUserOpsGroupsResponse,
    unknown,
    void,
    UpdateUserOpsGroupsRequestBody,
    void
  >("POST", `/user/updateUserOpsGroups`, props);

export interface AddRoleToUsersResponse {
  successfulUpdates: {}[];
  failedUpdates: {}[];
}

export interface AddRoleToUsersRequestBody {
  role: string;
  logins: string[];
}

export type AddRoleToUsersProps = Omit<
  MutateProps<
    AddRoleToUsersResponse,
    unknown,
    void,
    AddRoleToUsersRequestBody,
    void
  >,
  "path" | "verb"
>;

export const AddRoleToUsers = (props: AddRoleToUsersProps) => (
  <Mutate<
    AddRoleToUsersResponse,
    unknown,
    void,
    AddRoleToUsersRequestBody,
    void
  >
    verb="POST"
    path={`/user/addRoleToUsers`}
    {...props}
  />
);

export type UseAddRoleToUsersProps = Omit<
  UseMutateProps<
    AddRoleToUsersResponse,
    unknown,
    void,
    AddRoleToUsersRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useAddRoleToUsers = (props: UseAddRoleToUsersProps) =>
  useMutate<
    AddRoleToUsersResponse,
    unknown,
    void,
    AddRoleToUsersRequestBody,
    void
  >("POST", `/user/addRoleToUsers`, props);

export interface RemoveRoleFromUsersResponse {
  successfulUpdates: {}[];
  failedUpdates: {}[];
}

export interface RemoveRoleFromUsersRequestBody {
  role: string;
  logins: string[];
}

export type RemoveRoleFromUsersProps = Omit<
  MutateProps<
    RemoveRoleFromUsersResponse,
    unknown,
    void,
    RemoveRoleFromUsersRequestBody,
    void
  >,
  "path" | "verb"
>;

export const RemoveRoleFromUsers = (props: RemoveRoleFromUsersProps) => (
  <Mutate<
    RemoveRoleFromUsersResponse,
    unknown,
    void,
    RemoveRoleFromUsersRequestBody,
    void
  >
    verb="POST"
    path={`/user/removeRoleFromUsers`}
    {...props}
  />
);

export type UseRemoveRoleFromUsersProps = Omit<
  UseMutateProps<
    RemoveRoleFromUsersResponse,
    unknown,
    void,
    RemoveRoleFromUsersRequestBody,
    void
  >,
  "path" | "verb"
>;

export const useRemoveRoleFromUsers = (props: UseRemoveRoleFromUsersProps) =>
  useMutate<
    RemoveRoleFromUsersResponse,
    unknown,
    void,
    RemoveRoleFromUsersRequestBody,
    void
  >("POST", `/user/removeRoleFromUsers`, props);

export interface ValidateIfEmailAlreadyExistsResponse {
  userAlreadyExists?: boolean;
}

export interface ValidateIfEmailAlreadyExistsQueryParams {
  /**
   * The full email entered in the onboarding flow
   */
  email?: string;
}

export type ValidateIfEmailAlreadyExistsProps = Omit<
  GetProps<
    ValidateIfEmailAlreadyExistsResponse,
    unknown,
    ValidateIfEmailAlreadyExistsQueryParams,
    void
  >,
  "path"
>;

export const ValidateIfEmailAlreadyExists = (
  props: ValidateIfEmailAlreadyExistsProps
) => (
  <Get<
    ValidateIfEmailAlreadyExistsResponse,
    unknown,
    ValidateIfEmailAlreadyExistsQueryParams,
    void
  >
    path={`/onboarding/userEmailAlreadyExists`}
    {...props}
  />
);

export type UseValidateIfEmailAlreadyExistsProps = Omit<
  UseGetProps<
    ValidateIfEmailAlreadyExistsResponse,
    unknown,
    ValidateIfEmailAlreadyExistsQueryParams,
    void
  >,
  "path"
>;

export const useValidateIfEmailAlreadyExists = (
  props: UseValidateIfEmailAlreadyExistsProps
) =>
  useGet<
    ValidateIfEmailAlreadyExistsResponse,
    unknown,
    ValidateIfEmailAlreadyExistsQueryParams,
    void
  >(`/onboarding/userEmailAlreadyExists`, props);

export type GetUserOrganizationProps = Omit<
  GetProps<OrganizationResponse, unknown, void, void>,
  "path"
>;

/**
 * Returns the user's organization info
 */
export const GetUserOrganization = (props: GetUserOrganizationProps) => (
  <Get<OrganizationResponse, unknown, void, void>
    path={`/user/getUserOrganization`}
    {...props}
  />
);

export type UseGetUserOrganizationProps = Omit<
  UseGetProps<OrganizationResponse, unknown, void, void>,
  "path"
>;

/**
 * Returns the user's organization info
 */
export const useGetUserOrganization = (props: UseGetUserOrganizationProps) =>
  useGet<OrganizationResponse, unknown, void, void>(
    `/user/getUserOrganization`,
    props
  );

export type RenameServiceRequestCollectionProps = Omit<
  MutateProps<void, unknown, void, void, void>,
  "path" | "verb"
>;

export const RenameServiceRequestCollection = (
  props: RenameServiceRequestCollectionProps
) => (
  <Mutate<void, unknown, void, void, void>
    verb="POST"
    path={`/admin/renameServiceRequestCollection`}
    {...props}
  />
);

export type UseRenameServiceRequestCollectionProps = Omit<
  UseMutateProps<void, unknown, void, void, void>,
  "path" | "verb"
>;

export const useRenameServiceRequestCollection = (
  props: UseRenameServiceRequestCollectionProps
) =>
  useMutate<void, unknown, void, void, void>(
    "POST",
    `/admin/renameServiceRequestCollection`,
    props
  );

export type AddUserActivityProps = Omit<
  MutateProps<void, unknown, void, UserActivity, void>,
  "path" | "verb"
>;

export const AddUserActivity = (props: AddUserActivityProps) => (
  <Mutate<void, unknown, void, UserActivity, void>
    verb="POST"
    path={`/userActivity`}
    {...props}
  />
);

export type UseAddUserActivityProps = Omit<
  UseMutateProps<void, unknown, void, UserActivity, void>,
  "path" | "verb"
>;

export const useAddUserActivity = (props: UseAddUserActivityProps) =>
  useMutate<void, unknown, void, UserActivity, void>(
    "POST",
    `/userActivity`,
    props
  );

export type UserActivityBatchUpdateProps = Omit<
  MutateProps<void, unknown, void, UserActivity[], void>,
  "path" | "verb"
>;

export const UserActivityBatchUpdate = (
  props: UserActivityBatchUpdateProps
) => (
  <Mutate<void, unknown, void, UserActivity[], void>
    verb="POST"
    path={`/userActivity/batchUpdate`}
    {...props}
  />
);

export type UseUserActivityBatchUpdateProps = Omit<
  UseMutateProps<void, unknown, void, UserActivity[], void>,
  "path" | "verb"
>;

export const useUserActivityBatchUpdate = (
  props: UseUserActivityBatchUpdateProps
) =>
  useMutate<void, unknown, void, UserActivity[], void>(
    "POST",
    `/userActivity/batchUpdate`,
    props
  );

export interface GetVersionsQueryParams {
  /**
   * Searches the search fields on the each object
   */
  query?: {};
  /**
   * the type of object to search
   */
  objectType?: {};
  /**
   * current draft status
   */
  currentDraft?: {};
  /**
   * current published status
   */
  currentPublished?: {};
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: {};
  /**
   * starting with result number
   */
  offset?: {};
}

export type GetVersionsProps = Omit<
  GetProps<VersionedObject[], unknown, GetVersionsQueryParams, void>,
  "path"
>;

/**
 * List All versions
 *
 * Gets a list of all `Versionable` entities.
 */
export const GetVersions = (props: GetVersionsProps) => (
  <Get<VersionedObject[], unknown, GetVersionsQueryParams, void>
    path={`/version`}
    {...props}
  />
);

export type UseGetVersionsProps = Omit<
  UseGetProps<VersionedObject[], unknown, GetVersionsQueryParams, void>,
  "path"
>;

/**
 * List All versions
 *
 * Gets a list of all `Versionable` entities.
 */
export const useGetVersions = (props: UseGetVersionsProps) =>
  useGet<VersionedObject[], unknown, GetVersionsQueryParams, void>(
    `/version`,
    props
  );

export interface GetInitiativesQueryParams {
  /**
   * Initiative title
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
}

export type GetInitiativesProps = Omit<
  GetProps<Initiative[], unknown, GetInitiativesQueryParams, void>,
  "path"
>;

/**
 * List All Initiatives
 *
 * Gets a list of all `Initiative` entities
 */
export const GetInitiatives = (props: GetInitiativesProps) => (
  <Get<Initiative[], unknown, GetInitiativesQueryParams, void>
    path={`/initiative`}
    {...props}
  />
);

export type UseGetInitiativesProps = Omit<
  UseGetProps<Initiative[], unknown, GetInitiativesQueryParams, void>,
  "path"
>;

/**
 * List All Initiatives
 *
 * Gets a list of all `Initiative` entities
 */
export const useGetInitiatives = (props: UseGetInitiativesProps) =>
  useGet<Initiative[], unknown, GetInitiativesQueryParams, void>(
    `/initiative`,
    props
  );

export type CreateInitiativeProps = Omit<
  MutateProps<Initiative, ErrorResponse | void, void, Initiative, void>,
  "path" | "verb"
>;

/**
 * Create an `Initiative` entity
 *
 * Creates a new instance of an `Initiative`
 */
export const CreateInitiative = (props: CreateInitiativeProps) => (
  <Mutate<Initiative, ErrorResponse | void, void, Initiative, void>
    verb="POST"
    path={`/initiative`}
    {...props}
  />
);

export type UseCreateInitiativeProps = Omit<
  UseMutateProps<Initiative, ErrorResponse | void, void, Initiative, void>,
  "path" | "verb"
>;

/**
 * Create an `Initiative` entity
 *
 * Creates a new instance of an `Initiative`
 */
export const useCreateInitiative = (props: UseCreateInitiativeProps) =>
  useMutate<Initiative, ErrorResponse | void, void, Initiative, void>(
    "POST",
    `/initiative`,
    props
  );

export interface GetReusableConditionsQueryParams {
  /**
   * ReusableCondition title
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
}

export type GetReusableConditionsProps = Omit<
  GetProps<
    ReusableCondition[],
    unknown,
    GetReusableConditionsQueryParams,
    void
  >,
  "path"
>;

/**
 * List All ReusableConditions
 *
 * Gets a list of all `ReusableCondition` entities
 */
export const GetReusableConditions = (props: GetReusableConditionsProps) => (
  <Get<ReusableCondition[], unknown, GetReusableConditionsQueryParams, void>
    path={`/reusableCondition`}
    {...props}
  />
);

export type UseGetReusableConditionsProps = Omit<
  UseGetProps<
    ReusableCondition[],
    unknown,
    GetReusableConditionsQueryParams,
    void
  >,
  "path"
>;

/**
 * List All ReusableConditions
 *
 * Gets a list of all `ReusableCondition` entities
 */
export const useGetReusableConditions = (
  props: UseGetReusableConditionsProps
) =>
  useGet<ReusableCondition[], unknown, GetReusableConditionsQueryParams, void>(
    `/reusableCondition`,
    props
  );

export type CreateReusableConditionProps = Omit<
  MutateProps<
    ReusableCondition,
    ErrorResponse | void,
    void,
    ReusableCondition,
    void
  >,
  "path" | "verb"
>;

/**
 * Create an `ReusableCondition` entity
 *
 * Creates a new instance of an `ReusableCondition`
 */
export const CreateReusableCondition = (
  props: CreateReusableConditionProps
) => (
  <Mutate<
    ReusableCondition,
    ErrorResponse | void,
    void,
    ReusableCondition,
    void
  >
    verb="POST"
    path={`/reusableCondition`}
    {...props}
  />
);

export type UseCreateReusableConditionProps = Omit<
  UseMutateProps<
    ReusableCondition,
    ErrorResponse | void,
    void,
    ReusableCondition,
    void
  >,
  "path" | "verb"
>;

/**
 * Create an `ReusableCondition` entity
 *
 * Creates a new instance of an `ReusableCondition`
 */
export const useCreateReusableCondition = (
  props: UseCreateReusableConditionProps
) =>
  useMutate<
    ReusableCondition,
    ErrorResponse | void,
    void,
    ReusableCondition,
    void
  >("POST", `/reusableCondition`, props);

export interface GetSingleReusableConditionPathParams {
  /**
   * A unique identifier for a `ReusableCondition`.
   */
  id: string;
}

export type GetSingleReusableConditionProps = Omit<
  GetProps<
    ReusableCondition,
    unknown,
    void,
    GetSingleReusableConditionPathParams
  >,
  "path"
> &
  GetSingleReusableConditionPathParams;

/**
 * Gets the requested ReusableCondition
 *
 * Gets the details of a single instance of a `ReusableCondition`.
 */
export const GetSingleReusableCondition = ({
  id,
  ...props
}: GetSingleReusableConditionProps) => (
  <Get<ReusableCondition, unknown, void, GetSingleReusableConditionPathParams>
    path={`/reusableCondition/${id}`}
    {...props}
  />
);

export type UseGetSingleReusableConditionProps = Omit<
  UseGetProps<
    ReusableCondition,
    unknown,
    void,
    GetSingleReusableConditionPathParams
  >,
  "path"
> &
  GetSingleReusableConditionPathParams;

/**
 * Gets the requested ReusableCondition
 *
 * Gets the details of a single instance of a `ReusableCondition`.
 */
export const useGetSingleReusableCondition = ({
  id,
  ...props
}: UseGetSingleReusableConditionProps) =>
  useGet<
    ReusableCondition,
    unknown,
    void,
    GetSingleReusableConditionPathParams
  >(
    (paramsInPath: GetSingleReusableConditionPathParams) =>
      `/reusableCondition/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateReusableConditionPathParams {
  /**
   * A unique identifier for a `ReusableCondition`.
   */
  id: string;
}

export type UpdateReusableConditionProps = Omit<
  MutateProps<
    ReusableCondition,
    unknown,
    void,
    ReusableCondition,
    UpdateReusableConditionPathParams
  >,
  "path" | "verb"
> &
  UpdateReusableConditionPathParams;

/**
 * Update an ReusableCondition
 *
 * Updates an existing `ReusableCondition`.
 */
export const UpdateReusableCondition = ({
  id,
  ...props
}: UpdateReusableConditionProps) => (
  <Mutate<
    ReusableCondition,
    unknown,
    void,
    ReusableCondition,
    UpdateReusableConditionPathParams
  >
    verb="PUT"
    path={`/reusableCondition/${id}`}
    {...props}
  />
);

export type UseUpdateReusableConditionProps = Omit<
  UseMutateProps<
    ReusableCondition,
    unknown,
    void,
    ReusableCondition,
    UpdateReusableConditionPathParams
  >,
  "path" | "verb"
> &
  UpdateReusableConditionPathParams;

/**
 * Update an ReusableCondition
 *
 * Updates an existing `ReusableCondition`.
 */
export const useUpdateReusableCondition = ({
  id,
  ...props
}: UseUpdateReusableConditionProps) =>
  useMutate<
    ReusableCondition,
    unknown,
    void,
    ReusableCondition,
    UpdateReusableConditionPathParams
  >(
    "PUT",
    (paramsInPath: UpdateReusableConditionPathParams) =>
      `/reusableCondition/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteReusableConditionProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a ReusableCondition
 *
 * Deletes an existing `ReusableCondition`.
 */
export const DeleteReusableCondition = (
  props: DeleteReusableConditionProps
) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/reusableCondition`}
    {...props}
  />
);

export type UseDeleteReusableConditionProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a ReusableCondition
 *
 * Deletes an existing `ReusableCondition`.
 */
export const useDeleteReusableCondition = (
  props: UseDeleteReusableConditionProps
) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/reusableCondition`, {
    ...props,
  });

export interface GetReusableConditionCountQueryParams {
  /**
   * services for rule by name
   */
  query?: {};
  status?: string;
  name?: string;
  description?: string;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collection of drafts with productionReady set to true or false
   */
  productionReady?: string;
}

export type GetReusableConditionCountProps = Omit<
  GetProps<CountResult, unknown, GetReusableConditionCountQueryParams, void>,
  "path"
>;

export const GetReusableConditionCount = (
  props: GetReusableConditionCountProps
) => (
  <Get<CountResult, unknown, GetReusableConditionCountQueryParams, void>
    path={`/reusableCondition/count`}
    {...props}
  />
);

export type UseGetReusableConditionCountProps = Omit<
  UseGetProps<CountResult, unknown, GetReusableConditionCountQueryParams, void>,
  "path"
>;

export const useGetReusableConditionCount = (
  props: UseGetReusableConditionCountProps
) =>
  useGet<CountResult, unknown, GetReusableConditionCountQueryParams, void>(
    `/reusableCondition/count`,
    props
  );

export interface GetRulesUsingReusableConditionQueryParams {
  /**
   * services for rule by name
   */
  reusableConditionId: string;
}

export type GetRulesUsingReusableConditionProps = Omit<
  GetProps<
    ServiceRequestRule[],
    unknown,
    GetRulesUsingReusableConditionQueryParams,
    void
  >,
  "path"
>;

export const GetRulesUsingReusableCondition = (
  props: GetRulesUsingReusableConditionProps
) => (
  <Get<
    ServiceRequestRule[],
    unknown,
    GetRulesUsingReusableConditionQueryParams,
    void
  >
    path={`/reusableCondition/rulesUsingReusableCondition`}
    {...props}
  />
);

export type UseGetRulesUsingReusableConditionProps = Omit<
  UseGetProps<
    ServiceRequestRule[],
    unknown,
    GetRulesUsingReusableConditionQueryParams,
    void
  >,
  "path"
>;

export const useGetRulesUsingReusableCondition = (
  props: UseGetRulesUsingReusableConditionProps
) =>
  useGet<
    ServiceRequestRule[],
    unknown,
    GetRulesUsingReusableConditionQueryParams,
    void
  >(`/reusableCondition/rulesUsingReusableCondition`, props);

export interface GetQuestionsUsingReusableConditionQueryParams {
  /**
   * searches for question by reusableConditionId
   */
  reusableConditionId: string;
}

export type GetQuestionsUsingReusableConditionProps = Omit<
  GetProps<
    ClinicalQuestion[],
    unknown,
    GetQuestionsUsingReusableConditionQueryParams,
    void
  >,
  "path"
>;

export const GetQuestionsUsingReusableCondition = (
  props: GetQuestionsUsingReusableConditionProps
) => (
  <Get<
    ClinicalQuestion[],
    unknown,
    GetQuestionsUsingReusableConditionQueryParams,
    void
  >
    path={`/reusableCondition/questionsUsingReusableCondition`}
    {...props}
  />
);

export type UseGetQuestionsUsingReusableConditionProps = Omit<
  UseGetProps<
    ClinicalQuestion[],
    unknown,
    GetQuestionsUsingReusableConditionQueryParams,
    void
  >,
  "path"
>;

export const useGetQuestionsUsingReusableCondition = (
  props: UseGetQuestionsUsingReusableConditionProps
) =>
  useGet<
    ClinicalQuestion[],
    unknown,
    GetQuestionsUsingReusableConditionQueryParams,
    void
  >(`/reusableCondition/questionsUsingReusableCondition`, props);

export interface UpdateReusableConditionMappingsFromRulePathParams {
  /**
   * The ID of the rule being applied or removed.
   */
  ruleId: string;
}

export interface UpdateReusableConditionMappingsFromRuleRequestBody {
  /**
   * List of reusableCondition IDs from which the rule should be removed.
   */
  reusableConditionsWithRemovals: string[];
  /**
   * List of reusableCondition IDs to which the rule should be added.
   */
  reusableConditionsWithAdditions: string[];
  /**
   * A map of reusableConditionId to Indication object.
   */
  reusableConditionCIMAdditions?: {
    [key: string]: Indication[];
  };
}

export type UpdateReusableConditionMappingsFromRuleProps = Omit<
  MutateProps<
    ReusableCondition[],
    void,
    void,
    UpdateReusableConditionMappingsFromRuleRequestBody,
    UpdateReusableConditionMappingsFromRulePathParams
  >,
  "path" | "verb"
> &
  UpdateReusableConditionMappingsFromRulePathParams;

/**
 * Update ReusableCondition Mappings from Rule
 *
 * Updates the ruleIds for components by adding or removing a rule ID.
 */
export const UpdateReusableConditionMappingsFromRule = ({
  ruleId,
  ...props
}: UpdateReusableConditionMappingsFromRuleProps) => (
  <Mutate<
    ReusableCondition[],
    void,
    void,
    UpdateReusableConditionMappingsFromRuleRequestBody,
    UpdateReusableConditionMappingsFromRulePathParams
  >
    verb="PUT"
    path={`/reusableCondition/updateReusableConditionMappingsFromRule/${ruleId}`}
    {...props}
  />
);

export type UseUpdateReusableConditionMappingsFromRuleProps = Omit<
  UseMutateProps<
    ReusableCondition[],
    void,
    void,
    UpdateReusableConditionMappingsFromRuleRequestBody,
    UpdateReusableConditionMappingsFromRulePathParams
  >,
  "path" | "verb"
> &
  UpdateReusableConditionMappingsFromRulePathParams;

/**
 * Update ReusableCondition Mappings from Rule
 *
 * Updates the ruleIds for components by adding or removing a rule ID.
 */
export const useUpdateReusableConditionMappingsFromRule = ({
  ruleId,
  ...props
}: UseUpdateReusableConditionMappingsFromRuleProps) =>
  useMutate<
    ReusableCondition[],
    void,
    void,
    UpdateReusableConditionMappingsFromRuleRequestBody,
    UpdateReusableConditionMappingsFromRulePathParams
  >(
    "PUT",
    (paramsInPath: UpdateReusableConditionMappingsFromRulePathParams) =>
      `/reusableCondition/updateReusableConditionMappingsFromRule/${paramsInPath.ruleId}`,
    { pathParams: { ruleId }, ...props }
  );

export interface UpdateRulesUsingReusableConditionsPathParams {
  /**
   * The ID of the reusableCondition being updated accross rules.
   */
  reusableConditionId: string;
}

export type UpdateRulesUsingReusableConditionsProps = Omit<
  MutateProps<
    ReusableCondition[],
    void,
    void,
    void,
    UpdateRulesUsingReusableConditionsPathParams
  >,
  "path" | "verb"
> &
  UpdateRulesUsingReusableConditionsPathParams;

/**
 * Update ReusableCondition instance across rules
 *
 * Updates the each occurrence of the previous version of a reusableCondition to the latest one
 */
export const UpdateRulesUsingReusableConditions = ({
  reusableConditionId,
  ...props
}: UpdateRulesUsingReusableConditionsProps) => (
  <Mutate<
    ReusableCondition[],
    void,
    void,
    void,
    UpdateRulesUsingReusableConditionsPathParams
  >
    verb="PUT"
    path={`/reusableCondition/updateRulesUsingReusableConditions/${reusableConditionId}`}
    {...props}
  />
);

export type UseUpdateRulesUsingReusableConditionsProps = Omit<
  UseMutateProps<
    ReusableCondition[],
    void,
    void,
    void,
    UpdateRulesUsingReusableConditionsPathParams
  >,
  "path" | "verb"
> &
  UpdateRulesUsingReusableConditionsPathParams;

/**
 * Update ReusableCondition instance across rules
 *
 * Updates the each occurrence of the previous version of a reusableCondition to the latest one
 */
export const useUpdateRulesUsingReusableConditions = ({
  reusableConditionId,
  ...props
}: UseUpdateRulesUsingReusableConditionsProps) =>
  useMutate<
    ReusableCondition[],
    void,
    void,
    void,
    UpdateRulesUsingReusableConditionsPathParams
  >(
    "PUT",
    (paramsInPath: UpdateRulesUsingReusableConditionsPathParams) =>
      `/reusableCondition/updateRulesUsingReusableConditions/${paramsInPath.reusableConditionId}`,
    { pathParams: { reusableConditionId }, ...props }
  );

export interface ValidateReusableConditionUniquenessPathParams {
  reusableConditionId: string;
}

export type ValidateReusableConditionUniquenessProps = Omit<
  MutateProps<
    ReusableCondition[],
    unknown,
    void,
    void,
    ValidateReusableConditionUniquenessPathParams
  >,
  "path" | "verb"
> &
  ValidateReusableConditionUniquenessPathParams;

export const ValidateReusableConditionUniqueness = ({
  reusableConditionId,
  ...props
}: ValidateReusableConditionUniquenessProps) => (
  <Mutate<
    ReusableCondition[],
    unknown,
    void,
    void,
    ValidateReusableConditionUniquenessPathParams
  >
    verb="POST"
    path={`/reusableCondition/${reusableConditionId}/unique`}
    {...props}
  />
);

export type UseValidateReusableConditionUniquenessProps = Omit<
  UseMutateProps<
    ReusableCondition[],
    unknown,
    void,
    void,
    ValidateReusableConditionUniquenessPathParams
  >,
  "path" | "verb"
> &
  ValidateReusableConditionUniquenessPathParams;

export const useValidateReusableConditionUniqueness = ({
  reusableConditionId,
  ...props
}: UseValidateReusableConditionUniquenessProps) =>
  useMutate<
    ReusableCondition[],
    unknown,
    void,
    void,
    ValidateReusableConditionUniquenessPathParams
  >(
    "POST",
    (paramsInPath: ValidateReusableConditionUniquenessPathParams) =>
      `/reusableCondition/${paramsInPath.reusableConditionId}/unique`,
    { pathParams: { reusableConditionId }, ...props }
  );

export interface GetSimulationCountQueryParams {
  /**
   * the rule id
   */
  id: string;
}

export type GetSimulationCountProps = Omit<
  MutateProps<
    SimulationCountPayload,
    unknown,
    GetSimulationCountQueryParams,
    SimulationRequestPayload,
    void
  >,
  "path" | "verb"
>;

/**
 * Get count of SRs
 *
 * Get count of SRs
 */
export const GetSimulationCount = (props: GetSimulationCountProps) => (
  <Mutate<
    SimulationCountPayload,
    unknown,
    GetSimulationCountQueryParams,
    SimulationRequestPayload,
    void
  >
    verb="POST"
    path={`/serviceRequest/simulationCount`}
    {...props}
  />
);

export type UseGetSimulationCountProps = Omit<
  UseMutateProps<
    SimulationCountPayload,
    unknown,
    GetSimulationCountQueryParams,
    SimulationRequestPayload,
    void
  >,
  "path" | "verb"
>;

/**
 * Get count of SRs
 *
 * Get count of SRs
 */
export const useGetSimulationCount = (props: UseGetSimulationCountProps) =>
  useMutate<
    SimulationCountPayload,
    unknown,
    GetSimulationCountQueryParams,
    SimulationRequestPayload,
    void
  >("POST", `/serviceRequest/simulationCount`, props);

export interface GetSingleInitiativePathParams {
  /**
   * A unique identifier for a `Initiative`.
   */
  id: string;
}

export type GetSingleInitiativeProps = Omit<
  GetProps<Initiative, unknown, void, GetSingleInitiativePathParams>,
  "path"
> &
  GetSingleInitiativePathParams;

/**
 * Gets the requested Initiative
 *
 * Gets the details of a single instance of a `Initiative`.
 */
export const GetSingleInitiative = ({
  id,
  ...props
}: GetSingleInitiativeProps) => (
  <Get<Initiative, unknown, void, GetSingleInitiativePathParams>
    path={`/initiative/${id}`}
    {...props}
  />
);

export type UseGetSingleInitiativeProps = Omit<
  UseGetProps<Initiative, unknown, void, GetSingleInitiativePathParams>,
  "path"
> &
  GetSingleInitiativePathParams;

/**
 * Gets the requested Initiative
 *
 * Gets the details of a single instance of a `Initiative`.
 */
export const useGetSingleInitiative = ({
  id,
  ...props
}: UseGetSingleInitiativeProps) =>
  useGet<Initiative, unknown, void, GetSingleInitiativePathParams>(
    (paramsInPath: GetSingleInitiativePathParams) =>
      `/initiative/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateInitiativePathParams {
  /**
   * A unique identifier for a `Initiative`.
   */
  id: string;
}

export type UpdateInitiativeProps = Omit<
  MutateProps<
    Initiative,
    unknown,
    void,
    Initiative,
    UpdateInitiativePathParams
  >,
  "path" | "verb"
> &
  UpdateInitiativePathParams;

/**
 * Update an Initiative
 *
 * Updates an existing `Initiative`.
 */
export const UpdateInitiative = ({ id, ...props }: UpdateInitiativeProps) => (
  <Mutate<Initiative, unknown, void, Initiative, UpdateInitiativePathParams>
    verb="PUT"
    path={`/initiative/${id}`}
    {...props}
  />
);

export type UseUpdateInitiativeProps = Omit<
  UseMutateProps<
    Initiative,
    unknown,
    void,
    Initiative,
    UpdateInitiativePathParams
  >,
  "path" | "verb"
> &
  UpdateInitiativePathParams;

/**
 * Update an Initiative
 *
 * Updates an existing `Initiative`.
 */
export const useUpdateInitiative = ({
  id,
  ...props
}: UseUpdateInitiativeProps) =>
  useMutate<Initiative, unknown, void, Initiative, UpdateInitiativePathParams>(
    "PUT",
    (paramsInPath: UpdateInitiativePathParams) =>
      `/initiative/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteInitiativeProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Initiative
 *
 * Deletes an existing `Initiative`.
 */
export const DeleteInitiative = (props: DeleteInitiativeProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/initiative`}
    {...props}
  />
);

export type UseDeleteInitiativeProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a Initiative
 *
 * Deletes an existing `Initiative`.
 */
export const useDeleteInitiative = (props: UseDeleteInitiativeProps) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/initiative`, {
    ...props,
  });

export interface GetDenialsTemplateQueryParams {
  /**
   * DenialsTemplate templateTitle
   */
  query?: string;
  /**
   * Maximum number of results to request (capped at 100)
   */
  max?: number;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collections of drafts marked as productionReady
   */
  productionReady?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collection of active templates
   */
  active?: string;
}

export type GetDenialsTemplateProps = Omit<
  GetProps<DenialsTemplate[], unknown, GetDenialsTemplateQueryParams, void>,
  "path"
>;

/**
 * List All Denials Templates
 *
 * Gets a list of all `DenialsTemplate` entities
 */
export const GetDenialsTemplate = (props: GetDenialsTemplateProps) => (
  <Get<DenialsTemplate[], unknown, GetDenialsTemplateQueryParams, void>
    path={`/denialsTemplate`}
    {...props}
  />
);

export type UseGetDenialsTemplateProps = Omit<
  UseGetProps<DenialsTemplate[], unknown, GetDenialsTemplateQueryParams, void>,
  "path"
>;

/**
 * List All Denials Templates
 *
 * Gets a list of all `DenialsTemplate` entities
 */
export const useGetDenialsTemplate = (props: UseGetDenialsTemplateProps) =>
  useGet<DenialsTemplate[], unknown, GetDenialsTemplateQueryParams, void>(
    `/denialsTemplate`,
    props
  );

export type CreateDenialsTemplateProps = Omit<
  MutateProps<
    DenialsTemplate,
    ErrorResponse | void,
    void,
    DenialsTemplate,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a `DenialsTemplate` entity
 *
 * Creates a new instance of a `DenialsTemplate`
 */
export const CreateDenialsTemplate = (props: CreateDenialsTemplateProps) => (
  <Mutate<DenialsTemplate, ErrorResponse | void, void, DenialsTemplate, void>
    verb="POST"
    path={`/denialsTemplate`}
    {...props}
  />
);

export type UseCreateDenialsTemplateProps = Omit<
  UseMutateProps<
    DenialsTemplate,
    ErrorResponse | void,
    void,
    DenialsTemplate,
    void
  >,
  "path" | "verb"
>;

/**
 * Create a `DenialsTemplate` entity
 *
 * Creates a new instance of a `DenialsTemplate`
 */
export const useCreateDenialsTemplate = (
  props: UseCreateDenialsTemplateProps
) =>
  useMutate<DenialsTemplate, ErrorResponse | void, void, DenialsTemplate, void>(
    "POST",
    `/denialsTemplate`,
    props
  );

export interface GetDenialsTemplatesCountQueryParams {
  /**
   * DenialsTemplate templateTitle
   */
  query?: string;
  /**
   * returns collection of drafts, published, or all
   */
  versionStatus?: string;
  /**
   * returns collection of active objects
   */
  active?: string;
  /**
   * returns collection of published, unpublished, all
   */
  published?: string;
  /**
   * returns collections of drafts marked as productionReady
   */
  productionReady?: string;
  policyType?: string;
}

export type GetDenialsTemplatesCountProps = Omit<
  GetProps<CountResult, unknown, GetDenialsTemplatesCountQueryParams, void>,
  "path"
>;

/**
 * List All DenialsTemplates
 *
 * Gets a list of all `DenialsTemplates` entities.
 */
export const GetDenialsTemplatesCount = (
  props: GetDenialsTemplatesCountProps
) => (
  <Get<CountResult, unknown, GetDenialsTemplatesCountQueryParams, void>
    path={`/denialsTemplate/count`}
    {...props}
  />
);

export type UseGetDenialsTemplatesCountProps = Omit<
  UseGetProps<CountResult, unknown, GetDenialsTemplatesCountQueryParams, void>,
  "path"
>;

/**
 * List All DenialsTemplates
 *
 * Gets a list of all `DenialsTemplates` entities.
 */
export const useGetDenialsTemplatesCount = (
  props: UseGetDenialsTemplatesCountProps
) =>
  useGet<CountResult, unknown, GetDenialsTemplatesCountQueryParams, void>(
    `/denialsTemplate/count`,
    props
  );

export type TemplatesBatchUpdateProps = Omit<
  MutateProps<DenialsTemplate[], unknown, void, DenialsTemplate[], void>,
  "path" | "verb"
>;

/**
 * Updated DenialsTemplates as published.
 */
export const TemplatesBatchUpdate = (props: TemplatesBatchUpdateProps) => (
  <Mutate<DenialsTemplate[], unknown, void, DenialsTemplate[], void>
    verb="PATCH"
    path={`/denialsTemplate/batch`}
    {...props}
  />
);

export type UseTemplatesBatchUpdateProps = Omit<
  UseMutateProps<DenialsTemplate[], unknown, void, DenialsTemplate[], void>,
  "path" | "verb"
>;

/**
 * Updated DenialsTemplates as published.
 */
export const useTemplatesBatchUpdate = (props: UseTemplatesBatchUpdateProps) =>
  useMutate<DenialsTemplate[], unknown, void, DenialsTemplate[], void>(
    "PATCH",
    `/denialsTemplate/batch`,
    props
  );

export interface GetSingleDenialsTemplatePathParams {
  /**
   * A unique identifier for a `DenialsTemplate`.
   */
  id: string;
}

export type GetSingleDenialsTemplateProps = Omit<
  GetProps<DenialsTemplate, unknown, void, GetSingleDenialsTemplatePathParams>,
  "path"
> &
  GetSingleDenialsTemplatePathParams;

/**
 * Gets the requested DenialsTemplate
 *
 * Gets the details of a single instance of a `DenialsTemplate`.
 */
export const GetSingleDenialsTemplate = ({
  id,
  ...props
}: GetSingleDenialsTemplateProps) => (
  <Get<DenialsTemplate, unknown, void, GetSingleDenialsTemplatePathParams>
    path={`/denialsTemplate/${id}`}
    {...props}
  />
);

export type UseGetSingleDenialsTemplateProps = Omit<
  UseGetProps<
    DenialsTemplate,
    unknown,
    void,
    GetSingleDenialsTemplatePathParams
  >,
  "path"
> &
  GetSingleDenialsTemplatePathParams;

/**
 * Gets the requested DenialsTemplate
 *
 * Gets the details of a single instance of a `DenialsTemplate`.
 */
export const useGetSingleDenialsTemplate = ({
  id,
  ...props
}: UseGetSingleDenialsTemplateProps) =>
  useGet<DenialsTemplate, unknown, void, GetSingleDenialsTemplatePathParams>(
    (paramsInPath: GetSingleDenialsTemplatePathParams) =>
      `/denialsTemplate/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateDenialsTemplatePathParams {
  /**
   * A unique identifier for a `DenialsTemplate`.
   */
  id: string;
}

export type UpdateDenialsTemplateProps = Omit<
  MutateProps<
    DenialsTemplate,
    unknown,
    void,
    DenialsTemplate,
    UpdateDenialsTemplatePathParams
  >,
  "path" | "verb"
> &
  UpdateDenialsTemplatePathParams;

/**
 * Update a DenialsTemplate
 *
 * Updates an existing `DenialsTemplate`.
 */
export const UpdateDenialsTemplate = ({
  id,
  ...props
}: UpdateDenialsTemplateProps) => (
  <Mutate<
    DenialsTemplate,
    unknown,
    void,
    DenialsTemplate,
    UpdateDenialsTemplatePathParams
  >
    verb="PUT"
    path={`/denialsTemplate/${id}`}
    {...props}
  />
);

export type UseUpdateDenialsTemplateProps = Omit<
  UseMutateProps<
    DenialsTemplate,
    unknown,
    void,
    DenialsTemplate,
    UpdateDenialsTemplatePathParams
  >,
  "path" | "verb"
> &
  UpdateDenialsTemplatePathParams;

/**
 * Update a DenialsTemplate
 *
 * Updates an existing `DenialsTemplate`.
 */
export const useUpdateDenialsTemplate = ({
  id,
  ...props
}: UseUpdateDenialsTemplateProps) =>
  useMutate<
    DenialsTemplate,
    unknown,
    void,
    DenialsTemplate,
    UpdateDenialsTemplatePathParams
  >(
    "PUT",
    (paramsInPath: UpdateDenialsTemplatePathParams) =>
      `/denialsTemplate/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteDenialsTemplateProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a DenialsTemplate
 *
 * Deletes an existing `DenialsTemplate`.
 */
export const DeleteDenialsTemplate = (props: DeleteDenialsTemplateProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/denialsTemplate`}
    {...props}
  />
);

export type UseDeleteDenialsTemplateProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Delete a DenialsTemplate
 *
 * Deletes an existing `DenialsTemplate`.
 */
export const useDeleteDenialsTemplate = (
  props: UseDeleteDenialsTemplateProps
) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/denialsTemplate`, {
    ...props,
  });

export interface SearchFinalDeterminationTemplatesQueryParams {
  /**
   * finalDeterminationTemplate Title
   */
  query?: string;
  /**
   * maximum number of results to return
   */
  max?: number;
}

export type SearchFinalDeterminationTemplatesProps = Omit<
  GetProps<
    FinalDeterminationTemplate[],
    unknown,
    SearchFinalDeterminationTemplatesQueryParams,
    void
  >,
  "path"
>;

export const SearchFinalDeterminationTemplates = (
  props: SearchFinalDeterminationTemplatesProps
) => (
  <Get<
    FinalDeterminationTemplate[],
    unknown,
    SearchFinalDeterminationTemplatesQueryParams,
    void
  >
    path={`/finalDeterminationTemplate`}
    {...props}
  />
);

export type UseSearchFinalDeterminationTemplatesProps = Omit<
  UseGetProps<
    FinalDeterminationTemplate[],
    unknown,
    SearchFinalDeterminationTemplatesQueryParams,
    void
  >,
  "path"
>;

export const useSearchFinalDeterminationTemplates = (
  props: UseSearchFinalDeterminationTemplatesProps
) =>
  useGet<
    FinalDeterminationTemplate[],
    unknown,
    SearchFinalDeterminationTemplatesQueryParams,
    void
  >(`/finalDeterminationTemplate`, props);

export type CreateFinalDeterminationTemplateProps = Omit<
  MutateProps<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    void
  >,
  "path" | "verb"
>;

export const CreateFinalDeterminationTemplate = (
  props: CreateFinalDeterminationTemplateProps
) => (
  <Mutate<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    void
  >
    verb="POST"
    path={`/finalDeterminationTemplate`}
    {...props}
  />
);

export type UseCreateFinalDeterminationTemplateProps = Omit<
  UseMutateProps<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    void
  >,
  "path" | "verb"
>;

export const useCreateFinalDeterminationTemplate = (
  props: UseCreateFinalDeterminationTemplateProps
) =>
  useMutate<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    void
  >("POST", `/finalDeterminationTemplate`, props);

export interface UpdateFinalDeterminationTemplatePathParams {
  /**
   * A unique identifier for a `FinalDeterminationTemplate`
   */
  id: string;
}

export type UpdateFinalDeterminationTemplateProps = Omit<
  MutateProps<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    UpdateFinalDeterminationTemplatePathParams
  >,
  "path" | "verb"
> &
  UpdateFinalDeterminationTemplatePathParams;

/**
 * Update existing instance of a `FinalDeterminationTemplate`
 */
export const UpdateFinalDeterminationTemplate = ({
  id,
  ...props
}: UpdateFinalDeterminationTemplateProps) => (
  <Mutate<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    UpdateFinalDeterminationTemplatePathParams
  >
    verb="PATCH"
    path={`/finalDeterminationTemplate/${id}`}
    {...props}
  />
);

export type UseUpdateFinalDeterminationTemplateProps = Omit<
  UseMutateProps<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    UpdateFinalDeterminationTemplatePathParams
  >,
  "path" | "verb"
> &
  UpdateFinalDeterminationTemplatePathParams;

/**
 * Update existing instance of a `FinalDeterminationTemplate`
 */
export const useUpdateFinalDeterminationTemplate = ({
  id,
  ...props
}: UseUpdateFinalDeterminationTemplateProps) =>
  useMutate<
    FinalDeterminationTemplate,
    unknown,
    void,
    FinalDeterminationTemplate,
    UpdateFinalDeterminationTemplatePathParams
  >(
    "PATCH",
    (paramsInPath: UpdateFinalDeterminationTemplatePathParams) =>
      `/finalDeterminationTemplate/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetFinalDeterminationLetterResponse {
  message?: string;
}

export interface GetFinalDeterminationLetterQueryParams {
  serviceRequestId: string;
  reviewId: string;
  reviewOutcome: string;
  generateProviderLetter?: boolean;
}

export type GetFinalDeterminationLetterProps = Omit<
  GetProps<
    GetFinalDeterminationLetterResponse,
    unknown,
    GetFinalDeterminationLetterQueryParams,
    void
  >,
  "path"
>;

/**
 * creates and retrieves the id for a draft Final Determination Letter
 *
 * Creates a Final Determination Letter PDF from an internal template
 *
 */
export const GetFinalDeterminationLetter = (
  props: GetFinalDeterminationLetterProps
) => (
  <Get<
    GetFinalDeterminationLetterResponse,
    unknown,
    GetFinalDeterminationLetterQueryParams,
    void
  >
    path={`/internalFinalDeterminationLetter`}
    {...props}
  />
);

export type UseGetFinalDeterminationLetterProps = Omit<
  UseGetProps<
    GetFinalDeterminationLetterResponse,
    unknown,
    GetFinalDeterminationLetterQueryParams,
    void
  >,
  "path"
>;

/**
 * creates and retrieves the id for a draft Final Determination Letter
 *
 * Creates a Final Determination Letter PDF from an internal template
 *
 */
export const useGetFinalDeterminationLetter = (
  props: UseGetFinalDeterminationLetterProps
) =>
  useGet<
    GetFinalDeterminationLetterResponse,
    unknown,
    GetFinalDeterminationLetterQueryParams,
    void
  >(`/internalFinalDeterminationLetter`, props);

export interface GetInternalFinalDeterminationLettersForServiceRequestResponse {
  message?: InternalFinalDeterminationLetterAttachment[];
}

export interface GetInternalFinalDeterminationLettersForServiceRequestPathParams {
  /**
   * A unique identifier for a `ServiceRequest`
   */
  srId: string;
}

export type GetInternalFinalDeterminationLettersForServiceRequestProps = Omit<
  GetProps<
    GetInternalFinalDeterminationLettersForServiceRequestResponse,
    unknown,
    void,
    GetInternalFinalDeterminationLettersForServiceRequestPathParams
  >,
  "path"
> &
  GetInternalFinalDeterminationLettersForServiceRequestPathParams;

/**
 * fetches the InternalFinalDeterminationLetterAttachment objects for a given Service Request
 *
 * fetches and returns internalFinalDeterminationLetter for a given SR, if they exist
 */
export const GetInternalFinalDeterminationLettersForServiceRequest = ({
  srId,
  ...props
}: GetInternalFinalDeterminationLettersForServiceRequestProps) => (
  <Get<
    GetInternalFinalDeterminationLettersForServiceRequestResponse,
    unknown,
    void,
    GetInternalFinalDeterminationLettersForServiceRequestPathParams
  >
    path={`/internalFinalDeterminationLetter/${srId}`}
    {...props}
  />
);

export type UseGetInternalFinalDeterminationLettersForServiceRequestProps =
  Omit<
    UseGetProps<
      GetInternalFinalDeterminationLettersForServiceRequestResponse,
      unknown,
      void,
      GetInternalFinalDeterminationLettersForServiceRequestPathParams
    >,
    "path"
  > &
    GetInternalFinalDeterminationLettersForServiceRequestPathParams;

/**
 * fetches the InternalFinalDeterminationLetterAttachment objects for a given Service Request
 *
 * fetches and returns internalFinalDeterminationLetter for a given SR, if they exist
 */
export const useGetInternalFinalDeterminationLettersForServiceRequest = ({
  srId,
  ...props
}: UseGetInternalFinalDeterminationLettersForServiceRequestProps) =>
  useGet<
    GetInternalFinalDeterminationLettersForServiceRequestResponse,
    unknown,
    void,
    GetInternalFinalDeterminationLettersForServiceRequestPathParams
  >(
    (
      paramsInPath: GetInternalFinalDeterminationLettersForServiceRequestPathParams
    ) => `/internalFinalDeterminationLetter/${paramsInPath.srId}`,
    { pathParams: { srId }, ...props }
  );

export interface CreateTestFinalDeterminationLetterResponse {
  message?: string;
}

export interface CreateTestFinalDeterminationLetterRequestBody {
  templateBody?: string;
  footer?: string;
  imageList?: string[];
}

export type CreateTestFinalDeterminationLetterProps = Omit<
  MutateProps<
    CreateTestFinalDeterminationLetterResponse,
    unknown,
    void,
    CreateTestFinalDeterminationLetterRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * creates and retrieves the id for a test Final Determination Letter
 *
 * Creates a test Final Determination Letter PDF from an internal template with test data
 *
 */
export const CreateTestFinalDeterminationLetter = (
  props: CreateTestFinalDeterminationLetterProps
) => (
  <Mutate<
    CreateTestFinalDeterminationLetterResponse,
    unknown,
    void,
    CreateTestFinalDeterminationLetterRequestBody,
    void
  >
    verb="POST"
    path={`/internalFinalDeterminationLetter/test`}
    {...props}
  />
);

export type UseCreateTestFinalDeterminationLetterProps = Omit<
  UseMutateProps<
    CreateTestFinalDeterminationLetterResponse,
    unknown,
    void,
    CreateTestFinalDeterminationLetterRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * creates and retrieves the id for a test Final Determination Letter
 *
 * Creates a test Final Determination Letter PDF from an internal template with test data
 *
 */
export const useCreateTestFinalDeterminationLetter = (
  props: UseCreateTestFinalDeterminationLetterProps
) =>
  useMutate<
    CreateTestFinalDeterminationLetterResponse,
    unknown,
    void,
    CreateTestFinalDeterminationLetterRequestBody,
    void
  >("POST", `/internalFinalDeterminationLetter/test`, props);

export interface GetServiceSummaryPDFPathParams {
  id: string;
}

export type GetServiceSummaryPDFProps = Omit<
  GetProps<void, unknown, void, GetServiceSummaryPDFPathParams>,
  "path"
> &
  GetServiceSummaryPDFPathParams;

/**
 * retrieves the summary PDF for a service request
 *
 * Fetches the service summary PDF for a service request
 *
 */
export const GetServiceSummaryPDF = ({
  id,
  ...props
}: GetServiceSummaryPDFProps) => (
  <Get<void, unknown, void, GetServiceSummaryPDFPathParams>
    path={`/serviceRequest/${id}/serviceSummary`}
    {...props}
  />
);

export type UseGetServiceSummaryPDFProps = Omit<
  UseGetProps<void, unknown, void, GetServiceSummaryPDFPathParams>,
  "path"
> &
  GetServiceSummaryPDFPathParams;

/**
 * retrieves the summary PDF for a service request
 *
 * Fetches the service summary PDF for a service request
 *
 */
export const useGetServiceSummaryPDF = ({
  id,
  ...props
}: UseGetServiceSummaryPDFProps) =>
  useGet<void, unknown, void, GetServiceSummaryPDFPathParams>(
    (paramsInPath: GetServiceSummaryPDFPathParams) =>
      `/serviceRequest/${paramsInPath.id}/serviceSummary`,
    { pathParams: { id }, ...props }
  );

export interface GetNewServiceFaxNoticePDFResponse {
  message?: string;
}

export interface GetNewServiceFaxNoticePDFRequestBody {
  srId?: string;
  attachmentId?: string;
}

export type GetNewServiceFaxNoticePDFProps = Omit<
  MutateProps<
    GetNewServiceFaxNoticePDFResponse,
    unknown,
    void,
    GetNewServiceFaxNoticePDFRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * retrieves the new service fax notice PDF for a service request
 *
 * Fetches the new service fax notice PDF for a service request
 *
 */
export const GetNewServiceFaxNoticePDF = (
  props: GetNewServiceFaxNoticePDFProps
) => (
  <Mutate<
    GetNewServiceFaxNoticePDFResponse,
    unknown,
    void,
    GetNewServiceFaxNoticePDFRequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/newServiceFaxNotice`}
    {...props}
  />
);

export type UseGetNewServiceFaxNoticePDFProps = Omit<
  UseMutateProps<
    GetNewServiceFaxNoticePDFResponse,
    unknown,
    void,
    GetNewServiceFaxNoticePDFRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * retrieves the new service fax notice PDF for a service request
 *
 * Fetches the new service fax notice PDF for a service request
 *
 */
export const useGetNewServiceFaxNoticePDF = (
  props: UseGetNewServiceFaxNoticePDFProps
) =>
  useMutate<
    GetNewServiceFaxNoticePDFResponse,
    unknown,
    void,
    GetNewServiceFaxNoticePDFRequestBody,
    void
  >("POST", `/serviceRequest/newServiceFaxNotice`, props);

export interface GetAuthorizationServiceSummaryPDFPathParams {
  id: string;
}

export type GetAuthorizationServiceSummaryPDFProps = Omit<
  GetProps<void, unknown, void, GetAuthorizationServiceSummaryPDFPathParams>,
  "path"
> &
  GetAuthorizationServiceSummaryPDFPathParams;

/**
 * retrieves the service summary PDF for an authorization
 *
 * Fetches the service summary PDF for an authorization
 *
 */
export const GetAuthorizationServiceSummaryPDF = ({
  id,
  ...props
}: GetAuthorizationServiceSummaryPDFProps) => (
  <Get<void, unknown, void, GetAuthorizationServiceSummaryPDFPathParams>
    path={`/authorization/${id}/serviceSummary`}
    {...props}
  />
);

export type UseGetAuthorizationServiceSummaryPDFProps = Omit<
  UseGetProps<void, unknown, void, GetAuthorizationServiceSummaryPDFPathParams>,
  "path"
> &
  GetAuthorizationServiceSummaryPDFPathParams;

/**
 * retrieves the service summary PDF for an authorization
 *
 * Fetches the service summary PDF for an authorization
 *
 */
export const useGetAuthorizationServiceSummaryPDF = ({
  id,
  ...props
}: UseGetAuthorizationServiceSummaryPDFProps) =>
  useGet<void, unknown, void, GetAuthorizationServiceSummaryPDFPathParams>(
    (paramsInPath: GetAuthorizationServiceSummaryPDFPathParams) =>
      `/authorization/${paramsInPath.id}/serviceSummary`,
    { pathParams: { id }, ...props }
  );

export interface AuthorizationTransferPathParams {
  authorizationId: string;
  receiverPatientId: string;
}

export type AuthorizationTransferProps = Omit<
  MutateProps<
    AuthorizationResponse,
    unknown,
    void,
    void,
    AuthorizationTransferPathParams
  >,
  "path" | "verb"
> &
  AuthorizationTransferPathParams;

/**
 * Transfers non-decisioned authorizations between users of the same Health Plan
 *
 * Used to transfer non-decisioned authorizations from one patient to another
 *
 */
export const AuthorizationTransfer = ({
  authorizationId,
  receiverPatientId,
  ...props
}: AuthorizationTransferProps) => (
  <Mutate<
    AuthorizationResponse,
    unknown,
    void,
    void,
    AuthorizationTransferPathParams
  >
    verb="PATCH"
    path={`/authorization/${authorizationId}/transfer/${receiverPatientId}`}
    {...props}
  />
);

export type UseAuthorizationTransferProps = Omit<
  UseMutateProps<
    AuthorizationResponse,
    unknown,
    void,
    void,
    AuthorizationTransferPathParams
  >,
  "path" | "verb"
> &
  AuthorizationTransferPathParams;

/**
 * Transfers non-decisioned authorizations between users of the same Health Plan
 *
 * Used to transfer non-decisioned authorizations from one patient to another
 *
 */
export const useAuthorizationTransfer = ({
  authorizationId,
  receiverPatientId,
  ...props
}: UseAuthorizationTransferProps) =>
  useMutate<
    AuthorizationResponse,
    unknown,
    void,
    void,
    AuthorizationTransferPathParams
  >(
    "PATCH",
    (paramsInPath: AuthorizationTransferPathParams) =>
      `/authorization/${paramsInPath.authorizationId}/transfer/${paramsInPath.receiverPatientId}`,
    { pathParams: { authorizationId, receiverPatientId }, ...props }
  );

export interface GetAllHistoricPatientStayDatesPathParams {
  authorizationId: string;
}

export type GetAllHistoricPatientStayDatesProps = Omit<
  GetProps<
    GroupedPatientStayDate[],
    unknown,
    void,
    GetAllHistoricPatientStayDatesPathParams
  >,
  "path"
> &
  GetAllHistoricPatientStayDatesPathParams;

/**
 * Gets list of all patient history stay dates
 */
export const GetAllHistoricPatientStayDates = ({
  authorizationId,
  ...props
}: GetAllHistoricPatientStayDatesProps) => (
  <Get<
    GroupedPatientStayDate[],
    unknown,
    void,
    GetAllHistoricPatientStayDatesPathParams
  >
    path={`/authorization/${authorizationId}/getAllHistoricPatientStayDates`}
    {...props}
  />
);

export type UseGetAllHistoricPatientStayDatesProps = Omit<
  UseGetProps<
    GroupedPatientStayDate[],
    unknown,
    void,
    GetAllHistoricPatientStayDatesPathParams
  >,
  "path"
> &
  GetAllHistoricPatientStayDatesPathParams;

/**
 * Gets list of all patient history stay dates
 */
export const useGetAllHistoricPatientStayDates = ({
  authorizationId,
  ...props
}: UseGetAllHistoricPatientStayDatesProps) =>
  useGet<
    GroupedPatientStayDate[],
    unknown,
    void,
    GetAllHistoricPatientStayDatesPathParams
  >(
    (paramsInPath: GetAllHistoricPatientStayDatesPathParams) =>
      `/authorization/${paramsInPath.authorizationId}/getAllHistoricPatientStayDates`,
    { pathParams: { authorizationId }, ...props }
  );

export interface GetAuthorizationByIdQueryParams {
  view?: "excludeServiceRequests" | "expanded" | "historyCard";
}

export interface GetAuthorizationByIdPathParams {
  id: string;
}

export type GetAuthorizationByIdProps = Omit<
  GetProps<
    AuthorizationResponse,
    unknown,
    GetAuthorizationByIdQueryParams,
    GetAuthorizationByIdPathParams
  >,
  "path"
> &
  GetAuthorizationByIdPathParams;

/**
 * GET an authorization by its id
 *
 * returns authorization with just summarized version of each service request
 *
 * which fields are returned in serviceRequest field is based on value specified in view query param
 * - when empty (default): ServiceRequestLightResponse
 * - excludeServiceRequests: does not include serviceRequests;
 * - expanded: serviceRequests contains full ServiceRequestResponse
 */
export const GetAuthorizationById = ({
  id,
  ...props
}: GetAuthorizationByIdProps) => (
  <Get<
    AuthorizationResponse,
    unknown,
    GetAuthorizationByIdQueryParams,
    GetAuthorizationByIdPathParams
  >
    path={`/authorization/${id}`}
    {...props}
  />
);

export type UseGetAuthorizationByIdProps = Omit<
  UseGetProps<
    AuthorizationResponse,
    unknown,
    GetAuthorizationByIdQueryParams,
    GetAuthorizationByIdPathParams
  >,
  "path"
> &
  GetAuthorizationByIdPathParams;

/**
 * GET an authorization by its id
 *
 * returns authorization with just summarized version of each service request
 *
 * which fields are returned in serviceRequest field is based on value specified in view query param
 * - when empty (default): ServiceRequestLightResponse
 * - excludeServiceRequests: does not include serviceRequests;
 * - expanded: serviceRequests contains full ServiceRequestResponse
 */
export const useGetAuthorizationById = ({
  id,
  ...props
}: UseGetAuthorizationByIdProps) =>
  useGet<
    AuthorizationResponse,
    unknown,
    GetAuthorizationByIdQueryParams,
    GetAuthorizationByIdPathParams
  >(
    (paramsInPath: GetAuthorizationByIdPathParams) =>
      `/authorization/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateAuthorizationByIdQueryParams {
  view?: "excludeServiceRequests" | "expanded" | "historyCard";
}

export interface UpdateAuthorizationByIdPathParams {
  id: string;
}

export type UpdateAuthorizationByIdProps = Omit<
  MutateProps<
    AuthorizationResponse,
    unknown,
    UpdateAuthorizationByIdQueryParams,
    AuthorizationUpdatePayload,
    UpdateAuthorizationByIdPathParams
  >,
  "path" | "verb"
> &
  UpdateAuthorizationByIdPathParams;

/**
 * Updates an authorization
 */
export const UpdateAuthorizationById = ({
  id,
  ...props
}: UpdateAuthorizationByIdProps) => (
  <Mutate<
    AuthorizationResponse,
    unknown,
    UpdateAuthorizationByIdQueryParams,
    AuthorizationUpdatePayload,
    UpdateAuthorizationByIdPathParams
  >
    verb="PATCH"
    path={`/authorization/${id}`}
    {...props}
  />
);

export type UseUpdateAuthorizationByIdProps = Omit<
  UseMutateProps<
    AuthorizationResponse,
    unknown,
    UpdateAuthorizationByIdQueryParams,
    AuthorizationUpdatePayload,
    UpdateAuthorizationByIdPathParams
  >,
  "path" | "verb"
> &
  UpdateAuthorizationByIdPathParams;

/**
 * Updates an authorization
 */
export const useUpdateAuthorizationById = ({
  id,
  ...props
}: UseUpdateAuthorizationByIdProps) =>
  useMutate<
    AuthorizationResponse,
    unknown,
    UpdateAuthorizationByIdQueryParams,
    AuthorizationUpdatePayload,
    UpdateAuthorizationByIdPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateAuthorizationByIdPathParams) =>
      `/authorization/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams {
  authorizationId: string;
}

export type UpdateAuthorizationAndAllServiceRequestsByAuthNumberProps = Omit<
  MutateProps<
    AuthorizationResponse,
    unknown,
    void,
    ServiceRequestUpdatePayload,
    UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams
  >,
  "path" | "verb"
> &
  UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams;

/**
 * Updates an authorization and all associated service requests by authNumber
 */
export const UpdateAuthorizationAndAllServiceRequestsByAuthNumber = ({
  authorizationId,
  ...props
}: UpdateAuthorizationAndAllServiceRequestsByAuthNumberProps) => (
  <Mutate<
    AuthorizationResponse,
    unknown,
    void,
    ServiceRequestUpdatePayload,
    UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams
  >
    verb="PATCH"
    path={`/authorization/${authorizationId}/bulkUpdate`}
    {...props}
  />
);

export type UseUpdateAuthorizationAndAllServiceRequestsByAuthNumberProps = Omit<
  UseMutateProps<
    AuthorizationResponse,
    unknown,
    void,
    ServiceRequestUpdatePayload,
    UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams
  >,
  "path" | "verb"
> &
  UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams;

/**
 * Updates an authorization and all associated service requests by authNumber
 */
export const useUpdateAuthorizationAndAllServiceRequestsByAuthNumber = ({
  authorizationId,
  ...props
}: UseUpdateAuthorizationAndAllServiceRequestsByAuthNumberProps) =>
  useMutate<
    AuthorizationResponse,
    unknown,
    void,
    ServiceRequestUpdatePayload,
    UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams
  >(
    "PATCH",
    (
      paramsInPath: UpdateAuthorizationAndAllServiceRequestsByAuthNumberPathParams
    ) => `/authorization/${paramsInPath.authorizationId}/bulkUpdate`,
    { pathParams: { authorizationId }, ...props }
  );

export interface AuthorizationSearchRequestBody {
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * Number of results to skip
   */
  offset?: number;
  /**
   * Fuzzy text search on authorization object. Searches against patient name, member id, tracking id (cohereId), authorization number
   */
  query?: string;
  /**
   * Authorization status
   */
  authStatus?: string;
  /**
   * Authorization
   */
  authNumber?: {};
  /**
   * Patient member ID
   */
  patientMemberId?: string;
  /**
   * Patient full name
   */
  patientName?: string;
  /**
   * Start date
   */
  startDate?: {};
  /**
   * End date
   */
  endDate?: {};
  /**
   * Patient ID (can see requests from other providers if this is specified)
   */
  patient?: {};
  /**
   * Direction for sorting
   */
  sort?: "asc" | "desc";
}

export type AuthorizationSearchProps = Omit<
  MutateProps<
    AuthorizationResponse[],
    unknown,
    void,
    AuthorizationSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Using POST so that PHI(memberId, patientName etc...) does not end up in query params
 * Does not search on linked ServiceRequests
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 */
export const AuthorizationSearch = (props: AuthorizationSearchProps) => (
  <Mutate<
    AuthorizationResponse[],
    unknown,
    void,
    AuthorizationSearchRequestBody,
    void
  >
    verb="POST"
    path={`/authorization/search`}
    {...props}
  />
);

export type UseAuthorizationSearchProps = Omit<
  UseMutateProps<
    AuthorizationResponse[],
    unknown,
    void,
    AuthorizationSearchRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Using POST so that PHI(memberId, patientName etc...) does not end up in query params
 * Does not search on linked ServiceRequests
 * If no patient is specified, then the following service requests are returned:
 *   - created by this practice/group
 * If a patient is specified, then the following are returned:
 *   - created by this practice/group
 *   - (view-only) any non-draft by another practice/group
 */
export const useAuthorizationSearch = (props: UseAuthorizationSearchProps) =>
  useMutate<
    AuthorizationResponse[],
    unknown,
    void,
    AuthorizationSearchRequestBody,
    void
  >("POST", `/authorization/search`, props);

export type CreateAuthorizationProps = Omit<
  MutateProps<
    AuthorizationResponse,
    unknown,
    void,
    AuthorizationCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * creates a new authorization
 */
export const CreateAuthorization = (props: CreateAuthorizationProps) => (
  <Mutate<
    AuthorizationResponse,
    unknown,
    void,
    AuthorizationCreatePayload,
    void
  >
    verb="POST"
    path={`/authorization`}
    {...props}
  />
);

export type UseCreateAuthorizationProps = Omit<
  UseMutateProps<
    AuthorizationResponse,
    unknown,
    void,
    AuthorizationCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * creates a new authorization
 */
export const useCreateAuthorization = (props: UseCreateAuthorizationProps) =>
  useMutate<
    AuthorizationResponse,
    unknown,
    void,
    AuthorizationCreatePayload,
    void
  >("POST", `/authorization`, props);

export interface GetAuthorizationsQueryParams {
  /**
   * Patient ID
   */
  patient?: string;
  /**
   * The auth number of the authorization
   */
  authNumber?: string;
  /**
   * The view to return for an authorization
   */
  view?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  sort?: string;
  renderSR?: boolean;
  authStatus?: string;
  "semanticProcedureCodes.code"?: string;
  startDate?: string;
  id?: string;
}

export type GetAuthorizationsProps = Omit<
  GetProps<
    AuthorizationResponse[] | AuthorizationsWithCountResponse,
    unknown,
    GetAuthorizationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Returns a list of authorizations. Filter by patient, authNumber
 *
 */
export const GetAuthorizations = (props: GetAuthorizationsProps) => (
  <Get<
    AuthorizationResponse[] | AuthorizationsWithCountResponse,
    unknown,
    GetAuthorizationsQueryParams,
    void
  >
    path={`/authorization`}
    {...props}
  />
);

export type UseGetAuthorizationsProps = Omit<
  UseGetProps<
    AuthorizationResponse[] | AuthorizationsWithCountResponse,
    unknown,
    GetAuthorizationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Returns a list of authorizations. Filter by patient, authNumber
 *
 */
export const useGetAuthorizations = (props: UseGetAuthorizationsProps) =>
  useGet<
    AuthorizationResponse[] | AuthorizationsWithCountResponse,
    unknown,
    GetAuthorizationsQueryParams,
    void
  >(`/authorization`, props);

export interface AddServiceRequestOnAuthorizationPathParams {
  id: string;
}

export type AddServiceRequestOnAuthorizationProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ServiceRequestCommandPayload,
    AddServiceRequestOnAuthorizationPathParams
  >,
  "path" | "verb"
> &
  AddServiceRequestOnAuthorizationPathParams;

/**
 * Creates a new serviceRequest on an authorization
 */
export const AddServiceRequestOnAuthorization = ({
  id,
  ...props
}: AddServiceRequestOnAuthorizationProps) => (
  <Mutate<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ServiceRequestCommandPayload,
    AddServiceRequestOnAuthorizationPathParams
  >
    verb="POST"
    path={`/authorization/${id}/serviceRequest`}
    {...props}
  />
);

export type UseAddServiceRequestOnAuthorizationProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ServiceRequestCommandPayload,
    AddServiceRequestOnAuthorizationPathParams
  >,
  "path" | "verb"
> &
  AddServiceRequestOnAuthorizationPathParams;

/**
 * Creates a new serviceRequest on an authorization
 */
export const useAddServiceRequestOnAuthorization = ({
  id,
  ...props
}: UseAddServiceRequestOnAuthorizationProps) =>
  useMutate<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    ServiceRequestCommandPayload,
    AddServiceRequestOnAuthorizationPathParams
  >(
    "POST",
    (paramsInPath: AddServiceRequestOnAuthorizationPathParams) =>
      `/authorization/${paramsInPath.id}/serviceRequest`,
    { pathParams: { id }, ...props }
  );

export interface UpdateServiceRequestOnAuthorizationPathParams {
  id: string;
  serviceRequestId: string;
}

export type UpdateServiceRequestOnAuthorizationProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCommandPayload,
    UpdateServiceRequestOnAuthorizationPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestOnAuthorizationPathParams;

/**
 * Updates a serviceRequest on an authorization
 */
export const UpdateServiceRequestOnAuthorization = ({
  id,
  serviceRequestId,
  ...props
}: UpdateServiceRequestOnAuthorizationProps) => (
  <Mutate<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCommandPayload,
    UpdateServiceRequestOnAuthorizationPathParams
  >
    verb="PATCH"
    path={`/authorization/${id}/serviceRequest/${serviceRequestId}`}
    {...props}
  />
);

export type UseUpdateServiceRequestOnAuthorizationProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCommandPayload,
    UpdateServiceRequestOnAuthorizationPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestOnAuthorizationPathParams;

/**
 * Updates a serviceRequest on an authorization
 */
export const useUpdateServiceRequestOnAuthorization = ({
  id,
  serviceRequestId,
  ...props
}: UseUpdateServiceRequestOnAuthorizationProps) =>
  useMutate<
    ServiceRequestResponse,
    unknown,
    void,
    ServiceRequestCommandPayload,
    UpdateServiceRequestOnAuthorizationPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateServiceRequestOnAuthorizationPathParams) =>
      `/authorization/${paramsInPath.id}/serviceRequest/${paramsInPath.serviceRequestId}`,
    { pathParams: { id, serviceRequestId }, ...props }
  );

export interface CreateIdempotentContinuationPathParams {
  authorizationId: string;
  serviceRequestId: string;
}

export type CreateIdempotentContinuationProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    IdempotentContinuationPayload,
    CreateIdempotentContinuationPathParams
  >,
  "path" | "verb"
> &
  CreateIdempotentContinuationPathParams;

/**
 * Using the new Idempotent endpoint to create continuations without duplicates
 */
export const CreateIdempotentContinuation = ({
  authorizationId,
  serviceRequestId,
  ...props
}: CreateIdempotentContinuationProps) => (
  <Mutate<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    IdempotentContinuationPayload,
    CreateIdempotentContinuationPathParams
  >
    verb="POST"
    path={`/authorization/${authorizationId}/createIdempotentContinuation/${serviceRequestId}`}
    {...props}
  />
);

export type UseCreateIdempotentContinuationProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    IdempotentContinuationPayload,
    CreateIdempotentContinuationPathParams
  >,
  "path" | "verb"
> &
  CreateIdempotentContinuationPathParams;

/**
 * Using the new Idempotent endpoint to create continuations without duplicates
 */
export const useCreateIdempotentContinuation = ({
  authorizationId,
  serviceRequestId,
  ...props
}: UseCreateIdempotentContinuationProps) =>
  useMutate<
    ServiceRequestResponse,
    {
      /**
       * error message
       */
      message?: string;
    },
    void,
    IdempotentContinuationPayload,
    CreateIdempotentContinuationPathParams
  >(
    "POST",
    (paramsInPath: CreateIdempotentContinuationPathParams) =>
      `/authorization/${paramsInPath.authorizationId}/createIdempotentContinuation/${paramsInPath.serviceRequestId}`,
    { pathParams: { authorizationId, serviceRequestId }, ...props }
  );

export interface GetAuthorizationReviewsPathParams {
  authorizationId: string;
}

export type GetAuthorizationReviewsProps = Omit<
  GetProps<ReviewType[], unknown, void, GetAuthorizationReviewsPathParams>,
  "path"
> &
  GetAuthorizationReviewsPathParams;

export const GetAuthorizationReviews = ({
  authorizationId,
  ...props
}: GetAuthorizationReviewsProps) => (
  <Get<ReviewType[], unknown, void, GetAuthorizationReviewsPathParams>
    path={`/authorization/${authorizationId}/review`}
    {...props}
  />
);

export type UseGetAuthorizationReviewsProps = Omit<
  UseGetProps<ReviewType[], unknown, void, GetAuthorizationReviewsPathParams>,
  "path"
> &
  GetAuthorizationReviewsPathParams;

export const useGetAuthorizationReviews = ({
  authorizationId,
  ...props
}: UseGetAuthorizationReviewsProps) =>
  useGet<ReviewType[], unknown, void, GetAuthorizationReviewsPathParams>(
    (paramsInPath: GetAuthorizationReviewsPathParams) =>
      `/authorization/${paramsInPath.authorizationId}/review`,
    { pathParams: { authorizationId }, ...props }
  );

export interface UpdateRestrictedDataSettingsPathParams {
  authorizationId: string;
}

export type UpdateRestrictedDataSettingsProps = Omit<
  MutateProps<
    AuthorizationResponse,
    ErrorResponse,
    void,
    RestrictedDataSettings,
    UpdateRestrictedDataSettingsPathParams
  >,
  "path" | "verb"
> &
  UpdateRestrictedDataSettingsPathParams;

export const UpdateRestrictedDataSettings = ({
  authorizationId,
  ...props
}: UpdateRestrictedDataSettingsProps) => (
  <Mutate<
    AuthorizationResponse,
    ErrorResponse,
    void,
    RestrictedDataSettings,
    UpdateRestrictedDataSettingsPathParams
  >
    verb="POST"
    path={`/authorization/${authorizationId}/restrictedDataSettings`}
    {...props}
  />
);

export type UseUpdateRestrictedDataSettingsProps = Omit<
  UseMutateProps<
    AuthorizationResponse,
    ErrorResponse,
    void,
    RestrictedDataSettings,
    UpdateRestrictedDataSettingsPathParams
  >,
  "path" | "verb"
> &
  UpdateRestrictedDataSettingsPathParams;

export const useUpdateRestrictedDataSettings = ({
  authorizationId,
  ...props
}: UseUpdateRestrictedDataSettingsProps) =>
  useMutate<
    AuthorizationResponse,
    ErrorResponse,
    void,
    RestrictedDataSettings,
    UpdateRestrictedDataSettingsPathParams
  >(
    "POST",
    (paramsInPath: UpdateRestrictedDataSettingsPathParams) =>
      `/authorization/${paramsInPath.authorizationId}/restrictedDataSettings`,
    { pathParams: { authorizationId }, ...props }
  );

export type GetPotentialDuplicatesForServiceRequestProps = Omit<
  MutateProps<
    SimilarServiceRequest[],
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const GetPotentialDuplicatesForServiceRequest = (
  props: GetPotentialDuplicatesForServiceRequestProps
) => (
  <Mutate<
    SimilarServiceRequest[],
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >
    verb="POST"
    path={`/authorization/getDuplicates`}
    {...props}
  />
);

export type UseGetPotentialDuplicatesForServiceRequestProps = Omit<
  UseMutateProps<
    SimilarServiceRequest[],
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

export const useGetPotentialDuplicatesForServiceRequest = (
  props: UseGetPotentialDuplicatesForServiceRequestProps
) =>
  useMutate<
    SimilarServiceRequest[],
    unknown,
    void,
    ServiceRequestCreatePayload,
    void
  >("POST", `/authorization/getDuplicates`, props);

export interface GetPossibleContinuationsQueryParams {
  procedureCodes: string;
  diagnosisCode: string;
  patient: string;
  authStatus: string;
  endDate: string;
}

export type GetPossibleContinuationsProps = Omit<
  GetProps<
    PossibleContinuationsAuthorizationResponse[],
    unknown,
    GetPossibleContinuationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Used to determine and return a list of service requests that possibly match and fit the criteria to be a continuation
 */
export const GetPossibleContinuations = (
  props: GetPossibleContinuationsProps
) => (
  <Get<
    PossibleContinuationsAuthorizationResponse[],
    unknown,
    GetPossibleContinuationsQueryParams,
    void
  >
    path={`/authorization/getPossibleContinuations`}
    {...props}
  />
);

export type UseGetPossibleContinuationsProps = Omit<
  UseGetProps<
    PossibleContinuationsAuthorizationResponse[],
    unknown,
    GetPossibleContinuationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Used to determine and return a list of service requests that possibly match and fit the criteria to be a continuation
 */
export const useGetPossibleContinuations = (
  props: UseGetPossibleContinuationsProps
) =>
  useGet<
    PossibleContinuationsAuthorizationResponse[],
    unknown,
    GetPossibleContinuationsQueryParams,
    void
  >(`/authorization/getPossibleContinuations`, props);

export interface GetPossibleContinuationSRsQueryParams {
  procedureCodes: string;
  diagnosisCode: string;
  patient: string;
  authStatus: string;
  endDate: string;
}

export type GetPossibleContinuationSRsProps = Omit<
  GetProps<
    ServiceRequestResponse[],
    unknown,
    GetPossibleContinuationSRsQueryParams,
    void
  >,
  "path"
>;

/**
 * Used to determine and return a list of service requests that possibly match and fit the criteria to be a continuation
 */
export const GetPossibleContinuationSRs = (
  props: GetPossibleContinuationSRsProps
) => (
  <Get<
    ServiceRequestResponse[],
    unknown,
    GetPossibleContinuationSRsQueryParams,
    void
  >
    path={`/serviceRequest/getPossibleContinuations`}
    {...props}
  />
);

export type UseGetPossibleContinuationSRsProps = Omit<
  UseGetProps<
    ServiceRequestResponse[],
    unknown,
    GetPossibleContinuationSRsQueryParams,
    void
  >,
  "path"
>;

/**
 * Used to determine and return a list of service requests that possibly match and fit the criteria to be a continuation
 */
export const useGetPossibleContinuationSRs = (
  props: UseGetPossibleContinuationSRsProps
) =>
  useGet<
    ServiceRequestResponse[],
    unknown,
    GetPossibleContinuationSRsQueryParams,
    void
  >(`/serviceRequest/getPossibleContinuations`, props);

export interface GetEpisodicAuthsQueryParams {
  procedureCodes: string;
  patient: string;
  encounterType: string;
  startDate: string | null;
}

export type GetEpisodicAuthsProps = Omit<
  GetProps<
    ServiceRequestResponse[],
    unknown,
    GetEpisodicAuthsQueryParams,
    void
  >,
  "path"
>;

/**
 * Used to determine and return a list of service requests that are related to the episode of care
 */
export const GetEpisodicAuths = (props: GetEpisodicAuthsProps) => (
  <Get<ServiceRequestResponse[], unknown, GetEpisodicAuthsQueryParams, void>
    path={`/serviceRequest/episodicAuths`}
    {...props}
  />
);

export type UseGetEpisodicAuthsProps = Omit<
  UseGetProps<
    ServiceRequestResponse[],
    unknown,
    GetEpisodicAuthsQueryParams,
    void
  >,
  "path"
>;

/**
 * Used to determine and return a list of service requests that are related to the episode of care
 */
export const useGetEpisodicAuths = (props: UseGetEpisodicAuthsProps) =>
  useGet<ServiceRequestResponse[], unknown, GetEpisodicAuthsQueryParams, void>(
    `/serviceRequest/episodicAuths`,
    props
  );

export interface GetEpisodicAuthAutoApprovalCheckResponse {
  willAutoApprove?: boolean;
}

export interface GetEpisodicAuthAutoApprovalCheckPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  serviceRequestId: string;
}

export type GetEpisodicAuthAutoApprovalCheckProps = Omit<
  GetProps<
    GetEpisodicAuthAutoApprovalCheckResponse,
    unknown,
    void,
    GetEpisodicAuthAutoApprovalCheckPathParams
  >,
  "path"
> &
  GetEpisodicAuthAutoApprovalCheckPathParams;

/**
 * Determine if an episodic authorization will AA.
 *
 */
export const GetEpisodicAuthAutoApprovalCheck = ({
  serviceRequestId,
  ...props
}: GetEpisodicAuthAutoApprovalCheckProps) => (
  <Get<
    GetEpisodicAuthAutoApprovalCheckResponse,
    unknown,
    void,
    GetEpisodicAuthAutoApprovalCheckPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/episodicAuths/autoApprovalCheck`}
    {...props}
  />
);

export type UseGetEpisodicAuthAutoApprovalCheckProps = Omit<
  UseGetProps<
    GetEpisodicAuthAutoApprovalCheckResponse,
    unknown,
    void,
    GetEpisodicAuthAutoApprovalCheckPathParams
  >,
  "path"
> &
  GetEpisodicAuthAutoApprovalCheckPathParams;

/**
 * Determine if an episodic authorization will AA.
 *
 */
export const useGetEpisodicAuthAutoApprovalCheck = ({
  serviceRequestId,
  ...props
}: UseGetEpisodicAuthAutoApprovalCheckProps) =>
  useGet<
    GetEpisodicAuthAutoApprovalCheckResponse,
    unknown,
    void,
    GetEpisodicAuthAutoApprovalCheckPathParams
  >(
    (paramsInPath: GetEpisodicAuthAutoApprovalCheckPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/episodicAuths/autoApprovalCheck`,
    { pathParams: { serviceRequestId }, ...props }
  );

export type CreateServiceRequestConfigurationSpecificationProps = Omit<
  MutateProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a new instance of a `ServiceRequestConfigurationSpecification`.
 */
export const CreateServiceRequestConfigurationSpecification = (
  props: CreateServiceRequestConfigurationSpecificationProps
) => (
  <Mutate<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    void
  >
    verb="POST"
    path={`/serviceRequestConfigurationSpecification`}
    {...props}
  />
);

export type UseCreateServiceRequestConfigurationSpecificationProps = Omit<
  UseMutateProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a new instance of a `ServiceRequestConfigurationSpecification`.
 */
export const useCreateServiceRequestConfigurationSpecification = (
  props: UseCreateServiceRequestConfigurationSpecificationProps
) =>
  useMutate<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    void
  >("POST", `/serviceRequestConfigurationSpecification`, props);

export interface GetServiceRequestConfigurationSpecificationsQueryParams {
  /**
   * A Health Plan id
   */
  healthPlan: string;
  /**
   * The delegated vendor name
   */
  delegatedVendorName?: string;
  /**
   * The clinical service id
   */
  clinicalServiceId?: string;
}

export type GetServiceRequestConfigurationSpecificationsProps = Omit<
  GetProps<
    ServiceRequestConfigurationSpecification[],
    unknown,
    GetServiceRequestConfigurationSpecificationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets serviceRequestConfigurationSpecifications, this will not perform any overrides or payer logic, just retrieves
 * the database object
 *
 */
export const GetServiceRequestConfigurationSpecifications = (
  props: GetServiceRequestConfigurationSpecificationsProps
) => (
  <Get<
    ServiceRequestConfigurationSpecification[],
    unknown,
    GetServiceRequestConfigurationSpecificationsQueryParams,
    void
  >
    path={`/serviceRequestConfigurationSpecification`}
    {...props}
  />
);

export type UseGetServiceRequestConfigurationSpecificationsProps = Omit<
  UseGetProps<
    ServiceRequestConfigurationSpecification[],
    unknown,
    GetServiceRequestConfigurationSpecificationsQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets serviceRequestConfigurationSpecifications, this will not perform any overrides or payer logic, just retrieves
 * the database object
 *
 */
export const useGetServiceRequestConfigurationSpecifications = (
  props: UseGetServiceRequestConfigurationSpecificationsProps
) =>
  useGet<
    ServiceRequestConfigurationSpecification[],
    unknown,
    GetServiceRequestConfigurationSpecificationsQueryParams,
    void
  >(`/serviceRequestConfigurationSpecification`, props);

export interface UpdateServiceRequestConfigurationSpecificationPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type UpdateServiceRequestConfigurationSpecificationProps = Omit<
  MutateProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    UpdateServiceRequestConfigurationSpecificationPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestConfigurationSpecificationPathParams;

/**
 * Update existing instance of a `ServiceRequestConfigurationSpecification`.
 */
export const UpdateServiceRequestConfigurationSpecification = ({
  id,
  ...props
}: UpdateServiceRequestConfigurationSpecificationProps) => (
  <Mutate<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    UpdateServiceRequestConfigurationSpecificationPathParams
  >
    verb="PATCH"
    path={`/serviceRequestConfigurationSpecification/${id}`}
    {...props}
  />
);

export type UseUpdateServiceRequestConfigurationSpecificationProps = Omit<
  UseMutateProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    UpdateServiceRequestConfigurationSpecificationPathParams
  >,
  "path" | "verb"
> &
  UpdateServiceRequestConfigurationSpecificationPathParams;

/**
 * Update existing instance of a `ServiceRequestConfigurationSpecification`.
 */
export const useUpdateServiceRequestConfigurationSpecification = ({
  id,
  ...props
}: UseUpdateServiceRequestConfigurationSpecificationProps) =>
  useMutate<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    ServiceRequestConfigurationSpecificationCreatePayload,
    UpdateServiceRequestConfigurationSpecificationPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateServiceRequestConfigurationSpecificationPathParams) =>
      `/serviceRequestConfigurationSpecification/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetServiceRequestConfigurationSpecificationPathParams {
  /**
   * A unique identifier for a `ServiceRequest`.
   */
  id: string;
}

export type GetServiceRequestConfigurationSpecificationProps = Omit<
  GetProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    GetServiceRequestConfigurationSpecificationPathParams
  >,
  "path"
> &
  GetServiceRequestConfigurationSpecificationPathParams;

export const GetServiceRequestConfigurationSpecification = ({
  id,
  ...props
}: GetServiceRequestConfigurationSpecificationProps) => (
  <Get<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    GetServiceRequestConfigurationSpecificationPathParams
  >
    path={`/serviceRequestConfigurationSpecification/${id}`}
    {...props}
  />
);

export type UseGetServiceRequestConfigurationSpecificationProps = Omit<
  UseGetProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    GetServiceRequestConfigurationSpecificationPathParams
  >,
  "path"
> &
  GetServiceRequestConfigurationSpecificationPathParams;

export const useGetServiceRequestConfigurationSpecification = ({
  id,
  ...props
}: UseGetServiceRequestConfigurationSpecificationProps) =>
  useGet<
    ServiceRequestConfigurationSpecification,
    unknown,
    void,
    GetServiceRequestConfigurationSpecificationPathParams
  >(
    (paramsInPath: GetServiceRequestConfigurationSpecificationPathParams) =>
      `/serviceRequestConfigurationSpecification/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface SearchServiceRequestConfigurationSpecificationQueryParams {
  patientId: string;
  startDate: string;
  procedureCodes?: string[];
  encounterType?: FacilityCategory;
  serviceType?: OscarServiceType;
  placeOfService?: string;
  caseType?: OscarCaseType;
  noPxServiceRequired?: boolean;
  serviceRequestType?: ServiceRequestType;
  serviceRequestId?: string;
  clinicalServiceId?: string;
}

export type SearchServiceRequestConfigurationSpecificationProps = Omit<
  GetProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    SearchServiceRequestConfigurationSpecificationQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets a serviceRequestConfigurationSpecification for a given healthPlan, delegatedVendorName and other params,
 * this will also apply any overrides due to given params and is how we should fetch ConfigurationSpecifications
 * when creating new service requests
 *
 */
export const SearchServiceRequestConfigurationSpecification = (
  props: SearchServiceRequestConfigurationSpecificationProps
) => (
  <Get<
    ServiceRequestConfigurationSpecification,
    unknown,
    SearchServiceRequestConfigurationSpecificationQueryParams,
    void
  >
    path={`/serviceRequestConfigurationSpecification/search`}
    {...props}
  />
);

export type UseSearchServiceRequestConfigurationSpecificationProps = Omit<
  UseGetProps<
    ServiceRequestConfigurationSpecification,
    unknown,
    SearchServiceRequestConfigurationSpecificationQueryParams,
    void
  >,
  "path"
>;

/**
 * Gets a serviceRequestConfigurationSpecification for a given healthPlan, delegatedVendorName and other params,
 * this will also apply any overrides due to given params and is how we should fetch ConfigurationSpecifications
 * when creating new service requests
 *
 */
export const useSearchServiceRequestConfigurationSpecification = (
  props: UseSearchServiceRequestConfigurationSpecificationProps
) =>
  useGet<
    ServiceRequestConfigurationSpecification,
    unknown,
    SearchServiceRequestConfigurationSpecificationQueryParams,
    void
  >(`/serviceRequestConfigurationSpecification/search`, props);

export interface GetBenefitPlanGroupQueryParams {
  view?: "expanded";
}

export interface GetBenefitPlanGroupPathParams {
  /**
   * A unique identifier for a `Benefit Plan Group`.
   */
  id: string;
}

export type GetBenefitPlanGroupProps = Omit<
  GetProps<
    BenefitPlanGroup,
    unknown,
    GetBenefitPlanGroupQueryParams,
    GetBenefitPlanGroupPathParams
  >,
  "path"
> &
  GetBenefitPlanGroupPathParams;

export const GetBenefitPlanGroup = ({
  id,
  ...props
}: GetBenefitPlanGroupProps) => (
  <Get<
    BenefitPlanGroup,
    unknown,
    GetBenefitPlanGroupQueryParams,
    GetBenefitPlanGroupPathParams
  >
    path={`/BenefitPlanGroup/${id}`}
    {...props}
  />
);

export type UseGetBenefitPlanGroupProps = Omit<
  UseGetProps<
    BenefitPlanGroup,
    unknown,
    GetBenefitPlanGroupQueryParams,
    GetBenefitPlanGroupPathParams
  >,
  "path"
> &
  GetBenefitPlanGroupPathParams;

export const useGetBenefitPlanGroup = ({
  id,
  ...props
}: UseGetBenefitPlanGroupProps) =>
  useGet<
    BenefitPlanGroup,
    unknown,
    GetBenefitPlanGroupQueryParams,
    GetBenefitPlanGroupPathParams
  >(
    (paramsInPath: GetBenefitPlanGroupPathParams) =>
      `/BenefitPlanGroup/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateBenefitPlanGroupQueryParams {
  view?: "expanded";
}

export interface UpdateBenefitPlanGroupPathParams {
  /**
   * A unique identifier for a `Benefit Plan Group`.
   */
  id: string;
}

export type UpdateBenefitPlanGroupProps = Omit<
  MutateProps<
    BenefitPlanGroupResponse,
    unknown,
    UpdateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    UpdateBenefitPlanGroupPathParams
  >,
  "path" | "verb"
> &
  UpdateBenefitPlanGroupPathParams;

/**
 * Update a BenefitPlanGroup
 *
 * Updates an existing `ServiceRequestRule`.
 */
export const UpdateBenefitPlanGroup = ({
  id,
  ...props
}: UpdateBenefitPlanGroupProps) => (
  <Mutate<
    BenefitPlanGroupResponse,
    unknown,
    UpdateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    UpdateBenefitPlanGroupPathParams
  >
    verb="PUT"
    path={`/BenefitPlanGroup/${id}`}
    {...props}
  />
);

export type UseUpdateBenefitPlanGroupProps = Omit<
  UseMutateProps<
    BenefitPlanGroupResponse,
    unknown,
    UpdateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    UpdateBenefitPlanGroupPathParams
  >,
  "path" | "verb"
> &
  UpdateBenefitPlanGroupPathParams;

/**
 * Update a BenefitPlanGroup
 *
 * Updates an existing `ServiceRequestRule`.
 */
export const useUpdateBenefitPlanGroup = ({
  id,
  ...props
}: UseUpdateBenefitPlanGroupProps) =>
  useMutate<
    BenefitPlanGroupResponse,
    unknown,
    UpdateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    UpdateBenefitPlanGroupPathParams
  >(
    "PUT",
    (paramsInPath: UpdateBenefitPlanGroupPathParams) =>
      `/BenefitPlanGroup/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetBenefitPlanGroupsQueryParams {
  /**
   * Max number of results to return
   */
  max?: number;
}

export type GetBenefitPlanGroupsProps = Omit<
  GetProps<
    BenefitPlanGroupResponse[],
    unknown,
    GetBenefitPlanGroupsQueryParams,
    void
  >,
  "path"
>;

export const GetBenefitPlanGroups = (props: GetBenefitPlanGroupsProps) => (
  <Get<
    BenefitPlanGroupResponse[],
    unknown,
    GetBenefitPlanGroupsQueryParams,
    void
  >
    path={`/BenefitPlanGroup`}
    {...props}
  />
);

export type UseGetBenefitPlanGroupsProps = Omit<
  UseGetProps<
    BenefitPlanGroupResponse[],
    unknown,
    GetBenefitPlanGroupsQueryParams,
    void
  >,
  "path"
>;

export const useGetBenefitPlanGroups = (props: UseGetBenefitPlanGroupsProps) =>
  useGet<
    BenefitPlanGroupResponse[],
    unknown,
    GetBenefitPlanGroupsQueryParams,
    void
  >(`/BenefitPlanGroup`, props);

export interface CreateBenefitPlanGroupQueryParams {
  /**
   * Max number of results to return
   */
  max?: number;
}

export type CreateBenefitPlanGroupProps = Omit<
  MutateProps<
    BenefitPlanGroupResponse,
    unknown,
    CreateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    void
  >,
  "path" | "verb"
>;

export const CreateBenefitPlanGroup = (props: CreateBenefitPlanGroupProps) => (
  <Mutate<
    BenefitPlanGroupResponse,
    unknown,
    CreateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    void
  >
    verb="POST"
    path={`/BenefitPlanGroup`}
    {...props}
  />
);

export type UseCreateBenefitPlanGroupProps = Omit<
  UseMutateProps<
    BenefitPlanGroupResponse,
    unknown,
    CreateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    void
  >,
  "path" | "verb"
>;

export const useCreateBenefitPlanGroup = (
  props: UseCreateBenefitPlanGroupProps
) =>
  useMutate<
    BenefitPlanGroupResponse,
    unknown,
    CreateBenefitPlanGroupQueryParams,
    BenefitPlanGroup,
    void
  >("POST", `/BenefitPlanGroup`, props);

export interface PostOutreachAttemptPathParams {
  outreachOpportunityId: string;
}

export type PostOutreachAttemptProps = Omit<
  MutateProps<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PostOutreachAttemptPathParams
  >,
  "path" | "verb"
> &
  PostOutreachAttemptPathParams;

export const PostOutreachAttempt = ({
  outreachOpportunityId,
  ...props
}: PostOutreachAttemptProps) => (
  <Mutate<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PostOutreachAttemptPathParams
  >
    verb="POST"
    path={`/outreachOpportunity/${outreachOpportunityId}/outreachAttempt`}
    {...props}
  />
);

export type UsePostOutreachAttemptProps = Omit<
  UseMutateProps<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PostOutreachAttemptPathParams
  >,
  "path" | "verb"
> &
  PostOutreachAttemptPathParams;

export const usePostOutreachAttempt = ({
  outreachOpportunityId,
  ...props
}: UsePostOutreachAttemptProps) =>
  useMutate<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PostOutreachAttemptPathParams
  >(
    "POST",
    (paramsInPath: PostOutreachAttemptPathParams) =>
      `/outreachOpportunity/${paramsInPath.outreachOpportunityId}/outreachAttempt`,
    { pathParams: { outreachOpportunityId }, ...props }
  );

export interface PatchOutreachAttemptPathParams {
  outreachOpportunityId: string;
  outreachAttemptId: string;
}

export type PatchOutreachAttemptProps = Omit<
  MutateProps<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PatchOutreachAttemptPathParams
  >,
  "path" | "verb"
> &
  PatchOutreachAttemptPathParams;

export const PatchOutreachAttempt = ({
  outreachOpportunityId,
  outreachAttemptId,
  ...props
}: PatchOutreachAttemptProps) => (
  <Mutate<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PatchOutreachAttemptPathParams
  >
    verb="PATCH"
    path={`/outreachOpportunity/${outreachOpportunityId}/outreachAttempt/${outreachAttemptId}`}
    {...props}
  />
);

export type UsePatchOutreachAttemptProps = Omit<
  UseMutateProps<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PatchOutreachAttemptPathParams
  >,
  "path" | "verb"
> &
  PatchOutreachAttemptPathParams;

export const usePatchOutreachAttempt = ({
  outreachOpportunityId,
  outreachAttemptId,
  ...props
}: UsePatchOutreachAttemptProps) =>
  useMutate<
    OutreachAttempt,
    unknown,
    void,
    OutreachAttempt,
    PatchOutreachAttemptPathParams
  >(
    "PATCH",
    (paramsInPath: PatchOutreachAttemptPathParams) =>
      `/outreachOpportunity/${paramsInPath.outreachOpportunityId}/outreachAttempt/${paramsInPath.outreachAttemptId}`,
    { pathParams: { outreachOpportunityId, outreachAttemptId }, ...props }
  );

export type PostOutreachOpportunityProps = Omit<
  MutateProps<OutreachOpportunity, unknown, void, OutreachOpportunity, void>,
  "path" | "verb"
>;

export const PostOutreachOpportunity = (
  props: PostOutreachOpportunityProps
) => (
  <Mutate<OutreachOpportunity, unknown, void, OutreachOpportunity, void>
    verb="POST"
    path={`/outreachOpportunity`}
    {...props}
  />
);

export type UsePostOutreachOpportunityProps = Omit<
  UseMutateProps<OutreachOpportunity, unknown, void, OutreachOpportunity, void>,
  "path" | "verb"
>;

export const usePostOutreachOpportunity = (
  props: UsePostOutreachOpportunityProps
) =>
  useMutate<OutreachOpportunity, unknown, void, OutreachOpportunity, void>(
    "POST",
    `/outreachOpportunity`,
    props
  );

export interface SearchOutreachAttemptsQueryParams {
  /**
   * A unique identifier for a `serviceRequest`
   */
  serviceRequestId?: string;
  /**
   * List of service Requests on authorization
   */
  serviceRequestIds?: string[];
}

export type SearchOutreachAttemptsProps = Omit<
  GetProps<OutreachAttempt[], unknown, SearchOutreachAttemptsQueryParams, void>,
  "path"
>;

export const SearchOutreachAttempts = (props: SearchOutreachAttemptsProps) => (
  <Get<OutreachAttempt[], unknown, SearchOutreachAttemptsQueryParams, void>
    path={`/outreachAttempt/search`}
    {...props}
  />
);

export type UseSearchOutreachAttemptsProps = Omit<
  UseGetProps<
    OutreachAttempt[],
    unknown,
    SearchOutreachAttemptsQueryParams,
    void
  >,
  "path"
>;

export const useSearchOutreachAttempts = (
  props: UseSearchOutreachAttemptsProps
) =>
  useGet<OutreachAttempt[], unknown, SearchOutreachAttemptsQueryParams, void>(
    `/outreachAttempt/search`,
    props
  );

export interface GetAppealsQueryParams {
  /**
   * A unique identifier for a `serviceRequest`
   */
  serviceRequest?: string;
  /**
   * Status of appeal
   */
  appealStatus?: string;
}

export type GetAppealsProps = Omit<
  GetProps<AppealResponse[], unknown, GetAppealsQueryParams, void>,
  "path"
>;

export const GetAppeals = (props: GetAppealsProps) => (
  <Get<AppealResponse[], unknown, GetAppealsQueryParams, void>
    path={`/appeal`}
    {...props}
  />
);

export type UseGetAppealsProps = Omit<
  UseGetProps<AppealResponse[], unknown, GetAppealsQueryParams, void>,
  "path"
>;

export const useGetAppeals = (props: UseGetAppealsProps) =>
  useGet<AppealResponse[], unknown, GetAppealsQueryParams, void>(
    `/appeal`,
    props
  );

export interface GetAppealsForAuthorizationPathParams {
  /**
   * A unique identifier for a `Authorization`.
   */
  authorizationId: string;
}

export type GetAppealsForAuthorizationProps = Omit<
  GetProps<
    AppealResponse[],
    unknown,
    void,
    GetAppealsForAuthorizationPathParams
  >,
  "path"
> &
  GetAppealsForAuthorizationPathParams;

export const GetAppealsForAuthorization = ({
  authorizationId,
  ...props
}: GetAppealsForAuthorizationProps) => (
  <Get<AppealResponse[], unknown, void, GetAppealsForAuthorizationPathParams>
    path={`/appeal/${authorizationId}/getAppealsForAuthorization`}
    {...props}
  />
);

export type UseGetAppealsForAuthorizationProps = Omit<
  UseGetProps<
    AppealResponse[],
    unknown,
    void,
    GetAppealsForAuthorizationPathParams
  >,
  "path"
> &
  GetAppealsForAuthorizationPathParams;

export const useGetAppealsForAuthorization = ({
  authorizationId,
  ...props
}: UseGetAppealsForAuthorizationProps) =>
  useGet<AppealResponse[], unknown, void, GetAppealsForAuthorizationPathParams>(
    (paramsInPath: GetAppealsForAuthorizationPathParams) =>
      `/appeal/${paramsInPath.authorizationId}/getAppealsForAuthorization`,
    { pathParams: { authorizationId }, ...props }
  );

export interface DownloadPriorAuthListPathParams {
  /**
   * A unique identifier for a `benefitPlanGroup`.
   */
  id: string;
}

export type DownloadPriorAuthListProps = Omit<
  GetProps<void, unknown, void, DownloadPriorAuthListPathParams>,
  "path"
> &
  DownloadPriorAuthListPathParams;

export const DownloadPriorAuthList = ({
  id,
  ...props
}: DownloadPriorAuthListProps) => (
  <Get<void, unknown, void, DownloadPriorAuthListPathParams>
    path={`/benefitPlanGroup/${id}/priorAuthList`}
    {...props}
  />
);

export type UseDownloadPriorAuthListProps = Omit<
  UseGetProps<void, unknown, void, DownloadPriorAuthListPathParams>,
  "path"
> &
  DownloadPriorAuthListPathParams;

export const useDownloadPriorAuthList = ({
  id,
  ...props
}: UseDownloadPriorAuthListProps) =>
  useGet<void, unknown, void, DownloadPriorAuthListPathParams>(
    (paramsInPath: DownloadPriorAuthListPathParams) =>
      `/benefitPlanGroup/${paramsInPath.id}/priorAuthList`,
    { pathParams: { id }, ...props }
  );

export interface ReplacePriorAuthListPathParams {
  /**
   * A unique identifier for a `benefitPlanGroup`.
   */
  id: string;
}

export type ReplacePriorAuthListProps = Omit<
  MutateProps<
    ReplacePriorAuthListResponse,
    unknown,
    void,
    void,
    ReplacePriorAuthListPathParams
  >,
  "path" | "verb"
> &
  ReplacePriorAuthListPathParams;

/**
 * Replace prior auth list
 */
export const ReplacePriorAuthList = ({
  id,
  ...props
}: ReplacePriorAuthListProps) => (
  <Mutate<
    ReplacePriorAuthListResponse,
    unknown,
    void,
    void,
    ReplacePriorAuthListPathParams
  >
    verb="POST"
    path={`/benefitPlanGroup/${id}/priorAuthList`}
    {...props}
  />
);

export type UseReplacePriorAuthListProps = Omit<
  UseMutateProps<
    ReplacePriorAuthListResponse,
    unknown,
    void,
    void,
    ReplacePriorAuthListPathParams
  >,
  "path" | "verb"
> &
  ReplacePriorAuthListPathParams;

/**
 * Replace prior auth list
 */
export const useReplacePriorAuthList = ({
  id,
  ...props
}: UseReplacePriorAuthListProps) =>
  useMutate<
    ReplacePriorAuthListResponse,
    unknown,
    void,
    void,
    ReplacePriorAuthListPathParams
  >(
    "POST",
    (paramsInPath: ReplacePriorAuthListPathParams) =>
      `/benefitPlanGroup/${paramsInPath.id}/priorAuthList`,
    { pathParams: { id }, ...props }
  );

export interface GetOrdersQueryParams {
  vendor?: EhrVendor;
  orderStatus?: string;
}

export interface GetOrdersPathParams {
  practiceId: string;
  departmentId: string;
  patientId: string;
}

export type GetOrdersProps = Omit<
  GetProps<
    ServiceRequestResponse[],
    unknown,
    GetOrdersQueryParams,
    GetOrdersPathParams
  >,
  "path"
> &
  GetOrdersPathParams;

export const GetOrders = ({
  practiceId,
  departmentId,
  patientId,
  ...props
}: GetOrdersProps) => (
  <Get<
    ServiceRequestResponse[],
    unknown,
    GetOrdersQueryParams,
    GetOrdersPathParams
  >
    path={`/ehr/order/practice/${practiceId}/department/${departmentId}/patient/${patientId}`}
    {...props}
  />
);

export type UseGetOrdersProps = Omit<
  UseGetProps<
    ServiceRequestResponse[],
    unknown,
    GetOrdersQueryParams,
    GetOrdersPathParams
  >,
  "path"
> &
  GetOrdersPathParams;

export const useGetOrders = ({
  practiceId,
  departmentId,
  patientId,
  ...props
}: UseGetOrdersProps) =>
  useGet<
    ServiceRequestResponse[],
    unknown,
    GetOrdersQueryParams,
    GetOrdersPathParams
  >(
    (paramsInPath: GetOrdersPathParams) =>
      `/ehr/order/practice/${paramsInPath.practiceId}/department/${paramsInPath.departmentId}/patient/${paramsInPath.patientId}`,
    { pathParams: { practiceId, departmentId, patientId }, ...props }
  );

export interface GetOrderQueryParams {
  vendor?: EhrVendor;
}

export interface GetOrderPathParams {
  practiceId: string;
  patientId: string;
  encounterId: string;
  orderId: string;
}

export type GetOrderProps = Omit<
  GetProps<
    ServiceRequestResponse,
    unknown,
    GetOrderQueryParams,
    GetOrderPathParams
  >,
  "path"
> &
  GetOrderPathParams;

export const GetOrder = ({
  practiceId,
  patientId,
  encounterId,
  orderId,
  ...props
}: GetOrderProps) => (
  <Get<ServiceRequestResponse, unknown, GetOrderQueryParams, GetOrderPathParams>
    path={`/ehr/order/practice/${practiceId}/patient/${patientId}/encounter/${encounterId}/order/${orderId}`}
    {...props}
  />
);

export type UseGetOrderProps = Omit<
  UseGetProps<
    ServiceRequestResponse,
    unknown,
    GetOrderQueryParams,
    GetOrderPathParams
  >,
  "path"
> &
  GetOrderPathParams;

export const useGetOrder = ({
  practiceId,
  patientId,
  encounterId,
  orderId,
  ...props
}: UseGetOrderProps) =>
  useGet<
    ServiceRequestResponse,
    unknown,
    GetOrderQueryParams,
    GetOrderPathParams
  >(
    (paramsInPath: GetOrderPathParams) =>
      `/ehr/order/practice/${paramsInPath.practiceId}/patient/${paramsInPath.patientId}/encounter/${paramsInPath.encounterId}/order/${paramsInPath.orderId}`,
    { pathParams: { practiceId, patientId, encounterId, orderId }, ...props }
  );

export interface GetCandidateSuggestedValuesResponse {
  [key: string]: any;
}

export interface GetCandidateSuggestedValuesQueryParams {
  /**
   * query param flag to determine if results should be rank-ordered
   */
  rankResults?: boolean;
  /**
   * query param flag to determine if results should be filtered for RAW status entities
   */
  raw?: boolean;
  /**
   * query param flag to determine if results should be filtered for shadow entities
   */
  shadow?: boolean;
}

export interface GetCandidateSuggestedValuesPathParams {
  attachmentId: string;
}

export type GetCandidateSuggestedValuesProps = Omit<
  GetProps<
    GetCandidateSuggestedValuesResponse,
    unknown,
    GetCandidateSuggestedValuesQueryParams,
    GetCandidateSuggestedValuesPathParams
  >,
  "path"
> &
  GetCandidateSuggestedValuesPathParams;

/**
 * Gets all SuggestedValues associated with an attachment in ranked order
 */
export const GetCandidateSuggestedValues = ({
  attachmentId,
  ...props
}: GetCandidateSuggestedValuesProps) => (
  <Get<
    GetCandidateSuggestedValuesResponse,
    unknown,
    GetCandidateSuggestedValuesQueryParams,
    GetCandidateSuggestedValuesPathParams
  >
    path={`/attachment/${attachmentId}/suggestedValues`}
    {...props}
  />
);

export type UseGetCandidateSuggestedValuesProps = Omit<
  UseGetProps<
    GetCandidateSuggestedValuesResponse,
    unknown,
    GetCandidateSuggestedValuesQueryParams,
    GetCandidateSuggestedValuesPathParams
  >,
  "path"
> &
  GetCandidateSuggestedValuesPathParams;

/**
 * Gets all SuggestedValues associated with an attachment in ranked order
 */
export const useGetCandidateSuggestedValues = ({
  attachmentId,
  ...props
}: UseGetCandidateSuggestedValuesProps) =>
  useGet<
    GetCandidateSuggestedValuesResponse,
    unknown,
    GetCandidateSuggestedValuesQueryParams,
    GetCandidateSuggestedValuesPathParams
  >(
    (paramsInPath: GetCandidateSuggestedValuesPathParams) =>
      `/attachment/${paramsInPath.attachmentId}/suggestedValues`,
    { pathParams: { attachmentId }, ...props }
  );

export interface CreateSuggestedValueQueryParams {
  /**
   * query param flag to determine if results should published as RAW status entities
   */
  raw?: boolean;
  /**
   * query param flag to determine if results should be published as shadow entities
   */
  shadow?: boolean;
}

export type CreateSuggestedValueProps = Omit<
  MutateProps<
    SuggestedValue,
    unknown,
    CreateSuggestedValueQueryParams,
    SuggestedValue,
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a SuggestedValue
 */
export const CreateSuggestedValue = (props: CreateSuggestedValueProps) => (
  <Mutate<
    SuggestedValue,
    unknown,
    CreateSuggestedValueQueryParams,
    SuggestedValue,
    void
  >
    verb="POST"
    path={`/suggestedValue`}
    {...props}
  />
);

export type UseCreateSuggestedValueProps = Omit<
  UseMutateProps<
    SuggestedValue,
    unknown,
    CreateSuggestedValueQueryParams,
    SuggestedValue,
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a SuggestedValue
 */
export const useCreateSuggestedValue = (props: UseCreateSuggestedValueProps) =>
  useMutate<
    SuggestedValue,
    unknown,
    CreateSuggestedValueQueryParams,
    SuggestedValue,
    void
  >("POST", `/suggestedValue`, props);

export interface BatchCreateSuggestedValuesQueryParams {
  /**
   * query param flag to determine if results should published as RAW status entities
   */
  raw?: boolean;
  /**
   * query param flag to determine if results should be published as shadow entities
   */
  shadow?: boolean;
}

export type BatchCreateSuggestedValuesProps = Omit<
  MutateProps<
    BatchCreateSuggestedValueResponse[],
    unknown,
    BatchCreateSuggestedValuesQueryParams,
    SuggestedValue[],
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a batch of suggestedValues
 */
export const BatchCreateSuggestedValues = (
  props: BatchCreateSuggestedValuesProps
) => (
  <Mutate<
    BatchCreateSuggestedValueResponse[],
    unknown,
    BatchCreateSuggestedValuesQueryParams,
    SuggestedValue[],
    void
  >
    verb="POST"
    path={`/suggestedValue/bulkSave`}
    {...props}
  />
);

export type UseBatchCreateSuggestedValuesProps = Omit<
  UseMutateProps<
    BatchCreateSuggestedValueResponse[],
    unknown,
    BatchCreateSuggestedValuesQueryParams,
    SuggestedValue[],
    void
  >,
  "path" | "verb"
>;

/**
 * Creates a batch of suggestedValues
 */
export const useBatchCreateSuggestedValues = (
  props: UseBatchCreateSuggestedValuesProps
) =>
  useMutate<
    BatchCreateSuggestedValueResponse[],
    unknown,
    BatchCreateSuggestedValuesQueryParams,
    SuggestedValue[],
    void
  >("POST", `/suggestedValue/bulkSave`, props);

export interface UpdateCandidateFeedbackPathParams {
  suggestedValueId: string;
}

export interface UpdateCandidateFeedbackRequestBody {
  feedback?: SuggestedValueFeedback;
}

export type UpdateCandidateFeedbackProps = Omit<
  MutateProps<
    SuggestedValue,
    unknown,
    void,
    UpdateCandidateFeedbackRequestBody,
    UpdateCandidateFeedbackPathParams
  >,
  "path" | "verb"
> &
  UpdateCandidateFeedbackPathParams;

/**
 * Updates the candidate with user feedback
 */
export const UpdateCandidateFeedback = ({
  suggestedValueId,
  ...props
}: UpdateCandidateFeedbackProps) => (
  <Mutate<
    SuggestedValue,
    unknown,
    void,
    UpdateCandidateFeedbackRequestBody,
    UpdateCandidateFeedbackPathParams
  >
    verb="PATCH"
    path={`/suggestedValue/${suggestedValueId}/feedback`}
    {...props}
  />
);

export type UseUpdateCandidateFeedbackProps = Omit<
  UseMutateProps<
    SuggestedValue,
    unknown,
    void,
    UpdateCandidateFeedbackRequestBody,
    UpdateCandidateFeedbackPathParams
  >,
  "path" | "verb"
> &
  UpdateCandidateFeedbackPathParams;

/**
 * Updates the candidate with user feedback
 */
export const useUpdateCandidateFeedback = ({
  suggestedValueId,
  ...props
}: UseUpdateCandidateFeedbackProps) =>
  useMutate<
    SuggestedValue,
    unknown,
    void,
    UpdateCandidateFeedbackRequestBody,
    UpdateCandidateFeedbackPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateCandidateFeedbackPathParams) =>
      `/suggestedValue/${paramsInPath.suggestedValueId}/feedback`,
    { pathParams: { suggestedValueId }, ...props }
  );

export interface BulkUpdateCandidateFeedbackRequestBody {
  accepted?: string[];
  rejected?: string[];
}

export type BulkUpdateCandidateFeedbackProps = Omit<
  MutateProps<
    BatchUpdateSuggestedValueFeedbackResponse[],
    unknown,
    void,
    BulkUpdateCandidateFeedbackRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Bulk updates the given candidates with the appropriate feedback
 */
export const BulkUpdateCandidateFeedback = (
  props: BulkUpdateCandidateFeedbackProps
) => (
  <Mutate<
    BatchUpdateSuggestedValueFeedbackResponse[],
    unknown,
    void,
    BulkUpdateCandidateFeedbackRequestBody,
    void
  >
    verb="POST"
    path={`/suggestedValue/bulkFeedback`}
    {...props}
  />
);

export type UseBulkUpdateCandidateFeedbackProps = Omit<
  UseMutateProps<
    BatchUpdateSuggestedValueFeedbackResponse[],
    unknown,
    void,
    BulkUpdateCandidateFeedbackRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Bulk updates the given candidates with the appropriate feedback
 */
export const useBulkUpdateCandidateFeedback = (
  props: UseBulkUpdateCandidateFeedbackProps
) =>
  useMutate<
    BatchUpdateSuggestedValueFeedbackResponse[],
    unknown,
    void,
    BulkUpdateCandidateFeedbackRequestBody,
    void
  >("POST", `/suggestedValue/bulkFeedback`, props);

export interface GetAuthCategoriesQueryParams {
  healthPlanName?: string;
  healthPlans?: string;
  delegatedVendorName?: string;
  lineOfBusiness?: string[];
  enumName?: AuthCategory;
}

export type GetAuthCategoriesProps = Omit<
  GetProps<AuthCategoryResponse[], unknown, GetAuthCategoriesQueryParams, void>,
  "path"
>;

/**
 * GETs the auth categories available to a healthplan
 *
 * performs GET for auth categories using health plan name
 */
export const GetAuthCategories = (props: GetAuthCategoriesProps) => (
  <Get<AuthCategoryResponse[], unknown, GetAuthCategoriesQueryParams, void>
    path={`/authCategory`}
    {...props}
  />
);

export type UseGetAuthCategoriesProps = Omit<
  UseGetProps<
    AuthCategoryResponse[],
    unknown,
    GetAuthCategoriesQueryParams,
    void
  >,
  "path"
>;

/**
 * GETs the auth categories available to a healthplan
 *
 * performs GET for auth categories using health plan name
 */
export const useGetAuthCategories = (props: UseGetAuthCategoriesProps) =>
  useGet<AuthCategoryResponse[], unknown, GetAuthCategoriesQueryParams, void>(
    `/authCategory`,
    props
  );

export type GetPostgridAddressVerificationProps = Omit<
  MutateProps<
    PostgridAddressVerificationResponse,
    unknown,
    void,
    PostgridAddressVerificationBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Verify Address
 *
 * Verifies the provided address information.
 */
export const GetPostgridAddressVerification = (
  props: GetPostgridAddressVerificationProps
) => (
  <Mutate<
    PostgridAddressVerificationResponse,
    unknown,
    void,
    PostgridAddressVerificationBody,
    void
  >
    verb="POST"
    path={`/postgrid/address/verify`}
    {...props}
  />
);

export type UseGetPostgridAddressVerificationProps = Omit<
  UseMutateProps<
    PostgridAddressVerificationResponse,
    unknown,
    void,
    PostgridAddressVerificationBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Verify Address
 *
 * Verifies the provided address information.
 */
export const useGetPostgridAddressVerification = (
  props: UseGetPostgridAddressVerificationProps
) =>
  useMutate<
    PostgridAddressVerificationResponse,
    unknown,
    void,
    PostgridAddressVerificationBody,
    void
  >("POST", `/postgrid/address/verify`, props);

export type PostMdNoteFeedbackProps = Omit<
  MutateProps<string, unknown, void, MdNoteFeedback, void>,
  "path" | "verb"
>;

export const PostMdNoteFeedback = (props: PostMdNoteFeedbackProps) => (
  <Mutate<string, unknown, void, MdNoteFeedback, void>
    verb="POST"
    path={`/mdNoteFeedback`}
    {...props}
  />
);

export type UsePostMdNoteFeedbackProps = Omit<
  UseMutateProps<string, unknown, void, MdNoteFeedback, void>,
  "path" | "verb"
>;

export const usePostMdNoteFeedback = (props: UsePostMdNoteFeedbackProps) =>
  useMutate<string, unknown, void, MdNoteFeedback, void>(
    "POST",
    `/mdNoteFeedback`,
    props
  );

export type PostPreferredLabelFeedbackProps = Omit<
  MutateProps<
    BulkSavePreferredLabelFeedbackResponse[],
    unknown,
    void,
    PreferredLabelFeedback[],
    void
  >,
  "path" | "verb"
>;

export const PostPreferredLabelFeedback = (
  props: PostPreferredLabelFeedbackProps
) => (
  <Mutate<
    BulkSavePreferredLabelFeedbackResponse[],
    unknown,
    void,
    PreferredLabelFeedback[],
    void
  >
    verb="POST"
    path={`/preferredLabelFeedback/bulkSave`}
    {...props}
  />
);

export type UsePostPreferredLabelFeedbackProps = Omit<
  UseMutateProps<
    BulkSavePreferredLabelFeedbackResponse[],
    unknown,
    void,
    PreferredLabelFeedback[],
    void
  >,
  "path" | "verb"
>;

export const usePostPreferredLabelFeedback = (
  props: UsePostPreferredLabelFeedbackProps
) =>
  useMutate<
    BulkSavePreferredLabelFeedbackResponse[],
    unknown,
    void,
    PreferredLabelFeedback[],
    void
  >("POST", `/preferredLabelFeedback/bulkSave`, props);

export interface FindClaimHistoryResponse {
  results?: FindingRecord[];
  count?: number;
}

export type FindClaimHistoryProps = Omit<
  MutateProps<
    FindClaimHistoryResponse,
    unknown,
    void,
    FindingRecordSearchPayload,
    void
  >,
  "path" | "verb"
>;

export const FindClaimHistory = (props: FindClaimHistoryProps) => (
  <Mutate<
    FindClaimHistoryResponse,
    unknown,
    void,
    FindingRecordSearchPayload,
    void
  >
    verb="POST"
    path={`/claimHistory/search`}
    {...props}
  />
);

export type UseFindClaimHistoryProps = Omit<
  UseMutateProps<
    FindClaimHistoryResponse,
    unknown,
    void,
    FindingRecordSearchPayload,
    void
  >,
  "path" | "verb"
>;

export const useFindClaimHistory = (props: UseFindClaimHistoryProps) =>
  useMutate<
    FindClaimHistoryResponse,
    unknown,
    void,
    FindingRecordSearchPayload,
    void
  >("POST", `/claimHistory/search`, props);

export type BulkSaveFindingRecordsProps = Omit<
  MutateProps<
    FindingRecordBulkSaveResponse,
    unknown,
    void,
    FindingRecordPostRequest[],
    void
  >,
  "path" | "verb"
>;

export const BulkSaveFindingRecords = (props: BulkSaveFindingRecordsProps) => (
  <Mutate<
    FindingRecordBulkSaveResponse,
    unknown,
    void,
    FindingRecordPostRequest[],
    void
  >
    verb="POST"
    path={`/findingRecord/bulkSave`}
    {...props}
  />
);

export type UseBulkSaveFindingRecordsProps = Omit<
  UseMutateProps<
    FindingRecordBulkSaveResponse,
    unknown,
    void,
    FindingRecordPostRequest[],
    void
  >,
  "path" | "verb"
>;

export const useBulkSaveFindingRecords = (
  props: UseBulkSaveFindingRecordsProps
) =>
  useMutate<
    FindingRecordBulkSaveResponse,
    unknown,
    void,
    FindingRecordPostRequest[],
    void
  >("POST", `/findingRecord/bulkSave`, props);

export type GetActuatorInfoProps = Omit<
  GetProps<ActuatorInfo, unknown, void, void>,
  "path"
>;

/**
 * Gets information about the latest core-platform commit
 */
export const GetActuatorInfo = (props: GetActuatorInfoProps) => (
  <Get<ActuatorInfo, unknown, void, void> path={`/actuator/info`} {...props} />
);

export type UseGetActuatorInfoProps = Omit<
  UseGetProps<ActuatorInfo, unknown, void, void>,
  "path"
>;

/**
 * Gets information about the latest core-platform commit
 */
export const useGetActuatorInfo = (props: UseGetActuatorInfoProps) =>
  useGet<ActuatorInfo, unknown, void, void>(`/actuator/info`, props);

export interface GetCoverageFieldsDistinctValuesByHealthPlanResponse {
  productIdOptions?: string[];
  planTypeOptions?: string[];
  groupIdOptions?: string[];
  lineOfBusinessOptions?: string[];
  lineOfBusinessDescriptionOptions?: string[];
  coverageProductTypeOptions?: string[];
  coverageLineOfBusinessTypeOptions?: string[];
  marketOptions?: string[];
  legalEntityOptions?: string[];
}

export interface GetCoverageFieldsDistinctValuesByHealthPlanQueryParams {
  healthPlanName: string;
}

export type GetCoverageFieldsDistinctValuesByHealthPlanProps = Omit<
  GetProps<
    GetCoverageFieldsDistinctValuesByHealthPlanResponse,
    unknown,
    GetCoverageFieldsDistinctValuesByHealthPlanQueryParams,
    void
  >,
  "path"
>;

/**
 * API fetchs all possible distinct values of coverage fields by healthPlanName (required)
 */
export const GetCoverageFieldsDistinctValuesByHealthPlan = (
  props: GetCoverageFieldsDistinctValuesByHealthPlanProps
) => (
  <Get<
    GetCoverageFieldsDistinctValuesByHealthPlanResponse,
    unknown,
    GetCoverageFieldsDistinctValuesByHealthPlanQueryParams,
    void
  >
    path={`/patient/getCoverageFieldDistinctValues`}
    {...props}
  />
);

export type UseGetCoverageFieldsDistinctValuesByHealthPlanProps = Omit<
  UseGetProps<
    GetCoverageFieldsDistinctValuesByHealthPlanResponse,
    unknown,
    GetCoverageFieldsDistinctValuesByHealthPlanQueryParams,
    void
  >,
  "path"
>;

/**
 * API fetchs all possible distinct values of coverage fields by healthPlanName (required)
 */
export const useGetCoverageFieldsDistinctValuesByHealthPlan = (
  props: UseGetCoverageFieldsDistinctValuesByHealthPlanProps
) =>
  useGet<
    GetCoverageFieldsDistinctValuesByHealthPlanResponse,
    unknown,
    GetCoverageFieldsDistinctValuesByHealthPlanQueryParams,
    void
  >(`/patient/getCoverageFieldDistinctValues`, props);

export type BulkUpdateProviderTagMappingsProps = Omit<
  MutateProps<
    BulkUpdateProviderTagMappingsResponse,
    unknown,
    void,
    BulkOperationProviderTagMappingsBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Add multiple tins or npis to a provider tag
 */
export const BulkUpdateProviderTagMappings = (
  props: BulkUpdateProviderTagMappingsProps
) => (
  <Mutate<
    BulkUpdateProviderTagMappingsResponse,
    unknown,
    void,
    BulkOperationProviderTagMappingsBody,
    void
  >
    verb="POST"
    path={`/providerTagMapping/bulkUpdateProviderTagMappings`}
    {...props}
  />
);

export type UseBulkUpdateProviderTagMappingsProps = Omit<
  UseMutateProps<
    BulkUpdateProviderTagMappingsResponse,
    unknown,
    void,
    BulkOperationProviderTagMappingsBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Add multiple tins or npis to a provider tag
 */
export const useBulkUpdateProviderTagMappings = (
  props: UseBulkUpdateProviderTagMappingsProps
) =>
  useMutate<
    BulkUpdateProviderTagMappingsResponse,
    unknown,
    void,
    BulkOperationProviderTagMappingsBody,
    void
  >("POST", `/providerTagMapping/bulkUpdateProviderTagMappings`, props);

export interface GetBenefitPlanGroupHistoricalInformationPathParams {
  benefitPlanGroupId: string;
}

export type GetBenefitPlanGroupHistoricalInformationProps = Omit<
  GetProps<
    BenefitPlanGroup,
    unknown,
    void,
    GetBenefitPlanGroupHistoricalInformationPathParams
  >,
  "path"
> &
  GetBenefitPlanGroupHistoricalInformationPathParams;

export const GetBenefitPlanGroupHistoricalInformation = ({
  benefitPlanGroupId,
  ...props
}: GetBenefitPlanGroupHistoricalInformationProps) => (
  <Get<
    BenefitPlanGroup,
    unknown,
    void,
    GetBenefitPlanGroupHistoricalInformationPathParams
  >
    path={`/crossEnvironmentData/${benefitPlanGroupId}/bpgHistoricalInformation`}
    {...props}
  />
);

export type UseGetBenefitPlanGroupHistoricalInformationProps = Omit<
  UseGetProps<
    BenefitPlanGroup,
    unknown,
    void,
    GetBenefitPlanGroupHistoricalInformationPathParams
  >,
  "path"
> &
  GetBenefitPlanGroupHistoricalInformationPathParams;

export const useGetBenefitPlanGroupHistoricalInformation = ({
  benefitPlanGroupId,
  ...props
}: UseGetBenefitPlanGroupHistoricalInformationProps) =>
  useGet<
    BenefitPlanGroup,
    unknown,
    void,
    GetBenefitPlanGroupHistoricalInformationPathParams
  >(
    (paramsInPath: GetBenefitPlanGroupHistoricalInformationPathParams) =>
      `/crossEnvironmentData/${paramsInPath.benefitPlanGroupId}/bpgHistoricalInformation`,
    { pathParams: { benefitPlanGroupId }, ...props }
  );

export interface GetHealthPlanHistoricalInformationPathParams {
  healthPlanId: string;
}

export type GetHealthPlanHistoricalInformationProps = Omit<
  GetProps<
    HealthPlan,
    unknown,
    void,
    GetHealthPlanHistoricalInformationPathParams
  >,
  "path"
> &
  GetHealthPlanHistoricalInformationPathParams;

export const GetHealthPlanHistoricalInformation = ({
  healthPlanId,
  ...props
}: GetHealthPlanHistoricalInformationProps) => (
  <Get<HealthPlan, unknown, void, GetHealthPlanHistoricalInformationPathParams>
    path={`/crossEnvironmentData/${healthPlanId}/healthPlanHistoricalInformation`}
    {...props}
  />
);

export type UseGetHealthPlanHistoricalInformationProps = Omit<
  UseGetProps<
    HealthPlan,
    unknown,
    void,
    GetHealthPlanHistoricalInformationPathParams
  >,
  "path"
> &
  GetHealthPlanHistoricalInformationPathParams;

export const useGetHealthPlanHistoricalInformation = ({
  healthPlanId,
  ...props
}: UseGetHealthPlanHistoricalInformationProps) =>
  useGet<
    HealthPlan,
    unknown,
    void,
    GetHealthPlanHistoricalInformationPathParams
  >(
    (paramsInPath: GetHealthPlanHistoricalInformationPathParams) =>
      `/crossEnvironmentData/${paramsInPath.healthPlanId}/healthPlanHistoricalInformation`,
    { pathParams: { healthPlanId }, ...props }
  );

export interface GetCrossDataEnvironmentsResponse {
  environments?: string[];
}

export type GetCrossDataEnvironmentsProps = Omit<
  GetProps<GetCrossDataEnvironmentsResponse, unknown, void, void>,
  "path"
>;

/**
 * Get environments from the same tier of the current one
 */
export const GetCrossDataEnvironments = (
  props: GetCrossDataEnvironmentsProps
) => (
  <Get<GetCrossDataEnvironmentsResponse, unknown, void, void>
    path={`/crossEnvironmentData/getSameTierEnvironments`}
    {...props}
  />
);

export type UseGetCrossDataEnvironmentsProps = Omit<
  UseGetProps<GetCrossDataEnvironmentsResponse, unknown, void, void>,
  "path"
>;

/**
 * Get environments from the same tier of the current one
 */
export const useGetCrossDataEnvironments = (
  props: UseGetCrossDataEnvironmentsProps
) =>
  useGet<GetCrossDataEnvironmentsResponse, unknown, void, void>(
    `/crossEnvironmentData/getSameTierEnvironments`,
    props
  );

export interface GetCrossTierDataEnvironmentsResponse {
  [key: string]: any;
}

export type GetCrossTierDataEnvironmentsProps = Omit<
  GetProps<GetCrossTierDataEnvironmentsResponse, unknown, void, void>,
  "path"
>;

/**
 * Get all environments available for propagation
 */
export const GetCrossTierDataEnvironments = (
  props: GetCrossTierDataEnvironmentsProps
) => (
  <Get<GetCrossTierDataEnvironmentsResponse, unknown, void, void>
    path={`/crossEnvironmentData/getAllEnvironments`}
    {...props}
  />
);

export type UseGetCrossTierDataEnvironmentsProps = Omit<
  UseGetProps<GetCrossTierDataEnvironmentsResponse, unknown, void, void>,
  "path"
>;

/**
 * Get all environments available for propagation
 */
export const useGetCrossTierDataEnvironments = (
  props: UseGetCrossTierDataEnvironmentsProps
) =>
  useGet<GetCrossTierDataEnvironmentsResponse, unknown, void, void>(
    `/crossEnvironmentData/getAllEnvironments`,
    props
  );

export interface GetCrossEnvironmentDataByIdPathParams {
  /**
   * Name of the collection to get the data
   */
  domain: string;
  /**
   * A unique identifier
   */
  id: string;
}

export type GetCrossEnvironmentDataByIdProps = Omit<
  GetProps<
    GetSameTierCrossEnvironmentResponse[],
    unknown,
    void,
    GetCrossEnvironmentDataByIdPathParams
  >,
  "path"
> &
  GetCrossEnvironmentDataByIdPathParams;

/**
 * Get object propagation value on same tier environments
 */
export const GetCrossEnvironmentDataById = ({
  domain,
  id,
  ...props
}: GetCrossEnvironmentDataByIdProps) => (
  <Get<
    GetSameTierCrossEnvironmentResponse[],
    unknown,
    void,
    GetCrossEnvironmentDataByIdPathParams
  >
    path={`/crossEnvironmentData/${domain}/${id}`}
    {...props}
  />
);

export type UseGetCrossEnvironmentDataByIdProps = Omit<
  UseGetProps<
    GetSameTierCrossEnvironmentResponse[],
    unknown,
    void,
    GetCrossEnvironmentDataByIdPathParams
  >,
  "path"
> &
  GetCrossEnvironmentDataByIdPathParams;

/**
 * Get object propagation value on same tier environments
 */
export const useGetCrossEnvironmentDataById = ({
  domain,
  id,
  ...props
}: UseGetCrossEnvironmentDataByIdProps) =>
  useGet<
    GetSameTierCrossEnvironmentResponse[],
    unknown,
    void,
    GetCrossEnvironmentDataByIdPathParams
  >(
    (paramsInPath: GetCrossEnvironmentDataByIdPathParams) =>
      `/crossEnvironmentData/${paramsInPath.domain}/${paramsInPath.id}`,
    { pathParams: { domain, id }, ...props }
  );

export interface GetCrossEnvironmentDataByIdAndEnvironmentResponse {
  [key: string]: any;
}

export interface GetCrossEnvironmentDataByIdAndEnvironmentPathParams {
  /**
   * Name of the collection to get the data
   */
  domain: string;
  /**
   * A unique identifier
   */
  id: string;
  /**
   * Environment to fetch the object from
   */
  environment: string;
}

export type GetCrossEnvironmentDataByIdAndEnvironmentProps = Omit<
  GetProps<
    GetCrossEnvironmentDataByIdAndEnvironmentResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdAndEnvironmentPathParams
  >,
  "path"
> &
  GetCrossEnvironmentDataByIdAndEnvironmentPathParams;

/**
 * Get object propagation value on other environments
 */
export const GetCrossEnvironmentDataByIdAndEnvironment = ({
  domain,
  id,
  environment,
  ...props
}: GetCrossEnvironmentDataByIdAndEnvironmentProps) => (
  <Get<
    GetCrossEnvironmentDataByIdAndEnvironmentResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdAndEnvironmentPathParams
  >
    path={`/crossEnvironmentData/crossTier/${domain}/${id}/${environment}`}
    {...props}
  />
);

export type UseGetCrossEnvironmentDataByIdAndEnvironmentProps = Omit<
  UseGetProps<
    GetCrossEnvironmentDataByIdAndEnvironmentResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdAndEnvironmentPathParams
  >,
  "path"
> &
  GetCrossEnvironmentDataByIdAndEnvironmentPathParams;

/**
 * Get object propagation value on other environments
 */
export const useGetCrossEnvironmentDataByIdAndEnvironment = ({
  domain,
  id,
  environment,
  ...props
}: UseGetCrossEnvironmentDataByIdAndEnvironmentProps) =>
  useGet<
    GetCrossEnvironmentDataByIdAndEnvironmentResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdAndEnvironmentPathParams
  >(
    (paramsInPath: GetCrossEnvironmentDataByIdAndEnvironmentPathParams) =>
      `/crossEnvironmentData/crossTier/${paramsInPath.domain}/${paramsInPath.id}/${paramsInPath.environment}`,
    { pathParams: { domain, id, environment }, ...props }
  );

export interface GetCrossEnvironmentDataByIdEnvironmentAndIdentifierResponse {
  [key: string]: any;
}

export interface GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams {
  /**
   * Name of the collection to get the data
   */
  domain: string;
  /**
   * A unique identifier
   */
  id: string;
  /**
   * Environment to fetch the object from
   */
  environment: string;
  /**
   * Specific value to query on another environment as key
   */
  identifier: string;
}

export type GetCrossEnvironmentDataByIdEnvironmentAndIdentifierProps = Omit<
  GetProps<
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams
  >,
  "path"
> &
  GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams;

/**
 * Get object propagation value on other environments
 */
export const GetCrossEnvironmentDataByIdEnvironmentAndIdentifier = ({
  domain,
  id,
  environment,
  identifier,
  ...props
}: GetCrossEnvironmentDataByIdEnvironmentAndIdentifierProps) => (
  <Get<
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams
  >
    path={`/crossEnvironmentData/crossTier/${domain}/${id}/${environment}/${identifier}`}
    {...props}
  />
);

export type UseGetCrossEnvironmentDataByIdEnvironmentAndIdentifierProps = Omit<
  UseGetProps<
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams
  >,
  "path"
> &
  GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams;

/**
 * Get object propagation value on other environments
 */
export const useGetCrossEnvironmentDataByIdEnvironmentAndIdentifier = ({
  domain,
  id,
  environment,
  identifier,
  ...props
}: UseGetCrossEnvironmentDataByIdEnvironmentAndIdentifierProps) =>
  useGet<
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierResponse,
    unknown,
    void,
    GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams
  >(
    (
      paramsInPath: GetCrossEnvironmentDataByIdEnvironmentAndIdentifierPathParams
    ) =>
      `/crossEnvironmentData/crossTier/${paramsInPath.domain}/${paramsInPath.id}/${paramsInPath.environment}/${paramsInPath.identifier}`,
    { pathParams: { domain, id, environment, identifier }, ...props }
  );

export interface CrossEnvironmentDataPropagateRequestBody {
  targets?: string[];
  domain?: string;
  domainIdentifier?: string;
  propagationIdentifier?: string;
}

export type CrossEnvironmentDataPropagateProps = Omit<
  MutateProps<
    PropagationResult[],
    unknown,
    void,
    CrossEnvironmentDataPropagateRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Propagate specifc data to same tier environments
 */
export const CrossEnvironmentDataPropagate = (
  props: CrossEnvironmentDataPropagateProps
) => (
  <Mutate<
    PropagationResult[],
    unknown,
    void,
    CrossEnvironmentDataPropagateRequestBody,
    void
  >
    verb="POST"
    path={`/crossEnvironmentData/propagateToSameTier`}
    {...props}
  />
);

export type UseCrossEnvironmentDataPropagateProps = Omit<
  UseMutateProps<
    PropagationResult[],
    unknown,
    void,
    CrossEnvironmentDataPropagateRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Propagate specifc data to same tier environments
 */
export const useCrossEnvironmentDataPropagate = (
  props: UseCrossEnvironmentDataPropagateProps
) =>
  useMutate<
    PropagationResult[],
    unknown,
    void,
    CrossEnvironmentDataPropagateRequestBody,
    void
  >("POST", `/crossEnvironmentData/propagateToSameTier`, props);

export interface CrossTierPropagationRequestBody {
  targets?: string[];
  domain?: string;
  domainIdentifier?: string;
  propagationIdentifier?: string;
}

export type CrossTierPropagationProps = Omit<
  MutateProps<
    PropagationResult[],
    unknown,
    void,
    CrossTierPropagationRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Propagate specifc data to environments on other tiers
 */
export const CrossTierPropagation = (props: CrossTierPropagationProps) => (
  <Mutate<
    PropagationResult[],
    unknown,
    void,
    CrossTierPropagationRequestBody,
    void
  >
    verb="POST"
    path={`/crossEnvironmentData/crossTierPropagation`}
    {...props}
  />
);

export type UseCrossTierPropagationProps = Omit<
  UseMutateProps<
    PropagationResult[],
    unknown,
    void,
    CrossTierPropagationRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Propagate specifc data to environments on other tiers
 */
export const useCrossTierPropagation = (props: UseCrossTierPropagationProps) =>
  useMutate<
    PropagationResult[],
    unknown,
    void,
    CrossTierPropagationRequestBody,
    void
  >("POST", `/crossEnvironmentData/crossTierPropagation`, props);

export interface CrossTierAuthResponse {
  access_token?: string;
  [key: string]: any;
}

export interface CrossTierAuthRequestBody {
  username?: string;
  password?: string;
  environment?: string;
}

export type CrossTierAuthProps = Omit<
  MutateProps<
    CrossTierAuthResponse,
    unknown,
    void,
    CrossTierAuthRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Get okta token from other tier environment
 */
export const CrossTierAuth = (props: CrossTierAuthProps) => (
  <Mutate<CrossTierAuthResponse, unknown, void, CrossTierAuthRequestBody, void>
    verb="POST"
    path={`/crossEnvironmentData/crossEnvironmentAuth`}
    {...props}
  />
);

export type UseCrossTierAuthProps = Omit<
  UseMutateProps<
    CrossTierAuthResponse,
    unknown,
    void,
    CrossTierAuthRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Get okta token from other tier environment
 */
export const useCrossTierAuth = (props: UseCrossTierAuthProps) =>
  useMutate<
    CrossTierAuthResponse,
    unknown,
    void,
    CrossTierAuthRequestBody,
    void
  >("POST", `/crossEnvironmentData/crossEnvironmentAuth`, props);

export type UploadPolicyPdfToS3Props = Omit<
  MutateProps<void, void, void, void, void>,
  "path" | "verb"
>;

/**
 * Upload a policy PDF to S3
 */
export const UploadPolicyPdfToS3 = (props: UploadPolicyPdfToS3Props) => (
  <Mutate<void, void, void, void, void>
    verb="POST"
    path={`/policyStamp/uploadPolicyPdfToS3`}
    {...props}
  />
);

export type UseUploadPolicyPdfToS3Props = Omit<
  UseMutateProps<void, void, void, void, void>,
  "path" | "verb"
>;

/**
 * Upload a policy PDF to S3
 */
export const useUploadPolicyPdfToS3 = (props: UseUploadPolicyPdfToS3Props) =>
  useMutate<void, void, void, void, void>(
    "POST",
    `/policyStamp/uploadPolicyPdfToS3`,
    props
  );

export type GetPolicyIngestionPolicyTypesProps = Omit<
  GetProps<string[], unknown, void, void>,
  "path"
>;

/**
 * Get Policy/Pdf types accepted by faster policy ingestion
 */
export const GetPolicyIngestionPolicyTypes = (
  props: GetPolicyIngestionPolicyTypesProps
) => (
  <Get<string[], unknown, void, void>
    path={`/policyStamp/getPolicyIngestionPolicyTypes`}
    {...props}
  />
);

export type UseGetPolicyIngestionPolicyTypesProps = Omit<
  UseGetProps<string[], unknown, void, void>,
  "path"
>;

/**
 * Get Policy/Pdf types accepted by faster policy ingestion
 */
export const useGetPolicyIngestionPolicyTypes = (
  props: UseGetPolicyIngestionPolicyTypesProps
) =>
  useGet<string[], unknown, void, void>(
    `/policyStamp/getPolicyIngestionPolicyTypes`,
    props
  );

export interface GetReferralRequestPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
}

export type GetReferralRequestProps = Omit<
  GetProps<
    ReferralRequestResponse,
    unknown,
    void,
    GetReferralRequestPathParams
  >,
  "path"
> &
  GetReferralRequestPathParams;

export const GetReferralRequest = ({
  id,
  ...props
}: GetReferralRequestProps) => (
  <Get<ReferralRequestResponse, unknown, void, GetReferralRequestPathParams>
    path={`/referralRequest/${id}`}
    {...props}
  />
);

export type UseGetReferralRequestProps = Omit<
  UseGetProps<
    ReferralRequestResponse,
    unknown,
    void,
    GetReferralRequestPathParams
  >,
  "path"
> &
  GetReferralRequestPathParams;

export const useGetReferralRequest = ({
  id,
  ...props
}: UseGetReferralRequestProps) =>
  useGet<ReferralRequestResponse, unknown, void, GetReferralRequestPathParams>(
    (paramsInPath: GetReferralRequestPathParams) =>
      `/referralRequest/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateReferralRequestPathParams {
  /**
   * A unique identifier for a `ReferralRequest`.
   */
  id: string;
}

export type UpdateReferralRequestProps = Omit<
  MutateProps<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestCreatePayload,
    UpdateReferralRequestPathParams
  >,
  "path" | "verb"
> &
  UpdateReferralRequestPathParams;

export const UpdateReferralRequest = ({
  id,
  ...props
}: UpdateReferralRequestProps) => (
  <Mutate<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestCreatePayload,
    UpdateReferralRequestPathParams
  >
    verb="PATCH"
    path={`/referralRequest/${id}`}
    {...props}
  />
);

export type UseUpdateReferralRequestProps = Omit<
  UseMutateProps<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestCreatePayload,
    UpdateReferralRequestPathParams
  >,
  "path" | "verb"
> &
  UpdateReferralRequestPathParams;

export const useUpdateReferralRequest = ({
  id,
  ...props
}: UseUpdateReferralRequestProps) =>
  useMutate<
    ReferralRequestResponse,
    ReferralRequestUpdateErrorResponse,
    void,
    ReferralRequestCreatePayload,
    UpdateReferralRequestPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateReferralRequestPathParams) =>
      `/referralRequest/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteReferralRequestProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Soft delete
 */
export const DeleteReferralRequest = (props: DeleteReferralRequestProps) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/referralRequest`}
    {...props}
  />
);

export type UseDeleteReferralRequestProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Soft delete
 */
export const useDeleteReferralRequest = (
  props: UseDeleteReferralRequestProps
) =>
  useMutate<void, unknown, void, string, void>("DELETE", `/referralRequest`, {
    ...props,
  });

export interface CreateReferralRequestQueryParams {
  /**
   * Patient ID
   */
  patient?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * The view to return for an referral request
   */
  view?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  sort?: string;
  id?: string;
}

export type CreateReferralRequestProps = Omit<
  MutateProps<
    ReferralRequestResponse,
    unknown,
    CreateReferralRequestQueryParams,
    ReferralRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * creates a new referralRequest
 */
export const CreateReferralRequest = (props: CreateReferralRequestProps) => (
  <Mutate<
    ReferralRequestResponse,
    unknown,
    CreateReferralRequestQueryParams,
    ReferralRequestCreatePayload,
    void
  >
    verb="POST"
    path={`/referralRequest`}
    {...props}
  />
);

export type UseCreateReferralRequestProps = Omit<
  UseMutateProps<
    ReferralRequestResponse,
    unknown,
    CreateReferralRequestQueryParams,
    ReferralRequestCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * creates a new referralRequest
 */
export const useCreateReferralRequest = (
  props: UseCreateReferralRequestProps
) =>
  useMutate<
    ReferralRequestResponse,
    unknown,
    CreateReferralRequestQueryParams,
    ReferralRequestCreatePayload,
    void
  >("POST", `/referralRequest`, props);

export interface GetReferralRequestsQueryParams {
  /**
   * Patient ID
   */
  patient?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * The view to return for an referral request
   */
  view?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  sort?: string;
  id?: string;
}

export type GetReferralRequestsProps = Omit<
  GetProps<
    ReferralRequestResponse[],
    unknown,
    GetReferralRequestsQueryParams,
    void
  >,
  "path"
>;

export const GetReferralRequests = (props: GetReferralRequestsProps) => (
  <Get<ReferralRequestResponse[], unknown, GetReferralRequestsQueryParams, void>
    path={`/referralRequest`}
    {...props}
  />
);

export type UseGetReferralRequestsProps = Omit<
  UseGetProps<
    ReferralRequestResponse[],
    unknown,
    GetReferralRequestsQueryParams,
    void
  >,
  "path"
>;

export const useGetReferralRequests = (props: UseGetReferralRequestsProps) =>
  useGet<
    ReferralRequestResponse[],
    unknown,
    GetReferralRequestsQueryParams,
    void
  >(`/referralRequest`, props);

export interface GetGuidelineFromMCGQueryParams {
  healthPlanId: string;
  product: string;
  edition: string;
  guidelineCode: string;
}

export type GetGuidelineFromMCGProps = Omit<
  GetProps<MCGGuidelineInformation, void, GetGuidelineFromMCGQueryParams, void>,
  "path"
>;

export const GetGuidelineFromMCG = (props: GetGuidelineFromMCGProps) => (
  <Get<MCGGuidelineInformation, void, GetGuidelineFromMCGQueryParams, void>
    path={`/mcg/getGuideline`}
    {...props}
  />
);

export type UseGetGuidelineFromMCGProps = Omit<
  UseGetProps<
    MCGGuidelineInformation,
    void,
    GetGuidelineFromMCGQueryParams,
    void
  >,
  "path"
>;

export const useGetGuidelineFromMCG = (props: UseGetGuidelineFromMCGProps) =>
  useGet<MCGGuidelineInformation, void, GetGuidelineFromMCGQueryParams, void>(
    `/mcg/getGuideline`,
    props
  );

export interface IngestGuidelineFromMCGQueryParams {
  healthPlanId: string;
  product: string;
  edition: string;
  guidelineCode: string;
  contentTitle: string;
}

export type IngestGuidelineFromMCGProps = Omit<
  MutateProps<Guideline, void, IngestGuidelineFromMCGQueryParams, void, void>,
  "path" | "verb"
>;

export const IngestGuidelineFromMCG = (props: IngestGuidelineFromMCGProps) => (
  <Mutate<Guideline, void, IngestGuidelineFromMCGQueryParams, void, void>
    verb="POST"
    path={`/mcg/MCGGuidelineIngestion`}
    {...props}
  />
);

export type UseIngestGuidelineFromMCGProps = Omit<
  UseMutateProps<
    Guideline,
    void,
    IngestGuidelineFromMCGQueryParams,
    void,
    void
  >,
  "path" | "verb"
>;

export const useIngestGuidelineFromMCG = (
  props: UseIngestGuidelineFromMCGProps
) =>
  useMutate<Guideline, void, IngestGuidelineFromMCGQueryParams, void, void>(
    "POST",
    `/mcg/MCGGuidelineIngestion`,
    props
  );

export interface UploadDocxTemplateResponse {
  finalDeterminationTemplate?: FinalDeterminationTemplate;
}

export type UploadDocxTemplateProps = Omit<
  MutateProps<UploadDocxTemplateResponse, unknown, void, void, void>,
  "path" | "verb"
>;

/**
 * Uploads a document template
 *
 * This will return the S3 url for the uploaded file
 */
export const UploadDocxTemplate = (props: UploadDocxTemplateProps) => (
  <Mutate<UploadDocxTemplateResponse, unknown, void, void, void>
    verb="POST"
    path={`/notificationTemplate/uploadDocxTemplate`}
    {...props}
  />
);

export type UseUploadDocxTemplateProps = Omit<
  UseMutateProps<UploadDocxTemplateResponse, unknown, void, void, void>,
  "path" | "verb"
>;

/**
 * Uploads a document template
 *
 * This will return the S3 url for the uploaded file
 */
export const useUploadDocxTemplate = (props: UseUploadDocxTemplateProps) =>
  useMutate<UploadDocxTemplateResponse, unknown, void, void, void>(
    "POST",
    `/notificationTemplate/uploadDocxTemplate`,
    props
  );

export interface GetDocxTemplateResponse {
  template?: string;
}

export interface GetDocxTemplateQueryParams {
  /**
   * The name of the file to be fetched
   */
  name: string;
  /**
   * If set to bytes the it returns the file in binary format
   */
  format?: string;
  /**
   * Specific version to be fetched
   */
  versionId?: string;
}

export type GetDocxTemplateProps = Omit<
  GetProps<GetDocxTemplateResponse, unknown, GetDocxTemplateQueryParams, void>,
  "path"
>;

/**
 * Get the base64 encoded string for the template
 *
 * Gets a template file by its name
 */
export const GetDocxTemplate = (props: GetDocxTemplateProps) => (
  <Get<GetDocxTemplateResponse, unknown, GetDocxTemplateQueryParams, void>
    path={`/notificationTemplate/getDocxTemplate`}
    {...props}
  />
);

export type UseGetDocxTemplateProps = Omit<
  UseGetProps<
    GetDocxTemplateResponse,
    unknown,
    GetDocxTemplateQueryParams,
    void
  >,
  "path"
>;

/**
 * Get the base64 encoded string for the template
 *
 * Gets a template file by its name
 */
export const useGetDocxTemplate = (props: UseGetDocxTemplateProps) =>
  useGet<GetDocxTemplateResponse, unknown, GetDocxTemplateQueryParams, void>(
    `/notificationTemplate/getDocxTemplate`,
    props
  );

export interface SimulateNotificationPdfResponse {
  result?: string;
}

export interface SimulateNotificationPdfRequestBody {
  templateKey?: string;
  template?: string;
  variables?: { [key: string]: any };
}

export type SimulateNotificationPdfProps = Omit<
  MutateProps<
    SimulateNotificationPdfResponse,
    unknown,
    void,
    SimulateNotificationPdfRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Simulate a pdf based on variables and template key
 *
 * Generate a pdf based on variables and template key
 */
export const SimulateNotificationPdf = (
  props: SimulateNotificationPdfProps
) => (
  <Mutate<
    SimulateNotificationPdfResponse,
    unknown,
    void,
    SimulateNotificationPdfRequestBody,
    void
  >
    verb="POST"
    path={`/notificationTemplate/simulate`}
    {...props}
  />
);

export type UseSimulateNotificationPdfProps = Omit<
  UseMutateProps<
    SimulateNotificationPdfResponse,
    unknown,
    void,
    SimulateNotificationPdfRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Simulate a pdf based on variables and template key
 *
 * Generate a pdf based on variables and template key
 */
export const useSimulateNotificationPdf = (
  props: UseSimulateNotificationPdfProps
) =>
  useMutate<
    SimulateNotificationPdfResponse,
    unknown,
    void,
    SimulateNotificationPdfRequestBody,
    void
  >("POST", `/notificationTemplate/simulate`, props);

export interface UpdateAuthStatusResponse {
  message?: string;
}

export type UpdateAuthStatusProps = Omit<
  MutateProps<
    UpdateAuthStatusResponse,
    ErrorMessage,
    void,
    AuthStatusUpdatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * Updates authorization and service request auth statuses
 */
export const UpdateAuthStatus = (props: UpdateAuthStatusProps) => (
  <Mutate<
    UpdateAuthStatusResponse,
    ErrorMessage,
    void,
    AuthStatusUpdatePayload,
    void
  >
    verb="PATCH"
    path={`/adminDataOperation/reviewCorrections/authStatusUpdate`}
    {...props}
  />
);

export type UseUpdateAuthStatusProps = Omit<
  UseMutateProps<
    UpdateAuthStatusResponse,
    ErrorMessage,
    void,
    AuthStatusUpdatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * Updates authorization and service request auth statuses
 */
export const useUpdateAuthStatus = (props: UseUpdateAuthStatusProps) =>
  useMutate<
    UpdateAuthStatusResponse,
    ErrorMessage,
    void,
    AuthStatusUpdatePayload,
    void
  >("PATCH", `/adminDataOperation/reviewCorrections/authStatusUpdate`, props);

export interface SendLettersResponse {
  message?: string;
}

export type SendLettersProps = Omit<
  MutateProps<
    SendLettersResponse,
    ErrorMessage,
    void,
    ReviewSendLettersPayload,
    void
  >,
  "path" | "verb"
>;

export const SendLetters = (props: SendLettersProps) => (
  <Mutate<
    SendLettersResponse,
    ErrorMessage,
    void,
    ReviewSendLettersPayload,
    void
  >
    verb="PATCH"
    path={`/adminDataOperation/reviewCorrections/sendLetters`}
    {...props}
  />
);

export type UseSendLettersProps = Omit<
  UseMutateProps<
    SendLettersResponse,
    ErrorMessage,
    void,
    ReviewSendLettersPayload,
    void
  >,
  "path" | "verb"
>;

export const useSendLetters = (props: UseSendLettersProps) =>
  useMutate<
    SendLettersResponse,
    ErrorMessage,
    void,
    ReviewSendLettersPayload,
    void
  >("PATCH", `/adminDataOperation/reviewCorrections/sendLetters`, props);

export interface SendFinalDeterminationNotificationRequestBody {
  serviceRequestId?: string;
  recipients?: string[];
  manuallyCreated?: boolean;
}

export type SendFinalDeterminationNotificationProps = Omit<
  MutateProps<
    SendFinalDeterminationLetterResponse,
    unknown,
    void,
    SendFinalDeterminationNotificationRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Send final determination notification
 */
export const SendFinalDeterminationNotification = (
  props: SendFinalDeterminationNotificationProps
) => (
  <Mutate<
    SendFinalDeterminationLetterResponse,
    unknown,
    void,
    SendFinalDeterminationNotificationRequestBody,
    void
  >
    verb="POST"
    path={`/notification/finalDetermination`}
    {...props}
  />
);

export type UseSendFinalDeterminationNotificationProps = Omit<
  UseMutateProps<
    SendFinalDeterminationLetterResponse,
    unknown,
    void,
    SendFinalDeterminationNotificationRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Send final determination notification
 */
export const useSendFinalDeterminationNotification = (
  props: UseSendFinalDeterminationNotificationProps
) =>
  useMutate<
    SendFinalDeterminationLetterResponse,
    unknown,
    void,
    SendFinalDeterminationNotificationRequestBody,
    void
  >("POST", `/notification/finalDetermination`, props);

export interface GetServiceRequestReviewChatbotFindingsQueryParams {
  /**
   * Filter to include only the latest non-manual chatbot findings + all the manual chatbot findings
   */
  onlyLatestNonManualWithAllManual?: boolean;
}

export interface GetServiceRequestReviewChatbotFindingsPathParams {
  /**
   * The ID of the service request
   */
  serviceRequestId: string;
}

export type GetServiceRequestReviewChatbotFindingsProps = Omit<
  GetProps<
    ReviewChatbotFinding[],
    unknown,
    GetServiceRequestReviewChatbotFindingsQueryParams,
    GetServiceRequestReviewChatbotFindingsPathParams
  >,
  "path"
> &
  GetServiceRequestReviewChatbotFindingsPathParams;

/**
 * Retrieve chatbot findings for a service request
 */
export const GetServiceRequestReviewChatbotFindings = ({
  serviceRequestId,
  ...props
}: GetServiceRequestReviewChatbotFindingsProps) => (
  <Get<
    ReviewChatbotFinding[],
    unknown,
    GetServiceRequestReviewChatbotFindingsQueryParams,
    GetServiceRequestReviewChatbotFindingsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/reviewChatbot/findings`}
    {...props}
  />
);

export type UseGetServiceRequestReviewChatbotFindingsProps = Omit<
  UseGetProps<
    ReviewChatbotFinding[],
    unknown,
    GetServiceRequestReviewChatbotFindingsQueryParams,
    GetServiceRequestReviewChatbotFindingsPathParams
  >,
  "path"
> &
  GetServiceRequestReviewChatbotFindingsPathParams;

/**
 * Retrieve chatbot findings for a service request
 */
export const useGetServiceRequestReviewChatbotFindings = ({
  serviceRequestId,
  ...props
}: UseGetServiceRequestReviewChatbotFindingsProps) =>
  useGet<
    ReviewChatbotFinding[],
    unknown,
    GetServiceRequestReviewChatbotFindingsQueryParams,
    GetServiceRequestReviewChatbotFindingsPathParams
  >(
    (paramsInPath: GetServiceRequestReviewChatbotFindingsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reviewChatbot/findings`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetServiceRequestReviewChatbotRelevantPromptsPathParams {
  /**
   * The ID of the service request
   */
  serviceRequestId: string;
}

export type GetServiceRequestReviewChatbotRelevantPromptsProps = Omit<
  GetProps<
    ReviewChatbotConfigurationPrompt[],
    unknown,
    void,
    GetServiceRequestReviewChatbotRelevantPromptsPathParams
  >,
  "path"
> &
  GetServiceRequestReviewChatbotRelevantPromptsPathParams;

/**
 * Retrieve relevant prompts for a service request
 */
export const GetServiceRequestReviewChatbotRelevantPrompts = ({
  serviceRequestId,
  ...props
}: GetServiceRequestReviewChatbotRelevantPromptsProps) => (
  <Get<
    ReviewChatbotConfigurationPrompt[],
    unknown,
    void,
    GetServiceRequestReviewChatbotRelevantPromptsPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/reviewChatbot/relevantPrompts`}
    {...props}
  />
);

export type UseGetServiceRequestReviewChatbotRelevantPromptsProps = Omit<
  UseGetProps<
    ReviewChatbotConfigurationPrompt[],
    unknown,
    void,
    GetServiceRequestReviewChatbotRelevantPromptsPathParams
  >,
  "path"
> &
  GetServiceRequestReviewChatbotRelevantPromptsPathParams;

/**
 * Retrieve relevant prompts for a service request
 */
export const useGetServiceRequestReviewChatbotRelevantPrompts = ({
  serviceRequestId,
  ...props
}: UseGetServiceRequestReviewChatbotRelevantPromptsProps) =>
  useGet<
    ReviewChatbotConfigurationPrompt[],
    unknown,
    void,
    GetServiceRequestReviewChatbotRelevantPromptsPathParams
  >(
    (paramsInPath: GetServiceRequestReviewChatbotRelevantPromptsPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reviewChatbot/relevantPrompts`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetReviewChatbotResponsesRequestBody {
  /**
   * A list of questions from the user (UI limited to one item)
   */
  user_input: string[];
  /**
   * Service Request ID
   */
  sr_id: string;
  /**
   * hashed id for current user chatbot session
   */
  session_id?: string;
  review?: {
    id?: string;
  };
  is_click_to_ask_question?: boolean;
}

export type GetReviewChatbotResponsesProps = Omit<
  MutateProps<
    ReviewChatbotApiResponse,
    unknown,
    void,
    GetReviewChatbotResponsesRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Query review chatbot on a given SR
 */
export const GetReviewChatbotResponses = (
  props: GetReviewChatbotResponsesProps
) => (
  <Mutate<
    ReviewChatbotApiResponse,
    unknown,
    void,
    GetReviewChatbotResponsesRequestBody,
    void
  >
    verb="POST"
    path={`/api`}
    {...props}
  />
);

export type UseGetReviewChatbotResponsesProps = Omit<
  UseMutateProps<
    ReviewChatbotApiResponse,
    unknown,
    void,
    GetReviewChatbotResponsesRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Query review chatbot on a given SR
 */
export const useGetReviewChatbotResponses = (
  props: UseGetReviewChatbotResponsesProps
) =>
  useMutate<
    ReviewChatbotApiResponse,
    unknown,
    void,
    GetReviewChatbotResponsesRequestBody,
    void
  >("POST", `/api`, props);

export interface VerifyChatbotServiceRequestOcrAndEmbedStatusRequestBody {
  /**
   * A list of attachment IDs to check OCR and embedding status
   */
  attachmentIds?: string[];
  /**
   * Service Request ID
   */
  serviceRequestId: string;
}

export type VerifyChatbotServiceRequestOcrAndEmbedStatusProps = Omit<
  MutateProps<
    string,
    unknown,
    void,
    VerifyChatbotServiceRequestOcrAndEmbedStatusRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Verify if OCR has been run on a service request attachments
 */
export const VerifyChatbotServiceRequestOcrAndEmbedStatus = (
  props: VerifyChatbotServiceRequestOcrAndEmbedStatusProps
) => (
  <Mutate<
    string,
    unknown,
    void,
    VerifyChatbotServiceRequestOcrAndEmbedStatusRequestBody,
    void
  >
    verb="POST"
    path={`/api/service_request/ocr_embed_status`}
    {...props}
  />
);

export type UseVerifyChatbotServiceRequestOcrAndEmbedStatusProps = Omit<
  UseMutateProps<
    string,
    unknown,
    void,
    VerifyChatbotServiceRequestOcrAndEmbedStatusRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Verify if OCR has been run on a service request attachments
 */
export const useVerifyChatbotServiceRequestOcrAndEmbedStatus = (
  props: UseVerifyChatbotServiceRequestOcrAndEmbedStatusProps
) =>
  useMutate<
    string,
    unknown,
    void,
    VerifyChatbotServiceRequestOcrAndEmbedStatusRequestBody,
    void
  >("POST", `/api/service_request/ocr_embed_status`, props);

export interface SubmitBulkAsynchronousRuleRunResponse {
  message?: string;
}

export interface SubmitBulkAsynchronousRuleRunRequestBody {
  serviceRequestIds?: string[];
  notes?: string;
}

export type SubmitBulkAsynchronousRuleRunProps = Omit<
  MutateProps<
    SubmitBulkAsynchronousRuleRunResponse,
    unknown,
    void,
    SubmitBulkAsynchronousRuleRunRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Allows running of rules again on select service requests specified in body
 */
export const SubmitBulkAsynchronousRuleRun = (
  props: SubmitBulkAsynchronousRuleRunProps
) => (
  <Mutate<
    SubmitBulkAsynchronousRuleRunResponse,
    unknown,
    void,
    SubmitBulkAsynchronousRuleRunRequestBody,
    void
  >
    verb="POST"
    path={`/serviceRequest/bulkAsynchronousRuleRun`}
    {...props}
  />
);

export type UseSubmitBulkAsynchronousRuleRunProps = Omit<
  UseMutateProps<
    SubmitBulkAsynchronousRuleRunResponse,
    unknown,
    void,
    SubmitBulkAsynchronousRuleRunRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Allows running of rules again on select service requests specified in body
 */
export const useSubmitBulkAsynchronousRuleRun = (
  props: UseSubmitBulkAsynchronousRuleRunProps
) =>
  useMutate<
    SubmitBulkAsynchronousRuleRunResponse,
    unknown,
    void,
    SubmitBulkAsynchronousRuleRunRequestBody,
    void
  >("POST", `/serviceRequest/bulkAsynchronousRuleRun`, props);

export interface GetAsyncRuleRunAuditHistoryQueryParams {
  /**
   * The ID of the service request
   */
  serviceRequestId?: string;
  /**
   * Date to start querying from
   */
  startDate?: string;
  /**
   * Date to end querying at
   */
  endDate?: string;
  /**
   * Maximum number of values to return
   */
  max?: string;
  /**
   * The field to sort on
   */
  sort?: string;
  /**
   * Sort order - asc or desc
   */
  order?: string;
}

export type GetAsyncRuleRunAuditHistoryProps = Omit<
  GetProps<
    ManualRuleRunSummary[],
    unknown,
    GetAsyncRuleRunAuditHistoryQueryParams,
    void
  >,
  "path"
>;

/**
 * Get a list of previous rule runs
 */
export const GetAsyncRuleRunAuditHistory = (
  props: GetAsyncRuleRunAuditHistoryProps
) => (
  <Get<
    ManualRuleRunSummary[],
    unknown,
    GetAsyncRuleRunAuditHistoryQueryParams,
    void
  >
    path={`/asyncRuleRunAuditHistory`}
    {...props}
  />
);

export type UseGetAsyncRuleRunAuditHistoryProps = Omit<
  UseGetProps<
    ManualRuleRunSummary[],
    unknown,
    GetAsyncRuleRunAuditHistoryQueryParams,
    void
  >,
  "path"
>;

/**
 * Get a list of previous rule runs
 */
export const useGetAsyncRuleRunAuditHistory = (
  props: UseGetAsyncRuleRunAuditHistoryProps
) =>
  useGet<
    ManualRuleRunSummary[],
    unknown,
    GetAsyncRuleRunAuditHistoryQueryParams,
    void
  >(`/asyncRuleRunAuditHistory`, props);

export interface CreateReopeningPathParams {
  serviceRequestId: string;
}

export type CreateReopeningProps = Omit<
  MutateProps<
    Reopening,
    ErrorResponse,
    void,
    ReopeningSubmissionPayload,
    CreateReopeningPathParams
  >,
  "path" | "verb"
> &
  CreateReopeningPathParams;

/**
 * Create a reopening
 */
export const CreateReopening = ({
  serviceRequestId,
  ...props
}: CreateReopeningProps) => (
  <Mutate<
    Reopening,
    ErrorResponse,
    void,
    ReopeningSubmissionPayload,
    CreateReopeningPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/reopening`}
    {...props}
  />
);

export type UseCreateReopeningProps = Omit<
  UseMutateProps<
    Reopening,
    ErrorResponse,
    void,
    ReopeningSubmissionPayload,
    CreateReopeningPathParams
  >,
  "path" | "verb"
> &
  CreateReopeningPathParams;

/**
 * Create a reopening
 */
export const useCreateReopening = ({
  serviceRequestId,
  ...props
}: UseCreateReopeningProps) =>
  useMutate<
    Reopening,
    ErrorResponse,
    void,
    ReopeningSubmissionPayload,
    CreateReopeningPathParams
  >(
    "POST",
    (paramsInPath: CreateReopeningPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reopening`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetReopeningsByServiceRequestIdPathParams {
  serviceRequestId: string;
}

export type GetReopeningsByServiceRequestIdProps = Omit<
  GetProps<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByServiceRequestIdPathParams
  >,
  "path"
> &
  GetReopeningsByServiceRequestIdPathParams;

/**
 * List reopenings for a service request
 */
export const GetReopeningsByServiceRequestId = ({
  serviceRequestId,
  ...props
}: GetReopeningsByServiceRequestIdProps) => (
  <Get<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByServiceRequestIdPathParams
  >
    path={`/serviceRequest/${serviceRequestId}/reopening`}
    {...props}
  />
);

export type UseGetReopeningsByServiceRequestIdProps = Omit<
  UseGetProps<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByServiceRequestIdPathParams
  >,
  "path"
> &
  GetReopeningsByServiceRequestIdPathParams;

/**
 * List reopenings for a service request
 */
export const useGetReopeningsByServiceRequestId = ({
  serviceRequestId,
  ...props
}: UseGetReopeningsByServiceRequestIdProps) =>
  useGet<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByServiceRequestIdPathParams
  >(
    (paramsInPath: GetReopeningsByServiceRequestIdPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reopening`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface UpdateReopeningByIdPathParams {
  serviceRequestId: string;
  id: string;
}

export type UpdateReopeningByIdProps = Omit<
  MutateProps<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    UpdateReopeningByIdPathParams
  >,
  "path" | "verb"
> &
  UpdateReopeningByIdPathParams;

/**
 * Update a reopening
 */
export const UpdateReopeningById = ({
  serviceRequestId,
  id,
  ...props
}: UpdateReopeningByIdProps) => (
  <Mutate<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    UpdateReopeningByIdPathParams
  >
    verb="PUT"
    path={`/serviceRequest/${serviceRequestId}/reopening/${id}`}
    {...props}
  />
);

export type UseUpdateReopeningByIdProps = Omit<
  UseMutateProps<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    UpdateReopeningByIdPathParams
  >,
  "path" | "verb"
> &
  UpdateReopeningByIdPathParams;

/**
 * Update a reopening
 */
export const useUpdateReopeningById = ({
  serviceRequestId,
  id,
  ...props
}: UseUpdateReopeningByIdProps) =>
  useMutate<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    UpdateReopeningByIdPathParams
  >(
    "PUT",
    (paramsInPath: UpdateReopeningByIdPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reopening/${paramsInPath.id}`,
    { pathParams: { serviceRequestId, id }, ...props }
  );

export interface PatchReopeningByIdPathParams {
  serviceRequestId: string;
  id: string;
}

export type PatchReopeningByIdProps = Omit<
  MutateProps<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    PatchReopeningByIdPathParams
  >,
  "path" | "verb"
> &
  PatchReopeningByIdPathParams;

/**
 * Patch a reopening
 */
export const PatchReopeningById = ({
  serviceRequestId,
  id,
  ...props
}: PatchReopeningByIdProps) => (
  <Mutate<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    PatchReopeningByIdPathParams
  >
    verb="PATCH"
    path={`/serviceRequest/${serviceRequestId}/reopening/${id}`}
    {...props}
  />
);

export type UsePatchReopeningByIdProps = Omit<
  UseMutateProps<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    PatchReopeningByIdPathParams
  >,
  "path" | "verb"
> &
  PatchReopeningByIdPathParams;

/**
 * Patch a reopening
 */
export const usePatchReopeningById = ({
  serviceRequestId,
  id,
  ...props
}: UsePatchReopeningByIdProps) =>
  useMutate<
    Reopening,
    ErrorResponse,
    void,
    Reopening,
    PatchReopeningByIdPathParams
  >(
    "PATCH",
    (paramsInPath: PatchReopeningByIdPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reopening/${paramsInPath.id}`,
    { pathParams: { serviceRequestId, id }, ...props }
  );

export interface GetReopeningByIdPathParams {
  id: string;
}

export type GetReopeningByIdProps = Omit<
  GetProps<Reopening, ErrorResponse, void, GetReopeningByIdPathParams>,
  "path"
> &
  GetReopeningByIdPathParams;

/**
 * Get a single reopening
 */
export const GetReopeningById = ({ id, ...props }: GetReopeningByIdProps) => (
  <Get<Reopening, ErrorResponse, void, GetReopeningByIdPathParams>
    path={`/reopening/${id}`}
    {...props}
  />
);

export type UseGetReopeningByIdProps = Omit<
  UseGetProps<Reopening, ErrorResponse, void, GetReopeningByIdPathParams>,
  "path"
> &
  GetReopeningByIdPathParams;

/**
 * Get a single reopening
 */
export const useGetReopeningById = ({
  id,
  ...props
}: UseGetReopeningByIdProps) =>
  useGet<Reopening, ErrorResponse, void, GetReopeningByIdPathParams>(
    (paramsInPath: GetReopeningByIdPathParams) =>
      `/reopening/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteReopeningByIdProps = Omit<
  MutateProps<void, ErrorResponse, void, string, void>,
  "path" | "verb"
>;

/**
 * Not allowed
 */
export const DeleteReopeningById = (props: DeleteReopeningByIdProps) => (
  <Mutate<void, ErrorResponse, void, string, void>
    verb="DELETE"
    path={`/reopening`}
    {...props}
  />
);

export type UseDeleteReopeningByIdProps = Omit<
  UseMutateProps<void, ErrorResponse, void, string, void>,
  "path" | "verb"
>;

/**
 * Not allowed
 */
export const useDeleteReopeningById = (props: UseDeleteReopeningByIdProps) =>
  useMutate<void, ErrorResponse, void, string, void>("DELETE", `/reopening`, {
    ...props,
  });

export interface GetReopeningsByAuthorizationIdPathParams {
  authorizationId: string;
}

export type GetReopeningsByAuthorizationIdProps = Omit<
  GetProps<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByAuthorizationIdPathParams
  >,
  "path"
> &
  GetReopeningsByAuthorizationIdPathParams;

/**
 * Get reopenings for an authorization
 */
export const GetReopeningsByAuthorizationId = ({
  authorizationId,
  ...props
}: GetReopeningsByAuthorizationIdProps) => (
  <Get<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByAuthorizationIdPathParams
  >
    path={`/reopening/authorization/${authorizationId}`}
    {...props}
  />
);

export type UseGetReopeningsByAuthorizationIdProps = Omit<
  UseGetProps<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByAuthorizationIdPathParams
  >,
  "path"
> &
  GetReopeningsByAuthorizationIdPathParams;

/**
 * Get reopenings for an authorization
 */
export const useGetReopeningsByAuthorizationId = ({
  authorizationId,
  ...props
}: UseGetReopeningsByAuthorizationIdProps) =>
  useGet<
    Reopening[],
    ErrorResponse,
    void,
    GetReopeningsByAuthorizationIdPathParams
  >(
    (paramsInPath: GetReopeningsByAuthorizationIdPathParams) =>
      `/reopening/authorization/${paramsInPath.authorizationId}`,
    { pathParams: { authorizationId }, ...props }
  );

export interface CreateReconsiderationPathParams {
  serviceRequestId: string;
}

export type CreateReconsiderationProps = Omit<
  MutateProps<
    Reconsideration,
    ErrorResponse,
    void,
    ReconsiderationSubmissionPayload,
    CreateReconsiderationPathParams
  >,
  "path" | "verb"
> &
  CreateReconsiderationPathParams;

/**
 * Create a reconsideration
 */
export const CreateReconsideration = ({
  serviceRequestId,
  ...props
}: CreateReconsiderationProps) => (
  <Mutate<
    Reconsideration,
    ErrorResponse,
    void,
    ReconsiderationSubmissionPayload,
    CreateReconsiderationPathParams
  >
    verb="POST"
    path={`/serviceRequest/${serviceRequestId}/reconsideration`}
    {...props}
  />
);

export type UseCreateReconsiderationProps = Omit<
  UseMutateProps<
    Reconsideration,
    ErrorResponse,
    void,
    ReconsiderationSubmissionPayload,
    CreateReconsiderationPathParams
  >,
  "path" | "verb"
> &
  CreateReconsiderationPathParams;

/**
 * Create a reconsideration
 */
export const useCreateReconsideration = ({
  serviceRequestId,
  ...props
}: UseCreateReconsiderationProps) =>
  useMutate<
    Reconsideration,
    ErrorResponse,
    void,
    ReconsiderationSubmissionPayload,
    CreateReconsiderationPathParams
  >(
    "POST",
    (paramsInPath: CreateReconsiderationPathParams) =>
      `/serviceRequest/${paramsInPath.serviceRequestId}/reconsideration`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetReconsiderationByIdPathParams {
  id: string;
}

export type GetReconsiderationByIdProps = Omit<
  GetProps<
    Reconsideration,
    ErrorResponse,
    void,
    GetReconsiderationByIdPathParams
  >,
  "path"
> &
  GetReconsiderationByIdPathParams;

/**
 * Get a single reconsideration
 */
export const GetReconsiderationById = ({
  id,
  ...props
}: GetReconsiderationByIdProps) => (
  <Get<Reconsideration, ErrorResponse, void, GetReconsiderationByIdPathParams>
    path={`/reconsideration/${id}`}
    {...props}
  />
);

export type UseGetReconsiderationByIdProps = Omit<
  UseGetProps<
    Reconsideration,
    ErrorResponse,
    void,
    GetReconsiderationByIdPathParams
  >,
  "path"
> &
  GetReconsiderationByIdPathParams;

/**
 * Get a single reconsideration
 */
export const useGetReconsiderationById = ({
  id,
  ...props
}: UseGetReconsiderationByIdProps) =>
  useGet<
    Reconsideration,
    ErrorResponse,
    void,
    GetReconsiderationByIdPathParams
  >(
    (paramsInPath: GetReconsiderationByIdPathParams) =>
      `/reconsideration/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface GetReconsiderationsByAuthorizationIdPathParams {
  authId: string;
}

export type GetReconsiderationsByAuthorizationIdProps = Omit<
  GetProps<
    Reconsideration[],
    ErrorResponse,
    void,
    GetReconsiderationsByAuthorizationIdPathParams
  >,
  "path"
> &
  GetReconsiderationsByAuthorizationIdPathParams;

/**
 * Get reconsiderations for an authorization
 */
export const GetReconsiderationsByAuthorizationId = ({
  authId,
  ...props
}: GetReconsiderationsByAuthorizationIdProps) => (
  <Get<
    Reconsideration[],
    ErrorResponse,
    void,
    GetReconsiderationsByAuthorizationIdPathParams
  >
    path={`/reconsideration/authorization/${authId}`}
    {...props}
  />
);

export type UseGetReconsiderationsByAuthorizationIdProps = Omit<
  UseGetProps<
    Reconsideration[],
    ErrorResponse,
    void,
    GetReconsiderationsByAuthorizationIdPathParams
  >,
  "path"
> &
  GetReconsiderationsByAuthorizationIdPathParams;

/**
 * Get reconsiderations for an authorization
 */
export const useGetReconsiderationsByAuthorizationId = ({
  authId,
  ...props
}: UseGetReconsiderationsByAuthorizationIdProps) =>
  useGet<
    Reconsideration[],
    ErrorResponse,
    void,
    GetReconsiderationsByAuthorizationIdPathParams
  >(
    (paramsInPath: GetReconsiderationsByAuthorizationIdPathParams) =>
      `/reconsideration/authorization/${paramsInPath.authId}`,
    { pathParams: { authId }, ...props }
  );

export type GetServiceRequestRuleDataModelProps = Omit<
  GetProps<ServiceRequestRuleDataModelJson[], unknown, void, void>,
  "path"
>;

/**
 * Get the data model in JSON format
 */
export const GetServiceRequestRuleDataModel = (
  props: GetServiceRequestRuleDataModelProps
) => (
  <Get<ServiceRequestRuleDataModelJson[], unknown, void, void>
    path={`/serviceRequestRule/getServiceRequestRuleDataModel`}
    {...props}
  />
);

export type UseGetServiceRequestRuleDataModelProps = Omit<
  UseGetProps<ServiceRequestRuleDataModelJson[], unknown, void, void>,
  "path"
>;

/**
 * Get the data model in JSON format
 */
export const useGetServiceRequestRuleDataModel = (
  props: UseGetServiceRequestRuleDataModelProps
) =>
  useGet<ServiceRequestRuleDataModelJson[], unknown, void, void>(
    `/serviceRequestRule/getServiceRequestRuleDataModel`,
    props
  );

export interface PostMcgRedirectResponse {
  /**
   * url to request to
   */
  url: string;
  /**
   * params to pass to carewebqi endpoint via form data
   */
  params: { [key: string]: any };
}

export interface PostMcgRedirectPathParams {
  serviceRequestId: string;
}

export interface PostMcgRedirectRequestBody {
  returnUrl?: string;
}

export type PostMcgRedirectProps = Omit<
  MutateProps<
    PostMcgRedirectResponse,
    unknown,
    void,
    PostMcgRedirectRequestBody,
    PostMcgRedirectPathParams
  >,
  "path" | "verb"
> &
  PostMcgRedirectPathParams;

/**
 * Get the data necessary to generate form-post redirect request
 */
export const PostMcgRedirect = ({
  serviceRequestId,
  ...props
}: PostMcgRedirectProps) => (
  <Mutate<
    PostMcgRedirectResponse,
    unknown,
    void,
    PostMcgRedirectRequestBody,
    PostMcgRedirectPathParams
  >
    verb="POST"
    path={`/carewebqi/${serviceRequestId}/v2`}
    {...props}
  />
);

export type UsePostMcgRedirectProps = Omit<
  UseMutateProps<
    PostMcgRedirectResponse,
    unknown,
    void,
    PostMcgRedirectRequestBody,
    PostMcgRedirectPathParams
  >,
  "path" | "verb"
> &
  PostMcgRedirectPathParams;

/**
 * Get the data necessary to generate form-post redirect request
 */
export const usePostMcgRedirect = ({
  serviceRequestId,
  ...props
}: UsePostMcgRedirectProps) =>
  useMutate<
    PostMcgRedirectResponse,
    unknown,
    void,
    PostMcgRedirectRequestBody,
    PostMcgRedirectPathParams
  >(
    "POST",
    (paramsInPath: PostMcgRedirectPathParams) =>
      `/carewebqi/${paramsInPath.serviceRequestId}/v2`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface RefreshRequestBody {
  serviceRequestId?: string;
  authNumber?: string;
  healthPlanName?: string;
}

export type RefreshProps = Omit<
  MutateProps<AuthorizationResponse, unknown, void, RefreshRequestBody, void>,
  "path" | "verb"
>;

/**
 * Refresh service request from source system
 *
 * Refresh service request from source system
 */
export const Refresh = (props: RefreshProps) => (
  <Mutate<AuthorizationResponse, unknown, void, RefreshRequestBody, void>
    verb="POST"
    path={`/serviceRequest/refresh`}
    {...props}
  />
);

export type UseRefreshProps = Omit<
  UseMutateProps<
    AuthorizationResponse,
    unknown,
    void,
    RefreshRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Refresh service request from source system
 *
 * Refresh service request from source system
 */
export const useRefresh = (props: UseRefreshProps) =>
  useMutate<AuthorizationResponse, unknown, void, RefreshRequestBody, void>(
    "POST",
    `/serviceRequest/refresh`,
    props
  );

export interface ApplyCarewebqiUpdatesPathParams {
  serviceRequestId: string;
}

export type ApplyCarewebqiUpdatesProps = Omit<
  MutateProps<
    CarewebqiUpdateResponse,
    unknown,
    void,
    void,
    ApplyCarewebqiUpdatesPathParams
  >,
  "path" | "verb"
> &
  ApplyCarewebqiUpdatesPathParams;

/**
 * this applies updates from MCG Carewebqi to a given serviceRequest
 *
 * update serviceRequest with changes made within Carewebqi
 */
export const ApplyCarewebqiUpdates = ({
  serviceRequestId,
  ...props
}: ApplyCarewebqiUpdatesProps) => (
  <Mutate<
    CarewebqiUpdateResponse,
    unknown,
    void,
    void,
    ApplyCarewebqiUpdatesPathParams
  >
    verb="POST"
    path={`/carewebqi/${serviceRequestId}/applyCarewebqiUpdates`}
    {...props}
  />
);

export type UseApplyCarewebqiUpdatesProps = Omit<
  UseMutateProps<
    CarewebqiUpdateResponse,
    unknown,
    void,
    void,
    ApplyCarewebqiUpdatesPathParams
  >,
  "path" | "verb"
> &
  ApplyCarewebqiUpdatesPathParams;

/**
 * this applies updates from MCG Carewebqi to a given serviceRequest
 *
 * update serviceRequest with changes made within Carewebqi
 */
export const useApplyCarewebqiUpdates = ({
  serviceRequestId,
  ...props
}: UseApplyCarewebqiUpdatesProps) =>
  useMutate<
    CarewebqiUpdateResponse,
    unknown,
    void,
    void,
    ApplyCarewebqiUpdatesPathParams
  >(
    "POST",
    (paramsInPath: ApplyCarewebqiUpdatesPathParams) =>
      `/carewebqi/${paramsInPath.serviceRequestId}/applyCarewebqiUpdates`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface GetBillableFamiliesQueryParams {
  procedureCodes?: string;
}

export type GetBillableFamiliesProps = Omit<
  GetProps<
    (BillableFamily | null)[],
    unknown,
    GetBillableFamiliesQueryParams,
    void
  >,
  "path"
>;

/**
 * Get the Billable families associated with procedure codes
 */
export const GetBillableFamilies = (props: GetBillableFamiliesProps) => (
  <Get<(BillableFamily | null)[], unknown, GetBillableFamiliesQueryParams, void>
    path={`/procedureCode/billableFamilies`}
    {...props}
  />
);

export type UseGetBillableFamiliesProps = Omit<
  UseGetProps<
    (BillableFamily | null)[],
    unknown,
    GetBillableFamiliesQueryParams,
    void
  >,
  "path"
>;

/**
 * Get the Billable families associated with procedure codes
 */
export const useGetBillableFamilies = (props: UseGetBillableFamiliesProps) =>
  useGet<
    (BillableFamily | null)[],
    unknown,
    GetBillableFamiliesQueryParams,
    void
  >(`/procedureCode/billableFamilies`, props);

export interface OutboundFaxesByServiceCaseQueryParams {
  /**
   * Comma delineated list of oubound fax integration statuses
   */
  statuses?: string;
}

export interface OutboundFaxesByServiceCasePathParams {
  serviceCaseId: string;
}

export type OutboundFaxesByServiceCaseProps = Omit<
  MutateProps<
    (OutboundFax | null)[],
    unknown,
    OutboundFaxesByServiceCaseQueryParams,
    void,
    OutboundFaxesByServiceCasePathParams
  >,
  "path" | "verb"
> &
  OutboundFaxesByServiceCasePathParams;

/**
 * Outbound faxes associated to a service case
 */
export const OutboundFaxesByServiceCase = ({
  serviceCaseId,
  ...props
}: OutboundFaxesByServiceCaseProps) => (
  <Mutate<
    (OutboundFax | null)[],
    unknown,
    OutboundFaxesByServiceCaseQueryParams,
    void,
    OutboundFaxesByServiceCasePathParams
  >
    verb="POST"
    path={`/serviceCase/${serviceCaseId}/outboundFaxes`}
    {...props}
  />
);

export type UseOutboundFaxesByServiceCaseProps = Omit<
  UseMutateProps<
    (OutboundFax | null)[],
    unknown,
    OutboundFaxesByServiceCaseQueryParams,
    void,
    OutboundFaxesByServiceCasePathParams
  >,
  "path" | "verb"
> &
  OutboundFaxesByServiceCasePathParams;

/**
 * Outbound faxes associated to a service case
 */
export const useOutboundFaxesByServiceCase = ({
  serviceCaseId,
  ...props
}: UseOutboundFaxesByServiceCaseProps) =>
  useMutate<
    (OutboundFax | null)[],
    unknown,
    OutboundFaxesByServiceCaseQueryParams,
    void,
    OutboundFaxesByServiceCasePathParams
  >(
    "POST",
    (paramsInPath: OutboundFaxesByServiceCasePathParams) =>
      `/serviceCase/${paramsInPath.serviceCaseId}/outboundFaxes`,
    { pathParams: { serviceCaseId }, ...props }
  );

export interface GetOutboundFaxDownloadPathParams {
  /**
   * A unique identifier for a `Outbound fax`.
   */
  id: string;
}

export type GetOutboundFaxDownloadProps = Omit<
  GetProps<void, unknown, void, GetOutboundFaxDownloadPathParams>,
  "path"
> &
  GetOutboundFaxDownloadPathParams;

/**
 * Download an Outbound fax
 *
 * Downloads an existing `Outbound fax`.
 */
export const GetOutboundFaxDownload = ({
  id,
  ...props
}: GetOutboundFaxDownloadProps) => (
  <Get<void, unknown, void, GetOutboundFaxDownloadPathParams>
    path={`/outboundFax/${id}/download`}
    {...props}
  />
);

export type UseGetOutboundFaxDownloadProps = Omit<
  UseGetProps<void, unknown, void, GetOutboundFaxDownloadPathParams>,
  "path"
> &
  GetOutboundFaxDownloadPathParams;

/**
 * Download an Outbound fax
 *
 * Downloads an existing `Outbound fax`.
 */
export const useGetOutboundFaxDownload = ({
  id,
  ...props
}: UseGetOutboundFaxDownloadProps) =>
  useGet<void, unknown, void, GetOutboundFaxDownloadPathParams>(
    (paramsInPath: GetOutboundFaxDownloadPathParams) =>
      `/outboundFax/${paramsInPath.id}/download`,
    { pathParams: { id }, ...props }
  );

export interface GetProcedureCodeUnitsConfigurationsRequestBody {
  healthPlanName?: string;
  encounterType?: string;
  clinicalServiceUnitsRequests?: ClinicalServiceSlim[];
}

export type GetProcedureCodeUnitsConfigurationsProps = Omit<
  MutateProps<
    ClinicalServiceUnitsConfiguration[],
    unknown,
    void,
    GetProcedureCodeUnitsConfigurationsRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Get Procedure Code Units Configurations Grouped by Clinical Services
 *
 * Gets all procedure code units configurations associated with clinical services and procedure codes
 */
export const GetProcedureCodeUnitsConfigurations = (
  props: GetProcedureCodeUnitsConfigurationsProps
) => (
  <Mutate<
    ClinicalServiceUnitsConfiguration[],
    unknown,
    void,
    GetProcedureCodeUnitsConfigurationsRequestBody,
    void
  >
    verb="POST"
    path={`/procedureCodeUnitsConfiguration/clinicalServiceConfigurations`}
    {...props}
  />
);

export type UseGetProcedureCodeUnitsConfigurationsProps = Omit<
  UseMutateProps<
    ClinicalServiceUnitsConfiguration[],
    unknown,
    void,
    GetProcedureCodeUnitsConfigurationsRequestBody,
    void
  >,
  "path" | "verb"
>;

/**
 * Get Procedure Code Units Configurations Grouped by Clinical Services
 *
 * Gets all procedure code units configurations associated with clinical services and procedure codes
 */
export const useGetProcedureCodeUnitsConfigurations = (
  props: UseGetProcedureCodeUnitsConfigurationsProps
) =>
  useMutate<
    ClinicalServiceUnitsConfiguration[],
    unknown,
    void,
    GetProcedureCodeUnitsConfigurationsRequestBody,
    void
  >(
    "POST",
    `/procedureCodeUnitsConfiguration/clinicalServiceConfigurations`,
    props
  );

export interface GetAdvanceNotificationPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
}

export type GetAdvanceNotificationProps = Omit<
  GetProps<
    AdvanceNotificationResponse,
    unknown,
    void,
    GetAdvanceNotificationPathParams
  >,
  "path"
> &
  GetAdvanceNotificationPathParams;

export const GetAdvanceNotification = ({
  id,
  ...props
}: GetAdvanceNotificationProps) => (
  <Get<
    AdvanceNotificationResponse,
    unknown,
    void,
    GetAdvanceNotificationPathParams
  >
    path={`/advanceNotification/${id}`}
    {...props}
  />
);

export type UseGetAdvanceNotificationProps = Omit<
  UseGetProps<
    AdvanceNotificationResponse,
    unknown,
    void,
    GetAdvanceNotificationPathParams
  >,
  "path"
> &
  GetAdvanceNotificationPathParams;

export const useGetAdvanceNotification = ({
  id,
  ...props
}: UseGetAdvanceNotificationProps) =>
  useGet<
    AdvanceNotificationResponse,
    unknown,
    void,
    GetAdvanceNotificationPathParams
  >(
    (paramsInPath: GetAdvanceNotificationPathParams) =>
      `/advanceNotification/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export interface UpdateAdvanceNotificationPathParams {
  /**
   * A unique identifier for a `AdvanceNotification`.
   */
  id: string;
}

export type UpdateAdvanceNotificationProps = Omit<
  MutateProps<
    AdvanceNotificationResponse,
    AdvanceNotificationUpdateErrorResponse,
    void,
    AdvanceNotificationCreatePayload,
    UpdateAdvanceNotificationPathParams
  >,
  "path" | "verb"
> &
  UpdateAdvanceNotificationPathParams;

export const UpdateAdvanceNotification = ({
  id,
  ...props
}: UpdateAdvanceNotificationProps) => (
  <Mutate<
    AdvanceNotificationResponse,
    AdvanceNotificationUpdateErrorResponse,
    void,
    AdvanceNotificationCreatePayload,
    UpdateAdvanceNotificationPathParams
  >
    verb="PATCH"
    path={`/advanceNotification/${id}`}
    {...props}
  />
);

export type UseUpdateAdvanceNotificationProps = Omit<
  UseMutateProps<
    AdvanceNotificationResponse,
    AdvanceNotificationUpdateErrorResponse,
    void,
    AdvanceNotificationCreatePayload,
    UpdateAdvanceNotificationPathParams
  >,
  "path" | "verb"
> &
  UpdateAdvanceNotificationPathParams;

export const useUpdateAdvanceNotification = ({
  id,
  ...props
}: UseUpdateAdvanceNotificationProps) =>
  useMutate<
    AdvanceNotificationResponse,
    AdvanceNotificationUpdateErrorResponse,
    void,
    AdvanceNotificationCreatePayload,
    UpdateAdvanceNotificationPathParams
  >(
    "PATCH",
    (paramsInPath: UpdateAdvanceNotificationPathParams) =>
      `/advanceNotification/${paramsInPath.id}`,
    { pathParams: { id }, ...props }
  );

export type DeleteAdvanceNotificationProps = Omit<
  MutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Soft delete
 */
export const DeleteAdvanceNotification = (
  props: DeleteAdvanceNotificationProps
) => (
  <Mutate<void, unknown, void, string, void>
    verb="DELETE"
    path={`/advanceNotification`}
    {...props}
  />
);

export type UseDeleteAdvanceNotificationProps = Omit<
  UseMutateProps<void, unknown, void, string, void>,
  "path" | "verb"
>;

/**
 * Soft delete
 */
export const useDeleteAdvanceNotification = (
  props: UseDeleteAdvanceNotificationProps
) =>
  useMutate<void, unknown, void, string, void>(
    "DELETE",
    `/advanceNotification`,
    { ...props }
  );

export interface CreateAdvanceNotificationQueryParams {
  /**
   * Patient ID
   */
  patientId?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * The view to return for an advance notification
   */
  view?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  sort?: string;
  id?: string;
}

export type CreateAdvanceNotificationProps = Omit<
  MutateProps<
    AdvanceNotificationResponse,
    unknown,
    CreateAdvanceNotificationQueryParams,
    AdvanceNotificationCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * creates a new advance notification
 */
export const CreateAdvanceNotification = (
  props: CreateAdvanceNotificationProps
) => (
  <Mutate<
    AdvanceNotificationResponse,
    unknown,
    CreateAdvanceNotificationQueryParams,
    AdvanceNotificationCreatePayload,
    void
  >
    verb="POST"
    path={`/advanceNotification`}
    {...props}
  />
);

export type UseCreateAdvanceNotificationProps = Omit<
  UseMutateProps<
    AdvanceNotificationResponse,
    unknown,
    CreateAdvanceNotificationQueryParams,
    AdvanceNotificationCreatePayload,
    void
  >,
  "path" | "verb"
>;

/**
 * creates a new advance notification
 */
export const useCreateAdvanceNotification = (
  props: UseCreateAdvanceNotificationProps
) =>
  useMutate<
    AdvanceNotificationResponse,
    unknown,
    CreateAdvanceNotificationQueryParams,
    AdvanceNotificationCreatePayload,
    void
  >("POST", `/advanceNotification`, props);

export interface GetAdvanceNotificationsQueryParams {
  /**
   * Patient ID
   */
  patientId?: string;
  /**
   * Cohere ID
   */
  cohereId?: string;
  /**
   * The view to return for an advance notification
   */
  view?: string;
  /**
   * Number of results to return
   */
  max?: number;
  /**
   * starting with result number
   */
  offset?: number;
  sort?: string;
  id?: string;
}

export type GetAdvanceNotificationsProps = Omit<
  GetProps<
    AdvanceNotificationResponse[],
    unknown,
    GetAdvanceNotificationsQueryParams,
    void
  >,
  "path"
>;

export const GetAdvanceNotifications = (
  props: GetAdvanceNotificationsProps
) => (
  <Get<
    AdvanceNotificationResponse[],
    unknown,
    GetAdvanceNotificationsQueryParams,
    void
  >
    path={`/advanceNotification`}
    {...props}
  />
);

export type UseGetAdvanceNotificationsProps = Omit<
  UseGetProps<
    AdvanceNotificationResponse[],
    unknown,
    GetAdvanceNotificationsQueryParams,
    void
  >,
  "path"
>;

export const useGetAdvanceNotifications = (
  props: UseGetAdvanceNotificationsProps
) =>
  useGet<
    AdvanceNotificationResponse[],
    unknown,
    GetAdvanceNotificationsQueryParams,
    void
  >(`/advanceNotification`, props);

export interface SubmitAdvanceNotificationPathParams {
  /**
   * The ID of the advance notification to submit
   */
  advanceNotificationId: string;
}

export type SubmitAdvanceNotificationProps = Omit<
  MutateProps<
    AdvanceNotification,
    AdvanceNotificationSubmitErrorResponse,
    void,
    AdvanceNotification,
    SubmitAdvanceNotificationPathParams
  >,
  "path" | "verb"
> &
  SubmitAdvanceNotificationPathParams;

/**
 * Submit Advance Notification
 */
export const SubmitAdvanceNotification = ({
  advanceNotificationId,
  ...props
}: SubmitAdvanceNotificationProps) => (
  <Mutate<
    AdvanceNotification,
    AdvanceNotificationSubmitErrorResponse,
    void,
    AdvanceNotification,
    SubmitAdvanceNotificationPathParams
  >
    verb="PATCH"
    path={`/advanceNotification/${advanceNotificationId}/submit`}
    {...props}
  />
);

export type UseSubmitAdvanceNotificationProps = Omit<
  UseMutateProps<
    AdvanceNotification,
    AdvanceNotificationSubmitErrorResponse,
    void,
    AdvanceNotification,
    SubmitAdvanceNotificationPathParams
  >,
  "path" | "verb"
> &
  SubmitAdvanceNotificationPathParams;

/**
 * Submit Advance Notification
 */
export const useSubmitAdvanceNotification = ({
  advanceNotificationId,
  ...props
}: UseSubmitAdvanceNotificationProps) =>
  useMutate<
    AdvanceNotification,
    AdvanceNotificationSubmitErrorResponse,
    void,
    AdvanceNotification,
    SubmitAdvanceNotificationPathParams
  >(
    "PATCH",
    (paramsInPath: SubmitAdvanceNotificationPathParams) =>
      `/advanceNotification/${paramsInPath.advanceNotificationId}/submit`,
    { pathParams: { advanceNotificationId }, ...props }
  );

export interface AdvanceNotificationTransitionFromServiceRequestPathParams {
  /**
   * A unique identifier for a `Service Request`.
   */
  serviceRequestId: string;
}

export type AdvanceNotificationTransitionFromServiceRequestProps = Omit<
  MutateProps<
    AdvanceNotification,
    unknown,
    void,
    AdvanceNotification,
    AdvanceNotificationTransitionFromServiceRequestPathParams
  >,
  "path" | "verb"
> &
  AdvanceNotificationTransitionFromServiceRequestPathParams;

/**
 * Service Request transition to an Advance Notification
 */
export const AdvanceNotificationTransitionFromServiceRequest = ({
  serviceRequestId,
  ...props
}: AdvanceNotificationTransitionFromServiceRequestProps) => (
  <Mutate<
    AdvanceNotification,
    unknown,
    void,
    AdvanceNotification,
    AdvanceNotificationTransitionFromServiceRequestPathParams
  >
    verb="POST"
    path={`/advanceNotification/transitionFrom/serviceRequest/${serviceRequestId}`}
    {...props}
  />
);

export type UseAdvanceNotificationTransitionFromServiceRequestProps = Omit<
  UseMutateProps<
    AdvanceNotification,
    unknown,
    void,
    AdvanceNotification,
    AdvanceNotificationTransitionFromServiceRequestPathParams
  >,
  "path" | "verb"
> &
  AdvanceNotificationTransitionFromServiceRequestPathParams;

/**
 * Service Request transition to an Advance Notification
 */
export const useAdvanceNotificationTransitionFromServiceRequest = ({
  serviceRequestId,
  ...props
}: UseAdvanceNotificationTransitionFromServiceRequestProps) =>
  useMutate<
    AdvanceNotification,
    unknown,
    void,
    AdvanceNotification,
    AdvanceNotificationTransitionFromServiceRequestPathParams
  >(
    "POST",
    (paramsInPath: AdvanceNotificationTransitionFromServiceRequestPathParams) =>
      `/advanceNotification/transitionFrom/serviceRequest/${paramsInPath.serviceRequestId}`,
    { pathParams: { serviceRequestId }, ...props }
  );

export interface RevertToServiceRequestPathParams {
  /**
   * A unique identifier for an `Advance Notification`.
   */
  advanceNotificationId: string;
}

export type RevertToServiceRequestProps = Omit<
  MutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    RevertToServiceRequestPathParams
  >,
  "path" | "verb"
> &
  RevertToServiceRequestPathParams;

/**
 * Advance Notification transition to a Service Request
 */
export const RevertToServiceRequest = ({
  advanceNotificationId,
  ...props
}: RevertToServiceRequestProps) => (
  <Mutate<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    RevertToServiceRequestPathParams
  >
    verb="POST"
    path={`/advanceNotification/${advanceNotificationId}/revertToServiceRequest`}
    {...props}
  />
);

export type UseRevertToServiceRequestProps = Omit<
  UseMutateProps<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    RevertToServiceRequestPathParams
  >,
  "path" | "verb"
> &
  RevertToServiceRequestPathParams;

/**
 * Advance Notification transition to a Service Request
 */
export const useRevertToServiceRequest = ({
  advanceNotificationId,
  ...props
}: UseRevertToServiceRequestProps) =>
  useMutate<
    ServiceRequestResponse,
    unknown,
    void,
    void,
    RevertToServiceRequestPathParams
  >(
    "POST",
    (paramsInPath: RevertToServiceRequestPathParams) =>
      `/advanceNotification/${paramsInPath.advanceNotificationId}/revertToServiceRequest`,
    { pathParams: { advanceNotificationId }, ...props }
  );

export type SummarizeReviewAiInsightsProps = Omit<
  MutateProps<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightSummaryRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Summarize Review AI insights for a given entity
 */
export const SummarizeReviewAiInsights = (
  props: SummarizeReviewAiInsightsProps
) => (
  <Mutate<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightSummaryRequest,
    void
  >
    verb="POST"
    path={`/reviewai/insights/summary`}
    {...props}
  />
);

export type UseSummarizeReviewAiInsightsProps = Omit<
  UseMutateProps<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightSummaryRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Summarize Review AI insights for a given entity
 */
export const useSummarizeReviewAiInsights = (
  props: UseSummarizeReviewAiInsightsProps
) =>
  useMutate<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightSummaryRequest,
    void
  >("POST", `/reviewai/insights/summary`, props);

export type GetReviewAiClinicalInsightsProps = Omit<
  MutateProps<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightClinicalRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Get Review AI clinical insights for a given entity and framework
 */
export const GetReviewAiClinicalInsights = (
  props: GetReviewAiClinicalInsightsProps
) => (
  <Mutate<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightClinicalRequest,
    void
  >
    verb="POST"
    path={`/reviewai/insights/clinical`}
    {...props}
  />
);

export type UseGetReviewAiClinicalInsightsProps = Omit<
  UseMutateProps<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightClinicalRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Get Review AI clinical insights for a given entity and framework
 */
export const useGetReviewAiClinicalInsights = (
  props: UseGetReviewAiClinicalInsightsProps
) =>
  useMutate<
    ReviewAiInsightsResponse,
    unknown,
    void,
    ReviewAiInsightClinicalRequest,
    void
  >("POST", `/reviewai/insights/clinical`, props);

export type GetReviewAiClinicalInsightsSupportedConditionsProps = Omit<
  MutateProps<
    ReviewAiInsightSupportedConditionsResponse,
    unknown,
    void,
    ReviewAiInsightSupportedConditionsRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Get Review AI insight supported conditions for a given entity
 */
export const GetReviewAiClinicalInsightsSupportedConditions = (
  props: GetReviewAiClinicalInsightsSupportedConditionsProps
) => (
  <Mutate<
    ReviewAiInsightSupportedConditionsResponse,
    unknown,
    void,
    ReviewAiInsightSupportedConditionsRequest,
    void
  >
    verb="POST"
    path={`/reviewai/insights/supportedConditions`}
    {...props}
  />
);

export type UseGetReviewAiClinicalInsightsSupportedConditionsProps = Omit<
  UseMutateProps<
    ReviewAiInsightSupportedConditionsResponse,
    unknown,
    void,
    ReviewAiInsightSupportedConditionsRequest,
    void
  >,
  "path" | "verb"
>;

/**
 * Get Review AI insight supported conditions for a given entity
 */
export const useGetReviewAiClinicalInsightsSupportedConditions = (
  props: UseGetReviewAiClinicalInsightsSupportedConditionsProps
) =>
  useMutate<
    ReviewAiInsightSupportedConditionsResponse,
    unknown,
    void,
    ReviewAiInsightSupportedConditionsRequest,
    void
  >("POST", `/reviewai/insights/supportedConditions`, props);

export type CreateReviewAIMetadataDocumentProps = Omit<
  MutateProps<ReviewAIMetadata, unknown, void, ReviewAIMetadata, void>,
  "path" | "verb"
>;

/**
 * Create a review ai metadata document
 */
export const CreateReviewAIMetadataDocument = (
  props: CreateReviewAIMetadataDocumentProps
) => (
  <Mutate<ReviewAIMetadata, unknown, void, ReviewAIMetadata, void>
    verb="POST"
    path={`/reviewai/reviewAIMetadata`}
    {...props}
  />
);

export type UseCreateReviewAIMetadataDocumentProps = Omit<
  UseMutateProps<ReviewAIMetadata, unknown, void, ReviewAIMetadata, void>,
  "path" | "verb"
>;

/**
 * Create a review ai metadata document
 */
export const useCreateReviewAIMetadataDocument = (
  props: UseCreateReviewAIMetadataDocumentProps
) =>
  useMutate<ReviewAIMetadata, unknown, void, ReviewAIMetadata, void>(
    "POST",
    `/reviewai/reviewAIMetadata`,
    props
  );
